import { useState, useEffect, useMemo, useCallback, useRef, memo, Fragment, type ReactNode } from 'react';
import QuickBuildProgress from '@/components/QuickBuildProgress';
import type { LocalQuick } from '@/lib/quickBuildModel';
import { AlbumThemeBar } from '@/components/theme/ThemeBar';
import { albumCountForBundle } from '@/lib/followUpStateMachine';
import { FIVE_UP_TOP_FRAC, FOUR_MAIN_TOP_FRAC, BASE_GAP_MM } from '@/lib/slotGeometry';
/** Strip thumbnails: the fixed top band of the layouts that have one. */
const THUMB_TOP_FRAC: Record<string, number> = { '3-up': 0.6, '5-up': FIVE_UP_TOP_FRAC, '4-up-main': FOUR_MAIN_TOP_FRAC };
import { gapPx as photoGapPx, slotArs as photoGapSlotArs, clampGapMm, normalizeGapColor, MAX_GAP_MM, DEFAULT_GAP_COLOR, photoGapOf, withThemedBook, framePaddingPx, twoRowFlex, evenRowPct, EVEN_CONTAINER, type PhotoGap } from '@/lib/photoGap';
import { isThemedBook, isLeftPage, printsIntro } from '@/lib/bookStructure';
import { createPortal } from 'react-dom';
import { List as VirtualList, type RowComponentProps } from 'react-window';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS as DnDCSS } from '@dnd-kit/utilities';
import { spreadDragTransform } from '@/lib/spreadDragStyle';
import { hasStroke, strokeLayerCss, fillOpacity, RECT_FILL_DEFAULT, ELLIPSE_FILL_DEFAULT } from '@/lib/decor/shapePaint';
import { resolveSpineTextColor } from '@/lib/spineColor';
import { isIosDevice, galleryAccept } from '@/lib/iosPicker';
import { classifyUploadSelection, uploadNoticeFor } from '@/lib/uploadFileFilter';
import { putOrCleanupOrphan } from '@/lib/uploadOrphanCleanup';
import { uploadFailureReason } from '@/lib/uploadFailureReason';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlbumSubmittedScreen } from '@/components/AlbumSubmittedScreen';
import { SafeSvg } from '@/components/SafeSvg';
import { isAwaitingConfirmation, isUploadWindowOpen } from '@/lib/editorAccess';
import heic2any from 'heic2any';
import * as exifr from 'exifr';
import {
  ordersApi,
  sessionsApi,
  photosApi,
  coversApi,
  settingsApi,
  aiApi,
  editorResourcesApi,
  templatesApi,
  uploadToSignedUrl, PDF_UPLOAD,
  type Photo,
  type Cover,
  type CoverCategory,
  type Order,
  type EditorIcon,
  type AlbumCategory,
  type LayoutTemplate,
  type LayoutTemplateSlot,
  type ChapterMeta,
  type HiddenDuplicatePhoto,
} from '@/lib/api';
import { forAutomaticLayout, placedIdsOf } from '@/lib/photoDedup';
import { HiddenDuplicatesSection } from '@/components/HiddenDuplicatesSection';
import { analyzePhoto, releaseModels as releaseAnalyzerModels } from '@/lib/photoAnalyzer';
import { releaseModels as releaseLazyModels } from '@/lib/lazyPhotoAnalysis';
import { useGridThumbs, clearGridThumbs } from '@/lib/gridThumb';
import { convertImageFileToPng, isConvertibleImageType, MAX_COVER_UPLOAD_BYTES, normalizeCoverImageFile, isHeicFile } from '@/lib/imageUtils';
import { normalizeJpegOrientation } from '@/lib/normalizeOrientation';
import { processForUpload } from '@/lib/imagePipeline';
import { PRINT_MAX_DIM, PRINT_JPEG_QUALITY, DISPLAY_MAX_DIM, DISPLAY_JPEG_QUALITY, TILE_MAX_DIM, TILE_JPEG_QUALITY } from '@/lib/imageLimits';
import { photoPrintQuality } from '@/lib/photoPrintQuality';
import { sha256Hex } from '@/lib/contentHash';
import { pdfPhotoSourceUrl } from '@/lib/pdfPhotoSource';
import { rotationCoverGrowth } from '@/lib/slotRotation';
import { supabase } from '@/lib/supabase';
import { cn, getCheckoutRedirect } from '@/lib/utils';
import { withAlpha } from '@/lib/brand';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import type { TextZoneOverride, ExtraTextZone } from '@/components/CoverThumbnail';
import { Modal } from '@/components/ui/Modal';
import { iconDisplayUrl } from '@/lib/coverIcons';
import { PageBackgroundLayer, ElementShadow, photoMaskStyle } from '@/components/page/PageDecor';
import { TintFilter, tintCss } from '@/components/page/TintFilter';
import { shadowPlan } from '@/lib/decor/decorStyle';
import { hiddenInPrint, HOLDER_HINT, type HolderRole } from '@/lib/decor/holders';
import { printOpticalSize, textLineHeight } from '@/lib/pdfTextLayout';
import { CoverIconPickerModal } from '@/components/CoverIconPickerModal';
import { pickerThumbUrl } from '@/lib/coverImageSource';
import { TextOverlayChrome } from '@/components/TextOverlayChrome';
import { fontStackFor, hasArabic } from '@/lib/textBidi';
import { AiCaptionsDialog } from '@/components/editor/AiCaptionsDialog';
import { detachCaption, followCaptionsInEditor, mergeElements, photoMetaFrom } from '@/lib/editorCaptions';
import { bandCss } from '@/lib/textBand';
import { bandStyleFrom, type CaptionPage } from '@/lib/captionLayout';
import { TemplatedPhotoSlot, type PhotoFitInSlot } from '@/components/TemplatedPhotoSlot';
import { TemplatedPageWithSvgFetch } from '@/components/TemplatedPage';
import { OnboardingHint } from '@/components/OnboardingHint';
import HorizontalScrollbar from '@/components/HorizontalScrollbar';
import { useTouchDragPhoto } from '@/lib/useTouchDragPhoto';
import { useStuckUser } from '@/lib/useStuckUser';
import { EDITOR_TEXT_FONTS } from '@/lib/editorFonts';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermissions } from '@/lib/permissionsStore';
import { useAdminTheme } from '@/lib/theme/adminTheme';
import { ThemeToggle } from '@/components/appShell/ThemeToggle';
import { generateAlbumPdfs, type PdfGenProgress } from '@/lib/pdfGenerator';
import { renderAndUploadAlbumPdfs, photoGapHashInput } from '@/lib/multiAlbumPdf';
import { compositeSourceHash } from '@/lib/albumSourceHash';
import type { AlbumCouponOutcome, OrderCouponPlan } from '@/lib/couponPrint';
import { soloLandscapeFitWidth } from '@/lib/soloFit';
import { printPageOf, printedInteriorTotal } from '@/lib/printPageNumbers';
import { spreadCount, spreadPages, spreadOf, clampSpread, lastSpread, spreadAlbumPages, spreadPagesLabel, spreadPrintLabel, stripSpreadLabel, stripOutroLabel, insertAfterIndex, reorderSpreads } from '@/lib/editorSpreads';
import { renumberAfterDelete } from '@/lib/pageDelete';
import { countOutroPages, insertOutroPage, isOutroPage, movePageBy, reinsertOutroPages, remapPageKeyed, splitOutroPages } from '@/lib/outroPage';
import { resolveBasePagesPerAlbum, resolvePricePerPage } from '@/lib/orderPricing';
import { chooseLandscapeArrangement } from '@/lib/aspectSlotting';
import { acquireScreenWakeLock, installUnloadGuard, withRetry } from '@/lib/submissionResilience';
import { IosUploadNote } from '@/components/IosUploadNote';
import { freshSessionGate } from '@/lib/editorSessionGate';
// « Nouvel éditeur mobile » (2026-10-19) — a new PHONE layout around the same
// components; one switch (lib/mobileEditorV2.ts), OFF by default.
import { useMobileEditorV2, isViewOnlyAlbum } from '@/lib/mobileEditorV2';
import { MobileDockContext, useMobileDock, DOCKED_BAR_STYLE } from '@/components/editor/mobile/dock';
import { MobileTopBar } from '@/components/editor/mobile/MobileTopBar';
import { MobileTabBar } from '@/components/editor/mobile/MobileTabBar';
import { type M2Tab } from '@/components/editor/mobile/m2Model';
import { MobileSheet } from '@/components/editor/mobile/MobileSheet';
import { M2Row, M2SectionTitle, M2LockedBar, M2_HELP_MESSAGE } from '@/components/editor/mobile/MobileMenu';
import { useVisualViewport } from '@/components/editor/mobile/useVisualViewport';
import { waSupportLink } from '@/lib/whatsappSupport';
import { albumContentKey, onlyStampMoved } from '@/lib/staleWriteRecovery';
import { titlePrefill, albumTitle } from '@/lib/titleSource';
import { ThemeModelPicker } from '@/components/editor/ThemeModelPicker';
import { isDeleteKey, isTypingTarget, keyDeletable } from '@/lib/elementDeleteKey';
import { StickersSection, CoverIconsSection } from '@/components/editor/StickerPanel';
import { albumThemeFrom, stickerElement, stickerMedUrl, stickerOriginalUrl, type Sticker } from '@/lib/stickers/catalog';
import {
  clampToFace, coverImageElement, coverPhotoElement, elementsOnFace, faceAspect, moveLayer, moveToFace, newCoverElementId,
  readCoverElements, boxAtPoint, COVER_FACE_LABEL, type CoverElement, type CoverFace, type LayerMove,
  coverTextElement, coverTextPt, coverFontSizeForPt, coverSafeZonePct, crossesSafeZone, fitIntoSafeZone, printedExtent,
  type SafeZoneSettings,
} from '@/lib/coverElements';
import { CoverTextBox, SafeZoneWarning } from '@/components/CoverTextBox';
import { boxUnits, canvasMeasureAt, fitFontSize } from '@/lib/coverText';
import { DEFAULT_PRINT_SETTINGS, withDerivedVinyl, type PrintSettings } from '@/lib/assemblyTemplate';
import { onTileDrop, type TileDragPayload, type TileDropDetail } from '@/lib/tileDrag';
import { backCoverPlan, isBlankBack } from '@/lib/backCoverPlan';
import { COVER_PANEL_ASPECT } from '@/components/CoverThumbnail';
import { type EditorThemedPage } from '@/lib/editorModelsApi';
import { boxAspect, currentCrop, facesFrom, headsCut, headsCutOnHalf, panCrop, swapCrop, zoomCrop, zoomOf, type WindowPhoto } from '@/lib/decor/themedWindow';
import { applyModel, applySpreadModel, isModelWindow, reattachAfterRegen, removeModel, repairModelPages, spreadMateIndex, swapIntoWindow } from '@/lib/themedPageEdits';
import { cropCss } from '@/lib/decor/cropMath';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ─── HEIC Conversion Pipeline (shared with PhotoUploadPage) ──────────────────
const HEIC_CONCURRENT = 2;
const MAX_PHOTO_DIM = PRINT_MAX_DIM;      // see lib/imageLimits.ts
const JPEG_QUALITY = PRINT_JPEG_QUALITY;
const THUMB_DIM = DISPLAY_MAX_DIM;        // light editor/grid tier (screen only)
const THUMB_QUALITY = DISPLAY_JPEG_QUALITY;

class ConversionSemaphore {
  private slots: number;
  private queue: (() => void)[] = [];
  constructor(limit: number) { this.slots = limit; }
  acquire(): Promise<void> {
    if (this.slots > 0) { this.slots--; return Promise.resolve(); }
    return new Promise<void>((resolve) => this.queue.push(resolve));
  }
  release() {
    if (this.queue.length > 0) { this.queue.shift()!(); } else { this.slots++; }
  }
}

const editorHeicSemaphore = new ConversionSemaphore(HEIC_CONCURRENT);
const editorHeicCache = new Map<string, Promise<File>>();

/** Canvas resize to max dimension — reduces upload size dramatically */
async function resizeJpegBlob(blob: Blob, maxDim: number, quality: number): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      if (w <= maxDim && h <= maxDim) { resolve(blob); return; }
      const scale = maxDim / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((result) => resolve(result ?? blob), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
    img.src = url;
  });
}

/** Convert HEIC→JPEG with semaphore gating + resize + caching */
function startEditorHeicConversion(cacheKey: string, file: File): Promise<File> {
  if (editorHeicCache.has(cacheKey)) return editorHeicCache.get(cacheKey)!;
  const p = (async () => {
    await editorHeicSemaphore.acquire();
    try {
      const rawBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const jpegBlob = Array.isArray(rawBlob) ? rawBlob[0] : rawBlob;
      // Resize off the main thread (shared worker) — same engine as the step
      // flow; falls back to the main-thread canvas if the worker is unavailable.
      const resized = (await processForUpload(jpegBlob, MAX_PHOTO_DIM, JPEG_QUALITY))
        ?? await resizeJpegBlob(jpegBlob, MAX_PHOTO_DIM, JPEG_QUALITY);
      const baseName = file.name.replace(/\.(heic|heif)$/i, '');
      return new File([resized], `${baseName}.jpg`, { type: 'image/jpeg' });
    } finally {
      editorHeicSemaphore.release();
    }
  })();
  editorHeicCache.set(cacheKey, p);
  return p;
}

/** Resize non-HEIC images if they exceed max dimension */
async function resizeIfNeeded(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/heic') return file;
  try {
    const resized = await resizeJpegBlob(file, MAX_PHOTO_DIM, JPEG_QUALITY);
    if (resized.size < file.size * 0.95) {
      return new File([resized], file.name, { type: 'image/jpeg' });
    }
  } catch { /* keep original */ }
  return file;
}

/**
 * Sample the dominant edge color of a cover image. Edges (top/bottom/left/right
 * borders) are usually the solid background, while the center holds artwork —
 * so border-only sampling gives a much better "background color" estimate than
 * full-image averaging. We use the channel-wise median for robustness against
 * gradients or darker accents.
 *
 * Returns a "#rrggbb" string, or null on CORS / decode failure.
 */
async function extractDominantColorFromUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const W = 60, H = 60;
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, W, H);
        const data = ctx.getImageData(0, 0, W, H).data;

        const rs: number[] = [], gs: number[] = [], bs: number[] = [];
        const sample = (x: number, y: number) => {
          const i = (y * W + x) * 4;
          if (data[i + 3] < 128) return; // skip transparent
          rs.push(data[i]); gs.push(data[i + 1]); bs.push(data[i + 2]);
        };
        for (let x = 0; x < W; x++) { sample(x, 0); sample(x, H - 1); }
        for (let y = 1; y < H - 1; y++) { sample(0, y); sample(W - 1, y); }

        if (rs.length === 0) return resolve(null);

        const median = (arr: number[]) => {
          const sorted = [...arr].sort((a, b) => a - b);
          return sorted[Math.floor(sorted.length / 2)];
        };
        const hex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
        resolve(`#${hex(median(rs))}${hex(median(gs))}${hex(median(bs))}`);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// ─── Brand Colors ─────────────────────────────────────────────────────────────

// Editor color palette — pinned to canonical brand tokens (see lib/brand.ts).
// Keep field names local-friendly; only the literal hex values are unified
// across customer pages.
const COLORS = {
  gold:       '#C9A24A',  // BRAND.gold
  goldLight:  '#D4B86A',
  goldDark:   '#8B6F26',
  goldDeep:   '#A8842F',
  dark:       '#1F2937',  // BRAND.dark
  darkSoft:   '#2A2A2A',
  beige:      '#F5EFE0',
  beigeLight: '#FAF6EC',
  white:      '#FFFFFF',
  overlay:    'rgba(31,41,55,0.6)',
  muted:      '#6B7280',
  danger:     '#EF4444',
  success:    '#10B981',
};

// ─── Night mode: the editor's CHROME palette (owner 2026-10-03) ──────────────
// A staff member in dark gets the editor's chrome in night mode (header, side
// panels, tray, page strip, menus, dialogs); a customer never does. Each value
// is a CSS variable whose LIGHT value is EXACTLY the COLORS hex it replaces
// (index.css @theme static), so light mode is unchanged to the pixel. Inside a
// light island ([data-adm-media]: the pages, the cover spread, the page
// thumbnails) they resolve to the light hex again. CHROME ONLY — never a saved
// colour (fontColor, fills, gap colour…), an SVG attribute or a canvas draw:
// those keep COLORS. Guard: lib/theme/editorDark.guard.test.ts
const UI = {
  ink:      'var(--color-ed-ink)',        // COLORS.dark — text
  muted:    'var(--color-adm-muted)',     // COLORS.muted — secondary text
  surface:  'var(--color-adm-surface)',   // COLORS.white — bars, panels, cards, menus
  panel:    'var(--color-ed-panel)',      // COLORS.beigeLight — soft panels, fields
  desk:     'var(--color-ed-desk)',       // COLORS.beige — behind the open book
  goldInk:  'var(--color-adm-gold-ink)',  // COLORS.goldDark — gold text
  goldDeep: 'var(--color-ed-gold-deep)',  // COLORS.goldDeep — deep-gold text
  danger:   'var(--color-ed-danger)',     // COLORS.danger — red TEXT (fills keep COLORS.danger)
  // the editor's few grey literals, same light values
  line:     'var(--color-adm-line)',      // '#E5E7EB' — hairlines, chip edges
  chip:     'var(--color-adm-surface-3)', // '#F3F4F6' — neutral chips / secondary buttons
  faint:    'var(--color-adm-faint)',     // '#9CA3AF' — placeholder-grade hints
  hint:     'var(--color-ed-hint)',       // `${COLORS.muted}80` — tiny half-faded labels (AA at night)
  hint60:   'var(--color-ed-hint-60)',    // `${COLORS.muted}99`
} as const;

const MAX_PAGE_COUNT = 300;
// NOTE: no PREMIUM_PAGE_THRESHOLD constant here any more. The threshold IS the
// order's included-page allowance, which is frozen per order
// (`orders.base_pages_per_album`, migration 082) — a module constant would tint
// pages 37-50 as "premium" on an order that was sold with 50 free pages.
// It is threaded down as `premiumAfter`.

// The blank "Couverture vierge" cover is a CLIENT-ONLY synthetic — there is no
// such row in the DB. It MUST persist as `cover_id = null` (the column is a
// nullable UUID; saving its literal string id '__blank__' 500s every update,
// which is why blank edits never stuck and the last real cover "reappeared").
// ONE definition, shared by the Vierge picker option AND session hydration, so
// a saved-blank reloads as the blank canvas — not the previous design.
const BLANK_COVER = {
  id: '__blank__',
  name: 'Vierge',
  categories: [],
  product_types: [],
  storage_path: null,
  thumbnail_url: null,
  design_url: null,
  format: null,
  text_zones: [
    { type: 'destination' as const, x: 0.1, y: 0.3, width: 0.8, height: 0.12, font_size: 64, font_family: 'Londrina Solid', color: '#1A1A1A', align: 'center' as const, default_text: 'DESTINATION' },
    { type: 'year' as const, x: 0.1, y: 0.45, width: 0.8, height: 0.08, font_size: 32, font_family: 'Londrina Solid', color: '#8A8578', align: 'center' as const, default_text: '2025' },
  ],
  dominant_color: '#FFFFFF',
  width_px: 1860,
  height_px: 2480,
  back_cover_style: 'blank',
  description: 'Couverture vierge',
  search_tags: ['vierge', 'blanc', 'blank', 'custom'],
  usage_count: 0,
  is_visible: true,
  sort_order: -1,
  created_at: '',
  updated_at: '',
} as Cover;

// The cover_id to PERSIST: null for the blank synthetic ('__blank__' is not a
// valid UUID) or no cover; otherwise the real catalogue id. NEVER send '__blank__'.
const persistedCoverId = (c: { id?: string | null } | null | undefined): string | null =>
  !c || c.id === '__blank__' ? null : (c.id ?? null);

// ─── Intro page QR safe zone ───────────────────────────────────────────────
// The intro branding image carries a QR code along its BOTTOM edge. The client
// can add their own text / photos / shapes on top of the intro, but must NOT
// cover the QR. We reserve the bottom band of the page as a protected zone and
// CLAMP every intro element so its box can never enter it. Because the PDF just
// renders the elements over the same background, this single editor-side clamp
// is enough — the QR is guaranteed visible in print.
// TODO: this can later become an admin setting (app_settings.intro_qr_safe_zone_pct).
const INTRO_QR_SAFE_ZONE_PCT = 0.18; // bottom 18% of page height
// Max bottom edge (in page %) an intro element may reach: 100 − 18 = 82.
const INTRO_SAFE_MAX_BOTTOM_PCT = 100 - INTRO_QR_SAFE_ZONE_PCT * 100;

/** Clamp an intro element's box so it stays out of the bottom QR safe zone.
 *  Operates on a partial update merged onto the current element: if the
 *  resulting box's bottom edge (y + height) would dip into the safe band, we
 *  push y up so bottom == INTRO_SAFE_MAX_BOTTOM_PCT. Width/height are left as
 *  authored; only the vertical position is corrected. Returns the (possibly
 *  adjusted) y/height-bearing updates merged in. */
function clampIntroElementBox<T extends { y?: number; height?: number }>(
  current: { y: number; height: number },
  updates: T,
): T {
  const y = updates.y ?? current.y;
  const height = updates.height ?? current.height;
  let nextY = y;
  // Element taller than the safe area → pin to top, can't avoid overlap, but
  // at least keep its top on-page.
  if (height >= INTRO_SAFE_MAX_BOTTOM_PCT) {
    nextY = 0;
  } else if (y + height > INTRO_SAFE_MAX_BOTTOM_PCT) {
    nextY = INTRO_SAFE_MAX_BOTTOM_PCT - height;
  }
  if (nextY < 0) nextY = 0;
  if (nextY === y) return updates;
  return { ...updates, y: nextY };
}

/** The words a new text box starts with. Still there when editing starts →
 *  selected whole, so the first keystroke or paste replaces them
 *  (TextOverlayChrome selectAllOnEdit; #WA-269649 p37 printed « Votre textePequeños… »). */
const NEW_TEXT_PLACEHOLDER = 'Votre texte';

// ─── Interior page print safe zone (FIX 2) ──────────────────────────────────
// Premium, NON-BLOCKING print-safety guidance for interior pages, like Canva's
// margin guides. A faint dashed rectangle is always drawn 5mm inside the trim
// edge; when an element's box crosses into that band we show a small warning
// pill ("risque de coupure"). The client may still place it there — purely
// advisory, no clamp, no PDF/parity impact (the PDF renders by its own bleed).
// 5mm of the 210mm width = 2.38%; 5mm of the 297mm height = 1.68%.
const INTERIOR_SAFE_INSET_X = (5 / 210) * 100; // ≈ 2.38% of page width
const INTERIOR_SAFE_INSET_Y = (5 / 297) * 100; // ≈ 1.68% of page height

/** True when any edge of the element's %-box sits inside the 5mm safe band. */
function isElementInSafeZone(box: { x: number; y: number; width: number; height: number }): boolean {
  return (
    box.x < INTERIOR_SAFE_INSET_X ||
    box.y < INTERIOR_SAFE_INSET_Y ||
    box.x + box.width > 100 - INTERIOR_SAFE_INSET_X ||
    box.y + box.height > 100 - INTERIOR_SAFE_INSET_Y
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PageElement {
  id: string;
  type: 'text' | 'photo' | 'rect' | 'ellipse' | 'icon';
  x: number;       // percent 0-100
  y: number;       // percent 0-100
  width: number;   // percent 0-100
  height: number;  // percent 0-100
  // text-specific
  text?: string;
  /** Captions (2026-10-06, #WA-269649): a thin outline around each letter,
   *  `outlineWidth` × the font size, stroked under the fill (editor and PDF). */
  outlineColor?: string;
  outlineWidth?: number;
  /** The photo this caption belongs to (lib/captionLayout.ts): the caption
   *  tool replaces it on a re-run, and it follows its photo to another page. */
  captionFor?: string;
  /** The slot its photo had, and the page layout, when the caption was placed (captionLayout). */
  captionSlot?: number;
  captionLayout?: string;
  /** The book-colour band behind a caption (lib/textBand.ts, captionLayout.ts). */
  bandFill?: string;
  bandOpacity?: number;
  /** COVER TEXT (2026-10-30) — line spacing (× the font size, default 1.3) and
   *  the band's margin round the box (% of the face width). lib/coverText.ts */
  lineHeight?: number;
  bandPad?: number;
  fontFamily?: string;
  fontSize?: number;
  fontColor?: string;
  fontWeight?: string;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  textAlign?: 'left' | 'center' | 'right';
  // shape-specific
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  borderRadius?: number;
  // photo-specific (extra photo overlay)
  photoId?: string;
  // icon-specific (Section 9): raw SVG markup from editor_icons.
  // Re-fillable via the existing fontColor / fill controls — we recolour
  // any `currentColor` strokes/fills inside the SVG with the chosen tint.
  svg?: string;
  /** Catalogue-décor icon (public covers-bucket PNG URL). When set, the icon
   *  renders as this image (contain-fit) instead of svg. PDF-mirrored. */
  imageUrl?: string;
  /** STICKERS (2026-10-14) — a lighter copy of `imageUrl` (decor/med/…, 900 px
   *  WebP) that ONLY the editor and the preview DOM draw. The PDF always
   *  embeds `imageUrl` (print quality). Same box, same contain-fit: the two
   *  files are the same picture at two sizes. */
  previewUrl?: string;
  iconColor?: string;
  /** Horizontal flip — applies to photo & shape elements. Renderer
   *  mirrors the visual on the X axis without changing position/size. */
  flipH?: boolean;
  /** Vertical flip — same as flipH but on the Y axis. */
  flipV?: boolean;
  /** Rotation in degrees, applied AROUND the element's centre. Driven
   *  by the rotation drag-handle below the SelectionFrame. Combines with
   *  flipH/flipV (rotation outside, flip inside). Persists with the
   *  element. */
  rotation?: number;
  /** When true the element ignores drag + resize + rotate gestures so
   *  the customer can pin it in place. Toolbar lock toggle switches it.
   *  Delete + duplicate stay enabled — locking is "pin position", not
   *  "make read-only". */
  locked?: boolean;
  /** LOCK SIZE (2026-07-07) — narrower than `locked`: freezes only the SIZE
   *  (resize handles + −/+ zoom disabled) while drag/move (and rotate) stay
   *  active, so the customer can reposition a detached photo — e.g. uncrop a
   *  head — without the dimensions drifting. Independent of `locked`. Persists
   *  with the element (page.elements → DB); the PDF reads x/y/width/height and
   *  ignores this flag, so render parity is unaffected. */
  lockSize?: boolean;
  /** CROP (2026-07-23) — photo elements only. NON-DESTRUCTIVE window into the
   *  original image, in normalized source coords (0..1 fractions of the full
   *  photo): x/y = top-left, w/h = size. The original file is never modified —
   *  re-opening the crop tool shows the whole photo again with this rect
   *  pre-placed. The visible pixels are the crop region COVER-fitted into the
   *  element box (editor CSS = PDF canvas pre-crop in drawPageElements —
   *  parity contract). Absent/undefined = whole photo (today's behavior). */
  crop?: { x: number; y: number; w: number; h: number };
  /** DESIGN SYSTEM STEP 2 — the one soft shadow (lib/decor/decorStyle.ts
   *  SHADOW). Only image icons and photos cast it (`shadowable`). */
  shadow?: boolean;
  /** Elements that form one piece (a polaroid = photo + frame). Their shadows
   *  are all drawn before the group's first element (`shadowPlan`). */
  group?: string;
  /** Photo elements: an alpha PNG (torn edge) stretched to the box. Editor =
   *  CSS mask-image; PDF = /SMask on the photo's JPEG. */
  maskUrl?: string;
  /** DESIGN SYSTEM STEP 3 — icons: `fill` stretches the piece to its box
   *  (torn paper scraps); default contain. */
  fit?: 'fill';
  /** A text HOLDER placed by a theme model (lib/decor/holders.ts): its role.
   *  Empty → not printed. */
  holder?: string;
  /** A label shape that belongs to the holder with this id: printed only when
   *  that holder has words. */
  labelFor?: string;
  /** DESIGN SYSTEM v2 — what the element is in its model (worker expand.ts
   *  ElementLayer): `photo` = a photo WINDOW (fixed box, the client swaps,
   *  pans and zooms the photo inside it); paper / frame / tape / object /
   *  label / mark / letter = the model's decoration (not movable); `holder` =
   *  a text holder. Absent = an element the client added herself. */
  layer?: string;
  /** A holder's photo (Légendes IA writes that photo's caption there). */
  holderFor?: string;
  /** A TINTED piece (lib/decor/tint.ts — 4-10 « Collage anniversaire »): its file is a
   *  colour map drawn in these four colours (deep, accent, second, light). Same matrix as the print. */
  tint?: readonly string[];
}

interface LayoutPage {
  page: number;
  layout: '1-up' | '2-up' | '3-up' | '4-up' | 'wide-top' | '3-up-flip' | '2-up-side' | '5-up' | '6-up' | '4-up-main' | '2-up-even';
  photos: string[];
  elements?: PageElement[];
  /** DESIGN SYSTEM STEP 2 — the page's paper: a colour and/or a full-page
   *  image cover-fitted to the BLEED box (decorStyle.backgroundBoxPct). Drawn
   *  under everything; the photo gaps show it. `previewUrl` is a lighter copy
   *  for the editor; the PDF always uses `imageUrl`. */
  background?: { imageUrl?: string | null; previewUrl?: string | null; color?: string | null; tint?: readonly string[] | null };
  /** Per-slot pan position the customer set. Keyed by `s{slotIndex}`.
   *  Saved alongside the page so the PDF generator renders the exact
   *  framing the customer saw in the preview. */
  slot_positions?: Record<string, SlotPosition>;
  /** WIDE-TOP layout only — top band height as a fraction of page height
   *  (0..1). Computed at placement from the wide photo's intrinsic aspect
   *  ratio so the top slot's aspect ratio EXACTLY equals the photo's AR and
   *  the wide group photo cover-fits with ZERO crop (every face shown). The
   *  bottom companion slot takes the remaining `1 - top_h_frac` of height.
   *  This is the render-parity key: the editor, preview, and PDF all derive
   *  the two slot rectangles from this single value so the three pipelines
   *  stay visually identical. Persisted verbatim in the pages JSONB by
   *  `withSlotPositions` / `sessionsApi.upsert` (they serialize whole page
   *  objects, so arbitrary fields survive). */
  top_h_frac?: number;
  /** 1-up landscape cropped to FILL the page (composer, 2026-10-13) — soloFit.ts. */
  fill?: boolean;
  /** STEP 3 — built from a theme model (photos drawn as layers). */
  theme?: string;
  model?: string;
  /** Optional layout-template id. When set, the editor renders the
   *  template's background PNG on top of the photos and (in future
   *  phases) uses the template's slot rectangles instead of the
   *  layout-derived grid. Persisted via `album_sessions.pages`. */
  template_id?: string | null;

  /** BLANK PAGE (2026-07-05) — true = pure white divider page: no photo
   *  slots, PDF fills white; the customer can add text/photos/icons as
   *  free elements. Reversible (toggle back to the AI layout). Kept in the
   *  page array so page count / divisible-by-4 are unaffected. */
  blank?: boolean;
  /** BLANK PAGE colour (2026-07-06) — background fill for a blank page, like
   *  the cover's colour picker. Defaults to white when unset. Editor + PDF
   *  both read it. */
  blank_color?: string;
  /** « PAGE DE FIN » (coupons plan 2026-10-28, lib/outroPage.ts): 'outro' =
   *  this page draws the brand outro image (outro_page_2_url) full page in the
   *  editor, preview and PDF. Always paired with `blank: true` and no photos. */
  kind?: 'outro';
}

// ── Slot-position model ─────────────────────────────────────────────────────
// We used to store `{x, y}` as object-position percentages (0..100 with 50,50
// = centred). That model can crop an image larger than its slot but can't
// move an image smaller than its slot — at scale < 1 the image stayed pinned
// to the centre and dragging did nothing. Switched to a translate-based
// model: x, y are pan offsets in % of slot dimensions (0 = centred), scale
// is a multiplier on the cover-fit base. Together they let the customer
// move the image freely at any zoom.
//
// `_v: 2` marks the new format. Records without it are migrated on load —
// the formula maps legacy object-position to the visually equivalent
// translate offset at the saved scale. At scale ≤ 1 the legacy crop had no
// visible effect so the translate falls to 0.
type SlotPosition = {
  x: number;
  y: number;
  scale?: number;
  /** Clockwise rotation in degrees (default 0). Rotates the photo inside its
   *  slot; the cover scale auto-grows (slotRotation.ts) so the slot stays
   *  fully covered. Mirrored in pdfMirrorBox + the PDF for render parity. */
  rotation?: number;
  /** LOCK SIZE (2026-07-07) — freezes the photo's zoom (`scale`): resize
   *  handles, pinch and −/+ are disabled while crop-PAN (x/y drag) stays active,
   *  so the customer can reposition the crop inside the cell without the size
   *  drifting. Editor-only; the PDF reads scale/x/y/rotation and ignores it, so
   *  render parity is unaffected. Persists per-slot via withSlotPositions. */
  lockSize?: boolean;
  /** REAL CROP (2026-07-24) — the window of the ORIGINAL photo this slot
   *  shows, in normalized source coords (0..1: x/y = top-left, w/h = size).
   *  Non-destructive (the file is untouched; re-opening the crop tool shows
   *  the whole photo with this rect pre-placed) but SEMANTICALLY A REAL CUT:
   *  every downstream calculation treats the crop window AS the photo, so its
   *  cropped-away parts can NEVER reappear by zooming out or resizing the
   *  frame — the previous "compile the crop into scale/translate" approach
   *  did exactly that and read as "the crop was ignored". Pan/zoom (x/y/scale)
   *  apply ON TOP of the cropped source. Absent = whole photo. */
  crop?: { x: number; y: number; w: number; h: number };
  /** A crop size-locks the photo (it must stay exactly as cropped). This flag
   *  is the customer's explicit "unlock anyway" — set by the padlock button —
   *  so zoom/resize come back on a cropped photo. Reset on every new crop. */
  cropUnlocked?: boolean;
  /** Format marker. v2 = translate-based; missing/older = legacy object-position. */
  _v?: 2;
};

/** Effective source dimensions for a slot: the CROP WINDOW when one is set,
 *  else the full image. Every cover-fit / pan-clamp / PDF-mirror calculation
 *  must use these so the crop behaves like a real cut. */
export function croppedDims(
  imgWidth: number | null,
  imgHeight: number | null,
  crop?: { x: number; y: number; w: number; h: number } | null,
): { w: number | null; h: number | null } {
  if (!imgWidth || !imgHeight) return { w: imgWidth, h: imgHeight };
  if (!crop || !(crop.w > 0) || !(crop.h > 0)) return { w: imgWidth, h: imgHeight };
  return { w: imgWidth * crop.w, h: imgHeight * crop.h };
}

/** Inner-<img> geometry (in % of its crop-window wrapper) that makes the crop
 *  region exactly fill the wrapper. The wrapper is positioned/sized by
 *  pdfMirrorBox(croppedDims…) and clips (overflow:hidden), so the cropped-away
 *  pixels are physically outside it — they can't be revealed by any zoom/pan/
 *  resize. No crop → the image simply fills the wrapper (identical to before).
 *  Mirrors the PDF's oversize-and-clip draw in pdfGenerator (render parity). */
export function cropImgStyle(
  crop?: { x: number; y: number; w: number; h: number } | null,
): { left: string; top: string; width: string; height: string } {
  if (!crop || !(crop.w > 0) || !(crop.h > 0)) {
    return { left: '0%', top: '0%', width: '100%', height: '100%' };
  }
  return {
    left: `${(-crop.x / crop.w) * 100}%`,
    top: `${(-crop.y / crop.h) * 100}%`,
    width: `${(1 / crop.w) * 100}%`,
    height: `${(1 / crop.h) * 100}%`,
  };
}

export type SlotPositionMap = Record<string, SlotPosition>;

function migrateSlotPosition(p: SlotPosition | undefined | null): SlotPosition | undefined {
  if (!p) return undefined;
  if (p._v === 2) return p;
  // Legacy object-position model. The visual effect of an off-centre crop
  // only kicks in when the image overflows the slot (scale > 1) — at
  // scale ≤ 1 the cropped image fits the slot exactly regardless of the
  // legacy (x, y), so the equivalent translate is (0, 0).
  const scale = p.scale ?? 1;
  const overflow = Math.max(0, scale - 1);
  return {
    x: (50 - (p.x ?? 50)) * overflow,
    y: (50 - (p.y ?? 50)) * overflow,
    scale,
    rotation: p.rotation, // preserve any rotation (legacy rows have none)
    _v: 2,
  };
}

export function withSlotPositions(pages: LayoutPage[], positions: SlotPositionMap): LayoutPage[] {
  return pages.map((p) => {
    const perPage: SlotPositionMap = {};
    for (let si = 0; si < p.photos.length; si++) {
      const pos = positions[`p${p.page}-s${si}`];
      // PERSISTENCE FIX (2026-07-05): a slot present in the positions map has
      // been TOUCHED (the customer panned it, or it was hydrated from a prior
      // save / AI face-centering). Persist it verbatim. The old code only saved
      // when x≠0/y≠0/scale≠1, so dragging a photo TO centre (0,0,1) — including
      // via the centre snap-guide — wrote nothing and the photo reverted to its
      // AI framing on the next load. Presence-in-map is the correct signal.
      if (!pos) continue;
      // Always serialise under v2 so the next load skips migration.
      perPage[`s${si}`] = { ...pos, _v: 2 };
    }
    return Object.keys(perPage).length > 0 ? { ...p, slot_positions: perPage } : p;
  });
}

function hydrateSlotPositions(pages: LayoutPage[]): SlotPositionMap {
  const out: SlotPositionMap = {};
  for (const p of pages) {
    if (!p.slot_positions) continue;
    for (const [slotKey, pos] of Object.entries(p.slot_positions)) {
      const migrated = migrateSlotPosition(pos);
      if (migrated) out[`p${p.page}-${slotKey}`] = migrated;
    }
  }
  return out;
}

// ── Face-aware AI placement helpers ─────────────────────────────────────────
// After the AI returns a layout plan, we compute per-slot translate offsets
// that put each photo's largest detected face at the center of its slot.
// The translate values are persisted into `slot_positions`, so both the
// editor preview and the PDF generator render with the same face-centered
// framing (the PDF honours customer-saved positions verbatim — see
// `pdfGenerator.ts` :1196). Without this, a portrait photo dropped into a
// landscape cell could clip the face entirely at the cell edge — exactly
// the regression the customer reported ("the dress shows but no face").
//
// Slot aspect ratios are derived from `getSlots` in pdfGenerator.ts at the
// finished interior trim — 200×297mm (the 200mm board width; NOT A4's 210) —
// with the 2.5mm gutter. Off by a percent or two vs the editor's exact CSS
// dimensions, but the face-centering math is tolerant — we just need to know
// "image taller or wider than slot". Half-cells = (200-2.5)/2 = 98.75.
export const SLOT_AR_BY_LAYOUT: Record<Exclude<LayoutPage['layout'], 'wide-top'>, number[]> = {
  '1-up': [200 / 297],
  '2-up': [200 / 147.25, 200 / 147.25],
  '3-up': [200 / 176, 98.75 / 116, 98.75 / 116],
  '4-up': [98.75 / 147.25, 98.75 / 147.25, 98.75 / 147.25, 98.75 / 147.25],
  // 3-up-flip: 2 portraits (top, half-width) + 1 wide landscape (bottom), equal
  // 50/50 rows. Portraits AR = 4-up cell (0.67); wide AR = 2-up cell (1.35).
  '3-up-flip': [98.75 / 147.25, 98.75 / 147.25, 200 / 147.25],
  // 2-up-side (base rules): two full-height portraits side by side.
  '2-up-side': [98.75 / 297, 98.75 / 297],
  // 5-up / 6-up (base rules) — edge-to-edge approximations; base-rules albums
  // read their exact framed ratios from photoGap.slotArs.
  '5-up': [200 / 148.5, 98.75 / 74.25, 98.75 / 74.25, 98.75 / 74.25, 98.75 / 74.25],
  '6-up': [98.75 / 99, 98.75 / 99, 98.75 / 99, 98.75 / 99, 98.75 / 99, 98.75 / 99],
  '4-up-main': [200 / 172, 65 / 125, 65 / 125, 65 / 125],
  // 2-up-even (base rules): the real ratios come from photoGap.slotArs with top_h_frac.
  '2-up-even': [173 / 97, 173 / 97],
};

// ── WIDE-TOP layout (wide group photos: show ALL faces, no crop, no gaps) ────
// A landscape group photo (AR ≥ 1.4, face_count ≥ 3) forced into a tall 1-up
// gets cover-fit and clips the outermost faces. WIDE-TOP instead gives it a
// full-width TOP BAND whose height = pageWidth / photoAR, so the photo fills
// it EXACTLY at cover-scale 1 → zero crop. A COMPANION photo fills the
// remaining bottom band (cover-fit; cropping the companion is fine). The two
// slots are stacked full-width, so the page has no empty/cream gaps.
//
// Page is the finished interior trim: 200 × 297 mm. The top band height is
// clamped to [MIN_TOP_MM, MAX_TOP_MM] so the companion stays a usable size for
// extreme aspect ratios. For normal landscape ARs (1.3–2.0) topHmm = 200/AR
// lands in a sane range → NO clamping. `top_h_frac = topHmm / 297`.
export const WIDE_TOP_PAGE_W_MM = 200;
export const WIDE_TOP_PAGE_H_MM = 297;
const WIDE_TOP_MIN_MM = 70;
const WIDE_TOP_MAX_MM = 200;
/** Intrinsic aspect-ratio threshold (w/h) above which a face_count≥3 photo is
 *  considered "wide" enough to warrant a WIDE-TOP page instead of 1-up. */
export const WIDE_TOP_AR_MIN = 1.4;

/** Compute the top-band height fraction for a WIDE-TOP page from the wide
 *  photo's intrinsic aspect ratio. Returns top_h_frac ∈ (0,1). The top slot's
 *  aspect ratio will equal `pageW / topHmm`, which equals `photoAR` whenever
 *  topHmm is unclamped (the common case) → cover-fit translate ≈ 0, zero crop. */
export function computeWideTopFrac(photoAR: number): number {
  const topHmm = Math.max(
    WIDE_TOP_MIN_MM,
    Math.min(WIDE_TOP_MAX_MM, WIDE_TOP_PAGE_W_MM / photoAR),
  );
  return topHmm / WIDE_TOP_PAGE_H_MM;
}

/** The two slot aspect ratios (W/H) for a WIDE-TOP page given `top_h_frac`.
 *  Both slots are full page width. Used by the editor's getSlots branch +
 *  computeAiSlotPositions. Mirrors the PDF/preview wide-top geometry. */
export function wideTopSlotARs(topHFrac: number): [number, number] {
  const f = topHFrac > 0 && topHFrac < 1 ? topHFrac : 0.5;
  const topAR = WIDE_TOP_PAGE_W_MM / (f * WIDE_TOP_PAGE_H_MM);
  const botAR = WIDE_TOP_PAGE_W_MM / ((1 - f) * WIDE_TOP_PAGE_H_MM);
  return [topAR, botAR];
}

function largestFace(faceData: unknown): { x: number; y: number; width: number; height: number } | null {
  if (!Array.isArray(faceData) || faceData.length === 0) return null;
  const faces = faceData as { x: number; y: number; width: number; height: number }[];
  return faces.reduce((a, b) => (a.width * a.height >= b.width * b.height ? a : b));
}

/** Translate offset (in % of slot dimensions) chosen so the photo's
 *  largest face is fully inside the slot with a safe margin — but NEVER
 *  shifts past the point where it would crop the subject's body on the
 *  opposite side. The previous version always pulled the face toward dead
 *  centre; for full-body shots (face at top, body below) that shoved the
 *  image down and cut the legs off, which the customer reported as
 *  "subject cropped".
 *
 *  Algorithm: compute where the face lands at scale=1 cover-fit with no
 *  translate. If it's already inside the safe band [FACE_PAD .. 100-FACE_PAD]
 *  of the slot, return 0 — the natural cover-fit is fine, do not disturb
 *  the body framing. Only translate when the face would actually clip,
 *  and only by the minimum needed to bring it inside the safe band. The
 *  clamp to overflow bounds keeps the slot from showing empty edges. */
// A3 — face-protection check. Mirrors computeFaceCenterTranslate's math
// exactly so the answer is "given the translate we'd actually apply, would
// the largest face still end up outside the [FACE_PAD, 100-FACE_PAD] safe
// band of the slot?". When true, the caller should escalate the photo to
// a larger slot (1-up). When false, the translate is enough.
//
// Why a separate function vs reading the translate from
// computeFaceCenterTranslate: we want the *clamping* signal. The translate
// alone is meaningless without comparing it to `maxAbs`. Inlining the math
// keeps the comparison explicit and side-effect-free.
function wouldFaceBeClipped(
  photo: ScorablePhoto,
  slotAR: number,
): boolean {
  const face = largestFace(photo.face_data);
  if (!face) return false;
  const imgW = photo.width ?? 0;
  const imgH = photo.height ?? 0;
  if (imgW <= 0 || imgH <= 0) return false;
  const cxPct = ((face.x + face.width / 2) / imgW) * 100;
  const cyPct = ((face.y + face.height / 2) / imgH) * 100;
  const imgAR = imgW / imgH;
  const eps = 0.01;
  const FACE_PAD = 18;
  if (imgAR < slotAR - eps) {
    // Image taller; cover-fit overflows vertically.
    const shOverSlotH = slotAR / imgAR;
    const maxAbs = ((shOverSlotH - 1) / 2) * 100;
    const imgTopPct = (1 - shOverSlotH) / 2 * 100;
    const faceY = imgTopPct + cyPct * shOverSlotH;
    let desired = 0;
    if (faceY < FACE_PAD) desired = FACE_PAD - faceY;
    else if (faceY > 100 - FACE_PAD) desired = (100 - FACE_PAD) - faceY;
    const actualTY = Math.max(-maxAbs, Math.min(maxAbs, desired));
    const finalFaceY = faceY + actualTY;
    return finalFaceY < FACE_PAD || finalFaceY > 100 - FACE_PAD;
  }
  if (imgAR > slotAR + eps) {
    // Image wider; cover-fit overflows horizontally.
    const swOverSlotW = imgAR / slotAR;
    const maxAbs = ((swOverSlotW - 1) / 2) * 100;
    const imgLeftPct = (1 - swOverSlotW) / 2 * 100;
    const faceX = imgLeftPct + cxPct * swOverSlotW;
    let desired = 0;
    if (faceX < FACE_PAD) desired = FACE_PAD - faceX;
    else if (faceX > 100 - FACE_PAD) desired = (100 - FACE_PAD) - faceX;
    const actualTX = Math.max(-maxAbs, Math.min(maxAbs, desired));
    const finalFaceX = faceX + actualTX;
    return finalFaceX < FACE_PAD || finalFaceX > 100 - FACE_PAD;
  }
  // imgAR ≈ slotAR: no overflow, no translate possible. Face stays put.
  return cxPct < FACE_PAD || cxPct > 100 - FACE_PAD
      || cyPct < FACE_PAD || cyPct > 100 - FACE_PAD;
}

function computeFaceCenterTranslate(
  photo: { width: number | null; height: number | null; face_data: unknown; aspect_ratio?: 'landscape' | 'portrait' | 'square' | null },
  slotAR: number,
): { x: number; y: number } | null {
  const face = largestFace(photo.face_data);
  if (!face) return null;
  const imgW = photo.width ?? 0;
  const imgH = photo.height ?? 0;
  if (imgW <= 0 || imgH <= 0) return null;
  const cxPct = ((face.x + face.width / 2) / imgW) * 100;
  const cyPct = ((face.y + face.height / 2) / imgH) * 100;
  const imgAR = imgW / imgH;
  const eps = 0.01;
  // Safety margin in % of slot dimension. Was 12 — too tight; faces still
  // landed close to cell borders in the customer's review. 18 keeps a
  // visible cushion (≈ 9% on each side) which matches what a human
  // designer would leave when composing portraits.
  const FACE_PAD = 18;
  let translateX = 0;
  let translateY = 0;
  if (imgAR < slotAR - eps) {
    // Image taller than slot — vertical overflow exists.
    const shOverSlotH = slotAR / imgAR;
    const maxAbs = ((shOverSlotH - 1) / 2) * 100;
    // Where the face centroid lands at scale=1, no translate. Cover-fit
    // centres the image: top edge of image sits at (1 - shOverSlotH)/2 of
    // slot height (negative when image overflows).
    const imgTopPct = (1 - shOverSlotH) / 2 * 100;
    const faceY = imgTopPct + cyPct * shOverSlotH;
    let desired = 0;
    if (faceY < FACE_PAD) {
      desired = FACE_PAD - faceY;       // shift down so face clears top margin
    } else if (faceY > 100 - FACE_PAD) {
      desired = (100 - FACE_PAD) - faceY; // shift up so face clears bottom margin
    }
    translateY = Math.max(-maxAbs, Math.min(maxAbs, desired));
  } else if (imgAR > slotAR + eps) {
    // Image wider than slot — horizontal overflow exists.
    const swOverSlotW = imgAR / slotAR;
    const maxAbs = ((swOverSlotW - 1) / 2) * 100;
    const imgLeftPct = (1 - swOverSlotW) / 2 * 100;
    const faceX = imgLeftPct + cxPct * swOverSlotW;
    let desired = 0;
    if (faceX < FACE_PAD) {
      desired = FACE_PAD - faceX;
    } else if (faceX > 100 - FACE_PAD) {
      desired = (100 - FACE_PAD) - faceX;
    }
    translateX = Math.max(-maxAbs, Math.min(maxAbs, desired));
  }
  return {
    x: Math.round(translateX * 100) / 100,
    y: Math.round(translateY * 100) / 100,
  };
}

function computeAiSlotPositions(
  pages: LayoutPage[],
  photos: { id: string; width: number | null; height: number | null; face_data: unknown }[],
): SlotPositionMap {
  const byId = new Map(photos.map((p) => [p.id, p]));
  const out: SlotPositionMap = {};
  for (const page of pages) {
    // Templated pages render via the template's own SVG/clip-path
    // pipeline; the grid `SLOT_AR_BY_LAYOUT` numbers don't apply, so any
    // p{N}-s{i} translates we'd compute here would be meaningless. Skip.
    if (page.template_id) continue;
    // WIDE-TOP: slot 0 is the wide photo's exact-fit band (AR == photoAR →
    // cover-fit translate ≈ 0, zero crop) and slot 1 the cover-fit companion.
    // Both ARs come from `top_h_frac` so they match the editor/preview/PDF.
    const ars =
      page.layout === 'wide-top'
        ? wideTopSlotARs(page.top_h_frac ?? computeWideTopFrac(WIDE_TOP_AR_MIN))
        : (SLOT_AR_BY_LAYOUT[page.layout] ?? SLOT_AR_BY_LAYOUT['1-up']);
    for (let si = 0; si < page.photos.length; si++) {
      const pid = page.photos[si];
      if (!pid) continue;
      const photo = byId.get(pid);
      if (!photo) continue;
      // RULE 1 (2026-06-09): a solo-landscape slot renders fit-width (full image,
      // letterbox) so the WHOLE photo is visible — there are no faces to bring
      // into frame, and a horizontal translate would only shove the letterbox
      // sideways (Problem A). Leave it centred (no entry → x=0, y=0).
      if (soloLandscapeFitWidth(page.layout, photo.width, photo.height, page.fill)) continue;
      const slotAR = ars[si] ?? ars[0];
      const t = computeFaceCenterTranslate(photo, slotAR);
      if (!t || (t.x === 0 && t.y === 0)) continue;
      out[`p${page.page}-s${si}`] = { x: t.x, y: t.y, scale: 1, _v: 2 };
    }
  }
  return out;
}

/** The aspect ratio (W/H) of slot `si` on a page, derived the SAME way as
 *  `computeAiSlotPositions` (layout + top_h_frac). Used to decide whether a
 *  customer's manual pan/scale — recorded against the slot AR it was edited in
 *  — is still geometrically valid after a re-layout. Returns `null` for
 *  templated pages (template clip-paths don't use the grid AR, so a recorded
 *  pan can't be re-applied meaningfully → fall back to the AI position).
 *  The AR is what governs cover-fit framing, so two slots with the same AR
 *  produce identical geometry → a recorded pan/scale stays visually correct. */
function slotARForPage(page: LayoutPage, slotIdx: number): number | null {
  if (page.template_id) return null;
  const ars =
    page.layout === 'wide-top'
      ? wideTopSlotARs(page.top_h_frac ?? computeWideTopFrac(WIDE_TOP_AR_MIN))
      : (SLOT_AR_BY_LAYOUT[page.layout] ?? SLOT_AR_BY_LAYOUT['1-up']);
  return ars[slotIdx] ?? ars[0] ?? null;
}

/** A customer's manual pan/scale edit, recorded BY PHOTO so it can follow the
 *  photo across an AI re-layout. `pos` is the slot_positions v2 entry; `layout`
 *  + `ar` capture the slot geometry at edit time so we only re-apply when the
 *  photo lands in a geometrically-compatible slot after regen. */
type ManualSlotEdit = {
  photoId: string;
  pos: SlotPosition;
  layout: LayoutPage['layout'];
  ar: number | null;
};
export type ManualSlotEditMap = Record<string, ManualSlotEdit>;

/** Score a photo as a hero candidate. Uses the metadata we already capture
 *  on upload — face count, largest face area, aspect ratio — so this runs
 *  in O(1) per photo with no extra inference. Higher = better hero.
 *
 *  Honest scope: this is NOT sharpness/emotion detection. It's "the metadata
 *  we have right now". A close-up portrait with a single large face scores
 *  near 80; a wide landscape with no face scores 0. The promotion pass uses
 *  this to flag clear winners, not to make subtle aesthetic judgments. */
function scoreHero(photo: {
  face_data: unknown;
  width: number | null;
  height: number | null;
  aspect_ratio: 'landscape' | 'portrait' | 'square' | null;
  vision_tags?: { hero_score?: number; subject?: string; background_quality?: string } | null;
}): number {
  // Vision tags (when present) are the source of truth — Gemini Flash saw
  // the actual pixels, so its 0..10 hero_score beats any face-geometry
  // heuristic. We rescale 0..10 to 0..85 so the two paths sit on the same
  // scale as the metadata-only fallback below.
  const v = photo.vision_tags;
  if (v && typeof v.hero_score === 'number') {
    let score = v.hero_score * 8.5; // 0..85
    if (v.background_quality === 'clean') score += 3;
    else if (v.background_quality === 'distracting') score -= 5;
    // Subject priority for family/baby albums: people are heroes, animals
    // / decor / scenery are not. The customer reported a sheep close-up
    // getting prime placement in a baby shower album — this multiplier
    // makes sure that doesn't happen even when Gemini gives the sheep a
    // moderate hero_score.
    const subject = v.subject ?? '';
    if (subject === 'baby' || subject === 'child') score += 20;
    else if (subject === 'group' || subject === 'adult') score += 10;
    else if (subject === 'pet' || subject === 'object' || subject === 'landscape') score -= 25;
    else if (subject === 'indoor') score -= 10;
    return Math.max(0, score);
  }
  // Fallback when no vision pass has run yet — use face geometry only.
  const faces = Array.isArray(photo.face_data) ? (photo.face_data as { x: number; y: number; width: number; height: number }[]) : [];
  if (faces.length === 0) return 0;
  const imgW = photo.width ?? 0;
  const imgH = photo.height ?? 0;
  if (imgW <= 0 || imgH <= 0) return 0;
  const imgArea = imgW * imgH;
  const largestFaceArea = faces.reduce((m, f) => Math.max(m, f.width * f.height), 0);
  const faceAreaPct = (largestFaceArea / imgArea) * 100;
  let score = 0;
  // Face area dominates — a face filling 20%+ of the frame is unmistakably
  // hero-grade; we cap the bonus at 30% to avoid scoring blown-up selfies
  // higher than a well-framed portrait.
  score += Math.min(faceAreaPct, 30) * 2; // up to 60
  // Multiple faces hint at a group moment (still good), but not as strong
  // as a single big face.
  score += Math.min(faces.length, 3) * 5; // up to 15
  // Portraits and squares frame heroes better than landscapes.
  if (photo.aspect_ratio === 'portrait' || photo.aspect_ratio === 'square') score += 10;
  return score;
}

export type ScorablePhoto = {
  face_data: unknown;
  width: number | null;
  height: number | null;
  aspect_ratio: 'landscape' | 'portrait' | 'square' | null;
  vision_tags?: { hero_score?: number; subject?: string; background_quality?: string; has_date_stamp?: boolean } | null;
};

/** Pull hero-class photos out of multi-slot pages and dedicate the page to
 *  them, demoting the others to unassigned so the existing redistribution
 *  loop places them elsewhere. A "hero" is a photo whose score clears
 *  HERO_MIN AND clears the page's runner-up by HERO_MARGIN — both gates
 *  prevent random photos from hijacking 1-up pages just because they have
 *  a face.
 *
 *  Also strips throwaway photos (vision hero_score ≤ 3) out of every page
 *  before promotion. The customer marked these as the bad photos they
 *  wanted gone — juice boxes, distant supermarket shots, empty playgrounds.
 *  They go to `unassigned` and will NOT be re-placed by the slot-fill loop
 *  if the album already has enough strong photos to fill its pages. */
function promoteHeroes(
  pages: LayoutPage[],
  unassigned: string[],
  photoById: Map<string, ScorablePhoto>,
): { pages: LayoutPage[]; unassigned: string[] } {
  // Tuned down from 65/20 because the album was looking like a flat 4-up
  // grid everywhere — almost no photos cleared the bar. With vision tags
  // a clear baby/child portrait now lands around 75–90 (8.5×hero_score +
  // 20 subject boost), so 50/12 captures real heroes without false
  // positives from generic adult/group shots.
  const HERO_MIN = 50;
  const HERO_MARGIN = 12;
  const out = pages.map((p) => ({ ...p, photos: [...p.photos] }));
  const remaining = [...unassigned];

  // Hero promotion: a multi-photo page where the top scorer dominates
  // gets converted to 1-up with the hero; the other photos go to
  // `remaining` to be re-placed by the slot-fill loop downstream.
  //
  // NOTE: this function used to also strip "throwaway" photos (vision
  // hero_score ≤ 3) from every page. That was the cause of the regen
  // regression — after the user clicked Régénérer IA, ~14 perfectly
  // valid photos disappeared into the unplaced sidebar. The throwaway
  // tag is unreliable enough (a sleeping baby with eyes closed got
  // hero_score 3 in one case) that auto-ejecting is too risky.
  // Throwaways now just SCORE LOW (via subject penalty in scoreHero) so
  // they sink to support cells rather than vanish.
  for (const page of out) {
    if (page.photos.length < 2) continue;
    const scored = page.photos
      .map((pid) => ({ pid, score: photoById.has(pid) ? scoreHero(photoById.get(pid)!) : 0 }))
      .sort((a, b) => b.score - a.score);
    const top = scored[0];
    const runner = scored[1];
    if (top.score >= HERO_MIN && top.score - runner.score >= HERO_MARGIN) {
      page.layout = '1-up';
      page.photos = [top.pid];
      for (let i = 1; i < scored.length; i++) remaining.push(scored[i].pid);
    }
  }
  return { pages: out, unassigned: remaining };
}

/** Drop the per-page layout down to match the actual photo count. AI
 *  occasionally returns a 4-up with 2 photos AND zero leftover unassigned
 *  — the slot-fill pass then has nothing to add, so the page renders as
 *  4-up with 2 empty cells (the "80% empty page with two tiny photos in
 *  the corners" the customer reported). Snapping the layout to match the
 *  photo count fills the page properly with the same photos. */
export function snapLayoutToPhotoCount(pages: LayoutPage[]): LayoutPage[] {
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
  return pages.map((p) => {
    // Templated pages have a fixed slot count from their template — never snap.
    if (p.template_id) return p;
    const filled = p.photos.filter(Boolean).length;
    if (filled === 0) return p;
    // WIDE-TOP is a deliberate 2-slot layout (wide photo + companion). Keep it
    // ONLY while it actually holds 2 photos — never snap a full 2-photo wide-top
    // to '2-up' (that would re-split the wide photo and re-introduce the
    // face-clipping it exists to prevent). But a wide-top that has lost its
    // companion (1 photo) MUST downgrade to a full-page 1-up, or slot 1 renders
    // as an empty "Toucher pour ajouter" blank zone (rev 2026-06-08, order 1074).
    if (p.layout === 'wide-top') {
      return filled >= 2 ? p : { ...p, layout: '1-up' };
    }
    // 3-UP-FLIP (2 portraits + 1 wide) is a deliberate 3-slot choice from the
    // aspect-enforcement pass. Keep it while full (3 photos); if it lost photos,
    // downgrade by count so we never render an empty slot.
    if (p.layout === '3-up-flip') {
      return filled >= 3 ? p : { ...p, layout: countToLayout(filled) };
    }
    const desired = countToLayout(filled);
    return desired === p.layout ? p : { ...p, layout: desired };
  });
}

/** A1 — final no-blank-page sweep. Drops pages whose photo array is empty
 *  (AI sometimes emits these; some pass edge cases can produce them too)
 *  and re-issues page numbers contiguously so downstream slot keys and
 *  the safety net never collide. Run AT THE END of the pipeline so all
 *  upstream passes still see the original page-number arithmetic. */
function compactPages(pages: LayoutPage[]): LayoutPage[] {
  return pages
    // Keep any page that carries real intent: photos, a customer blank-divider
    // flag, or free elements (text/photo/icons) — only truly-empty AI residue
    // is dropped. (2026-07-05: blank pages + element-only pages preserved.)
    .filter((p) => p.blank || (p.elements?.length ?? 0) > 0 || p.photos.some((pid) => !!pid))
    .map((p, idx) => ({ ...p, page: idx + 1 }));
}

/** Slot-size rank: how prominent a slot is. Higher = more attention.
 *  Used by the priority-swap pass below so a pet/object never ends up
 *  on a 1-up while a close-up baby is buried in a 4-up corner. */
function slotRank(layout: LayoutPage['layout'], slotIndex: number): number {
  if (layout === '1-up') return 4; // full page
  // WIDE-TOP: slot 0 is the feature wide band (rank 4, equal to a 1-up — it
  // is the reason the page exists); slot 1 is the companion (rank 3). This
  // keeps the wide group photo as a hero and stops the priority-swap pass
  // from demoting it.
  if (layout === 'wide-top') return slotIndex === 0 ? 4 : 3;
  if (layout === '2-up') return 3; // half-page slot
  if (layout === '3-up') return slotIndex === 0 ? 3 : 2; // top wide / bottom narrow
  // 3-up-flip: the wide landscape (slot 2, bottom) is the feature → rank 3; the
  // two top portraits → rank 2 (mirror of 3-up).
  if (layout === '3-up-flip') return slotIndex === 2 ? 3 : 2;
  return 1; // 4-up = small grid cell
}

/** How prominent a slot a photo deserves. Category-aware (A4):
 *  - PET album: pets ARE the heroes (subject="pet" → rank 4 when hero_score ≥ 7).
 *  - TRAVEL album: landscapes can hero when hero_score ≥ 8.
 *  - HUMAN-SUBJECT albums (baby/wedding/birthday/graduation/family/religious):
 *    pet/object/landscape/indoor → rank 1 only; baby/child/group photos float up.
 *  Multi-face group photos (≥3 detected faces) MUST be on a 1-up (rank 4)
 *  regardless of category — geometric truth, not a tuning parameter. */
function preferredMinRank(
  photo: ScorablePhoto | undefined,
  category: AlbumCategory | null,
): number {
  if (!photo) return 1;
  const v = photo.vision_tags;
  const subject = v?.subject ?? '';
  const heroScore = v?.hero_score ?? 0;

  // Category-specific subject rules. PET inverts the usual pet penalty.
  if (category === 'pet') {
    if (subject === 'pet') {
      if (heroScore >= 7) return 4;
      if (heroScore >= 5) return 3;
      return 2;
    }
    if (subject === 'object' || subject === 'indoor') return 1;
    // Humans + landscapes fall through to the face-based rules below.
  } else {
    // All non-pet categories: pet/object never hero.
    if (subject === 'pet' || subject === 'object') return 1;
    if (subject === 'landscape') {
      // TRAVEL allows strong landscapes to hero. Other categories cap at rank 1.
      if (category === 'travel' && heroScore >= 8) return 3;
      return 1;
    }
    if (subject === 'indoor') return 1;
  }

  const faces = Array.isArray(photo.face_data)
    ? (photo.face_data as { x: number; y: number; width: number; height: number }[])
    : [];
  const imgArea = (photo.width ?? 1) * (photo.height ?? 1);
  const largestFaceArea = faces.reduce((m, f) => Math.max(m, f.width * f.height), 0);
  const faceAreaPct = imgArea > 0 ? (largestFaceArea / imgArea) * 100 : 0;
  const faceCount = faces.length;
  // Multi-face hard rule: ≥3 detected faces → must be 1-up. Cover-fitting
  // a group photo into a grid cell always clips a face at the edge; no
  // translate can rescue it. The fallback pass downstream creates a new
  // 1-up page when no swap partner is available.
  if (faceCount >= 3) return 4;
  if (subject === 'baby' || subject === 'child') {
    if (faceAreaPct >= 12 && heroScore >= 7) return 3;
    return 2;
  }
  if (subject === 'group' || subject === 'adult') {
    return faceCount >= 2 ? 3 : 2;
  }
  return 1;
}

/** Force-promote multi-face / close-up portraits that the priority swap
 *  couldn't fit into a big-enough slot. For each photo still placed in a
 *  slot below rank 4 when its preferred minimum is 4, we INSERT a new
 *  1-up page immediately after the current spread containing just that
 *  photo, and downgrade the donor page's layout to match its now-smaller
 *  photo list. This guarantees every multi-face group photo lands on its
 *  own page — at the cost of growing page count, which the customer
 *  explicitly approved ("we price per page, goal is quality").
 *
 *  Page count + photo count: no photos are lost. Some photos that were
 *  in the donor's slot may need to be re-placed if the layout downgrade
 *  drops them — they go to `remaining` so the slot-fill loop catches
 *  them (caller must run slot-fill again after this pass). */
function forceSoloForMultiFace(
  pages: LayoutPage[],
  photoById: Map<string, ScorablePhoto>,
  category: AlbumCategory | null,
  /** Max NEW 1-up pages this pass may add. It used to be unbounded, which is
   *  how #D325 grew from a 76-page target to 179 pages — past the album cap.
   *  A face that would be cropped is still worth its own page, but not at any
   *  price: once the budget is spent the photo stays in its grid cell. */
  soloBudget = Number.POSITIVE_INFINITY,
): { pages: LayoutPage[] } {
  let budget = soloBudget;
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
  const result: LayoutPage[] = [];
  let pageCursor = 1;
  for (const page of pages) {
    // Find any photo on this page that REQUIRES rank-4 (1-up) but is in
    // a non-1-up slot. A3 extension: ALSO catch single-face photos whose
    // face would still be cropped after the maximum translate the slot's
    // overflow can produce. In both cases the photo gets its own new 1-up
    // page (rank 4) so no face ever ends up on a cell edge.
    // WIDE-TOP pages are already the ideal home for a wide multi-face photo
    // (slot 0 shows every face at zero crop), so they bypass force-solo just
    // like a 1-up — pushing through would wrongly tear the photo onto a new
    // page and discard the companion arrangement.
    if (page.layout === 'wide-top') {
      result.push({ ...page, page: pageCursor++ });
      continue;
    }
    const slotARs = SLOT_AR_BY_LAYOUT[page.layout] ?? SLOT_AR_BY_LAYOUT['1-up'];
    const needsSolo: string[] = [];
    const keepers: string[] = [];
    for (let si = 0; si < page.photos.length; si++) {
      const pid = page.photos[si];
      if (!pid) continue;
      const meta = photoById.get(pid);
      const pref = preferredMinRank(meta, category);
      const slotAR = slotARs[si] ?? slotARs[0];
      const clipsAfterTranslate = meta ? wouldFaceBeClipped(meta, slotAR) : false;
      // Budget check happens HERE, not at insert time: a photo we can't afford
      // to promote must stay a keeper, so it keeps its slot instead of being
      // silently dropped from the album.
      if ((pref >= 4 || clipsAfterTranslate) && page.layout !== '1-up' && budget > 0) {
        needsSolo.push(pid);
        budget--;
      } else {
        keepers.push(pid);
      }
    }
    if (needsSolo.length === 0 || page.layout === '1-up') {
      result.push({ ...page, page: pageCursor++ });
      continue;
    }
    // The donor page keeps `keepers` (may be empty), gets a downgraded
    // layout. Each needs-solo photo gets its own new 1-up page inserted
    // after the donor.
    if (keepers.length > 0) {
      result.push({
        ...page,
        page: pageCursor++,
        photos: keepers,
        layout: countToLayout(keepers.length),
      });
    } // else: donor page is dropped entirely
    for (const soloPid of needsSolo) {
      result.push({ page: pageCursor++, layout: '1-up', photos: [soloPid] });
    }
  }
  return { pages: result };
}

/** Intrinsic aspect ratio (w/h) of a photo from its DB dimensions — the SAME
 *  source the renderer trusts for slot fit. Returns 0 when dims are missing. */
function photoAspectRatio(meta: ScorablePhoto | undefined): number {
  const w = meta?.width ?? 0;
  const h = meta?.height ?? 0;
  return w > 0 && h > 0 ? w / h : 0;
}

/** Face count from the live `face_data` array (NOT a stale meta map — see the
 *  layout-engine skill: the two can desync after a re-detect). */
function faceCountOf(meta: ScorablePhoto | undefined): number {
  return Array.isArray(meta?.face_data) ? (meta!.face_data as unknown[]).length : 0;
}

/** True when a photo is BOTH landscape (AR ≥ WIDE_TOP_AR_MIN) AND a group
 *  shot (face_count ≥ 3). These are the photos a tall 1-up would crop on the
 *  left/right, losing the outermost faces — WIDE-TOP is built for them.
 *  PORTRAIT face_count≥3 photos return false → they keep their 1-up path. */
function isWideGroupPhoto(meta: ScorablePhoto | undefined): boolean {
  return photoAspectRatio(meta) >= WIDE_TOP_AR_MIN && faceCountOf(meta) >= 3;
}

/** WIDE-TOP promotion pass. Pulls every LANDSCAPE face_count≥3 photo out of
 *  its current slot and gives it a dedicated WIDE-TOP page: a full-width top
 *  band sized to the photo's exact aspect ratio (zero crop, every face shown)
 *  plus a full-width companion below. The companion is taken from `remaining`
 *  (the unplaced pool); if `remaining` is empty (e.g. the wide photo was the
 *  last unplaced one) we FALL BACK to a plain 1-up page — an acceptable rare
 *  edge that keeps the no-drop contract intact.
 *
 *  Any photos displaced from a donor page (the wide photo's former
 *  page-mates) are re-pushed to `remaining` so the caller's slot-fill / safety
 *  net re-places them — nothing is ever dropped. PORTRAIT group photos are
 *  left untouched (they still get 1-up via forceSoloForMultiFace downstream).
 *
 *  Runs BEFORE forceSoloForMultiFace so that pass sees the finished wide-top
 *  page and skips it (a wide-top is the ideal home for a wide group photo). */
export function applyWideTopForWideGroups(
  pages: LayoutPage[],
  remaining: string[],
  photoById: Map<string, ScorablePhoto>,
): { pages: LayoutPage[]; remaining: string[] } {
  const rem = [...remaining];
  const out: LayoutPage[] = [];
  let pageCursor = 1;
  for (const page of pages) {
    // Already-wide-top pages and templated pages pass through verbatim.
    if (page.layout === 'wide-top' || page.template_id) {
      out.push({ ...page, page: pageCursor++ });
      continue;
    }
    // Find the FIRST landscape photo on this page that its current cell would
    // CROP (one wide-top page per donor keeps the rebuild simple; a second wide
    // photo on the same page is caught on the next regen / by the safety net).
    // A landscape is promoted to a wide band when (rev 2026-06-08, S3):
    //   • it is a group shot (face_count ≥ 3) — always, as before; OR
    //   • it has face(s) AND its cell overflows ≥15% horizontally — a portrait
    //     1-up / 3-up / 4-up cell clips the edge faces. A 2-up cell (AR≈1.43)
    //     fits most landscapes, so photos already in 2-up are left in place; OR
    //   • it is ALONE on the page and its cell would crop it — a lone landscape
    //     gets a full-bleed band instead of a cropped 1-up.
    // Faceless landscapes that merely share a grid are left alone: cropping
    // background (not faces) is acceptable and avoids page-count inflation.
    const liveSlotARs = SLOT_AR_BY_LAYOUT[page.layout as Exclude<LayoutPage['layout'], 'wide-top'>] ?? [];
    const aloneOnPage = page.photos.filter(Boolean).length === 1;
    let wideIdx = -1;
    for (let si = 0; si < page.photos.length; si++) {
      const pid = page.photos[si];
      if (!pid) continue;
      const meta = photoById.get(pid);
      const ar = photoAspectRatio(meta);
      if (ar < WIDE_TOP_AR_MIN) continue; // not landscape enough
      const faces = faceCountOf(meta);
      const slotAR = liveSlotARs[si] ?? liveSlotARs[0] ?? 1;
      const cropsHard = slotAR > 0 ? ar / slotAR > 1.15 : true; // >15% horizontal overflow ⇒ left/right crop
      if (isWideGroupPhoto(meta) || (faces >= 1 && cropsHard) || (aloneOnPage && cropsHard)) {
        wideIdx = si; break;
      }
    }
    if (wideIdx === -1) {
      out.push({ ...page, page: pageCursor++ });
      continue;
    }
    const widePid = page.photos[wideIdx];
    // Everything else on the donor page goes back to `remaining`.
    const others = page.photos.filter((_, si) => si !== wideIdx).filter(Boolean) as string[];
    rem.push(...others);
    // Companion = next photo from the pool, if any.
    const companion = rem.length > 0 ? rem.shift()! : null;
    const photoAR = photoAspectRatio(photoById.get(widePid)) || WIDE_TOP_AR_MIN;
    if (!companion) {
      // A wide-top needs TWO photos. With no companion available, a lone wide
      // photo becomes a full-page 1-up — NEVER a single-photo wide-top, which
      // would render an empty "Toucher pour ajouter" bottom slot (the order-1074
      // blank-zone bug). The slot-count invariant (1 photo → 1 slot) holds; the
      // 1-up cover-fits the landscape. (rev 2026-06-08 — supersedes the earlier
      // "cream band when no companion" path.)
      out.push({ page: pageCursor++, layout: '1-up', photos: [widePid] });
      continue;
    }
    out.push({
      page: pageCursor++,
      layout: 'wide-top',
      photos: [widePid, companion],
      top_h_frac: computeWideTopFrac(photoAR),
    });
  }
  return { pages: out, remaining: rem };
}

/** Priority-swap pass — pet/object photos sink to small cells, close-up
 *  babies float up to big cells. Greedy: walk every slot, find any photo
 *  in a slot below its preferred rank, look for a swap partner in a
 *  higher-rank slot whose own preference would be HAPPIER in this small
 *  slot. Swap if the trade improves both sides. Doesn't change page count
 *  or layout types — only photo assignments within existing slots. */
function applyPrioritySwapPass(
  pages: LayoutPage[],
  photoById: Map<string, ScorablePhoto>,
  category: AlbumCategory | null,
): LayoutPage[] {
  const result = pages.map((p) => ({ ...p, photos: [...p.photos] }));
  // Build flat list of (pageIdx, slotIdx, slotRank, photoId, photoPref).
  type Cell = { pageIdx: number; slotIdx: number; rank: number; pid: string; pref: number };
  const cells: Cell[] = [];
  result.forEach((page, pageIdx) => {
    page.photos.forEach((pid, slotIdx) => {
      if (!pid) return;
      const rank = slotRank(page.layout, slotIdx);
      const pref = preferredMinRank(photoById.get(pid), category);
      cells.push({ pageIdx, slotIdx, rank, pid, pref });
    });
  });

  // For each "underplaced" photo (rank < pref), try to find an
  // "overplaced" photo (rank > pref) whose preference would be met by
  // this cell's smaller rank. Swap them.
  for (const under of cells) {
    if (under.rank >= under.pref) continue; // already happy
    const partner = cells.find(
      (c) =>
        c !== under &&
        c.rank >= under.pref && // c's slot can hold under
        under.rank >= c.pref && // under's slot can hold c (downgrade for c is fine)
        c.pref < under.pref,    // c is overplaced relative to its preference
    );
    if (!partner) continue;
    // Swap photo IDs.
    result[under.pageIdx].photos[under.slotIdx] = partner.pid;
    result[partner.pageIdx].photos[partner.slotIdx] = under.pid;
    // Update the cells list so the next iteration sees the new state.
    const oldUnderPid = under.pid;
    under.pid = partner.pid;
    partner.pid = oldUnderPid;
    const oldUnderPref = under.pref;
    under.pref = partner.pref;
    partner.pref = oldUnderPref;
  }
  return result;
}

/** Quality gate — anti-monotone spread pass. After the AI returns its
 *  plan, this pass scans every spread (pair of pages) and:
 *
 *  - If BOTH pages are 2-up of unrelated photos (no shared burst), pick
 *    the highest-scored photo in the spread and promote it to its own
 *    1-up. The remaining photos shuffle down: the donor page becomes
 *    1-up with its other photo, and the orphaned photo from the donor
 *    moves to the facing page to keep it as 2-up.
 *
 *  - If a 4-up page contains photos that DON'T share a burst, downgrade
 *    it to 2-up + push the bottom-scored photos to `unassigned`. 4-up
 *    is only kept when it's a true burst grid.
 *
 *  The goal: every 3rd spread or so gets a clear hero, instead of the
 *  whole album reading as one long 2-up + 2-up grid. */
function applyAntiMonotonePass(
  pages: LayoutPage[],
  unassigned: string[],
  photoById: Map<string, ScorablePhoto>,
  /** When the album is packed (≈2.6+ photos per page), keep 4-up pages intact.
   *  Pass 2 (spread variety) still runs — it only promotes an existing hero and
   *  never adds pages, so the album stays varied without losing the density. */
  denseAlbum = false,
): { pages: LayoutPage[]; unassigned: string[] } {
  const result = pages.map((p) => ({ ...p, photos: [...p.photos] }));
  const remaining = [...unassigned];
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';

  // Pass 1: downgrade 4-up. The new prompt forbids 4-up except for
  // bursts, but we don't get burst data back from the worker response,
  // so we conservatively cap 4-up output — keep top-2 by score and push
  // the rest to unassigned. A real burst-of-4 loses 2 photos here; the
  // benefit (no flat contact-sheet grids) outweighs that edge case.
  for (const page of result) {
    if (denseAlbum) break;   // packed album — 4-up pages are the point, keep them
    if (page.layout !== '4-up' || page.photos.length !== 4) continue;
    const scored = page.photos
      .map((pid) => ({ pid, score: photoById.has(pid) ? scoreHero(photoById.get(pid)!) : 0 }))
      .sort((a, b) => b.score - a.score);
    const kept = scored.slice(0, 2).map((s) => s.pid);
    for (let i = 2; i < scored.length; i++) remaining.push(scored[i].pid);
    page.photos = kept;
    page.layout = '2-up';
  }

  // Pass 2: spread variety. For each spread of two 2-up pages, promote
  // the top-scored photo on the spread to its own 1-up so the customer's
  // eye has a hero to land on. The other photos shuffle into the facing
  // page (3-up max — anything bigger gets clamped). Only fires when the
  // top photo clears a meaningful score floor; otherwise we leave the
  // spread alone instead of inventing fake heroes from weak photos.
  for (let i = 0; i + 1 < result.length; i += 2) {
    const a = result[i];
    const b = result[i + 1];
    if (a.photos.length !== 2 || b.photos.length !== 2) continue;
    if (a.layout !== '2-up' || b.layout !== '2-up') continue;
    const ranked = [...a.photos, ...b.photos]
      .map((pid) => ({ pid, score: photoById.has(pid) ? scoreHero(photoById.get(pid)!) : 0 }))
      .sort((x, y) => y.score - x.score);
    if (ranked[0].score < 50) continue;
    const heroId = ranked[0].pid;
    const rest = ranked.slice(1).map((r) => r.pid);
    a.photos = [heroId];
    a.layout = '1-up';
    b.photos = rest;
    b.layout = countToLayout(rest.length);
  }
  return { pages: result, unassigned: remaining };
}

/** A2 — humanized flow / slow-start, slow-end pass. Real albums open and
 *  close gently: the first 2 spreads (= first 4 pages) and the last 2
 *  spreads should be 1-up or 2-up, never a 3-up or 4-up contact sheet.
 *  Photos ejected from a downgraded page go to `remaining` so the
 *  slot-fill loop catches them — nothing is dropped, just relocated to
 *  the middle of the album where dense grids fit the rhythm. */
function applySlowStartAndEndPass(
  pages: LayoutPage[],
  unassigned: string[],
): { pages: LayoutPage[]; unassigned: string[] } {
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
  const result = pages.map((p) => ({ ...p, photos: [...p.photos] }));
  const remaining = [...unassigned];
  // 2 spreads = 4 pages. Customer-visible: opening + closing of the book.
  const SLOW_PAGES = 4;
  const downgradeIfDense = (page: LayoutPage) => {
    if (page.layout !== '3-up' && page.layout !== '4-up') return;
    const keepCount = 2; // both 3-up and 4-up become 2-up at the cap
    const ejected = page.photos.slice(keepCount);
    page.photos = page.photos.slice(0, keepCount);
    page.layout = countToLayout(page.photos.length);
    remaining.push(...ejected);
  };
  // Slow opening
  for (let i = 0; i < Math.min(SLOW_PAGES, result.length); i++) {
    downgradeIfDense(result[i]);
  }
  // Slow ending. Guard against the same page being touched twice on very
  // short albums (e.g. 3-page album where start and end overlap).
  const endStart = Math.max(SLOW_PAGES, result.length - SLOW_PAGES);
  for (let i = endStart; i < result.length; i++) {
    downgradeIfDense(result[i]);
  }
  return { pages: result, unassigned: remaining };
}

/** Detect empty pages sitting next to a multi-photo neighbour in the same
 *  spread and rebalance by moving one photo across. Prevents the
 *  "hero on the left, empty page on the right" regression. Keeps page
 *  count + page ordering intact; only adjusts photos[] and layout per
 *  page. */
function balanceSpreadFills(pages: LayoutPage[]): LayoutPage[] {
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
  const result = pages.map((p) => ({ ...p, photos: [...p.photos] }));
  for (let i = 0; i + 1 < result.length; i += 2) {
    const a = result[i];
    const b = result[i + 1];
    // Never pull a photo OUT of a wide-top donor — shifting its wide photo or
    // companion away breaks the deliberate 2-slot composition. A wide-top with
    // an empty neighbour is left as-is (slot 1 already holds its companion).
    if (a.photos.length === 0 && b.photos.length >= 2 && b.layout !== 'wide-top') {
      a.photos.push(b.photos.shift()!);
      a.layout = '1-up';
      b.layout = countToLayout(b.photos.length);
    } else if (b.photos.length === 0 && a.photos.length >= 2 && a.layout !== 'wide-top') {
      b.photos.unshift(a.photos.pop()!);
      b.layout = '1-up';
      a.layout = countToLayout(a.photos.length);
    }
  }
  return result;
}

interface LayoutPlan {
  pages: LayoutPage[];
  unassigned_photos: string[];
}

interface PhotoWithUrl extends Photo {
  signed_url: string | null;
  /** Small (~320px) tier for the photo-LIST grids (desktop tray, slot chooser,
   *  mobile "Mes photos"). NOT used by canvas/preview/PDF — those keep
   *  signed_url (1400px) / original_signed_url (full). */
  tray_signed_url?: string | null;
  /** The full-resolution print file. What a rotation must turn (2026-09-26). */
  original_signed_url?: string | null;
}

/** Minimal shape `composeFinalLayout` needs out of each photo. Lets the
 *  shared pipeline run against any photo source (editor's `allPhotos`
 *  query, magic flow's `photosApi.list` response) without coupling to a
 *  specific component's state shape. */
export interface ComposablePhoto {
  id: string;
  face_data: unknown;
  width: number | null;
  height: number | null;
  aspect_ratio?: 'landscape' | 'portrait' | 'square' | null;
  vision_tags?: {
    hero_score?: number;
    subject?: string;
    background_quality?: string;
    has_date_stamp?: boolean;
  } | null;
}

/** Fix 2 Case B — never leave a landscape photo in a portrait slot.
 *
 *  Cover-fit crops a wide photo to a tall slot (it loses its left + right
 *  edges). This pass walks the FINAL pages and, for any page that holds a
 *  landscape, reorders the photos so landscapes land in WIDE slots, choosing
 *  H2 (3-up, wide top) or H3 (3-up-flip, wide bottom) for the common
 *  1-landscape / 2-portrait page and alternating the two for variety.
 *
 *  PER-PAGE ONLY: a pure in-page reorder + a swap between same-slot-count
 *  layouts (3-up ↔ 3-up-flip). It never moves a photo across pages, never adds
 *  or drops a page, and never drops a photo — so the "0 unplaced" guarantee and
 *  the album's page_count / pricing are untouched (storiz-layout-engine
 *  contract). Runs on the final page set, after the last snap and before slot
 *  positions are computed, so nothing downstream undoes the chosen layout. */
/**
 * Bring the composed album to EXACTLY `target` pages, or as close as the photos
 * physically allow.
 *
 * Too many pages → merge the least-full pages into a neighbour (a page holds at
 *   most 4), repeatedly, until we hit the target or no legal merge is left.
 * Too few pages  → split the fullest pages, then append empties.
 *
 * INVARIANT, enforced at the end: not one photo id may be added, dropped or
 * duplicated. If the result would violate that, the ORIGINAL list is returned —
 * a wrong page count is a annoyance, a lost photo is a ruined album.
 *
 * The floor is ceil(photos / 4): 298 photos can never fit in 70 pages. When the
 * target is below the floor we stop there and return the floor, which is the
 * honest answer.
 */
export function reconcileToTarget(pages: LayoutPage[], target: number): LayoutPage[] {
  const countToLayout = (n: number): LayoutPage['layout'] =>
    n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
  const before: string[] = [];
  for (const p of pages) for (const id of p.photos) if (id) before.push(id);

  // Templated and blank pages are structural — never merge or split them.
  const movable = (p: LayoutPage) => !p.template_id && !p.blank;
  let out = pages.map((p) => ({ ...p, photos: [...p.photos] }));

  // ── Too many pages: dissolve the emptiest page into pages that have room ──
  // NOT a pairwise merge of neighbours: an album of uniformly 3-photo pages has
  // no adjacent pair that fits in one page (3+3 > 4), so pairwise merging can't
  // compact it at all and silently returns the wrong count. Instead take the
  // LEAST-full page and redistribute its photos into whatever pages have spare
  // slots, nearest first so the reading order barely moves.
  let guard = out.length * 4 + 16;   // termination guard, never an early exit
  while (out.length > target && guard-- > 0) {
    let donorI = -1;
    let donorFill = Infinity;
    for (let i = 0; i < out.length; i++) {
      if (!movable(out[i])) continue;
      const fill = out[i].photos.filter(Boolean).length;
      if (fill < donorFill) { donorFill = fill; donorI = i; }
    }
    if (donorI === -1) break;

    const donor = out[donorI].photos.filter(Boolean);
    // Capacity elsewhere, ordered by distance from the donor so photos stay
    // near where they were.
    const targets = out
      .map((p, i) => ({ p, i }))
      .filter(({ p, i }) => i !== donorI && movable(p) && p.photos.filter(Boolean).length < 4)
      .sort((a, b) => Math.abs(a.i - donorI) - Math.abs(b.i - donorI));

    const capacity = targets.reduce((s, t) => s + (4 - t.p.photos.filter(Boolean).length), 0);
    if (capacity < donor.length) break;   // genuinely full — this is the floor

    let k = 0;
    for (const t of targets) {
      while (k < donor.length && t.p.photos.filter(Boolean).length < 4) {
        t.p.photos = [...t.p.photos.filter(Boolean), donor[k++]];
        t.p.layout = countToLayout(t.p.photos.length);
      }
      if (k >= donor.length) break;
    }
    out.splice(donorI, 1);
  }

  // ── Too few pages: split the fullest, then pad with empties ──────────────
  guard = target * 2 + 16;
  while (out.length < target && guard-- > 0) {
    let bestI = -1;
    let bestFill = 1;
    for (let i = 0; i < out.length; i++) {
      const p = out[i];
      if (!movable(p)) continue;
      const fill = p.photos.filter(Boolean).length;
      if (fill > bestFill) { bestFill = fill; bestI = i; }
    }
    if (bestI === -1) break;
    const ph = out[bestI].photos.filter(Boolean);
    const half = Math.ceil(ph.length / 2);
    const left = ph.slice(0, half), right = ph.slice(half);
    out.splice(bestI, 1,
      { ...out[bestI], photos: left, layout: countToLayout(left.length) },
      { ...out[bestI], photos: right, layout: countToLayout(right.length) },
    );
  }
  while (out.length < target) {
    out.push({ page: out.length + 1, layout: '1-up', photos: [] } as LayoutPage);
  }

  out = out.map((p, i) => ({ ...p, page: i + 1 }));

  // Hard invariant — same photos in, same photos out.
  const after: string[] = [];
  for (const p of out) for (const id of p.photos) if (id) after.push(id);
  if (after.length !== before.length || new Set(after).size !== new Set(before).size) {
    console.error('[reconcileToTarget] photo-set changed — keeping the original layout', {
      before: before.length, after: after.length,
    });
    return pages;
  }
  return out;
}

function enforceLandscapeSlots(
  pages: LayoutPage[],
  photoById: Map<string, ScorablePhoto>,
): LayoutPage[] {
  const isLand = (id: string): boolean => {
    const m = photoById.get(id);
    if (!m) return false;
    if (m.aspect_ratio === 'landscape') return true;
    if (m.aspect_ratio === 'portrait' || m.aspect_ratio === 'square') return false;
    // No stored aspect_ratio: fall back to raw dims (clearly wider than tall).
    return !!m.width && !!m.height && m.width > m.height * 1.15;
  };
  let flip = 0; // alternate H2 / H3 across qualifying pages for visual variety
  return pages.map((p) => {
    if (p.template_id) return p; // templated pages have fixed slots — never touch
    if (!p.photos.some((id) => !!id && isLand(id))) return p; // no landscape here
    const filled = p.photos.filter(Boolean).length;
    const nLand = p.photos.filter((id) => !!id && isLand(id)).length;
    // Only the 1-landscape / 2-portrait page alternates between H2 and H3.
    const preferH3 = filled === 3 && nLand === 1 ? (flip++ % 2 === 1) : false;
    const ars = SLOT_AR_BY_LAYOUT[p.layout as Exclude<LayoutPage['layout'], 'wide-top'>];
    const r = chooseLandscapeArrangement(p.photos, p.layout, ars, isLand, preferH3);
    if (r.layout === p.layout && r.photos === p.photos) return p; // unchanged
    return { ...p, layout: r.layout as LayoutPage['layout'], photos: r.photos };
  });
}

/** Run the full client-side post-AI pipeline against an AI layout response
 *  and return the final `{pages, unassigned, slotPositions}`. Same code as
 *  the editor's Régénérer IA button, now also reusable by the magic-upload
 *  flow so customers get the safety-net + face-aware translates + category
 *  rules + slow-start/end + compact-pages BEFORE landing in the editor
 *  (previously the magic flow shipped the raw worker response and the
 *  customer had to manually click Régénérer IA to repair it — that was
 *  what produced D75's "13 photos unplaced" first-impression).
 *
 *  Returns a result that can be persisted via `withSlotPositions` and
 *  shown directly in the preview or editor. Optional `trace` enables the
 *  per-pass `[regen-trace]` console logs (default off for the magic flow,
 *  on for the editor button which uses it for diagnostics). */
export function composeFinalLayout(
  aiLayout: {
    pages: LayoutPage[];
    unassigned_photos?: string[];
    /** photo id → chapter id. Present only for multi-destination albums.
     *  Absent ⇒ the pipeline runs exactly as it did pre-chapters. */
    photo_chapters?: Record<string, number>;
    /** Travel-ordered chapter metadata. Present only when photo_chapters is. */
    chapters?: ChapterMeta[];
  },
  allPhotos: ComposablePhoto[],
  category: AlbumCategory | null,
  options: {
    trace?: boolean;
    useUploadOrder?: boolean;
    /** Photos ÷ target pages. Tells the variety passes how much room they have.
     *  A dense album (≈4) must KEEP its 4-up pages or it can never hit the
     *  customer's page target; a roomy one (≈1.5) is free to spread out.
     *  Omitted ⇒ derived from the layout we were handed. */
    targetPages?: number;
  } = {},
): { pages: LayoutPage[]; unassigned: string[]; slotPositions: SlotPositionMap } {
  const trace = !!options.trace;
  // How packed is this album? Used only to decide whether the variety passes
  // may break up dense pages — never to place a photo.
  const densityTarget = options.targetPages && options.targetPages > 0
    ? options.targetPages
    : (aiLayout.pages?.length || 1);
  const density = allPhotos.length / Math.max(1, densityTarget);
  // At ≥2.6 photos/page, demoting 4-ups to 2-ups would need ~2× the pages the
  // customer asked for, so the variety passes stand down. Below that they run
  // exactly as before.
  const denseAlbum = density >= 2.6;
  // Upload-order mode (admin opt-in): the worker already returns pages in the
  // customer's created_at sequence. To keep album position N ≈ Nth-oldest, we
  // SKIP the four passes that reorder for visual variety (promoteHeroes,
  // applyAntiMonotonePass, balanceSpreadFills, applyPrioritySwapPass) while
  // keeping every placement / face-safety / cropping pass (slot-fill,
  // snapLayoutToPhotoCount, forceSoloForMultiFace, the safety net, and
  // computeAiSlotPositions). OFF path is unchanged.
  const useUploadOrder = !!options.useUploadOrder;
  const log = (label: string, pages: LayoutPage[], rem: string[]) => {
    if (!trace) return;
    const placed = new Set<string>();
    for (const pg of pages) for (const pid of pg.photos) if (pid) placed.add(pid);
    // eslint-disable-next-line no-console
    console.log(
      `[regen-trace] ${label}: placed=${placed.size}, remaining=${rem.length}, total=${placed.size + rem.length}, allPhotos=${allPhotos.length}, pages=${pages.length}`,
    );
  };

  const photoById = new Map<string, ScorablePhoto>(allPhotos.map((p) => [p.id, {
    face_data: p.face_data,
    width: p.width,
    height: p.height,
    aspect_ratio: p.aspect_ratio ?? null,
    vision_tags: p.vision_tags ?? null,
  }]));

  /** Run the full single-group post-AI pipeline (promoteHeroes → ... →
   *  safety-net → compact → template re-merge) against ONE group of pages +
   *  photos and return its `{pages, unassigned}`. This is the original
   *  pre-chapters body, lifted verbatim so each chapter composes in full
   *  isolation. Slot positions are computed once by the caller over the
   *  concatenated result. */
  const composeOneGroup = (
    groupAi: { pages: LayoutPage[]; unassigned_photos?: string[] },
    groupPhotos: ComposablePhoto[],
    tracePrefix: string,
  ): { pages: LayoutPage[]; unassigned: string[] } => {
    const glog = (label: string, pages: LayoutPage[], rem: string[]) =>
      log(`${tracePrefix}${label}`, pages, rem);

    // De-duplicate pages by page number (AI occasionally emits duplicates).
    const seen = new Set<number>();
    const dedupedPages = groupAi.pages.filter((p) => {
      if (seen.has(p.page)) return false;
      seen.add(p.page);
      return true;
    });

    glog('00 ai-raw', dedupedPages, groupAi.unassigned_photos ?? []);

    // Templated pages bypass every post-AI pass — those passes can eject
    // photos or rewrite the layout field, which would corrupt the template's
    // fixed slot count. We snapshot them aside (preserving original page
    // numbers as their merge key), run the full pipeline on the grid pages
    // only, then splice the templated pages back in by page number at the
    // end. Net effect: templates are honored verbatim; grid pages still get
    // promoteHeroes / anti-monotone / safety-net etc.
    const templatedPages: LayoutPage[] = [];
    const gridPages: LayoutPage[] = [];
    for (const p of dedupedPages) {
      if (p.template_id) templatedPages.push({ ...p, photos: [...p.photos] });
      else gridPages.push(p);
    }

    // promoteHeroes reorders for prominence — skip in upload-order mode.
    let finalPages: LayoutPage[];
    let remaining: string[];
    if (useUploadOrder) {
      finalPages = gridPages.map((p) => ({ ...p, photos: [...p.photos] }));
      remaining = [...(groupAi.unassigned_photos ?? [])];
    } else {
      const promoted = promoteHeroes(gridPages, groupAi.unassigned_photos ?? [], photoById);
      finalPages = [...promoted.pages];
      remaining = [...promoted.unassigned];
    }
    glog('01 promoteHeroes', finalPages, remaining);

    // applyAntiMonotonePass reorders for variety — skip in upload-order mode,
    // and skip its 4-up demotion on a dense album (see denseAlbum). That pass
    // breaks EVERY full 4-up page into a 2-up and ejects 2 photos; on a packed
    // album that alone made the customer's "4 per page" impossible and pushed
    // the page count far past the target (#D325: 115 of 179 pages ended 1-up).
    if (!useUploadOrder) {
      const antiMono = applyAntiMonotonePass(finalPages, remaining, photoById, denseAlbum);
      finalPages = antiMono.pages;
      remaining = antiMono.unassigned;
    }
    glog('02 antiMonotone', finalPages, remaining);

    const slowed = applySlowStartAndEndPass(finalPages, remaining);
    finalPages = slowed.pages;
    remaining = slowed.unassigned;
    glog('02b slowStartEnd', finalPages, remaining);

    // Slot-fill + new-page loop (capped at MAX_PAGE_COUNT).
    if (remaining.length > 0) {
      for (let pi = 0; pi < finalPages.length && remaining.length > 0; pi++) {
        const pg = finalPages[pi];
        const maxSlots = ({ '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4, 'wide-top': 2, '3-up-flip': 3, '2-up-side': 2, '5-up': 5, '6-up': 6, '4-up-main': 4, '2-up-even': 2 } as const)[pg.layout] ?? 1;
        while (pg.photos.length < maxSlots && remaining.length > 0) {
          pg.photos.push(remaining.shift()!);
        }
      }
      while (remaining.length > 0 && finalPages.length < MAX_PAGE_COUNT) {
        const layoutChoice: LayoutPage['layout'] =
          remaining.length >= 4 ? '4-up' : remaining.length >= 3 ? '3-up' : remaining.length >= 2 ? '2-up' : '1-up';
        const slotsNeeded = ({ '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4 } as const)[layoutChoice];
        finalPages.push({
          page: finalPages.length + 1,
          layout: layoutChoice,
          photos: remaining.splice(0, slotsNeeded),
        });
      }
    }
    glog('03 slotFill+newPages', finalPages, remaining);

    finalPages = snapLayoutToPhotoCount(finalPages);
    glog('04 snapLayout-a', finalPages, remaining);

    // balanceSpreadFills moves photos across spreads — skip in upload-order mode.
    if (!useUploadOrder) finalPages = balanceSpreadFills(finalPages);
    glog('05 balanceSpreadFills', finalPages, remaining);
    finalPages = snapLayoutToPhotoCount(finalPages);
    glog('06 snapLayout-b', finalPages, remaining);

    // applyPrioritySwapPass swaps photos so rank matches content (reorders) —
    // skip in upload-order mode.
    if (!useUploadOrder) finalPages = applyPrioritySwapPass(finalPages, photoById, category);
    glog('07 prioritySwap', finalPages, remaining);

    // WIDE-TOP: landscape group photos (AR≥1.4, face_count≥3) get a full-width
    // top band sized to their exact AR (zero crop, all faces) + a companion
    // below. Runs BEFORE forceSolo so that pass leaves the finished wide-top
    // pages alone. Consumes a companion from `remaining`; any displaced
    // page-mates go back to `remaining` and are re-placed by the safety net.
    const wideTopped = applyWideTopForWideGroups(finalPages, remaining, photoById);
    finalPages = wideTopped.pages;
    remaining = wideTopped.remaining;
    glog('07a wideTop', finalPages, remaining);

    // Budget the face-rescue pages against the album's own size: up to 15% of
    // the target on a normal album, 8% when packed. Unbounded before — the
    // single biggest reason a 76-page target became 179 pages.
    const soloBudget = Math.max(2, Math.round(densityTarget * (denseAlbum ? 0.08 : 0.15)));
    finalPages = forceSoloForMultiFace(finalPages, photoById, category, soloBudget).pages;
    glog('07b forceSolo', finalPages, remaining);

    // FINAL SAFETY NET — guarantee 0 unplaced (within this group).
    const placedIds = new Set<string>();
    for (const pg of finalPages) for (const pid of pg.photos) if (pid) placedIds.add(pid);
    const stillUnplaced = groupPhotos.filter((p) => !placedIds.has(p.id) && !remaining.includes(p.id));
    if (trace) {
      // eslint-disable-next-line no-console
      console.log(
        `[regen-trace] ${tracePrefix}safety-net pre: placedIds=${placedIds.size}, remaining=${remaining.length}, stillUnplaced=${stillUnplaced.length}, groupPhotos=${groupPhotos.length}, MAX_PAGE_COUNT=${MAX_PAGE_COUNT}, pages=${finalPages.length}`,
      );
    }
    remaining.push(...stillUnplaced.map((p) => p.id));
    while (remaining.length > 0 && finalPages.length < MAX_PAGE_COUNT) {
      const need: LayoutPage['layout'] =
        remaining.length >= 4 ? '4-up' : remaining.length >= 3 ? '3-up' : remaining.length >= 2 ? '2-up' : '1-up';
      const slots = ({ '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4 } as const)[need];
      finalPages.push({
        page: finalPages.length + 1,
        layout: need,
        photos: remaining.splice(0, slots),
      });
    }
    glog('08 safetyNet', finalPages, remaining);

    finalPages = compactPages(finalPages);
    glog('09 compactPages', finalPages, remaining);

    // Slot-count invariant — FINAL enforcement before positioning/save. Runs
    // AFTER wide-top / forceSolo / safety-net (the only passes that create new
    // pages), so any page whose photo count is below its layout's slot count is
    // downgraded here — e.g. a wide-top that never found a companion → 1-up. No
    // page can reach computeAiSlotPositions / the renderer with an empty slot.
    // (rev 2026-06-08, order 1074 blank-zone fix.)
    finalPages = snapLayoutToPhotoCount(finalPages);
    glog('09b snapFinal', finalPages, remaining);

    // Re-merge templated pages. Each templated page keeps its photos intact
    // (none of the post-passes ran on it) but its `page` number is now stale
    // — compactPages renumbered every grid page contiguously. We splice each
    // templated page back in at the position its ORIGINAL page number
    // suggests, then renumber the merged list 1..N so downstream slot keys
    // (p{n}-s{i}) stay contiguous.
    if (templatedPages.length > 0) {
      // Filter out any photo ids in templated pages that are duplicates of
      // grid placements (rare but possible if the AI accidentally reuses an
      // index). Templated pages win the duplicate — they were the model's
      // explicit decoration intent.
      const templatedPhotoIds = new Set<string>();
      for (const tp of templatedPages) for (const pid of tp.photos) if (pid) templatedPhotoIds.add(pid);
      if (templatedPhotoIds.size > 0) {
        finalPages = finalPages.map((p) => ({
          ...p,
          photos: p.photos.filter((pid) => !templatedPhotoIds.has(pid)),
        }));
        finalPages = compactPages(finalPages);
      }
      // Insert by original page number. Sort templated by original page so
      // earlier intents land earlier; ties tolerated.
      const sortedTemplated = [...templatedPages].sort((a, b) => a.page - b.page);
      const merged: LayoutPage[] = [];
      let gi = 0;
      let nextNum = 1;
      for (const tp of sortedTemplated) {
        while (gi < finalPages.length && finalPages[gi].page < tp.page) {
          merged.push({ ...finalPages[gi], page: nextNum++ });
          gi++;
        }
        merged.push({ ...tp, page: nextNum++ });
      }
      while (gi < finalPages.length) {
        merged.push({ ...finalPages[gi], page: nextNum++ });
        gi++;
      }
      finalPages = merged;
      glog('10 mergeTemplated', finalPages, remaining);
    }

    return { pages: finalPages, unassigned: remaining };
  };

  const photoChapters = aiLayout.photo_chapters;
  const useChapters = !!photoChapters && Object.keys(photoChapters).length > 0;

  let finalPages: LayoutPage[];
  let remaining: string[];

  if (useChapters) {
    // Multi-chapter (multi-destination) album. Each chapter is composed in
    // FULL ISOLATION so no page ever mixes photos from two chapters, and the
    // chapters land contiguously in travel order. We split the AI plan and
    // the photo pool per chapter, run the single-group pipeline on each, then
    // concatenate the resulting pages in chapter order with contiguous page
    // numbers. A photo with no chapter assignment is treated as a trailing
    // "orphan" group so it is never dropped.
    const chapterOf = (id: string): number =>
      Object.prototype.hasOwnProperty.call(photoChapters!, id) ? photoChapters![id] : Number.POSITIVE_INFINITY;

    // Travel order: prefer the explicit `chapters` ordering; fall back to the
    // sorted set of chapter ids actually present in photo_chapters. Always
    // append an orphan bucket (Infinity) last if any photo lacks a chapter.
    const presentIds = new Set<number>(Object.values(photoChapters!));
    const orderedChapterIds: number[] = (aiLayout.chapters && aiLayout.chapters.length > 0)
      ? aiLayout.chapters.map((c) => c.id)
      : [...presentIds].sort((a, b) => a - b);
    const groupOrder: number[] = [...orderedChapterIds];
    const hasOrphans = allPhotos.some((p) => chapterOf(p.id) === Number.POSITIVE_INFINITY);
    if (hasOrphans) groupOrder.push(Number.POSITIVE_INFINITY);

    finalPages = [];
    remaining = [];
    let pageCursor = 0;

    for (const chId of groupOrder) {
      const groupPhotos = allPhotos.filter((p) => chapterOf(p.id) === chId);
      if (groupPhotos.length === 0) continue;
      const groupPhotoIds = new Set(groupPhotos.map((p) => p.id));

      // Restrict each AI page to this chapter's photos. Pages that end up
      // empty after the cross-chapter strip are dropped (a page that mixed
      // chapters is split: its photos land in their respective chapter group).
      const groupPages: LayoutPage[] = aiLayout.pages
        .map((pg) => ({ ...pg, photos: pg.photos.filter((id) => groupPhotoIds.has(id)) }))
        .filter((pg) => pg.photos.length > 0);
      const groupUnassigned = (aiLayout.unassigned_photos ?? []).filter((id) => groupPhotoIds.has(id));

      const prefix = chId === Number.POSITIVE_INFINITY ? 'orphans/' : `ch${chId}/`;
      const composed = composeOneGroup(
        { pages: groupPages, unassigned_photos: groupUnassigned },
        groupPhotos,
        prefix,
      );

      // Renumber this group's pages so they sit after the previous chapters'.
      for (const pg of composed.pages) {
        finalPages.push({ ...pg, page: ++pageCursor });
      }
      remaining.push(...composed.unassigned);
    }
    log('11 chaptersConcat', finalPages, remaining);
  } else {
    // Single-chapter / pre-chapters path: compose everything as one group,
    // identical to the original behavior.
    const composed = composeOneGroup(
      { pages: aiLayout.pages, unassigned_photos: aiLayout.unassigned_photos },
      allPhotos,
      '',
    );
    finalPages = composed.pages;
    remaining = composed.unassigned;
  }

  // ── RECONCILE TO THE CUSTOMER'S TARGET (2026-07-26) ──────────────────────
  // Until now `targetPages` was ADVISORY: it produced a density hint and a solo
  // budget, and nothing ever compared the finished list to it. Every
  // page-adding pass (promoteHeroes, slow-start/end, wide-top, the fill loop,
  // the safety net) bounded itself on MAX_PAGE_COUNT instead, so "I asked for
  // 76" reliably came back as more. Runs album-wide AFTER the chapter concat,
  // so a multi-destination album is reconciled once rather than per chapter.
  if (options.targetPages && options.targetPages > 0) {
    finalPages = reconcileToTarget(finalPages, options.targetPages);
    log(`reconcileToTarget(${options.targetPages})`, finalPages, remaining);
  }

  // Fix 2 Case B — aspect-aware slotting. Last pass before positions are
  // computed: keep every landscape in a wide slot (H2/H3), no photo moved off
  // its page, no page added/removed. photoById carries width/height/aspect.
  finalPages = enforceLandscapeSlots(finalPages, photoById);

  const slotPositions = computeAiSlotPositions(
    finalPages,
    // computeAiSlotPositions expects { id, width, height, face_data }.
    allPhotos.map((p) => ({ id: p.id, width: p.width, height: p.height, face_data: p.face_data })),
  );

  return { pages: finalPages, unassigned: remaining, slotPositions };
}

interface SpineConfig {
  text: string;
  year: string;
  bgColor: string;
  textColor: string;
  yearColor: string;
  fontSize: number;
  yearFontSize: number;
  rotation: number;
  scale: number;
  fontFamily?: string;
}

type EditorView = 'cover' | 'pages' | 'intro' | 'outro';
type SidebarTab = 'images' | 'layouts' | 'themes' | 'elements';

// ─── HEIC Detection & Conversion Cache ───────────────────────────────────────
// We detect HEIC by checking the stored file URL path (not original_filename)
// because converted HEIC files are stored as .jpg — only truly-HEIC uploads
// (where conversion failed) still have .heic in their storage path.

const heicCache = new Map<string, string>();
const heicPending = new Map<string, Promise<string>>();

// Bound the display-HEIC cache so a long HEIC-heavy session doesn't leak blob
// URLs (each one retains its decoded Blob in memory). Evict oldest + revoke.
const HEIC_CACHE_MAX = 30;
function heicCacheSet(key: string, url: string) {
  if (url && heicCache.size >= HEIC_CACHE_MAX) {
    const oldest = heicCache.keys().next().value;
    if (oldest !== undefined) {
      const evicted = heicCache.get(oldest);
      if (evicted) { try { URL.revokeObjectURL(evicted); } catch { /* ignore */ } }
      heicCache.delete(oldest);
    }
  }
  heicCache.set(key, url);
}

/** Synchronously free as much memory as possible RIGHT before the native iOS
 *  photo picker opens. iOS backgrounds the web page while the picker is up and
 *  discards a heavy page under memory pressure — reloading it on return, which
 *  wipes the photo selection before it can upload (the "tap the blue Add button
 *  → page reloads → nothing saved" bug). A lighter page survives the round-trip.
 *  These are all synchronous frees so they take effect before the picker opens. */
function freeMemoryForPicker(): void {
  try {
    for (const u of heicCache.values()) { if (u) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } } }
    heicCache.clear();
    heicPending.clear();
    editorHeicCache.clear();
  } catch { /* ignore */ }
  try { releaseLazyModels(); } catch { /* ignore */ }
  try { releaseAnalyzerModels(); } catch { /* ignore */ }
}

/** True only if the file stored in Supabase is actually a HEIC/HEIF */
function isStoredAsHeic(signedUrl: string): boolean {
  try {
    const path = signedUrl.split('?')[0].toLowerCase();
    return path.endsWith('.heic') || path.endsWith('.heif');
  } catch {
    return false;
  }
}

/** Kept for backward compat in components that only have filename */
function isHeicFilename(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

// Semaphore for display-side HEIC conversions (viewing stored HEIC files)
const displayHeicSemaphore = new ConversionSemaphore(HEIC_CONCURRENT);

async function getDisplayUrl(signedUrl: string): Promise<string> {
  if (!isStoredAsHeic(signedUrl)) return signedUrl;
  if (heicCache.has(signedUrl)) return heicCache.get(signedUrl)!;
  if (heicPending.has(signedUrl)) return heicPending.get(signedUrl)!;

  const promise = (async () => {
    await displayHeicSemaphore.acquire();
    try {
      const resp = await fetch(signedUrl);
      const blob = await resp.blob();

      // Strategy 1: Native browser HEIC decode. iOS Safari (and the
      // WebKit-based admin tools on macOS Sonoma+) renders HEIC in <img>
      // natively, so once it's drawn to canvas we can re-encode as JPEG.
      // This is the ONLY strategy that handles modern iPhone HEIC variants
      // reliably — libheif WASM (heic2any) chokes on them.
      try {
        const img = new Image();
        img.src = URL.createObjectURL(blob);
        await img.decode();
        if (img.naturalWidth && img.naturalHeight) {
          // Cap the decode canvas to 1600px long edge — display tiles are tiny,
          // so full-res (~4032px ≈ 48MB transient) is pure waste + an iOS memory
          // spike. PDF uses original_signed_url, so print quality is untouched.
          const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.naturalWidth * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const jpegBlob: Blob | null = await new Promise((resolve) =>
              canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85),
            );
            URL.revokeObjectURL(img.src);
            canvas.width = 0; canvas.height = 0; // free the decode canvas now
            if (jpegBlob) {
              const url = URL.createObjectURL(jpegBlob);
              heicCacheSet(signedUrl, url);
              return url;
            }
          }
        }
      } catch { /* browser can't decode HEIC natively — try libheif next */ }

      // Strategy 2: extract embedded JPEG thumbnail via exifr. Smaller
      // resolution but works for older iPhone HEICs with EXIF thumbnails.
      try {
        const thumb = (await exifr.thumbnail(blob)) as Uint8Array | null | undefined;
        if (thumb && thumb.byteLength > 100) {
          const url = URL.createObjectURL(new Blob([thumb as BlobPart], { type: 'image/jpeg' }));
          heicCacheSet(signedUrl, url);
          return url;
        }
      } catch { /* try heic2any next */ }

      // Strategy 3: full WASM decode via heic2any. Last resort — often
      // fails on iOS 17+ HEVC variants but works for older HEICs.
      try {
        const jpegBlob = await heic2any({ blob, toType: 'image/jpeg', quality: 0.82 });
        const result = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
        const url = URL.createObjectURL(result);
        heicCacheSet(signedUrl, url);
        return url;
      } catch { /* fall through */ }

      // All strategies failed — cache empty so we stop retrying and the
      // consumer can show a placeholder instead of an infinite spinner.
      heicCache.set(signedUrl, '');
      return '';
    } finally {
      displayHeicSemaphore.release();
      heicPending.delete(signedUrl);
    }
  })();

  heicPending.set(signedUrl, promise);
  return promise;
}

// ─── Hook: useConvertedUrl ────────────────────────────────────────────────────
// Returns the displayable URL for a photo:
//  • Non-HEIC: returns signedUrl immediately (no conversion needed)
//  • Stored-as-HEIC: converts via heic2any in background, returns null while converting

function useConvertedUrl(signedUrl: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!signedUrl) return null;
    if (!isStoredAsHeic(signedUrl)) return signedUrl;
    return heicCache.get(signedUrl) ?? null;
  });

  useEffect(() => {
    if (!signedUrl) { setUrl(null); return; }
    if (!isStoredAsHeic(signedUrl)) { setUrl(signedUrl); return; }
    const cached = heicCache.get(signedUrl);
    if (cached !== undefined) { setUrl(cached || null); return; }
    let cancelled = false;
    getDisplayUrl(signedUrl).then((u) => { if (!cancelled) setUrl(u || null); });
    return () => { cancelled = true; };
  }, [signedUrl]);

  return url;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AlbumEditorPage() {
  const { shopifyOrderId, albumIndex: albumIndexParam } = useParams<{
    shopifyOrderId: string;
    albumIndex: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `next` is set by the Shopify COD form so we know where to send the
  // customer after submit (typically /pages/checkout-storiz?order_id=…).
  // Persisted in sessionStorage so it survives reloads inside the editor —
  // otherwise a refresh mid-editing would lose the destination and dump them
  // back at the dashboard. Cleared once consumed by the success handler.
  const nextUrl = (() => {
    const fromQuery = searchParams.get('next');
    if (fromQuery) {
      try { sessionStorage.setItem('storiz_post_submit_next', fromQuery); } catch { /* ignore */ }
      return fromQuery;
    }
    try { return sessionStorage.getItem('storiz_post_submit_next'); } catch { return null; }
  })();
  const albumIndex = parseInt(albumIndexParam ?? '1', 10);
  // Operator editor (2026-07-22): the internal editor experience — Régénérer
  // le PDF, "Lien client", "Redresser pour PDF", the admin cover tools, and no
  // customer submit-gate — is for INTERNAL staff, not just role='admin'.
  // Confirmation Agents (staff with `editor.access`) manage orders end-to-end
  // and must be able to regenerate the PDF, so a staff member holding
  // editor.access gets the same editor as an admin. Real customers have no
  // staff profile → unaffected (they keep the "Commander" experience).
  const profileRole = useAuthStore((s) => s.profile?.role);
  // #1194: two admins in one album, minutes apart, and neither knew. The
  // session row says who saved last; if it was somebody else, recently, say so.
  const myEmail = useAuthStore((s) => s.user?.email ?? null);
  const { can: canPerm } = usePermissions();
  const isAdmin = profileRole === 'admin' || (profileRole === 'staff' && canPerm('editor.access'));
  // Night mode (2026-10-03): a staff member's own light/dark choice follows
  // them here (lib/theme/adminTheme.ts); a customer never gets it.
  const { staff: nightStaff } = useAdminTheme();

  // Mark "reached the editor" server-side (monotonic flow_step = 'editor') as
  // soon as this album's editor opens — BEFORE any layout/pages exist. This is
  // the critical write: without it, the resume / continue link found
  // "cover+photos, no layout" and bounced the customer back to /flow instead of
  // the editor (confirmed bug). Guarded by a ref so it fires ONCE per album
  // open (re-opening a different album re-fires). Fire-and-forget — admins are
  // excluded so admin previews don't advance a customer's furthest step.
  const flowStepMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shopifyOrderId || isAdmin) return;
    // « Nouvel éditeur mobile »: marked once the order is known (below) — a
    // view-only album is never marked (the server answers 404 to it)
    if (m2Ref.current) return;
    const tag = `${shopifyOrderId}#${albumIndex}`;
    if (flowStepMarkedRef.current === tag) return;
    flowStepMarkedRef.current = tag;
    sessionsApi.advanceFlowStep(shopifyOrderId, albumIndex, 'editor');
  }, [shopifyOrderId, albumIndex, isAdmin]);

  const [editorView, setEditorView] = useState<EditorView>('cover');
  const [currentSpread, setCurrentSpread] = useState(0);

  // ── ISSUE 7 (2026-07-05) — reload protection + exact-position restore ──────
  // (a) iOS Safari's pull-to-refresh could reload the editor from a normal
  //     downward drag at the top (native body scroll, no guard). Block the
  //     overscroll glow/refresh for the editor route only; restored on leave.
  useEffect(() => {
    const html = document.documentElement;
    const prevHtml = html.style.overscrollBehaviorY;
    const prevBody = document.body.style.overscrollBehaviorY;
    html.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehaviorY = 'none';
    return () => {
      html.style.overscrollBehaviorY = prevHtml;
      document.body.style.overscrollBehaviorY = prevBody;
    };
  }, []);
  // (b) A reload used to land on the COVER at spread 0 (view/spread were never
  //     persisted). Persist both continuously; the mount effect below restores
  //     them once pages are loaded. ?focusPage deep links still win (they set
  //     the spread after this and are then stripped from the URL).
  const editorPosKey = `storiz_editor_pos_${shopifyOrderId ?? ''}_${albumIndexParam ?? '1'}`;
  const restoredPosRef = useRef(false);
  useEffect(() => {
    // Persist ONLY after the restore pass has run — the very first render
    // otherwise writes the DEFAULT ('cover', spread 0) and clobbers the saved
    // position before the restore effect gets to read it (verified live).
    if (!restoredPosRef.current) return;
    try {
      sessionStorage.setItem(editorPosKey, JSON.stringify({ v: editorView, s: currentSpread }));
    } catch { /* private mode — restore just won't happen */ }
  }, [editorPosKey, editorView, currentSpread]);
  // Admin "copy client link (this page)" confirmation flash.
  const [clientLinkCopied, setClientLinkCopied] = useState(false);
  // Fix H (2026-05-28): stuck-user detector. Fed by undo/slot-touch/progress
  // signals scattered through the handlers below. When `isStuck` flips true
  // we surface a small WhatsApp pill inside the zoom toolbar.
  const stuck = useStuckUser();
  // Ref to the bottom carousel — used to auto-scroll the active spread into
  // view whenever currentSpread changes via the prev/next arrows, the deep
  // link, or handleInsertPage. Keeps top preview and bottom strip in sync.
  const bottomStripRef = useRef<HTMLDivElement | null>(null);
  // Drag-to-scroll (desktop mouse): grab the strip body and slide it 1:1 with
  // the cursor. Gated by isReorderingRef so it fully yields to dnd-kit's
  // press-hold reorder (no double-move). Touch keeps its native momentum
  // scroll untouched — this path is mouse-only on purpose.
  const isReorderingRef = useRef(false);
  const handleStripMouseDown = useMouseDragScroll(isReorderingRef);
  // iOS keyboard-catcher: a hidden input we focus SYNCHRONOUSLY when the
  // customer adds text, so Safari raises the keyboard inside the tap gesture.
  // Focus then transfers to the new text element's contentEditable on mount.
  const keyboardCatcherRef = useRef<HTMLInputElement | null>(null);
  const [selectedPageSide, setSelectedPageSide] = useState<'left' | 'right'>('left');
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('images');
  const [selectedCover, setSelectedCover] = useState<Cover | null>(null);
  // FEATURE 2 — per-album cover preview/change popup. Holds the open state of
  // the cover Modal that wraps the searchable cover grid (ThemesSidebar).
  const [coverModalOpen, setCoverModalOpen] = useState(false);

  // Spine configuration
  const [spineConfig, setSpineConfig] = useState<SpineConfig>({
    text: '',
    year: '',
    bgColor: '#D35400',
    textColor: '#5B8CDB',
    yearColor: '#C9A24A',
    fontSize: 28,
    yearFontSize: 14,
    rotation: 0,
    scale: 1,
  });

  // Spine editing panel
  const [editingSpine, setEditingSpine] = useState(false);

  // ── Local editable pages (mutable copy of session layout) ──────────────────
  const [localPages, setLocalPages] = useState<LayoutPage[]>([]);
  // ISSUE 7b — restore the saved view + spread ONCE, after pages have loaded
  // (so the spread index can be clamped to real bounds). A ?focusPage deep
  // link wins: it targets an explicit page, so we skip the restore entirely.
  useEffect(() => {
    if (restoredPosRef.current || localPages.length === 0) return;
    restoredPosRef.current = true;
    try {
      if (new URLSearchParams(window.location.search).get('focusPage')) return;
      const raw = sessionStorage.getItem(editorPosKey);
      if (!raw) return;
      const p = JSON.parse(raw) as { v?: EditorView; s?: number };
      // Spreads as the printed book opens (editorSpreads.ts): a themed book
      // has one more — its S1 opening stands alone.
      const maxSpread = lastSpread(localPages.length, isThemedBook(localPages));
      if (typeof p.s === 'number' && p.s > 0) {
        setCurrentSpread(Math.min(Math.max(0, p.s), maxSpread));
      }
      if (p.v === 'pages' || p.v === 'cover' || p.v === 'intro' || p.v === 'outro') {
        setEditorView(p.v);
      }
    } catch { /* corrupted/absent — default behaviour */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPages.length]);
  const [localUnassigned, setLocalUnassigned] = useState<string[]>([]);
  // ── Intro page client element layer ───────────────────────────────────────
  // Free-floating elements (text / photo / shape / icon) the client places on
  // top of the intro branding background. Kept OUTSIDE localPages on purpose so
  // the intro never enters the AI layout / balancing / page-count / pricing
  // paths — it stays a FREE bookend. Persisted as `pages.intro_elements` in the
  // session JSONB (migration-free); rendered in the PDF via the same
  // drawPageElements the content pages use, over the intro background.
  const [introElements, setIntroElements] = useState<PageElement[]>([]);

  // ── Added pages tracking ──────────────────────────────────────────────────
  const [addedPagesCount, setAddedPagesCount] = useState(0);
  const [pricePerPage, setPricePerPage] = useState(8); // MAD — THIS order's frozen price, set when the order loads
  const [insertAfterPageIdx, setInsertAfterPageIdx] = useState<number | null>(null); // opened insert modal
  // « PAGE DE FIN » (coupons plan 2026-10-28, lib/outroPage.ts) — the « + » menu
  // offers it only when app_settings.editor_outro_page_enabled is on (absent =
  // off: the « + » adds 2 photo pages directly, as before). `insertMenu` = the
  // open « + » menu: where it inserts and the button it hangs from.
  const [outroPageEnabled, setOutroPageEnabled] = useState(false);
  const [insertMenu, setInsertMenu] = useState<{ afterIdx: number; left: number; top: number; bottom: number } | null>(null);
  // Coupons print switch — the admin « Régénérer » browser render asks for the
  // order's coupons only when it is on (lib/couponPrint.ts).
  const couponsEnabledRef = useRef(false);
  // Fix #4/#5 (2026-05-28, revised): the delete X on every spread thumb
  // is now permanently visible (no long-press reveal). `useIsMobile`
  // gives SpreadThumbButton a 22 px chip on phones for touch comfort,
  // 18 px on desktop.
  const isMobile = useIsMobile();

  // ── « Nouvel éditeur mobile » (2026-10-19) ─────────────────────────────────
  // ONE switch: phones only AND (?m2=1 / localStorage sz-m2 OR the settings
  // flag, migration 257 not applied). Off = this page renders exactly as before.
  const [mobileV2Flag, setMobileV2Flag] = useState<boolean | null>(null);
  const m2 = useMobileEditorV2(mobileV2Flag);
  const m2Ref = useRef(m2);
  m2Ref.current = m2;
  // The sheet open in the new layout. « Photos » and « Mise en page » reuse the
  // classic `mobileSidebarOpen` state ('images' / 'layouts') so every existing
  // door into them (empty slot → photos, Remplacer → photos…) still opens them.
  const [m2Sheet, setM2Sheet] = useState<'pages' | 'text' | 'cover' | 'menu' | null>(null);
  // One page at a time (default) or the whole spread.
  const [m2Double, setM2Double] = useState(false);
  // One page at a time on the cover too: the front (default) or the back;
  // « Double page » shows the whole wrap (back · spine · front).
  const [m2CoverFace, setM2CoverFace] = useState<'front' | 'back'>('front');
  // the Photos sheet's « Outils » view; the Mise en page sheet's page tools
  const [m2PhotoTools, setM2PhotoTools] = useState(false);
  // the Texte sheet's own input (a real field: iOS raises the keyboard on it)
  const [m2NewText, setM2NewText] = useState('');
  const [m2PageTools, setM2PageTools] = useState(false);
  // The context dock: the selection's bars portal into it (components/editor/mobile/dock.tsx).
  const [m2DockEl, setM2DockEl] = useState<HTMLDivElement | null>(null);
  const m2Viewport = useVisualViewport(m2);
  // Swipe → page step, one-finger pan when zoomed, double-tap = fit (refs: the
  // touch handlers are declared before the spread state they act on).
  const m2NavRef = useRef<((dir: 1 | -1) => void) | null>(null);
  const m2PanRef = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null);
  const m2TapRef = useRef<{ x: number; y: number; t: number; plain: boolean } | null>(null);
  const m2LastTapRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const m2SuppressClickUntilRef = useRef(0);
  const m2SwipeableElRef = useRef<(id: string) => boolean>(() => false);
  const m2SelBeforeTouchRef = useRef<string | null>(null);
  // View-only album in the new layout: nothing can change, so nothing is ever
  // sent (the server would answer 423 to every save — the console noise and
  // « Non enregistré » of audit B1).
  const m2ViewOnlyRef = useRef(false);
  const m2OrderKnownRef = useRef(false);
  const m2SaveHeldRef = useRef(false);
  // The new layout's own hidden file inputs (cover photo / illustration / back
  // cover): usable from any view, wired to the SAME upload handlers.
  const m2BlankInputRef = useRef<HTMLInputElement | null>(null);
  const m2IconInputRef = useRef<HTMLInputElement | null>(null);
  const m2BackInputRef = useRef<HTMLInputElement | null>(null);

  // ── New-page hint (from /flow?addedPages=…) ───────────────────────────────
  // Set of page numbers (1-indexed) that should pulse the "Tap to add photos"
  // hint. Populated from the URL query param when the customer comes in via
  // the preview's "Ajouter 2 pages" button. Cleared when the customer taps
  // any slot so the hint doesn't linger after the intent is satisfied.
  const [pendingPagesHint, setPendingPagesHint] = useState<Set<number>>(new Set());

  // ── Cover text & customizations ────────────────────────────────────────────
  const [coverDestText, setCoverDestText] = useState('');
  // The customer edits the front title → the spine follows: one title, read
  // edge on (owner: « the title needs to match with the spine text as well »).
  // The spine panel's own text box still overrides it until the next edit.
  const editCoverTitle = useCallback((text: string) => {
    setCoverDestText(text);
    setSpineConfig((prev) => (prev.text === text ? prev : { ...prev, text }));
  }, []);
  const [coverYearText, setCoverYearText] = useState('');
  // Last non-empty title values — used to auto-restore the mandatory cover
  // title if a customer clears it (Permanent Fix 2). Admins may leave it blank.
  const lastCoverDestRef = useRef('');
  const lastCoverYearRef = useRef('');
  useEffect(() => { if (coverDestText.trim()) lastCoverDestRef.current = coverDestText; }, [coverDestText]);
  useEffect(() => { if (coverYearText.trim()) lastCoverYearRef.current = coverYearText; }, [coverYearText]);
  const [coverCustomTexts, setCoverCustomTexts] = useState<Record<number, string>>({});
  const [coverZoneOverrides, setCoverZoneOverrides] = useState<Record<number, TextZoneOverride>>({});
  const [coverExtraZones, setCoverExtraZones] = useState<ExtraTextZone[]>([]);
  const [selectedCoverZone, setSelectedCoverZone] = useState<string | null>(null);
  // Cover-zone double-click puts the zone into text-edit mode. Distinct from
  // selection: a selected (but not editing) zone is draggable by its body.
  const [editingCoverZone, setEditingCoverZone] = useState<string | null>(null);
  // Back cover background — defaults to front cover dominant color, user-overridable
  const [backCoverBgColor, setBackCoverBgColor] = useState<string | null>(null);
  // Front cover background override — when null we fall back to cover.dominant_color.
  // Set via the color picker on the front cover and the SpineEditor "Couleur de fond avant".
  const [frontCoverBgColor, setFrontCoverBgColor] = useState<string | null>(null);
  // When true, the cover thumbnail image is hidden so only the bg color shows
  // (lets the user fully recolor the front face).
  const [hideFrontCoverImage, setHideFrontCoverImage] = useState(false);
  // Position + scale of the transparent design overlay on the front face.
  // x/y are container-relative percentages (0 = no offset), scale is a
  // multiplier where 1 = contain-fit (the default the editor shipped with).
  // Saved into cover_customizations so the PDF can match the editor.
  type CoverDesignTransform = { x: number; y: number; scale: number };
  const [coverDesignTransform, setCoverDesignTransform] = useState<CoverDesignTransform>({ x: 0, y: 0, scale: 1 });
  // The design overlay's drag/zoom toolbar is hidden until the customer
  // clicks the artwork. Mirrors the page-element selection pattern so
  // floating controls don't crowd the cover preview by default.
  const [coverDesignSelected, setCoverDesignSelected] = useState(false);

  // Back-cover images persist as { storage_path } in cover_customizations.
  // The signed_url is recomputed on every load so it never expires mid-session.
  // Earlier versions kept blob: URLs from URL.createObjectURL — those died on
  // refresh, which is why customers' back-cover photos kept disappearing.
  // Declared up here so the history-snapshot hook can capture it.
  type BackCoverImage = { storage_path: string; signed_url: string | null; full_page?: boolean };
  const [backCoverImages, setBackCoverImages] = useState<BackCoverImage[]>([]);
  // COVER ELEMENTS (owner 2026-10-20) — stickers / icons / the client's own
  // photos placed freely on the front, back and spine (lib/coverElements.ts).
  // Persisted as cover_customizations.coverElements; the PDF draws them with
  // the interior's drawPageElements. Placed by the team (admin); a client sees
  // them read-only. THE COVER IS SACRED: no automatic process touches them.
  const [coverElements, setCoverElements] = useState<CoverElement[]>([]);
  const [selectedCoverElId, setSelectedCoverElId] = useState<string | null>(null);
  /** What every cover_customizations writer persists (a plain copy). */
  const coverElementsForSave = () => coverElements.map((e) => ({ ...e }));
  // The spine face's real width comes from the admin's print settings.
  const [coverSpineSettings, setCoverSpineSettings] = useState<{ spineWmm: number; jointGapMm: number } | undefined>(undefined);
  // The whole print settings (board, safe zone…) — the faces' SAFE ZONE is
  // computed from them exactly as the cover PDF reads them (multiAlbumPdf:
  // { ...DEFAULT_PRINT_SETTINGS, ...print_template_settings }).
  const [coverPrintSettings, setCoverPrintSettings] = useState<PrintSettings>(DEFAULT_PRINT_SETTINGS);

  // Per-order override for the front-cover illustration ("door"). Two
  // flavours share this single render path (CoverThumbnail
  // frontImageSrcOverride + the PDF's frontCoverImageOverrideUrl):
  //  - { storage_path } — admin-uploaded custom art; persisted path only,
  //    the signed view URL is regenerated on every load.
  //  - { url } — a catalogue icon the CUSTOMER picked on a blank ("vierge")
  //    cover; design_url lives in the public covers bucket so it needs no
  //    signing and persists as-is.
  // When set it replaces the catalogue cover.design_url in the editor + PDF,
  // without touching the shared covers row, so other orders are unaffected.
  const [frontCoverImageOverride, setFrontCoverImageOverride] =
    useState<{ storage_path?: string; url?: string } | null>(null);
  const [frontCoverImageOverrideUrl, setFrontCoverImageOverrideUrl] =
    useState<string | null>(null);

  // Client-supplied FRONT-cover image on a blank ("vierge") cover. Distinct
  // from the admin frontCoverImageOverride (different upload prefix + audience)
  // but rendered through the SAME front-image path (CoverThumbnail
  // frontImageSrcOverride + the PDF's frontCoverImageOverrideUrl) — a blank
  // cover has no catalogue design to override so the two never collide.
  // Persisted as cover_customizations.blankCoverImage = { storage_path };
  // signed URL regenerated on every load (mirrors back-cover to dodge the
  // blob:-URL-expiry bug).
  const [blankCoverImage, setBlankCoverImage] =
    useState<{ storage_path: string } | null>(null);
  const [blankCoverImageUrl, setBlankCoverImageUrl] =
    useState<string | null>(null);
  // IMPORTED FULL-WRAP COVER — written by the manual-order PDF import
  // (importNativeAlbum.ts:196) and read by the generator
  // (multiAlbumPdf.ts:221). The editor has no UI for it, but it MUST survive an
  // editor save: cover_customizations is persisted as a whole literal below and
  // the worker replaces the column wholesale, so any key we don't echo back is
  // destroyed. Before this ref existed, simply OPENING an imported album
  // dropped the wrap on the first autosave and the next regen printed a blank
  // cover. A ref, not state: it is passed through untouched, never rendered.
  const importedCoverWrapRef = useRef<{ storage_path: string } | null>(null);
  // Her photo order written by the team (worker lib/clientOrder.ts — #1248):
  // a sibling of `pages`; every save below echoes it, or it would be erased.
  const clientOrderRef = useRef<string[] | null>(null);
  // Pan/zoom the client applied to their blank-cover photo (translate % +
  // scale on the cover-fit base). Persisted as cover_customizations
  // .blankCoverTransform; mirrored in the PDF (render-parity).
  const [blankCoverTransform, setBlankCoverTransform] =
    useState<{ x: number; y: number; scale: number }>({ x: 0, y: 0, scale: 1 });

  // Customer "Choisir une icône" picker on the blank ("vierge") cover —
  // picks a catalogue décor into frontCoverImageOverride ({ url } flavour).
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Per-slot photo fit for templated pages. Keyed by page number → slot index
  // → { scale, offsetX, offsetY }. Persisted into cover_customizations so the
  // preview + PDF can mirror the editor's framing of the clipped photo.
  const [templatePhotoFits, setTemplatePhotoFits] = useState<
    Record<number, Record<number, PhotoFitInSlot>>
  >({});

  const updateFit = useCallback((
    pageNum: number,
    slotIdx: number,
    fit: PhotoFitInSlot,
  ) => {
    setTemplatePhotoFits((prev) => ({
      ...prev,
      [pageNum]: { ...(prev[pageNum] ?? {}), [slotIdx]: fit },
    }));
  }, []);

  // ── Page interaction ───────────────────────────────────────────────────────
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null); // "p{n}-s{m}"
  // FIX #1 (2026-07-05): template-slot EDIT mode (parallel of panModeKey for
  // normal slots). 1st tap on a templated photo selects (SelectedActionsBar);
  // 2nd tap enters edit (ClampedPill with scale/reset/trash/Terminé). Kept
  // separate from panModeKey because template slots use clip-path + %-offset
  // geometry, not the pdfMirrorBox pan machinery.
  const [templateEditKey, setTemplateEditKey] = useState<string | null>(null);
  // Fix H (2026-05-28): wrap the setter so every selection feeds the
  // stuck-user heuristic. `stuck.noteSlotTouch` is a ref-backed function so
  // it's stable across renders.
  const handleSelectSlot = useCallback((key: string | null) => {
    if (key) {
      stuck.noteSlotTouch(key);
      // ITEM 1 (2026-07-06) — single active selection. Choosing a slot clears
      // any selected page ELEMENT (and its edit state) so a photo-element's
      // pill can't stay stacked over the slot's controls.
      setSelectedElementId(null);
      setSelectedElementIds(new Set());
      setEditingElementId(null);
    }
    setSelectedSlotKey(key);
  }, [stuck]);
  const [panModeKey, setPanModeKey] = useState<string | null>(null);
  const [focusedSlot, setFocusedSlot] = useState<{ pageNumber: number; slotIdx: number; photoId: string } | null>(null);
  const [slotPositions, setSlotPositions] = useState<Record<string, { x: number; y: number; scale?: number; rotation?: number; lockSize?: boolean }>>({});
  // ── Manual-edit memory keyed BY PHOTO (Fix A: preserve on Régénérer) ───────
  // When the customer manually pans/scales a slot, we record `{photoId, pos,
  // layout, ar}` here so the edit can FOLLOW the photo across an AI re-layout.
  // A ref (not state) — it never needs to trigger a render; it's read once at
  // regen time and rehydrated from the persisted pages on load. Keyed by
  // photoId; last manual edit per photo wins.
  const manualSlotEditsRef = useRef<ManualSlotEditMap>({});
  // "Utiliser les modèles" — whether Régénérer offers designed model templates
  // to the AI. Default ON (the AI-Magic first generation is always ON — the
  // toggle only appears for regeneration). Persisted per album as a pages-JSONB
  // sibling (`use_templates`), like manual_slot_edits — migration-free.
  const [useTemplates, setUseTemplates] = useState(true);
  // Latest localPages, so the manual-edit recorder can map a slotKey →
  // the photo currently sitting in that slot at edit time without depending
  // on render-time closures.
  const localPagesRef = useRef<LayoutPage[]>(localPages);
  useEffect(() => { localPagesRef.current = localPages; }, [localPages]);
  // Records a USER-GESTURE pan/scale edit (distinct from the AI's
  // computeAiSlotPositions writes, which never call this) against the photo
  // currently in the slot. Wrap the four manual setSlotPositions writers with
  // this so "a photo I fixed stays fixed" survives a re-layout.
  const recordManualSlotEdit = useCallback((slotKey: string, pos: SlotPosition) => {
    const m = /^p(\d+)-s(\d+)$/.exec(slotKey);
    if (!m) return;
    const pageNum = Number(m[1]);
    const slotIdx = Number(m[2]);
    const page = localPagesRef.current.find((p) => p.page === pageNum);
    if (!page) return;
    const photoId = page.photos[slotIdx];
    if (!photoId) return;
    manualSlotEditsRef.current = {
      ...manualSlotEditsRef.current,
      [photoId]: { photoId, pos: { ...pos, _v: 2 }, layout: page.layout, ar: slotARForPage(page, slotIdx) },
    };
  }, []);
  const [pageCountWarning, setPageCountWarning] = useState<string | null>(null);
  // Pending page-deletion awaiting confirmation in the shared warning popup.
  // `pages` holds every page number to remove in one confirm (1 for a single
  // page, 2 for a scroller spread). The popup copy + the delete itself derive
  // from this so all entry points (scroller, desktop panel, mobile hold) share
  // one premium dialog instead of window.confirm.
  const [pageDeleteRequest, setPageDeleteRequest] = useState<{ pages: number[] } | null>(null);
  // Page-count slider DECREASE request — held pending the confirm modal so no
  // content is ever removed silently. `toRemove` = trailing empty pages first,
  // then content pages if still short; `removesContent` drives the warning copy.
  const [pageCountDecrease, setPageCountDecrease] = useState<{ target: number; toRemove: number[]; removesContent: boolean } | null>(null);
  // The page count the customer last asked for via the slider. Régénérer uses
  // THIS as the target (not the current localPages length) so a request for
  // FEWER pages actually reaches the worker instead of echoing the current
  // count back. Null = never touched → fall back to the current page count.
  const [pageCountTarget, setPageCountTarget] = useState<number | null>(null);
  // Uncommitted page-count the customer is adjusting in "Mises en page".
  // null = slider mirrors the album. Owned HERE, not in the slider, so it
  // survives that panel unmounting (tab switch / mobile sheet dismiss) and so
  // nothing is applied until Valider. See PageCountSlider's draft model.
  const [pageCountDraft, setPageCountDraft] = useState<number | null>(null);
  // The customer's chosen photo-density for AI regen: 'one' = 1 photo/page,
  // 'mixed' = 1–4 photos/page. Initialised from order.layout (their COD choice)
  // but overridable in the "Mises en page" panel — the stored value can be
  // stale, so the live toggle is what Régénérer sends (as layout_override).
  const [layoutChoice, setLayoutChoice] = useState<'one' | 'mixed'>('mixed');
  // Whether the customer has touched the layout toggle this session. Until they
  // do, it tracks order.layout as it loads; after, we never clobber their pick.
  const layoutChoiceTouchedRef = useRef(false);
  // Refs mirror the two regen inputs so handleGenerateFromEditor can read the
  // latest values without listing them in its deps (keeps the callback identity
  // stable — same pattern the file already uses for allPhotos/localPages).
  const layoutChoiceRef = useRef<'one' | 'mixed'>(layoutChoice);
  useEffect(() => { layoutChoiceRef.current = layoutChoice; }, [layoutChoice]);
  const pageCountTargetRef = useRef<number | null>(pageCountTarget);
  useEffect(() => { pageCountTargetRef.current = pageCountTarget; }, [pageCountTarget]);
  // Mobile hold-to-delete: the page number currently "lifted" (held ~1s),
  // showing its bin affordance. Null = no page lifted.
  const [liftedPageNumber, setLiftedPageNumber] = useState<number | null>(null);
  const [draggingPhotoId, setDraggingPhotoId] = useState<string | null>(null);
  // Tap-to-place flow for touch devices (drag-and-drop is unreliable on mobile).
  // Two complementary flows:
  //   • photo-first  → user taps a photo in the strip, then an empty slot
  //   • slot-first   → user taps an empty slot, then a photo in the strip
  const [pickedPhotoForPlace, setPickedPhotoForPlace] = useState<string | null>(null);
  const [pendingPlaceTarget, setPendingPlaceTarget] = useState<{ page: number; slot: number } | null>(null);
  // ISSUE 8 — an EMPTY photo ELEMENT waiting for a picture: the next photo
  // tapped in "Mes photos" fills it (element.photoId) instead of a slot.
  const [photoElementTarget, setPhotoElementTarget] = useState<{ page: number; elementId: string } | null>(null);
  const handlePickPhotoForElement = useCallback((pageNum: number, elementId: string) => {
    setPhotoElementTarget({ page: pageNum, elementId });
    setPendingPlaceTarget(null);
    setSidebarTab('images');
    setMobileSidebarOpen('images');
  }, []);

  // ── Page elements (text, shapes, extra photos) ─────────────────────────────
  type ActiveTool = 'select' | 'text' | 'photo' | 'rect' | 'ellipse';
  const [activeTool, setActiveTool] = useState<ActiveTool>('select');
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  // D78 Fix 1 (2026-05-21): gates the heavy FloatingElementToolbar. False
  // by default — selecting an element only shows the compact Canva pill.
  // The pill's "More" button flips this to true; clicking outside (or X
  // in the toolbar) resets it back. Without this gate the heavy bottom
  // toolbar appeared the moment ANY element was selected, even when the
  // customer just wanted to drag/rotate/delete from the pill.
  const [elementToolbarOpen, setElementToolbarOpen] = useState(false);
  // Multi-select set (Section 3). The "primary" selected element stays
  // in `selectedElementId` so the floating toolbar has something to
  // anchor; this set contains EVERY selected element (including the
  // primary). When size > 1, group-delete + group-copy work on the
  // entire set.
  const [selectedElementIds, setSelectedElementIds] = useState<Set<string>>(new Set());

  // Update primary + set together. When `additive` (shift+click on
  // desktop, multi-tap on mobile), toggle the id in the set. When not,
  // single-select replaces both.
  const selectElement = useCallback((id: string | null, additive = false) => {
    // Reset the heavy-toolbar gate when the selection CHANGES. The
    // post-render useEffect below re-opens it automatically when the
    // newly-selected element is a TEXT element (spec 2026-06-05, "Unify
    // text system" — text gets the heavy toolbar directly, shapes stay
    // gated behind the pill's More button as before).
    // FIX (2026-06-10): only reset when the id actually changes. A real tap
    // on a text element fires onSelect twice (pointerdown + click); the
    // second call re-selected the SAME id, reset the gate to false, and the
    // open-effect (dep: [selectedElementId]) never re-ran because the id was
    // unchanged — so tapping an EXISTING intro/page text showed no toolbar.
    if (id !== selectedElementId) setElementToolbarOpen(false);
    if (id !== null) {
      // ITEM 1 (2026-07-06) — single active selection. Selecting an element
      // clears any slot selection / pan / template-edit so two control popups
      // never show at once.
      setSelectedSlotKey(null);
      setPanModeKey(null);
      setTemplateEditKey(null);
    }
    if (id === null) {
      setSelectedElementId(null);
      setSelectedElementIds(new Set());
      // INVARIANT (2026-06-10): an element can never be editing while not
      // selected. On mobile the hidden keyboard-catcher holds focus during
      // typing, so the text chrome's own blur→stopEditing never fires;
      // deselecting then stranded the element in edit mode, whose
      // contenteditable span swallows every later tap — the element became
      // un-reselectable (no toolbar, no pill). Clearing editing here kills
      // that orphan state for intro AND content pages.
      setEditingElementId(null);
      return;
    }
    if (additive) {
      setSelectedElementIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          // If we just removed the primary, demote to another element
          // in the set (any one — last item wins) so the toolbar
          // doesn't suddenly hide on a still-multi-selection.
          if (id === selectedElementId) {
            const remaining = Array.from(next);
            setSelectedElementId(remaining.length > 0 ? remaining[remaining.length - 1] : null);
          }
        } else {
          next.add(id);
          setSelectedElementId(id); // promote new addition to primary
        }
        return next;
      });
    } else {
      setSelectedElementId(id);
      setSelectedElementIds(new Set([id]));
    }
  }, [selectedElementId]);

  // Text elements get the heavy FloatingElementToolbar (image 4 from the
  // 2026-06-05 spec) the moment they're selected — no "More" gate. Shapes
  // / icons / extra photos keep the pill-first flow.
  useEffect(() => {
    if (!selectedElementId) return;
    // Use a ref to avoid the effect running every render — we only care
    // about the selection change. localPagesRef tracks the latest pages
    // but the cheap path: walk localPages here at effect-time.
    let isText = false;
    for (const p of localPages) {
      const found = (p.elements ?? []).find((el) => el.id === selectedElementId);
      if (found) { isText = found.type === 'text'; break; }
    }
    // FIX C (2026-06-08): intro elements live OUTSIDE localPages (free
    // bookend), so scan them too when the intro view is active — this is what
    // makes a selected intro text element auto-open the same font/size/colour
    // /B-I-U toolbar the content pages get (incl. right after Done/Enter,
    // since the element stays selected once editing ends).
    if (!isText && editorView === 'intro') {
      const iFound = introElements.find((el) => el.id === selectedElementId);
      if (iFound) isText = iFound.type === 'text';
    }
    if (isText) setElementToolbarOpen(true);
  // We intentionally do NOT depend on localPages here — opening the toolbar
  // is a one-shot side-effect of the selection change, not of every
  // element edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElementId]);

  // ── Mobile sidebar overlay ─────────────────────────────────────────────────
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<SidebarTab | null>(null);

  // ── Mobile tools strip (rev 2026-06-08) ────────────────────────────────────
  // Phones/tablets have no lg+ right sidebar, so the 5 editing tools (Texte,
  // Photo, Mise en page, Thèmes, Modèles) now live in an ALWAYS-VISIBLE
  // vertical icon strip pinned to the left edge (rendered below). This replaces
  // the old hidden left-edge SWIPE drawer — clients never discovered it. The
  // gesture helpers in lib/edgeSwipeDrawer.ts are no longer wired here.
  //
  // Smart hint: if the customer taps the page a few times WITHOUT adding
  // anything, a one-time tooltip points at the Texte tool. `pageTapCount`
  // counts taps on the page surface; `hasAddedElementRef` suppresses the hint
  // once the customer has actually added an element. OnboardingHint handles the
  // once-per-device persistence + 3 s auto-dismiss.
  const [pageTapCount, setPageTapCount] = useState(0);
  const [showTextHint, setShowTextHint] = useState(false);
  const hasAddedElementRef = useRef(false);
  // Mobile tools strip is collapsed by default behind a half-circle edge tab
  // (rev 2026-06-08) so it never overlaps the canvas / CTA. Tap the tab to slide
  // it in; tap a tool, the backdrop, or outside to slide it out.
  const [toolsStripOpen, setToolsStripOpen] = useState(false);
  useEffect(() => {
    if (pageTapCount >= 3 && !hasAddedElementRef.current) {
      setShowTextHint(true);
      setToolsStripOpen(true); // open the strip so the hint can anchor to the Texte tool
    }
  }, [pageTapCount]);
  // First-open pulse rings on the closed tools button — a one-time, once-per-
  // session hint that it's tappable (rev 2026-06-08). sessionStorage flag so it
  // never replays this session (or on remount); plays ~2 rings then stops.
  const [toolsPulse, setToolsPulse] = useState(() => {
    try { return sessionStorage.getItem('storiz.toolsPulse') !== '1'; } catch { return true; }
  });
  useEffect(() => {
    if (!toolsPulse) return;
    const t = window.setTimeout(() => {
      try { sessionStorage.setItem('storiz.toolsPulse', '1'); } catch { /* quota / SSR */ }
      setToolsPulse(false);
    }, 3000);
    return () => window.clearTimeout(t);
  }, [toolsPulse]);

  // G1 (2026-06-08): long-press-to-delete on the mobile photos modal. `photoToDelete`
  // holds the id pending the "Supprimer cette photo ?" confirm; the press timer
  // fires after ~500ms hold, and `photoPressFiredRef` suppresses the trailing
  // click so a long-press never also arms/places the photo.
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const photoPressTimerRef = useRef<number | null>(null);
  const photoPressFiredRef = useRef(false);

  // Multi-select + bulk delete on the mobile "Mes photos" sheet (iPhone-Photos
  // style). `selectMode` flips tile taps from pick-for-placement to toggle-select;
  // `selectedPhotoIds` holds the chosen ids. Both reset when the sheet closes.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleSelect = useCallback((id: string) => {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedPhotoIds(new Set());
  }, []);

  // ── Layout-template gallery picker (Phase 1) ───────────────────────────────
  // Holds the page-array index of the page whose Modèles button was tapped.
  // Null = picker closed. Picking a template applies it to that page.
  const [templatePickerForPage, setTemplatePickerForPage] = useState<number | null>(null);
  // FIX (2026-07-07): when the "Choisir un modèle" picker (or the fill-preview
  // modal) opens, dismiss any floating photo/slot bar. Those bars are portaled
  // to <body> at max z-index, so without this they float ON TOP of the modal.
  // Clearing the selection unmounts them; the picker is page-level and doesn't
  // need a selected slot.
  useEffect(() => {
    if (templatePickerForPage !== null) {
      setSelectedSlotKey(null);
      setPanModeKey(null);
      setTemplateEditKey(null);
      setSelectedElementId(null);
      setSelectedElementIds(new Set());
    }
  }, [templatePickerForPage]);
  // After a template is picked, hold it here so the fill-preview modal opens
  // (assign a photo to each zone) BEFORE the template is applied to the page.
  const [templateFill, setTemplateFill] = useState<{ pageIndex: number; template: LayoutTemplate } | null>(null);
  // (2026-07-05) Model switching now lives in the page-options menu
  // (openPageMenu → "Modèles" → setTemplatePickerForPage). The old direct
  // background double-tap → template picker was folded into that menu.

  // ── Admin-managed dynamic fonts (Section 8) ────────────────────────────────
  // Fetch the editor_fonts catalogue once on mount, inject each Google
  // Fonts URL as a <link> in <head>, and expose the list so the font
  // picker (in FloatingElementToolbar) can append them under "All fonts".
  const [customFonts, setCustomFonts] = useState<{ family: string; label: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fonts } = await editorResourcesApi.listFonts();
        if (cancelled) return;
        const list: { family: string; label: string }[] = [];
        for (const f of fonts) {
          // Inject the <link> exactly once per URL. Tag with data-attr so
          // we don't accumulate duplicates on hot-reload.
          const sig = `editor-font-${f.id}`;
          if (!document.head.querySelector(`link[data-editor-font="${sig}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = f.google_url;
            link.dataset.editorFont = sig;
            document.head.appendChild(link);
          }
          // Build a CSS family string from the name (best-effort) so it
          // resolves against the loaded font.
          list.push({ family: `'${f.name}', system-ui, sans-serif`, label: f.name });
        }
        setCustomFonts(list);
      } catch (e) {
        console.warn('[editor] Custom fonts fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Canvas zoom + pan (Section 2) ──────────────────────────────────────────
  // zoom = 1.0 means "fit-to-viewport" (i.e. the editor's default). The
  // spread wrapper applies transform: scale(zoom) translate(panX, panY).
  // panX/panY are stored in CSS pixels and reset on view change or zoom-
  // reset.
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const ZOOM_MIN = 0.25;
  const ZOOM_MAX = 4;
  const viewportRef = useRef<HTMLElement>(null);
  // FIT-HEIGHT (2026-07-24): the spread page used to be sized by WIDTH only
  // (min(42vw, 400px) + aspect-ratio) — on short effective viewports (13"
  // Windows laptops at 125–175 % display scale → ~500–740 CSS px tall) the
  // 594 px-tall page overflowed the canvas, hiding the bottom third of every
  // page behind the film strip ("agent can't edit photos"). Measure the real
  // canvas area and cap the page width so the page ALWAYS fits vertically.
  // On tall screens the height cap is looser than 400 px → nothing changes.
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => setCanvasSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-attach when the pages view (which owns viewportRef) mounts.
  }, [editorView]);
  // The pages bar (« Régénérer » + « Modèles » + zoom) is a row of its own
  // above the spread (UI overlap fix 2026-10-18) — its height is measured so
  // the spread is fitted into the room left UNDER it, never behind it.
  const pagesBarRef = useRef<HTMLDivElement | null>(null);
  const [pagesBarH, setPagesBarH] = useState(0);
  useEffect(() => {
    const el = pagesBarRef.current;
    if (!el) { setPagesBarH(0); return; }
    const update = () => setPagesBarH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editorView]);
  // Page width = the old min(42vw, 400px), further capped so page height
  // (width × 297/200) fits the measured canvas minus the pages bar and some
  // breathing room — and, from tablet width up, so the spread stays clear of
  // the prev/next arrows (left/right-4 + 44 px each).
  const pageFitWidthCss = (() => {
    // « Nouvel éditeur mobile »: the album takes ALL the canvas — one page (or
    // the spread) at the printed 200 × 297 ratio, nothing floating over it.
    if (m2) {
      if (canvasSize.w <= 0 || canvasSize.h <= 0) return 'min(86vw, 400px)';
      const across = m2Double ? 2 : 1;
      const byW = (canvasSize.w - 16 - (across - 1) * 3) / across;
      const byH = (canvasSize.h - 16) * (200 / 297);
      return `${Math.max(60, Math.floor(Math.min(byW, byH)))}px`;
    }
    if (canvasSize.h <= 0) return 'min(42vw, 400px)';
    // phones: the menu button (bottom-left) and the zoom control (bottom-right)
    // float over the canvas bottom — keep the centred spread clear of them.
    const phoneReserve = isMobile ? 2 * 64 : 0;
    const fitW = Math.max(isMobile ? 120 : 180, Math.floor((canvasSize.h - pagesBarH - 28 - phoneReserve) * (200 / 297)));
    if (isMobile || canvasSize.w <= 0) return `min(42vw, 400px, ${fitW}px)`;
    const fitByWidth = Math.max(160, Math.floor((canvasSize.w - 2 * (16 + 44 + 10)) / 2));
    return `min(42vw, 400px, ${fitW}px, ${fitByWidth}px)`;
  })();
  // Mobile text-edit zoom: saved canvas view to restore when editing ends, and
  // a flag that lengthens the wrapper's transition for a smooth zoom in/out.
  const editViewRestoreRef = useRef<{ zoom: number; panX: number; panY: number } | null>(null);
  const [zoomAnim, setZoomAnim] = useState(false);
  // Space-bar held → next mouse drag pans the canvas.
  const [spaceHeld, setSpaceHeld] = useState(false);
  // Pinch + two-finger pan tracking for touch devices.
  const touchPinchRef = useRef<{ startDist: number; startZoom: number } | null>(null);
  const touchPanRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);
  // Fix #6 (2026-05-28): one-finger swipe → spread navigation. Recorded on
  // touchstart and committed on touchend if the gesture was short + fast +
  // mostly-horizontal. Reset whenever multi-touch or pinch kicks in so we
  // don't misread a pinch-end as a swipe. `totalSpreadsRef` mirrors the
  // derived totalSpreads value into a ref so the touchend closure always
  // reads the latest count without needing it in deps (avoids TDZ — the
  // const is defined later in the function body).
  const touchSwipeRef = useRef<{ startX: number; startY: number; startTime: number; cancelled: boolean } | null>(null);
  const totalSpreadsRef = useRef(1);
  // Debounce for desktop trackpad horizontal-swipe navigation (one gesture
  // fires many wheel events) — last nav timestamp.
  const lastWheelNavRef = useRef(0);

  const resetZoom = useCallback(() => { setZoom(1); setPanX(0); setPanY(0); }, []);
  const fitToWindow = useCallback(() => { setZoom(1); setPanX(0); setPanY(0); }, []);
  const zoomIn  = useCallback(() => setZoom((z) => Math.min(ZOOM_MAX, +(z * 1.2).toFixed(3))), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(ZOOM_MIN, +(z / 1.2).toFixed(3))), []);

  // While the editor is open, pin the viewport to maximum-scale=1 so the
  // browser NEVER does its own focus-zoom (iOS) — the editor owns all zoom via
  // the canvas transform below, so it can guarantee a smooth zoom-back-out.
  // Restored on unmount. The editor's own pinch (handleViewportTouchStart →
  // setZoom) is JS and unaffected.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (!vp) return;
    const original = vp.getAttribute('content') ?? 'width=device-width, initial-scale=1.0';
    vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    return () => { vp.setAttribute('content', original); };
  }, []);

  // Mobile text edit: smoothly zoom the canvas to the text element on edit
  // start (saving the prior view) and smoothly restore the EXACT prior zoom +
  // pan on Done. Touch only. Deps intentionally just `editingElementId` — the
  // current zoom/pan are read at edit-start to save; restore uses the saved ref.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // the new phone layout has its own (below): the frame follows the keyboard
    if (m2Ref.current) return;
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    if (editingElementId) {
      if (!editViewRestoreRef.current) editViewRestoreRef.current = { zoom, panX, panY };
      // Let focus + the keyboard begin so visualViewport reflects the usable area.
      const t = window.setTimeout(() => {
        const wrapper = document.querySelector('[data-spread="true"]') as HTMLElement | null;
        const node = document.activeElement as HTMLElement | null;
        if (!wrapper || !node || typeof node.getBoundingClientRect !== 'function') return;
        const w = wrapper.getBoundingClientRect();
        const e = node.getBoundingClientRect();
        if (!e.width || !e.height) return;
        const Wcx = w.left + w.width / 2, Wcy = w.top + w.height / 2;
        const ex = e.left + e.width / 2, ey = e.top + e.height / 2;
        const Z0 = zoom;
        const vv = window.visualViewport;
        const vpW = vv?.width ?? window.innerWidth;
        const vpH = vv?.height ?? window.innerHeight;
        const vpLeft = vv?.offsetLeft ?? 0;
        const vpTop = vv?.offsetTop ?? 0;
        // Target zoom: element ~60% of viewport width, capped so we never over-zoom.
        let Z1 = (vpW * 0.6 / Math.max(1, e.width)) * Z0;
        Z1 = Math.max(Z0, Math.min(ZOOM_MAX, Math.min(Z1, Z0 * 2.4)));
        // Element offset from wrapper centre, un-scaled (see transform math).
        const px = (ex - Wcx) / Z0, py = (ey - Wcy) / Z0;
        // Land the element a bit above the visible-area centre (above keyboard).
        const tx = vpLeft + vpW / 2;
        const ty = vpTop + vpH * 0.40;
        const nPanX = tx - (Wcx - panX) - px * Z1;
        const nPanY = ty - (Wcy - panY) - py * Z1;
        setZoomAnim(true);
        setZoom(Z1); setPanX(nPanX); setPanY(nPanY);
        window.setTimeout(() => setZoomAnim(false), 340);
      }, 120);
      return () => window.clearTimeout(t);
    }
    if (editViewRestoreRef.current) {
      const r = editViewRestoreRef.current;
      editViewRestoreRef.current = null;
      setZoomAnim(true);
      setZoom(r.zoom); setPanX(r.panX); setPanY(r.panY);
      const t = window.setTimeout(() => setZoomAnim(false), 340);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingElementId]);

  // ── « Nouvel éditeur mobile »: typing ─────────────────────────────────────
  // The frame is sized to the visible screen while the keyboard is up (root
  // style), the text bar is docked right above it, and the album zooms so the
  // text being typed sits in the middle of what is left — re-placed whenever
  // that room changes (keyboard sliding in, the bar wrapping). « Terminé »
  // puts the view back EXACTLY where it was (zoom, pan, page).
  const m2ViewRef = useRef({ zoom, panX, panY });
  m2ViewRef.current = { zoom, panX, panY };
  const m2EditRestoreRef = useRef<{ zoom: number; panX: number; panY: number } | null>(null);
  // what is being typed into: a page / intro element, or a cover text zone
  const m2EditKey = editingElementId ?? (editingCoverZone != null ? `cover:${editingCoverZone}` : null);
  useEffect(() => {
    if (!m2) return;
    if (!m2EditKey) {
      const r = m2EditRestoreRef.current;
      if (!r) return;
      m2EditRestoreRef.current = null;
      setZoomAnim(true);
      setZoom(r.zoom); setPanX(r.panX); setPanY(r.panY);
      const t = window.setTimeout(() => setZoomAnim(false), 340);
      return () => window.clearTimeout(t);
    }
    if (!m2EditRestoreRef.current) m2EditRestoreRef.current = { ...m2ViewRef.current };
    let raf = 0;
    const place = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const canvas = viewportRef.current?.getBoundingClientRect();
        // the zoomed surface: the spread, the cover or the intro (all [data-m2-zoomable])
        const wrapper = document.querySelector('[data-m2-zoomable]') as HTMLElement | null;
        const node = (editingElementId
          ? document.querySelector(`[data-element-id="${CSS.escape(editingElementId)}"]`)
          : document.querySelector('[data-cover-spread] [contenteditable="true"]')?.parentElement) as HTMLElement | null;
        if (!canvas || !wrapper || !node || canvas.height < 40) return;
        const w = wrapper.getBoundingClientRect();
        const e = node.getBoundingClientRect();
        if (!e.width || !e.height) return;
        // the transform as DRAWN right now (it may be mid-animation): scale + pan
        let Z0 = m2ViewRef.current.zoom, pX = m2ViewRef.current.panX, pY = m2ViewRef.current.panY;
        try {
          const m = new DOMMatrixReadOnly(getComputedStyle(wrapper).transform);
          if (m.a > 0) { Z0 = m.a; pX = m.e; pY = m.f; }
        } catch { /* 'none' → the state values */ }
        const restore = m2EditRestoreRef.current ?? { zoom: 1 };
        // the text box fills the width of the free room (≈ 92 %) and never
        // more than ~80 % of its height — whatever the keyboard leaves
        let Z1 = Math.min((canvas.width * 0.92) / (e.width / Z0), (canvas.height * 0.8) / (e.height / Z0));
        // …and the letters being typed show at ≥ 16 px (the text keeps its
        // print size — only the view zooms)
        const ed = (node.querySelector('[contenteditable="true"]') ?? node) as HTMLElement;
        const fontPx = parseFloat(getComputedStyle(ed).fontSize) || 16;
        Z1 = Math.max(Z1, 16 / fontPx);
        Z1 = Math.max(restore.zoom, Math.min(ZOOM_MAX, Z1));
        const Wcx = w.left + w.width / 2, Wcy = w.top + w.height / 2;
        const px = (e.left + e.width / 2 - Wcx) / Z0, py = (e.top + e.height / 2 - Wcy) / Z0;
        const nPanX = canvas.left + canvas.width / 2 - (Wcx - pX) - px * Z1;
        const nPanY = canvas.top + canvas.height * 0.5 - (Wcy - pY) - py * Z1;
        const cur = m2ViewRef.current;
        if (Math.abs(Z1 - cur.zoom) < 0.01 && Math.abs(nPanX - cur.panX) < 1 && Math.abs(nPanY - cur.panY) < 1) return;
        setZoomAnim(true);
        setZoom(Z1); setPanX(nPanX); setPanY(nPanY);
        window.setTimeout(() => setZoomAnim(false), 340);
      });
    };
    const t1 = window.setTimeout(place, 120);
    const t2 = window.setTimeout(place, 480); // the keyboard has slid in by now
    const el = viewportRef.current;
    const ro = el ? new ResizeObserver(place) : null;
    if (el && ro) ro.observe(el);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); cancelAnimationFrame(raf); ro?.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // canvasSize: the keyboard / a bar changed the room → the page re-fits → place again
  }, [m2EditKey, m2, canvasSize.w, canvasSize.h]);
  // the zoom belongs to one view: a new view (cover ⇄ intro ⇄ pages) starts fitted
  useEffect(() => {
    if (!m2) return;
    m2EditRestoreRef.current = null;
    setZoom(1); setPanX(0); setPanY(0);
  }, [m2, editorView]);

  // Wheel zoom (Ctrl/Cmd + wheel) — keeps the cursor anchor steady so
  // zooming feels natural. Bound on the viewport via a useEffect because
  // React's onWheel is passive-by-default and can't preventDefault.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) {
        // Desktop trackpad: a horizontal two-finger swipe (deltaX dominant, no
        // modifier) navigates spreads — the trackpad equivalent of the touch
        // swipe. Debounced so one gesture = one spread. Left-swipe → next.
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 30) {
          e.preventDefault();
          const now = Date.now();
          if (now - lastWheelNavRef.current > 500) {
            lastWheelNavRef.current = now;
            if (e.deltaX > 0) {
              setCurrentSpread((s) => Math.min(totalSpreadsRef.current - 1, s + 1));
            } else {
              setCurrentSpread((s) => Math.max(0, s - 1));
            }
          }
        }
        return;
      }
      // BROWSER ZOOM UNBLOCKED (2026-07-24): Ctrl/Cmd+wheel used to be
      // captured here for CANVAS zoom, which silently disabled the browser's
      // own page zoom — on high-scale laptops (Windows 125–150 %) users
      // couldn't zoom out to make the editor fit ("browser zoom doesn't
      // work"). Canvas zoom stays available via the on-screen −/+ pill and
      // touch pinch; Ctrl+wheel belongs to the browser again.
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as any);
    // editorView: <main ref={viewportRef}> only exists on the 'pages' view —
    // re-attach when it mounts (the old [panX, panY] deps masked this).
  }, [editorView]);

  // Keyboard: Cmd/Ctrl + = / - / 0 / Shift+H
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (!typing && (e.code === 'Space') && !e.repeat) { setSpaceHeld(true); }
      if (e.repeat || typing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      // BROWSER ZOOM UNBLOCKED (2026-07-24): Ctrl+= / Ctrl+- / Ctrl+0 are no
      // longer intercepted for canvas zoom — they reach the browser so users
      // on small/high-scale screens can zoom the whole page. Canvas zoom =
      // on-screen −/+ pill; Shift+H (fit) kept.
      if (k === 'h' && e.shiftKey) { e.preventDefault(); fitToWindow(); }
    };
    const onKeyUp = (e: KeyboardEvent) => { if (e.code === 'Space') setSpaceHeld(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [fitToWindow]);

  // Touch pinch (zoom) + two-finger pan
  const handleViewportTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Multi-touch → never a swipe; cancel any pending swipe candidate.
      if (touchSwipeRef.current) touchSwipeRef.current.cancelled = true;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchPinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: zoom };
      touchPanRef.current = {
        startX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        startY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        startPanX: panX,
        startPanY: panY,
      };
    } else if (e.touches.length === 1) {
      // Fix #6 (2026-05-28): one-finger swipe candidate. Only count it as
      // a swipe on touchend if it stays single-finger, lands in < 350ms,
      // travels > 60px horizontally and < 40px vertically.
      const t = e.touches[0];
      if (m2Ref.current) {
        // « Nouvel éditeur mobile »: photos are NOT excluded — on a one-page
        // view they cover most of the page, and a quick sideways flick must
        // still turn it. Only things that own the gesture are: a photo in
        // edit mode, a text / shape / free photo, a control, the page strip.
        const target = e.target as HTMLElement | null;
        // a model's photo window (not selected) lets a flick through; texts,
        // stickers, free photos and the selected window own the gesture
        const elId = target?.closest('[data-element-id]')?.getAttribute('data-element-id') ?? null;
        const owned = !!target?.closest(
          '[data-pan-mode="true"], [data-spread-idx], [data-editor-toolbar], button, a, input, textarea, select, [contenteditable="true"]'
        ) || (!!elId && !m2SwipeableElRef.current(elId));
        m2TapRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), plain: !owned && !target?.closest('[data-slot-drop-target]') };
        if (owned) { touchSwipeRef.current = null; m2PanRef.current = null; return; }
        if (zoom > 1.05) {
          // zoomed in: one finger moves the album (a swipe would fight it)
          touchSwipeRef.current = null;
          m2PanRef.current = target?.closest('[data-slot-drop-target]')
            ? null
            : { x: t.clientX, y: t.clientY, px: panX, py: panY, moved: false };
          return;
        }
        m2PanRef.current = null;
        touchSwipeRef.current = { startX: t.clientX, startY: t.clientY, startTime: Date.now(), cancelled: false };
        return;
      }
      // Touches landing on interactive surfaces (slot drop targets, the
      // PhotoSlot wrapper, the page thumbnails, buttons) are NOT swipe
      // candidates — those gestures belong to the element under the
      // finger. We test the closest ancestor for known interactive marks.
      const target = e.target as HTMLElement | null;
      const insideInteractive = !!target?.closest(
        '[data-slot-drop-target], [data-pan-mode], [data-spread-idx], button, a, input, textarea, select, [contenteditable="true"]'
      );
      if (insideInteractive) {
        touchSwipeRef.current = null;
      } else {
        touchSwipeRef.current = {
          startX: t.clientX,
          startY: t.clientY,
          startTime: Date.now(),
          cancelled: false,
        };
      }
    }
  }, [zoom, panX, panY]);

  const handleViewportTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // the new layout's canvas is touch-action:none — no (passive, logged) preventDefault there
      if (!m2Ref.current) e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      if (touchPinchRef.current) {
        const ratio = dist / touchPinchRef.current.startDist;
        const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, touchPinchRef.current.startZoom * ratio));
        setZoom(next);
      }
      if (touchPanRef.current) {
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        setPanX(touchPanRef.current.startPanX + (cx - touchPanRef.current.startX));
        setPanY(touchPanRef.current.startPanY + (cy - touchPanRef.current.startY));
      }
    } else if (e.touches.length === 1 && m2PanRef.current) {
      const t = e.touches[0];
      const p = m2PanRef.current;
      const dx = t.clientX - p.x;
      const dy = t.clientY - p.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) p.moved = true;
      setPanX(p.px + dx);
      setPanY(p.py + dy);
    } else if (e.touches.length === 1 && touchSwipeRef.current && !touchSwipeRef.current.cancelled) {
      // Watch for "too vertical" or "too slow" — bail out so we don't
      // hijack a normal vertical scroll. We don't preventDefault here.
      const t = e.touches[0];
      const dx = t.clientX - touchSwipeRef.current.startX;
      const dy = t.clientY - touchSwipeRef.current.startY;
      if (Math.abs(dy) > 40 && Math.abs(dy) > Math.abs(dx)) {
        touchSwipeRef.current.cancelled = true;
      }
    }
  }, []);

  const handleViewportTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      touchPinchRef.current = null;
      touchPanRef.current = null;
    }
    // Fix #6 (2026-05-28): commit swipe if the gesture matched the
    // short + fast + horizontal profile. Use the last lifted finger
    // via `changedTouches`. Setting currentSpread directly (clamped)
    // ensures we never overshoot the album.
    // « Nouvel éditeur mobile »: double-tap (outside photos, texts, controls)
    // = fit the album back to the screen. The click that follows is swallowed
    // so the page's own double-tap (page options) does not fire on top.
    if (m2Ref.current && e.touches.length === 0) {
      const t = e.changedTouches[0];
      const start = m2TapRef.current;
      const panned = m2PanRef.current?.moved ?? false;
      m2PanRef.current = null;
      m2TapRef.current = null;
      const isTap = !!start && !panned && Date.now() - start.t < 300
        && Math.abs(t.clientX - start.x) < 12 && Math.abs(t.clientY - start.y) < 12;
      if (isTap && start!.plain) {
        const last = m2LastTapRef.current;
        const v = m2ViewRef.current;
        const fitted = Math.abs(v.zoom - 1) < 0.01 && Math.abs(v.panX) < 1 && Math.abs(v.panY) < 1;
        if (!fitted && last && Date.now() - last.t < 320 && Math.abs(last.x - t.clientX) < 40 && Math.abs(last.y - t.clientY) < 40) {
          m2LastTapRef.current = null;
          m2SuppressClickUntilRef.current = Date.now() + 450;
          setZoomAnim(true);
          setZoom(1); setPanX(0); setPanY(0);
          window.setTimeout(() => setZoomAnim(false), 340);
        } else {
          m2LastTapRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
        }
      }
      if (touchSwipeRef.current && !touchSwipeRef.current.cancelled) {
        const dx = t.clientX - touchSwipeRef.current.startX;
        const dy = t.clientY - touchSwipeRef.current.startY;
        const dt = Date.now() - touchSwipeRef.current.startTime;
        // sideways, clearly horizontal; a flick that starts on a photo must be quick
        const startedOnPhoto = start ? !start.plain : false;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && (!startedOnPhoto || dt < 400)) {
          m2NavRef.current?.(dx < 0 ? 1 : -1);
        }
      }
      touchSwipeRef.current = null;
      return;
    }
    if (e.touches.length === 0 && touchSwipeRef.current && !touchSwipeRef.current.cancelled) {
      const t = e.changedTouches[0];
      const dx = t.clientX - touchSwipeRef.current.startX;
      const dy = t.clientY - touchSwipeRef.current.startY;
      const dt = Date.now() - touchSwipeRef.current.startTime;
      // Two swipe directions are accepted: horizontal (left/right) keeps the
      // book-turn metaphor working on tablets, vertical (up/down) was the
      // customer's explicit ask for one-thumb phone use. Whichever axis
      // dominates by ≥1.5× wins; equal motion is ignored as a smudge.
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      const isQuick = dt < 350;
      const horizontalDominant = absX > absY * 1.5;
      const verticalDominant   = absY > absX * 1.5;
      // Spec (2026-06-08): a horizontal swipe of >40px on the page background
      // navigates spreads, regardless of SPEED (no time gate) — only horizontal
      // dominance matters, so a vertical scroll still bails (cancelled in
      // touchmove when dy>40). Touches starting on a photo / pan-mode / button
      // are already excluded in touchstart, so image pan/drag is never hijacked.
      // Swipe LEFT (dx<0) → next spread, swipe RIGHT (dx>0) → previous.
      if (absX > 40 && horizontalDominant) {
        if (dx < 0) {
          setCurrentSpread((s) => Math.min(totalSpreadsRef.current - 1, s + 1));
        } else {
          setCurrentSpread((s) => Math.max(0, s - 1));
        }
      } else if (isQuick && absY > 60 && verticalDominant) {
        // Up = next spread (book metaphor: "scrolling deeper into the book"),
        // Down = previous. Matches the user's spec; flip the comparator if
        // you'd rather match natural-scrolling direction.
        if (dy < 0) {
          setCurrentSpread((s) => Math.min(totalSpreadsRef.current - 1, s + 1));
        } else {
          setCurrentSpread((s) => Math.max(0, s - 1));
        }
      }
    }
    if (e.touches.length === 0) {
      touchSwipeRef.current = null;
    }
  }, []);

  // Exit slot edit mode whenever the user clicks outside the active pan-mode
  // slot. The slot itself stamps `data-pan-mode="true"` so a single ancestor
  // check tells us whether the pointer landed inside or outside. Also
  // listens for Escape so the keyboard works the same way.
  useEffect(() => {
    if (!panModeKey) return;
    const onPointer = (ev: PointerEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-pan-mode="true"]')) return;
      // The scale/rotate/swap pill is createPortal'd to <body>, so it lives
      // OUTSIDE the slot's [data-pan-mode] subtree. Without this guard a tap on
      // any of its buttons reads as "outside" and closes edit mode before the
      // button's onClick runs (the pill unmounts) — so nothing worked. Treat the
      // portaled toolbar as "inside" too. (mirrors the cover-design pattern.)
      if (target?.closest('[data-editor-toolbar="true"]')) return;
      setPanModeKey(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setPanModeKey(null);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [panModeKey]);

  // Same pattern for the cover design selection — clicks anywhere outside
  // the artwork itself or its floating toolbar deselect the design and hide
  // the −/100%/+ chip.
  useEffect(() => {
    if (!coverDesignSelected) return;
    const onPointer = (ev: PointerEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-cover-design="true"]')) return;
      if (target?.closest('[data-cover-design-toolbar="true"]')) return;
      setCoverDesignSelected(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setCoverDesignSelected(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [coverDesignSelected]);

  // Dismiss the single-tap slot popup (SelectedActionsBar: "2× pour éditer ·
  // Remplacer · ✕") when the customer clicks anywhere outside it. Same proven
  // shape as the panModeKey + coverDesignSelected handlers above — the slot
  // popup was the one selection mode missing an outside-click dismiss.
  // "Inside" (no dismiss) = the portaled bar itself (data-editor-toolbar) or any
  // slot (data-slot-drop-target): tapping another photo lets that slot's own
  // handler switch the selection, and tapping the same photo lets it toggle/
  // enter edit. Everything else — cream canvas, page background, top toolbar —
  // dismisses. Gated on `!panModeKey` so free-transform (double-tap) and its
  // Terminé flow are left entirely to their own handler, untouched.
  useEffect(() => {
    if (!selectedSlotKey || panModeKey) return;
    const onPointer = (ev: PointerEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-editor-toolbar="true"]')) return;
      if (target?.closest('[data-slot-drop-target]')) return;
      setSelectedSlotKey(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setSelectedSlotKey(null);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [selectedSlotKey, panModeKey]);

  // Space + drag pan (desktop). Mouse-down only fires when Space is held.
  const handleViewportMouseDown = useCallback((e: React.MouseEvent) => {
    if (!spaceHeld) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startPanX = panX, startPanY = panY;
    const onMove = (ev: MouseEvent) => {
      setPanX(startPanX + (ev.clientX - startX));
      setPanY(startPanY + (ev.clientY - startY));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [spaceHeld, panX, panY]);

  // ── Undo/Redo History (rewritten — auto-records EVERY state change) ────────
  // Snapshots cover every editable bit of the album: layout, photos,
  // per-slot pan, nested page elements (text/shape/extra-photo with their
  // text/font/color/position/opacity), AND every cover edit (texts, zone
  // overrides, extra zones, spine config, per-face background colors,
  // back-cover photos, image-hide toggle). A debounced effect captures
  // changes as one "action" — rapid drags collapse into a single undo
  // step. Stack capped at 50 entries.
  type HistorySnapshot = {
    pages: LayoutPage[];
    unassigned: string[];
    positions: Record<string, { x: number; y: number; scale?: number }>;
    coverDestText: string;
    coverYearText: string;
    coverCustomTexts: Record<number, string>;
    coverZoneOverrides: Record<number, TextZoneOverride>;
    coverExtraZones: ExtraTextZone[];
    spineConfig: SpineConfig;
    backCoverBgColor: string | null;
    frontCoverBgColor: string | null;
    hideFrontCoverImage: boolean;
    coverDesignTransform: CoverDesignTransform;
    backCoverImages: BackCoverImage[];
    coverElements: CoverElement[];
  };
  const [undoStack, setUndoStack] = useState<HistorySnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<HistorySnapshot[]>([]);
  const skipHistoryRef = useRef(false);              // suppress capture during undo/redo restore
  const lastSnapshotKeyRef = useRef<string>('');     // serialised previous state
  const lastSnapshotRef = useRef<HistorySnapshot | null>(null);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper that builds a deep-cloned snapshot of every state slice the
  // editor can modify. Centralised so undo/redo + the auto-recorder can't
  // drift apart on which fields they capture.
  const buildSnapshot = useCallback((): HistorySnapshot => ({
    pages: JSON.parse(JSON.stringify(localPages)),
    unassigned: [...localUnassigned],
    positions: JSON.parse(JSON.stringify(slotPositions)),
    coverDestText,
    coverYearText,
    coverCustomTexts: { ...coverCustomTexts },
    coverZoneOverrides: JSON.parse(JSON.stringify(coverZoneOverrides)),
    coverExtraZones: JSON.parse(JSON.stringify(coverExtraZones)),
    spineConfig: { ...spineConfig },
    backCoverBgColor,
    frontCoverBgColor,
    hideFrontCoverImage,
    coverDesignTransform: { ...coverDesignTransform },
    backCoverImages: backCoverImages.map((i) => ({ ...i })),
    coverElements: coverElements.map((e) => ({ ...e })),
  }), [
    localPages, localUnassigned, slotPositions,
    coverDestText, coverYearText, coverCustomTexts,
    coverZoneOverrides, coverExtraZones, spineConfig,
    backCoverBgColor, frontCoverBgColor, hideFrontCoverImage,
    coverDesignTransform, backCoverImages, coverElements,
  ]);

  // Apply a snapshot back into the editor (used by both undo + redo). The
  // skipHistoryRef guard is released by the caller after React commits.
  const restoreSnapshot = useCallback((s: HistorySnapshot) => {
    setLocalPages(s.pages);
    setLocalUnassigned(s.unassigned);
    setSlotPositions(s.positions);
    setCoverDestText(s.coverDestText);
    setCoverYearText(s.coverYearText);
    setCoverCustomTexts(s.coverCustomTexts);
    setCoverZoneOverrides(s.coverZoneOverrides);
    setCoverExtraZones(s.coverExtraZones);
    setSpineConfig(s.spineConfig);
    setBackCoverBgColor(s.backCoverBgColor);
    setFrontCoverBgColor(s.frontCoverBgColor);
    setHideFrontCoverImage(s.hideFrontCoverImage);
    setCoverDesignTransform(s.coverDesignTransform);
    setBackCoverImages(s.backCoverImages);
    setCoverElements(s.coverElements ?? []);
  }, []);

  // Auto-record snapshots on any state change. Debounced so a drag or
  // a typing burst counts as ONE undoable action.
  useEffect(() => {
    if (skipHistoryRef.current) return;
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = setTimeout(() => {
      const key = JSON.stringify({
        p: localPages, u: localUnassigned, s: slotPositions,
        cd: coverDestText, cy: coverYearText, cc: coverCustomTexts,
        co: coverZoneOverrides, ce: coverExtraZones,
        sp: spineConfig,
        bb: backCoverBgColor, fb: frontCoverBgColor, hf: hideFrontCoverImage,
        dt: coverDesignTransform,
        bi: backCoverImages.map((i) => i.storage_path + (i.full_page ? '*' : '')),
        el: coverElements,
      });
      if (key === lastSnapshotKeyRef.current) return;
      // First-ever snapshot: establish baseline without pushing anything
      if (lastSnapshotKeyRef.current === '') {
        lastSnapshotKeyRef.current = key;
        lastSnapshotRef.current = buildSnapshot();
        return;
      }
      // Push the PREVIOUS state (what user is undoing back to)
      const previous = lastSnapshotRef.current;
      if (previous) {
        setUndoStack((prev) => {
          const trimmed = prev.length >= 50 ? prev.slice(-49) : prev;
          return [...trimmed, previous];
        });
        setRedoStack([]);
      }
      lastSnapshotRef.current = buildSnapshot();
      lastSnapshotKeyRef.current = key;
    }, 300);
    return () => {
      if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    };
  }, [
    localPages, localUnassigned, slotPositions,
    coverDestText, coverYearText, coverCustomTexts,
    coverZoneOverrides, coverExtraZones, spineConfig,
    backCoverBgColor, frontCoverBgColor, hideFrontCoverImage,
    coverDesignTransform, backCoverImages, coverElements, buildSnapshot,
  ]);

  const snapshotKey = useCallback((s: HistorySnapshot) => JSON.stringify({
    p: s.pages, u: s.unassigned, s: s.positions,
    cd: s.coverDestText, cy: s.coverYearText, cc: s.coverCustomTexts,
    co: s.coverZoneOverrides, ce: s.coverExtraZones,
    sp: s.spineConfig,
    bb: s.backCoverBgColor, fb: s.frontCoverBgColor, hf: s.hideFrontCoverImage,
    dt: s.coverDesignTransform,
    bi: s.backCoverImages.map((i) => i.storage_path + (i.full_page ? '*' : '')),
    el: s.coverElements ?? [],
  }), []);

  const handleUndo = useCallback(() => {
    // Fix H (2026-05-28): note undo for the stuck-user heuristic.
    stuck.noteUndo();
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const rest = prev.slice(0, -1);
      const currentSnapshot = buildSnapshot();
      setRedoStack((redo) => [...redo, currentSnapshot]);
      // Suppress the auto-recorder while we restore.
      skipHistoryRef.current = true;
      restoreSnapshot(last);
      lastSnapshotRef.current = last;
      lastSnapshotKeyRef.current = snapshotKey(last);
      // Release the guard after React commits the new state.
      setTimeout(() => { skipHistoryRef.current = false; }, 350);
      return rest;
    });
  }, [buildSnapshot, restoreSnapshot, snapshotKey, stuck]);

  const handleRedo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const rest = prev.slice(0, -1);
      const currentSnapshot = buildSnapshot();
      setUndoStack((undo) => [...undo, currentSnapshot]);
      skipHistoryRef.current = true;
      restoreSnapshot(last);
      lastSnapshotRef.current = last;
      lastSnapshotKeyRef.current = snapshotKey(last);
      setTimeout(() => { skipHistoryRef.current = false; }, 350);
      return rest;
    });
  }, [buildSnapshot, restoreSnapshot, snapshotKey]);

  // ── Clipboard for copy / paste / duplicate ─────────────────────────────────
  // Holds the last-copied element so Cmd+V pastes onto the current page
  // with a small offset. Survives across selection changes.
  const clipboardRef = useRef<PageElement | null>(null);

  const findElementById = useCallback((id: string): { pageNumber: number; element: PageElement } | null => {
    for (const page of localPages) {
      const found = page.elements?.find((el) => el.id === id);
      if (found) return { pageNumber: page.page, element: found };
    }
    return null;
  }, [localPages]);

  const handleCopy = useCallback(() => {
    if (!selectedElementId) return;
    const found = findElementById(selectedElementId);
    if (!found) return;
    clipboardRef.current = JSON.parse(JSON.stringify(found.element));
  }, [selectedElementId, findElementById]);

  const handlePaste = useCallback(() => {
    const src = clipboardRef.current;
    if (!src) return;
    // Paste onto whichever page is currently visible. If a page is
    // selected via the active element, paste there; else first page.
    const currentPageNum = selectedElementId
      ? (findElementById(selectedElementId)?.pageNumber ?? localPages[0]?.page)
      : localPages[0]?.page;
    if (currentPageNum == null) return;
    const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const offset = 3; // percent units — matches existing 10px-ish nudge
    const pasted: PageElement = {
      ...JSON.parse(JSON.stringify(src)),
      id: newId,
      x: Math.min(95, (src.x ?? 0) + offset),
      y: Math.min(95, (src.y ?? 0) + offset),
    };
    setLocalPages((pages) => pages.map((p) =>
      p.page === currentPageNum
        ? { ...p, elements: [...(p.elements ?? []), pasted] }
        : p
    ));
    setSelectedElementId(newId);
  }, [selectedElementId, findElementById, localPages]);

  const handleDuplicate = useCallback(() => {
    if (!selectedElementId) return;
    handleCopy();
    handlePaste();
  }, [selectedElementId, handleCopy, handlePaste]);

  // Keyboard shortcuts: Ctrl/Cmd + Z / Shift+Z / Y / C / V / D
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't hijack shortcuts while user is typing in an input/textarea
      const target = e.target as HTMLElement | null;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if (isTyping || isTypingTarget(target)) return;
      // Delete / Backspace — the selected element(s) the client added herself
      // (lib/elementDeleteKey.ts). Never while a text is being edited.
      if (isDeleteKey(e) && !editingElementId) {
        const ids = selectedElementIds.size ? [...selectedElementIds] : selectedElementId ? [selectedElementId] : [];
        const gone = keyDeletable(localPages, ids);
        if (gone.length) {
          e.preventDefault();
          const byPage = new Map<number, Set<string>>();
          for (const g of gone) byPage.set(g.pageNumber, (byPage.get(g.pageNumber) ?? new Set()).add(g.id));
          setLocalPages((prev) => prev.map((p) => {
            const drop = byPage.get(p.page);
            return drop ? { ...p, elements: (p.elements ?? []).filter((el) => !drop.has(el.id)) } : p;
          }));
          setSelectedElementId(null);
          setSelectedElementIds(new Set());
          setEditingElementId(null);
        }
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if (key === 'z' && e.shiftKey) { e.preventDefault(); handleRedo(); }
      else if (key === 'y') { e.preventDefault(); handleRedo(); }
      else if (key === 'c') { e.preventDefault(); handleCopy(); }
      else if (key === 'v') { e.preventDefault(); handlePaste(); }
      else if (key === 'd') { e.preventDefault(); handleDuplicate(); }
      else if (key === 'a') {
        // Cmd+A — select every element across the active spread. Useful
        // alongside the new shift+click multi-select for whole-page tweaks.
        e.preventDefault();
        const ids = new Set<string>();
        for (const p of localPages) for (const el of p.elements ?? []) ids.add(el.id);
        setSelectedElementIds(ids);
        const first = ids.values().next().value;
        setSelectedElementId(first ?? null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo, handleCopy, handlePaste, handleDuplicate, editingElementId, selectedElementId, selectedElementIds, localPages]);

  // ── Save status tracking ───────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Last-writer guard (2026-07-23): the last_saved_at of the state THIS editor
  // loaded (then of each save response). Echoed on every autosave; the worker
  // 409s when the stored row is newer (someone else saved since) instead of
  // letting us overwrite their edits. On conflict we STOP autosaving and show
  // the banner below — reload = pick up the newest version.
  const sessionSavedAtRef = useRef<string | null>(null);
  // The album as the server last showed it (load, then every save) — lets a
  // 409 that only moved the timestamp be told apart from a real conflict.
  // lib/staleWriteRecovery.ts (#1173 test order, 2026-10-06).
  const knownContentRef = useRef<string | null>(null);
  const staleRetriedRef = useRef(false);
  const [editConflict, setEditConflict] = useState(false);
  const editConflictRef = useRef(false);
  // The conflict banner is a full-width bar pinned over the toolbar, so it can
  // sit in the way once you've read it. Dismissing hides the BAR ONLY — the
  // conflict itself is unchanged and autosave stays stopped, because someone
  // else's newer work is on the server and overwriting it would lose their
  // edits. Any NEW conflict re-shows the bar (see the reset below).
  const [editConflictDismissed, setEditConflictDismissed] = useState(false);
  useEffect(() => { if (!editConflict) setEditConflictDismissed(false); }, [editConflict]);
  // Checkpoint-history panel (operators only — button gated on isAdmin).
  const [historyOpen, setHistoryOpen] = useState(false);
  // « ✍️ Légendes IA » (staff, #WA-269649): the model's captions, shown before applying.
  const [captionsOpen, setCaptionsOpen] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // ── Album project name (editable) ──────────────────────────────────────────
  const [projectName, setProjectName] = useState('');
  const [editingProjectName, setEditingProjectName] = useState(false);

  // ── Submission validation ──────────────────────────────────────────────────
  const [showValidation, setShowValidation] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // ⚠️ NOT THE SAME LIST AS `validationErrors`, and that is the point. An
  // error BLOCKS; a warning is said once and then obeyed. Owner, 2026-09-22:
  // « i need to be able to regenerate without a text if i want to ». A missing
  // cover title used to sit in the blocking list, so an album he deliberately
  // wanted with no words on the front could not be submitted OR regenerated —
  // the same rule the WhatsApp side already dropped (albumScoreboard
  // `titleSettled`: words, or a recorded decision to print none).
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Cancellation tracking for the submit flow. Closing the modal with the
  // X button while the PDFs are still rendering used to leave a 1.5s
  // setTimeout primed for redirect — the customer would land on the
  // checkout page even after explicitly cancelling. These refs let the X
  // handler revoke the pending redirect AND tell the in-flight Promise
  // chain to abort after its current await without firing the redirect.
  const submitCancelledRef = useRef(false);
  const submitRedirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The order row returned by /submit — has the recomputed total_price +
  // persisted extra_pages. Both the auto-redirect and the success-modal
  // "Continuer vers le paiement" button hydrate the checkout URL from this.
  const [submittedOrder, setSubmittedOrder] = useState<Order | null>(null);
  const [pdfProgress, setPdfProgress] = useState<PdfGenProgress | null>(null);
  const MIN_PHOTOS = 20;

  // ── Upload modal (shown when no photos/layout, or when user clicks "add photos") ──
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ name: string; progress: number; status: 'pending' | 'converting' | 'uploading' | 'done' | 'error'; error?: string }[]>([]);
  // The files of the last upload that failed — « Réessayer » sends ONLY them
  // again (#1227, 2026-10-03: 35 « Erreur » with no reason and no retry).
  const failedUploadsRef = useRef<File[]>([]);
  // Photos added in the current add-photos modal session — rendered as a square
  // thumbnail grid in the modal once they land (bug 3 / "show them in the grid").
  const [lastAddedPhotoIds, setLastAddedPhotoIds] = useState<string[]>([]);
  const [isGeneratingLayout, setIsGeneratingLayout] = useState(false);
  // Premium AI-Magic UX (Fix #2):
  //   * `aiError` — surfaced as a gold inline toast near the gold button,
  //     auto-dismissed after 5 s. Replaces the silent console.error so the
  //     customer knows when /ai/layout fails.
  //   * `aiRevealKey` — bumps on a successful regen. The current spread
  //     keys off it to replay the gold-cascade fade-in (60 ms stagger:
  //     left page first, right page second), so the layout doesn't just
  //     "appear" — it reveals.
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiRevealKey, setAiRevealKey] = useState(0);
  useEffect(() => {
    if (!aiError) return;
    const t = setTimeout(() => setAiError(null), 5000);
    return () => clearTimeout(t);
  }, [aiError]);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // iOS picker-open feedback. iOS withholds the input's `change` event until
  // it has finished downloading every selected iCloud original — many seconds
  // for a big selection. Arm the inline spinner BEFORE the picker opens so the
  // modal never looks frozen. Cleared on `change` / `cancel` / safety timeout.
  // iOS-only.
  const [uploadPicking, setUploadPicking] = useState(false);
  useEffect(() => {
    if (!uploadPicking) return;
    const el = uploadFileInputRef.current;
    const clear = () => setUploadPicking(false);
    // Safety net 1 — the standard `cancel` event (modern browsers).
    el?.addEventListener('cancel', clear);
    // Safety net 2 — older iOS/Safari fire NO `cancel`. When focus / the tab
    // returns and the picker is still armed (no `change` fired), the user
    // dismissed it. Deferred ~300ms so a real selection (whose onChange clears
    // it) wins the race. This is the fix for the "picker stays stuck open" bug.
    const onReturn = () => { window.setTimeout(() => setUploadPicking((v) => (v ? false : v)), 300); };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    // Safety net 3 — hard backstop. Was 90s (modal looked frozen forever); 8s
    // covers even a slow iCloud export.
    const t = window.setTimeout(clear, 8000);
    return () => {
      el?.removeEventListener('cancel', clear);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
      window.clearTimeout(t);
    };
  }, [uploadPicking]);

  // Visible feedback when a selection produces nothing to upload (empty
  // FileList / iCloud-placeholder zero-byte files / non-image / dedup hit).
  // Replaces the old silent `return`s that left the customer staring at an
  // unchanged modal. See lib/uploadFileFilter.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);

  // ── Logo from settings ─────────────────────────────────────────────────────
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // Print bleed ("fond perdu") in mm, read from admin print settings and
  // forwarded to the interior-PDF generator. Defaults to 3 mm so albums ship
  // print-ready even before the settings fetch resolves.
  const [bleedMm, setBleedMm] = useState<number>(3);
  // (BackCoverImage state moved earlier in the component so the history-
  // snapshot hook can include it. Original location kept here as a no-op.)
  const backCoverFileRef = useRef<HTMLInputElement>(null);

  // ── Categories for cover library (Themes tab) ──────────────────────────────
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
    staleTime: 60_000,
  });
  const categories = categoriesData?.categories ?? [];

  // ── Save tracking ──────────────────────────────────────────────────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  // Auto-retry a FAILED autosave (transient network/5xx) so an edit is never
  // silently dropped — the "she lost her edits" guard. Reset to 0 on success;
  // exponential backoff, capped attempts (a new edit re-arms a fresh save
  // anyway). doSaveRef points at the latest doSave closure so the retry always
  // persists the CURRENT state, not a stale snapshot.
  const saveRetryRef = useRef(0);
  const saveRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doSaveRef = useRef<(opts?: { keepalive?: boolean }) => void>(() => {});
  /** A save was requested while another was in flight — replay it on release,
   *  instead of silently dropping the edits made during that window. */
  const saveQueuedRef = useRef(false);
  // Set to true once the session has been loaded from the server and applied
  // to local state. Blocks autosave from racing in and overwriting saved
  // data (back-cover photos, zone overrides, etc.) with empty initial state.
  const hasHydratedRef = useRef(false);

  // ── AI layout pre-warm ─────────────────────────────────────────────────────
  // Kick off /ai/layout in the background as soon as photos are analyzed and
  // no layout exists yet, so that "Faire avec l'IA" returns instantly. The
  // result is held in a ref and consumed by handleGenerateFromEditor.
  const prewarmRef = useRef<Promise<{ layout: LayoutPlan }> | null>(null);
  const prewarmKeyRef = useRef<string | null>(null);

  useEffect(() => {
    document.title = "Editeur d'album — Storiz";
  }, []);

  // ─── Fetch order ────────────────────────────────────────────────────────

  const { data: orderData } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });

  const order = orderData?.order;
  m2OrderKnownRef.current = !!order;
  m2ViewOnlyRef.current = m2 && isViewOnlyAlbum(order?.status, profileRole);
  useEffect(() => {
    if (!m2 || !shopifyOrderId || isAdmin || !order) return;
    if (isViewOnlyAlbum(order.status, profileRole)) return;
    const tag = `${shopifyOrderId}#${albumIndex}`;
    if (flowStepMarkedRef.current === tag) return;
    flowStepMarkedRef.current = tag;
    sessionsApi.advanceFlowStep(shopifyOrderId, albumIndex, 'editor');
  }, [m2, shopifyOrderId, albumIndex, isAdmin, order, profileRole]);
  // THIS order's frozen extra-page price (migration 230), never the live setting.
  useEffect(() => { setPricePerPage(resolvePricePerPage(order)); }, [order?.price_per_page]);

  // Pages included in this order's base price. Frozen at creation (migration
  // 082), so an order sold under the old 50-page base keeps 50 free pages while
  // new orders get 36. Everything money-facing in this editor — the page-count
  // slider's live surcharge, the "+N pages" chip, the premium tint — must bill
  // against THIS, never the current default.
  const basePagesPerAlbum = resolveBasePagesPerAlbum(order?.base_pages_per_album);

  // Seed the layout toggle from the customer's stored COD choice as the order
  // loads. Values in order.layout aren't normalised ("1 image par page",
  // "1 à 4 images par page", "1-4", "2", null…) — treat ONLY the exact
  // one-per-page string as 'one'; everything else (incl. null) is 'mixed'.
  // Stops seeding once the customer has toggled it themselves this session.
  useEffect(() => {
    if (layoutChoiceTouchedRef.current) return;
    const stored = (order?.layout ?? '').trim();
    setLayoutChoice(stored === '1 image par page' ? 'one' : 'mixed');
  }, [order?.layout]);

  // Admin re-entry after a customer submit: the order is already past
  // pending-album and admin is here to fix something + regenerate the PDF.
  // In this mode the editor's final action is a silent PDF rewrite — NOT a
  // resubmit, NOT a status change, NOT a redirect to checkout. Detected from
  // order state (album_submitted_at != null) rather than a URL flag so the
  // admin's "Régénérer" link in PdfGenerationCard doesn't have to know the
  // mode.
  //
  // Why not just `isAdmin`: when admin tests a FRESH Manual/COD order they're
  // still in pending-album (album_submitted_at is null) and get the normal
  // customer-flow submit — preserves the deliberate behavior at L4596-4602
  // ("Always land on checkout — admin or not").
  // ⚠️ THE TEAM ALWAYS REGENERATES, ALBUM BY ALBUM (owner, 2026-10-17 — #1249):
  // « Régénérer le PDF » replaces « Commander » for an admin on EVERY album,
  // submitted or not, Solo or Duo/Trio — and rebuilds only THIS album
  // (pdfRegenerate `album`). A rebuild never submits the album (pdf.ts
  // finalize: « A PDF landing never submits the album »).
  const isAdminRegenMode = isAdmin;
  // ADMIN-only: print the cover assembly guides (corner cut diagonals + spine
  // ticks) on this regenerated cover. ON by default for ADMIN regens — the
  // printer wants the cut/fold guides, and forgetting the checkbox kept shipping
  // clean covers. Customer PDFs stay clean regardless: every call site gates on
  // `isAdminRegenMode && adminCoverGuides`. Admin can still untick for a clean one.
  const [adminCoverGuides, setAdminCoverGuides] = useState(true);
  // The admin « Régénérer le PDF » was handed to the server (no tab to keep open).
  const [regenOnServer, setRegenOnServer] = useState(false);
  // The quick 3D preview drawn in this tab right after (lib/quickPreview.ts).
  const [, setQuickPreview] = useState<'idle' | 'running' | 'done' | 'skipped'>('idle');
  // The live bars (components/QuickBuildProgress): when the click happened, and
  // the in-tab redraw's own progress when this tab is the one drawing.
  const [regenClickAt, setRegenClickAt] = useState(0);
  const [localQuick, setLocalQuick] = useState<LocalQuick | null>(null);

  // ADMIN-only: lay photos out in UPLOAD ORDER (the order the client added
  // photos) instead of the date-based AI ordering. Default OFF — when OFF the
  // /ai/layout behaviour is byte-identical to today. Seeded from the session
  // below and persisted to the session row on toggle so the next /ai/layout
  // (and a reload) sees it.
  const [useUploadOrder, setUseUploadOrder] = useState(false);
  // B5 — space between photos, per album. Seeded from the session row below;
  // saved with every autosave so it can never drift from the pages it applies to.
  const [photoGap, setPhotoGap] = useState<PhotoGap>({ mm: 0, color: DEFAULT_GAP_COLOR });

  // ─── Fetch session ──────────────────────────────────────────────────────

  const sessionQuery = useQuery({
    queryKey: ['session', shopifyOrderId, albumIndex],
    queryFn: () => sessionsApi.get(shopifyOrderId!, albumIndex),
    enabled: !!shopifyOrderId,
    // While editing, the in-editor state is authoritative and autosave pushes
    // to the server. A background refetch (window refocus after a rotate /
    // app-switch) would pull server pages back over the live edit and make the
    // editor appear to "revert / jump to the start" — so never auto-refetch
    // this mid-session.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    // STALE-REOPEN FIX (2026-07-23): staleTime:Infinity alone meant a REOPEN
    // within the 5-min cache window served the old snapshot WITHOUT fetching —
    // then the mount autosave wrote that stale state back over anything saved
    // in between (the "client's edits reverted, agent touched nothing"
    // incident). gcTime:0 discards the snapshot when the editor closes, so a
    // reopen is a TRUE fresh load (spinner → server-latest → hydrate); the
    // one-shot hydration can never run on a stale cached copy. Once mounted,
    // the two flags above still block all mid-edit churn.
    refetchOnMount: 'always',
    gcTime: 0,
  });
  // …and the cover picker / upload page share this key with their own gcTime,
  // so a snapshot read BEFORE their save can still be in the cache when the
  // editor mounts. Hydrate only from our own post-mount read — see
  // lib/editorSessionGate.ts (#WA-402224: false « modifié ailleurs »).
  const { data: sessionData, loading: sessionLoading } = freshSessionGate(sessionQuery);

  const session = sessionData?.session;
  const layout = session?.pages as LayoutPlan | null;

  // ─── THE BOOK'S STRUCTURE (design system v2 §7 S1, bookStructure.ts) ────
  // A book that opens on its themed S1 page prints NO intro page and its album
  // page 1 is a RIGHT page. Decided from the editor's CURRENT pages, never the
  // row it loaded: deleting or moving the S1 page gives the plain book back on
  // the spot — intro, labels and every base-rules frame flip with it. A plain
  // book keeps the very same `photoGap` object (withThemedBook).
  const themedBook = isThemedBook(localPages.length > 0 ? localPages : (layout?.pages ?? []));
  const bookGap = useMemo(() => withThemedBook(photoGap, themedBook), [photoGap, themedBook]);

  // ─── Fetch photos ───────────────────────────────────────────────────────

  const { data: photosData, isLoading: photosLoading } = useQuery({
    queryKey: ['editor-photos', session?.id],
    queryFn: () => photosApi.list(session!.id),
    enabled: !!session?.id,
    staleTime: 60_000,
    // Same reasoning as the session query: don't let a window-refocus refetch
    // churn `allPhotos` mid-edit. Explicit invalidations (rotate, add photos)
    // still refetch on demand. refetchOnMount 'always' = fresh on every OPEN.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  });

  const allPhotos = (photosData?.photos ?? []) as PhotoWithUrl[];
  // Migration 267 — photos the dedup hid, for the tray's « Doublons masqués ».
  const hiddenDuplicates = photosData?.hidden_duplicates ?? [];
  const refetchEditorPhotos = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
  }, [queryClient, session?.id]);

  // D74 trace finding (2026-05-20): handleGenerateFromEditor's useCallback
  // intentionally omits allPhotos from its deps (to keep the function's
  // identity stable across photo loads), but that means the closure
  // captures whichever value allPhotos held when the callback was first
  // created — usually [] on initial mount, before the photos query has
  // resolved. Every post-AI pass (promoteHeroes, antiMonotone,
  // applyPrioritySwapPass, forceSoloForMultiFace, computeAiSlotPositions)
  // then builds its photoById map from an empty array and becomes a no-op.
  // We mirror allPhotos into a ref and read from the ref inside the
  // regen callback so the post-passes always see the freshest data.
  const allPhotosRef = useRef<PhotoWithUrl[]>(allPhotos);
  useEffect(() => { allPhotosRef.current = allPhotos; }, [allPhotos]);

  // ─── CAPTIONS FOLLOW THEIR PHOTOS (#WA-269649) ───────────────────────────
  // A caption (`captionFor`) belongs to its photo. Dragged to another page,
  // swapped, or moved by « Régénérer IA » (which re-attaches elements by page
  // NUMBER), the photo takes its words along, placed again clear of its faces
  // (lib/captionLayout.ts). followCaptionsInEditor returns the SAME array when
  // nothing moved, so this effect settles after one pass.
  const captionMeta = useMemo(() => photoMetaFrom(allPhotos), [allPhotos]);
  useEffect(() => {
    if (!localPages.some((p) => (p.elements ?? []).some((e) => typeof e.captionFor === 'string'))) return;
    const framing = Object.fromEntries(withSlotPositions(localPages, slotPositions).map((p) => [p.page, p.slot_positions]));
    // The whole gap object: with base rules it carries the page frame too.
    const next = followCaptionsInEditor(localPages, framing, captionMeta, bookGap);
    if (next !== localPages) setLocalPages(next);
  }, [localPages, slotPositions, captionMeta, bookGap]);

  // ─── Phase 1: lazy face analysis ────────────────────────────────────────
  // Photos uploaded via the magic link (especially HEIC from non-Safari
  // browsers) arrive without face data. Run face-api on them here, in the
  // background, the first time the editor opens. Results power the AI
  // layout engine in Phase 3.
  useEffect(() => {
    if (!allPhotos.length) return;
    // Lazy-import so the face-api models (~10MB) only load when actually needed.
    import('@/lib/lazyPhotoAnalysis').then((m) => {
      m.queueLazyAnalysis(allPhotos.map((p) => ({
        id: p.id,
        signed_url: p.signed_url,
        analyzed_at: (p as any).analyzed_at ?? null,
        is_sideways: (p as any).vision_tags?.is_sideways ?? false,
      })));
    });
  }, [allPhotos]);

  // ── Pre-warm AI layout once photos are analyzed and no layout exists yet ──
  // Fires once per session id; the resolved promise is consumed by the
  // "Faire avec l'IA" button, making the click feel instant.
  useEffect(() => {
    if (!session?.id || !order) return;
    if (allPhotos.length === 0) return;
    if (prewarmKeyRef.current === session.id) return;
    if ((layout?.pages?.length ?? 0) > 0) return;
    const allAnalyzed = allPhotos.every((p) => (p as any).analyzed_at);
    if (!allAnalyzed) return;

    const totalPages = order.page_count ?? 24;
    const pref = order.layout || 'balanced';
    prewarmKeyRef.current = session.id;
    // #1194: compute only. The worker used to WRITE this layout for a click
    // that never came.
    prewarmRef.current = aiApi.generateLayout(session.id, totalPages, pref, { shopify_order_id: shopifyOrderId ?? '', persist: false });
    // Swallow rejection so an unhandled-promise warning doesn't fire; the
    // user-clicked path will rerun the call if the prewarm errored.
    prewarmRef.current.catch(() => {
      prewarmRef.current = null;
      prewarmKeyRef.current = null;
    });
  }, [session?.id, order, allPhotos, layout?.pages?.length, shopifyOrderId]);

  // ─── Fetch covers for library ───────────────────────────────────────────

  const { data: coversData } = useQuery({
    queryKey: ['covers-library'],
    queryFn: () => coversApi.list(),
  });

  const covers = coversData?.covers ?? [];

  // ─── Fetch layout templates (Phase 1) ───────────────────────────────────
  // Filter by the current album category when known. Loaded once per
  // session-category combo; the gallery modal reads from this list.
  const albumCategory: AlbumCategory | null =
    (session as { album_category?: AlbumCategory | null } | null)?.album_category ?? null;
  const { data: templatesData } = useQuery({
    queryKey: ['editor-templates', albumCategory],
    queryFn: () =>
      templatesApi.list({
        ...(albumCategory ? { category: albumCategory } : {}),
      }),
    staleTime: 60_000,
    // S5 (2026-06-08): when the session resolves and albumCategory flips
    // null→'travel' the query re-keys + refetches; keep showing the previous
    // result instead of flashing an empty grid during that swap.
    placeholderData: (prev) => prev,
  });
  const allTemplates: LayoutTemplate[] = templatesData?.templates ?? [];
  const templatesById = useMemo(() => {
    const m = new Map<string, LayoutTemplate>();
    for (const t of allTemplates) m.set(t.id, t);
    return m;
  }, [allTemplates]);
  // S5 (2026-06-08): preload template thumbnails at editor mount so the Modèles
  // panel paints INSTANTLY from cache. Previously each full-size PNG/SVG thumb
  // was only fetched + decoded when the panel opened (loading="lazy",
  // fetchpriority="low" on conditionally-mounted <img>s), giving the few-second
  // delay. Warming the HTTP + decode cache off-DOM via Image() fixes both the
  // desktop sidebar and the mobile sheet (both read this same list).
  useEffect(() => {
    for (const t of allTemplates) {
      const url = t.preview_image_url || t.background_url;
      if (!url) continue;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    }
  }, [allTemplates]);

  // ─── Initialize spine from session ──────────────────────────────────────

  useEffect(() => {
    if (session) {
      const yr = session.year ?? order?.year_1 ?? '';
      // The spine is the TITLE edge on — not the occasion (lib/titleSource.ts).
      setSpineConfig((prev) => ({
        ...prev,
        text: prev.text || titlePrefill(session, order?.destination_1),
        year: prev.year || yr,
      }));
    }
  }, [session, order]);

  // Seed the upload-order toggle from the session row (default OFF).
  useEffect(() => {
    if (session) setUseUploadOrder(session.use_upload_order === true);
  }, [session?.id]);
  useEffect(() => {
    // Base rules (migration 234): photoGapOf reads the stamp — 4 mm + page margins.
    if (session) setPhotoGap(photoGapOf(session));
  }, [session?.id]);

  // Persist the upload-order toggle to the session row immediately so the next
  // /ai/layout (which reads use_upload_order off the row) honours it, and it
  // survives reload. Optimistic local flip; reverts on failure.
  const handleToggleUploadOrder = useCallback(async (next: boolean) => {
    if (!shopifyOrderId) return;
    setUseUploadOrder(next);
    try {
      await sessionsApi.upsert(shopifyOrderId, albumIndex, { use_upload_order: next });
      queryClient.setQueryData(
        ['session', shopifyOrderId, albumIndex],
        (prev: { session: typeof session } | undefined) =>
          prev?.session ? { ...prev, session: { ...prev.session, use_upload_order: next } } : prev,
      );
    } catch {
      setUseUploadOrder(!next); // revert on failure
    }
  }, [shopifyOrderId, albumIndex, queryClient]);

  // Load the selected cover from the session. cover_id is `undefined` only while
  // the session hasn't loaded; once loaded it's a UUID (real cover) or `null`
  // (the BLANK cover was chosen). A persisted null must hydrate the blank canvas
  // — NOT leave the previous design, which was the "design reappears" bug.
  useEffect(() => {
    if (covers.length === 0) return;
    const cid = session?.cover_id; // undefined = not loaded, null = blank, string = real
    if (cid === undefined) return;
    if (cid === null) { setSelectedCover(BLANK_COVER); return; }
    const c = covers.find((cv) => cv.id === cid);
    if (c) setSelectedCover(c);
  }, [session?.cover_id, covers]);

  // Initialize localPages from session layout
  useEffect(() => {
    if (layout?.pages && layout.pages.length > 0 && localPages.length === 0) {
      // A model page whose photos and windows disagree (#WA-510802) opens as
      // the plain grid of its photos — exactly what the PDF prints for it
      // (lib/themedPageEdits.repairModelPages, also run in pdfGenerator).
      const healed = repairModelPages(layout.pages);
      if (healed.repaired.length) console.warn('[editor] model pages out of step with their photos, opened as grids:', healed.repaired);
      setLocalPages(healed.pages);
      setLocalUnassigned(layout.unassigned_photos ?? []);
      // Restore the customer's per-slot pan positions from the saved
      // pages so the editor + PDF agree on framing across reloads.
      const restoredPositions = hydrateSlotPositions(layout.pages);
      if (Object.keys(restoredPositions).length > 0) {
        setSlotPositions((prev) => ({ ...restoredPositions, ...prev }));
      }
      // Restore the client's intro-page overlay elements from the same pages
      // JSONB (sibling field). Absent on legacy / unedited sessions → stays [].
      const savedIntro = (layout as { intro_elements?: PageElement[] }).intro_elements;
      if (Array.isArray(savedIntro) && savedIntro.length > 0) {
        setIntroElements(savedIntro);
      }
      // Restore the manual-edit-by-photo memory (Fix A) so a customer's
      // hand-tuned framing still FOLLOWS the photo across a re-layout even
      // after a reload. Persisted as a sibling of `intro_elements`. Absent on
      // legacy sessions → stays {} (regen then falls back to AI positions).
      const savedOrder = (layout as { client_order?: unknown }).client_order;
      clientOrderRef.current = Array.isArray(savedOrder) ? savedOrder.filter((x): x is string => typeof x === 'string') : null;
      const savedManual = (layout as { manual_slot_edits?: ManualSlotEditMap }).manual_slot_edits;
      if (savedManual && typeof savedManual === 'object') {
        manualSlotEditsRef.current = savedManual;
      }
      const savedUseTemplates = (layout as { use_templates?: boolean }).use_templates;
      if (typeof savedUseTemplates === 'boolean') setUseTemplates(savedUseTemplates);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout?.pages?.length]); // intentional: only on first load

  // ── Sync bottom carousel scroll with currentSpread ──────────────────────────
  // When the customer flips spreads via the prev/next arrows or the deep
  // link, scroll the bottom strip so the active spread sits in view.
  // data-spread-idx is set on each spread button below.
  useEffect(() => {
    const strip = bottomStripRef.current;
    if (!strip || editorView !== 'pages') return;
    const target = strip.querySelector<HTMLElement>(`[data-spread-idx="${currentSpread}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentSpread, editorView]);

  // Fix #4/#5 dismiss effect removed (2026-05-28 revised): the X chip
  // is now permanently visible on every deletable spread, so there is
  // nothing to dismiss on outside-tap.

  // ── Clear element selection when the customer navigates to a different
  //     spread (D78 2026-05-21). Without this the floating toolbar kept
  //     showing the previous-page text element's controls even after the
  //     customer paged to p.5-6 / p.7-8, which the customer reported as
  //     "the text-edit popup doesn't disappear when I switch pages". The
  //     toolbar still tracks the element via `findElementById` regardless
  //     of which spread is on screen, so the deselect has to be explicit.
  useEffect(() => {
    if (!selectedElementId) return;
    // Intro / outro elements are FREE bookends stored OUTSIDE localPages, so
    // findElementById (which only walks localPages) never finds them. Without
    // this guard, selecting an intro text element would fall into the `!found`
    // branch below and immediately deselect it — one render after the
    // toolbar-open effect set elementToolbarOpen, so the font/size/colour
    // toolbar never actually rendered. The spread-navigation deselect only
    // makes sense for content pages anyway. (FIX 2026-06-10.)
    if (editorView === 'intro' || editorView === 'outro') return;
    const found = findElementById(selectedElementId);
    if (!found) {
      selectElement(null);
      return;
    }
    // The pages open on this spread, paired as the printed book (a themed
    // book's opening stands alone on the right).
    const [li, ri] = spreadPages(currentSpread, isThemedBook(localPages), localPages.length);
    const leftPageNum = li != null ? localPages[li]?.page : undefined;
    const rightPageNum = ri != null ? localPages[ri]?.page : undefined;
    if (found.pageNumber !== leftPageNum && found.pageNumber !== rightPageNum) {
      selectElement(null);
    }
    // localPages identity changes every regen — depend on the actual
    // page numbers of the visible spread, not the array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpread, selectedElementId, findElementById, selectElement, editorView]);

  // ── Deep-link from /flow's "Ajouter 2 pages" button ─────────────────────────
  // The preview step passes ?focusPage=<1-indexed>&addedPages=<N>. When we
  // see them, jump to the spread containing that page, flip into the pages
  // view, and mark the N pages starting at focusPage so PhotoSlot can flash
  // the "Tap to add photos" hint. Strip the params afterwards so a refresh
  // doesn't keep flashing.
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (localPages.length === 0) return; // wait for layout to load
    const focusPage = parseInt(searchParams.get('focusPage') ?? '', 10);
    const addedPages = parseInt(searchParams.get('addedPages') ?? '', 10);
    if (!focusPage || focusPage <= 0) return;
    deepLinkHandledRef.current = true;

    const arrayIdx = localPages.findIndex((p) => p.page === focusPage);
    if (arrayIdx >= 0) {
      setEditorView('pages');
      setCurrentSpread(spreadOf(arrayIdx, isThemedBook(localPages)));
    }
    if (Number.isFinite(addedPages) && addedPages > 0) {
      const hint = new Set<number>();
      for (let i = 0; i < addedPages; i++) hint.add(focusPage + i);
      setPendingPagesHint(hint);
      // Clear the hint after 8s so it doesn't pulse forever.
      setTimeout(() => setPendingPagesHint(new Set()), 8000);
    }
    // Strip the params so a refresh doesn't re-fire the deep link.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('focusPage');
      url.searchParams.delete('addedPages');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
  }, [localPages.length, searchParams]);

  // ── Deep-link to a specific page: /editor/:id/:album/edit?page=<1-indexed> ──
  // Used by the admin "copier le lien (page actuelle)" button so a link they
  // share opens on the exact page they were viewing, not page 1. Independent
  // of the focusPage handler above (which also flashes an add-photos hint).
  const pageDeepLinkRef = useRef(false);
  useEffect(() => {
    if (pageDeepLinkRef.current) return;
    if (localPages.length === 0) return; // wait for layout to load
    const page = parseInt(searchParams.get('page') ?? '', 10);
    if (!page || page <= 0) return;
    pageDeepLinkRef.current = true;

    const arrayIdx = localPages.findIndex((p) => p.page === page);
    if (arrayIdx >= 0) {
      setEditorView('pages');
      setCurrentSpread(spreadOf(arrayIdx, isThemedBook(localPages)));
    }
    // Strip the param so a refresh doesn't keep re-jumping.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
  }, [localPages.length, searchParams]);

  // ── Legacy auto-smart-crop effect — REMOVED ───────────────────────────────
  // This used to compute face-centered slot positions in the OLD
  // object-position model (x/y in 0..100, SUBJECT_ZOOM=1.12, HOLD_BIAS=12).
  // After the slot model migrated to translate-based v2 those values were
  // being persisted with the `_v: 2` marker by `withSlotPositions`, which
  // skipped migration on reload and treated them as translate offsets.
  // Result: every photo opened in the editor showed up at 1.12× scale
  // with extreme crops (the BABY SHOWER 2026 / #D71 regression).
  // Face-aware positioning now lives in `computeAiSlotPositions` (runs on
  // AI regen), which produces correct v2 translate values directly.

  // Initialize cover texts and customizations from session
  useEffect(() => {
    if (!session) return;
    // Permanent Fix 2: for customers the title is mandatory, so a blank stored
    // value (from an older deletion bug) is restored from the COD form values
    // on open. Admins see whatever was saved (incl. blank).
    // COD pre-fill is PER ALBUM: session.destination/year hold what the client
    // typed in the order form for THIS album (Duo/Trio albums 2-3 have their
    // own); order.destination_1 was wrongly used for every album before.
    const codDest = session.destination || order?.destination_1 || '';
    const codYear = session.year || order?.year_1 || '';
    // ⚠️ ONE ANSWER (2026-10-06, lib/titleSource.ts): the stored title; the
    // order words only while it is OWED; EMPTY for « no title » — this used to
    // put the order words back over his decision, and the next autosave
    // printed them. Admins still see a saved blank as blank.
    const storedDest = titlePrefill(session, codDest);
    const adminBlank = isAdmin && session.cover_destination_text === '';
    const storedYear = session.cover_year_text ?? codYear;
    setCoverDestText(adminBlank ? '' : storedDest);
    setCoverYearText(!isAdmin && !storedYear ? codYear : storedYear);
    if (session.cover_customizations) {
      try {
        const c: any = typeof session.cover_customizations === 'string'
          ? JSON.parse(session.cover_customizations)
          : session.cover_customizations;
        // Carry the imported wrap through untouched (see importedCoverWrapRef).
        importedCoverWrapRef.current = c?.coverWrapImage?.storage_path
          ? { storage_path: String(c.coverWrapImage.storage_path) }
          : null;
        if (c?.zoneOverrides) setCoverZoneOverrides(c.zoneOverrides);
        if (c?.extraZones) setCoverExtraZones(c.extraZones);
        setCoverElements(readCoverElements(c?.coverElements));
        if (c?.customTexts) setCoverCustomTexts(c.customTexts);
        if (c?.templatePhotoFits && typeof c.templatePhotoFits === 'object') {
          setTemplatePhotoFits(c.templatePhotoFits);
        }
        if (c?.spineConfig) setSpineConfig(c.spineConfig);
        if (c?.backCoverBgColor) setBackCoverBgColor(c.backCoverBgColor);
        if (c?.frontCoverBgColor) setFrontCoverBgColor(c.frontCoverBgColor);
        if (typeof c?.hideFrontCoverImage === 'boolean') setHideFrontCoverImage(c.hideFrontCoverImage);
        if (c?.coverDesignTransform && typeof c.coverDesignTransform === 'object') {
          const t = c.coverDesignTransform as Partial<CoverDesignTransform>;
          setCoverDesignTransform({
            x: typeof t.x === 'number' ? t.x : 0,
            y: typeof t.y === 'number' ? t.y : 0,
            scale: typeof t.scale === 'number' ? t.scale : 1,
          });
        }

        // Restore the front-cover override. The { url } flavour (customer-
        // picked catalogue icon) is a public covers-bucket URL — no signing.
        // The { storage_path } flavour (admin upload) is re-signed through
        // the worker like back-cover images, since RLS blocks browser-side
        // signing for the album-photos back-cover/ prefix.
        if (c?.frontCoverImageOverride?.url) {
          const ovUrl = c.frontCoverImageOverride.url as string;
          setFrontCoverImageOverride({ url: ovUrl });
          setFrontCoverImageOverrideUrl(ovUrl);
        } else if (c?.frontCoverImageOverride?.storage_path) {
          const ovPath = c.frontCoverImageOverride.storage_path as string;
          setFrontCoverImageOverride({ storage_path: ovPath });
          (async () => {
            try {
              const { urls } = await photosApi.signBackCoverUrls([ovPath]);
              setFrontCoverImageOverrideUrl(urls?.[0]?.signed_url ?? null);
            } catch (e) {
              console.warn('[Editor] cover-icon override URL refresh failed:', e);
              setFrontCoverImageOverrideUrl(null);
            }
          })();
        } else {
          setFrontCoverImageOverride(null);
          setFrontCoverImageOverrideUrl(null);
        }

        // Restore the client's blank-cover front image and re-sign it (same
        // worker-signed path as the back-cover / cover-icon prefixes).
        if (c?.blankCoverTransform && typeof c.blankCoverTransform === 'object') {
          const t = c.blankCoverTransform as { x?: number; y?: number; scale?: number };
          setBlankCoverTransform({ x: Number(t.x) || 0, y: Number(t.y) || 0, scale: Number(t.scale) || 1 });
        } else {
          setBlankCoverTransform({ x: 0, y: 0, scale: 1 });
        }
        if (c?.blankCoverImage?.storage_path) {
          const blankPath = c.blankCoverImage.storage_path as string;
          setBlankCoverImage({ storage_path: blankPath });
          (async () => {
            try {
              const { urls } = await photosApi.signBackCoverUrls([blankPath]);
              setBlankCoverImageUrl(urls?.[0]?.signed_url ?? null);
            } catch (e) {
              console.warn('[Editor] blank-cover image URL refresh failed:', e);
              setBlankCoverImageUrl(null);
            }
          })();
        } else {
          setBlankCoverImage(null);
          setBlankCoverImageUrl(null);
        }

        // Restore back-cover images: convert saved storage_paths to fresh
        // signed view URLs. Older sessions may have raw URL strings here
        // (legacy blob: URLs that no longer resolve) — we filter those out.
        if (Array.isArray(c?.backCoverImages)) {
          const restored = c.backCoverImages
            .map((item: any): BackCoverImage | null => {
              if (typeof item === 'string') {
                // legacy entry — only keep if it looks like a real Storage path
                if (item.startsWith('blob:') || item.startsWith('data:')) return null;
                return { storage_path: item, signed_url: null };
              }
              if (item && typeof item.storage_path === 'string') {
                return { storage_path: item.storage_path, signed_url: null, full_page: item.full_page === true || undefined };
              }
              return null;
            })
            .filter((x: BackCoverImage | null): x is BackCoverImage => !!x);
          setBackCoverImages(restored);
          // Re-sign every storage_path so the editor shows fresh URLs.
          // Routed through the worker (service role) because RLS on the
          // album-photos bucket blocks browser-side signing for the
          // back-cover/ prefix → was returning 400 and leaving the grid empty.
          if (restored.length > 0) {
            (async () => {
              const paths = restored.map((r: BackCoverImage) => r.storage_path);
              try {
                const { urls } = await photosApi.signBackCoverUrls(paths);
                if (urls?.length) {
                  setBackCoverImages((prev) =>
                    prev.map((img) => {
                      const found = urls.find((u) => u.storage_path === img.storage_path);
                      return found?.signed_url
                        ? { ...img, signed_url: found.signed_url }
                        : img;
                    }),
                  );
                }
              } catch (e) {
                console.warn('[Editor] back-cover URL refresh failed:', e);
              }
            })();
          }
        }
      } catch {}
    }
    // Hydration complete — autosave is now allowed to fire. Anything before
    // this point would have raced against DB-persisted state with empty
    // React-state values and wiped out saved back-cover photos / overrides.
    // Capture the loaded snapshot's last_saved_at for the last-writer guard.
    sessionSavedAtRef.current = (session as { last_saved_at?: string } | undefined)?.last_saved_at ?? null;
    knownContentRef.current = albumContentKey(session as unknown as Record<string, unknown> | undefined);
    hasHydratedRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Auto-set spine + back-cover bg from cover when it changes.
  // Strategy: kick off with the cover record's dominant_color (fast, sometimes
  // wrong), then asynchronously sample the actual image's border pixels to get
  // a color that genuinely matches what the user sees. This second pass fixes
  // the "stored dominant doesn't match the image" mismatch (e.g. the Mate cover
  // showing a slate spine against a bright-blue front).
  useEffect(() => {
    if (!selectedCover) return;
    // After hydration, the session's saved customizations are the source of
    // truth — re-deriving spine/back colors from the cover image would wipe
    // the customer's pick on every editor remount (e.g. round-trip via
    // checkout). The cover-picker handler `handleSelectCover` already resets
    // both colors when the user actually picks a different cover, so this
    // effect is only useful for the very first load when no customizations
    // exist yet.
    if (hasHydratedRef.current) return;

    if (selectedCover.dominant_color) {
      setSpineConfig((prev) => ({ ...prev, bgColor: selectedCover.dominant_color! }));
      setBackCoverBgColor((prev) => prev ?? selectedCover.dominant_color!);
    }

    // Spine text color follows the front cover's destination zone color so
    // the spine matches the front by default (instead of the old hardcoded
    // blue `#5B8CDB`). Year zone is the second-best fallback so we still
    // pull a brand color from the cover even on covers without a dest zone.
    const destZone = selectedCover.text_zones?.find((z) => z.type === 'destination');
    const yearZone = selectedCover.text_zones?.find((z) => z.type === 'year');
    const spineFallbackColor = destZone?.color ?? yearZone?.color ?? null;
    if (spineFallbackColor) {
      setSpineConfig((prev) => ({ ...prev, textColor: spineFallbackColor }));
    }

    // The DB `dominant_color` (admin-curated flat cover plate) is AUTHORITATIVE
    // for the back + spine background — it was already applied above. Only fall
    // back to a client-side pixel sample when the cover has NO curated colour.
    // A pixel-average of the thumbnail picks the saturated artwork, not the flat
    // plate (ISLANDE: correct light #E8F1F2 was being clobbered by a royal blue),
    // so it must never override a curated value.
    if (selectedCover.dominant_color) return;
    const imgUrl = selectedCover.thumbnail_url ?? null;
    if (!imgUrl) return;
    let cancelled = false;
    extractDominantColorFromUrl(imgUrl).then((color) => {
      if (cancelled || !color) return;
      // Guard the async pass too — if hydration completed between schedule
      // and resolution, do not overwrite the restored customizations.
      if (hasHydratedRef.current) return;
      setSpineConfig((prev) => ({ ...prev, bgColor: color }));
      setBackCoverBgColor(color);
    });
    return () => { cancelled = true; };
  }, [selectedCover?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the spine text colors in sync with the front cover's title (and
  // year) colors. The destination zone drives `spineConfig.textColor`; the
  // year zone drives `spineConfig.yearColor`. We honour the customer's live
  // override first (set via the cover text toolbar) and fall back to the
  // cover record's static `color` field. Unlike the initial-load effect
  // above this runs on every change so editing the title color on the
  // front face propagates to the spine immediately — without this, a red
  // title with a default white spine ships to the printer mismatched.
  // Baseline for the spine colour sync above: the last title/year colours we
  // propagated. `seeded:false` until the first observation so hydration (or a
  // saved spine colour) is never overwritten.
  const spineColorSyncRef = useRef<{ seeded: boolean; dest: string | null; year: string | null }>({ seeded: false, dest: null, year: null });
  useEffect(() => {
    if (!selectedCover) return;
    const zones = selectedCover.text_zones ?? [];
    const destIdx = zones.findIndex((z) => z.type === 'destination');
    const yearIdx = zones.findIndex((z) => z.type === 'year');
    const destColor =
      coverZoneOverrides[destIdx]?.color ?? zones[destIdx]?.color ?? null;
    const yearColor =
      coverZoneOverrides[yearIdx]?.color ?? zones[yearIdx]?.color ?? null;
    if (!destColor && !yearColor) return;
    // SPINE COLOUR STUCK ON WHITE (2026-07-24, Yahya) — this sync used to
    // re-assert the title/year colour onto the spine on EVERY run. So the
    // moment the customer picked a spine colour (or a saved blue was loaded
    // from the session), the next render pushed the cover's own zone colour
    // (#ffffff on the Europe design) straight back over it: "it turns blue
    // for a second then goes white again", and the PDF printed white.
    // Now we only propagate when the SOURCE colour actually CHANGES, and the
    // very first observation just seeds the baseline (so hydration never
    // clobbers a saved spine colour).
    const prevSync = spineColorSyncRef.current;
    const seeded = prevSync.seeded;
    // Never seed off PRE-hydration state. The load effect (declared above,
    // keyed on the same session?.id that is in this effect's deps) restores the
    // saved spineConfig; seeding before it would lock in a baseline the restore
    // then contradicts, and the adopt-the-title branch below would judge the
    // hardcoded default instead of the customer's saved colour.
    if (!seeded && !hasHydratedRef.current) return;
    const destChanged = seeded && destColor !== prevSync.dest;
    const yearChanged = seeded && yearColor !== prevSync.year;
    spineColorSyncRef.current = { seeded: true, dest: destColor, year: yearColor };

    // ALWAYS-BLUE SPINE (2026-08-04, Yahya: "i dont want the blue spine there
    // forever"). Seeding alone was not enough: every saved session carried the
    // hardcoded '#5B8CDB' default, so the baseline was blue, nothing ever
    // "changed", and the spine stayed blue for the album's whole life. On the
    // FIRST observation we now adopt the title colour when nothing was ever
    // deliberately chosen — resolveSpineTextColor treats only the legacy
    // default (or no colour) as unchosen, so the explicit pick that 2026-07-24
    // fixed still survives untouched.
    if (!seeded) {
      setSpineConfig((prev) => {
        const next = resolveSpineTextColor({
          zones, overrides: coverZoneOverrides, current: prev.textColor,
          bgColor: prev.bgColor,
        });
        return !next || next === prev.textColor ? prev : { ...prev, textColor: next };
      });
    }

    if (!destChanged && !yearChanged) return;
    setSpineConfig((prev) => {
      const nextText = destChanged && destColor ? destColor : prev.textColor;
      const nextYear = yearChanged && yearColor ? yearColor : prev.yearColor;
      if (nextText === prev.textColor && nextYear === prev.yearColor) return prev;
      return { ...prev, textColor: nextText, yearColor: nextYear };
    });
    // session?.id: re-run once hydration has restored the saved spineConfig, so
    // the seed above observes the DB value rather than the initial default.
  }, [selectedCover, coverZoneOverrides, session?.id]);

  // Keep the spine background colour in sync with the front cover's bg.
  // The front cover renders `frontCoverBgColor ?? dominant_color`, and the
  // async `extractDominantColorFromUrl` pass above used to write a
  // border-sampled colour to the spine + back without touching the front,
  // which left the spine looking a shade off (orange-ish red against a
  // pure red front). Anchoring the spine to the same source the front
  // uses guarantees they paint with one swatch.
  //
  // ⚠️ ONLY WHEN THE FRONT COLOUR CHANGES (2026-10-30, #WA-363209). This used
  // to fire on every editor load too: the owner picked a light-blue spine
  // (#5d9ce5) on a navy « Vierge » cover, and every reopen / regenerate
  // painted the spine back to the front's navy and autosaved it — « it goes
  // back to a colour I haven't chosen ». The first observation after
  // hydration only seeds the baseline; a saved spine colour is never touched.
  // Hydration lands the SAVED front colour (one render after the load effect
  // flags hydrated), so a target equal to it is never a change of hers.
  const spineBgSyncRef = useRef<{ seeded: boolean; source: string | null }>({ seeded: false, source: null });
  const savedFrontBg = ((session as { cover_customizations?: { frontCoverBgColor?: string | null } } | undefined)
    ?.cover_customizations?.frontCoverBgColor) ?? null;
  useEffect(() => {
    if (!selectedCover) return;
    if (!hasHydratedRef.current) return;
    const target = frontCoverBgColor ?? selectedCover.dominant_color ?? null;
    const prev = spineBgSyncRef.current;
    spineBgSyncRef.current = { seeded: true, source: target };
    if (!prev.seeded || !target || target === prev.source || target === savedFrontBg) return;
    setSpineConfig((p) => (p.bgColor === target ? p : { ...p, bgColor: target }));
  }, [selectedCover, frontCoverBgColor, session?.id, savedFrontBg]);

  // Fetch logo + price + intro/outro page URLs from app settings. The
  // intro/outro pages bookend the customer's photos in the printed
  // album (admin uploads them via Settings → Identité de marque). We
  // cache them so the editor preview can show them at the start/end of
  // the spread carousel and the submit flow can include them in the
  // interior PDF without a second fetch.
  const [introOutroUrls, setIntroOutroUrls] = useState<{
    intro: string | null; outro: string | null;
  }>({ intro: null, outro: null });
  useEffect(() => {
    settingsApi.get().then(({ settings }) => {
      if (settings?.logo_url) setLogoUrl(settings.logo_url);
      setBleedMm(settings?.pdf_bleed_mm ?? 3);
      // « Nouvel éditeur mobile » for every phone (migration 257; absent = off).
      setMobileV2Flag(settings?.mobile_editor_v2 === true);
      setOutroPageEnabled(settings?.editor_outro_page_enabled === true);
      couponsEnabledRef.current = settings?.coupons_enabled === true;
      const pts = (settings as { print_template_settings?: { spineWmm?: unknown; jointGapMm?: unknown } } | null)?.print_template_settings;
      if (pts && typeof pts.spineWmm === 'number' && typeof pts.jointGapMm === 'number') {
        setCoverSpineSettings({ spineWmm: pts.spineWmm, jointGapMm: pts.jointGapMm });
      }
      if (pts && typeof pts === 'object') {
        setCoverPrintSettings(withDerivedVinyl({ ...DEFAULT_PRINT_SETTINGS, ...(pts as Partial<PrintSettings>) }));
      }
      setIntroOutroUrls({
        intro: settings?.intro_page_1_url ?? null,
        outro: settings?.outro_page_2_url ?? null,
      });
    }).catch(() => {});
  }, []);

  // ── Autosave ───────────────────────────────────────────────────────────────
  // The actual persist, shared by the debounced trigger AND the unload flush.
  const doSave = useCallback(async (opts?: { keepalive?: boolean }) => {
    if (!shopifyOrderId) return;
    // RE-ARM A DROPPED SAVE (2026-07-26). This used to `return` when a save was
    // already in flight — with no retry and no dirty flag, so the edit made
    // during that window was never written. On a slow connection a round trip
    // easily outlasts the 500 ms debounce, which is how work vanished across a
    // reload with nothing in the console. Now the request is remembered and
    // replayed the moment the in-flight save finishes.
    if (m2ViewOnlyRef.current) return;
    // order not loaded yet → we can't tell a locked album: hold the save, it
    // runs the moment the order arrives (effect below)
    if (m2Ref.current && !m2OrderKnownRef.current) { m2SaveHeldRef.current = true; return; }
    if (isSavingRef.current) { saveQueuedRef.current = true; return; }
    // Conflict detected earlier → autosave stays OFF until reload (banner up).
    if (editConflictRef.current) return;
    isSavingRef.current = true;
    try {
      // Build the exact persisted objects ONCE so we can both send them and
      // mirror them into the query cache below (see the cache write after the
      // upsert). cover_id can be undefined (no change) — resolve it here.
      const savedCoverId = selectedCover ? persistedCoverId(selectedCover) : undefined;
      const savedCoverCustomizations = {
          spineConfig,
          zoneOverrides: coverZoneOverrides,
          extraZones: coverExtraZones,
          customTexts: coverCustomTexts,
          backCoverBgColor,
          frontCoverBgColor,
          hideFrontCoverImage,
          coverDesignTransform,
          // Front-cover override (per-order). Customer-picked catalogue icon
          // persists as { url } (public, stable); admin upload persists as
          // { storage_path } only — its signed URL is regenerated on load.
          frontCoverImageOverride: frontCoverImageOverride
            ? frontCoverImageOverride.url
              ? { url: frontCoverImageOverride.url }
              : { storage_path: frontCoverImageOverride.storage_path }
            : null,
          // Client's own front-cover image on a blank cover. storage_path
          // only; signed URL regenerated on load (mirrors back-cover).
          blankCoverTransform,
          blankCoverImage: blankCoverImage
            ? { storage_path: blankCoverImage.storage_path }
            : null,
          // Only persist storage_path; signed URLs are short-lived and
          // regenerated on every load.
          backCoverImages: backCoverImages.map((i) => ({ storage_path: i.storage_path, ...(i.full_page ? { full_page: true } : {}) })),
          // Stickers / icons / photos on the cover faces (lib/coverElements.ts).
          coverElements: coverElementsForSave(),
          templatePhotoFits,
          // Echo the imported full-wrap cover back. The editor never edits it,
          // but omitting it here deletes it — this object REPLACES the stored
          // column. Spread last and conditionally so nothing is written for a
          // normal (non-imported) album.
          ...(importedCoverWrapRef.current ? { coverWrapImage: importedCoverWrapRef.current } : {}),
      };
      const savedPages = {
          pages: withSlotPositions(localPages, slotPositions),
          unassigned_photos: localUnassigned,
          // Client intro-page overlay elements — sibling field in the pages
          // JSONB. Free bookend layer; does NOT affect page_count / pricing.
          intro_elements: introElements,
          // Manual pan/scale-by-photo memory (Fix A) so the framing follows the
          // photo across a re-layout, even after reload. Sibling field; does NOT
          // affect render (render reads slot_positions per page).
          manual_slot_edits: manualSlotEditsRef.current,
          use_templates: useTemplates,
          ...(clientOrderRef.current ? { client_order: clientOrderRef.current } : {}),
      };
      const saveRes = await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
        cover_id: savedCoverId,
        cover_destination_text: coverDestText || undefined,
        cover_year_text: coverYearText || undefined,
        // Single source of truth for the cover PDF — every visible element in
        // the editor's preview panel is captured here.
        cover_customizations: savedCoverCustomizations,
        pages: savedPages,
        // B5 — the gap travels with the pages it applies to.
        photo_gap_mm: photoGap.mm,
        photo_gap_color: photoGap.color,
        // Last-writer guard: the worker rejects this save (409 stale_write) if
        // someone else saved after the state we loaded — never overwrite newer
        // edits made elsewhere (client on their phone vs agent in the office).
        ...(sessionSavedAtRef.current ? { expected_last_saved_at: sessionSavedAtRef.current } : {}),
      }, { keepalive: opts?.keepalive });
      // Advance the guard cursor to OUR new save stamp.
      const newSavedAt = (saveRes?.session as { last_saved_at?: string } | undefined)?.last_saved_at;
      if (newSavedAt) sessionSavedAtRef.current = newSavedAt;
      knownContentRef.current = albumContentKey(saveRes?.session as unknown as Record<string, unknown> | undefined) ?? knownContentRef.current;
      staleRetriedRef.current = false;
      // PERSISTENCE FIX (2026-07-05): mirror the just-saved state into the
      // ['session'] query cache. Autosave previously wrote only to the DB, so
      // navigating away and back remounted the editor, read the STALE cached
      // session, and the one-shot hydration effect reverted the customer's
      // moved photos/elements. Writing the cache makes it authoritative so a
      // remount hydrates the real, saved positions.
      queryClient.setQueryData(
        ['session', shopifyOrderId, albumIndex],
        (prev: { session?: Record<string, unknown> } | undefined) => {
          if (!prev?.session) return prev;
          return {
            ...prev,
            session: {
              ...prev.session,
              ...(savedCoverId !== undefined ? { cover_id: savedCoverId } : {}),
              ...(coverDestText ? { cover_destination_text: coverDestText } : {}),
              ...(coverYearText ? { cover_year_text: coverYearText } : {}),
              cover_customizations: savedCoverCustomizations,
              pages: savedPages,
            },
          };
        },
      );
      setSaveStatus('saved');
      setLastSaveTime(new Date());
      // Success → clear any pending retry backoff.
      saveRetryRef.current = 0;
      if (saveRetryTimerRef.current) { clearTimeout(saveRetryTimerRef.current); saveRetryTimerRef.current = null; }
    } catch (e) {
      // 409 stale_write = someone else saved since we loaded. NOT transient:
      // retrying would keep hammering (and eventually overwrite their newer
      // work if the guard were ever relaxed). Stop autosaving, surface the
      // conflict banner — reload picks up the newest version.
      const status = (e as { status?: number; body?: { code?: string } })?.status;
      const code = (e as { status?: number; body?: { code?: string } })?.body?.code;
      if (status === 409 && code === 'stale_write') {
        // Only the stamp moved (our own « reached the editor » mark, or another
        // bookkeeping write)? Then the album is exactly what we last knew:
        // adopt the new stamp and save again — once. A changed album is a
        // real conflict and keeps the banner. lib/staleWriteRecovery.ts.
        if (!staleRetriedRef.current) {
          staleRetriedRef.current = true;
          try {
            const fresh = (await sessionsApi.get(shopifyOrderId!, albumIndex))?.session as unknown as Record<string, unknown> | undefined;
            if (onlyStampMoved(knownContentRef.current, fresh)) {
              sessionSavedAtRef.current = (fresh?.last_saved_at as string | undefined) ?? sessionSavedAtRef.current;
              console.info('[AutoSave] stale_write was a timestamp-only change — saving again');
              saveQueuedRef.current = true;
              return;
            }
          } catch { /* cannot read it back → treat as a real conflict */ }
        }
        console.warn('[AutoSave] stale_write conflict — album was modified elsewhere; autosave stopped');
        editConflictRef.current = true;
        setEditConflict(true);
        setSaveStatus('error');
        return;
      }
      console.warn('[AutoSave] failed', e);
      setSaveStatus('error');
      // Auto-retry so a transient failure never leaves an edit unsaved. Capped
      // + exponential backoff; retries the LATEST state via doSaveRef.
      if (saveRetryRef.current < 6) {
        saveRetryRef.current += 1;
        const backoff = Math.min(1500 * 2 ** (saveRetryRef.current - 1), 20000);
        if (saveRetryTimerRef.current) clearTimeout(saveRetryTimerRef.current);
        saveRetryTimerRef.current = setTimeout(() => { doSaveRef.current(); }, backoff);
      }
    } finally {
      isSavingRef.current = false;
      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        // Replay the save that was dropped while this one was in flight.
        setTimeout(() => { void doSaveRef.current?.(); }, 0);
      }
    }
  }, [shopifyOrderId, albumIndex, selectedCover?.id, coverDestText, coverYearText, spineConfig, coverZoneOverrides, coverExtraZones, coverCustomTexts, backCoverBgColor, frontCoverBgColor, hideFrontCoverImage, coverDesignTransform, frontCoverImageOverride, blankCoverImage, blankCoverTransform, backCoverImages, coverElements, templatePhotoFits, localPages, localUnassigned, slotPositions, introElements, useTemplates, photoGap, queryClient]);

  // « Nouvel éditeur mobile »: a save held because the order was not known yet
  // runs now — unless the album turned out to be view-only.
  useEffect(() => {
    if (!m2 || !order || !m2SaveHeldRef.current) return;
    m2SaveHeldRef.current = false;
    if (!m2ViewOnlyRef.current) void doSave();
  }, [m2, order, doSave]);

  // Keep doSaveRef pointed at the latest closure (used by the failure-retry
  // timer above so it always persists the CURRENT editor state).
  useEffect(() => { doSaveRef.current = doSave; }, [doSave]);
  // Clear any pending retry on unmount so it can't fire against a dead page.
  useEffect(() => () => { if (saveRetryTimerRef.current) clearTimeout(saveRetryTimerRef.current); }, []);

  const triggerSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => { void doSave(); }, 500);
  }, [doSave]);

  // B5 — one setter for the whole album; the debounced autosave persists it
  // with the pages, and the PDF re-renders because the gap is in the hash.
  const handlePhotoGapChange = useCallback((next: Partial<PhotoGap>) => {
    setPhotoGap((prev) => ({
      // Base rules fix the gap at 4 mm: the album-wide toggle cannot move it.
      mm: prev.baseRules ? prev.mm : next.mm !== undefined ? clampGapMm(next.mm) : prev.mm,
      color: next.color !== undefined ? normalizeGapColor(next.color) : prev.color,
      ...(prev.baseRules ? { baseRules: true } : {}),
    }));
    triggerSave();
  }, [triggerSave]);

  // CRITICAL data-safety: flush the pending debounced save IMMEDIATELY when the
  // tab is hidden or unloading. iOS Safari fires `visibilitychange`→hidden (and
  // `pagehide`) just BEFORE it kills a backgrounded tab under memory pressure —
  // catching it here means a self-reload can never lose the last edits sitting
  // in the 500ms debounce window. A ref keeps the listener pointed at the latest
  // closure without re-binding on every edit.
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    // keepalive:true so the request finishes even as iOS evicts the page on
    // photo-picker open — the existing hydration then restores it on reload.
    if (hasHydratedRef.current) void doSave({ keepalive: true });
  }, [doSave]);

  // "+ Ajouter" entry point. On iOS the in-editor native gallery picker reloads
  // the whole editor: iOS discards the heavy backgrounded page the moment the
  // gallery opens, then reloads on return — BEFORE the photos upload, so nothing
  // saves. Route iOS to the LIGHT dedicated upload page (empty grid, no canvas,
  // no face-api) which survives the picker; the photos upload to the same album
  // and the editor reloads with them when the user comes back. Flush the save
  // first (keepalive) so no in-editor edits are lost on the way out. Desktop has
  // no eviction, so it keeps the convenient in-place modal.
  const openAddPhotos = useCallback(() => {
    if (isIosDevice()) {
      try { flushSave(); } catch { /* ignore */ }
      // from=editor → the upload page shows "Ajouter à l'album" (just return,
      // photos already uploaded) instead of "Générer l'album" (AI regenerate,
      // which would wipe the existing layout).
      navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/photos?from=editor`);
      return;
    }
    setShowUploadModal(true);
  }, [navigate, shopifyOrderId, albumIndexParam, flushSave]);

  const flushSaveRef = useRef(flushSave);
  useEffect(() => { flushSaveRef.current = flushSave; }, [flushSave]);
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushSaveRef.current(); };
    const onPageHide = () => flushSaveRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // Free the module-level HEIC caches on unmount so their blob URLs (each one
  // pins a decoded Blob in memory) don't survive a route change / remount —
  // part of keeping the editor under the iOS memory ceiling. Display cache holds
  // object URLs (revoke them); upload cache holds File promises (just clear).
  useEffect(() => () => {
    for (const u of heicCache.values()) { if (u) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } } }
    heicCache.clear();
    heicPending.clear();
    editorHeicCache.clear();
    clearGridThumbs();
  }, []);

  useEffect(() => {
    // Skip until the load effect has restored DB state into React state.
    // Otherwise the first render's empty defaults race ahead and overwrite
    // saved back-cover photos / zone overrides / spine config.
    if (!hasHydratedRef.current) return;
    triggerSave();
  }, [localPages, slotPositions, introElements, selectedCover?.id, spineConfig, coverZoneOverrides, coverExtraZones, coverCustomTexts, coverDestText, coverYearText, backCoverBgColor, frontCoverBgColor, hideFrontCoverImage, coverDesignTransform, frontCoverImageOverride, blankCoverImage, blankCoverTransform, backCoverImages, coverElements, templatePhotoFits]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: the comprehensive history-recording effect lives next to the
  // undo/redo state declarations above. It captures every state change
  // (pages, unassigned photos, slot positions, AND nested elements)
  // with a single debounce, so we don't need a separate watcher here.

  // Init project name from session/order
  useEffect(() => {
    if (order && !projectName) {
      const dest = order.destination_1 || 'Album';
      const yr = order.year_1 || '';
      setProjectName(`${dest}${yr ? ` ${yr}` : ''}`);
    }
  }, [order]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Layout change ──────────────────────────────────────────────────────────
  const handleLayoutChange = useCallback((pageNumber: number, newLayout: '1-up' | '2-up' | '3-up' | '4-up') => {
    const requiredSlots = { '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4 }[newLayout];
    setLocalPages((prev) => {
      const idx = prev.findIndex((p) => p.page === pageNumber);
      if (idx === -1) return prev;
      const page = prev[idx];
      if (isOutroPage(page)) return prev;   // « Page de fin »: no grid to change
      const keptPhotos = page.photos.filter(Boolean).slice(0, requiredSlots);
      const released = page.photos.filter(Boolean).slice(requiredSlots);
      if (released.length > 0) setLocalUnassigned((u) => [...u, ...released]);

      // The page AS IT WILL BE after the switch — needed to compute each slot's
      // NEW aspect ratio for the manual-edit compatibility test below.
      const newPage = { ...page, layout: newLayout, photos: keptPhotos };

      // PRESERVE MANUAL EDITS ACROSS LAYOUT CHANGE (2026-07-10). Previously every
      // kept photo's pan + rotation was blanket-reset to centred (only a
      // zoom-in scale survived), so a customer's manual crop/position was lost
      // every time they touched the "Mise en page" selector. Now we mirror the
      // AI-"Régénérer" path (see handleGenerateFromEditor): a photo's recorded
      // manual edit FOLLOWS the photo (map keyed by photoId) and is re-applied
      // IFF the new slot's AR matches the AR the edit was made in — so the saved
      // pan/scale/rotation is still geometrically valid. Only a photo that lands
      // in a genuinely different-shaped slot resets (preserving a deliberate
      // zoom-in, as before) — exactly the "reset only incompatible slots"
      // behaviour the customer expects. Untouched: the selector UI + the AI regen
      // logic; this only changes how existing transform data is handled on apply.
      const AR_EPS = 0.02;
      const manualMap = manualSlotEditsRef.current;
      setSlotPositions((prevPos) => {
        const nextPos: SlotPositionMap = { ...prevPos };
        keptPhotos.forEach((photoId, slotIdx) => {
          if (!photoId) return;
          const slotKey = `p${pageNumber}-s${slotIdx}`;
          const edit = manualMap[photoId];
          const liveAR = slotARForPage(newPage, slotIdx);
          const arOk = !!edit && edit.ar != null && liveAR != null
            && Math.abs(liveAR - edit.ar) <= AR_EPS;
          if (arOk) {
            // Slot shape unchanged for this photo → keep the manual pan/scale/rotation.
            nextPos[slotKey] = { ...edit.pos, _v: 2 };
          } else {
            // Different-shaped (or templated) slot → the saved pan would crop
            // wrong. Reset to centred, preserving only a deliberate zoom-in
            // (>1); a stale contain-fit scale < 1 is discarded so the photo
            // fills the new cell instead of floating small inside it.
            const existing = prevPos[slotKey];
            const preservedScale = existing?.scale && existing.scale > 1 ? existing.scale : 1;
            nextPos[slotKey] = { x: 0, y: 0, scale: preservedScale, _v: 2 };
          }
        });
        return nextPos;
      });

      return [
        ...prev.slice(0, idx),
        newPage,
        ...prev.slice(idx + 1),
      ];
    });
  }, [allPhotos]);

  // ── Apply / remove layout template ──────────────────────────────────────────
  // Templates are applied through the fill-preview modal (the customer assigns a
  // photo to each zone first), so the only apply path is `applyTemplateWithPhotos`
  // below. `removeTemplate` just clears `template_id`; the page reverts to
  // whatever grid layout it was already on.

  // Apply a template AND fill its zones with the photos the customer picked in
  // the fill-preview modal. `photoIds[i]` is the photo for slot i (null = leave
  // empty). Photos pulled in from other pages/unassigned are de-duplicated so a
  // photo never appears twice; this page's old, now-unused photos are released
  // back to the unassigned tray.
  const applyTemplateWithPhotos = useCallback((pageIndex: number, template: LayoutTemplate, photoIds: (string | null)[]) => {
    const slotCount = template.slot_count;
    const filled = Array.from({ length: slotCount }, (_, i) => photoIds[i] || '');
    const assigned = new Set(filled.filter(Boolean));
    setLocalPages((prev) => {
      const next = prev.map((p) => ({ ...p, photos: [...p.photos] }));
      const page = next[pageIndex];
      if (!page) return prev;
      const released = page.photos.filter((id) => id && !assigned.has(id));
      // Strip the assigned photos out of any OTHER page so they aren't placed twice.
      for (let pi = 0; pi < next.length; pi++) {
        if (pi === pageIndex) continue;
        next[pi].photos = next[pi].photos.map((id) => (id && assigned.has(id) ? '' : id));
      }
      next[pageIndex] = { ...page, template_id: template.id, photos: filled };
      // Release this page's dropped photos; remove assigned ones from the tray.
      setLocalUnassigned((u) => [...u, ...released].filter((id) => id && !assigned.has(id)));
      return next;
    });
  }, []);

  const removeTemplate = useCallback((pageIndex: number) => {
    setLocalPages((prev) => {
      const next = [...prev];
      const page = next[pageIndex];
      if (!page) return prev;
      next[pageIndex] = { ...page, template_id: null };
      return next;
    });
  }, []);

  // ── DESIGN SYSTEM v2 « MODÈLES » (owner 2026-10-14: « I need to be able to
  // use the new models in here, and the images that I want »). The picker
  // (components/editor/ThemeModelPicker) builds the page on the worker
  // (routes/themeEditor.ts); these put it in the album and let the client
  // change the photos in its windows. Every change is a localPages change:
  // undo-able (the history recorder) and autosaved like any other edit.
  const [themeNotice, setThemeNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!themeNotice) return;
    const t = setTimeout(() => setThemeNotice(null), 7000);
    return () => clearTimeout(t);
  }, [themeNotice]);

  /** A photo's size and faces, for its window's crop (photos.face_data, DB size — what the worker used). */
  // Read through refs so the handlers below stay the same functions across edits
  // (PageView is memoised: a new handler per edit would re-draw every page).
  // (allPhotosRef: the editor's own ref to the latest photos, above.)
  const localUnassignedRef = useRef(localUnassigned);
  localUnassignedRef.current = localUnassigned;
  const windowPhotoOf = useCallback((id: string | null | undefined): WindowPhoto | null => {
    if (!id) return null;
    const p = allPhotosRef.current.find((x) => x.id === id);
    if (!p || !p.width || !p.height) return null;
    return { width: p.width, height: p.height, faces: facesFrom(p.face_data) };
  }, []);

  /** The decoration images the album's OTHER pages carry (an object at most twice per album). */
  const usedImagesExcept = useCallback((pageNumber: number): string[] => (
    localPagesRef.current.filter((p) => p.page !== pageNumber)
      .flatMap((p) => (p.elements ?? []).filter((e) => e.type === 'icon' && e.imageUrl && e.layer).map((e) => e.imageUrl as string))
  ), []);

  /** Apply a model the worker built: page N becomes it (lib/themedPageEdits.applyModel). */
  const applyThemedModel = useCallback((pageIndex: number, themed: EditorThemedPage, photoIds: string[]) => {
    // A panorama half replaced by a one-page model: its other half goes back to a plain page too.
    const mate = spreadMateIndex(localPages, pageIndex);
    const r = applyModel(localPages, localUnassigned, pageIndex, themed, photoIds);
    if (mate >= 0) r.pages[mate] = removeModel(r.pages[mate]);
    setLocalPages(r.pages);
    setLocalUnassigned(r.unassigned);
  }, [localPages, localUnassigned]);

  /** « Double page » (S5 « Panorama », 2026-10-18): album pages leftPage and rightPage become its two halves. */
  const applyThemedSpread = useCallback((left: EditorThemedPage, right: EditorThemedPage, leftPage: number, rightPage: number, photoIds: string[]) => {
    const li = localPages.findIndex((p) => p.page === leftPage);
    if (li < 0 || localPages[li + 1]?.page !== rightPage) return;
    // Halves of ANOTHER panorama next to these pages lose their other half: plain pages again.
    const mates = [spreadMateIndex(localPages, li), spreadMateIndex(localPages, li + 1)].filter((j) => j >= 0 && j !== li && j !== li + 1);
    const r = applySpreadModel(localPages, localUnassigned, li, left, right, photoIds);
    for (const j of mates) r.pages[j] = removeModel(r.pages[j]);
    setLocalPages(r.pages);
    setLocalUnassigned(r.unassigned);
  }, [localPages, localUnassigned]);

  /** « Retirer le modèle » — the page goes back to a plain grid of its photos (the client's own elements stay). */
  const handleRemoveThemeModel = useCallback((pageNumber: number) => {
    // A panorama's two halves are one model: « Retirer le modèle » on either removes both.
    setLocalPages((prev) => {
      const i = prev.findIndex((p) => p.page === pageNumber);
      const mate = i >= 0 ? spreadMateIndex(prev, i) : -1;
      return prev.map((p, k) => (k === i || k === mate ? removeModel(p) : p));
    });
  }, []);

  /**
   * A photo into a model's window: dropped from « Mes photos » or another page, or picked with
   * « Changer la photo » (lib/themedPageEdits.swapIntoWindow): the window of the new photo that
   * keeps every face whole, else the centred one and a warning — the client decides.
   */
  const handleSwapThemedPhoto = useCallback((pageNumber: number, elementId: string, newId: string, source: 'slot' | 'sidebar' = 'sidebar') => {
    if ((localPagesRef.current.find((p) => p.page === pageNumber) as { spreadHalf?: string } | undefined)?.spreadHalf) {
      setThemeNotice('Ce panorama est une seule photo sur deux pages : pour la changer, ouvrez « Modèles » → « Double page ».');
      return;
    }
    const r = swapIntoWindow(localPagesRef.current, localUnassignedRef.current, pageNumber, elementId, newId, source, windowPhotoOf);
    if (!r) return;
    setLocalPages(r.pages);
    setLocalUnassigned(r.unassigned);
    if (r.faceCut) setThemeNotice('Attention : dans ce cadre, un visage de cette photo est coupé. Touchez la photo pour la déplacer ou zoomer, ou choisissez-en une autre.');
  }, [windowPhotoOf]);

  /** A photo element filled from « Mes photos »: a model's window swaps (crop + counters), any other photo element takes the id. */
  const fillPhotoElement = useCallback((pageNumber: number, elementId: string, photoId: string) => {
    const page = localPagesRef.current.find((p) => p.page === pageNumber);
    const el = page?.elements?.find((e) => e.id === elementId);
    if (isModelWindow(page, el)) handleSwapThemedPhoto(pageNumber, elementId, photoId, 'sidebar');
    else handleUpdateElement(pageNumber, elementId, { photoId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSwapThemedPhoto]);

  /**
   * « Retirer » on a model's window: only when the model allows it (a special page that also
   * exists without its photo) — the worker re-draws the page without it; any other model
   * answers with the hint (replace the photo, or a model with one photo fewer). The photo
   * goes back to « non placées ». The words the client typed in its holders stay.
   */
  // ⚠️ THE FRAME EMPTIES, THE DESIGN STAYS (owner, 2026-10-17): « I need to be
  // able to delete it and add another image ». The model is no longer asked to
  // re-draw itself with one photo fewer (it refused: « Ce modèle a besoin de 1
  // photo »). The window is left empty — a photo dropped or picked fills it —
  // and the photo goes back to « non placées ». An empty window never prints.
  const handleRemoveThemedPhoto = useCallback((pageNumber: number, elementId: string) => {
    const page = localPagesRef.current.find((p) => p.page === pageNumber);
    const rm = page?.elements?.find((e) => e.id === elementId)?.photoId;
    if (!page || !rm) return;
    setLocalPages((prev) => prev.map((p) => (p.page !== pageNumber ? p : {
      ...p,
      photos: p.photos.filter((id) => id !== rm),
      elements: (p.elements ?? []).map((e) => (e.id === elementId ? { ...e, photoId: undefined, crop: undefined } : e)),
    })));
    setLocalUnassigned((u) => (u.includes(rm) || localPagesRef.current.some((p) => p.page !== pageNumber && p.photos.includes(rm)) ? u : [...u, rm]));
    setSelectedElementId(null);
  }, []);

  // ── BLANK PAGE (2026-07-05) — page-options menu + toggle ───────────────────
  // Which page's options menu is open (page number), or null.
  const [pageMenuFor, setPageMenuFor] = useState<number | null>(null);
  const openPageMenu = useCallback((pageNum: number) => setPageMenuFor(pageNum), []);
  // Toggle a page to/from a pure-white blank divider. Reversible: turning it
  // ON keeps the page's photos in the array (just hidden while blank) so
  // turning it OFF restores the exact AI layout. A blank page carries any
  // elements (text/photo/icons) the customer adds on top.
  const handleToggleBlank = useCallback((pageNumber: number) => {
    setLocalPages((prev) => {
      const idx = prev.findIndex((p) => p.page === pageNumber);
      if (idx === -1) return prev;
      const page = prev[idx];
      const next = [...prev];
      next[idx] = { ...page, blank: !page.blank };
      return next;
    });
    setSelectedSlotKey(null);
    setPageMenuFor(null);
  }, []);
  // ITEM 8 (2026-07-06) — set a blank page's background colour (like the cover
  // colour picker). Persists via the normal pages autosave.
  const handleSetBlankColor = useCallback((pageNumber: number, color: string) => {
    setLocalPages((prev) => {
      const idx = prev.findIndex((p) => p.page === pageNumber);
      if (idx === -1) return prev;
      const page = prev[idx];
      const next = [...prev];
      next[idx] = { ...page, blank_color: color };
      return next;
    });
  }, []);

  // ── Remove photo from slot ─────────────────────────────────────────────────
  const handleRemoveFromSlot = useCallback((pageNumber: number, slotIndex: number) => {
    setLocalPages((prev) => {
      const idx = prev.findIndex((p) => p.page === pageNumber);
      if (idx === -1) return prev;
      const page = prev[idx];
      const removedId = page.photos[slotIndex];
      if (!removedId) return prev;
      setLocalUnassigned((u) => [...u, removedId]);
      const newPhotos = [...page.photos];
      if (page.template_id) {
        // Templated page: keep slot positions stable. Empty-string sentinel
        // means "drop target." Layout is fixed by the template — don't snap.
        newPhotos[slotIndex] = '';
        return [
          ...prev.slice(0, idx),
          { ...page, photos: newPhotos },
          ...prev.slice(idx + 1),
        ];
      }
      newPhotos.splice(slotIndex, 1);
      // Slot-count invariant (rev 2026-06-08): removing a photo from a GRID page
      // must not leave an empty slot — downgrade the layout to match the new
      // photo count (e.g. 2-up → 1-up, a count-1 wide-top → 1-up). Mirrors the
      // AI pipeline's snapLayoutToPhotoCount so manual edits stay consistent.
      const liveCount = newPhotos.filter(Boolean).length;
      const snapLayout = (n: number): LayoutPage['layout'] =>
        n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : '4-up';
      return [
        ...prev.slice(0, idx),
        { ...page, photos: newPhotos, layout: snapLayout(liveCount) },
        ...prev.slice(idx + 1),
      ];
    });
    setSelectedSlotKey(null);
    // Also drop any active edit/pan state — the per-image trash button (TWEAK,
    // 2026-06-08) deletes from WITHIN edit mode, so without this the now-empty
    // slot would keep a stale panModeKey. Harmless for the resting-state X
    // (panModeKey is already null there).
    setPanModeKey(null);
  }, []);

  // ── Drop photo to slot ──────────────────────────────────────────────────
  // source='slot'    → swap (slot-to-slot drag rearranges)
  // source='sidebar' → copy (drag from photo strip lets the same photo
  //                   appear on multiple pages; source slot untouched)
  const handleDropToSlot = useCallback((
    pageNumber: number,
    slotIndex: number,
    photoId: string,
    source: 'slot' | 'sidebar' = 'sidebar',
  ) => {
    setLocalPages((prev) => {
      const pages = [...prev];
      const targetIdx = pages.findIndex((p) => p.page === pageNumber);
      if (targetIdx === -1) return prev;

      const targetPage = { ...pages[targetIdx], photos: [...pages[targetIdx].photos] };
      const displacedId = targetPage.photos[slotIndex];

      // Find existing slot for swap — only when the drag started from a slot.
      let sourcePageIdx = -1;
      let sourceSlotIdx = -1;
      if (source === 'slot') {
        for (let pi = 0; pi < pages.length; pi++) {
          const si = pages[pi].photos.indexOf(photoId);
          if (si !== -1) { sourcePageIdx = pi; sourceSlotIdx = si; break; }
        }
      }

      // Place the dragged photo in target slot
      targetPage.photos[slotIndex] = photoId;
      pages[targetIdx] = targetPage;

      if (sourcePageIdx !== -1 && sourceSlotIdx !== -1) {
        // Slot-to-slot swap: put the displaced photo back where the dragged one came from.
        if (sourcePageIdx === targetIdx) {
          pages[targetIdx] = { ...pages[targetIdx], photos: [...pages[targetIdx].photos] };
          pages[targetIdx].photos[sourceSlotIdx] = displacedId || '';
        } else {
          const sourcePage = { ...pages[sourcePageIdx], photos: [...pages[sourcePageIdx].photos] };
          sourcePage.photos[sourceSlotIdx] = displacedId || '';
          // A model's window it came from (owner, 2026-10-17): its frame shows the traded photo.
          if (sourcePage.theme && sourcePage.elements) {
            sourcePage.elements = sourcePage.elements.map((e) => (e.type === 'photo' && e.photoId === photoId
              ? { ...e, photoId: displacedId || undefined, crop: undefined } : e));
            if (!displacedId) sourcePage.photos = sourcePage.photos.filter(Boolean);
          }
          pages[sourcePageIdx] = sourcePage;
        }
      } else if (displacedId && displacedId !== photoId) {
        // Sidebar copy onto a non-empty slot — bumped photo goes to unassigned
        // unless it's still placed somewhere else on the album.
        const stillPlaced = pages.some((pg, pi) =>
          pg.photos.some((id, si) => id === displacedId && !(pi === targetIdx && si === slotIndex)),
        );
        if (!stillPlaced) setLocalUnassigned((u) => [...u, displacedId]);
      }

      return pages;
    });
    setLocalUnassigned((u) => u.filter((id) => id !== photoId));
    setDraggingPhotoId(null);

    // Fix H (2026-05-28): a successful drop is meaningful progress —
    // reset the stuck-user idle clock.
    stuck.noteProgress();

    // Behavior log — fuels future AI training on what humans actually do
    // with the AI's suggestions.
    if (session?.id) {
      aiApi.feedback(session.id, 'photo_moved', {
        photo_id: photoId,
        to_page: pageNumber,
        to_slot: slotIndex,
        source,
      });
    }

    // Reset pan to centred (0, 0). For scale: keep deliberate zoom-ins
    // (>1) so the customer's framing survives a re-drop, but reset
    // anything ≤ 1 to a fresh fill. Carrying a leftover scale < 1 from
    // a previous photo silently shrunk new drops inside the cell — the
    // regression that broke AI-regenerated and layout-switched pages.
    const slotKey = `p${pageNumber}-s${slotIndex}`;
    setSlotPositions((prev) => {
      const existing = prev[slotKey];
      const preservedScale = existing?.scale && existing.scale > 1 ? existing.scale : 1;
      return {
        ...prev,
        [slotKey]: { x: 0, y: 0, scale: preservedScale, _v: 2 as const },
      };
    });
  }, [allPhotos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fix #7 (2026-05-28): touch-drag pipeline. Photo-strip thumbnails opt
  // in via onTouchStart={(e) => beginTouchDrag(photo.id, photo.signed_url, e)}.
  // The slot wrappers carry `data-slot-drop-target="<slotKey>"` so
  // useTouchDragPhoto can read the target via elementFromPoint and fire
  // onDrop with the slotKey. Two formats coexist: the legacy
  // `p<page>-s<slot>` from PhotoSlot, and `slot:<page>:<slot>` from
  // TemplatedPhotoSlot. We accept both.
  // Stable drag-end handler so the memoized PhotoThumbnail tiles don't all
  // re-render whenever the tray re-renders (was an inline closure → new identity
  // each render → memo never bit).
  const handleSidebarDragEnd = useCallback(() => setDraggingPhotoId(null), []);

  const { beginTouchDrag } = useTouchDragPhoto((photoId, slotKey, at) => {
    // A cover face (team): the photo lands where the finger lifted.
    if (slotKey.startsWith('cover:')) {
      const face = slotKey.slice(6);
      if (face === 'front' || face === 'back' || face === 'spine') addPhotoToCover(photoId, face, at ?? null);
      return;
    }
    const matchLegacy = slotKey.match(/^p(\d+)-s(\d+)$/);
    const matchTemplated = slotKey.match(/^slot:(\d+):(\d+)$/);
    const m = matchLegacy ?? matchTemplated;
    if (!m) return;
    const pageNumber = parseInt(m[1], 10);
    const slotIndex = parseInt(m[2], 10);
    // Detect whether this photo is already placed somewhere — if so the
    // drag started from a SLOT (Fix 2: slot-to-slot mobile drag) and
    // handleDropToSlot's swap branch must run; otherwise it's a sidebar
    // pickup and we treat it as a fresh placement. Without this, slot-to-
    // slot drags hardcoded 'sidebar' and left the source slot with a stale
    // copy of the photo. `localPages` is captured fresh on each render
    // because useTouchDragPhoto reassigns its inner onDrop ref every
    // render (see hook impl), so this closure sees the latest state.
    const alreadyPlaced = localPages.some(
      (p) => p.photos.includes(photoId),
    );
    handleDropToSlot(pageNumber, slotIndex, photoId, alreadyPlaced ? 'slot' : 'sidebar');
  });

  // ── Insert page at position ────────────────────────────────────────────────
  const handleInsertPage = useCallback((afterArrayIndex: number, layout: LayoutPage['layout']) => {
    let insertedSpreadIdx = -1;
    setLocalPages((prev) => {
      if (prev.length >= MAX_PAGE_COUNT) {
        setPageCountWarning(`Maximum de ${MAX_PAGE_COUNT} pages atteint !`);
        setTimeout(() => setPageCountWarning(null), 3000);
        return prev;
      }
      // Insert-at-start (afterArrayIndex = -1): basePageNum must be 0 so the
      // new pages start numbering at 1 and every existing page shifts down by
      // addCount. The old `prev[-1]?.page ?? prev.length` fallback returned
      // prev.length (e.g. 24), which produced new pages 25, 26 — colliding
      // with the renumbered tail and breaking page identity.
      const basePageNum = afterArrayIndex < 0
        ? 0
        : (prev[afterArrayIndex]?.page ?? prev.length);
      const addCount = prev.length + 2 <= MAX_PAGE_COUNT ? 2 : 1;
      const newPages: LayoutPage[] = [];
      for (let i = 0; i < addCount; i++) {
        newPages.push({ page: basePageNum + 1 + i, layout, photos: [] });
      }
      const before = prev.slice(0, afterArrayIndex + 1);
      const after = prev.slice(afterArrayIndex + 1).map((p) => ({ ...p, page: p.page + addCount }));
      const result = [...before, ...newPages, ...after];
      // The newly inserted pages start at array index (afterArrayIndex + 1).
      // Compute the spread index for that position — in the book as it now
      // stands (editorSpreads.ts: a themed book's opening stands alone).
      insertedSpreadIdx = spreadOf(afterArrayIndex + 1, isThemedBook(result));
      return result;
    });
    setAddedPagesCount((n) => n + 2);
    // Manual page edits override a stale slider target — Régénérer should then
    // use the real current page count, not the last slider value.
    setPageCountTarget(null);
    setInsertAfterPageIdx(null);
    // Navigate to the newly created spread
    setTimeout(() => {
      if (insertedSpreadIdx >= 0) {
        setCurrentSpread(insertedSpreadIdx);
        setEditorView('pages');
      }
    }, 50);
  }, []);

  // ── « Page de fin » (coupons plan 2026-10-28, lib/outroPage.ts) ────────────
  // ONE page that draws the brand outro image, inserted after array index
  // `afterArrayIndex` (-1 = right after the intro). A normal entry of the
  // pages: billed like any page (+1), the ÷4 print padding follows. The pages
  // after it shift by one, so their framings are re-keyed with them.
  const handleInsertOutroPage = useCallback((afterArrayIndex: number) => {
    setInsertMenu(null);
    setInsertAfterPageIdx(null);
    const cur = localPagesRef.current;
    if (cur.length >= MAX_PAGE_COUNT) {
      setPageCountWarning(`Maximum de ${MAX_PAGE_COUNT} pages atteint !`);
      setTimeout(() => setPageCountWarning(null), 3000);
      return;
    }
    const r = insertOutroPage(cur, afterArrayIndex);
    const next = r.pages as LayoutPage[];
    setLocalPages(next);
    setSlotPositions((pos) => remapPageKeyed(pos, r.pageMap));
    setAddedPagesCount((n) => n + 1);
    setPageCountTarget(null);
    const spread = spreadOf(r.insertedIndex, isThemedBook(next));
    setTimeout(() => { setCurrentSpread(spread); setEditorView('pages'); }, 50);
  }, []);

  // « ← Déplacer / Déplacer → » on a « Page de fin »: one page, one place. The
  // strip's drag still moves whole spreads (handleReorderSpreads).
  const handleMoveOutroPage = useCallback((pageNumber: number, dir: -1 | 1) => {
    const cur = localPagesRef.current;
    const r = movePageBy(cur, pageNumber, dir, isThemedBook(cur));
    setPageMenuFor(null);
    if (!r) return;
    setLocalPages(r.pages);
    setSlotPositions((pos) => remapPageKeyed(pos, r.pageMap));
    setCurrentSpread(spreadOf(r.newIndex, isThemedBook(r.pages)));
  }, []);

  // The strip's « + »: with the switch off it adds 2 photo pages at once, as
  // always; with it on it opens the small menu (« 2 pages photo » / « Page de
  // fin »), hung from the button that was pressed.
  const handleStripPlus = useCallback((afterIdx: number, el: HTMLElement) => {
    if (!outroPageEnabled) { handleInsertPage(afterIdx, '1-up'); return; }
    const r = el.getBoundingClientRect();
    setInsertMenu({ afterIdx, left: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  }, [outroPageEnabled, handleInsertPage]);

  // ── Delete a page ──────────────────────────────────────────────────────────
  // Removes the page at `pageNumber` (1-indexed) from the album, returns any
  // photos placed on it to the unassigned pool, renumbers subsequent pages,
  // and decrements the extra-pages counter. Blocks deletion below the
  // bundle's base page count so a customer can't ship fewer pages than they
  // paid for.
  const handleDeletePage = useCallback((pageNumber: number) => {
    if (!order) return;
    // Hard floor only — one spread (2 pages) must remain. Deletion below the
    // paid `page_count` is allowed on purpose (admin/customer can remove unwanted
    // spreads); the real page count + price reconcile on the next regen/submit.
    const minPages = 2;
    if (localPages.length <= minPages) {
      setPageCountWarning(`Minimum de ${minPages} pages — impossible de supprimer`);
      setTimeout(() => setPageCountWarning(null), 3000);
      return;
    }
    setLocalPages((prev) => {
      const idx = prev.findIndex((p) => p.page === pageNumber);
      if (idx === -1) return prev;
      // Orphaned photos go back to the unassigned strip so nothing is lost.
      const photosToReturn = prev[idx].photos.filter(Boolean);
      if (photosToReturn.length) {
        setLocalUnassigned((u) => [...u, ...photosToReturn]);
      }
      // Single source of truth for positional renumbering (tested in pageDelete.test.ts).
      return renumberAfterDelete(prev, pageNumber);
    });
    setAddedPagesCount((n) => Math.max(0, n - 1));
    // Manual delete overrides a stale slider target (see handleInsertPage).
    setPageCountTarget(null);
    // Clamp spread index — if the deleted page was the last one, fall back.
    // Counted in the book that remains (deleting the S1 opening of a themed
    // book gives the plain book back — bookStructure.ts).
    setCurrentSpread((s) => {
      const remaining = localPages.filter((p) => p.page !== pageNumber);
      return clampSpread(s, remaining.length, isThemedBook(remaining));
    });
    if (session?.id) {
      aiApi.feedback(session.id, 'page_removed', { page: pageNumber });
    }
  }, [order, localPages, session?.id]);

  // ── Shared delete-confirmation flow ────────────────────────────────────────
  // Every delete entry point (scroller ✕, desktop tools panel, mobile
  // hold-to-delete bin) routes through one premium warning popup instead of
  // window.confirm. `requestDeletePages` opens it; `confirmPageDelete` runs the
  // deletes highest-page-first (renumbering shifts lower pages down, so doing
  // the bottom one first keeps the second target valid).
  const requestDeletePages = useCallback((pageNumbers: number[]) => {
    const pages = pageNumbers.filter((n) => Number.isFinite(n));
    if (!pages.length) return;
    setLiftedPageNumber(null);
    setPageDeleteRequest({ pages });
  }, []);

  const confirmPageDelete = useCallback(() => {
    const req = pageDeleteRequest;
    if (!req) return;
    [...req.pages].sort((a, b) => b - a).forEach((p) => handleDeletePage(p));
    setPageDeleteRequest(null);
  }, [pageDeleteRequest, handleDeletePage]);

  // ── Page-count slider commit (2026-07-07) ──────────────────────────────────
  // The slider fires this on release with a ÷4 target (20–200). INCREASE appends
  // genuinely-empty pages (client fills them manually or via AI Magic) and
  // re-bills extras as `max(0, target - base)` — matching the worker's
  // authoritative `page_count - base` formula (not the old +2/-1 delta counter).
  // DECREASE never deletes silently: it picks the pages to drop (trailing EMPTY
  // first, then content) and defers to a confirm modal.
  const isPageEmpty = useCallback(
    (p: LayoutPage) => !p.blank && (p.elements?.length ?? 0) === 0 && !p.photos.some(Boolean),
    [],
  );
  const applyPageCount = useCallback((rawTarget: number) => {
    // NO SNAP HERE. The slider's Valider already rounded UP to a multiple of 4
    // and showed the customer that exact number. Re-snapping here (it used to
    // call snapPageCount = round-to-NEAREST) is what made "+1" a no-op on the
    // first click and a 4-page jump on the second: the child sent 53, this
    // rounded it back to 52, and the early return below swallowed it silently.
    const target = clampPageCount(rawTarget);
    const prev = localPagesRef.current;
    const current = prev.length;
    // Record the customer's requested count so Régénérer targets IT (repacking
    // photos to fit), rather than echoing the current page count back. This is
    // what makes "set 52 → Régénérer → 52 pages" work even when the album
    // currently has more pages than the target.
    setPageCountTarget(target);
    if (target === current) return;
    // Any pre-warmed AI layout was computed for the OLD count — drop it so the
    // next AI Magic issues a fresh call at the new page count.
    prewarmRef.current = null;
    prewarmKeyRef.current = null;

    if (target > current) {
      // INCREASE — append empty pages (NOT auto-filled, NOT `blank` dividers).
      const next = [...prev];
      for (let i = 0; i < target - current; i++) {
        next.push({ page: current + 1 + i, layout: '1-up', photos: [] });
      }
      setLocalPages(next);
      setAddedPagesCount(Math.max(0, target - basePagesPerAlbum));
      setEditorView('pages');
      // Jump to the first new spread so the client sees the empty pages.
      setCurrentSpread(spreadOf(current, isThemedBook(next)));
      return;
    }

    // DECREASE — choose which pages to remove: empty ones (highest page# first),
    // then content pages if still short. Confirm before anything is deleted.
    const need = current - target;
    const empties = prev.filter(isPageEmpty).map((p) => p.page).sort((a, b) => b - a);
    const contents = prev.filter((p) => !isPageEmpty(p)).map((p) => p.page).sort((a, b) => b - a);
    const toRemove: number[] = [];
    for (const p of empties) { if (toRemove.length < need) toRemove.push(p); }
    for (const p of contents) { if (toRemove.length < need) toRemove.push(p); }
    const contentSet = new Set(contents);
    const removesContent = toRemove.some((p) => contentSet.has(p));
    setPageCountDecrease({ target, toRemove, removesContent });
  }, [isPageEmpty, basePagesPerAlbum]);

  // Customer flips the layout-density toggle in "Mises en page". Marks it
  // touched (so order.layout stops re-seeding it) and drops any pre-warmed
  // layout (computed for the previous choice) so the next Régénérer re-issues.
  const handleLayoutChoiceChange = useCallback((choice: 'one' | 'mixed') => {
    layoutChoiceTouchedRef.current = true;
    setLayoutChoice(choice);
    prewarmRef.current = null;
    prewarmKeyRef.current = null;
  }, []);

  const confirmPageCountDecrease = useCallback(() => {
    const req = pageCountDecrease;
    if (!req) return;
    // Highest page# first so renumbering never invalidates a later target
    // (handleDeletePage returns any photos to the unassigned strip — no loss).
    [...req.toRemove].sort((a, b) => b - a).forEach((p) => handleDeletePage(p));
    // Override the per-delete decrement with the authoritative extras value.
    setAddedPagesCount(Math.max(0, req.target - basePagesPerAlbum));
    setPageCountDecrease(null);
    setPageCountDraft(null);   // change applied — slider mirrors the album again
  }, [pageCountDecrease, handleDeletePage, basePagesPerAlbum]);

  // ── Mobile hold-to-delete (canvas) ─────────────────────────────────────────
  // Press-and-hold ~1s on an EMPTY canvas page → it lifts + shows a bin
  // affordance; tapping the bin opens the shared warning popup. Gated to empty
  // pages on purpose: it matches the spec flow ("delete all images first, then
  // hold") AND sidesteps any collision with the 700ms photo-drag gesture, which
  // only exists when a slot has a photo. A finger move > 10px (scroll/pan) or an
  // early release cancels the hold.
  const HOLD_TO_DELETE_MS = 1000;
  const HOLD_MOVE_CANCEL_PX = 10;
  const holdTimerRef = useRef<number | null>(null);
  const holdStartRef = useRef<{ x: number; y: number } | null>(null);
  const clearHold = useCallback(() => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    holdStartRef.current = null;
  }, []);
  const makePageHoldHandlers = useCallback(
    (pageNumber: number | undefined, isEmpty: boolean) => {
      if (!isMobile || !isEmpty || pageNumber == null) return {};
      return {
        onPointerDown: (e: React.PointerEvent) => {
          if (e.pointerType !== 'touch') return;
          clearHold();
          holdStartRef.current = { x: e.clientX, y: e.clientY };
          holdTimerRef.current = window.setTimeout(() => {
            navigator.vibrate?.(40);
            setLiftedPageNumber(pageNumber);
            holdTimerRef.current = null;
          }, HOLD_TO_DELETE_MS);
        },
        onPointerMove: (e: React.PointerEvent) => {
          const s = holdStartRef.current;
          if (!s) return;
          if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > HOLD_MOVE_CANCEL_PX) clearHold();
        },
        onPointerUp: clearHold,
        onPointerCancel: clearHold,
      };
    },
    [isMobile, clearHold],
  );
  // Clear any pending hold timer on unmount.
  useEffect(() => () => clearHold(), [clearHold]);
  // Dismiss a lifted page on any pointer-down outside its bin pill. A
  // document-level listener (not a full-screen catcher div) avoids
  // stacking-context traps: the lifted page lives inside the canvas's
  // transformed container, so a sibling overlay can't reliably sit beneath its
  // bin. The listener ignores taps on the bin so the delete still fires.
  useEffect(() => {
    if (liftedPageNumber == null) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-page-lift-bin]')) return; // let the bin handle it
      setLiftedPageNumber(null);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [liftedPageNumber]);

  // ── Reorder spreads via drag-drop in the bottom strip ──────────────────────
  // A spread = the pages open side by side, paired as the printed book
  // (editorSpreads.reorderSpreads: plain (1, 2), (3, 4)…; themed (—, 1),
  // (2, 3)…). Reordering moves the entire spread block in localPages and
  // renumbers the resulting pages sequentially so `page` stays 1-indexed and
  // contiguous. Photo assignments travel with their slots because we move the
  // whole page object, not just photo arrays. A themed book's S1 opening is
  // print page 1 — it is never moved off the front, nor anything put before it.
  //
  // Selecting which spread becomes the new active one after reorder:
  // we keep the moved spread in view (snapping currentSpread to the
  // destination index) so customers see their drop land.
  const handleReorderSpreads = useCallback((fromSpread: number, toSpread: number) => {
    if (fromSpread === toSpread) return;
    // A refused move (the opening, or out of range) changes nothing — not
    // even the open spread.
    const cur = localPagesRef.current;
    if (reorderSpreads(cur, fromSpread, toSpread, isThemedBook(cur)) === cur) return;
    setLocalPages((prev) => reorderSpreads(prev, fromSpread, toSpread, isThemedBook(prev)));
    setCurrentSpread(toSpread);
  }, []);

  // ── Apply layout to ALL pages ──────────────────────────────────────────────
  const handleApplyLayoutToAll = useCallback((layout: '1-up' | '2-up' | '3-up' | '4-up') => {
    const requiredSlots = { '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4 }[layout];
    setLocalPages((prev) => {
      const allReleased: string[] = [];
      const updated = prev.map((page) => {
        // « Page de fin » (lib/outroPage.ts): no grid, nothing to apply.
        if (isOutroPage(page)) return page;
        const keptPhotos = page.photos.filter(Boolean).slice(0, requiredSlots);
        const released = page.photos.filter(Boolean).slice(requiredSlots);
        allReleased.push(...released);
        return { ...page, layout, photos: keptPhotos };
      });
      if (allReleased.length > 0) setLocalUnassigned((u) => [...u, ...allReleased]);
      return updated;
    });
    // Slot count + arrangement just shifted across the whole album; clear
    // pan/scale state so every photo gets a fresh fill in its new cell
    // rather than inheriting positions from the previous layout.
    setSlotPositions({});
  }, []);

  // ── Page element manipulation ──────────────────────────────────────────────

  const generateElementId = () => `el-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  // initialText (new phone layout): the words were typed in a real <input> first —
  // the text lands written and selected, no edit mode / keyboard hand-off needed.
  const handleAddElement = useCallback((pageNumber: number, type: PageElement['type'], initialText?: string) => {
    // « Page de fin » (lib/outroPage.ts): the brand image only — no element
    // would be drawn on it, so none is added.
    if (isOutroPage(localPagesRef.current.find((p) => p.page === pageNumber))) return;
    hasAddedElementRef.current = true; // S4 smart-hint: the customer added something
    setShowTextHint(false);
    const id = generateElementId();
    // Raise the iOS keyboard from WITHIN this tap (text only). The new element
    // isn't in the DOM yet, so we focus the catcher now; TextOverlayChrome
    // transfers focus to the real contentEditable when it mounts in edit mode.
    if (type === 'text' && !initialText) { try { keyboardCatcherRef.current?.focus(); } catch { /* defensive */ } }
    const defaults: Record<string, Partial<PageElement>> = {
      text: {
        x: 10, y: 10, width: 80, height: 12,
        text: NEW_TEXT_PLACEHOLDER,
        // Default BLACK (2026-07-05, Yahya): white text was invisible on
        // blank/white pages. Customers recolour from the toolbar when the
        // text sits on a photo.
        fontFamily: "'Londrina Solid', sans-serif",
        fontSize: 18,
        fontColor: COLORS.dark,
        fontWeight: '600',
        textAlign: 'center',
        opacity: 1,
      },
      rect: {
        x: 15, y: 15, width: 70, height: 20,
        fill: `${COLORS.gold}20`,
        stroke: COLORS.gold,
        strokeWidth: 1,
        opacity: 0.8,
        borderRadius: 4,
      },
      ellipse: {
        x: 25, y: 25, width: 50, height: 30,
        fill: `${COLORS.gold}15`,
        stroke: COLORS.gold,
        strokeWidth: 1,
        opacity: 0.8,
      },
      photo: {
        // ISSUE 8 (2026-07-05): ~60% of the page, CENTERED — was a small 40%
        // box parked top-left that read as broken.
        x: 20, y: 20, width: 60, height: 60,
        opacity: 1,
      },
    };
    const element: PageElement = { id, type, ...defaults[type], ...(type === 'text' && initialText ? { text: initialText } : {}) } as PageElement;
    setLocalPages((prev) =>
      prev.map((p) =>
        p.page === pageNumber ? { ...p, elements: [...(p.elements ?? []), element] } : p,
      ),
    );
    setSelectedElementId(id);
    // Text enters edit mode immediately so the keyboard (already raised via the
    // catcher) lands on the new element and the customer can type at once.
    if (type === 'text' && !initialText) setEditingElementId(id);
    setActiveTool('select');
  }, []);

  // ── DETACH a slot photo → free photo ELEMENT (2026-07-06, Option B(a)) ──────
  // "Déplacer librement": lift a photo out of its (grid or template) slot and
  // drop it into the page's free-element layer at the slot's current rect, so it
  // gains the SAME Canva-style transform as the QR/text — drag anywhere, corner
  // resize, rotate, alignment guides. The slot is emptied (a template's frame
  // stays visible as an empty slot). rect is in page-percent (0-100), computed
  // by the caller (template = slot fractions; grid = DOM rect vs page rect).
  const handleDetachSlotToElement = useCallback((
    pageNumber: number,
    slotIdx: number,
    photoId: string,
    rect: { x: number; y: number; w: number; h: number },
  ) => {
    if (!photoId) return;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const width = clamp(rect.w, 5, 100);
    const height = clamp(rect.h, 5, 100);
    const x = clamp(rect.x, 0, 100 - width);
    const y = clamp(rect.y, 0, 100 - height);
    const id = generateElementId();
    const element: PageElement = { id, type: 'photo', photoId, x, y, width, height, rotation: 0, opacity: 1 };
    setLocalPages((prev) =>
      prev.map((p) => {
        if (p.page !== pageNumber) return p;
        const photos = [...p.photos];
        if (photos[slotIdx]) photos[slotIdx] = ''; // empty the slot (frame stays)
        return { ...p, photos, elements: [...(p.elements ?? []), element] };
      }),
    );
    // Single active selection → the new free element (clears slot selection).
    setSelectedSlotKey(null);
    setPanModeKey(null);
    setTemplateEditKey(null);
    setSelectedElementId(id);
    setSelectedElementIds(new Set([id]));
    setActiveTool('select');
  }, []);

  const handleUpdateElement = useCallback((pageNumber: number, elementId: string, updates: Partial<PageElement>) => {
    setLocalPages((prev) =>
      prev.map((p) => {
        if (p.page !== pageNumber) return p;
        return {
          ...p,
          elements: (p.elements ?? []).map((el) =>
            el.id === elementId ? { ...el, ...updates } : el,
          ),
        };
      }),
    );
  }, []);

  const handleDeleteElement = useCallback((pageNumber: number, elementId: string) => {
    setLocalPages((prev) =>
      prev.map((p) => {
        if (p.page !== pageNumber) return p;
        return { ...p, elements: (p.elements ?? []).filter((el) => el.id !== elementId) };
      }),
    );
    setSelectedElementId(null);
    setEditingElementId(null);
  }, []);

  // Moves an element from one page to another mid-drag. Removes from the
  // source page's elements, re-inserts in the target page with new (x, y)
  // in the target page's coordinate system. Used by PageElementOverlay
  // when the customer drags an element across the spine in a 2-page
  // spread.
  const handleMoveElementToPage = useCallback((
    fromPage: number,
    toPage: number,
    elementId: string,
    newX: number,
    newY: number,
  ) => {
    if (fromPage === toPage) return;
    setLocalPages((prev) => {
      let moving: PageElement | null = null;
      const stripped = prev.map((p) => {
        if (p.page !== fromPage) return p;
        const elements = p.elements ?? [];
        const found = elements.find((el) => el.id === elementId);
        // Carried to another page by hand: a caption becomes the person's own
        // text, or the follow pass would put it back on its photo's page.
        if (found) moving = detachCaption({ ...found, x: newX, y: newY });
        return { ...p, elements: elements.filter((el) => el.id !== elementId) };
      });
      if (!moving) return prev;
      return stripped.map((p) =>
        p.page === toPage ? { ...p, elements: [...(p.elements ?? []), moving!] } : p,
      );
    });
  }, []);

  // ── Intro page element handlers ────────────────────────────────────────────
  // Mirror the content-page handlers (handleAddElement / handleUpdateElement /
  // handleDeleteElement) but operate on the standalone `introElements` state
  // instead of localPages. Every position-bearing change is run through
  // clampIntroElementBox so no element can enter the bottom QR safe zone.
  const handleAddIntroElement = useCallback((type: PageElement['type']) => {
    hasAddedElementRef.current = true; // S4 smart-hint: the customer added something
    setShowTextHint(false);
    const id = generateElementId();
    if (type === 'text') { try { keyboardCatcherRef.current?.focus(); } catch { /* defensive */ } }
    // Same visual defaults as content pages, but anchored in the SAFE upper
    // area (well above the bottom QR band).
    const defaults: Record<string, Partial<PageElement>> = {
      text: {
        x: 10, y: 8, width: 80, height: 12,
        text: NEW_TEXT_PLACEHOLDER,
        // FIX (2026-06-10): the intro renders on a near-WHITE branding page, so
        // a white default was invisible — customers couldn't see what they
        // typed and kept re-adding (one order accumulated 18 empty white
        // blocks). Default to brand-dark so new text is legible immediately;
        // colour stays changeable via the toolbar.
        fontFamily: "'Londrina Solid', sans-serif",
        fontSize: 18,
        fontColor: COLORS.dark,
        fontWeight: '600',
        textAlign: 'center',
        opacity: 1,
      },
      rect: {
        x: 15, y: 12, width: 70, height: 20,
        fill: `${COLORS.gold}20`, stroke: COLORS.gold, strokeWidth: 1, opacity: 0.8, borderRadius: 4,
      },
      ellipse: {
        x: 25, y: 15, width: 50, height: 30,
        fill: `${COLORS.gold}15`, stroke: COLORS.gold, strokeWidth: 1, opacity: 0.8,
      },
      photo: { x: 10, y: 8, width: 40, height: 40, opacity: 1 },
    };
    const base = defaults[type];
    // Defensive clamp: guarantees the default box also respects the safe zone
    // even if a future default is authored lower on the page.
    const clamped = clampIntroElementBox(
      { y: base.y ?? 0, height: base.height ?? 0 },
      base,
    );
    const element: PageElement = { id, type, ...clamped } as PageElement;
    setIntroElements((prev) => [...prev, element]);
    setSelectedElementId(id);
    if (type === 'text') setEditingElementId(id);
    setActiveTool('select');
  }, []);

  const handleUpdateIntroElement = useCallback((elementId: string, updates: Partial<PageElement>) => {
    setIntroElements((prev) =>
      prev.map((el) => {
        if (el.id !== elementId) return el;
        // Clamp any y/height change out of the bottom QR safe zone.
        const safe = clampIntroElementBox({ y: el.y, height: el.height }, updates);
        return { ...el, ...safe };
      }),
    );
  }, []);

  const handleDeleteIntroElement = useCallback((elementId: string) => {
    setIntroElements((prev) => prev.filter((el) => el.id !== elementId));
    setSelectedElementId(null);
    setEditingElementId(null);
  }, []);

  const handleDuplicateIntroElement = useCallback((elementId: string) => {
    setIntroElements((prev) => {
      const src = prev.find((el) => el.id === elementId);
      if (!src) return prev;
      const id = generateElementId();
      const offset = clampIntroElementBox(
        { y: src.y, height: src.height },
        { x: Math.min(src.x + 4, 100 - src.width), y: src.y + 4 },
      );
      const clone: PageElement = { ...src, id, ...offset };
      setSelectedElementId(id);
      return [...prev, clone];
    });
  }, []);

  const handleToolClick = useCallback((pageNumber: number | null, tool: ActiveTool) => {
    if (tool === 'select') {
      setActiveTool('select');
      return;
    }
    if (!pageNumber) return;
    // For text/rect/ellipse/photo, add the element immediately
    handleAddElement(pageNumber, tool === 'text' ? 'text' : tool === 'photo' ? 'photo' : tool === 'rect' ? 'rect' : 'ellipse');
  }, [handleAddElement]);

  // ── Upload photos from within the editor (optimized HEIC pipeline) ──────────
  const handleEditorUpload = useCallback(async (files: FileList | File[]) => {
    if (!shopifyOrderId) return;

    setUploadNotice(null);
    setLastAddedPhotoIds([]); // clear the previous add-session's grid
    // Surface why a selection produces nothing instead of returning silently.
    // See lib/uploadFileFilter for the three iPhone failure modes.
    const verdict = classifyUploadSelection(files);
    const notice = uploadNoticeFor(verdict);
    if (!notice.proceed) {
      setShowUploadModal(true);
      setUploadNotice(notice.blocked);
      return;
    }
    if (notice.partial) setUploadNotice(notice.partial);
    const fileArr = verdict.accepted;

    // Dedup against existing photos by name+size
    const existingNames = new Set(allPhotos.map((p) => p.original_filename?.toLowerCase()));
    const uniqueFiles = fileArr.filter((f) => !existingNames.has(f.name.toLowerCase()));
    if (uniqueFiles.length === 0) {
      setShowUploadModal(true);
      setUploadNotice('Ces photos sont déjà dans votre album.');
      return;
    }

    // Ensure session exists
    let sid = session?.id;
    if (!sid) {
      const dest = order?.destination_1 ?? null;
      const yr = order?.year_1 ?? null;
      const res = await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
        destination: dest, year: yr, status: 'uploading',
      });
      sid = res.session.id;
    }

    failedUploadsRef.current = [];
    // Build upload state with converting status for HEIC files
    setUploadingFiles(uniqueFiles.map((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      const isHeic = ext === 'heic' || ext === 'heif';
      return { name: f.name, progress: 0, status: isHeic ? 'converting' as const : 'pending' as const };
    }));
    setShowUploadModal(true);

    // Start HEIC conversions immediately (semaphore-limited to 2 concurrent)
    const heicPromises = new Map<number, Promise<File>>();
    for (let i = 0; i < uniqueFiles.length; i++) {
      const ext = uniqueFiles[i].name.split('.').pop()?.toLowerCase();
      if (ext === 'heic' || ext === 'heif') {
        const cacheKey = `editor-${uniqueFiles[i].name}-${uniqueFiles[i].size}`;
        heicPromises.set(i, startEditorHeicConversion(cacheKey, uniqueFiles[i]));
      }
    }

    const authHeaders: Record<string, string> = {};
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (authSession?.access_token) authHeaders['Authorization'] = `Bearer ${authSession.access_token}`;

    // Process files in batches of 10 (was 6) — Supabase Storage handles this
    // fine; monitor for rate-limit errors and drop back if needed.
    const addedIds: string[] = [];
    // BATCH=4 (was 10): at most 4 full-res images decode at once. 10 concurrent
    // full-res decodes spiked memory ~hundreds of MB → instant iOS tab-kill.
    // Throughput is network-PUT-bound, so wall-time barely changes.
    const BATCH = 4;
    for (let i = 0; i < uniqueFiles.length; i += BATCH) {
      const batch = uniqueFiles.slice(i, i + BATCH);
      await Promise.all(batch.map(async (file, batchIdx) => {
        const idx = i + batchIdx;
        try {
          let uploadFile = file;
          let ct = file.type || 'image/jpeg';
          const ext = file.name.split('.').pop()?.toLowerCase();

          // HEIC: await semaphore-gated conversion (already started above)
          if (ext === 'heic' || ext === 'heif') {
            setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, status: 'converting' } : f));
            try {
              uploadFile = await (heicPromises.get(idx) ?? startEditorHeicConversion(`editor-${file.name}-${file.size}`, file));
              ct = 'image/jpeg';
            } catch {
              // Conversion failed — upload original HEIC as fallback
            }
          } else {
            // Off-main-thread (shared worker): decode + bake EXIF orientation +
            // resize in ONE pass — same engine as the step flow. This stops the
            // main-thread decode from hanging/OOM-ing on iPhone (the "fails to
            // upload" half of the bug). Falls back to the main-thread path if
            // the worker pipeline is unavailable.
            const out = await processForUpload(file, MAX_PHOTO_DIM, JPEG_QUALITY);
            if (out) {
              uploadFile = new File([out], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
              ct = 'image/jpeg';
            } else {
              const upright = await normalizeJpegOrientation(file);
              uploadFile = await resizeIfNeeded(upright);
              ct = uploadFile.type || ct;
            }
          }

          setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, status: 'uploading' } : f));
          const content_hash = await sha256Hex(uploadFile).catch(() => null);
          const resp = await photosApi.getUploadUrl(sid!, uploadFile.name, ct, {
            content_hash,
            file_size_kb: Math.round(uploadFile.size / 1024),
          });
          // Silent dedup (FIX 1): photo already in this album — skip the PUT.
          // The worker created no row; the tray already shows the original.
          if (resp.duplicate) {
            console.log(`[upload] photo en double ignorée: ${uploadFile.name}`);
            setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, status: 'done', progress: 100 } : f));
            return;
          }
          if (!resp.upload_url || !resp.photo_id) throw new Error('Réponse upload-url invalide');
          const { upload_url, photo_id, thumbnail_upload_url, thumbnail_storage_path, tile_upload_url, tile_storage_path } = resp;
          // Small grid/editor tier (off-thread worker), so the tray + modal
          // render light thumbnails instead of the full-res original — matches
          // the step flow. Best-effort; the grid falls back to the original.
          const thumbBlob = thumbnail_upload_url
            ? await processForUpload(uploadFile, THUMB_DIM, THUMB_QUALITY)
            : null;
          const thumbFile = thumbBlob ? new File([thumbBlob], 'thumbnail.jpg', { type: 'image/jpeg' }) : null;
          // MIGRATION 128 — the ~320px tray tile, made in the same off-thread
          // pass. It replaces a BILLED Supabase image-transform (see the
          // worker's lib/photoTiers.ts). Best-effort: if it fails, the tray
          // simply falls back to the transform, exactly as before.
          const tileBlob = tile_upload_url
            ? await processForUpload(uploadFile, TILE_MAX_DIM, TILE_JPEG_QUALITY).catch(() => null)
            : null;
          const tileFile = tileBlob ? new File([tileBlob], 'tile.jpg', { type: 'image/jpeg' }) : null;
          // The worker created the photo row before this PUT — if the PUT
          // fails, best-effort delete the orphan row so it doesn't render as a
          // broken slot or inflate the admin count. See lib/uploadOrphanCleanup.
          await putOrCleanupOrphan(
            photo_id,
            () => uploadToSignedUrl(upload_url, uploadFile, ct, (pct) => {
              setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, progress: pct } : f));
            }),
            (id) => photosApi.delete(id),
          );
          if (thumbFile && thumbnail_upload_url && thumbnail_storage_path) {
            try {
              await uploadToSignedUrl(thumbnail_upload_url, thumbFile, 'image/jpeg');
              // The tile rides the same stamp when it uploaded cleanly; a
              // failed tile still lets the thumbnail be recorded.
              let tileOk = false;
              if (tileFile && tile_upload_url && tile_storage_path) {
                tileOk = await uploadToSignedUrl(tile_upload_url, tileFile, 'image/jpeg')
                  .then(() => true, () => false);
              }
              await photosApi.thumbnailDone(
                photo_id, thumbnail_storage_path, undefined,
                tileOk ? tile_storage_path : null,
              ).catch(() => { /* non-fatal */ });
            } catch { /* non-fatal — grid falls back to the original */ }
          }
          // Analyze (face detection + EXIF) on the light tier when available —
          // lower memory than full-res (matches the step flow's deferred path).
          await analyzePhoto(thumbFile ?? uploadFile, photo_id, API_BASE, authHeaders);
          addedIds.push(photo_id);
          setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, status: 'done', progress: 100 } : f));
        } catch (e) {
          failedUploadsRef.current.push(file);
          setUploadingFiles((prev) => prev.map((f, j) => j === idx ? { ...f, status: 'error', error: uploadFailureReason(e) } : f));
        }
      }));
    }

    // Refresh photos list, then surface the just-added photos as a square grid
    // in the modal (bug 3 — "show them in the grid").
    queryClient.invalidateQueries({ queryKey: ['editor-photos', sid] });
    setLastAddedPhotoIds(addedIds);
    // Free the converted-HEIC File refs now the batch is uploaded — this cache
    // was never cleared and grew for the whole session (memory; bug 4a). The
    // off-thread worker resize + analyzing the 1600px thumbnail (above) are the
    // bigger peak-memory wins that keep iOS from killing the tab.
    editorHeicCache.clear();
  }, [shopifyOrderId, albumIndex, session?.id, order, allPhotos, queryClient]);

  // ── Generate layout from within editor ─────────────────────────────────
  const handleGenerateFromEditor = useCallback(async () => {
    if (!session?.id || !order) return;
    setAiError(null);
    setIsGeneratingLayout(true);

    // Read the latest allPhotos via ref. The deps array intentionally
    // omits allPhotos to keep the callback identity stable across photo
    // loads, but that means the captured closure becomes [] when the
    // callback was created before photos resolved. D74's trace showed
    // exactly that: allPhotos=0 at every pass while the user clearly had
    // 58 loaded photos in the sidebar. Reading from the ref restores the
    // ground truth without touching deps.
    // ⚠️ NOT A HELD SIMILAR SHOT (owner, 2026-10-24, migration 267): a burst's
    // other shots wait in the tray — unless she already put one on a page.
    // Hidden duplicates never reach this list at all (the worker leaves them out).
    const allPhotos = forAutomaticLayout(allPhotosRef.current, placedIdsOf(localPagesRef.current));

    // Vision-gate (Issue 1 / D74): if vision tagging is in-flight, or any
    // photo still lacks vision_tags, wait for the batch before calling the
    // AI layout endpoint. Without this gate, regen ships layouts that were
    // computed on partial vision_tags — hero-grade newborn close-ups that
    // hadn't been analyzed yet get downgraded to small slots or unplaced.
    // The visible signal is the existing `visionProgress` pill in the
    // sidebar; we don't add a toast (no toast lib in this codebase).
    const stillUnanalyzed = allPhotos.filter(
      (p) => !(p as { vision_tags?: unknown }).vision_tags,
    ).length;
    if (visionInFlightRef.current || stillUnanalyzed > 0) {
      // eslint-disable-next-line no-console
      console.log(`[regen-trace] vision-gate hit: inFlight=${visionInFlightRef.current}, unanalyzed=${stillUnanalyzed}`);
      if (!visionInFlightRef.current && stillUnanalyzed > 0) {
        void handleVisionAnalyzeAll();
      }
      // Give vision a BOUNDED head-start so tags improve the first layout, but
      // NEVER block the layout on it. Big albums (100+ photos) can't finish
      // vision within any reasonable wait, and the old code ABORTED regen on
      // timeout — that's the "clicked Régénérer, got zero pages, no error" bug.
      // Vision tags are an enhancement, not a requirement: both the AI prompt
      // and the deterministic engine fall back to face_count when tags are
      // missing. So on timeout we PROCEED and let vision keep running in the
      // background; the next regen picks up the freshly-written tags.
      const waitMs = stillUnanalyzed > 40 ? 12_000 : 30_000;
      const deadline = Date.now() + waitMs;
      while (visionInFlightRef.current && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 250));
      }
      if (visionInFlightRef.current) {
        // eslint-disable-next-line no-console
        console.warn('[regen-trace] vision still running — generating layout now (tags will sharpen the next regen)');
      }
      // Refresh photos so the post-AI passes see whatever tags DID land. The
      // AI endpoint reads tags from DB at call time, so it always uses the
      // freshest available; client-side passes read from this closure.
      await queryClient.invalidateQueries({ queryKey: ['editor-photos', session.id] });
    }

    try {
      // TARGET page count: the slider's requested value when set (so a request
      // for FEWER pages reaches the worker and repacks photos), else the current
      // interior page count, else the order value / 24 for a fresh album.
      const target = pageCountTargetRef.current;
      // « Pages de fin » (lib/outroPage.ts) are not the AI's to lay out: they
      // stay where she put them (re-inserted below), so the photo layout is
      // asked for the rest of the count.
      const outroCount = countOutroPages(localPagesRef.current);
      const totalPages = Math.max(1, (target && target > 0 ? target : (localPagesRef.current.length || order.page_count || 24)) - outroCount);
      // The customer's LIVE layout choice from the "Mises en page" toggle. Sent
      // as both layout_preference and layout_override so it wins over a possibly
      // stale stored order.layout on the worker.
      const pref = layoutChoiceRef.current === 'one' ? '1 image par page' : '1 à 4 images par page';
      // Reuse the pre-warmed call if it's for this session. Once consumed,
      // null it out so re-generations issue a fresh call. (Any slider or toggle
      // change already dropped the prewarm, so a survivor matches pref+count.)
      const usePrewarm = prewarmRef.current && prewarmKeyRef.current === session.id;
      const result = usePrewarm
        ? await prewarmRef.current!
        // use_upload_order MUST be sent in the BODY: the body flag keeps the
        // AI + templates and hard-enforces photo order post-AI. Omitting it
        // while session.use_upload_order=true trips the worker's legacy
        // forceDeterministic branch → no AI, no templates (bug: Models ON +
        // Ordre d'upload produced zero templates).
        : await aiApi.generateLayout(session.id, totalPages, pref, { shopify_order_id: shopifyOrderId ?? '', use_templates: useTemplates, use_upload_order: useUploadOrder, layout_override: pref });
      if (usePrewarm) {
        prewarmRef.current = null;
        prewarmKeyRef.current = null;
      }

      // Log: AI generated a layout. Snapshot the proposal so we can later
      // diff it against what the customer actually shipped.
      aiApi.feedback(session.id, 'ai_layout_generated', {
        prewarmed: usePrewarm,
        page_count: result.layout?.pages?.length ?? 0,
        unassigned_count: result.layout?.unassigned_photos?.length ?? 0,
        pages: result.layout?.pages ?? [],
      });
      // Apply layout to local state. The whole post-AI pipeline lives in
      // `composeFinalLayout` (also reused by the magic-upload flow) so we
      // never drift between the two entry points.
      if (result.layout?.pages?.length > 0) {
        const albumCategory = ((session as { album_category?: AlbumCategory | null } | null)?.album_category ?? null);
        // Snapshot the PRE-regen pages BEFORE composeFinalLayout overwrites
        // local state. We harvest the customer's custom text/shape elements
        // off these (the AI result carries NO `elements` field), then
        // re-attach them onto the fresh layout by page number below.
        const prevPages = localPagesRef.current;

        // BASE LAYOUT RULES (migration 234): the server's composer already
        // applied every rule — order, one main per spread, face-safe framing.
        // The client passes would undo them (re-pack, re-crop), so its pages
        // and framings are taken exactly as they come.
        const fromBaseComposer = (result as { source?: string }).source === 'base-v1';
        const composed = fromBaseComposer
          ? {
              pages: result.layout.pages as LayoutPage[],
              unassigned: [] as string[],
              slotPositions: Object.fromEntries((result.layout.pages as Array<LayoutPage & { slot_positions?: Record<string, SlotPosition> }>)
                .flatMap((pg) => Object.entries(pg.slot_positions ?? {}).map(([k, v]) => [`p${pg.page}-${k}`, v] as const))) as SlotPositionMap,
            }
          : composeFinalLayout(result.layout, allPhotos, albumCategory, {
            trace: true,
            useUploadOrder,
            // The count the CUSTOMER asked for — the variety passes need it to
            // know how much room they have. Without it they measured against
            // the layout they were handed and happily doubled it.
            targetPages: totalPages,
          });
        // The album is now framed (or no longer framed): the page view follows.
        setPhotoGap((prev) => fromBaseComposer
          ? { mm: BASE_GAP_MM, color: prev.color, baseRules: true }
          : { mm: prev.mm, color: prev.color });
        const facePositions = composed.slotPositions;
        const remaining = composed.unassigned;

        // ── (1) TEXT/SHAPE ELEMENTS — re-attach by page number ───────────────
        // The AI controls the PHOTO layout; the customer's overlay text/shapes
        // persist on their page (nearest survivor when the page vanished).
        // ⚠️ #WA-510802: only HER elements travel (no `layer`), and they are
        // ADDED to the fresh page's own — this used to REPLACE them, so a fresh
        // model page kept the OLD model's windows and its new photos had none
        // (lib/themedPageEdits.reattachAfterRegen).
        // « PAGES DE FIN » (lib/outroPage.ts) — the photo layout is rebuilt, her
        // outro pages are not: each goes back to the same array index (clamped
        // to the new length), and the photo pages' framings follow the shift.
        // None placed → exactly the code path of before.
        const { content: prevContent, outros: prevOutros } = splitOutroPages(prevPages);
        let finalPages: LayoutPage[] = reattachAfterRegen(prevOutros.length ? prevContent : prevPages, composed.pages);
        let facePositionsFinal = facePositions;
        if (prevOutros.length) {
          const kept = reinsertOutroPages(finalPages, prevOutros);
          finalPages = kept.pages;
          facePositionsFinal = remapPageKeyed(facePositions, kept.pageMap);
        }

        // ── (2)+(3) PHOTO MANUAL POSITIONS — follow the photo, merge over AI ──
        // For each slot in the fresh layout, if its photo has a recorded manual
        // edit AND the slot's AR matches the AR the edit was made in, the manual
        // pan/scale is geometrically valid → use it instead of the AI
        // face-translate. Otherwise keep the AI position (incompatible-slot
        // fallback). Manual map keyed BY PHOTO, so the edit follows the photo.
        const AR_EPS = 0.02;
        const manualMap = manualSlotEditsRef.current;
        const mergedPositions: SlotPositionMap = { ...facePositionsFinal };
        const incompatible: { photoId: string; page: number; slot: number }[] = [];
        for (const page of finalPages) {
          for (let si = 0; si < page.photos.length; si++) {
            const pid = page.photos[si];
            if (!pid) continue;
            const edit = manualMap[pid];
            if (!edit) continue;
            const liveAR = slotARForPage(page, si);
            const arOk =
              edit.ar != null && liveAR != null && Math.abs(liveAR - edit.ar) <= AR_EPS;
            const key = `p${page.page}-s${si}`;
            if (arOk) {
              mergedPositions[key] = { ...edit.pos, _v: 2 };
            } else {
              // Photo landed in a different-AR (or templated) slot — the saved
              // pan/scale would crop wrong, so fall back to the AI position.
              incompatible.push({ photoId: pid, page: page.page, slot: si });
            }
          }
        }
        if (incompatible.length > 0) {
          // eslint-disable-next-line no-console
          console.log('[regen-trace] manual edits not re-applied (incompatible slot AR):', incompatible);
        }

        setLocalPages(finalPages);
        setLocalUnassigned(remaining);
        setSlotPositions(mergedPositions);
        // Trigger the gold-cascade reveal on the current spread. Bumped
        // AFTER state writes so React batches them into the same render
        // and the fade-in plays on the fresh layout, not the previous one.
        setAiRevealKey((k) => k + 1);
        // Save — embed the MERGED positions (manual-over-AI) + re-attached
        // elements into the page payload so the DB record matches local state
        // immediately (autosave is debounced 1.5s, which left a gap where a
        // fast nav lost them).
        await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
          // Preserve the client's intro overlay layer across an AI regen — the
          // pages JSONB is replaced wholesale, so omitting intro_elements would
          // drop them. Likewise re-persist the manual-edit-by-photo memory.
          pages: {
            pages: withSlotPositions(finalPages, mergedPositions),
            unassigned_photos: remaining,
            intro_elements: introElements,
            manual_slot_edits: manualMap,
            use_templates: useTemplates,
            ...(clientOrderRef.current ? { client_order: clientOrderRef.current } : {}),
          },
        });
        queryClient.invalidateQueries({ queryKey: ['session', shopifyOrderId, albumIndex] });
      }
    } catch (e) {
      console.error('[Editor] Layout generation failed', e);
      setAiError("L'IA n'a pas pu générer cette fois — réessayez dans un instant.");
    } finally {
      setIsGeneratingLayout(false);
      setShowUploadModal(false);
    }
    // handleVisionAnalyzeAll is referenced inside but declared later in the
    // component body (TDZ if included in deps). Its identity is stable per
    // session so the closure never goes stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, order, shopifyOrderId, albumIndex, queryClient, useUploadOrder, useTemplates]);

  // ── Clear all placed photos ────────────────────────────────────────────
  const handleClearAllPlaced = useCallback(() => {
    setLocalPages((prev) => {
      const allReleased: string[] = [];
      const cleared = prev.map((p) => {
        allReleased.push(...p.photos.filter(Boolean));
        return { ...p, photos: [] };
      });
      setLocalUnassigned((u) => [...u, ...allReleased]);
      return cleared;
    });
    setSlotPositions({});
    setSelectedSlotKey(null);
    setPanModeKey(null);
  }, []);

  // ── Smart rotate: try all 3 rotations, pick the one that maximises upright
  // face-detection confidence, re-encode + overwrite storage.
  // The right fix for sideways photos that lack EXIF orientation (WhatsApp
  // strips it). Single click = correct orientation, even when face-api can't
  // detect ANY face at the current orientation.
  const [rotatingPhotoId, setRotatingPhotoId] = useState<string | null>(null);

  // Internal helper: given a photo id, pick the best orientation (0/90/180/270)
  // by face-detection confidence and persist it. When `auto` is true the 0°
  // candidate is included, so a correctly-oriented photo is left untouched —
  // exactly what we want for a batch "auto-orient everything" pass. The
  // manual rotate button keeps the old behaviour (omit 0°) so a click always
  // rotates SOMETHING, matching the customer's mental model of a rotate
  // affordance.
  const runRotatePhoto = useCallback(async (photoId: string, opts: { auto: boolean }) => {
    const photo = allPhotos.find((p) => p.id === photoId);
    if (!photo?.signed_url) return { rotated: false, reason: 'no-photo' as const };
    const blob = await (await fetch(photo.signed_url)).blob();
    const objectUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);

    const faceapi = await import('face-api.js');
    const modelsLoaded = (faceapi.nets.ssdMobilenetv1 as { isLoaded: boolean }).isLoaded;
    if (!modelsLoaded) {
      await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
    }
    const detectOpts = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.4 });

    type Deg = 0 | 90 | 180 | 270;
    type Candidate = { deg: Deg; canvas: HTMLCanvasElement; score: number };
    const degrees: Deg[] = opts.auto ? [0, 90, 180, 270] : [90, 180, 270];
    const candidates: Candidate[] = [];
    for (const deg of degrees) {
      const swap = deg === 90 || deg === 270;
      const cw = swap ? img.naturalHeight : img.naturalWidth;
      const ch = swap ? img.naturalWidth : img.naturalHeight;
      const c = document.createElement('canvas');
      c.width = cw; c.height = ch;
      const cctx = c.getContext('2d');
      if (!cctx) continue;
      cctx.translate(cw / 2, ch / 2);
      cctx.rotate((deg * Math.PI) / 180);
      cctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      const detections = await faceapi.detectAllFaces(c, detectOpts);
      const score = detections.reduce((s, d) => s + d.score, 0);
      candidates.push({ deg, canvas: c, score });
    }

    // Pick the best. In auto mode, only commit a rotation if a non-zero
    // candidate beats 0° by a clear margin (5% confidence) — otherwise we'd
    // happily rotate landscape photos with zero faces into random new
    // orientations on every batch run.
    const zero = candidates.find((c) => c.deg === 0);
    let best: Candidate = zero ?? candidates[0];
    for (const c of candidates) {
      if (c.score > best.score) best = c;
    }
    if (opts.auto && zero && best.deg !== 0 && best.score - zero.score < 0.5) {
      best = zero;
    }
    if (best.deg === 0) return { rotated: false, reason: 'already-upright' as const };

    // ⚠️ THE ANGLE IS CHOSEN ON THE SMALL COPY; THE ORIGINAL IS WHAT TURNS
    // (2026-09-26, owner: « we need to keep the high quality of the image »).
    // The rotated bytes REPLACE the print file and post-rotate deletes the old
    // one — encoding `best.canvas`, the 1400 px quality-62 display copy, shrank
    // every rotated photo to ≤1400 px for print (26 photos in 10 albums, the
    // full originals gone). No original, no rotation: refusing beats a blur.
    const originalUrl = photo.original_signed_url ?? null;
    if (!originalUrl) return { rotated: false, reason: 'no-original' as const };
    const origBlob = await (await fetch(originalUrl)).blob();
    // 'from-image' so the turn starts from what the editor shows (the display
    // tier honours EXIF); the re-encoded JPEG carries no tag, so no double turn.
    const origBmp = await createImageBitmap(origBlob, { imageOrientation: 'from-image' });
    const swapFull = best.deg === 90 || best.deg === 270;
    const full = document.createElement('canvas');
    full.width = swapFull ? origBmp.height : origBmp.width;
    full.height = swapFull ? origBmp.width : origBmp.height;
    const fctx = full.getContext('2d');
    if (!fctx) { origBmp.close?.(); throw new Error('canvas 2d unavailable'); }
    fctx.translate(full.width / 2, full.height / 2);
    fctx.rotate((best.deg * Math.PI) / 180);
    fctx.drawImage(origBmp, -origBmp.width / 2, -origBmp.height / 2);
    origBmp.close?.();

    // Encode + upload the ORIGINAL, turned.
    const newBlob: Blob = await new Promise((resolve, reject) =>
      full.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.92),
    );
    // The worker signs a NEW versioned path (cache-bust fix — an in-place
    // overwrite left stale renders in the image-transform cache, printing the
    // pre-rotate orientation). Pass it back so post-rotate repoints the row.
    const { upload_url, storage_path } = await photosApi.overwriteUrl(photoId);
    const rotatedFile = new File([newBlob], `${photoId}.jpg`, { type: 'image/jpeg' });
    await uploadToSignedUrl(upload_url, rotatedFile, 'image/jpeg');
    await photosApi.postRotate(photoId, {
      new_width: full.width,
      new_height: full.height,
      storage_path,
    });
    return { rotated: true, deg: best.deg };
  }, [allPhotos]);

  const handleRotatePhoto = useCallback(async (photoId: string) => {
    if (rotatingPhotoId) return; // serial — one at a time
    setRotatingPhotoId(photoId);
    try {
      await runRotatePhoto(photoId, { auto: false });
      await queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
    } catch (e) {
      console.warn('[Editor] rotate failed', e);
    } finally {
      setRotatingPhotoId(null);
    }
  }, [rotatingPhotoId, runRotatePhoto, queryClient, session?.id]);

  // Vision tagging (Phase 5) — Gemini Flash analyses every photo and the
  // results live in `photo.vision_tags`. Runs automatically in the
  // background as soon as the editor mounts with photos that haven't
  // been analyzed yet. Cost is ~$0.001 per photo; per-album cap ~$0.07.
  //
  // CRITICAL: the trigger uses a ref-lock (visionInFlightRef), NOT React
  // state, because React's state updates create new identities on every
  // render and would cascade through `useCallback` deps and re-trigger
  // the effect — burning Gemini tokens on every navigation. The lock is
  // process-local; the worker is also idempotent (cached 7 days) so even
  // a double-fire wouldn't actually re-call Gemini.
  const [visionProgress, setVisionProgress] = useState<{ phase: 'running' | 'done'; total: number; analyzed: number; cached: number; failed: number } | null>(null);
  const visionInFlightRef = useRef(false);
  const handleVisionAnalyzeAll = useCallback(async () => {
    if (!session?.id) return;
    if (visionInFlightRef.current) return;
    visionInFlightRef.current = true;
    setVisionProgress({ phase: 'running', total: 0, analyzed: 0, cached: 0, failed: 0 });
    try {
      const r = await photosApi.visionAnalyzeSession(session.id);
      setVisionProgress({ phase: 'done', total: r.total, analyzed: r.analyzed, cached: r.cached, failed: r.failed });
      await queryClient.invalidateQueries({ queryKey: ['editor-photos', session.id] });
      setTimeout(() => setVisionProgress(null), 6000);
    } catch (e) {
      console.warn('[vision] batch failed', e);
      setVisionProgress(null);
    } finally {
      visionInFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, queryClient]);

  // Auto-trigger REMOVED at user request — the on-mount batch fired every
  // editor open (even with cached tags it still ran the request and showed
  // the "Analyse IA en cours…" pill), which was visual noise. The Magic
  // AI handler above (regen flow) already runs the batch on-demand and
  // waits for it before computing the layout, so we lose nothing by
  // dropping the mount-time fire.
  void handleVisionAnalyzeAll;  // kept in scope for regen on-demand use

  // Batch auto-orient: scan every photo, only rotate when face detection
  // says the current orientation is clearly worse than a 90/180/270 variant.
  // Used by the "Pivoter auto" sidebar button so customers don't have to
  // hunt for sideways photos one by one.
  const [autoOrientProgress, setAutoOrientProgress] = useState<{ done: number; total: number } | null>(null);
  const handleAutoOrientAll = useCallback(async () => {
    if (rotatingPhotoId || autoOrientProgress) return;
    const photos = allPhotos.filter((p) => !!p.signed_url);
    if (photos.length === 0) return;
    setAutoOrientProgress({ done: 0, total: photos.length });
    let rotatedCount = 0;
    for (let i = 0; i < photos.length; i++) {
      try {
        const r = await runRotatePhoto(photos[i].id, { auto: true });
        if (r.rotated) rotatedCount++;
      } catch (e) {
        console.warn('[auto-orient] failed for', photos[i].id, e);
      }
      setAutoOrientProgress({ done: i + 1, total: photos.length });
    }
    setAutoOrientProgress(null);
    if (rotatedCount > 0) {
      await queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
    }
  }, [rotatingPhotoId, autoOrientProgress, allPhotos, runRotatePhoto, queryClient, session?.id]);

  // ── Admin orientation backfill (fixes legacy photos for the regenerate PDF) ──
  // Re-stores each photo's FULL-RES ORIGINAL as the exact upright pixels the
  // editor displays. The browser <img> decode applies EXIF (incl. garbled tags
  // exifr missed, the WhatsApp/#1079 case), so drawing at 0° bakes the upright
  // pixels and re-encoding strips the tag — making the EXIF-blind worker PDF
  // (regenerate) match the editor. NOT face-detection (no guessing): it simply
  // makes the stored original equal what's on screen. Quality only — full
  // resolution, no downscale. Admin-only.
  const runNormalizeOrientation = useCallback(async (photoId: string) => {
    const photo = allPhotos.find((p) => p.id === photoId);
    if (!photo) return { normalized: false as const };
    const origUrl = pdfPhotoSourceUrl(photo);                       // full-res original (printed)
    const refUrl = photo.thumbnail_signed_url ?? photo.signed_url ?? null; // editor's display = TRUTH
    if (!origUrl) return { normalized: false as const };

    const load = (url: string) =>
      fetch(url).then((r) => r.blob()).then((b) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const u = URL.createObjectURL(b);
          const el = new Image();
          el.onload = () => { URL.revokeObjectURL(u); resolve(el); };
          el.onerror = () => { URL.revokeObjectURL(u); reject(new Error('decode failed')); };
          el.src = u;
        }),
      );
    // Rotate src into a fresh canvas (deg = clockwise; swaps dims for 90/270).
    const rotated = (src: CanvasImageSource, sw: number, sh: number, deg: number) => {
      const swap = deg === 90 || deg === 270;
      const c = document.createElement('canvas');
      c.width = swap ? sh : sw; c.height = swap ? sw : sh;
      const cx = c.getContext('2d')!;
      cx.translate(c.width / 2, c.height / 2);
      cx.rotate((deg * Math.PI) / 180);
      cx.drawImage(src, -sw / 2, -sh / 2, sw, sh);
      return c;
    };
    // 32×32 grayscale signature for content comparison (orientation-agnostic size).
    const sig = (src: CanvasImageSource): Float64Array => {
      const n = 32;
      const c = document.createElement('canvas'); c.width = n; c.height = n;
      const cx = c.getContext('2d')!; cx.drawImage(src, 0, 0, n, n);
      const d = cx.getImageData(0, 0, n, n).data;
      const g = new Float64Array(n * n);
      for (let i = 0; i < n * n; i++) g[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
      return g;
    };
    const mse = (a: Float64Array, b: Float64Array) => {
      let s = 0; for (let i = 0; i < a.length; i++) { const dd = a[i] - b[i]; s += dd * dd; } return s / a.length;
    };

    const orig = await load(origUrl);
    const ow = orig.naturalWidth, oh = orig.naturalHeight;
    if (!ow || !oh) return { normalized: false as const };

    // No reference thumbnail → can't safely decide; leave it untouched.
    if (!refUrl) return { normalized: false as const, reason: 'no-ref' };
    let ref: HTMLImageElement;
    try { ref = await load(refUrl); } catch { return { normalized: false as const, reason: 'ref-failed' }; }
    const refSig = sig(ref);

    // Pick the rotation of the ORIGINAL whose content best matches the editor's
    // displayed thumbnail. 0° best → already correct, leave it (don't re-encode).
    let bestDeg = 0, bestScore = Infinity;
    for (const deg of [0, 90, 180, 270]) {
      const cand = rotated(orig, ow, oh, deg);
      const score = mse(sig(cand), refSig);
      if (score < bestScore) { bestScore = score; bestDeg = deg; }
    }
    if (bestDeg === 0) return { normalized: false as const, reason: 'already-matches' };

    // Bake the chosen rotation at FULL resolution, re-upload, sync tiers.
    const out = rotated(orig, ow, oh, bestDeg);
    const newBlob: Blob = await new Promise((resolve, reject) =>
      out.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.95),
    );
    // Versioned path (cache-bust fix) — see runRotatePhoto for rationale.
    const { upload_url, storage_path } = await photosApi.overwriteUrl(photoId);
    await uploadToSignedUrl(upload_url, new File([newBlob], `${photoId}.jpg`, { type: 'image/jpeg' }), 'image/jpeg');
    await photosApi.postRotate(photoId, { new_width: out.width, new_height: out.height, storage_path });
    return { normalized: true as const, deg: bestDeg };
  }, [allPhotos]);

  const [normalizeProgress, setNormalizeProgress] = useState<{ done: number; total: number } | null>(null);
  const handleNormalizeAllOrientations = useCallback(async () => {
    if (rotatingPhotoId || autoOrientProgress || normalizeProgress) return;
    const photos = allPhotos.filter((p) => !!pdfPhotoSourceUrl(p));
    if (photos.length === 0) return;
    if (!window.confirm(
      `Redresser l'orientation de ${photos.length} photo(s) pour le PDF ? ` +
      `À lancer sur les anciennes commandes (ex. #1079) dont le PDF sort tourné. Qualité préservée.`,
    )) return;
    setNormalizeProgress({ done: 0, total: photos.length });
    for (let i = 0; i < photos.length; i++) {
      try {
        await runNormalizeOrientation(photos[i].id);
      } catch (e) {
        console.warn('[normalize] failed for', photos[i].id, e);
      }
      setNormalizeProgress({ done: i + 1, total: photos.length });
    }
    setNormalizeProgress(null);
    await queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
  }, [rotatingPhotoId, autoOrientProgress, normalizeProgress, allPhotos, runNormalizeOrientation, queryClient, session?.id]);

  // ── Delete photo from session entirely ─────────────────────────────────
  const handleDeletePhoto = useCallback(async (photoId: string) => {
    // Remove from placed pages
    setLocalPages((prev) =>
      prev.map((page) => ({
        ...page,
        photos: page.photos.filter((id) => id !== photoId),
      })),
    );
    setLocalUnassigned((u) => u.filter((id) => id !== photoId));
    if (session?.id) aiApi.feedback(session.id, 'photo_deleted', { photo_id: photoId });
    // Delete from server
    try {
      await photosApi.delete(photoId);
      queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
    } catch (e) {
      console.warn('[Editor] Delete photo failed', e);
    }
  }, [session?.id, queryClient]);

  // ── Bulk delete (mobile multi-select) ─────────────────────────────────────
  // Reuses the per-photo editor delete (photosApi.delete) with bounded
  // concurrency (~5). No batch editor endpoint exists — upload-link's
  // deletePhotos is a different (public-token) surface. Per-album scoping is
  // automatic: ids come only from this album's photosSortedByDate.
  const handleBulkDeletePhotos = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkDeleting(true);
    // Drop them from placed pages / unassigned up-front (same as single delete).
    const idSet = new Set(ids);
    setLocalPages((prev) =>
      prev.map((page) => ({
        ...page,
        photos: page.photos.filter((id) => !idSet.has(id)),
      })),
    );
    setLocalUnassigned((u) => u.filter((id) => !idSet.has(id)));
    if (session?.id) ids.forEach((id) => aiApi.feedback(session.id!, 'photo_deleted', { photo_id: id }));
    // Bounded-concurrency delete loop (pool of 5).
    const queue = [...ids];
    const CONCURRENCY = 5;
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        if (!id) break;
        try {
          await photosApi.delete(id);
        } catch (e) {
          console.warn('[Editor] Bulk delete failed for', id, e);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));
    await queryClient.invalidateQueries({ queryKey: ['editor-photos', session?.id] });
    setBulkDeleting(false);
    exitSelectMode();
  }, [session?.id, queryClient, exitSelectMode]);

  // ── « Supprimer toutes les photos » (owner, 2026-10-26) ───────────────────
  // Every photo of THIS album, placed or not: the same per-photo server delete
  // as the bulk delete (rows + files), pages kept with their slots emptied,
  // framings dropped. The list query is refetched, so the counter reads 0 and
  // a refresh shows nothing (the rows are gone, the emptied pages autosave).
  const handleDeleteAllPhotos = useCallback(async () => {
    const ids = allPhotos.map((p) => p.id);
    if (ids.length === 0) return;
    setSlotPositions({});
    setSelectedSlotKey(null);
    setPanModeKey(null);
    await handleBulkDeletePhotos(ids);
  }, [allPhotos, handleBulkDeletePhotos]);

  // ── Back-cover photo handlers ──────────────────────────────────────────────
  // Routes through the Worker (`POST /photos/back-cover-upload-url`) which
  // signs a one-shot PUT URL with the service role. This is what makes
  // back-cover uploads work for unauthenticated customers (magic-link flow)
  // — `supabase.storage.from(...).upload(...)` from the browser would silently
  // fail with an RLS denial, which is exactly the "I added photos, they
  // disappeared" bug we previously hit. Only the storage_path is persisted in
  // session JSON; signed read URLs are regenerated on every load.
  async function uploadOneBackCoverFile(file: File): Promise<BackCoverImage | null> {
    if (!session?.id) return null;
    try {
      // iPhone photos are HEIC — convert to JPEG so the back-cover <img>
      // preview renders everywhere (Chrome/Android can't decode HEIC) and the
      // PDF can embed it. Without this, picking an iPhone photo for the back
      // cover appeared to do nothing.
      let up = file;
      if (isHeicFile(file)) {
        try {
          up = await normalizeCoverImageFile(file);
        } catch {
          console.warn('[Editor] back-cover HEIC convert failed:', file.name);
          return null;
        }
      }
      const ct = up.type || 'image/jpeg';
      const { upload_url, storage_path, content_type } = await photosApi.getBackCoverUploadUrl(
        session.id,
        up.name,
        ct,
      );
      await uploadToSignedUrl(upload_url, up, content_type);
      // Fresh signed URL for immediate preview. Signed through the worker
      // (service role) so the bucket's RLS on back-cover/ doesn't 400 us.
      let signedUrl: string | null = null;
      try {
        const { urls } = await photosApi.signBackCoverUrls([storage_path]);
        signedUrl = urls?.[0]?.signed_url ?? null;
      } catch (e) {
        console.warn('[Editor] back-cover sign-after-upload failed:', (e as Error).message);
      }
      return { storage_path, signed_url: signedUrl };
    } catch (e) {
      console.error('[Editor] back-cover upload failed:', (e as Error).message);
      return null;
    }
  }

  const handleBackCoverFilesAdd = useCallback(async (files: File[]) => {
    if (files.length === 0 || !session?.id) return;
    const uploaded: BackCoverImage[] = [];
    for (const f of files) {
      const img = await uploadOneBackCoverFile(f);
      if (img) uploaded.push(img);
    }
    if (uploaded.length === 0) return;
    const isBlank = selectedCover?.id === '__blank__'
      || (selectedCover as any)?.back_cover_style === 'blank';
    setBackCoverImages((prev) =>
      isBlank
        ? uploaded.slice(0, 1)
        : [...prev, ...uploaded].slice(0, 4),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, selectedCover?.id]);

  // FULL-PAGE back photo (2026-07-15): mark ONE image as the full-bleed back
  // cover (exclusive — setting it clears the flag on the others). The other
  // images keep rendering in the centred grid on top of it.
  const handleBackCoverImageFullPage = useCallback((idx: number, on: boolean) => {
    setBackCoverImages((prev) => prev.map((img, i) => ({ ...img, full_page: on && i === idx ? true : undefined })));
  }, []);

  const handleBackCoverImageDelete = useCallback((idx: number) => {
    setBackCoverImages((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleBackCoverImagesClear = useCallback(() => {
    setBackCoverImages([]);
  }, []);

  // ── Admin custom cover-icon (front design) override ──────────────────────────
  const coverIconFileRef = useRef<HTMLInputElement>(null);

  // Admin replaces the catalogue front-cover illustration ("door") for THIS
  // order only. Converts any picked image to PNG (pdf-lib can't embed WebP;
  // PNG also preserves transparency), uploads via the back-cover signed-URL
  // path, and records the storage_path as a per-order override.
  const handleCoverIconUpload = useCallback(async (file: File) => {
    if (!session?.id) return;
    if (file.size > MAX_COVER_UPLOAD_BYTES) {
      alert('Image trop lourde (max 10 Mo).');
      return;
    }
    // HEIC (iPhone) → JPEG first, so the canvas decode below can read it and
    // iOS users aren't blocked by the "Format non supporté" guard.
    let src = file;
    if (isHeicFile(file)) {
      try {
        src = await normalizeCoverImageFile(file);
      } catch {
        alert('Impossible de lire cette photo iPhone (HEIC). Réessayez ou choisissez une autre photo.');
        return;
      }
    }
    if (!isConvertibleImageType(src.type)) {
      alert('Format non supporté. Utilisez PNG, JPG, WebP ou une photo iPhone.');
      return;
    }
    try {
      const pngBlob = await convertImageFileToPng(src);
      const pngFile = new File([pngBlob], `cover-icon-${Date.now()}.png`, { type: 'image/png' });
      const { upload_url, storage_path, content_type } = await photosApi.getBackCoverUploadUrl(
        session.id,
        pngFile.name,
        'image/png',
      );
      await uploadToSignedUrl(upload_url, pngFile, content_type);
      setFrontCoverImageOverride({ storage_path });
      // A freshly imported front image must NOT inherit the previous design's
      // transform — a saved off-screen value (e.g. x≈-86, y≈-99) would push the
      // new image fully off the panel and make it invisible/un-grabbable (the
      // #1081 dead-end). Reset to centered + 1× so it always appears visible.
      setCoverDesignTransform({ x: 0, y: 0, scale: 1 });
      // Uploading implies "show it" — clear any prior hide so the new icon is visible.
      setHideFrontCoverImage(false);
      try {
        const { urls } = await photosApi.signBackCoverUrls([storage_path]);
        setFrontCoverImageOverrideUrl(urls?.[0]?.signed_url ?? null);
      } catch {
        setFrontCoverImageOverrideUrl(null);
      }
    } catch (e) {
      console.error('[Editor] cover-icon upload failed:', (e as Error).message);
      alert('Échec du chargement de l’image. Réessayez.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Restore the catalogue door (drop the per-order override).
  const handleRestoreCoverIcon = useCallback(() => {
    setFrontCoverImageOverride(null);
    setFrontCoverImageOverrideUrl(null);
    // Re-center the transform too — otherwise the restored catalogue design
    // inherits whatever (possibly off-screen) transform the dropped override
    // had, leaving the original design stuck out of view.
    setCoverDesignTransform({ x: 0, y: 0, scale: 1 });
  }, []);

  // ── Client blank-cover front image ───────────────────────────────────────────
  const blankCoverFileRef = useRef<HTMLInputElement>(null);

  // Customer uploads their OWN front-cover image on a blank ("vierge") cover.
  // Uploads via the worker-signed blank-cover/ path (RLS-safe for the magic
  // link flow, like back-cover). No client-side format conversion — the PDF's
  // normaliseImageBlob re-encodes WebP/HEIC/etc. on render. Persisted as
  // cover_customizations.blankCoverImage = { storage_path }; signed URL is
  // regenerated on load. Rendered cover-fit behind the (Londrina) text zones.
  const handleBlankCoverUpload = useCallback(async (file: File) => {
    if (!session?.id) return;
    try {
      // iPhone photos are HEIC — convert to JPEG BEFORE the format guard so
      // iOS users can place their own photo on the cover (was rejected
      // outright with "Format non supporté", which is why "Ma photo (plein
      // cadre)" appeared to do nothing on iPhone). Size cap is checked on the
      // ORIGINAL (HEIC files are smaller than the JPEG they decode to).
      if (file.size > MAX_COVER_UPLOAD_BYTES) {
        alert('Image trop lourde (max 10 Mo).');
        return;
      }
      let up = file;
      if (isHeicFile(file)) {
        try {
          up = await normalizeCoverImageFile(file);
        } catch {
          alert('Impossible de lire cette photo iPhone (HEIC). Réessayez ou choisissez une autre photo.');
          return;
        }
      }
      if (!isConvertibleImageType(up.type)) {
        alert('Format non supporté. Utilisez PNG, JPG, WebP ou une photo iPhone.');
        return;
      }
      const { upload_url, storage_path, content_type } = await photosApi.getBlankCoverUploadUrl(
        session.id,
        up.name,
        up.type || 'image/jpeg',
      );
      await uploadToSignedUrl(upload_url, up, content_type);
      setBlankCoverImage({ storage_path });
      setBlankCoverTransform({ x: 0, y: 0, scale: 1 });
      // Sign the preview URL. If signing fails the upload still succeeded, but
      // the src would be null and the cover would show NO visual change — the
      // exact "nothing happened" symptom. Retry once, then surface an error so
      // the user isn't left staring at the old image with no feedback.
      let signed: string | null = null;
      try {
        const { urls } = await photosApi.signBackCoverUrls([storage_path]);
        signed = urls?.[0]?.signed_url ?? null;
      } catch {
        try {
          const { urls } = await photosApi.signBackCoverUrls([storage_path]);
          signed = urls?.[0]?.signed_url ?? null;
        } catch { signed = null; }
      }
      setBlankCoverImageUrl(signed);
      if (!signed) {
        alert('Photo enregistrée mais l’aperçu n’a pas pu se charger. Rechargez la page pour la voir.');
      }
    } catch (e) {
      console.error('[Editor] blank-cover upload failed:', (e as Error).message);
      alert('Échec du chargement de l’image. Réessayez.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const handleRemoveBlankCoverImage = useCallback(() => {
    setBlankCoverImage(null);
    setBlankCoverImageUrl(null);
    setBlankCoverTransform({ x: 0, y: 0, scale: 1 });
  }, []);

  // ── Cover theme selection ──────────────────────────────────────────────────
  const handleSelectCover = useCallback((cover: Cover) => {
    // Carry the customer's text-style overrides across the theme switch.
    // Zones are matched by `type` (destination / year) because the new
    // cover's text_zones order is independent of the old cover's; matching
    // by array index would copy the year override onto the destination
    // zone when zone orders differ. Custom-type zones are skipped — they
    // are theme-specific text slots and don't map cleanly.
    const oldZones = selectedCover?.text_zones ?? [];
    const newZones = cover.text_zones ?? [];
    const preservedOverrides: Record<number, TextZoneOverride> = {};
    for (let oldIdx = 0; oldIdx < oldZones.length; oldIdx++) {
      const ovr = coverZoneOverrides[oldIdx];
      if (!ovr) continue;
      const oldType = oldZones[oldIdx]?.type;
      if (oldType !== 'destination' && oldType !== 'year') continue;
      const newIdx = newZones.findIndex((z) => z.type === oldType);
      if (newIdx < 0) continue;
      // Carry ONLY the customer's style picks (font + colour). Position and
      // size are the NEW cover's designed layout — carrying x/y/scale across
      // covers broke titles badly: a scale override (≠1) disables autoScale,
      // so a typographic cover (e.g. Agadir, zone font_size 410 auto-shrunk
      // to its box) rendered the title GIANT, and old x/y put it mid-cover
      // instead of where the new design places it.
      preservedOverrides[newIdx] = {
        fontFamily: ovr.fontFamily,
        color: ovr.color,
      };
    }

    setSelectedCover(cover);
    setSelectedCoverZone(null);
    setCoverZoneOverrides(preservedOverrides);
    setCoverExtraZones([]);
    setCoverCustomTexts({});
    // Drop the per-face front-cover bg override so the new theme's dominant
    // colour shows on the front. Without this, picking a green theme over
    // a previously pink-overridden cover left the front pink while the
    // back + spine flipped to green. The spine sync effect anchors to the
    // effective front bg, so nulling this also fixes the spine.
    setFrontCoverBgColor(null);
    setHideFrontCoverImage(false);
    // Switching covers drops any admin custom-icon override — it belonged to
    // the previous cover's door. The new cover starts from its own design_url.
    setFrontCoverImageOverride(null);
    setFrontCoverImageOverrideUrl(null);
    // Switching covers also drops the client's blank-cover front image — it
    // only applies to the "vierge" cover the customer is leaving.
    setBlankCoverImage(null);
    setBlankCoverImageUrl(null);
    setBlankCoverTransform({ x: 0, y: 0, scale: 1 });
    setBackCoverBgColor(cover.dominant_color ?? null);
    // Spine text follows the NEW cover's title colour. This was the actual
    // source of the always-blue spine: the switch reset the spine BACKGROUND
    // but left textColor on the editor's hardcoded '#5B8CDB' default and then
    // persisted it — while the comment on the initial-load effect claimed this
    // handler "already resets both colors". force:true because the previous
    // colour belonged to the cover the customer just left, exactly like the bg
    // override dropped above.
    const nextSpineTextColor = resolveSpineTextColor({
      zones: newZones,
      overrides: preservedOverrides,
      current: spineConfig.textColor,
      // The spine plate the new cover is about to paint — judge legibility
      // against THAT, not the colour we are replacing.
      bgColor: cover.dominant_color ?? spineConfig.bgColor,
      force: true,
    }) ?? spineConfig.textColor;
    setSpineConfig((prev) => ({
      ...prev,
      bgColor: cover.dominant_color ?? prev.bgColor,
      textColor: nextSpineTextColor,
    }));
    // Save immediately
    const updatedSpine = {
      ...spineConfig,
      bgColor: cover.dominant_color || spineConfig.bgColor,
      textColor: nextSpineTextColor,
    };
    sessionsApi.upsert(shopifyOrderId!, albumIndex, {
      cover_id: persistedCoverId(cover),
      cover_customizations: {
        spineConfig: updatedSpine,
        zoneOverrides: preservedOverrides,
        extraZones: [],
        customTexts: {},
        frontCoverBgColor: null,
        hideFrontCoverImage: false,
        frontCoverImageOverride: null,
        blankCoverTransform: { x: 0, y: 0, scale: 1 },
        blankCoverImage: null,
        backCoverBgColor: cover.dominant_color ?? null,
        backCoverImages: [],
        // The cover is sacred: stickers / photos the team placed stay on the
        // album through a cover switch (they belong to the book, not the design).
        coverElements: coverElementsForSave(),
      },
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyOrderId, albumIndex, spineConfig, selectedCover, coverZoneOverrides, coverElements]);

  // ─── Photo URL map ──────────────────────────────────────────────────────

  const photoUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPhotos) {
      if (p.signed_url) map.set(p.id, p.signed_url);
    }
    return map;
  }, [allPhotos]);

  // Batch 1 perf: small (~320px) tier for the bottom SPREAD STRIP thumbnails —
  // they're tiny page minis, so the 1400px canvas tier was ~19x oversized RAM.
  // Falls back to signed_url when no tray tier exists. The canvas (PageView) +
  // preview keep photoUrlMap (1400px) for sharpness.
  const photoThumbUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPhotos) {
      const u = p.tray_signed_url ?? p.signed_url;
      if (u) map.set(p.id, u);
    }
    return map;
  }, [allPhotos]);

  // Photo metadata map — feeds the smart fit logic (Phase 2).
  const photoMetaMap = useMemo(() => {
    const map = new Map<string, {
      width: number | null;
      height: number | null;
      face_count: number | null;
      face_spread: number | null;
      aspect_ratio: 'landscape' | 'portrait' | 'square' | null;
    }>();
    for (const p of allPhotos) {
      map.set(p.id, {
        width: p.width,
        height: p.height,
        face_count: (p as any).face_count ?? null,
        face_spread: (p as any).face_spread ?? null,
        aspect_ratio: (p as any).aspect_ratio ?? null,
      });
    }
    return map;
  }, [allPhotos]);

  // Photo filename map (kept for display / tooltips)
  const photoFilenameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPhotos) {
      map.set(p.id, p.original_filename ?? '');
    }
    return map;
  }, [allPhotos]);

  // Photos sorted by UPLOAD ORDER (earliest upload first) for the sidebar.
  // Customers report they look for "the first picture I uploaded" — EXIF
  // date (when the photo was *taken*) shuffled that order whenever the
  // selection came from a mixed camera roll. `created_at` is the row's
  // server-stamped insertion timestamp, which IS upload order, even when
  // the customer picked photos out of chronological camera order.
  const photosSortedByDate = useMemo(() => {
    // Compare the created_at STRINGS, not Date.getTime(): JS Date is only
    // millisecond-precise, but parallel uploads land in the same millisecond and
    // differ only at the microsecond level. Lexical compare keeps that order, so
    // the grid matches the album sequence 1-for-1 when "Ordre d'upload" is on.
    return [...allPhotos].sort((a, b) => {
      const ca = a.created_at ?? '';
      const cb = b.created_at ?? '';
      return ca < cb ? -1 : ca > cb ? 1 : 0;
    });
  }, [allPhotos]);

  // Small (~320px) grid thumbnails for the mobile "Mes photos" sheet — only
  // generated while the sheet is open. Renders the full-size signed_url at 1600px
  // (~7.7MB decoded each) blew the iOS memory ceiling and reloaded the page when
  // the grid opened; these are ~25x lighter. See lib/gridThumb.
  const mobileGridThumbs = useGridThumbs(mobileSidebarOpen === 'images' ? photosSortedByDate : []);

  const placedPhotoIds = useMemo(() => {
    const ids = new Set<string>();
    for (const page of localPages.length > 0 ? localPages : (layout?.pages ?? [])) {
      for (const id of page.photos) {
        if (id) ids.add(id);
      }
    }
    return ids;
  }, [localPages, layout?.pages]);

  const unplacedPhotos = useMemo(
    () => allPhotos.filter((p) => !placedPhotoIds.has(p.id)),
    [allPhotos, placedPhotoIds],
  );

  // ─── Spread navigation ─────────────────────────────────────────────────

  const pages = localPages.length > 0 ? localPages : (layout?.pages ?? []);
  const totalPages = pages.length;
  // ─── THE SPREADS ARE THE PRINTED BOOK'S (design system v2 §7 S1) ────────
  // plain (1, 2), (3, 4)… behind the intro; themed (—, 1), (2, 3), (4, 5)… —
  // the S1 opening alone on the right, exactly as it prints. Every spread
  // number, label, insert point and drag below goes through editorSpreads.ts
  // (bookStructure.ts underneath); none of it multiplies a spread by 2 here.
  const totalSpreads = spreadCount(totalPages, themedBook);
  // The numbers the PRINTED book carries — intro, outro and the ÷4 padding the
  // assembler appends. Derived from printPageNumbers.ts, the one place that
  // knows the rule; this used to be `totalPages + 2` written by hand here and
  // it disagreed with the 3D preview (42 vs 44) on #1198.
  // ⚠️ "Is the intro printed?" is a fact about THIS book: a themed book has
  // none, whatever the brand setting holds (bookStructure.ts).
  const bookHasIntro = printsIntro(themedBook, Boolean(introOutroUrls.intro));
  // No intro page in a themed book → no intro view either (a restored
  // position or a stale click may still ask for it).
  useEffect(() => {
    if (themedBook && editorView === 'intro') setEditorView('pages');
  }, [themedBook, editorView]);
  const printedTotal = printedInteriorTotal(
    totalPages, bookHasIntro, Boolean(introOutroUrls.outro),
  );
  // « 3–4/40 · impr. 4–5/44 » — the customer's pages and the printed ones.
  const spreadPagesText = spreadPagesLabel(currentSpread, themedBook, totalPages);
  const spreadPrintText = spreadPrintLabel(
    currentSpread, themedBook, totalPages,
    bookHasIntro, Boolean(introOutroUrls.outro),
  );
  // Fix #6 (2026-05-28): mirror into ref so the touch-swipe handler (which
  // is declared before totalSpreads in this function body) can read it.
  totalSpreadsRef.current = totalSpreads;

  const [leftIdx, rightIdx] = spreadPages(currentSpread, themedBook, totalPages);
  const leftPage = leftIdx != null ? pages[leftIdx] ?? null : null;
  const rightPage = rightIdx != null ? pages[rightIdx] ?? null : null;
  // The open spread's album pages (1-based positions), for « is a problem on screen? ».
  const openAlbumPages = spreadAlbumPages(currentSpread, themedBook, totalPages);

  // A themed book's opening spread has NO left page (the inside of the
  // cover faces S1): whatever is added or applied goes to the right page.
  useEffect(() => {
    if (!leftPage && rightPage && selectedPageSide === 'left') setSelectedPageSide('right');
  }, [leftPage, rightPage, selectedPageSide]);

  // Keep `selectedPageSide` in sync with whatever the customer is actually
  // editing. Clicking a slot or an element previously stopped propagation
  // before the page's onClick fired, so the page outline (and the
  // "add element targets this page" logic) lagged. We derive the side
  // from the active selection so adding a new text/photo/shape always
  // lands on the page the user is looking at.
  useEffect(() => {
    if (selectedSlotKey) {
      // slotKey is `p{page}-s{slot}`
      const m = selectedSlotKey.match(/^p(\d+)-/);
      const pageNum = m ? Number(m[1]) : null;
      if (pageNum !== null) {
        if (leftPage?.page === pageNum) setSelectedPageSide('left');
        else if (rightPage?.page === pageNum) setSelectedPageSide('right');
      }
      return;
    }
    if (selectedElementId) {
      const found = findElementById(selectedElementId);
      if (found) {
        if (leftPage?.page === found.pageNumber) setSelectedPageSide('left');
        else if (rightPage?.page === found.pageNumber) setSelectedPageSide('right');
      }
    }
  }, [selectedSlotKey, selectedElementId, leftPage?.page, rightPage?.page, findElementById]);

  const goToSpread = useCallback(
    (idx: number) => {
      setCurrentSpread(Math.max(0, Math.min(idx, totalSpreads - 1)));
      setEditorView('pages');
    },
    [totalSpreads],
  );
  /** The spread on which album page `pageNumber` (1-based position) lies open. */
  const spreadOfAlbumPage = useCallback(
    (pageNumber: number) => spreadOf(pageNumber - 1, themedBook),
    [themedBook],
  );

  // ── "Corriger ces problèmes" — guided fix flow ─────────────────────────────
  // Live-derived list of pages that still have a problem (same checks the
  // verification modal uses, just keyed by page number so we can navigate +
  // indicate). Shrinks automatically as the customer fills pages.
  const problemPages = useMemo(() => {
    const out: Array<{ page: number; type: 'blank' | 'empty-slots' }> = [];
    for (const p of localPages) {
      // A page is only a PROBLEM when it's genuinely empty (2026-07-06, Yahya):
      // a deliberate blank page, or a page carrying ANY element (a free/detached
      // photo, text, icon, shape) is a valid client choice — never flag it.
      // Only a page with no filled slot AND no elements (or a partial grid the
      // client never customised) is a real gap to fix.
      if (p.blank) continue;
      if ((p.elements?.length ?? 0) > 0) continue;
      const maxSlots = ({ '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4, 'wide-top': 2 } as Record<string, number>)[p.layout] ?? 1;
      const filled = p.photos.filter(Boolean).length;
      if (filled === 0) out.push({ page: p.page, type: 'blank' });
      else if (filled < maxSlots) out.push({ page: p.page, type: 'empty-slots' });
    }
    return out;
  }, [localPages]);
  const problemPageSet = useMemo(() => new Set(problemPages.map((p) => p.page)), [problemPages]);
  // True after the customer taps "Corriger ces problèmes" — turns on the
  // (non-blocking) indicators + auto-advance until every problem is fixed.
  const [fixMode, setFixMode] = useState(false);

  // Auto-advance: once the page the customer is on is fixed (no longer in the
  // problem set), jump to the next remaining problem. Exit fix mode when all
  // are fixed. Only `problemPages` is a dep so manual navigation isn't yanked.
  useEffect(() => {
    if (!fixMode) return;
    if (problemPages.length === 0) { setFixMode(false); return; }
    const currentIsProblem = problemPages.some((pp) => openAlbumPages.includes(pp.page));
    if (!currentIsProblem) goToSpread(spreadOfAlbumPage(problemPages[0].page));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixMode, problemPages]);

  // Start the guided fix flow from the verification modal.
  const startFixFlow = useCallback(() => {
    setShowValidation(false);
    if (problemPages.length > 0) {
      setFixMode(true);
      goToSpread(spreadOfAlbumPage(problemPages[0].page));
    }
  }, [problemPages, goToSpread, spreadOfAlbumPage]);

  const prevSpread = useCallback(
    () => goToSpread(currentSpread - 1),
    [currentSpread, goToSpread],
  );
  const nextSpread = useCallback(
    () => goToSpread(currentSpread + 1),
    [currentSpread, goToSpread],
  );

  // ISSUE 1 (2026-07-05) — pre-warm the canvas images of the spreads around
  // the current one. The editor mounts ONE spread; every arrow-tap used to
  // fetch+decode that spread's 1400px images from zero → white page while
  // 5G caught up. Warming ±1/+2 spreads in the background means the bytes
  // are already in the HTTP/decode cache when the customer arrives.
  const warmedUrlsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const t = setTimeout(() => {
      const urls: string[] = [];
      const themed = isThemedBook(localPages);
      for (let sp = currentSpread - 1; sp <= currentSpread + 2; sp++) {
        if (sp < 0) continue;
        for (const pg of spreadPages(sp, themed, localPages.length).map((i) => (i != null ? localPages[i] : null))) {
          for (const pid of pg?.photos ?? []) {
            const u = photoUrlMap.get(pid);
            if (u && !warmedUrlsRef.current.has(u)) { warmedUrlsRef.current.add(u); urls.push(u); }
          }
        }
      }
      // Cap per hop so a fast arrow-mash never floods the network.
      for (const u of urls.slice(0, 24)) {
        const img = new Image();
        img.decoding = 'async';
        img.src = u;
      }
    }, 250); // let the CURRENT spread's own images start first
    return () => clearTimeout(t);
  }, [currentSpread, localPages, photoUrlMap]);

  // NAV visibility (2026-07-04): keep the ACTIVE spread thumbnail in view in
  // the bottom strip. Navigating via the canvas arrows used to leave the strip
  // parked wherever it was — the gold outline scrolled out of sight and the
  // customer lost their place. block:'nearest' keeps this from scrolling the
  // page vertically; inline:'center' centres the active thumb in the strip.
  useEffect(() => {
    const t = setTimeout(() => {
      document
        .querySelector(`[data-spread-idx="${currentSpread}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 80); // after the outline/label re-render settles
    return () => clearTimeout(t);
  }, [currentSpread]);

  // ─── Destination info ───────────────────────────────────────────────────

  const destination = session?.destination ?? order?.destination_1 ?? '';
  const year = session?.year ?? order?.year_1 ?? '';

  // ─── Éléments panel: stickers + the « Modèles » door (2026-10-14) ────────
  // The album's theme (the focused page's model, any themed page, else the
  // cover's categories) opens its stickers first.
  const focusedPage = selectedPageSide === 'right' ? rightPage : leftPage;
  const albumTheme = albumThemeFrom(
    focusedPage?.theme ?? localPages.find((p) => p.theme)?.theme,
    selectedCover?.categories,
  );
  /** A sticker onto the focused page — centred, a quarter of the page wide, at the
   *  original's aspect (lib/stickers/catalog.ts stickerElement). The canvas draws its
   *  900 px copy (previewUrl); the PDF prints the original (imageUrl). */
  const addStickerToFocusedPage = (s: Sticker): boolean => {
    const targetPage = selectedPageSide === 'right' ? rightPage : leftPage;
    if (!targetPage) return false;
    const element: PageElement = stickerElement(s, targetPage.elements ?? []);
    setLocalPages((pages) => pages.map((p) =>
      p.page === targetPage.page
        ? { ...p, elements: [...(p.elements ?? []), element] }
        : p
    ));
    setSelectedElementId(element.id);
    setSelectedElementIds(new Set([element.id]));
    return true;
  };
  // ─── COVER ELEMENTS + DRAG AND DROP (owner 2026-10-20) ──────────────────
  // « add a sticker or icon over the cover however I want », « add images from
  // the already uploaded images to the back cover », « I need to be able to
  // drag and drop ». The team places them (isAdmin); a client sees them
  // read-only. lib/coverElements.ts holds the model + the print maths.
  const canEditCoverElements = isAdmin;
  const [coverPhotoPickerOpen, setCoverPhotoPickerOpen] = useState(false);
  const addCoverElement = (el: CoverElement) => {
    setCoverElements((prev) => [...prev, el]);
    setSelectedCoverElId(el.id);
    setSelectedCoverZone(null);
    setEditingCoverZone(null);
  };
  const updateCoverElement = (id: string, updates: Partial<CoverElement>) => {
    setCoverElements((prev) => prev.map((e) => (e.id === id ? clampToFace({ ...e, ...updates } as CoverElement, coverSpineSettings) : e)));
  };
  const deleteCoverElement = (id: string) => {
    setCoverElements((prev) => prev.filter((e) => e.id !== id));
    setSelectedCoverElId((cur) => (cur === id ? null : cur));
  };
  const duplicateCoverElement = (id: string) => {
    const src = coverElements.find((e) => e.id === id);
    if (!src) return;
    const copy = clampToFace({ ...src, id: newCoverElementId(), x: src.x + 3, y: src.y + 3 }, coverSpineSettings);
    addCoverElement(copy);
  };
  const layerCoverElement = (id: string, move: LayerMove) => setCoverElements((prev) => moveLayer(prev, id, move));
  const moveCoverElementToFace = (id: string, face: CoverFace) =>
    setCoverElements((prev) => prev.map((e) => (e.id === id ? clampToFace(moveToFace(e, face, coverSpineSettings), coverSpineSettings) : e)));
  /** A tile (sticker / catalogue icon / admin SVG) as a cover element on `face`. */
  const coverElementFromPayload = (p: TileDragPayload, face: CoverFace, at: { fx: number; fy: number } | null): CoverElement | null => {
    if (p.kind === 'sticker') {
      return coverImageElement({
        imageUrl: stickerOriginalUrl(p.sticker), previewUrl: stickerMedUrl(p.sticker),
        aspect: p.sticker.w / p.sticker.h, shadow: p.sticker.shadow,
      }, face, at, coverSpineSettings);
    }
    if (p.kind === 'coverIcon') {
      const url = p.cover.icon_url ?? p.cover.design_url;
      return url ? coverImageElement({ imageUrl: url, aspect: 1 }, face, at, coverSpineSettings) : null;
    }
    return { ...coverImageElement({ svg: p.icon.svg, aspect: 1 }, face, at, coverSpineSettings), iconColor: '#1F2937' };
  };
  /** COVER TEXT (owner 2026-10-30) — a new text on `face`, centred (each new
   *  one a little lower so two never stack), in a colour that reads on the
   *  cover's background. Returns its id: the cover view starts typing in it. */
  const addTextToCover = (face: CoverFace): string | null => {
    if (!canEditCoverElements) return null;
    const bg = (face === 'back' ? backCoverBgColor : frontCoverBgColor) ?? selectedCover?.dominant_color ?? '#F5F0E8';
    const m = /^#?([0-9a-f]{6})$/i.exec(bg.trim());
    const lum = m ? (() => { const n = parseInt(m[1], 16); return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; })() : 1;
    const k = coverElements.filter((e) => e.face === face && e.type === 'text').length % 5;
    const at = face === 'spine' ? { fx: 0.5, fy: 0.5 } : { fx: 0.5, fy: 0.5 + 0.08 * k };
    const el = coverTextElement(face, at, coverSpineSettings, { fontColor: lum < 0.5 ? '#FFFFFF' : '#1F2937' });
    addCoverElement(el);
    return el.id;
  };
  /** A tap on a tile while the COVER is on screen (team) → the front, centred
   *  (each new one 4 % further down-right so two taps never stack exactly). */
  const addTileToCoverOnTap = (p: TileDragPayload): boolean => {
    if (editorView !== 'cover' || !canEditCoverElements) return false;
    const onFront = coverElements.filter((e) => e.face === 'front').length;
    const k = onFront % 6;
    const el = coverElementFromPayload(p, 'front', { fx: 0.5 + 0.04 * k, fy: 0.5 + 0.04 * k });
    if (el) addCoverElement(el);
    return true;
  };
  /** One of the album's photos onto a face (the back by default), at its shape. */
  const addPhotoToCover = (photoId: string, face: CoverFace = 'back', at?: { fx: number; fy: number } | null) => {
    if (!canEditCoverElements) return;
    const ph = allPhotos.find((p) => p.id === photoId);
    if (!ph) return;
    const aspect = ph.width && ph.height ? ph.width / ph.height : 1.5;
    let point = at ?? null;
    if (!point) {
      const k = coverElements.filter((e) => e.face === face && e.type === 'photo').length % 6;
      point = { fx: 0.35 + 0.06 * k, fy: 0.35 + 0.06 * k };
    }
    addCoverElement(coverPhotoElement(photoId, aspect, face, point, coverSpineSettings));
  };
  // Drops from the Icônes / Stickers tiles (lib/tileDrag.ts) — a cover face
  // (team) or any content page (everyone, like a tap), right under the pointer.
  const tileDropRef = useRef<(d: TileDropDetail) => void>(() => {});
  tileDropRef.current = (d: TileDropDetail) => {
    const at = { fx: d.fx, fy: d.fy };
    if (d.target.startsWith('cover:')) {
      const face = d.target.slice(6);
      if (!canEditCoverElements || (face !== 'front' && face !== 'back' && face !== 'spine')) return;
      const el = coverElementFromPayload(d.payload, face, at);
      if (el) addCoverElement(el);
      return;
    }
    const m = /^page:(\d+)$/.exec(d.target);
    if (!m) return;
    const n = Number(m[1]);
    const page = localPages.find((pg) => pg.page === n);
    if (!page) return;
    const p = d.payload;
    const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    let el: PageElement;
    if (p.kind === 'sticker') el = stickerElement(p.sticker, [], newId);
    else if (p.kind === 'coverIcon') {
      const url = p.cover.icon_url ?? p.cover.design_url;
      if (!url) return;
      el = { id: newId, type: 'icon', x: 0, y: 0, width: 30, height: 22, imageUrl: url };
    } else el = { id: newId, type: 'icon', x: 0, y: 0, width: 25, height: 25, svg: p.icon.svg, iconColor: '#1F2937' };
    el = { ...el, ...boxAtPoint(el.width, el.height, at) };
    setLocalPages((pages) => pages.map((pg) => (pg.page === n ? { ...pg, elements: [...(pg.elements ?? []), el] } : pg)));
    setSelectedElementId(el.id);
    setSelectedElementIds(new Set([el.id]));
    setMobileSidebarOpen(null);
  };
  useEffect(() => onTileDrop((d) => tileDropRef.current(d)), []);
  // Delete / Backspace removes the selected cover element (as on the pages).
  useEffect(() => {
    if (editorView !== 'cover' || !selectedCoverElId || !canEditCoverElements) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      deleteCoverElement(selectedCoverElId);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorView, selectedCoverElId, canEditCoverElements]);

  /** « Modèles de page » in the Éléments panel → the v2 picker on the focused page. */
  const openModelsForFocusedPage = session?.id ? (): boolean => {
    const focused = selectedPageSide === 'right' ? rightPage : leftPage;
    if (!focused) return false;
    const idx = localPages.findIndex((p) => p.page === focused.page);
    if (idx === -1) return false;
    setTemplatePickerForPage(idx);
    return true;
  } : undefined;
  /** « Retirer » the focused page's model (a v2 model, or a legacy template). */
  const removeFocusedPageModel = () => {
    const focused = selectedPageSide === 'right' ? rightPage : leftPage;
    if (!focused) return;
    if (focused.theme) { handleRemoveThemeModel(focused.page); return; }
    const idx = localPages.findIndex((p) => p.page === focused.page);
    if (idx !== -1) removeTemplate(idx);
  };

  // Permanent Fix 2 — the destination/year title is mandatory on the
  // customer-facing cover. If a customer clears it (sidebar input or inline
  // canvas edit), restore the last non-empty value, then the order's
  // destination/year. Admins may intentionally leave it blank — and so may
  // clients on a BLANK ("vierge") cover, where they own the whole design.
  const restoreEmptyCoverTitle = useCallback((type: 'destination' | 'year') => {
    if (isAdmin) return;
    const isBlank = selectedCover?.id === '__blank__'
      || (selectedCover as { back_cover_style?: string } | null)?.back_cover_style === 'blank';
    if (isBlank) return;
    if (type === 'destination' && !coverDestText.trim()) {
      // « No title » is his decision — a blur does not undo it.
      if (albumTitle(session).kind === 'none') return;
      editCoverTitle(lastCoverDestRef.current || destination || '');
    } else if (type === 'year' && !coverYearText.trim()) {
      setCoverYearText(lastCoverYearRef.current || year || '');
    }
  }, [isAdmin, selectedCover, coverDestText, coverYearText, destination, year, session, editCoverTitle]);

  // Admin shares an editor link that opens on the EXACT page they're viewing
  // (not page 1) by embedding ?page=<1-indexed>. The customer-side deep-link
  // effect reads it and jumps to that spread. Admin-only control in the header.
  const handleCopyClientLink = useCallback(() => {
    // The first page open on this spread (a themed opening: page 1, on the right).
    const [li, ri] = spreadPages(currentSpread, isThemedBook(localPages), localPages.length);
    const first = li ?? ri ?? 0;
    const pageNum = localPages[first]?.page ?? (first + 1);
    const url = `${window.location.origin}/editor/${shopifyOrderId}/${albumIndex}/edit?page=${pageNum}`;
    const flash = () => { setClientLinkCopied(true); setTimeout(() => setClientLinkCopied(false), 1600); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(flash, () => window.prompt('Copier le lien client :', url));
    } else {
      window.prompt('Copier le lien client :', url);
    }
  }, [localPages, currentSpread, shopifyOrderId, albumIndex]);

  // ─── Derived state (must be before any conditional return) ──────────────

  const hasNoLayout = !layout || !layout.pages || layout.pages.length === 0;
  const hasNoPhotos = allPhotos.length === 0;
  const needsSetup = hasNoLayout && hasNoPhotos && !sessionLoading && !photosLoading;
  // "Mode chooser" — entering the editor with photos uploaded but no pages
  // yet means we need to know how the user wants to fill the album: AI
  // auto-layout vs blank pages they fill themselves manually.
  const needsModeChoice =
    hasNoLayout && !hasNoPhotos && localPages.length === 0 && !sessionLoading && !photosLoading;

  // Auto-show upload modal when no photos or no layout
  useEffect(() => {
    if (needsSetup) setShowUploadModal(true);
  }, [needsSetup]);

  // Auto-show layout chooser when photos exist but no pages yet
  const [showLayoutChooser, setShowLayoutChooser] = useState(false);
  useEffect(() => {
    if (needsModeChoice) setShowLayoutChooser(true);
  }, [needsModeChoice]);

  // Manual mode: create N blank 1-up pages so the user can drag/tap photos
  // into them. N = order.page_count, default 30. Saves immediately so it
  // survives reload.
  const handleCreateBlankPages = useCallback(async () => {
    if (!session?.id) return;
    const totalPages = order?.page_count ?? 30;
    const blank: LayoutPage[] = Array.from({ length: totalPages }, (_, i) => ({
      page: i + 1,
      layout: '1-up',
      photos: [],
    }));
    setLocalPages(blank);
    setShowLayoutChooser(false);
    try {
      await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
        pages: { pages: blank, unassigned_photos: [], intro_elements: introElements },
      });
      queryClient.invalidateQueries({ queryKey: ['session', shopifyOrderId, albumIndex] });
    } catch (e) {
      console.error('[Editor] Failed to save blank pages', e);
    }
  }, [session?.id, order?.page_count, shopifyOrderId, albumIndex, queryClient, introElements]);

  // De-duplicate pages + strip invalid photo IDs
  useEffect(() => {
    if (localPages.length > 0 && allPhotos.length > 0) {
      const validIds = new Set(allPhotos.map((p) => p.id));
      const seen = new Set<number>();
      let changed = false;
      const cleaned = localPages
        .filter((p) => {
          if (seen.has(p.page)) { changed = true; return false; }
          seen.add(p.page);
          return true;
        })
        .map((page) => {
          const validPhotos = page.photos.filter((pid) => !pid || validIds.has(pid));
          if (validPhotos.length !== page.photos.length) changed = true;
          return { ...page, photos: validPhotos };
        });
      if (changed) {
        setLocalPages(cleaned);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPhotos.length, localPages.length]);

  // ─── Commander entry-point ──────────────────────────────────────────────
  // Shared between the desktop header button and the mobile floating CTA so
  // the two entry points can't drift on what counts as "ready to order". The
  // closure recomputes against the latest state on every render (no useCallback
  // needed — we read the latest values each click and the buttons don't memo).
  const COMMANDER_LOCKED_STATUSES = ['in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered'] as const;
  const isOrderLocked = !!(order && (COMMANDER_LOCKED_STATUSES as readonly string[]).includes(order.status));
  const handleCommanderClick = () => {
    if (isOrderLocked) return;
    const errors: string[] = [];
    if (allPhotos.length < MIN_PHOTOS) {
      errors.push(`Minimum ${MIN_PHOTOS} photos requises (vous en avez ${allPhotos.length})`);
    }
    if (!selectedCover) {
      errors.push('Veuillez choisir une couverture dans l\'onglet Thèmes');
    }
    // Only flag a page when it's genuinely empty (2026-07-06, Yahya): a
    // deliberate blank page, or a page carrying ANY element (free/detached
    // photo, text, icon, shape) is a valid client choice. A page with content
    // — a filled slot OR any element — is never a problem. Only a truly empty
    // page, or a partial grid the client never customised, is flagged.
    const hasContent = (p: LayoutPage) => p.blank || (p.elements?.length ?? 0) > 0;
    const emptySlotPages: number[] = [];
    for (const page of localPages) {
      if (hasContent(page)) continue;
      const maxSlots = { '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4, 'wide-top': 2, '3-up-flip': 3, '2-up-side': 2, '5-up': 5, '6-up': 6, '4-up-main': 4, '2-up-even': 2 }[page.layout] ?? 1;
      const filledSlots = page.photos.filter(Boolean).length;
      if (filledSlots < maxSlots && filledSlots > 0) emptySlotPages.push(page.page);
    }
    if (emptySlotPages.length > 0) {
      errors.push(`Pages avec emplacements vides: ${emptySlotPages.slice(0, 5).join(', ')}${emptySlotPages.length > 5 ? '...' : ''}`);
    }
    const blankPages = localPages.filter((p) => !hasContent(p) && p.photos.filter(Boolean).length === 0);
    if (blankPages.length > 0) {
      errors.push(`${blankPages.length} page${blankPages.length > 1 ? 's' : ''} sans photo`);
    }
    // A WARNING, never a blocker — see `validationWarnings`. Said once, in
    // case he simply forgot; passed the moment he confirms.
    const warnings: string[] = [];
    if (!coverDestText.trim()) {
      warnings.push('Aucun titre sur la couverture — elle sera imprimée sans texte.');
    }
    if (localPages.length === 0) {
      errors.push('Aucune page dans l\'album — générez d\'abord la mise en page');
    }
    setValidationErrors(errors.length > 0 ? errors : []);
    setValidationWarnings(warnings);
    setShowValidation(true);
  };

  // ─── Loading state ──────────────────────────────────────────────────────

  if (sessionLoading || photosLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: UI.desk, fontFamily: "'DM Sans', sans-serif" }}
      >
        <div className="text-center">
          <div className="mx-auto w-14 h-14 mb-5 relative">
            <div
              className="absolute inset-0 rounded-full border-3"
              style={{ borderColor: `${COLORS.gold}30` }}
            />
            <div
              className="absolute inset-0 rounded-full border-3 border-t-transparent animate-spin"
              style={{ borderColor: COLORS.gold, borderTopColor: 'transparent' }}
            />
          </div>
          <p style={{ color: UI.muted, fontSize: 14 }}>
            Chargement de l'editeur...
          </p>
        </div>
      </div>
    );
  }

  // Once submitted, the customer can no longer edit — show the "soumis" screen
  // instead of the editor (no edit controls, no link). Admins re-open submitted
  // orders to regenerate the PDF (isAdminRegenMode), so they bypass this gate.
  // An admin can also grant the customer a one-shot re-entry by unlocking this
  // album (session.editor_unlocked) — e.g. to re-upload photos that failed to
  // upload. /submit re-locks it automatically. See editorAccess.ts.
  //
  // The "share for upload" window (session.upload_allowed + upload_allowed_until)
  // also bypasses the gate — its grant sets editor_unlocked=true too, but we
  // additionally honour it explicitly so the window can outlive a stale
  // editor_unlocked read. Expiry is computed client-side, so once 48h pass the
  // album re-locks on the next render even without a server round-trip.
  const uploadWindowOpen = isUploadWindowOpen(session);
  if (
    order && isAwaitingConfirmation(order.status) && !isAdmin
    && !session?.editor_unlocked && !uploadWindowOpen
  ) {
    return <AlbumSubmittedScreen destination={order.destination_1} />;
  }

  // Zoom HUD (Section 2) — bottom-right floating chip with zoom % + quick
  // in/out/fit controls; 44×44 tap targets on phones. Fix H (2026-05-28): a
  // small WhatsApp dot joins the row when the stuck-user heuristic trips.
  // UI overlap fix (2026-10-18): from tablet width up it docks at the right end
  // of the pages bar (above the spread) instead of floating over the
  // bottom-right page corner; phones keep it floating bottom-right.
  const zoomHud = (
              <div
                className={cn('z-20 flex items-center gap-1 px-1.5 py-1 rounded-full bg-white shadow-lg shrink-0', isMobile && 'absolute bottom-3 right-3')}
                data-zoom-hud=""
                style={{ border: `1px solid ${COLORS.gold}25` }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={zoomOut}
                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-base font-bold transition-all active:scale-95"
                  style={{ color: UI.ink }}
                  title="Zoom arrière (Ctrl/Cmd −)"
                  aria-label="Zoom arrière"
                >−</button>
                <button
                  onClick={resetZoom}
                  className="px-2 h-9 sm:h-8 rounded-full text-xs font-semibold tabular-nums min-w-[3.5rem] transition-all active:scale-95"
                  style={{ color: UI.ink }}
                  title="Réinitialiser (Ctrl/Cmd 0)"
                  aria-label="Zoom actuel — clic pour réinitialiser"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  onClick={zoomIn}
                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-base font-bold transition-all active:scale-95"
                  style={{ color: UI.ink }}
                  title="Zoom avant (Ctrl/Cmd +)"
                  aria-label="Zoom avant"
                >+</button>
                <div className="w-px h-5" style={{ background: `${COLORS.gold}25` }} />
                <button
                  onClick={fitToWindow}
                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all active:scale-95"
                  style={{ color: UI.ink }}
                  title="Adapter à la fenêtre (Ctrl/Cmd ⇧ H)"
                  aria-label="Adapter à la fenêtre"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" />
                  </svg>
                </button>
                {stuck.isStuck && (
                  <>
                    <div className="w-px h-5" style={{ background: `${COLORS.gold}25` }} />
                    <a
                      href={`https://wa.me/212620066907?text=${encodeURIComponent("Bonjour, j'ai besoin d'aide pour mon album Storiz.")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-95"
                      style={{ background: '#25D366', color: '#fff', boxShadow: '0 1px 3px rgba(37, 211, 102, 0.35)' }}
                      title="Aide WhatsApp"
                      aria-label="Aide WhatsApp"
                    >
                      <svg width={16} height={16} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                        <path d="M16.001 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.55-1.71a12.74 12.74 0 0 0 6.25 1.62h.001c7.06 0 12.799-5.73 12.799-12.8 0-3.42-1.33-6.63-3.75-9.05A12.72 12.72 0 0 0 16.001 3.2zm0 23.31h-.001a10.5 10.5 0 0 1-5.36-1.47l-.38-.23-3.89 1.02 1.04-3.79-.25-.39a10.55 10.55 0 0 1-1.62-5.65c0-5.84 4.75-10.59 10.6-10.59 2.83 0 5.49 1.1 7.49 3.1a10.53 10.53 0 0 1 3.1 7.5c0 5.84-4.75 10.5-10.71 10.5zm5.83-7.91c-.32-.16-1.88-.93-2.17-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.57-.94-.84-1.58-1.88-1.77-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.72-1.73-.99-2.37-.26-.62-.53-.53-.72-.54-.18-.01-.4-.01-.61-.01-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.39 4.76.75.32 1.34.52 1.8.67.76.24 1.45.21 2 .13.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.37.18-1.51-.08-.13-.29-.21-.61-.37z" />
                      </svg>
                    </a>
                  </>
                )}
              </div>
  );

  // « Régénérer » — the canvas pill and the « Thème de l'album » bar's « Appliquer » share it.
  const regenerateLayout = () => {
    if (!session?.id || allPhotos.length === 0) return;
    const placedCount = localPages.reduce((n, p) => n + p.photos.filter(Boolean).length, 0);
    if (placedCount > 0 && !window.confirm("Régénérer la mise en page ? Vos placements actuels seront remplacés.")) return;
    prewarmRef.current = null;
    prewarmKeyRef.current = null;
    handleGenerateFromEditor();
  };
  // The bar is for staff who may change an album's theme (same rule as the order page's save).
  const showThemeBar = isAdmin && canPerm('settings.manage') && !!session?.id;
  const themeBarAlbums = albumCountForBundle((order?.bundle ?? null) as never);

  // The classic phone sheets' contents (Mise en page / Modèles & décorations),
  // shared with the « Nouvel éditeur mobile » sheets — same components, same props.
  const closeMobileSheet = () => { setMobileSidebarOpen(null); setM2Sheet(null); };
  const layoutsSidebarMobileEl = (
              <LayoutsSidebar
                selectedPage={selectedPageSide === 'left' ? leftPage : rightPage}
                pageCount={localPages.length}
                basePages={basePagesPerAlbum}
                pricePerPage={pricePerPage}
                hasIntroPage={bookHasIntro}
                hasOutroPage={Boolean(introOutroUrls.outro)}
                onPageCountCommit={applyPageCount}
                pageCountDraft={pageCountDraft}
                onPageCountDraftChange={setPageCountDraft}
                onLayoutChange={handleLayoutChange}
                onApplyToAll={handleApplyLayoutToAll}
                layoutChoice={layoutChoice}
                onLayoutChoiceChange={handleLayoutChoiceChange}
                showRegenerateStyle={!m2 || isAdmin}
              />
  );
  const elementsSidebarMobileEl = (
              <ElementsSidebar
                selectedPage={selectedPageSide === 'right' ? rightPage : leftPage}
                onOpenModels={openModelsForFocusedPage ? () => { if (openModelsForFocusedPage()) closeMobileSheet(); } : undefined}
                onRemoveTemplate={() => { removeFocusedPageModel(); closeMobileSheet(); }}
                albumTheme={albumTheme}
                // The sheet closes so the client sees her sticker land (as the icons do).
                onAddSticker={(st) => {
                  if (addTileToCoverOnTap({ kind: 'sticker', sticker: st })) { closeMobileSheet(); return; }
                  if (addStickerToFocusedPage(st)) closeMobileSheet();
                }}
                covers={covers}
                destination={destination}
                onInsertCoverIcon={(c) => {
                  if (!c.design_url) return;
                  if (addTileToCoverOnTap({ kind: 'coverIcon', cover: c })) { closeMobileSheet(); return; }
                  const targetPage = selectedPageSide === 'right' ? rightPage : leftPage;
                  if (!targetPage) return;
                  const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                  const element: PageElement = {
                    id: newId,
                    type: 'icon',
                    x: 32, y: 32, width: 30, height: 22,
                    // Trimmed icon asset when generated (hugs the artwork box);
                    // falls back to the full design PNG.
                    imageUrl: c.icon_url ?? c.design_url,
                  };
                  setLocalPages((pages) => pages.map((p) =>
                    p.page === targetPage.page
                      ? { ...p, elements: [...(p.elements ?? []), element] }
                      : p
                  ));
                  setSelectedElementId(newId);
                  setSelectedElementIds(new Set([newId]));
                }}
                onInsertIcon={(icon) => {
                  if (addTileToCoverOnTap({ kind: 'adminIcon', icon })) { closeMobileSheet(); return; }
                  const targetPage = selectedPageSide === 'right' ? rightPage : leftPage;
                  if (!targetPage) return;
                  const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                  const element: PageElement = {
                    id: newId, type: 'icon',
                    x: 30, y: 30, width: 25, height: 25,
                    svg: icon.svg, iconColor: '#1F2937',
                  };
                  setLocalPages((pages) => pages.map((p) =>
                    p.page === targetPage.page
                      ? { ...p, elements: [...(p.elements ?? []), element] }
                      : p
                  ));
                  setSelectedElementId(newId);
                  setSelectedElementIds(new Set([newId]));
                  closeMobileSheet();
                }}
              />
  );

  // The cover tools (« COUVERTURE » + SpineEditor: title, year, fonts, colours
  // per face, spine, « Texte en plus »). Desktop: the right panel. « Nouvel
  // éditeur mobile »: the « Couverture » sheet (it was desktop-only before).
  // Change the cover: the catalogue modal — or, in the new phone layout, the
  // same catalogue (ThemesSidebar) in a docked sheet under the cover.
  const openCoverPicker = () => {
    if (m2) { setM2Sheet(null); setMobileSidebarOpen('themes'); return; }
    setCoverModalOpen(true);
  };
  const coverToolsSidebarEl = (() => {
            // Resolve the destination + year zone indices on the active cover
            // so the sidebar font pickers can write to the right override slot.
            const destZoneIdx = selectedCover?.text_zones?.findIndex((z) => z.type === 'destination') ?? -1;
            const yearZoneIdx = selectedCover?.text_zones?.findIndex((z) => z.type === 'year') ?? -1;
            const destZone = destZoneIdx >= 0 ? selectedCover?.text_zones?.[destZoneIdx] : undefined;
            const yearZone = yearZoneIdx >= 0 ? selectedCover?.text_zones?.[yearZoneIdx] : undefined;
            const destFont = coverZoneOverrides[destZoneIdx]?.fontFamily ?? destZone?.font_family ?? '';
            const yearFont = coverZoneOverrides[yearZoneIdx]?.fontFamily ?? yearZone?.font_family ?? '';
            const writeZoneFont = (idx: number, family: string) => {
              if (idx < 0) return;
              setCoverZoneOverrides((p) => ({ ...p, [idx]: { ...p[idx], fontFamily: family } }));
            };
            return (
            <>
            {/* FEATURE 2 — clickable cover preview / change block. Sits at the
                top of the cover-view tools panel so it's visible whether or
                not a cover is set. Opens the cover Modal (searchable grid). */}
            <div className="px-3 pt-3 pb-2 shrink-0" style={{ borderBottom: `1px solid ${COLORS.gold}15` }}>
              <span className="text-xs font-semibold" style={{ color: UI.ink }}>COUVERTURE</span>
              {selectedCover ? (
                <button
                  type="button"
                  onClick={openCoverPicker}
                  className="mt-2 w-full flex items-center gap-3 p-2 rounded-xl text-left transition-all hover:shadow-md"
                  style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel }}
                >
                  <div
                    className="w-12 shrink-0 rounded-lg overflow-hidden"
                    style={{ border: `1px solid ${COLORS.gold}25`, background: selectedCover.dominant_color ?? COLORS.beige }}
                  >
                    {pickerThumbUrl(selectedCover.thumbnail_url ?? selectedCover.design_url) ? (
                      <img
                        src={pickerThumbUrl(selectedCover.thumbnail_url ?? selectedCover.design_url) ?? undefined}
                        alt={selectedCover.name}
                        className="w-full aspect-[3/4] object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full aspect-[3/4]" style={{ background: selectedCover.dominant_color ?? COLORS.beige }} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate" style={{ color: UI.ink }}>{selectedCover.name}</p>
                    <span className="text-[11px] font-medium" style={{ color: COLORS.gold }}>Changer la couverture</span>
                  </div>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openCoverPicker}
                  className="mt-2 w-full flex flex-col items-center justify-center gap-1 py-4 rounded-xl text-center transition-all hover:shadow-md"
                  style={{ border: `1px dashed ${COLORS.gold}45`, background: UI.panel }}
                >
                  <svg className="w-6 h-6" style={{ color: `${COLORS.gold}` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-xs font-semibold" style={{ color: UI.ink }}>Couverture à venir</span>
                  <span className="text-[11px] font-medium" style={{ color: COLORS.gold }}>Choisir une couverture</span>
                </button>
              )}
            </div>
            <SpineEditor
              config={spineConfig}
              onChange={setSpineConfig}
              editing={editingSpine}
              onToggleEditing={setEditingSpine}
              coverDestText={coverDestText}
              onCoverDestTextChange={editCoverTitle}
              coverYearText={coverYearText}
              onCoverYearTextChange={setCoverYearText}
              onCoverTitleBlur={restoreEmptyCoverTitle}
              frontCoverBgColor={frontCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight}
              onFrontCoverBgColorChange={setFrontCoverBgColor}
              backCoverBgColor={backCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight}
              onBackCoverBgColorChange={setBackCoverBgColor}
              hideFrontCoverImage={hideFrontCoverImage}
              onHideFrontCoverImageChange={setHideFrontCoverImage}
              isAdmin={isAdmin}
              coverDestFontFamily={destFont}
              onCoverDestFontFamilyChange={(f) => writeZoneFont(destZoneIdx, f)}
              coverYearFontFamily={yearFont}
              onCoverYearFontFamilyChange={(f) => writeZoneFont(yearZoneIdx, f)}
              extraZones={coverExtraZones}
              onExtraZoneAdd={() => {
                // Place the new text just UNDER the title zone so it reads as a
                // subtitle; fall back to a sensible spot on covers with no
                // destination zone (blank covers). Inherits the title's font +
                // colour at ~half its size so it pairs visually.
                const dz = selectedCover?.text_zones?.[destZoneIdx];
                // Centre the new text UNDER the title: anchor at the title's
                // horizontal centre, just below it, align:'center'. Inherits the
                // title's font + colour at ~half size so it reads as a subtitle.
                const newZone: ExtraTextZone = dz
                  ? {
                      text: '',
                      x: dz.x + (dz.width ?? 0.8) / 2,
                      y: Math.min(0.9, dz.y + (dz.height ?? 0.1) + 0.02),
                      width: dz.width ?? 0.8,
                      height: 0.08,
                      fontSize: Math.max(12, Math.round((dz.font_size ?? 32) * 0.5)),
                      fontFamily: dz.font_family ?? EDITOR_TEXT_FONTS[0].family,
                      color: dz.color ?? '#ffffff',
                      align: 'center',
                    }
                  : { text: '', x: 0.5, y: 0.6, width: 0.8, height: 0.08, fontSize: 18, fontFamily: EDITOR_TEXT_FONTS[0].family, color: '#ffffff', align: 'center' };
                setSelectedCoverZone(`extra-${coverExtraZones.length}`);
                setCoverExtraZones((p) => [...p, newZone]);
              }}
              onExtraZoneChange={(i, ez) => setCoverExtraZones((p) => p.map((e, idx) => (idx === i ? ez : e)))}
              onExtraZoneDelete={(i) => {
                setCoverExtraZones((p) => p.filter((_, idx) => idx !== i));
                setSelectedCoverZone((cur) => (cur === `extra-${i}` ? null : cur));
              }}
            />
            </>
            );
  })();

  // Recolours front + spine + back at once (the cover's brush buttons and the
  // « Couverture » sheet's colour). Legacy covers have a baked-in bg PNG that
  // would hide the picked colour, so the artwork is hidden for those; covers
  // with a transparent design_url keep the illustration visible.
  const handleAllCoverBgColorChange = (c: string) => {
    setBackCoverBgColor(c);
    setFrontCoverBgColor(c);
    setSpineConfig((prev) => ({ ...prev, bgColor: c }));
    if (!selectedCover?.design_url) {
      setHideFrontCoverImage(true);
    }
  };

  // The page strip (cover · intro · spreads · outro, add / delete / long-press
  // reorder). Classic: the bottom carousel. « Nouvel éditeur mobile »: inside
  // the « Pages » sheet. One strip, one ref — only one is ever mounted.
  const pageStripEl = (
      <div
        className="shrink-0 flex flex-col"
        style={{
          background: UI.surface,
          borderTop: `1px solid ${COLORS.gold}15`,
          paddingBottom: 'max(4px, env(safe-area-inset-bottom))',
        }}
      >
      <div
        ref={bottomStripRef}
        // min-h, not h (2026-10-18): a fixed 100 px clipped the « p.2–3 »
        // labels whenever the thumb + label ran taller than the box (fonts,
        // zoom, the active label's pill) — overflow-x:auto also clips Y. The
        // box now grows to its content instead.
        className="storiz-strip-no-native-bar min-h-[100px] py-1.5 flex items-center px-4 gap-2 overflow-x-auto overflow-y-hidden"
        style={{
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
        }}
        onMouseDown={handleStripMouseDown}
        onWheel={(e) => {
          // Desktop: translate a vertical wheel into horizontal strip scroll
          // (trackpads already emit deltaX for 2-finger horizontal swipes —
          // keep those native). Makes the strip scrollable with a plain
          // mouse wheel instead of hijacking a page drag.
          const el = e.currentTarget;
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) {
            el.scrollLeft += e.deltaY;
          }
        }}
      >
        {/* Cover thumbnail */}
        <button
          onClick={() => setEditorView('cover')}
          className="shrink-0 flex flex-col items-center gap-1 transition-all"
        >
          <div
            className="flex gap-px rounded-md overflow-hidden"
            style={{
              outline:
                editorView === 'cover'
                  ? `2px solid ${COLORS.gold}`
                  : `1px solid ${COLORS.gold}25`,
              outlineOffset: 1,
            }}
          >
            <CoverMiniThumbnail
              cover={selectedCover}
              spineConfig={spineConfig}
            />
          </div>
          <span
            className="text-[9px] font-medium"
            style={{
              color: editorView === 'cover' ? COLORS.gold : UI.muted,
            }}
          >
            Couverture
          </span>
        </button>

        <div
          className="h-10 w-px shrink-0"
          style={{ background: `${COLORS.gold}20` }}
        />

        {/* Intro branding page — auto-prepended in the printed book.
            Read-only here so the customer sees what will print but can't
            edit it (admin uploads it in Settings → Identité de marque). */}
        {/* PAGE NUMBERING (2026-08-11, Yahya): the intro IS page 1, so the
            first photo page is page 2 and every spread below is offset by one.
            Before this the intro was unnumbered and the first spread was
            "p.1–2", which did not match how the printed book reads. */}
        {/* A THEMED book has no intro page — its S1 opening is page 1
            (bookStructure.ts) — so there is nothing to show or edit here. */}
        {!themedBook && (
          <BookendThumb
            url={introOutroUrls.intro}
            label="Intro"
            pageLabel="p.1"
            active={editorView === 'intro'}
            onClick={() => setEditorView('intro')}
          />
        )}

        {/* Insert page button between Intro and the first spread. Customers
            asked for a way to add a fresh blank page right after the intro
            without re-ordering existing pages. Uses afterArrayIndex = -1,
            which `handleInsertPage` now special-cases so basePageNum starts
            at 0 and the new pages become page 1, 2, shifting all existing
            pages down.
            A THEMED book has no intro and opens on its S1 page: nothing goes
            before it (the « + » after the opening adds pages 2–3 instead). */}
        {!themedBook && (
        <button
          onClick={(e) => handleStripPlus(-1, e.currentTarget)}
          className="w-5 h-10 rounded flex items-center justify-center transition-all hover:bg-gold/10 shrink-0"
          title={outroPageEnabled ? 'Ajouter une page après l\'intro' : 'Ajouter 2 pages après l\'intro'}
          aria-label={outroPageEnabled ? 'Ajouter une page après l\'intro' : 'Ajouter 2 pages après l\'intro'}
          aria-haspopup={outroPageEnabled ? 'menu' : undefined}
          style={{ color: `${COLORS.gold}60` }}
          onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.gold)}
          onMouseLeave={(e) => (e.currentTarget.style.color = `${COLORS.gold}60`)}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        )}

        {/* Page spread thumbnails with insert buttons — wrapped in @dnd-kit
            SortableContext so long-press + drag reorders spreads on mobile
            and desktop. The DndContext sensors give 8px distance to avoid
            stealing taps and 200ms hold on touch to avoid stealing scrolls. */}
        <SpreadStripSortable
          totalSpreads={totalSpreads}
          onReorder={handleReorderSpreads}
          onActiveChange={(active) => { isReorderingRef.current = active; }}
        >
        {Array.from({ length: totalSpreads }).map((_, spreadIdx) => {
          // The pages open on this spread, paired as the printed book
          // (editorSpreads.ts): a themed book's opening stands alone.
          const [li, ri] = spreadPages(spreadIdx, themedBook, pages.length);
          const lp = li != null ? pages[li] ?? null : null;
          const rp = ri != null ? pages[ri] ?? null : null;
          const isActive =
            editorView === 'pages' && spreadIdx === currentSpread;
          // Insert after the right page of this spread.
          const insertAfterIdx = insertAfterIndex(spreadIdx, themedBook);
          // Any selected spread is deletable down to a 1-spread floor (deletion
          // below the paid page_count is allowed — see handleDeletePage).
          const canDeleteThisSpread = pages.length > 2;
          // Guided-fix indicator — a small red dot on spreads that still have a problem.
          const spreadHasProblem = fixMode && spreadAlbumPages(spreadIdx, themedBook, pages.length).some((n) => problemPageSet.has(n));
          // The themed opening is print page 1: it never moves (reorderSpreads refuses it too).
          const pinned = themedBook && spreadIdx === 0;
          return (
            <SortableSpreadItem key={`s${spreadIdx}`} spreadIdx={spreadIdx} disabled={pinned} data-spread-idx={spreadIdx} className="relative flex items-center gap-1 shrink-0">
              {spreadHasProblem && (
                <span className="absolute -top-0.5 left-1 z-10 flex h-2.5 w-2.5" aria-hidden>
                  <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: COLORS.danger }} />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 ring-2 ring-white" style={{ background: COLORS.danger }} />
                </span>
              )}
              <SpreadThumbButton
                spreadIdx={spreadIdx}
                // Hide the delete chip while any overlay is on top of the
                // editor (validation modal / Photos strip / Mises-en-page /
                // Thèmes / Modèles sheets / tools drawer). Stops the chip
                // from floating over the modal content (the old portal-based
                // chip had z-60 and ignored backdrops).
                canDelete={canDeleteThisSpread && !showValidation && !mobileSidebarOpen && !m2}
                isActive={isActive}
                isMobile={isMobile}
                onTap={() => { goToSpread(spreadIdx); if (m2) setSelectedPageSide('left'); }}
                onDelete={() => {
                  // Route through the shared warning popup. Deletes the whole
                  // spread (both pages); confirmPageDelete removes them
                  // highest-page-first so renumbering stays valid.
                  const targets = [lp?.page, rp?.page].filter(
                    (n): n is number => typeof n === 'number',
                  );
                  requestDeletePages(targets);
                }}
              >
                <div
                  data-spread-thumb-frame
                  className="flex gap-px rounded-md overflow-hidden"
                  style={{
                    outline: isActive
                      ? `2px solid ${COLORS.gold}`
                      : `1px solid ${COLORS.gold}25`,
                    outlineOffset: 1,
                  }}
                >
                  <SpreadThumb page={lp} photoUrlMap={photoThumbUrlMap} side="left" premiumAfter={basePagesPerAlbum} photoGap={bookGap} outroUrl={introOutroUrls.outro} />
                  <SpreadThumb page={rp} photoUrlMap={photoThumbUrlMap} side="right" premiumAfter={basePagesPerAlbum} photoGap={bookGap} outroUrl={introOutroUrls.outro} />
                </div>
                <span
                  // NAV visibility (2026-07-04): the ACTIVE spread's label is a
                  // filled gold chip (was just a gold-tinted 9px text — too
                  // subtle to tell where you are at a glance).
                  className={isActive ? 'text-[9px] font-bold px-1.5 py-px rounded-full' : 'text-[9px] font-medium'}
                  style={isActive ? { background: COLORS.gold, color: COLORS.white } : { color: UI.muted }}
                >
                  {/* +2 not +1: the intro page above is page 1 (2026-08-11).
                      A themed book has no intro: its album page N is page N,
                      and its opening spread is « p.1 » alone. */}
                  {stripSpreadLabel(spreadIdx, themedBook, pages.length)}
                </span>
              </SpreadThumbButton>

              {/* Insert page button — adds an empty 2-page spread immediately,
                  no layout dialog. Pages start with 1-up slots; the customer
                  drops photos in by drag-from-sidebar or by tapping the empty
                  slot to upload from camera roll. */}
              <div className="relative shrink-0">
                <button
                  onClick={(e) => handleStripPlus(insertAfterIdx, e.currentTarget)}
                  className="w-5 h-10 rounded flex items-center justify-center transition-all hover:bg-gold/10 group"
                  title={outroPageEnabled ? 'Ajouter une page' : 'Ajouter 2 pages'}
                  aria-label={outroPageEnabled ? 'Ajouter une page' : 'Ajouter 2 pages'}
                  aria-haspopup={outroPageEnabled ? 'menu' : undefined}
                  style={{ color: `${COLORS.gold}60` }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.gold)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = `${COLORS.gold}60`)}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {/* (Layout-pick popup removed — empty pages are inserted
                    directly so the customer skips a step.) */}
                {insertAfterPageIdx === insertAfterIdx && (
                  <div
                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 rounded-xl shadow-2xl p-3 z-50 w-40 hidden"
                    style={{ background: UI.surface, border: `1px solid ${COLORS.gold}30` }}
                  >
                    <button
                      onClick={() => setInsertAfterPageIdx(null)}
                      className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded"
                      style={{ color: UI.muted }}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </SortableSpreadItem>
          );
        })}
        </SpreadStripSortable>

        {/* Outro branding page — auto-appended in the printed book.
            Same read-only treatment as the intro at the start of the strip. */}
        <BookendThumb
          url={introOutroUrls.outro}
          label="Outro"
          // Last page of the book: intro (1) + every photo page (a themed
          // book: every photo page, no intro).
          pageLabel={stripOutroLabel(themedBook, pages.length)}
          active={editorView === 'outro'}
          onClick={() => setEditorView('outro')}
        />

        {/* Added-pages indicator — count only, no price.
            Pricing is hidden during editing per product call: the customer
            should focus on the album, not the running total. The full
            breakdown (base + extra) shows up at checkout / confirmation. */}
        {addedPagesCount > 0 && (
          <div
            className="shrink-0 ml-2 px-2.5 py-1.5 rounded-lg"
            style={{ background: `${COLORS.gold}15`, border: `1px solid ${COLORS.gold}40` }}
          >
            <p className="text-[9px] font-semibold" style={{ color: UI.goldInk }}>
              +{addedPagesCount} page{addedPagesCount > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Premium minimalist scrollbar — lives OUTSIDE the SortableContext
          (which is inside the strip via SpreadStripSortable) so dnd-kit
          never sees scrollbar drags as reorder gestures. Auto-hides when
          the strip content fits. */}
      <HorizontalScrollbar scrollRef={bottomStripRef} />
      {insertMenu && (
        <InsertPageMenu
          anchor={insertMenu}
          onTwoPages={() => { const at = insertMenu.afterIdx; setInsertMenu(null); handleInsertPage(at, '1-up'); }}
          onOutro={() => handleInsertOutroPage(insertMenu.afterIdx)}
          onClose={() => setInsertMenu(null)}
        />
      )}
      </div>
  );

  // « Mes photos » — the gallery card's content (classic: the photos popup;
  // « Nouvel éditeur mobile »: the Photos sheet). Same grid, same handlers.
  const photosPanelInner = (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 gap-2" style={{ borderBottom: `1px solid ${COLORS.gold}15` }}>
              {selectMode ? (
                <>
                  {/* Select-all / deselect-all toggle (left) */}
                  {(() => {
                    const allIds = photosSortedByDate.map((p) => p.id);
                    const allSelected = allIds.length > 0 && allIds.every((id) => selectedPhotoIds.has(id));
                    return (
                      <button
                        onClick={() => setSelectedPhotoIds(allSelected ? new Set() : new Set(allIds))}
                        className="px-2 py-1.5 rounded-lg text-xs font-medium min-h-11 active:scale-95"
                        style={{ color: UI.goldDeep }}
                      >
                        {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                    );
                  })()}
                  {/* Live count (center) */}
                  <span className="text-xs font-bold flex-1 text-center" style={{ color: UI.ink }}>
                    {selectedPhotoIds.size} sélectionnée{selectedPhotoIds.size > 1 ? 's' : ''}
                  </span>
                  {/* Cancel select mode (right) */}
                  <button
                    onClick={exitSelectMode}
                    className="px-3 py-1.5 rounded-full text-xs font-medium min-h-11 active:scale-95"
                    style={{ background: `${COLORS.gold}15`, color: UI.goldDeep, border: `1px solid ${COLORS.gold}40` }}
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <>
                  {m2 ? <span /> : (
                  <span className="text-sm font-bold" style={{ color: UI.ink, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
                    Mes photos ({photosSortedByDate.length})
                  </span>
                  )}
                  <div className="flex items-center gap-2">
                    {photosSortedByDate.length > 0 && (
                      <button
                        onClick={() => { setSelectMode(true); setPickedPhotoForPlace(null); setPendingPlaceTarget(null); }}
                        className="px-2 py-1.5 rounded-lg text-xs font-medium min-h-11 active:scale-95"
                        style={{ color: UI.goldDeep }}
                      >
                        Sélectionner
                      </button>
                    )}
                    <button
                      onClick={openAddPhotos}
                      className="px-3 py-1.5 rounded-full text-xs font-medium min-h-11 active:scale-95"
                      style={{ background: `${COLORS.gold}15`, color: UI.goldDeep, border: `1px solid ${COLORS.gold}40` }}
                    >
                      + Ajouter
                    </button>
                    {!m2 && (
                    <button
                      onClick={() => { setMobileSidebarOpen(null); setPickedPhotoForPlace(null); setPendingPlaceTarget(null); exitSelectMode(); }}
                      className="w-8 h-8 rounded-full flex items-center justify-center active:scale-95"
                      style={{ background: `${COLORS.gold}10`, color: UI.muted }}
                      aria-label="Fermer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Admin-only — "Ordre d'upload" toggle on MOBILE (same control as the
                desktop left column). Only the photo SEQUENCE on Régénérer changes.
                Hidden in select mode so the gallery header stays focused. */}
            {isAdmin && !selectMode && (
              <label
                className="mx-4 mt-2.5 px-3.5 py-2.5 rounded-xl flex items-center gap-3 text-xs font-medium cursor-pointer select-none active:scale-[0.99] transition-all"
                style={
                  useUploadOrder
                    ? { background: `${COLORS.gold}18`, color: UI.goldInk, border: `1px solid ${COLORS.gold}55` }
                    : { background: `${COLORS.gold}0a`, color: COLORS.gold, border: `1px solid ${COLORS.gold}22` }
                }
              >
                <input
                  type="checkbox"
                  checked={useUploadOrder}
                  onChange={(e) => handleToggleUploadOrder(e.target.checked)}
                  className="shrink-0 w-[18px] h-[18px]"
                  style={{ accentColor: COLORS.gold }}
                />
                <span className="flex-1 leading-tight">
                  Ordre d'upload
                  <span className="block text-[10px] font-normal mt-0.5" style={{ color: UI.muted }}>
                    Garder l'ordre exact où le client les a ajoutées
                  </span>
                </span>
              </label>
            )}

            {/* Contextual placement hint */}
            {(pickedPhotoForPlace || (pendingPlaceTarget && !pickedPhotoForPlace)) && (
              <div className="px-4 py-1.5 text-[11px] text-center font-medium" style={{ background: `${COLORS.gold}12`, color: UI.goldInk }}>
                {pickedPhotoForPlace ? 'Touchez un emplacement vide pour placer cette photo' : 'Choisissez une photo pour cet emplacement'}
              </div>
            )}

            {/* Gallery grid — iPhone-Photos-style square thumbnails. VIRTUALIZED
                (Batch 1): only on-screen rows mount, so 158 photos no longer all
                mount at once (the iOS reload cause). Column count adapts to the
                sheet width (minTile 80px = the old 5rem auto-fill floor). */}
            <div className="px-3 py-3 flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              {photosSortedByDate.length === 0 ? (
                <div className="text-xs py-10 text-center" style={{ color: UI.muted }}>
                  Aucune photo. Touchez « + Ajouter » pour téléverser.
                </div>
              ) : (
                <VirtualPhotoGrid
                  count={photosSortedByDate.length}
                  minTile={80}
                  gap={6}
                  renderTile={(i) => {
                    const p = photosSortedByDate[i];
                    if (!p) return null;
                    const placed = placedPhotoIds.has(p.id);
                    const picked = pickedPhotoForPlace === p.id;
                    const selected = selectMode && selectedPhotoIds.has(p.id);
                    // Prefer the worker's small (~320px) tray tier — it removes the
                    // in-browser 1400px download + canvas downscale. Fall back to the
                    // client gridThumb only when the worker didn't mint one (HEIC).
                    const gthumb = p.tray_signed_url ?? mobileGridThumbs.get(p.id) ?? null;
                    const startPress = () => {
                      if (photoPressTimerRef.current) window.clearTimeout(photoPressTimerRef.current);
                      photoPressFiredRef.current = false;
                      photoPressTimerRef.current = window.setTimeout(() => {
                        photoPressFiredRef.current = true; // suppress the trailing click
                        setPhotoToDelete(p.id);
                      }, 500);
                    };
                    const cancelPress = () => {
                      if (photoPressTimerRef.current) { window.clearTimeout(photoPressTimerRef.current); photoPressTimerRef.current = null; }
                    };
                    return (
                      <button
                        onClick={() => {
                          // SELECT MODE: tap toggles selection only — never picks for
                          // placement, never the long-press delete.
                          if (selectMode) { toggleSelect(p.id); return; }
                          // A long-press just fired (delete confirm) — swallow the click.
                          if (photoPressFiredRef.current) { photoPressFiredRef.current = false; return; }
                          // ISSUE 8 — an empty photo ELEMENT was tapped: this
                          // photo fills it (cover-fit) + closes the picker.
                          if (photoElementTarget) {
                            fillPhotoElement(photoElementTarget.page, photoElementTarget.elementId, p.id);
                            setPhotoElementTarget(null);
                            setPickedPhotoForPlace(null);
                            setMobileSidebarOpen(null);
                            return;
                          }
                          // Slot-first: a target empty cell was chosen — fill it + close.
                          if (pendingPlaceTarget) {
                            handleDropToSlot(pendingPlaceTarget.page, pendingPlaceTarget.slot, p.id);
                            setPendingPlaceTarget(null);
                            setPickedPhotoForPlace(null);
                            setMobileSidebarOpen(null); // fills the cell + closes the popup (G1)
                            return;
                          }
                          // Photo-first: arm this photo, keep the popup open for a slot tap.
                          setPickedPhotoForPlace(picked ? null : p.id);
                        }}
                        // Long-press single-delete only OUTSIDE select mode.
                        onTouchStart={(e) => { if (!selectMode) { startPress(); beginTouchDrag(p.id, p.signed_url ?? null, e); } }}
                        onTouchMove={cancelPress}
                        onTouchEnd={cancelPress}
                        onContextMenu={(e) => e.preventDefault()}
                        className={cn(
                          // Square via the padding-top:100% spacer <span> below — NOT
                          // aspect-ratio. Older iOS Safari mishandles aspect-ratio here
                          // and renders TALL masonry cards; percentage padding is
                          // bulletproof on every Safari version.
                          'w-full rounded-lg overflow-hidden relative transition-all',
                          // the grey square shown until the photo loads; at night a
                          // dark one (the photo itself is never touched)
                          'bg-[#eee] dark:bg-adm-surface-3',
                          picked && 'ring-2 ring-offset-2 scale-105 dark:ring-offset-adm-surface',
                          selected && 'scale-95',
                        )}
                        style={{
                          boxShadow: picked
                            ? `0 0 0 2px ${COLORS.gold}`
                            : selected
                              ? `0 0 0 3px ${COLORS.gold}`
                              : '0 1px 2px rgba(0,0,0,0.08)',
                        }}
                      >
                        {/* Spacer: padding-top:100% → height = width → perfect square on
                            every browser, no aspect-ratio dependency. */}
                        <span aria-hidden className="block" style={{ paddingTop: '100%' }} />
                        {/* Small (~320px) tile — NEVER the 1400px signed_url, which
                            decoded to ~7.7MB each and reloaded the page. #eee
                            background shows until the small one is ready. */}
                        {gthumb && (
                          <img
                            src={gthumb}
                            alt=""
                            // absolute inset-0 → out of flow, so the cell stays square
                            // and object-cover crops to a uniform iPhone-Photos tile.
                            className="absolute inset-0 w-full h-full object-cover"
                            draggable={false}
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                          />
                        )}
                        {placed && !picked && !selectMode && (
                          <>
                            <div className="absolute inset-0 bg-black/10 pointer-events-none" />
                            <span
                              className="absolute bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full text-white text-[8px] font-medium pointer-events-none"
                              style={{ background: 'rgba(0,0,0,0.45)' }}
                            >
                              Placée
                            </span>
                          </>
                        )}
                        {/* Select-mode check-circle overlay (top-right) + selected dim. */}
                        {selectMode && (
                          <>
                            {selected && <div className="absolute inset-0 bg-black/25 pointer-events-none" />}
                            <span
                              className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center pointer-events-none"
                              style={
                                selected
                                  ? { background: COLORS.gold, boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }
                                  : { background: 'rgba(0,0,0,0.18)', border: '2px solid rgba(255,255,255,0.9)' }
                              }
                            >
                              {selected && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          </>
                        )}
                      </button>
                    );
                  }}
                />
              )}
            </div>
            <HiddenDuplicatesSection photos={hiddenDuplicates} onRestored={refetchEditorPhotos} tile={80} />

            {/* Bulk-delete action bar — only in select mode with a selection. */}
            {selectMode && selectedPhotoIds.size > 0 && (
              <div className="px-4 pt-2 pb-3" style={{ borderTop: `1px solid ${COLORS.gold}15` }}>
                <button
                  disabled={bulkDeleting}
                  onClick={() => {
                    const ids = photosSortedByDate.map((p) => p.id).filter((id) => selectedPhotoIds.has(id));
                    if (ids.length === 0) return;
                    const ok = window.confirm(`Supprimer ${ids.length} photo(s) ? Cette action est définitive.`);
                    if (ok) handleBulkDeletePhotos(ids);
                  }}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white min-h-11 active:scale-95 disabled:opacity-60"
                  style={{ background: '#dc2626' }}
                >
                  {bulkDeleting ? 'Suppression…' : `Supprimer la sélection (${selectedPhotoIds.size})`}
                </button>
              </div>
            )}
          </>
  );
  const photoDeleteConfirmEl = (
          <>
          {photoToDelete && (
            <div className="absolute inset-0 z-10 flex items-center justify-center p-6" onClick={() => setPhotoToDelete(null)}>
              <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} />
              <div className="relative bg-white rounded-2xl shadow-2xl p-5 w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()}>
                <p className="text-sm font-semibold mb-1" style={{ color: UI.ink }}>Supprimer cette photo ?</p>
                <p className="text-xs mb-4" style={{ color: UI.muted }}>Elle sera retirée de l'album et de vos photos.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPhotoToDelete(null)}
                    className="flex-1 py-2 rounded-lg text-sm font-medium"
                    style={{ border: `1px solid ${COLORS.gold}30`, color: UI.ink }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => { const id = photoToDelete; setPhotoToDelete(null); if (id) handleDeletePhoto(id); }}
                    className="flex-1 py-2 rounded-lg text-sm font-bold text-white"
                    style={{ background: '#dc2626' }}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
  );

  // The text / element toolbar (FloatingElementToolbar). Classic layout: drawn
  // where it always was. « Nouvel éditeur mobile »: docked at the bottom, in
  // the column (never over the album) — above the keyboard while typing.
  const elementToolbarEl = (selectedElementId && elementToolbarOpen && !showValidation) ? (() => {
        // FIX C (2026-06-08): intro elements live outside localPages, so when
        // the intro view is active resolve the selection from introElements and
        // route edits/deletes to the intro handlers (which apply the QR
        // safe-zone clamp). findElementById is left untouched so copy/paste/
        // duplicate + the spread/coexistence effects never see intro ids.
        const introEl = editorView === 'intro'
          ? introElements.find((el) => el.id === selectedElementId)
          : undefined;
        const found = introEl
          ? { pageNumber: 0, element: introEl }
          : findElementById(selectedElementId);
        if (!found) return null;
        const isIntro = !!introEl;
        // Collect every font already used in the album so the picker's
        // "Document fonts" section is populated. De-dupe via Set, keep
        // order-of-first-appearance for stability between renders.
        const documentFonts: string[] = (() => {
          const seen = new Set<string>();
          const list: string[] = [];
          for (const page of localPages) {
            for (const el of page.elements ?? []) {
              if (el.type !== 'text') continue;
              const f = el.fontFamily;
              if (f && !seen.has(f)) { seen.add(f); list.push(f); }
            }
          }
          return list;
        })();
        return (
          <FloatingElementToolbar
            element={found.element}
            documentFonts={documentFonts}
            customFonts={customFonts}
            isEditing={editingElementId === found.element.id}
            onUpdate={(updates) => isIntro
              ? handleUpdateIntroElement(found.element.id, updates)
              : handleUpdateElement(found.pageNumber, found.element.id, updates)}
            onDelete={() => isIntro
              ? handleDeleteIntroElement(found.element.id)
              : handleDeleteElement(found.pageNumber, found.element.id)}
            // D78 (2026-05-21): Terminé / Escape / blur / outside-click all
            // route through here. We exit text-edit mode, close the heavy
            // toolbar, AND deselect the element so the entire editing
            // overlay disappears in one gesture — the customer expects a
            // single "I'm done" action, not three.
            onStopEditing={() => { setEditingElementId(null); setElementToolbarOpen(false); selectElement(null); }}
            boundsRef={viewportRef}
            inline={m2}
          />
        );
  })() : null;

  // ═══════════════ « NOUVEL ÉDITEUR MOBILE » (phones, switch on) ═══════════════
  // Everything below only RE-ARRANGES the components above for a phone: a slim
  // top bar, the album filling the rest at its printed ratio, a page row, docked
  // sheets and a tab bar. Same components, same handlers, same state.
  const m2Locked = m2 && isViewOnlyAlbum(order?.status, profileRole);
  // cover and intro zoom like the pages in the new layout (pinch, typing)
  const m2ZoomStyle: React.CSSProperties = {
    transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
    transformOrigin: 'center center',
    transition: zoomAnim ? 'transform 320ms ease' : 'transform 60ms linear',
    willChange: 'transform',
    ['--m2-zoom' as string]: zoom,
  };
  // one page's width in px (the intro page uses it — it used to follow 70vh)
  const m2PageFitPx = m2 && canvasSize.w > 0 && canvasSize.h > 0
    ? Math.max(60, Math.floor(Math.min(canvasSize.w - 16, (canvasSize.h - 16) * (200 / 297))))
    : 0;
  // a phone held sideways: shorter bars, the album keeps the height
  const m2Short = m2 && typeof window !== 'undefined' && window.innerHeight < 500 && !m2Viewport.keyboardOpen;
  // One page at a time: the page the customer is on (the spread's selected side).
  const m2Side: 'left' | 'right' = (selectedPageSide === 'left' && leftPage) || !rightPage ? 'left' : 'right';
  const m2ShownPage = m2Side === 'left' ? leftPage : rightPage;
  const m2HidePage = (side: 'left' | 'right') => m2 && !m2Double && side !== m2Side;
  const m2HasOutro = Boolean(introOutroUrls.outro);
  const m2AtStart = editorView === 'cover' && (m2Double || m2CoverFace === 'back');
  const m2AtEnd = editorView === 'outro'
    || (editorView === 'pages' && !m2HasOutro && currentSpread >= totalSpreads - 1 && (m2Double || m2Side === 'right' || !rightPage));
  // Cover → (intro) → page by page (or spread by spread) → outro, as the book reads.
  const m2Step = (dir: 1 | -1) => {
    if (editorView === 'cover') {
      // back ⇄ front, then into the book
      if (!m2Double && dir === -1 && m2CoverFace === 'front') { setM2CoverFace('back'); return; }
      if (!m2Double && dir === 1 && m2CoverFace === 'back') { setM2CoverFace('front'); return; }
      if (dir === 1) { if (bookHasIntro) setEditorView('intro'); else { goToSpread(0); setSelectedPageSide('left'); } }
      return;
    }
    if (editorView === 'intro') {
      if (dir === 1) { goToSpread(0); setSelectedPageSide('left'); } else { setM2CoverFace('front'); setEditorView('cover'); }
      return;
    }
    if (editorView === 'outro') {
      if (dir === -1) { goToSpread(totalSpreads - 1); setSelectedPageSide('right'); }
      return;
    }
    const lastSpreadIdx = totalSpreads - 1;
    // turning the page drops the selection (it would pull the view back to its page)
    selectElement(null);
    setSelectedSlotKey(null);
    setPanModeKey(null);
    if (dir === 1) {
      if (!m2Double && m2Side === 'left' && rightPage) { setSelectedPageSide('right'); return; }
      if (currentSpread < lastSpreadIdx) { goToSpread(currentSpread + 1); setSelectedPageSide('left'); return; }
      if (m2HasOutro) setEditorView('outro');
      return;
    }
    if (!m2Double && m2Side === 'right' && leftPage) { setSelectedPageSide('left'); return; }
    if (currentSpread > 0) { goToSpread(currentSpread - 1); setSelectedPageSide('right'); return; }
    if (!bookHasIntro) setM2CoverFace('front');
    setEditorView(bookHasIntro ? 'intro' : 'cover');
  };
  m2NavRef.current = m2 ? m2Step : null;
  m2SwipeableElRef.current = (id: string) => {
    if (id === m2SelBeforeTouchRef.current || id === editingElementId) return false;
    const f = findElementById(id);
    if (!f || f.element.type !== 'photo') return false;
    return !!localPages.find((p) => p.page === f.pageNumber)?.theme;
  };
  const m2Position = editorView === 'cover' ? (m2Double ? 'Couverture · dos et tranche' : m2CoverFace === 'back' ? 'Dos de l’album' : 'Couverture')
    : editorView === 'intro' ? 'Intro · p. 1'
    : editorView === 'outro' ? 'Dernière page'
    : m2Double ? `Pages ${spreadPrintText} / ${printedTotal}`
    : `Page ${m2ShownPage ? printPageOf(m2ShownPage.page, bookHasIntro) : '—'} / ${printedTotal}`;
  const m2ActiveTab: M2Tab | null = mobileSidebarOpen === 'images' ? 'photos'
    : mobileSidebarOpen === 'layouts' ? 'layout'
    : m2Sheet === 'pages' ? 'pages'
    : m2Sheet === 'text' ? 'text'
    : m2Sheet === 'cover' ? 'cover'
    : null;
  const m2CloseAll = () => {
    setM2Sheet(null);
    setMobileSidebarOpen(null);
    setPickedPhotoForPlace(null);
    setPendingPlaceTarget(null);
    exitSelectMode();
  };
  const m2SelectTab = (tab: M2Tab) => {
    if (m2ActiveTab === tab) { m2CloseAll(); return; }
    setM2Sheet(null);
    setMobileSidebarOpen(null);
    if (tab === 'photos') { setMobileSidebarOpen('images'); return; }
    if (tab === 'layout') { if (editorView !== 'pages') goToSpread(currentSpread); setMobileSidebarOpen('layouts'); return; }
    if (tab === 'cover') { setM2CoverFace('front'); setEditorView('cover'); setM2Sheet('cover'); return; }
    setM2Sheet(tab);
  };
  // The page a new text / photo / sticker lands on: the one on screen.
  const m2TargetPage = m2Double ? (selectedPageSide === 'right' ? rightPage : leftPage) : m2ShownPage;
  const m2AddTypedText = () => {
    const t = m2NewText.trim();
    if (!t) return;
    if (editorView !== 'pages') goToSpread(currentSpread);
    if (!m2TargetPage) return;
    handleAddElement(m2TargetPage.page, 'text', t);
    setM2NewText('');
    m2CloseAll();
  };
  const m2AddOnPage = (type: 'text' | 'photo' | 'rect' | 'ellipse') => {
    if (editorView === 'intro') {
      // the intro is a text page (IntroEditableView offers « Texte » only)
      if (type === 'text') { handleAddIntroElement('text'); m2CloseAll(); }
      return;
    }
    if (editorView !== 'pages') goToSpread(currentSpread);
    if (!m2TargetPage) return;
    // focus() happens INSIDE this tap (handleAddElement → keyboard catcher)
    handleToolClick(m2TargetPage.page, type);
    m2CloseAll();
  };
  const m2IsBlankCover = selectedCover?.id === '__blank__' || (selectedCover as { back_cover_style?: string } | null)?.back_cover_style === 'blank';
  const m2MaxBackImages = m2IsBlankCover ? 1 : 4;
  const m2BtnCls = 'min-h-12 px-3 rounded-xl flex items-center justify-center gap-2 text-[14px] font-semibold active:scale-[0.98] disabled:opacity-40';
  const m2SoftBtn: React.CSSProperties = { background: `${COLORS.gold}14`, color: UI.goldInk, border: `1px solid ${COLORS.gold}40` };

  const m2SheetEl = !m2 || m2Locked ? null
    : mobileSidebarOpen === 'images' ? (
      <MobileSheet sheetKey="photos" title={`Mes photos (${photosSortedByDate.length})`} onClose={m2CloseAll} height="50%"
        actions={
          <button type="button" onClick={() => setM2PhotoTools((v) => !v)} aria-expanded={m2PhotoTools}
            className="h-11 px-3 rounded-full text-[13px] font-semibold" style={m2PhotoTools ? { background: `${COLORS.gold}22`, color: UI.goldInk } : { color: UI.goldInk }}>
            Outils
          </button>
        }>
        {m2PhotoTools ? (
          <div className="px-3 pb-3 flex flex-col gap-2">
            <p className="px-1 text-[12.5px]" style={{ color: UI.muted }}>{placedPhotoIds.size} placées · {unplacedPhotos.length} non placées</p>
            <button type="button" className={m2BtnCls} style={m2SoftBtn}
              disabled={!!autoOrientProgress || !!rotatingPhotoId || allPhotos.length === 0} onClick={handleAutoOrientAll}>
              {autoOrientProgress ? `Redressement ${autoOrientProgress.done}/${autoOrientProgress.total}…` : 'Pivoter auto (photos à l’envers)'}
            </button>
            <button type="button" className={m2BtnCls} style={{ background: `${COLORS.danger}10`, color: UI.danger, border: `1px solid ${COLORS.danger}33` }}
              disabled={placedPhotoIds.size === 0}
              onClick={() => { if (window.confirm('Retirer toutes les photos des pages ? Elles restent dans « Mes photos ».')) handleClearAllPlaced(); }}>
              Retirer toutes les photos des pages
            </button>
            {isAdmin && (
              <button type="button" className={m2BtnCls} style={m2SoftBtn}
                disabled={!!normalizeProgress || !!autoOrientProgress || !!rotatingPhotoId || allPhotos.length === 0} onClick={handleNormalizeAllOrientations}>
                {normalizeProgress ? `Redressement PDF ${normalizeProgress.done}/${normalizeProgress.total}…` : '🛠️ Redresser pour PDF (équipe)'}
              </button>
            )}
            <button type="button" className={m2BtnCls} style={{ background: COLORS.gold, color: COLORS.white }} onClick={() => setM2PhotoTools(false)}>
              Retour aux photos
            </button>
          </div>
        ) : (
          <div className="flex flex-col h-full">{photosPanelInner}</div>
        )}
      </MobileSheet>
    )
    : mobileSidebarOpen === 'layouts' ? (
      <MobileSheet sheetKey="layout" title={m2TargetPage ? `Mise en page · page ${printPageOf(m2TargetPage.page, bookHasIntro)}` : 'Mise en page'} onClose={m2CloseAll} maxHeight="50%">
        <div className="px-3 pt-1 pb-1 grid grid-cols-2 gap-2">
          <button type="button" className={m2BtnCls} style={m2SoftBtn} disabled={!m2TargetPage}
            onClick={() => { if (m2TargetPage) openPageMenu(m2TargetPage.page); }}>
            Options de la page
          </button>
          <button type="button" className={m2BtnCls} style={m2SoftBtn} disabled={!m2TargetPage || !openModelsForFocusedPage}
            onClick={() => { if (openModelsForFocusedPage?.()) setMobileSidebarOpen(null); }}>
            Modèles de page
          </button>
        </div>
        <div className="px-3 pb-1">
          <button type="button" className={cn(m2BtnCls, 'w-full')} style={m2SoftBtn} aria-expanded={m2PageTools}
            onClick={() => setM2PageTools((v) => !v)}>
            {m2PageTools ? 'Masquer les outils de la page' : 'Outils de la page (espacement, formes…)'}
          </button>
        </div>
        {m2PageTools && (
          <PageToolsSidebar
            photoGap={photoGap}
            onPhotoGapChange={handlePhotoGapChange}
            selectedPage={m2TargetPage}
            selectedSide={m2Double ? selectedPageSide : m2Side}
            placedCount={placedPhotoIds.size}
            unplacedCount={unplacedPhotos.length}
            totalPages={totalPages}
            onLayoutChange={handleLayoutChange}
            activeTool={activeTool}
            onToolClick={(tool) => { if (m2TargetPage) { handleToolClick(m2TargetPage.page, tool as ActiveTool); m2CloseAll(); } }}
            selectedElement={(() => {
              if (!selectedElementId || !m2TargetPage) return null;
              return m2TargetPage.elements?.find((el) => el.id === selectedElementId) ?? null;
            })()}
            onUpdateElement={(updates) => { const f = selectedElementId ? findElementById(selectedElementId) : null; if (f) handleUpdateElement(f.pageNumber, f.element.id, updates); }}
            onDeleteElement={() => { const f = selectedElementId ? findElementById(selectedElementId) : null; if (f) handleDeleteElement(f.pageNumber, f.element.id); }}
            onDeletePage={(p) => requestDeletePages([p])}
            canDeletePage={localPages.length > 2}
            onOpenTemplatePicker={() => {
              if (!m2TargetPage) return;
              const idx = localPages.findIndex((p) => p.page === m2TargetPage.page);
              if (idx !== -1) setTemplatePickerForPage(idx);
            }}
            onRemoveTemplate={() => {
              if (!m2TargetPage) return;
              if (m2TargetPage.theme) { handleRemoveThemeModel(m2TargetPage.page); return; }
              const idx = localPages.findIndex((p) => p.page === m2TargetPage.page);
              if (idx !== -1) removeTemplate(idx);
            }}
          />
        )}
        {!m2PageTools && layoutsSidebarMobileEl}
      </MobileSheet>
    )
    : mobileSidebarOpen === 'themes' ? (
      <MobileSheet sheetKey="themes" title="Choisir la couverture" onClose={() => { setMobileSidebarOpen(null); setM2Sheet('cover'); }} height="50%">
        <ThemesSidebar
          title="COUVERTURES"
          covers={covers}
          categories={categories}
          selectedCoverId={selectedCover?.id ?? null}
          onSelectCover={(cover) => { handleSelectCover(cover); setMobileSidebarOpen(null); setM2Sheet('cover'); }}
        />
      </MobileSheet>
    )
    : mobileSidebarOpen === 'elements' ? (
      <MobileSheet sheetKey="elements" title="Modèles et décorations" onClose={m2CloseAll} maxHeight="50%">
        {elementsSidebarMobileEl}
      </MobileSheet>
    )
    : m2Sheet === 'pages' ? (
      <MobileSheet sheetKey="pages" title={`Pages · ${totalPages}`} onClose={m2CloseAll} maxHeight="50%">
        <div className="px-3 pb-2 grid grid-cols-2 gap-2">
          <button type="button" className={m2BtnCls} aria-pressed={!m2Double} onClick={() => setM2Double(false)}
            style={!m2Double ? { background: COLORS.gold, color: COLORS.white } : m2SoftBtn}>
            Une page
          </button>
          <button type="button" className={m2BtnCls} aria-pressed={m2Double} onClick={() => setM2Double(true)}
            style={m2Double ? { background: COLORS.gold, color: COLORS.white } : m2SoftBtn}>
            Double page
          </button>
        </div>
        {pageStripEl}
        {editorView === 'pages' && (
          <div className="px-3 pt-1 pb-1 grid grid-cols-2 gap-2">
            <button type="button" className={m2BtnCls} style={m2SoftBtn}
              aria-label={outroPageEnabled ? 'Ajouter une page après celles-ci' : 'Ajouter 2 pages après celles-ci'}
              aria-haspopup={outroPageEnabled ? 'menu' : undefined}
              onClick={(e) => handleStripPlus(insertAfterIndex(currentSpread, themedBook), e.currentTarget)}>
              {outroPageEnabled ? '+ Ajouter ici' : '+ 2 pages ici'}
            </button>
            <button type="button" className={m2BtnCls} style={{ background: `${COLORS.danger}10`, color: UI.danger, border: `1px solid ${COLORS.danger}33` }}
              aria-label="Supprimer ces pages" disabled={pages.length <= 2}
              onClick={() => {
                const [li, ri] = spreadPages(currentSpread, themedBook, pages.length);
                const targets = [li != null ? pages[li]?.page : undefined, ri != null ? pages[ri]?.page : undefined]
                  .filter((n): n is number => typeof n === 'number');
                if (targets.length) requestDeletePages(targets);
              }}>
              Supprimer ces pages
            </button>
          </div>
        )}
        <p className="px-4 pt-1 pb-3 text-[12.5px] leading-snug" style={{ color: UI.muted }}>
          Touchez une double page pour l'ouvrir · « + » ajoute 2 pages · appui long sur une vignette pour la déplacer.
        </p>
      </MobileSheet>
    )
    : m2Sheet === 'text' ? (
      <MobileSheet sheetKey="text" title="Texte et décorations" onClose={m2CloseAll} maxHeight="50%">
        {editorView === 'cover' ? (
          <>
            <p className="px-4 pb-2 text-[13px] leading-snug" style={{ color: UI.muted }}>
              Touchez un texte sur la couverture pour le modifier, ou réglez le titre, l'année et la tranche ici.
            </p>
            {coverToolsSidebarEl}
          </>
        ) : (
          <>
            {editorView !== 'intro' && (
              // A REAL field: a tap on it is the user's own focus, so iOS always
              // raises the keyboard (no hand-off from a hidden input).
              <form className="px-3 pb-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); m2AddTypedText(); }}>
                <input
                  value={m2NewText}
                  onChange={(e) => setM2NewText(e.target.value)}
                  placeholder="Écrivez votre texte…"
                  aria-label="Votre texte"
                  enterKeyHint="done"
                  className="flex-1 min-w-0 min-h-12 px-3 rounded-xl outline-none"
                  style={{ fontSize: 16, color: UI.ink, background: UI.panel, border: `1px solid ${COLORS.gold}55` }}
                />
                <button type="submit" disabled={!m2NewText.trim()} className={m2BtnCls} style={{ background: COLORS.gold, color: COLORS.white }} aria-label="Ajouter ce texte sur la page">
                  Ajouter
                </button>
              </form>
            )}
            <div className="px-3 pb-2 grid grid-cols-2 gap-2">
              <button type="button" className={cn(m2BtnCls, 'col-span-2')} style={editorView === 'intro' ? { background: COLORS.gold, color: COLORS.white } : m2SoftBtn}
                data-onboarding-target="add-text" aria-label="Ajouter un texte" onClick={() => m2AddOnPage('text')}>
                <span aria-hidden className="text-lg leading-none">T</span> {editorView === 'intro' ? 'Ajouter un texte' : 'Écrire directement sur la page'}
              </button>
              {editorView !== 'intro' && (
                <>
                  <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => m2AddOnPage('photo')}>Photo libre</button>
                  <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => m2AddOnPage('rect')}>Rectangle</button>
                  <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => m2AddOnPage('ellipse')}>Ellipse</button>
                </>
              )}
            </div>
            {editorView !== 'intro' && elementsSidebarMobileEl}
          </>
        )}
      </MobileSheet>
    )
    : m2Sheet === 'cover' ? (
      <MobileSheet sheetKey="cover" title="Couverture" onClose={m2CloseAll} maxHeight="50%">
        <div className="px-3 pb-2 grid grid-cols-2 gap-2">
          <button type="button" className={m2BtnCls} style={{ background: COLORS.gold, color: COLORS.white }}
            onClick={openCoverPicker}>
            {selectedCover ? 'Changer de couverture' : 'Choisir la couverture'}
          </button>
          <label className={cn(m2BtnCls, 'relative cursor-pointer')} style={m2SoftBtn}>
            <span aria-hidden className="w-5 h-5 rounded-full shrink-0" style={{ background: frontCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15)' }} />
            Couleur
            <input
              type="color"
              aria-label="Couleur de la couverture (recto, tranche & dos)"
              value={frontCoverBgColor ?? selectedCover?.dominant_color ?? '#F5F0E8'}
              onChange={(e) => handleAllCoverBgColorChange(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            />
          </label>
        </div>
        {m2IsBlankCover && !isAdmin && (
          <div className="px-3 pb-2 grid grid-cols-2 gap-2">
            <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => setShowIconPicker(true)}>
              {frontCoverImageOverride ? 'Changer l’icône' : 'Choisir une icône'}
            </button>
            {frontCoverImageOverride && (
              <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={handleRestoreCoverIcon}>Retirer l’icône</button>
            )}
            <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => m2BlankInputRef.current?.click()}>
              {blankCoverImageUrl ? 'Changer l’image' : 'Importer ma propre image'}
            </button>
            {blankCoverImageUrl && (
              <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={handleRemoveBlankCoverImage}>Retirer l’image</button>
            )}
          </div>
        )}
        {canEditCoverElements && (
          <div className="px-3 pb-2 grid grid-cols-2 gap-2">
            <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => { setM2Sheet(null); setMobileSidebarOpen('elements'); }}>
              + Sticker / icône
            </button>
            <button type="button" className={m2BtnCls} style={m2SoftBtn} onClick={() => setCoverPhotoPickerOpen(true)}>
              + Photo sur la couverture
            </button>
          </div>
        )}
        <M2SectionTitle>Dos de l'album ({backCoverImages.length}/{m2MaxBackImages})</M2SectionTitle>
        <div className="px-3 pb-2 flex flex-wrap gap-2">
          {backCoverImages.map((im, idx) => (
            <div key={im.storage_path} className="flex flex-col items-center gap-1 rounded-xl p-1.5" style={{ border: `1px solid ${COLORS.gold}30` }}>
              <div className="w-16 h-16 rounded-lg overflow-hidden" style={{ background: `${COLORS.gold}12` }}>
                {im.signed_url && <img src={im.signed_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex gap-1">
                {!m2IsBlankCover && (
                  <button type="button" className="w-11 h-11 rounded-lg flex items-center justify-center text-[11px] font-bold"
                    style={im.full_page ? { background: COLORS.gold, color: COLORS.white } : m2SoftBtn}
                    aria-pressed={!!im.full_page}
                    aria-label={im.full_page ? 'Remettre en vignette' : 'Pleine page'}
                    onClick={() => handleBackCoverImageFullPage(idx, !im.full_page)}>
                    ⤢
                  </button>
                )}
                <button type="button" className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ background: `${COLORS.danger}12`, color: UI.danger }}
                  aria-label="Supprimer cette image du dos"
                  onClick={() => (m2IsBlankCover ? handleBackCoverImagesClear() : handleBackCoverImageDelete(idx))}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
            </div>
          ))}
          {backCoverImages.length < m2MaxBackImages && (
            <button type="button" className={cn(m2BtnCls, 'min-w-[8rem]')} style={m2SoftBtn} onClick={() => m2BackInputRef.current?.click()}>
              + {m2IsBlankCover ? 'Ajouter une image' : 'Ajouter des photos'}
            </button>
          )}
        </div>
        {coverToolsSidebarEl}
      </MobileSheet>
    )
    : m2Sheet === 'menu' ? (
      <MobileSheet sheetKey="menu" title="Plus d'options" onClose={m2CloseAll} maxHeight="55%">
        <div className="pb-3">
          <M2Row label={m2Double ? 'Une page à la fois' : 'Double page'} hint={m2Double ? 'Agrandir la page en cours' : 'Voir les deux pages côte à côte'}
            icon="⧉" onClick={() => { setM2Double((v) => !v); m2CloseAll(); }} />
          <M2Row label={editorView === 'cover' ? 'Voir les pages' : 'Voir la couverture'} icon="📖"
            onClick={() => { if (editorView === 'cover') goToSpread(currentSpread); else setEditorView('cover'); m2CloseAll(); }} />
          {isAdmin && !m2Locked && (
            <M2Row label="Historique des versions" hint="Restaurer un état précédent" icon="🕘"
              onClick={() => { setHistoryOpen(true); m2CloseAll(); }} />
          )}
          {isAdmin && !m2Locked && session?.id && (
            <M2Row label="Légendes IA" hint="Une ligne par langue sur chaque photo" icon="✍️"
              onClick={() => { if (editorView !== 'pages') goToSpread(currentSpread); setCaptionsOpen(true); m2CloseAll(); }} />
          )}
          <M2Row label="Aide" hint="Une question ? Écrivez-nous sur WhatsApp" icon="💬" href={waSupportLink(M2_HELP_MESSAGE)} />
          {isAdmin && !m2Locked && (
            <>
              <M2SectionTitle team>Équipe Storiz · invisible pour la cliente</M2SectionTitle>
              {showThemeBar && session?.id && (
                <div className="px-3 pb-2">
                  <AlbumThemeBar
                    key={session.id}
                    sessionId={session.id}
                    collapsible
                    title={themeBarAlbums > 1 ? `Thème de l'album ${albumIndex}/${themeBarAlbums}` : undefined}
                    onRegenerate={allPhotos.length > 0 ? () => { m2CloseAll(); regenerateLayout(); } : undefined}
                    regenerating={isGeneratingLayout}
                    className="shadow-none"
                  />
                </div>
              )}
              {allPhotos.length > 0 && (
                <M2Row label={`Modèles ${useTemplates ? 'activés' : 'désactivés'}`} hint="Inclure les modèles décorés dans la régénération IA"
                  icon="▦" active={useTemplates} onClick={() => setUseTemplates((v) => !v)} />
              )}
              <M2Row label={isGeneratingLayout ? 'Régénération…' : 'Régénérer la mise en page'} hint="L'IA refait les pages (remplace les placements)"
                icon="✨" disabled={isGeneratingLayout || allPhotos.length === 0} onClick={() => { m2CloseAll(); regenerateLayout(); }} />
              {aiError && <p role="alert" className="px-4 py-1 text-[13px]" style={{ color: UI.goldDeep }}>{aiError}</p>}
              {selectedCover && (
                <>
                  {m2IsBlankCover && (
                    <M2Row label={frontCoverImageOverride ? 'Changer l’icône' : 'Choisir une icône'} icon="✨"
                      onClick={() => { setEditorView('cover'); setShowIconPicker(true); m2CloseAll(); }} />
                  )}
                  <M2Row label={blankCoverImageUrl ? 'Changer la photo (plein cadre)' : 'Ma photo (plein cadre)'} hint="Remplace l'illustration de la couverture" icon="🖼️"
                    onClick={() => m2BlankInputRef.current?.click()} />
                  {blankCoverImageUrl && (
                    <M2Row label="Retirer la photo" hint="Revenir à l'illustration" icon="↺"
                      onClick={() => { setEditorView('cover'); handleRemoveBlankCoverImage(); m2CloseAll(); }} />
                  )}
                  <M2Row label="Remplacer l'illustration" hint="Un PNG / logo ajusté, non plein cadre" icon="🎨"
                    onClick={() => m2IconInputRef.current?.click()} />
                  {frontCoverImageOverride && (
                    <M2Row label="Rétablir l'original" icon="↺" onClick={() => { setEditorView('cover'); handleRestoreCoverIcon(); m2CloseAll(); }} />
                  )}
                  <M2Row label={hideFrontCoverImage ? "Afficher l'illustration" : "Masquer l'illustration"} hint={hideFrontCoverImage ? undefined : 'Fond uni'} icon="◐"
                    onClick={() => { setEditorView('cover'); setHideFrontCoverImage(!hideFrontCoverImage); m2CloseAll(); }} />
                </>
              )}
              <M2Row label={clientLinkCopied ? 'Lien copié ✓' : 'Lien client (cette page)'} hint="Copier un lien client qui ouvre cette page" icon="🔗"
                onClick={handleCopyClientLink} />
              {nightStaff && (
                <div className="min-h-12 px-4 flex items-center justify-between gap-3">
                  <span className="font-semibold" style={{ fontSize: 15, color: UI.ink }}>Mode nuit</span>
                  <ThemeToggle />
                </div>
              )}
              <M2Row label="Régénérer le PDF" hint="Refaire le PDF d'impression de cet album" icon="📄"
                disabled={isOrderLocked} onClick={() => { m2CloseAll(); handleCommanderClick(); }} />
            </>
          )}
        </div>
      </MobileSheet>
    )
    : null;

  // The page row under the album: ‹ where you are › · une / double page · Commander.
  const m2NavRowEl = (
    <div
      data-m2-navrow=""
      className="shrink-0 flex items-center gap-1 px-1.5"
      style={{ height: m2Short ? 46 : 52, background: UI.surface, borderTop: `1px solid ${COLORS.gold}20` }}
    >
      <button type="button" onClick={() => m2Step(-1)} disabled={m2AtStart}
        className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center active:scale-95 disabled:opacity-30"
        style={{ color: UI.ink }} aria-label="Page précédente">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <span className="min-w-0 shrink truncate text-center tabular-nums font-medium" style={{ fontSize: 13, color: UI.ink }} data-m2-position="">
        {m2Position}
      </span>
      <button type="button" onClick={() => m2Step(1)} disabled={m2AtEnd}
        className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center active:scale-95 disabled:opacity-30"
        style={{ color: UI.ink }} aria-label="Page suivante">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
      </button>
      {(editorView === 'pages' || editorView === 'cover' || editorView === 'intro') && (Math.abs(zoom - 1) > 0.01 || Math.abs(panX) > 1 || Math.abs(panY) > 1) && (
        <button type="button" onClick={() => { setZoomAnim(true); setZoom(1); setPanX(0); setPanY(0); window.setTimeout(() => setZoomAnim(false), 340); }}
          aria-label="Adapter à l'écran" title="Adapter à l'écran"
          className="h-11 min-w-11 shrink-0 px-2 rounded-full flex items-center justify-center gap-1 active:scale-95"
          style={{ background: `${COLORS.gold}22`, color: UI.goldInk }}>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" /></svg>
          <span className="text-[12px] font-semibold tabular-nums">{Math.round(zoom * 100)}%</span>
        </button>
      )}
      {(editorView === 'pages' || editorView === 'cover') && (
        <button type="button" onClick={() => setM2Double((v) => !v)} aria-pressed={m2Double}
          aria-label={m2Double ? 'Une page' : 'Double page'} title={m2Double ? 'Une page' : 'Double page'}
          // under 360 px (iPhone SE) the toggle lives in « Pages » and « … » only
          className="h-11 min-w-11 shrink-0 px-2 rounded-full hidden min-[360px]:flex items-center justify-center gap-1 active:scale-95"
          style={m2Double ? { background: `${COLORS.gold}22`, color: UI.goldInk } : { color: UI.muted }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
            {m2Double ? <rect x="6" y="4" width="12" height="16" rx="1" /> : (<><rect x="3" y="4" width="8" height="16" rx="1" /><rect x="13" y="4" width="8" height="16" rx="1" /></>)}
          </svg>
          <span className="hidden min-[430px]:inline text-[12px] font-medium">{m2Double ? 'Une page' : 'Double page'}</span>
        </button>
      )}
      <div className="flex-1" />
      {!m2Locked && !isAdmin && (
        <button
          type="button"
          onClick={handleCommanderClick}
          disabled={isOrderLocked}
          data-onboarding-target="finish-order"
          aria-label="Terminer et commander"
          className="h-11 shrink-0 px-3.5 rounded-full font-bold text-[13px] flex items-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
          style={{ background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`, color: COLORS.white, boxShadow: `0 3px 10px ${COLORS.gold}45` }}
        >
          <span><span className="hidden min-[420px]:inline">Terminer & </span>Commander</span>
          <svg className="w-4 h-4 hidden min-[360px]:block" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" /></svg>
        </button>
      )}
    </div>
  );

  // the photo under the selection (a grid slot or a model's window) — « Pivoter »
  // (the desktop tray's hover-only rotate) lives next to its docked bar
  const m2SelPhotoId = (() => {
    if (!m2 || m2Locked) return null;
    const m = selectedSlotKey?.match(/^p(\d+)-s(\d+)$/);
    if (m) return localPages.find((p) => p.page === Number(m[1]))?.photos[Number(m[2])] || null;
    if (selectedElementId) { const f = findElementById(selectedElementId); if (f?.element.type === 'photo') return f.element.photoId ?? null; }
    return null;
  })();
  const m2BottomEl = !m2 ? null : (
    <>
      {/* own hidden inputs: the menu / sheet can open them from any view */}
      <input ref={m2BlankInputRef} type="file" accept={galleryAccept()} hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.currentTarget.value = ''; if (f) { setEditorView('cover'); handleBlankCoverUpload(f); m2CloseAll(); } }} />
      <input ref={m2IconInputRef} type="file" accept={galleryAccept()} hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.currentTarget.value = ''; if (f) { setEditorView('cover'); handleCoverIconUpload(f); m2CloseAll(); } }} />
      <input ref={m2BackInputRef} type="file" accept={galleryAccept()} multiple={!m2IsBlankCover} hidden
        onChange={(e) => { const files = Array.from(e.target.files ?? []); e.currentTarget.value = ''; if (files.length) { setEditorView('cover'); void handleBackCoverFilesAdd(files); } }} />
      {/* the page row steps aside while a sheet or the keyboard needs the room */}
      {fixMode && problemPages.length > 0 && !showValidation && !m2Locked && (() => {
        const active = problemPages.find((pp) => openAlbumPages.includes(pp.page)) ?? problemPages[0];
        return (
          <div data-m2-fix="" role="status" className="shrink-0 flex items-center gap-2 pl-3 pr-1" style={{ minHeight: 44, background: `${COLORS.danger}0D`, borderTop: `1px solid ${COLORS.danger}26` }}>
            <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: COLORS.danger }} /></span>
            <span className="flex-1 min-w-0 text-[13px] leading-snug" style={{ color: UI.ink }}>
              Page {active.page} — {active.type === 'blank' ? 'Aucune photo' : 'Emplacements vides'}
              <span style={{ color: UI.muted }}> · {problemPages.length} problème{problemPages.length > 1 ? 's' : ''}</span>
            </span>
            <button type="button" onClick={() => setFixMode(false)} className="w-11 h-11 shrink-0 flex items-center justify-center" aria-label="Masquer" style={{ color: UI.muted }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        );
      })()}
      {aiError && !m2Locked && (
        <div role="alert" className="shrink-0 px-4 py-2 text-[13px] text-center" style={{ background: '#fffaf0', color: UI.goldDeep, borderTop: `1px solid ${COLORS.gold}55` }}>{aiError}</div>
      )}
      {!m2Viewport.keyboardOpen && !elementToolbarEl && (m2Locked || !m2SheetEl) && m2NavRowEl}
      {m2Locked ? <M2LockedBar /> : (
        <>
          {m2SheetEl}
          {elementToolbarEl && (
            <div data-m2-element-bar="" className="shrink-0 py-1" style={{ background: UI.surface, borderTop: `1px solid ${COLORS.gold}20` }}>
              {elementToolbarEl}
            </div>
          )}
          {m2SelPhotoId && (
            <div className="shrink-0 flex justify-center gap-2 px-2 pt-1.5" style={{ background: UI.surface, borderTop: `1px solid ${COLORS.gold}20` }} data-editor-toolbar="true">
              <button type="button" disabled={!!rotatingPhotoId} onClick={() => { if (window.confirm('Pivoter cette photo de 90° ? (le fichier de la photo est tourné)')) handleRotatePhoto(m2SelPhotoId); }}
                className="min-h-11 px-4 rounded-full text-[13px] font-semibold disabled:opacity-50" style={m2SoftBtn} aria-label="Pivoter la photo de 90°">
                {rotatingPhotoId === m2SelPhotoId ? 'Rotation…' : '↻ Pivoter la photo'}
              </button>
            </div>
          )}
          <div ref={setM2DockEl} className="m2-dock" data-m2-dock="" />
          {!elementToolbarEl && <MobileTabBar active={m2ActiveTab} onSelect={m2SelectTab} compact={m2Short} />}
        </>
      )}
      {photoToDelete && <div className="fixed inset-0 z-[60]">{photoDeleteConfirmEl}</div>}
    </>
  );

  return (
    <MobileDockContext.Provider value={m2 ? m2DockEl : null}>
    <div
      // Mobile Chrome viewport fix: `h-screen` is 100vh, which is sized for the
      // viewport with the URL bar COLLAPSED. With the bar visible (default on
      // load), 100vh overflows the visible area by the bar height, so the
      // header (and Commander button) clip under the URL bar, and the bottom
      // tab bar slips below the fold. `100dvh` is the dynamic viewport — it
      // tracks Chrome's bars as they show/hide, so nothing is ever hidden.
      // The inline 100vh fallback handles browsers that predate dvh (~Safari 15).
      // safe-area-inset-* covers the iPhone notch + Chrome's bottom toolbar bar.
      className={cn('flex flex-col overflow-hidden select-none h-screen', m2 && 'm2-root')}
      data-m2={m2 ? (m2Locked ? 'locked' : 'on') : undefined}
      data-kb={m2 && m2Viewport.keyboardOpen ? '1' : undefined}
      style={{
        // « Nouvel éditeur mobile »: while the keyboard is up the frame is the
        // VISIBLE part of the screen, so the text bar sits right above it.
        height: m2 && m2Viewport.keyboardOpen ? `${m2Viewport.height}px` : '100dvh',
        ...(m2 && m2Viewport.keyboardOpen ? { position: 'fixed' as const, top: m2Viewport.offsetTop, left: 0, right: 0 } : {}),
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: m2 && m2Viewport.keyboardOpen ? 0 : 'env(safe-area-inset-bottom)',
        ...(m2 ? { paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' } : {}),
        background: m2 ? UI.surface : UI.desk,
        fontFamily: "'DM Sans', sans-serif",
        color: UI.ink,
      }}
    >
      {/* Fix H (2026-05-28): the WhatsApp FAB used to be a fixed bottom-
          right circle; it now lives inside the zoom toolbar pill and only
          appears when the stuck-user heuristic trips. AlbumFlowPage still
          renders the original fixed FAB — only the editor moved. */}

      {/* Fix #15 (2026-05-28): first-time onboarding hints. Each persists
          its dismissal in localStorage so a returning customer never sees
          the hint again. Click anywhere → dismiss. */}
      {!m2 && (<>
      <OnboardingHint
        id="page-strip-reorder"
        // A themed book's opening spread never moves — point at the next one.
        target={themedBook ? '[data-spread-idx="1"]' : '[data-spread-idx="0"]'}
        text="Touchez longuement pour réorganiser"
        placement="top"
        showAfterMs={1200}
      />
      <OnboardingHint
        id="regenerate-ai"
        target='[data-onboarding-target="regenerate-ai"]'
        text="L'IA peut repenser votre mise en page"
        placement="bottom"
        showAfterMs={1500}
      />
      <OnboardingHint
        id="empty-slot-drop"
        target='[data-slot-empty="true"]'
        text="Glissez vos photos ici"
        placement="top"
        showAfterMs={1800}
      />
      {/* Smart hint (S4 FIX 3, 2026-06-08): after the customer taps the page a
          few times without adding anything, nudge them toward the Texte tool in
          the left strip. Mounted only when triggered; OnboardingHint enforces
          once-per-device + the 3 s auto-dismiss. On desktop the target (lg:hidden
          strip) isn't in the DOM, so it simply never shows. */}
      {showTextHint && (
        <OnboardingHint
          id="add-text-tool"
          target='[data-onboarding-target="add-text"]'
          text="Appuyez ici pour ajouter un texte ✏️"
          placement="right"
          showAfterMs={0}
          autoDismissMs={3000}
        />
      )}
      {/* H3 (2026-06-09): cover-colour discoverability indicator. Mounted only on
          the cover view so it surfaces the first time the customer is there;
          points the coach-pill at the (now more prominent) colour dot. Once per
          device + auto-dismiss via OnboardingHint. */}
      {editorView === 'cover' && (
        <OnboardingHint
          id="cover-color"
          target='[data-onboarding-target="cover-color"]'
          text="Touchez ici pour changer la couleur 🎨"
          placement="bottom"
          showAfterMs={700}
          autoDismissMs={4000}
        />
      )}
      {/* Guidance (2026-07-04): the two remaining first-timer gaps.
          'edit-photo' teaches THE core gesture on the first filled slot
          (Remplacer/delete then teach themselves via the visible selected-state
          bar). 'finish-order' points at the mobile CTA once so the finish line
          is known. Both once-per-device + auto-dismiss — zero nag. */}
      {editorView === 'pages' && (
        <OnboardingHint
          id="edit-photo"
          target='[data-slot-key]:not([data-slot-empty="true"])'
          text="2× sur une photo pour l'ajuster ✨"
          placement="top"
          showAfterMs={2600}
          autoDismissMs={5000}
        />
      )}
      {editorView === 'pages' && (
        <OnboardingHint
          id="page-delete"
          target='[data-onboarding-target="page-delete"]'
          text="✕ pour supprimer une page"
          placement="top"
          showAfterMs={5200}
          autoDismissMs={4000}
        />
      )}
      {editorView === 'pages' && (
        <OnboardingHint
          id="finish-order"
          target='[data-onboarding-target="finish-order"]'
          text="Tout vous plaît ? Commandez ici"
          placement="top"
          showAfterMs={9000}
          autoDismissMs={5000}
        />
      )}
      </>)}

      {/* ═══════════════ MODE CHOOSER (AI vs Manuel) ═══════════════
          Shown the first time the editor opens with photos uploaded but no
          pages yet. Picks once, then never again for this session. */}
      {/* ═══════════════ AI LAYOUT LOADER (top-level overlay) ═══════════════
          Shown whenever /ai/layout is in flight, regardless of which modal
          triggered the call. z-[70] sits above showLayoutChooser (z-[55]) and
          showUploadModal (z-50) so the customer always sees rich progress. */}
      {isGeneratingLayout && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
             style={{ background: 'rgba(26,26,26,0.55)', backdropFilter: 'blur(6px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
               style={{ border: `1px solid ${COLORS.gold}30` }}>
            <AiLayoutLoader photoCount={allPhotos.length} />
          </div>
        </div>
      )}

      {/* FEATURE 2 — Cover preview / change Modal. Reuses ThemesSidebar (its
          own search bar + category tabs + "Couverture vierge"). Picking a
          cover calls handleSelectCover (sets + persists per-album) and closes.
          A larger current-cover preview sits above the grid. All data
          (covers/categories) is already cached in scope — no new loads. */}
      <Modal
        isOpen={coverModalOpen}
        onClose={() => setCoverModalOpen(false)}
        size="xl"
        title={`Couverture${(coverDestText || destination) ? ` — ${coverDestText || destination}` : ''}`}
      >
        <div className="flex flex-col" style={{ height: '70vh' }}>
          {selectedCover && (
            <div className="shrink-0 flex items-center justify-center px-4 py-4" style={{ background: UI.panel, borderBottom: `1px solid ${COLORS.gold}15` }}>
              <CoverThumbnail
                cover={selectedCover}
                className="w-40 shadow-lg rounded-lg overflow-hidden"
                destinationText={coverDestText}
                yearText={coverYearText}
                customTexts={coverCustomTexts}
                zoneOverrides={coverZoneOverrides}
                extraZones={coverExtraZones}
                bgColorOverride={frontCoverBgColor}
                hideThumbnail={hideFrontCoverImage}
              />
            </div>
          )}
          <div className="flex-1 min-h-0">
            <ThemesSidebar
              covers={covers}
              categories={categories}
              selectedCoverId={selectedCover?.id ?? null}
              onSelectCover={(c) => { handleSelectCover(c); setCoverModalOpen(false); }}
              {...(m2 ? { title: 'COUVERTURES' } : {})}
            />
          </div>
        </div>
      </Modal>

      {/* Customer "Choisir une icône" picker (blank cover). Picking a
          catalogue décor routes its public design_url through the SAME
          front-cover override chain the admin upload uses — contain-fit,
          drag/zoom (coverDesignTransform) and PDF parity come for free. */}
      <CoverIconPickerModal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        covers={covers}
        destination={destination}
        onPick={(cover) => {
          if (!cover.design_url) return;
          setFrontCoverImageOverride({ url: cover.design_url });
          setFrontCoverImageOverrideUrl(cover.design_url);
          // A freshly picked icon must NOT inherit the previous design's
          // transform (same off-screen trap as the admin upload path).
          setCoverDesignTransform({ x: 0, y: 0, scale: 1 });
          setHideFrontCoverImage(false);
          setShowIconPicker(false);
        }}
      />

      {showLayoutChooser && !(m2 && isViewOnlyAlbum(order?.status, profileRole)) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] flex items-center justify-center p-4"
             onClick={() => setShowLayoutChooser(false)}>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
               onClick={(e) => e.stopPropagation()}>
            {/* Close — dismiss without choosing a mode (you can still pick one
                later, as the footer note says). Tap the ✕ or the backdrop. */}
            <button
              type="button"
              onClick={() => setShowLayoutChooser(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center active:scale-95 transition"
              style={{ background: `${COLORS.gold}12`, color: UI.muted }}
              aria-label="Fermer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="text-center mb-5">
              <div className="text-4xl mb-2">✨</div>
              <h2 className="text-lg sm:text-xl font-bold" style={{ color: UI.ink }}>
                Comment voulez-vous créer votre album ?
              </h2>
              <p className="text-sm mt-1" style={{ color: UI.muted }}>
                {allPhotos.length} photo{allPhotos.length > 1 ? 's' : ''} prête{allPhotos.length > 1 ? 's' : ''} à utiliser
              </p>
            </div>

            <div className="space-y-3">
              {/* AI option */}
              <button
                type="button"
                onClick={() => { setShowLayoutChooser(false); handleGenerateFromEditor(); }}
                disabled={isGeneratingLayout}
                className="w-full text-left p-4 rounded-xl border-2 transition group hover:shadow-md bg-[#fffaf0] dark:bg-adm-gold-wash"
                style={{ borderColor: COLORS.gold }}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl shrink-0">🤖</div>
                  <div className="flex-1">
                    <div className="font-semibold text-base" style={{ color: UI.ink }}>
                      Mise en page automatique (IA)
                      <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: COLORS.gold, color: '#fff' }}>
                        Recommandé
                      </span>
                    </div>
                    <div className="text-sm mt-1" style={{ color: UI.muted }}>
                      L'IA arrange vos photos en {order?.page_count ?? 30} pages. Vous pourrez tout modifier ensuite.
                    </div>
                  </div>
                </div>
              </button>

              {/* Manual option */}
              <button
                type="button"
                onClick={handleCreateBlankPages}
                disabled={isGeneratingLayout}
                className="w-full text-left p-4 rounded-xl border-2 transition group hover:shadow-md"
                style={{ borderColor: UI.desk, background: UI.surface }}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl shrink-0">✋</div>
                  <div className="flex-1">
                    <div className="font-semibold text-base" style={{ color: UI.ink }}>
                      Placer mes photos moi-même
                    </div>
                    <div className="text-sm mt-1" style={{ color: UI.muted }}>
                      {order?.page_count ?? 30} pages vides — glissez-déposez ou tapez sur les emplacements pour placer vos photos.
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <p className="text-xs text-center mt-4" style={{ color: UI.muted }}>
              Vous pourrez toujours changer de mode plus tard.
            </p>
          </div>
        </div>
      )}

      {/* ═══════════════ UPLOAD / GENERATE MODAL ═══════════════ */}
      {/* a view-only album (new phone layout) never offers an upload */}
      {showUploadModal && !(m2 && isViewOnlyAlbum(order?.status, profileRole)) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" style={{ maxHeight: '80vh', overflow: 'auto' }}>
            {isGeneratingLayout ? (
              <AiLayoutLoader photoCount={allPhotos.length} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: UI.ink, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
                    {allPhotos.length === 0 ? 'Ajoutez vos photos' : `${allPhotos.length} photos prêtes`}
                  </h3>
                  {!needsSetup && (
                    <button onClick={() => { setShowUploadModal(false); setUploadPicking(false); setUploadNotice(null); }} style={{ color: UI.muted }}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Drop zone */}
                <div
                  className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-4 hover:border-gold/50"
                  style={{ borderColor: `${COLORS.gold}40`, background: UI.panel }}
                  onClick={() => {
                    freeMemoryForPicker(); // shed memory so iOS keeps the page alive across the picker
                    if (isIosDevice()) setUploadPicking(true);
                    uploadFileInputRef.current?.click();
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = COLORS.gold; e.currentTarget.style.background = `${COLORS.gold}10`; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = `${COLORS.gold}40`; e.currentTarget.style.background = UI.panel; }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    e.currentTarget.style.borderColor = `${COLORS.gold}40`;
                    e.currentTarget.style.background = UI.panel;
                    if (e.dataTransfer.files?.length > 0) handleEditorUpload(e.dataTransfer.files);
                  }}
                >
                  <input
                    ref={uploadFileInputRef}
                    type="file"
                    accept={galleryAccept()}
                    multiple
                    className="hidden"
                    onChange={(e) => { setUploadPicking(false); if (e.target.files) handleEditorUpload(e.target.files); e.target.value = ''; }}
                  />
                  <svg className="w-10 h-10 mx-auto mb-3" style={{ color: `${COLORS.gold}60` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="font-medium text-sm mb-1" style={{ color: UI.ink }}>Cliquez ou glissez vos photos ici</p>
                  <p className="text-[11px]" style={{ color: UI.muted }}>JPG, PNG, WebP, HEIC — conversion automatique</p>
                </div>

                {/* iOS-only iCloud import reassurance — under the modal
                    dropzone, deepens while files are importing. Off-iOS: null. */}
                <IosUploadNote active={uploadingFiles.length > 0} style={{ marginBottom: 16 }} />

                {/* iOS picker-open spinner — visible from the tap on the
                    dropzone until `change`/`cancel`/timeout. The modal stays
                    open so iOS users see something happening while their
                    iCloud originals download. */}
                {uploadPicking && (
                  <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: UI.ink }}>
                    <span
                      className="w-4 h-4 rounded-full border-2 animate-spin shrink-0"
                      style={{ borderColor: `${COLORS.gold}40`, borderTopColor: COLORS.gold }}
                    />
                    <span>Chargement de vos photos… ça peut prendre quelques secondes 📸</span>
                  </div>
                )}

                {/* Notice — explains why a selection produced nothing
                    (empty FileList, still on iCloud, already in album) instead
                    of the old silent no-op. */}
                {uploadNotice && (
                  <div
                    className="mb-4 rounded-xl px-3 py-2 text-[12px] leading-snug"
                    style={{ background: `${COLORS.gold}12`, color: UI.ink, border: `1px solid ${COLORS.gold}30` }}
                  >
                    {uploadNotice}
                  </div>
                )}

                {/* Upload progress — animated with per-file progress bars */}
                {uploadingFiles.length > 0 && (() => {
                  const doneCount = uploadingFiles.filter((f) => f.status === 'done').length;
                  const errorCount = uploadingFiles.filter((f) => f.status === 'error').length;
                  const convertingCount = uploadingFiles.filter((f) => f.status === 'converting').length;
                  const totalPct = uploadingFiles.length > 0
                    ? Math.round(uploadingFiles.reduce((s, f) => s + (f.status === 'done' ? 100 : f.progress), 0) / uploadingFiles.length)
                    : 0;
                  return (
                    <div className="mb-4">
                      {/* Overall progress bar */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: `${COLORS.gold}15` }}>
                          <div
                            className="h-full rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${totalPct}%`, background: `linear-gradient(90deg, ${COLORS.gold}, ${COLORS.goldLight})` }}
                          />
                        </div>
                        <span className="text-[10px] font-semibold shrink-0 tabular-nums" style={{ color: COLORS.gold }}>
                          {doneCount}/{uploadingFiles.length}
                        </span>
                      </div>
                      {/* Status summary */}
                      <div className="flex items-center gap-3 text-[10px] mb-2" style={{ color: UI.muted }}>
                        {convertingCount > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                            {convertingCount} conversion{convertingCount > 1 ? 's' : ''} HEIC
                          </span>
                        )}
                        {doneCount > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            {doneCount} terminée{doneCount > 1 ? 's' : ''}
                          </span>
                        )}
                        {errorCount > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            {errorCount} erreur{errorCount > 1 ? 's' : ''}
                          </span>
                        )}
                        {/* Only the failed files go again; the worker's content-hash
                            dedupe keeps a retry from ever making a second copy. */}
                        {errorCount > 0 && failedUploadsRef.current.length > 0
                          && !uploadingFiles.some((f) => f.status === 'pending' || f.status === 'converting' || f.status === 'uploading') && (
                          <button
                            type="button"
                            data-upload-retry
                            className="ml-auto min-h-8 rounded-md px-2 py-1 text-[11px] font-semibold"
                            style={{ background: `${COLORS.gold}20`, color: COLORS.dark }}
                            onClick={() => { const failed = failedUploadsRef.current.splice(0); if (failed.length) void handleEditorUpload(failed); }}
                          >
                            Réessayer {errorCount > 1 ? `les ${errorCount} photos` : 'la photo'}
                          </button>
                        )}
                      </div>
                      {/* Per-file list */}
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {uploadingFiles.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px]">
                            <span className={cn(
                              'w-2 h-2 rounded-full shrink-0',
                              f.status === 'done' ? 'bg-green-500' :
                              f.status === 'error' ? 'bg-red-500' :
                              f.status === 'converting' ? 'bg-purple-400 animate-pulse' :
                              f.status === 'uploading' ? 'bg-amber-500 animate-pulse' :
                              'bg-gray-300'
                            )} />
                            <span className="truncate flex-1" style={{ color: UI.ink }}>{f.name}</span>
                            <span className="shrink-0 tabular-nums" style={{ color: UI.muted }}>
                              {f.status === 'done' ? '✓' : f.status === 'error' ? (f.error ?? 'Erreur') : f.status === 'converting' ? 'HEIC…' : `${f.progress}%`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Square-thumbnail grid of the just-added photos (bug 3) — so
                    "Ajouter" ends in a gallery, not a stacked progress list.
                    Reuses the optimized thumbnail tier (signed_url). */}
                {(() => {
                  const added = allPhotos.filter((p) => lastAddedPhotoIds.includes(p.id));
                  if (added.length === 0) return null;
                  return (
                    <div className="mt-3">
                      <p className="text-[11px] mb-1.5" style={{ color: UI.muted }}>
                        {added.length} photo{added.length > 1 ? 's' : ''} ajoutée{added.length > 1 ? 's' : ''}
                      </p>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-1.5 max-h-56 overflow-y-auto items-start">
                        {added.map((p) => (
                          <div key={p.id} className="relative w-full rounded-md overflow-hidden" style={{ background: `${COLORS.gold}10` }}>
                            {/* padding-top:100% spacer → square on every Safari (no aspect-ratio) */}
                            <span aria-hidden className="block" style={{ paddingTop: '100%' }} />
                            {p.signed_url && <img src={p.signed_url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Upload-only footer: photo count + a plain "Terminé" close.
                    Adding photos must NOT trigger (re)generation — that is the
                    separate "Régénérer IA" action on the canvas. (Bug 2 fix.) */}
                {allPhotos.length > 0 && (
                  <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${COLORS.gold}20` }}>
                    <p className="text-sm" style={{ color: UI.muted }}>
                      {allPhotos.length} photo{allPhotos.length > 1 ? 's' : ''} au total
                    </p>
                    <button
                      onClick={() => setShowUploadModal(false)}
                      className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                      style={{ background: COLORS.gold, color: COLORS.white }}
                    >
                      Terminé
                    </button>
                  </div>
                )}

                {/* Navigate to full upload page */}
                {allPhotos.length === 0 && (
                  <div className="text-center pt-2">
                    <button
                      onClick={() => navigate(`/editor/${shopifyOrderId}/${albumIndexParam}/photos`)}
                      className="text-[11px] underline" style={{ color: UI.muted }}
                    >
                      Aller à la page d'upload complète
                    </button>
                  </div>
                )}

                {/* Close if has layout already */}
                {!hasNoLayout && (
                  <div className="text-center mt-3">
                    <button onClick={() => setShowUploadModal(false)} className="text-[11px]" style={{ color: UI.muted }}>
                      Fermer — garder la mise en page actuelle
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Page count warning toast */}
      {pageCountWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-xl shadow-2xl animate-bounce"
          style={{ background: COLORS.danger, color: COLORS.white }}>
          <p className="text-sm font-semibold">{pageCountWarning}</p>
        </div>
      )}

      {/* Shared page-delete confirmation — used by scroller ✕, desktop tools
          panel, and mobile hold-to-delete. Renumbering is automatic on confirm. */}
      {pageDeleteRequest && (
        <PageDeleteConfirmModal
          count={pageDeleteRequest.pages.length}
          onConfirm={confirmPageDelete}
          onCancel={() => setPageDeleteRequest(null)}
        />
      )}

      {/* Page-count slider DECREASE — reuse the premium confirm modal with copy
          that warns about content loss. Cancel = nothing removed; the slider
          snaps back to the real page count on its next re-sync. */}
      {pageCountDecrease && (
        <PageDeleteConfirmModal
          count={pageCountDecrease.toRemove.length}
          title={`Réduire à ${pageCountDecrease.target} pages ?`}
          message={
            pageCountDecrease.removesContent
              ? `Cela supprimera ${pageCountDecrease.toRemove.length} page${pageCountDecrease.toRemove.length > 1 ? 's' : ''}, dont certaines contiennent des photos ou du texte. Les photos reviendront dans vos images ; le reste sera perdu.`
              : `Cela supprimera ${pageCountDecrease.toRemove.length} page${pageCountDecrease.toRemove.length > 1 ? 's' : ''} vide${pageCountDecrease.toRemove.length > 1 ? 's' : ''}.`
          }
          confirmLabel="Réduire"
          onConfirm={confirmPageCountDecrease}
          onCancel={() => { setPageCountDecrease(null); setPageCountDraft(null); }}
        />
      )}

      {/* ═══════════════ FOCUS MODE (Canva-style) ═══════════════ */}
      {focusedSlot && (() => {
        const photo = allPhotos.find(p => p.id === focusedSlot.photoId);
        const signedUrl = photo ? photoUrlMap.get(photo.id) ?? null : null;
        const slotKey = `p${focusedSlot.pageNumber}-s${focusedSlot.slotIdx}`;
        const position = slotPositions[slotKey] ?? { x: 0, y: 0 };
        return (
          <FocusMode
            signedUrl={signedUrl}
            position={position}
            onPositionChange={(pos) => { recordManualSlotEdit(slotKey, pos); setSlotPositions(p => ({ ...p, [slotKey]: pos })); }}
            onClose={() => setFocusedSlot(null)}
            onSmartCrop={() => {
              if (!photo) return;
              let x = 50, y = 50;
              if (photo.face_data && Array.isArray(photo.face_data) && (photo.face_data as any[]).length > 0 && photo.width && photo.height) {
                const faces = photo.face_data as { x: number; y: number; width: number; height: number }[];
                const centerX = photo.width / 2;
                const centerY = photo.height / 2;
                let totalWeight = 0, weightedX = 0, weightedY = 0;
                for (const f of faces) {
                  const fx = f.x + f.width / 2, fy = f.y + f.height / 2;
                  const dist = Math.sqrt((fx - centerX) ** 2 + (fy - centerY) ** 2);
                  const maxDist = Math.sqrt(centerX ** 2 + centerY ** 2);
                  const w = (1 - dist / maxDist * 0.5) + (f.width * f.height) / (photo.width * photo.height) * 100;
                  totalWeight += w; weightedX += fx * w; weightedY += fy * w;
                }
                if (totalWeight > 0) {
                  x = Math.round(weightedX / totalWeight / photo.width * 100);
                  y = Math.round(weightedY / totalWeight / photo.height * 100);
                }
              }
              setSlotPositions(p => {
                const next = { x, y, scale: p[slotKey]?.scale ?? 1, _v: 2 as const };
                recordManualSlotEdit(slotKey, next);
                return { ...p, [slotKey]: next };
              });
            }}
          />
        );
      })()}

      {/* ═══════════════ LOCK BANNER ═══════════════ */}
      {(() => {
        const LOCKED_STATUSES = ['in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered'];
        const isLocked = order && LOCKED_STATUSES.includes(order.status);
        return isLocked && !m2Locked ? (
          <div
            className="shrink-0 px-4 py-2.5 text-center z-30 bg-[#FEF3C7] border-b border-[#FDE68A] dark:bg-status-waiting-bg dark:border-status-waiting-border"
          >
            <p className="text-xs font-medium text-[#92400E] dark:text-status-waiting">
              Votre album est en cours de production — aucune modification n'est possible.
            </p>
          </div>
        ) : null;
      })()}

      {/* ═══════════════ UPLOAD WINDOW BANNER ═══════════════
          When an admin opened the "share for upload" window on a submitted
          album, surface a prominent "Ajouter des photos" affordance at the top.
          Reuses the existing upload modal — uploaded photos APPEND to the
          current album (handleEditorUpload dedups + never deletes). The window
          self-closes once upload_allowed_until passes (gate re-locks). */}
      {uploadWindowOpen && (
        <div
          className="shrink-0 px-4 py-2.5 flex items-center justify-center gap-3 z-30"
          style={{ background: `${COLORS.gold}18`, borderBottom: `1px solid ${COLORS.gold}40` }}
        >
          <p className="text-xs font-medium" style={{ color: UI.ink }}>
            Vous pouvez ajouter d'autres photos à votre album.
          </p>
          <button
            type="button"
            onClick={openAddPhotos}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 transition-all"
            style={{ background: COLORS.gold, color: COLORS.white }}
          >
            + Ajouter des photos
          </button>
        </div>
      )}

      {/* ═══════════════ « NOUVEL ÉDITEUR MOBILE » TOP BAR ═══════════════ */}
      {m2 ? (
        <MobileTopBar
          title={projectName || order?.shopify_order_name || 'Mon album'}
          titleEditing={editingProjectName}
          onTitleEditStart={() => setEditingProjectName(true)}
          onTitleChange={setProjectName}
          onTitleEditEnd={() => setEditingProjectName(false)}
          // a view-only album saves nothing — no status to show
          saveStatus={m2Locked ? 'idle' : saveStatus}
          everSaved={!m2Locked && !!lastSaveTime}
          position={m2Position}
          showEditActions={!m2Locked}
          canUndo={undoStack.length > 0}
          canRedo={redoStack.length > 0}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onBack={() => {
            if (isAdmin) { navigate('/admin/orders'); return; }
            if (shopifyOrderId) { navigate(`/flow/${shopifyOrderId}`); return; }
            window.location.href = 'https://storiz.ma';
          }}
          onMenu={() => { if (m2Sheet === 'menu') m2CloseAll(); else { m2CloseAll(); setM2Sheet('menu'); } }}
          menuOpen={m2Sheet === 'menu'}
          compact={m2Short}
        />
      ) : (<>
      {/* ═══════════════ PROFESSIONAL HEADER BAR ═══════════════ */}
      <header
        className="h-12 flex items-center px-3 gap-1 sm:gap-2 shrink-0 z-20"
        style={{
          background: UI.surface,
          borderBottom: `1px solid ${COLORS.gold}20`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        {/* Back — anonymous customers came here via /flow; bounce them
            back to the wizard preview, NOT to '/' (which would trip
            ProtectedRoute → /login). Admins testing the editor get the
            usual admin-dashboard return path below if they came from
            there. */}
        <button
          // NAV visibility (2026-07-04): 44px target + visible outline (was a
          // bare muted 16px icon that read as decoration).
          className="flex items-center justify-center gap-1 rounded-full text-xs transition-colors shrink-0 min-w-10 min-h-10 px-2"
          style={{ color: UI.ink, border: `1px solid ${COLORS.gold}30`, background: UI.surface }}
          onClick={() => {
            if (isAdmin) {
              navigate('/admin/orders');
              return;
            }
            if (shopifyOrderId) {
              navigate(`/flow/${shopifyOrderId}`);
              return;
            }
            window.location.href = 'https://storiz.ma';
          }}
          title="Retour"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Brand */}
        <span className="hidden sm:inline text-xs tracking-wider font-bold shrink-0" style={{ fontFamily: "'Londrina Solid', sans-serif", color: COLORS.gold, letterSpacing: '0.08em' }}>
          STORIZ
        </span>

        <div className="h-5 w-px shrink-0 hidden sm:block" style={{ background: `${COLORS.gold}20` }} />

        {/* Undo / Redo — mobile bumps to 44px tap target; desktop stays compact */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            className="w-11 h-11 sm:w-8 sm:h-8 rounded-md flex items-center justify-center transition-all active:scale-95"
            style={{
              color: undoStack.length > 0 ? UI.ink : withAlpha(UI.muted, '50'),
              cursor: undoStack.length > 0 ? 'pointer' : 'not-allowed',
              background: undoStack.length > 0 ? 'transparent' : 'transparent',
            }}
            title="Défaire (Ctrl/Cmd+Z)"
            aria-label="Défaire"
          >
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>
          </button>
          <span className="text-[8px] hidden 2xl:inline" style={{ color: UI.muted }}>Défaire</span>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="w-11 h-11 sm:w-8 sm:h-8 rounded-md flex items-center justify-center transition-all active:scale-95"
            style={{
              color: redoStack.length > 0 ? UI.ink : withAlpha(UI.muted, '50'),
              cursor: redoStack.length > 0 ? 'pointer' : 'not-allowed',
            }}
            title="Refaire (Ctrl/Cmd+Shift+Z)"
            aria-label="Refaire"
          >
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg>
          </button>
          <span className="text-[8px] hidden 2xl:inline" style={{ color: UI.muted }}>Refaire</span>
        </div>

        <div className="h-5 w-px shrink-0 hidden sm:block" style={{ background: `${COLORS.gold}20` }} />

        {/* View toggle */}
        <div className="flex rounded-md overflow-hidden shrink-0" style={{ border: `1px solid ${COLORS.gold}25` }}>
          <button
            className="px-2 py-1 text-[10px] font-medium transition-all"
            style={{ background: editorView === 'cover' ? COLORS.gold : 'transparent', color: editorView === 'cover' ? COLORS.white : UI.muted }}
            onClick={() => setEditorView('cover')}
          >
            Couv.
          </button>
          <button
            className="px-2 py-1 text-[10px] font-medium transition-all"
            style={{ background: editorView === 'pages' ? COLORS.gold : 'transparent', color: editorView === 'pages' ? COLORS.white : UI.muted }}
            onClick={() => setEditorView('pages')}
          >
            Pages
          </button>
        </div>

        {/* Fix #10 (2026-05-28): Régénérer IA moved out of the header pill
            row and into a prominent gold-gradient banner above the editor
            canvas — see <RegenerateAIBanner /> below. */}

        {/* Project name + order info */}
        <div className="flex-1 min-w-0 flex items-center gap-2 px-1">
          {editingProjectName ? (
            <input
              autoFocus
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={() => setEditingProjectName(false)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingProjectName(false); }}
              className="text-xs font-semibold px-1.5 py-0.5 rounded outline-none min-w-[80px] max-w-[200px]"
              style={{ color: UI.ink, border: `1px solid ${COLORS.gold}`, background: `${COLORS.gold}08` }}
            />
          ) : (
            <button
              onClick={() => setEditingProjectName(true)}
              // min-w (2026-10-18): at 1280 px the staff buttons squeezed the
              // album name down to nothing (« Val… » then blank).
              className="text-xs font-semibold truncate min-w-0 md:min-w-[4.5rem] max-w-[200px] hover:underline"
              style={{ color: UI.ink }}
              title="Cliquez pour renommer"
            >
              {projectName || order?.shopify_order_name || 'Mon album'}
            </button>
          )}
          <span className="text-[9px] min-w-0 truncate hidden xl:inline" style={{ color: UI.muted }}>
            {order?.shopify_order_name}
          </span>
        </div>

        {/* Save status */}
        <div className="flex items-center gap-1.5 shrink-0 px-1 sm:px-2" title={lastSaveTime ? `Dernière sauvegarde: ${lastSaveTime.toLocaleTimeString('fr-FR')}` : 'Non sauvegardé'}>
          {saveStatus === 'saving' && (
            <div className="w-3 h-3 rounded-full border border-t-transparent animate-spin" style={{ borderColor: `${COLORS.gold}60`, borderTopColor: 'transparent' }} />
          )}
          {saveStatus === 'saved' && (
            <svg className="w-3.5 h-3.5" style={{ color: COLORS.success }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          )}
          {saveStatus === 'error' && (
            <svg className="w-3.5 h-3.5" style={{ color: UI.danger }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          )}
          <div className="hidden md:flex flex-col">
            <span className="text-[8px] leading-tight" style={{ color: UI.muted }}>
              {saveStatus === 'saving' ? 'Sauvegarde...' : saveStatus === 'saved' ? 'Sauvegardé' : saveStatus === 'error' ? 'Erreur' : ''}
            </span>
            {lastSaveTime && (
              <span className="text-[7px] leading-tight" style={{ color: UI.hint }}>
                {lastSaveTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        <div className="h-5 w-px shrink-0 hidden sm:block" style={{ background: `${COLORS.gold}20` }} />

        {/* Page info */}
        {editorView === 'pages' && (
          // Two different numbers exist and both are correct, so label them.
          // The editor counts the customer's pages; the printed book and the
          // client's 3D preview also contain our intro + outro, so their
          // numbering runs one ahead and their total is two higher. Staff kept
          // reading "23–24/50" here and "24–25 sur 52" in the preview and
          // thinking something was broken.
          <span
            className="text-[10px] shrink-0 hidden sm:inline tabular-nums"
            style={{ color: UI.muted }}
            title={`Vos pages : ${spreadPagesText} sur ${totalPages}. `
              // A themed book prints no intro page (its S1 opening is page 1).
              + `Dans le livre imprimé et l'aperçu 3D (${themedBook ? 'outro incluse' : 'intro + outro incluses'}) : `
              + `${spreadPrintText} sur ${printedTotal}.`}
          >
            {spreadPagesText}/{totalPages}
            {/* the print numbering stays in the tooltip below 2xl (room for the name) */}
            <span className="hidden 2xl:inline" style={{ opacity: 0.65 }}>
              {' '}· impr. {spreadPrintText}/{printedTotal}
            </span>
          </span>
        )}

        {/* Admin-only — copy a client editor link that opens on THIS page.
            Lets support send "here's where to fix it" instead of page 1. */}
        {isAdmin && (
          <button
            type="button"
            onClick={handleCopyClientLink}
            title="Copier un lien client qui ouvre cette page"
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0"
            style={{
              border: `1px solid ${COLORS.gold}40`,
              color: clientLinkCopied ? COLORS.success : UI.goldInk,
              background: UI.surface,
            }}
          >
            {clientLinkCopied ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                Lien copié
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.656-3.656l1.5-1.5a4 4 0 115.656 5.656l-3 3a4 4 0 01-5.656 0" /></svg>
                Lien client (cette page)
              </>
            )}
          </button>
        )}

        {/* Historique — operator-only. Opens the checkpoint panel (one entry
            per ended editing session; restore = confirmed overwrite). */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            title="Historique des versions — restaurer un état précédent"
            aria-label="Historique des versions"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0"
            style={{ border: `1px solid ${COLORS.gold}40`, color: UI.goldInk, background: UI.surface }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3M3.05 11a9 9 0 111.5 5M3 16v-5h5" />
            </svg>
            <span className="hidden md:inline">Historique</span>
          </button>
        )}

        {/* Légendes IA — operator-only (#WA-269649). The vision model writes a
            line per language on each photo; nothing is saved before « Appliquer ». */}
        {isAdmin && editorView === 'pages' && session?.id && (
          <button
            type="button"
            onClick={() => setCaptionsOpen(true)}
            title="Légendes IA — une ligne par langue sur chaque photo"
            aria-label="Légendes IA"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0"
            style={{ border: `1px solid ${COLORS.gold}40`, color: UI.goldInk, background: UI.surface }}
          >
            <span aria-hidden>✍️</span>
            <span className="hidden md:inline">Légendes IA</span>
          </button>
        )}

        {/* Night mode — one tap light ⇄ dark, like the admin header (staff
            only). From tablet width up: the phone header has no room left, and
            the choice made in the admin follows the person here anyway. */}
        {nightStaff && (
          <span className="hidden md:inline-flex shrink-0">
            <ThemeToggle />
          </span>
        )}

        {/* Submit CTA — desktop/tablet only. On mobile the cramped checkmark
            icon was hard to spot inside the busy top bar (and clipped behind
            Chrome's URL bar); replaced by a full-width floating pill near the
            bottom of the screen. See the "MOBILE COMMANDER CTA" block below. */}
        <button
          className="hidden md:inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold transition-all shadow-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isOrderLocked}
          style={{
            background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`,
            color: COLORS.white,
            boxShadow: `0 2px 10px ${COLORS.gold}40`,
          }}
          onClick={handleCommanderClick}
        >
          {/* « Régénérer le PDF » (as on the phone CTA): this button re-renders
              the PRINT PDF (handleCommanderClick). The gold ✨ « Régénérer » above
              the spread is a different action — it redoes the LAYOUT
              (regenerateLayout). Both read « Régénérer » before (2026-10-18). */}
          {isAdminRegenMode ? 'Régénérer le PDF' : 'Commander'}
        </button>
      </header>
      </>)}

      {/* ═══════════════ « THÈME DE L'ALBUM » (staff) ═══════════════
          This album's own theme settings — each album of a Duo/Trio its own.
          Saved as they change; « Appliquer : Régénérer » reruns this album's layout. */}
      {!m2 && showThemeBar && session?.id && (
        <div className="shrink-0 px-2 sm:px-3 py-1.5 z-10" style={{ background: UI.desk, borderBottom: `1px solid ${COLORS.gold}20` }}>
          <AlbumThemeBar
            key={session.id}
            sessionId={session.id}
            collapsible
            title={themeBarAlbums > 1 ? `Thème de l'album ${albumIndex}/${themeBarAlbums}` : undefined}
            onRegenerate={allPhotos.length > 0 ? regenerateLayout : undefined}
            regenerating={isGeneratingLayout}
            className="shadow-none"
          />
        </div>
      )}

      {/* ═══════════════ FLOATING ELEMENT TOOLBAR (Section 1) ═══════════════
          Renders bottom-centred when a PageElement is selected AND the
          customer has opened it via the compact pill's "More" button
          (D78 Fix 1, 2026-05-21). Hidden when nothing is selected, the
          customer hasn't tapped "More" yet, or the submit modal is
          open. The heavy controls (font / colour / opacity / position /
          flip / crop) live here; the pill above the element handles
          rotate / lock / duplicate / delete / more. */}
      {!m2 && elementToolbarEl}

      {/* Guided-fix pill — non-blocking. Shows the active problem page + how
          many remain; disappears when all are fixed or dismissed. */}
      {!m2 && fixMode && problemPages.length > 0 && !showValidation && (() => {
        const active = problemPages.find((pp) => openAlbumPages.includes(pp.page)) ?? problemPages[0];
        const label = active.type === 'blank' ? 'Aucune photo' : 'Emplacements vides';
        const remaining = problemPages.length;
        return (
          <div
            className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-2.5 rounded-full px-4 py-2 shadow-lg"
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)', background: UI.surface, border: `1px solid ${COLORS.gold}33` }}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: COLORS.danger }} />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: COLORS.danger }} />
            </span>
            <span className="text-xs font-medium" style={{ color: UI.ink }}>
              Page {active.page} — {label}
              <span style={{ color: UI.muted }}> · {remaining} problème{remaining > 1 ? 's' : ''} restant{remaining > 1 ? 's' : ''}</span>
            </span>
            <button onClick={() => setFixMode(false)} className="ml-1 text-gray-400 hover:text-gray-600" aria-label="Masquer">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        );
      })()}

      {/* ═══════════════ EDIT-CONFLICT BANNER (last-writer guard) ═══════════════
          Someone else saved this album AFTER this editor loaded (client editing
          while an agent has the order open, or vice-versa). Autosave is stopped
          so we never overwrite their newer work; reload picks up the latest. */}
      {(() => {
        const by = session?.hand_edited_by ?? null;
        const at = session?.hand_edited_at ? Date.parse(session.hand_edited_at) : NaN;
        const mins = Number.isFinite(at) ? Math.round((Date.now() - at) / 60000) : NaN;
        const other = by && by !== 'client' && myEmail && by !== myEmail && Number.isFinite(mins) && mins <= 15;
        if (!other || editConflict) return null;
        return (
          <div
            // new phone layout: in the column, so it never hides the top bar
            className={cn(m2 ? 'relative shrink-0' : 'fixed top-0 inset-x-0', 'z-[69] flex items-center justify-center gap-3 px-4 py-2')}
            style={{ background: '#1D4ED8', color: '#fff' }}
            role="status"
          >
            <span className="text-[12.5px] font-semibold">
              👥 {by} a enregistré cet album il y a {mins} min. Vous travaillez peut-être à deux dessus.
            </span>
          </div>
        );
      })()}
      {editConflict && !editConflictDismissed && (
        <div
          className={cn(m2 ? 'relative shrink-0' : 'fixed top-0 inset-x-0', 'z-[70] flex items-center justify-center gap-3 flex-wrap px-4 py-3 pr-12')}
          style={{ background: '#B45309', color: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,.25)' }}
          role="alert"
        >
          <span className="text-[13px] font-semibold">
            ⚠️ Cet album a été modifié ailleurs (autre appareil). Vos derniers changements ne sont PAS enregistrés.
          </span>
          <button
            onClick={() => window.location.reload()}
            className="px-3.5 py-1.5 rounded-lg text-[12.5px] font-bold"
            style={{ background: '#fff', color: '#B45309' }}
          >
            Recharger la dernière version
          </button>
          {/* Dismiss the BAR, not the conflict. Absolutely positioned so it
              stays at the far right on one line and doesn't reflow onto its own
              row when the message wraps on mobile. 44px target. */}
          <button
            onClick={() => setEditConflictDismissed(true)}
            aria-label="Masquer cet avertissement"
            title="Masquer — l'album reste non enregistré tant que vous ne rechargez pas"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full text-lg leading-none"
            style={{ color: '#fff', background: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.18)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ═══════════════ CHECKPOINT HISTORY PANEL (operators) ═══════════════ */}
      {historyOpen && isAdmin && shopifyOrderId && (
        <EditorHistoryPanel
          shopifyOrderId={shopifyOrderId}
          albumIndex={albumIndex}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* ═══════════════ LÉGENDES IA (operators, #WA-269649) ═══════════════ */}
      {captionsOpen && isAdmin && session?.id && (
        <AiCaptionsDialog
          open
          onClose={() => setCaptionsOpen(false)}
          sessionId={session.id}
          pages={withSlotPositions(localPages, slotPositions) as unknown as CaptionPage[]}
          photos={allPhotos}
          gapMm={photoGap.mm}
          bandStyle={bandStyleFrom({ spineConfig })}
          // A normal edit: only the pages' elements change — autosave, undo and
          // history treat it like any other change.
          onApply={(placed) => setLocalPages((prev) => mergeElements(prev, placed))}
        />
      )}

      {/* ═══════════════ VALIDATION / SUBMISSION MODAL ═══════════════ */}
      {showValidation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {submitSuccess ? (
              /* ── Success state ── */
              <div className="p-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: `${COLORS.success}15` }}>
                  <svg className="w-8 h-8" style={{ color: COLORS.success }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: UI.ink, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
                  {isAdminRegenMode ? (regenOnServer ? 'C’est parti sur le serveur' : 'PDF régénéré !') : 'Album soumis avec succès !'}
                </h3>
                <p className="text-sm mb-6" style={{ color: UI.muted }}>
                  {isAdminRegenMode
                    ? (regenOnServer
                      ? (localQuick && localQuick.stage !== 'done'
                        ? 'Aperçu 3D rapide en cours dans cet onglet — gardez-le ouvert quelques secondes. Le PDF d’impression se refait sur le serveur.'
                        : 'Vous pouvez fermer cette fenêtre : l’aperçu 3D et le PDF se refont sur le serveur et arrivent tout seuls.')
                      : 'Les nouveaux fichiers sont disponibles sur la fiche commande.')
                    : 'Votre album est en cours de révision. Vous recevrez une notification une fois approuvé.'}
                </p>
                {isAdminRegenMode && regenOnServer && shopifyOrderId && (
                  <div className="mb-6">
                    <QuickBuildProgress shopifyOrderId={shopifyOrderId} album={albumIndex} local={localQuick} since={regenClickAt} />
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowValidation(false);
                    setSubmitSuccess(false);
                    // Admin regen: go straight back to the order page. The
                    // PdfGenerationCard there will refetch and show the new
                    // PDFs without admin needing to hit reload.
                    if (isAdminRegenMode) {
                      navigate(`/admin/orders/${shopifyOrderId}`);
                      return;
                    }
                    // Customers + admin testing a fresh order: checkout.
                    try { sessionStorage.removeItem('storiz_post_submit_next'); } catch { /* ignore */ }
                    window.location.href = getCheckoutRedirect(
                      nextUrl,
                      submittedOrder ?? order ?? { shopify_order_id: shopifyOrderId ?? null },
                    );
                  }}
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: COLORS.gold, color: COLORS.white }}
                >
                  {isAdminRegenMode ? 'Retour à la commande' : 'Continuer vers le paiement'}
                </button>
              </div>
            ) : (
              /* ── Validation / Confirmation state ── */
              <>
                <div className="px-6 py-4" style={{ borderBottom: `1px solid ${COLORS.gold}15` }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: UI.ink, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
                      {validationErrors.length > 0
                        ? 'Vérification de l\'album'
                        : (isAdminRegenMode ? 'Régénérer le PDF' : 'Confirmer la soumission')}
                    </h3>
                    <button
                      onClick={() => {
                        // Cancel the entire submit pipeline:
                        // 1. Flip the guard so the post-render redirect bails
                        //    instead of dragging the customer to checkout.
                        // 2. Clear any already-scheduled redirect timeout.
                        // 3. Drop the busy/progress UI so the customer can
                        //    keep editing if they want.
                        submitCancelledRef.current = true;
                        if (submitRedirectTimeoutRef.current) {
                          clearTimeout(submitRedirectTimeoutRef.current);
                          submitRedirectTimeoutRef.current = null;
                        }
                        setShowValidation(false);
                        setIsSubmitting(false);
                        setPdfProgress(null);
                        setSubmitSuccess(false);
                      }}
                      style={{ color: UI.muted }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4">
                  {validationErrors.length > 0 ? (
                    <>
                      <div className="flex items-start gap-3 mb-4 p-3 rounded-lg" style={{ background: `${COLORS.danger}08`, border: `1px solid ${COLORS.danger}20` }}>
                        <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: UI.danger }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                          <p className="text-sm font-semibold mb-1" style={{ color: UI.danger }}>
                            Problèmes à corriger avant soumission :
                          </p>
                          <ul className="space-y-1">
                            {validationErrors.map((err, i) => (
                              <li key={i} className="text-xs flex items-start gap-1.5" style={{ color: UI.ink }}>
                                <span style={{ color: UI.danger }}>•</span> {err}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <button
                        onClick={startFixFlow}
                        className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all"
                        style={{ background: COLORS.gold, color: COLORS.white }}
                      >
                        Corriger ces problèmes
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Warnings — said once, then obeyed. An album with no
                          words on the cover is a choice, not a mistake, and
                          this must never stand between him and Régénérer. */}
                      {validationWarnings.length > 0 && (
                        <div className="flex items-start gap-3 mb-4 p-3 rounded-lg" style={{ background: `${COLORS.gold}0E`, border: `1px solid ${COLORS.gold}40` }}>
                          <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <ul className="space-y-1">
                            {validationWarnings.map((w, i) => (
                              <li key={i} className="text-xs" style={{ color: UI.ink }}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Summary */}
                      <div className="space-y-2 mb-5">
                        {[
                          { l: 'Projet', v: projectName || 'Mon album' },
                          { l: 'Photos', v: `${allPhotos.length} photos` },
                          { l: 'Pages', v: `${totalPages} pages` },
                          { l: 'Couverture', v: selectedCover?.name ?? 'Aucune' },
                          { l: 'Titre de l’album', v: coverDestText || destination || '—' },
                          // Show count, but not the price — total appears
                          // on the Shopify checkout page (built from base
                          // bundle + extra pages line item).
                          ...(addedPagesCount > 0 ? [{ l: 'Pages supplémentaires', v: `+${addedPagesCount}` }] : []),
                        ].map(({ l, v }) => (
                          <div key={l} className="flex items-center justify-between">
                            <span className="text-xs" style={{ color: UI.muted }}>{l}</span>
                            <span className="text-xs font-semibold" style={{ color: UI.ink }}>{v}</span>
                          </div>
                        ))}
                      </div>

                      {/* ADMIN-only toggle: print the cover assembly guides
                          (corner cut diagonals + spine ticks) on this regen.
                          Hidden for customers → their covers are always clean. */}
                      {isAdminRegenMode && (
                        <label
                          className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg cursor-pointer select-none"
                          style={{ background: `${COLORS.gold}0E`, border: `1px solid ${COLORS.gold}30` }}
                        >
                          <input
                            type="checkbox"
                            checked={adminCoverGuides}
                            onChange={(e) => setAdminCoverGuides(e.target.checked)}
                            className="w-4 h-4"
                            style={{ accentColor: COLORS.gold }}
                          />
                          <span className="text-xs font-medium" style={{ color: UI.ink }}>
                            Repères de montage sur la couverture (admin)
                          </span>
                        </label>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowValidation(false)}
                          className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all"
                          style={{ border: `1px solid ${COLORS.gold}30`, color: UI.ink }}
                        >
                          Annuler
                        </button>
                        <button
                          disabled={isSubmitting}
                          onClick={async () => {
                            // Reset cancellation state at the start of every
                            // attempt so the user can re-submit after a
                            // previous cancel.
                            submitCancelledRef.current = false;
                            if (submitRedirectTimeoutRef.current) {
                              clearTimeout(submitRedirectTimeoutRef.current);
                              submitRedirectTimeoutRef.current = null;
                            }
                            setIsSubmitting(true);
                            // Keep the work alive while the submit (render +
                            // multi-MB upload, ~15-30s) is in flight. Mobile
                            // browsers throttle and eventually suspend a
                            // backgrounded tab, which silently halts the render
                            // and surfaces to the customer as "it stopped". The
                            // screen Wake Lock blocks device auto-lock and
                            // re-acquires whenever the tab returns to the
                            // foreground; the unload guard fires the browser's
                            // native "Leave site?" prompt on an accidental
                            // close/back-swipe. Both are released in `finally`
                            // (including the multi-album early-return path).
                            const wakeLock = await acquireScreenWakeLock();
                            const removeUnloadGuard = installUnloadGuard();
                            try {
                              // Step 1: persist the validated cover layout + pages.
                              // Build the exact JSON we persist so we can ALSO fingerprint
                              // it (albumSourceHash) for edit-detection on regenerate.
                              const currentCoverCustomizations = {
                                spineConfig,
                                zoneOverrides: coverZoneOverrides,
                                extraZones: coverExtraZones,
                                customTexts: coverCustomTexts,
                                backCoverBgColor,
                                frontCoverBgColor,
                                hideFrontCoverImage,
                                coverDesignTransform,
                                frontCoverImageOverride: frontCoverImageOverride
                                  ? frontCoverImageOverride.url
                                    ? { url: frontCoverImageOverride.url }
                                    : { storage_path: frontCoverImageOverride.storage_path }
                                  : null,
                                // Blank-cover photo + its pan/zoom — MUST be in the
                                // submit snapshot too (they were autosave-only before,
                                // so submitting silently dropped the client's photo).
                                blankCoverImage: blankCoverImage
                                  ? { storage_path: blankCoverImage.storage_path }
                                  : null,
                                blankCoverTransform,
                                backCoverImages: backCoverImages.map((i) => ({ storage_path: i.storage_path, ...(i.full_page ? { full_page: true } : {}) })),
                                coverElements: coverElementsForSave(),
                                templatePhotoFits,
                                validated_at: new Date().toISOString(),
                              };
                              const currentPagesPayload = { pages: withSlotPositions(localPages, slotPositions), unassigned_photos: localUnassigned, intro_elements: introElements, manual_slot_edits: manualSlotEditsRef.current, ...(clientOrderRef.current ? { client_order: clientOrderRef.current } : {}) };
                              const { session: savedSession } = await sessionsApi.upsert(shopifyOrderId!, albumIndex, {
                                status: 'submitted',
                                cover_id: persistedCoverId(selectedCover),
                                cover_destination_text: coverDestText,
                                // ⚠️ A DECISION, NOT A BLANK. He confirmed the
                                // warning and submitted anyway, so record that
                                // he chose no words — otherwise every later
                                // gate that asks « is the title settled? »
                                // (albumScoreboard.titleSettled, the WhatsApp
                                // render gate) reads an empty column as an
                                // unanswered question and asks him again.
                                // Clears itself the moment a title exists.
                                // The PRINT reads the words: a blank prints blank,
                                // whatever the order said (lib/titleSource.ts).
                                title_declined: !coverDestText.trim(),
                                cover_year_text: coverYearText,
                                cover_customizations: currentCoverCustomizations,
                                pages: currentPagesPayload,
                              });

                              // Edit-detection for THIS album: hash the source we just
                              // saved. If it matches the hash stored at the last PDF
                              // generation AND both PDFs already exist, the printed PDF
                              // is already current — skip the heavy re-render/upload and
                              // just re-finalize (re-sign URLs + keep order status fresh).
                              // ⚠️ THE COMPOSITE, matching what finalize stores. A plain
                              // albumSourceHash would never equal the stored value and
                              // this skip would silently stop working — costing a full
                              // re-render on every submit rather than printing anything
                              // wrong, but costing it every time.
                              const currentAlbumHash = compositeSourceHash({
                                pages: currentPagesPayload,
                                cover_id: persistedCoverId(selectedCover),
                                cover_destination_text: coverDestText ?? null,
                                cover_year_text: coverYearText ?? null,
                                cover_customizations: currentCoverCustomizations,
                                // B5 — a gap-only change is a different book (#1148): without
                                // this the render was skipped as « unchanged ». Same input as
                                // multiAlbumPdf, so the two hashes agree.
                                photo_gap: photoGapHashInput({ photo_gap_mm: photoGap.mm, photo_gap_color: photoGap.color }),
                              });
                              // The stored PDF URLs must point at the CURRENT order id's
                              // storage path. After a draft→real-order promotion the
                              // shopifyOrderId changes, so the old URLs reference the
                              // draft-id path while finalize signs the real-id path —
                              // which has no files ("Failed to sign view URLs"). Treat a
                              // path mismatch as "no PDF yet" so we regenerate + upload to
                              // the correct path instead of skipping.
                              const storedPdfMatchesOrder =
                                !!savedSession?.interior_pdf_url &&
                                savedSession.interior_pdf_url.includes(`/${shopifyOrderId}/`);
                              const currentAlbumHasPdf =
                                !!savedSession?.cover_pdf_url && !!savedSession?.interior_pdf_url && storedPdfMatchesOrder;
                              const skipCurrentRender = currentAlbumHasPdf
                                && !!savedSession?.pdf_source_hash
                                && savedSession.pdf_source_hash === currentAlbumHash;

                              // Multi-album branch (Duo/Trio): if there are still
                              // albums after this one, drop the customer back into
                              // the wizard at the next album's design step. PDFs
                              // for ALL albums are rendered when the LAST album is
                              // confirmed (either here or via AlbumFlowPage), so
                              // we skip the heavy generate/upload work here.
                              const BUNDLE_QTY: Record<string, number> = { Solo: 1, Duo: 2, Trio: 3 };
                              const totalAlbums = BUNDLE_QTY[order?.bundle ?? 'Solo'] ?? 1;
                              // ONLY clients onboarding a multi-album order get dropped
                              // into the wizard at the next album. Admin "Régénérer" on an
                              // already-submitted album must regenerate THIS album in place
                              // (fall through to the render below) — never advance to the
                              // next album or re-show the cover-choice step.
                              if (!isAdminRegenMode && albumIndex < totalAlbums) {
                                setIsSubmitting(false);
                                setShowValidation(false);
                                // Carry the post-submit `next` URL forward so the
                                // checkout redirect at the end of the last album
                                // still knows where to go.
                                const nextParam = nextUrl ? `&next=${encodeURIComponent(nextUrl)}` : '';
                                navigate(`/flow/${shopifyOrderId}?album=${albumIndex + 1}${nextParam}`);
                                return;
                              }

                              // PHASE 2: the in-browser render below now runs ONLY for
                              // admin "Régénérer le PDF" (isAdminRegenMode). For customers
                              // the print PDF renders SERVER-SIDE in the background (fired
                              // by ordersApi.submit → runServerRender, which renders EVERY
                              // album of the order), so we skip the slow client render
                              // entirely and fall straight through to submit + checkout.
                              // ─── THE SERVER BUILDS IT (owner, 2026-10-15) ─────────────
                              // « I need to keep the window open so it can be
                              // regenerated. » The album was just saved above; the
                              // server's robot browser now rebuilds every album of the
                              // order (+ the 3D pages) — it starts within a minute and
                              // nobody has to wait on this tab. The browser render
                              // below is only the fallback when the server refuses.
                              let builtOnServer = false;
                              if (isAdminRegenMode) {
                                setPdfProgress({ stage: 'cover', current: 0, total: 1, message: 'Envoi au serveur...' });
                                let quickOnServer = false;
                                setRegenClickAt(Date.now());
                                setLocalQuick(null);
                                try {
                                  const rg = await withRetry(() => ordersApi.pdfRegenerate(shopifyOrderId!, { guides: adminCoverGuides, album: albumIndex }));
                                  builtOnServer = true;
                                  // A warm runner took the quick 3D job (2026-10-21): it draws in
                                  // seconds on the server — nothing to keep open here.
                                  quickOnServer = !!rg?.quick?.runner && (rg.quick.albums ?? []).includes(albumIndex);
                                } catch (e) {
                                  console.warn('[Submit] server regenerate refused — rendering in this tab', e);
                                }
                                setRegenOnServer(builtOnServer);
                                // ⚡ THE 3D PREVIEW IN SECONDS (owner, 2026-10-18). The
                                // server rebuilds the print (minutes); this tab redraws
                                // only the changed pages for the preview now. Detached:
                                // it survives « Retour à la commande » (same app), never
                                // blocks the submit, and any failure leaves the preview
                                // to the server exactly as before.
                                if (builtOnServer && quickOnServer) {
                                  setQuickPreview('done');
                                } else if (builtOnServer) {
                                  // No runner answered: the redraw of 2026-10-18, in this tab.
                                  setQuickPreview('running');
                                  setLocalQuick({ stage: 'load', done: 0, total: 0 });
                                  // Lazy, like the preview raster in multiAlbumPdf: out of the editor bundle.
                                  void import('@/lib/quickPreview')
                                    .then(({ runQuickPreview }) => runQuickPreview({
                                      shopifyOrderId: shopifyOrderId!, albumIndex,
                                      onProgress: (p) => setLocalQuick({ stage: p.stage, done: p.done, total: p.total }),
                                    }))
                                    .then((r) => { setQuickPreview(r.ok ? 'done' : 'skipped'); setLocalQuick({ stage: 'done', done: 1, total: 1, ok: r.ok }); })
                                    .catch(() => { setQuickPreview('skipped'); setLocalQuick({ stage: 'done', done: 0, total: 0, ok: false }); });
                                }
                              }
                              if (isAdminRegenMode && !builtOnServer) {
                              // ── COUPONS (plan 2026-10-28) — this IS a print render
                              // (the server refused the job), so it is the one place
                              // in this tab that may ask for the order's coupons
                              // (GET print-data issues the codes). Same steps as the
                              // server's render (pages/internal/ServerRenderProbe):
                              // plan → each album draws its leaf → finish. Switch off
                              // (or any failure) → no plan → every line below runs
                              // exactly as before. Never for a preview.
                              let couponPlan: OrderCouponPlan | null = null;
                              const couponOutcomes = new Map<number, AlbumCouponOutcome>();
                              if (couponsEnabledRef.current) {
                                try {
                                  const { planOrderCoupons, fetchCouponPrintData } = await import('@/lib/couponPrint');
                                  couponPlan = await planOrderCoupons({
                                    fetchData: () => fetchCouponPrintData(shopifyOrderId!),
                                    getSession: (i) => sessionsApi.get(shopifyOrderId!, i).then((r) => r.session ?? null),
                                    albumIndexes: Array.from({ length: totalAlbums }, (_, i) => i + 1),
                                    introUrl: introOutroUrls.intro,
                                    outroUrl: introOutroUrls.outro,
                                  });
                                } catch (e) {
                                  console.warn('[coupons] plan failed — no coupon in this render:', (e as Error)?.message);
                                  couponPlan = null;
                                }
                              }
                              // THIS album's leaf, when it is the one carrying them: its
                              // interior hash then includes the coupons (multiAlbumPdf).
                              const ownCoupons = couponPlan?.couponsFor(albumIndex) ?? null;
                              const ownCouponHash = ownCoupons ? (couponPlan?.hashFor(albumIndex) ?? null) : null;
                              const ownHashWithCoupons = ownCouponHash
                                ? compositeSourceHash({
                                    pages: currentPagesPayload,
                                    cover_id: persistedCoverId(selectedCover),
                                    cover_destination_text: coverDestText ?? null,
                                    cover_year_text: coverYearText ?? null,
                                    cover_customizations: currentCoverCustomizations,
                                    photo_gap: photoGapHashInput({ photo_gap_mm: photoGap.mm, photo_gap_color: photoGap.color }),
                                    coupons: ownCouponHash,
                                  })
                                : currentAlbumHash;
                              const skipOwnRender = ownCouponHash
                                ? currentAlbumHasPdf && savedSession?.pdf_source_hash === ownHashWithCoupons
                                : skipCurrentRender;
                              const ownCouponRun: { pages: number[] | null; locked: boolean } = { pages: null, locked: false };
                              // Steps 2–3: render + upload the PDFs. Skipped when
                              // this album is unchanged since its last generation
                              // (the printed files are already current) — only the
                              // re-finalize below runs, re-signing the existing
                              // storage objects. This is what stops a Trio from
                              // re-rendering all 3 albums when only one was edited.
                              if (skipOwnRender) {
                                console.info(`[Submit] album ${albumIndex} unchanged since last PDF — skipping re-render`);
                              } else {
                              // Step 2: render the PDFs in the browser at full
                              // quality. Server-side rendering on Supabase Edge
                              // Functions kept hitting the per-invocation
                              // compute cap; the browser has no such limit and
                              // already has the photos cached, so this is the
                              // right place to do the heavy work.
                              setPdfProgress({ stage: 'cover', current: 0, total: 1, message: 'Préparation...' });

                              // Re-sign photo URLs so they're guaranteed fresh
                              // (the editor's URLs are 1h TTL and may have
                              // expired during a long edit session).
                              let freshPhotos = allPhotos;
                              try {
                                if (session?.id) {
                                  const r = await photosApi.list(session.id);
                                  if (r.photos?.length) freshPhotos = r.photos as PhotoWithUrl[];
                                }
                              } catch (e) {
                                console.warn('[Submit] photo refresh failed, using cached URLs:', e);
                              }

                              // Sign the cover image URL ourselves — many
                              // covers don't have a thumbnail_url filled in,
                              // so the generator needs a direct signed URL to
                              // the storage_path (PNG/JPG/PDF).
                              let coverImageUrl: string | null = null;
                              if (selectedCover?.storage_path) {
                                try {
                                  const { data } = await supabase.storage
                                    .from('covers')
                                    .createSignedUrl(selectedCover.storage_path, 3600);
                                  coverImageUrl = data?.signedUrl ?? null;
                                } catch (e) {
                                  console.warn('[Submit] cover sign failed:', e);
                                }
                              }

                              const blobs = await generateAlbumPdfs({
                                // Merge the live per-slot pan/zoom state into the
                                // pages before rendering. The customer's zoom +
                                // drag live in the flat `slotPositions` state, NOT
                                // in `localPages` (which only carries load-time
                                // slot_positions). The save-to-DB path already does
                                // this merge; the PDF path must too, or the printed
                                // album ignores every zoom/crop the customer made
                                // in the editor. (Render-parity: PDF reads
                                // slot_positions[`s${si}`].scale / x / y.)
                                pages: withSlotPositions(localPages, slotPositions),
                                // B5 — the gap between photos. Missing here, the
                                // admin « Régénérer » (this path) printed the album
                                // with NO gap while the editor showed one (#1148,
                                // 2026-10-08). multiAlbumPdf passes the same two.
                                photoGapMm: photoGap.mm,
                                photoGapColor: photoGap.color,
                                photos: freshPhotos.map((p) => ({
                                  id: p.id,
                                  storage_path: p.storage_path,
                                  // P0: embed the full-res ORIGINAL in the printed PDF,
                                  // never the small editor thumbnail. signed_url is the
                                  // display tier (thumbnail ?? preview ?? original); the
                                  // PDF must source from original_signed_url.
                                  signed_url: pdfPhotoSourceUrl(p),
                                  width: p.width,
                                  height: p.height,
                                  face_data: p.face_data,
                                  // CRITICAL — without these the PDF's decideFitMode
                                  // can't see if a photo is a group shot, so it
                                  // defaults to cover and crops the image instead of
                                  // letterboxing it like the editor does.
                                  face_count: (p as { face_count?: number | null }).face_count ?? null,
                                  face_spread: (p as { face_spread?: number | null }).face_spread ?? null,
                                  aspect_ratio: (p as { aspect_ratio?: 'landscape' | 'portrait' | 'square' | null }).aspect_ratio ?? null,
                                })),
                                cover: selectedCover ? {
                                  id: selectedCover.id,
                                  storage_path: selectedCover.storage_path,
                                  thumbnail_url: selectedCover.thumbnail_url,
                                  design_url: selectedCover.design_url,
                                  dominant_color: selectedCover.dominant_color,
                                  width_px: selectedCover.width_px,
                                  text_zones: selectedCover.text_zones,
                                } : null,
                                coverImageUrl,
                                destinationText: coverDestText,
                                yearText: coverYearText,
                                customTexts: coverCustomTexts,
                                zoneOverrides: coverZoneOverrides,
                                extraZones: coverExtraZones,
                                spineConfig,
                                backCoverBgColor,
                                frontCoverBgColor,
                                hideFrontCoverImage,
                                coverDesignTransform,
                                frontCoverImageOverrideUrl,
                                // Client's own front-cover image on a blank
                                // cover, drawn cover-fit behind the text zones.
                                blankCoverImageUrl,
                                blankCoverTransform,
                                // PDF generator just needs URL strings; the
                                // signed URLs were freshened on load and on
                                // every add. Stale entries with no signed_url
                                // (e.g. legacy paths from before this code)
                                // are dropped — they'd have failed to render
                                // anyway.
                                // FULL-PAGE parity (2026-10-20): the flagged photo
                                // fills the back panel and leaves the grid — the
                                // same plan multiAlbumPdf uses (lib/backCoverPlan).
                                // This path used to send it as a 5th grid tile.
                                ...(() => {
                                  const plan = backCoverPlan(
                                    backCoverImages.map((i) => ({ url: i.signed_url ?? null, full: !!i.full_page })),
                                    isBlankBack(selectedCover?.id === '__blank__' ? null : (selectedCover as { back_cover_style?: string | null } | null)),
                                  );
                                  return { backCoverImages: plan.grid, backCoverFullPageImage: plan.fullPage };
                                })(),
                                // Stickers / icons / photos on the cover faces.
                                coverElements: coverElementsForSave(),
                                logoUrl,
                                // A themed book prints no intro page (the
                                // generator checks the pages too).
                                introPageUrl: themedBook ? null : introOutroUrls.intro,
                                outroPageUrl: introOutroUrls.outro,
                                // Client intro-page overlay elements, rendered
                                // over the intro background by drawPageElements.
                                introElements,
                                sessionId: session?.id ?? null,
                                templatesById,
                                // Per-slot photoFit (scale + offset) for
                                // templated pages — same state that drives
                                // the editor + preview. The PDF renderer
                                // applies it inside each slot's clip path
                                // so the printed page matches what the
                                // customer saw in the editor.
                                templatePhotoFits,
                                // Print bleed from admin settings (default 3mm)
                                // → interior pages export at 216×303 with photos
                                // extended past the trim edge for clean cutting.
                                bleedMm,
                                // Marry-label for the bindery (cover bottom margin
                                // + interior first page): "#1074 1/3".
                                orderLabel: order?.shopify_order_name
                                  ? `${order.shopify_order_name} ${albumIndex}/${totalAlbums}`
                                  : undefined,
                                // ADMIN-only assembly guides on the cover wrap.
                                // Off for customers; toggled in admin-regen mode.
                                showAssemblyGuides: isAdminRegenMode && adminCoverGuides,
                                // COUPONS — only when THIS album carries the order's leaf.
                                ...(ownCoupons ? {
                                  coupons: ownCoupons,
                                  onCouponPlan: (pl: { pages: number[] }) => { ownCouponRun.pages = pl.pages; },
                                } : {}),
                              }, setPdfProgress);

                              // Step 3: ask the worker for short-lived signed
                              // upload URLs for cover.pdf + interior.pdf, then
                              // PUT directly to Storage. The worker uses the
                              // service role to sign, which lets unauthenticated
                              // customers (no Supabase JWT) submit — direct
                              // `supabase.storage.upload()` from the browser
                              // hits the bucket's RLS and returns
                              // 'new row violates row-level security policy'.
                              setPdfProgress({ stage: 'done', current: 0, total: 1, message: 'Téléversement...' });
                              // Idempotent: each call just re-signs short-lived
                              // upload URLs, so retrying a throttled-tab network
                              // blip is safe.
                              const signed = await withRetry(() => ordersApi.pdfUploadUrls(shopifyOrderId!, albumIndex));
                              // MANUAL-COVER OVERRIDE (migration 067): when an
                              // operator has replaced this album's cover by
                              // hand, the worker returns cover:null and issues
                              // an interior URL only, so a regeneration can
                              // never overwrite the manual file. Upload just
                              // the interior — layout and interior PDF are
                              // unaffected by the override.
                              ownCouponRun.locked = !signed.interior;
                              if (!signed.cover || !signed.interior) {
                                console.info(`[submit] album ${albumIndex} manual override — cover:${!signed.cover} interior:${!signed.interior}; uploading only what is unlocked`);
                              }
                              await Promise.all([
                                ...(signed.cover
                                  ? [uploadToSignedUrl(signed.cover.upload_url, new File([blobs.cover], 'cover.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD)]
                                  : []),
                                ...(signed.interior
                                  ? [uploadToSignedUrl(signed.interior.upload_url, new File([blobs.interior], 'interior.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD)]
                                  : []),
                              ]);
                              } // end render+upload (skipped when unchanged)

                              // Step 4: tell the worker the PDFs are in place
                              // so it writes the URLs onto the order row and
                              // flips pdf_status to 'ready'. Always runs (even on
                              // skip) to re-sign the URLs + keep order status in
                              // sync; passes the source hash so the next regen can
                              // detect "unchanged" and skip this album too.
                              // Idempotent — re-running rewrites the same row.
                              // COUPONS: the stored hash holds the coupon leaf only if
                              // this PDF really has it (or matched with it and was kept)
                              // — a leaf that could not be drawn is retried next time.
                              const ownCouponsInPdf = !!ownCouponHash
                                && (skipOwnRender || (!ownCouponRun.locked && (ownCouponRun.pages?.length ?? 0) > 0));
                              const finalizeHash = ownCouponsInPdf ? ownHashWithCoupons : currentAlbumHash;
                              await withRetry(() => ordersApi.pdfFinalize(shopifyOrderId!, albumIndex, finalizeHash), {
                                onRetry: (_e, attempt) =>
                                  setPdfProgress({ stage: 'done', current: 0, total: 1, message: `Nouvel essai (${attempt})...` }),
                              });
                              if (ownCoupons) {
                                couponOutcomes.set(albumIndex, {
                                  ok: true,
                                  // null = not rebuilt (already current); a manually
                                  // replaced interior holds no coupon → [].
                                  pages: skipOwnRender ? null : ownCouponRun.locked ? [] : (ownCouponRun.pages ?? []),
                                });
                              }

                              // Step 4b: for Duo/Trio LAST album, also render
                              // PDFs for every PREVIOUS album. Those sessions
                              // were saved during their own confirm step but
                              // never had PDFs generated (the flow defers all
                              // PDF gen until the final album). Loaded fresh
                              // from DB so the rendered output matches whatever
                              // the customer last saved.
                              if (totalAlbums > 1) {
                                for (let i = 1; i < albumIndex; i++) {
                                  const albumCoupons = couponPlan?.couponsFor(i) ?? null;
                                  const run: { pages: number[] | null } = { pages: null };
                                  const ok = await renderAndUploadAlbumPdfs({
                                    shopifyOrderId: shopifyOrderId!,
                                    albumIndex: i,
                                    covers,
                                    logoUrl,
                                    introPageUrl: introOutroUrls.intro,
                                    outroPageUrl: introOutroUrls.outro,
                                    bleedMm,
                                    showAssemblyGuides: isAdminRegenMode && adminCoverGuides,
                                    onProgress: setPdfProgress,
                                    ...(albumCoupons ? {
                                      coupons: albumCoupons,
                                      couponHash: couponPlan?.hashFor(i) ?? null,
                                      onCouponPages: (pages: number[] | null) => { run.pages = pages; },
                                    } : {}),
                                  });
                                  if (couponPlan) couponOutcomes.set(i, { ok, pages: run.pages });
                                }
                                setPdfProgress(null);
                              }
                              // COUPONS — report where they went, or build the separate
                              // coupons.pdf. Never throws; an album never waits on it.
                              if (couponPlan) {
                                const { finishOrderCoupons } = await import('@/lib/couponPrint');
                                const done = await finishOrderCoupons({
                                  plan: couponPlan,
                                  shopifyOrderId: shopifyOrderId!,
                                  outcomes: couponOutcomes,
                                  bleedMm,
                                  orderName: order?.shopify_order_name ?? undefined,
                                });
                                console.info('[coupons] result', JSON.stringify(done));
                              }
                              } // end admin-regen-only client render — customers skip to the submit below

                              // Step 5: now mark the order as album-submitted.
                              // Done last so the admin only sees the order in
                              // the submitted state once the PDFs actually exist.
                              // Capture the returned order into component state
                              // so both the auto-redirect AND the success-modal
                              // "Continuer vers le paiement" button can hydrate
                              // the Shopify checkout URL with the recomputed
                              // total_price + persisted extras, not the stale
                              // pre-submit useQuery snapshot.
                              //
                              // SKIPPED in admin-regen mode: the order is
                              // already submitted, the customer already paid
                              // the price computed from page_count, and the
                              // ordersApi.submit endpoint resets
                              // album_submitted_at to NOW which would distort
                              // analytics on customer-side submission timing.
                              let postSubmitOrder: Order | null = null;
                              if (!isAdminRegenMode) {
                                try {
                                  // Idempotent: re-submitting recomputes the same
                                  // total from page_count/extras, so retry through
                                  // a throttled-tab blip rather than failing.
                                  const res = await withRetry(() => ordersApi.submit(shopifyOrderId!, {
                                    page_count: totalPages,
                                    extra_pages: addedPagesCount,
                                    extra_pages_cost: addedPagesCount * pricePerPage,
                                  }));
                                  postSubmitOrder = res?.order ?? null;
                                  if (postSubmitOrder) setSubmittedOrder(postSubmitOrder);
                                } catch (orderErr) {
                                  console.error('[Submit] Order status update failed:', orderErr);
                                }
                              }

                              setSubmitSuccess(true);
                              setPdfProgress(null);

                              // Snapshot the final shipped layout. Combined
                              // with the earlier ai_layout_generated event
                              // this gives us "AI proposed → user shipped"
                              // pairs for future model training.
                              if (session?.id) {
                                aiApi.feedback(session.id, 'layout_submitted', {
                                  page_count: totalPages,
                                  extra_pages: addedPagesCount,
                                  pages: localPages,
                                  unassigned: localUnassigned,
                                });
                              }

                              // Auto-redirect after the success modal — admins
                              // back to the order detail (they're authed),
                              // customers always to /pages/checkout-storiz so
                              // they can see upsells + pick a payment method.
                              // NEVER fall through to /dashboard, /admin, or
                              // the storefront homepage for a non-admin: the
                              // customer flow runs anonymously (URL-pass auth)
                              // and the homepage silently drops the confirmation.
                              submitRedirectTimeoutRef.current = setTimeout(() => {
                                // User closed the submit modal during render
                                // → don't drag them to checkout against their
                                // will. Bail out of the redirect.
                                if (submitCancelledRef.current) return;
                                // Admin regen mode (order already submitted):
                                // bounce back to the order detail page where
                                // PdfGenerationCard will pick up the freshly
                                // written cover/interior URLs. No checkout —
                                // the customer already paid.
                                if (isAdminRegenMode) {
                                  navigate(`/admin/orders/${shopifyOrderId}`);
                                  return;
                                }
                                // Everyone else (customers + admin testing a
                                // fresh Manual/COD order): land on checkout.
                                // Sending admin to /admin from a fresh-order
                                // flow broke the Manual / Duo / Trio testing
                                // journey, so keep this branch for non-regen
                                // admins too.
                                try { sessionStorage.removeItem('storiz_post_submit_next'); } catch { /* ignore */ }
                                window.location.href = getCheckoutRedirect(
                                  nextUrl,
                                  postSubmitOrder ?? order ?? { shopify_order_id: shopifyOrderId ?? null },
                                );
                              }, 1500);
                            } catch (e) {
                              console.error('[Submit] Failed:', e);
                              setPdfProgress(null);
                              setValidationErrors([
                                'Erreur lors de la soumission: ' + ((e as Error)?.message ?? 'inconnue'),
                              ]);
                            } finally {
                              // Stop blocking navigation + let the screen sleep
                              // again the moment the submit settles (success OR
                              // failure OR multi-album early return).
                              removeUnloadGuard();
                              void wakeLock.release();
                              setIsSubmitting(false);
                            }
                          }}
                          className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2"
                          style={{ background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`, color: COLORS.white, opacity: isSubmitting ? 0.7 : 1 }}
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                              {pdfProgress ? pdfProgress.message : (isAdminRegenMode ? 'Régénération...' : 'Envoi...')}
                            </>
                          ) : (
                            isAdminRegenMode ? 'Régénérer le PDF' : 'Confirmer & soumettre'
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ MAIN CONTENT ═══════════════ */}
      <div className="flex-1 flex overflow-hidden">
        {!m2 && (<>
        {/* ────── Left Sidebar ────── */}
        <aside
          className="hidden md:flex md:w-64 flex-col shrink-0"
          style={{
            background: UI.surface,
            borderRight: `1px solid ${COLORS.gold}15`,
          }}
        >
          {/* Sidebar tabs */}
          <div
            className="flex shrink-0"
            style={{ borderBottom: `1px solid ${COLORS.gold}15` }}
          >
            {(
              [
                { key: 'images', icon: ImageIcon, label: 'Images' },
                { key: 'layouts', icon: LayoutIcon, label: 'Mises en page' },
                { key: 'themes', icon: ThemeIcon, label: 'Themes' },
                { key: 'elements', icon: ImageIcon, label: 'Modèles' },
              ] as const
            ).map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                className="flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-all relative"
                style={{
                  color: sidebarTab === key ? COLORS.gold : UI.muted,
                }}
                onClick={() => setSidebarTab(key)}
              >
                <Icon active={sidebarTab === key} />
                <span>{label}</span>
                {sidebarTab === key && (
                  <div
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                    style={{ background: COLORS.gold }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === 'images' && (
              <ImagesSidebar
                hiddenDuplicates={hiddenDuplicates}
                onRestoredDuplicate={refetchEditorPhotos}
                allPhotos={photosSortedByDate}
                placedPhotoIds={placedPhotoIds}
                unplacedCount={unplacedPhotos.length}
                draggingPhotoId={draggingPhotoId}
                onDragStart={setDraggingPhotoId}
                onDragEnd={handleSidebarDragEnd}
                onUploadMore={openAddPhotos}
                onDeletePhoto={handleDeletePhoto}
                onRotatePhoto={handleRotatePhoto}
                rotatingPhotoId={rotatingPhotoId}
                onClearAll={handleClearAllPlaced}
                onDeleteAll={handleDeleteAllPhotos}
                deletingAll={bulkDeleting}
                onAutoOrientAll={handleAutoOrientAll}
                autoOrientProgress={autoOrientProgress}
                isAdmin={isAdmin}
                onNormalizeAll={handleNormalizeAllOrientations}
                normalizeProgress={normalizeProgress}
                visionProgress={visionProgress}
                onTouchDragStart={beginTouchDrag}
                useUploadOrder={useUploadOrder}
                onToggleUploadOrder={handleToggleUploadOrder}
                // DESKTOP pick mode (2026-07-08): clicking an empty photo
                // ELEMENT set photoElementTarget + switched to this tab, but
                // desktop tiles never consumed it (only the mobile drawer did)
                // — so "click the frame, nothing happens". Now a tile click
                // fills the element, exactly like the mobile flow.
                onPickForTarget={photoElementTarget ? (pid) => {
                  fillPhotoElement(photoElementTarget.page, photoElementTarget.elementId, pid);
                  setPhotoElementTarget(null);
                } : undefined}
                onCancelPick={photoElementTarget ? () => setPhotoElementTarget(null) : undefined}
              />
            )}
            {sidebarTab === 'layouts' && (
              <LayoutsSidebar
                selectedPage={
                  selectedPageSide === 'left' ? leftPage : rightPage
                }
                pageCount={localPages.length}
                basePages={basePagesPerAlbum}
                pricePerPage={pricePerPage}
                hasIntroPage={bookHasIntro}
                hasOutroPage={Boolean(introOutroUrls.outro)}
                onPageCountCommit={applyPageCount}
                pageCountDraft={pageCountDraft}
                onPageCountDraftChange={setPageCountDraft}
                onLayoutChange={handleLayoutChange}
                onApplyToAll={handleApplyLayoutToAll}
                layoutChoice={layoutChoice}
                onLayoutChoiceChange={handleLayoutChoiceChange}
              />
            )}
            {sidebarTab === 'themes' && (
              <ThemesSidebar
                covers={covers}
                categories={categories}
                selectedCoverId={selectedCover?.id ?? null}
                onSelectCover={handleSelectCover}
              />
            )}
            {sidebarTab === 'elements' && (
              <ElementsSidebar
                selectedPage={selectedPageSide === 'right' ? rightPage : leftPage}
                onOpenModels={openModelsForFocusedPage ? () => { openModelsForFocusedPage(); } : undefined}
                onRemoveTemplate={removeFocusedPageModel}
                albumTheme={albumTheme}
                onAddSticker={(st) => { if (addTileToCoverOnTap({ kind: 'sticker', sticker: st })) return; addStickerToFocusedPage(st); }}
                covers={covers}
                destination={destination}
                onInsertCoverIcon={(c) => {
                  if (!c.design_url) return;
                  if (addTileToCoverOnTap({ kind: 'coverIcon', cover: c })) { setMobileSidebarOpen(null); return; }
                  const targetPage = selectedPageSide === 'right' ? rightPage : leftPage;
                  if (!targetPage) return;
                  const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                  const element: PageElement = {
                    id: newId,
                    type: 'icon',
                    x: 32, y: 32, width: 30, height: 22,
                    // Trimmed icon asset when generated (hugs the artwork box);
                    // falls back to the full design PNG.
                    imageUrl: c.icon_url ?? c.design_url,
                  };
                  setLocalPages((pages) => pages.map((p) =>
                    p.page === targetPage.page
                      ? { ...p, elements: [...(p.elements ?? []), element] }
                      : p
                  ));
                  setSelectedElementId(newId);
                  setSelectedElementIds(new Set([newId]));
                }}
                onInsertIcon={(icon) => {
                  if (addTileToCoverOnTap({ kind: 'adminIcon', icon })) { setMobileSidebarOpen(null); return; }
                  // Add to whichever page is currently in focus (left/right
                  // of the active spread). Falls back to the left page.
                  const targetPage = selectedPageSide === 'right' ? rightPage : leftPage;
                  if (!targetPage) return;
                  const newId = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                  const element: PageElement = {
                    id: newId,
                    type: 'icon',
                    x: 30, y: 30, width: 25, height: 25,
                    svg: icon.svg,
                    iconColor: '#1F2937',
                  };
                  setLocalPages((pages) => pages.map((p) =>
                    p.page === targetPage.page
                      ? { ...p, elements: [...(p.elements ?? []), element] }
                      : p
                  ));
                  setSelectedElementId(newId);
                  setSelectedElementIds(new Set([newId]));
                }}
              />
            )}
          </div>
        </aside>
        </>)}

        {/* ────── Center Canvas ────── */}
        <main
          ref={viewportRef}
          className="flex-1 flex flex-col items-center justify-center relative overflow-hidden"
          style={{ background: UI.desk, cursor: spaceHeld ? 'grab' : undefined, touchAction: m2 ? 'none' : 'pan-y' }}
          data-m2-canvas={m2 ? '' : undefined}
          // « Nouvel éditeur mobile »: the click after a double-tap « fit » is swallowed
          onClickCapture={m2 ? (e) => { if (Date.now() < m2SuppressClickUntilRef.current) { e.stopPropagation(); e.preventDefault(); } } : undefined}
          // (capture: runs before a model window selects itself on press)
          onPointerDownCapture={m2 ? () => { m2SelBeforeTouchRef.current = selectedElementId; } : undefined}
          onMouseDown={handleViewportMouseDown}
          onTouchStart={handleViewportTouchStart}
          onTouchMove={handleViewportTouchMove}
          onTouchEnd={handleViewportTouchEnd}
        >
          {/* Fix #10 (2026-05-28): Régénérer IA — circular premium gold
              button. Visual family matches the "Magie AI" CTA on the
              flow's StepMode (AlbumFlowPage.tsx ~line 1758): same gold
              gradient (BRAND.gold → BRAND.goldDeep), same sparkle icon,
              same hover:scale interaction. Sized 46px on mobile / 52px
              on md+ per spec. Editing mode only; hidden on cover /
              intro / outro views. Absolutely positioned so it doesn't
              disturb the existing `justify-center` flex layout — sits in
              the otherwise-empty band between the header and the page
              preview. Tooltip preserves the full label the banner had. */}
          {/* UI overlap fix (2026-10-18): this row used to be ABSOLUTE over the
              top of the canvas, so the pill + « Modèles » chip sat on the top of
              the spread whenever the spread filled the height. It is now a row
              of its own, in the flow: its measured height is taken out of the
              room the spread is fitted into (pageFitWidthCss), and on tablet /
              desktop the zoom control docks at its right end (it used to float
              over the bottom-right page corner). */}
          {!m2 && editorView === 'pages' && (
            <div
              ref={pagesBarRef}
              data-pages-bar=""
              className="relative shrink-0 w-full flex items-center gap-2 px-3 pt-2 pb-2"
              style={{ zIndex: 25 }}
            >
              {/* equal side columns keep the pill centred; the right one holds the
                  zoom control from tablet width up (never under the pill). */}
              <div className="flex-1 basis-0" aria-hidden />
              <div className="shrink-0 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 max-w-full">
              <button
                onClick={regenerateLayout}
                disabled={isGeneratingLayout || allPhotos.length === 0}
                data-onboarding-target="regenerate-ai"
                aria-label="Régénérer la mise en page avec l'IA"
                title="Régénérer la mise en page avec l'IA"
                className="inline-flex items-center gap-1.5 h-9 md:h-10 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-all disabled:cursor-not-allowed hover:scale-105 active:scale-95 pointer-events-auto"
                style={{
                  color: '#fff',
                  background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDeep})`,
                  boxShadow: isGeneratingLayout
                    ? `0 0 0 4px ${COLORS.gold}22, 0 8px 24px ${COLORS.gold}59, 0 0 0 1px ${COLORS.gold}40 inset`
                    : `0 8px 24px ${COLORS.gold}59, 0 0 0 1px ${COLORS.gold}40 inset`,
                  opacity: allPhotos.length === 0 ? 0.5 : 1,
                }}
              >
                {/* Pill redesign (2026-06-08): ✨ + label so it clearly reads as
                    AI regeneration. While /ai/layout is in flight, swap to a thin
                    spinner + "Régénération…" (storizSpinSlow keyframe from
                    AiLayoutLoader). */}
                {isGeneratingLayout ? (
                  <>
                    <span
                      aria-hidden
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.35)',
                        borderTopColor: '#fff',
                        animation: 'storizSpinSlow 0.9s linear infinite',
                        display: 'inline-block',
                      }}
                    />
                    <span>Régénération…</span>
                  </>
                ) : (
                  <>
                    <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>✨</span>
                    <span>Régénérer</span>
                  </>
                )}
              </button>
              {/* "Utiliser les modèles" — whether the NEXT regeneration offers
                  designed model templates to the AI (category-matched, used in
                  moderation, always fully filled). Persists per album. The
                  first AI-Magic generation is always ON; this chip governs
                  regenerations only. */}
              {allPhotos.length > 0 && (
                <button
                  type="button"
                  onClick={() => setUseTemplates((v) => !v)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors pointer-events-auto',
                    // night: a dark glass pill (light: exactly the old translucent white)
                    !useTemplates && 'bg-[rgba(255,255,255,0.75)] dark:bg-adm-chrome',
                  )}
                  style={{
                    background: useTemplates ? UI.surface : undefined,
                    color: useTemplates ? UI.goldInk : UI.muted,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.10), 0 0 0 1px rgba(201,162,74,0.25)',
                  }}
                  title="Inclure les modèles décorés dans la régénération IA"
                  aria-pressed={useTemplates}
                >
                  <span
                    aria-hidden
                    className="inline-block rounded-full transition-colors"
                    style={{ width: 8, height: 8, background: useTemplates ? COLORS.gold : '#C4BDB0' }}
                  />
                  Modèles {useTemplates ? 'activés' : 'désactivés'}
                </button>
              )}

              {/* Inline error toast — auto-dismissed by the aiError effect.
                  Gold pill so it reads as part of the premium AI experience
                  rather than a destructive red. */}
              {aiError && (
                <div
                  role="alert"
                  className="basis-full w-fit mx-auto pointer-events-auto px-3 py-1.5 rounded-full text-xs font-medium shadow-md bg-[#fffaf0] dark:bg-adm-surface"
                  style={{
                    color: UI.goldDeep,
                    border: `1px solid ${COLORS.gold}`,
                    boxShadow: `0 4px 12px ${COLORS.gold}33`,
                    maxWidth: 320,
                    textAlign: 'center',
                  }}
                >
                  {aiError}
                </div>
              )}
              </div>
              <div className="flex-1 basis-0 flex justify-end min-w-0">
                {!isMobile && zoomHud}
              </div>
            </div>
          )}

          {editorView === 'intro' ? (
            // Editable intro: branding background (incl. bottom QR) + a client
            // element layer using the SAME element components content pages use.
            // Elements clamp out of the bottom QR safe zone. Stays a FREE bookend
            // (never enters localPages → page_count / pricing untouched).
            <M2Zoom on={m2} style={m2ZoomStyle}>
            <IntroEditableView
              url={introOutroUrls.intro}
              elements={introElements}
              selectedElementId={selectedElementId}
              editingElementId={editingElementId}
              // FIX C (2026-06-08): route through selectElement (not the raw
              // setter) so selecting / deselecting an intro element resets the
              // heavy-toolbar gate the same way content pages do; the open
              // effect then re-opens it for intro text.
              onSelectElement={(id) => selectElement(id)}
              onAddElement={handleAddIntroElement}
              onUpdateElement={handleUpdateIntroElement}
              onDeleteElement={handleDeleteIntroElement}
              onDuplicateElement={handleDuplicateIntroElement}
              onSetEditingElement={(id) => setEditingElementId(id)}
              hideTools={m2}
              fitHeight={m2 && m2PageFitPx ? `${Math.round(m2PageFitPx * 297 / 200)}px` : undefined}
            />
            </M2Zoom>
          ) : editorView === 'outro' ? (
            <BookendPreview
              url={introOutroUrls.outro}
              label="Page d'outro"
              caption="Dernière page de l'album, ajoutée automatiquement par Storiz."
            />
          ) : editorView === 'cover' ? (
            <M2Zoom on={m2} style={m2ZoomStyle}>
            <CoverView
              selectedCover={selectedCover}
              spineConfig={spineConfig}
              destination={destination}
              year={year}
              onSpineClick={() => setEditingSpine(true)}
              coverDestText={coverDestText}
              coverYearText={coverYearText}
              coverZoneOverrides={coverZoneOverrides}
              coverExtraZones={coverExtraZones}
              selectedCoverZone={selectedCoverZone}
              onCoverZoneSelect={(key) => {
                setSelectedCoverZone(key);
                // Selecting a different zone (or deselecting) exits any
                // active text-edit mode — only one editor at a time.
                if (key !== editingCoverZone) setEditingCoverZone(null);
              }}
              editingCoverZone={editingCoverZone}
              onCoverZoneEditChange={(key) => {
                // Permanent Fix 2: when a customer finishes editing the
                // mandatory destination/year title and left it blank, restore
                // it (see restoreEmptyCoverTitle). Admins may clear it.
                if (key === null && editingCoverZone !== null && !isAdmin) {
                  const z = selectedCover?.text_zones?.[Number(editingCoverZone)];
                  if (z?.type === 'destination' || z?.type === 'year') {
                    restoreEmptyCoverTitle(z.type);
                  }
                }
                setEditingCoverZone(key);
              }}
              onCoverZoneChange={(i, ovr) => setCoverZoneOverrides((p) => ({ ...p, [i]: ovr }))}
              onCoverZoneDelete={(i) => {
                // "Delete" a template zone = HIDE it (CoverThumbnail + the PDF
                // skip a zone whose override has `hidden: true`). The old code did
                // `delete n[i]`, which only dropped the override and RESET the zone
                // to its default_text — so a custom zone like "MATE" reappeared
                // instead of disappearing. Hide it, and clear its selection.
                setCoverZoneOverrides((p) => ({ ...p, [i]: { ...p[i], hidden: true } }));
                setSelectedCoverZone((cur) => (cur === String(i) ? null : cur));
              }}
              onCoverExtraZoneChange={(i, ez) => setCoverExtraZones((p) => p.map((e, idx) => idx === i ? ez : e))}
              onCoverExtraZoneDelete={(i) => {
                setCoverExtraZones((p) => p.filter((_, idx) => idx !== i));
                // Clear selection so the now-removed zone's toolbar/handles
                // disappear and indices don't point at a shifted zone.
                setSelectedCoverZone((cur) => (cur === `extra-${i}` ? null : cur));
              }}
              onCoverZoneTextChange={(i, newText) => {
                const z = selectedCover?.text_zones?.[i];
                if (!z) return;
                if (z.type === 'destination') editCoverTitle(newText);
                else if (z.type === 'year') setCoverYearText(newText);
                else if (z.type === 'custom') setCoverCustomTexts((prev) => ({ ...prev, [i]: newText }));
              }}
              logoUrl={logoUrl}
              backCoverImages={backCoverImages}
              onBackCoverFilesAdd={handleBackCoverFilesAdd}
              onBackCoverImageDelete={handleBackCoverImageDelete}
              onBackCoverImageFullPage={handleBackCoverImageFullPage}
              onBackCoverImagesClear={handleBackCoverImagesClear}
              backCoverFileRef={backCoverFileRef}
              backCoverBgColor={backCoverBgColor}
              frontCoverBgColor={frontCoverBgColor}
              hideFrontCoverImage={hideFrontCoverImage}
              onHideFrontCoverImageChange={setHideFrontCoverImage}
              coverDesignTransform={coverDesignTransform}
              onCoverDesignTransformChange={setCoverDesignTransform}
              coverDesignSelected={coverDesignSelected}
              onCoverDesignSelectedChange={setCoverDesignSelected}
              onAllCoverBgColorChange={handleAllCoverBgColorChange}
              isAdmin={isAdmin}
              frontCoverImageOverrideUrl={frontCoverImageOverrideUrl}
              hasCoverIconOverride={!!frontCoverImageOverride}
              onUploadCoverIcon={handleCoverIconUpload}
              onRestoreCoverIcon={handleRestoreCoverIcon}
              coverIconFileRef={coverIconFileRef}
              blankCoverTransform={blankCoverTransform}
              onBlankCoverTransformChange={setBlankCoverTransform}
              blankCoverImageUrl={blankCoverImageUrl}
              onUploadBlankCoverImage={handleBlankCoverUpload}
              onRemoveBlankCoverImage={handleRemoveBlankCoverImage}
              blankCoverFileRef={blankCoverFileRef}
              onOpenIconPicker={() => setShowIconPicker(true)}
              m2={m2}
              viewOnly={m2Locked}
              m2Face={m2 && !m2Double ? m2CoverFace : null}
              coverElements={coverElements}
              canEditCoverElements={canEditCoverElements}
              selectedCoverElId={selectedCoverElId}
              onSelectCoverEl={(id) => {
                setSelectedCoverElId(id);
                if (id) { setSelectedCoverZone(null); setEditingCoverZone(null); setCoverDesignSelected(false); }
              }}
              onUpdateCoverEl={updateCoverElement}
              onDeleteCoverEl={deleteCoverElement}
              onDuplicateCoverEl={duplicateCoverElement}
              onLayerCoverEl={layerCoverElement}
              onMoveCoverElToFace={moveCoverElementToFace}
              coverPhotoUrl={(pid) => photoUrlMap.get(pid) ?? null}
              onCoverPhotoDrop={(pid, face, at) => addPhotoToCover(pid, face, at)}
              onOpenCoverPhotoPicker={() => setCoverPhotoPickerOpen(true)}
              onOpenCoverStickers={() => {
                setSidebarTab('elements');
                if (m2 || (typeof window !== 'undefined' && window.innerWidth < 768)) { setM2Sheet(null); setMobileSidebarOpen('elements'); }
              }}
              spineSettings={coverSpineSettings}
              coverPrintSettings={coverPrintSettings}
              onAddCoverText={addTextToCover}
            />
            </M2Zoom>
          ) : null}
          {coverPhotoPickerOpen && canEditCoverElements && (
            <CoverPhotoPickerModal
              photos={allPhotos}
              onPick={(pid) => addPhotoToCover(pid, 'back')}
              onClose={() => setCoverPhotoPickerOpen(false)}
            />
          )}

          {/* ═══════════ MOBILE cover trigger (rev 2026-06-30) ═══════════
              Desktop opens the cover Modal from the right sidebar
              (`hidden lg:flex`, ~line 8042), which mobile never sees — so
              on phones add a compact floating pill on the cover canvas that
              opens the SAME `coverModalOpen` Modal. Top-RIGHT so it clears
              the existing mobile floating controls (those sit bottom-/top-
              LEFT at `left-3`). Notch-safe via safe-area insets. lg:hidden
              keeps the desktop sidebar block untouched. */}
          {!m2 && editorView === 'cover' && (
            <button
              type="button"
              onClick={() => setCoverModalOpen(true)}
              aria-label={selectedCover ? 'Changer la couverture' : 'Choisir la couverture'}
              className="absolute z-30 lg:hidden flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full active:scale-95 transition-transform"
              style={{
                top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                right: 'calc(env(safe-area-inset-right, 0px) + 0.75rem)',
                background: UI.surface,
                border: `0.5px solid ${COLORS.gold}`,
                boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
              }}
            >
              {selectedCover ? (
                <span
                  className="w-7 h-9 shrink-0 rounded-md overflow-hidden flex items-center justify-center"
                  style={{ border: `1px solid ${COLORS.gold}30`, background: selectedCover.dominant_color ?? COLORS.beige }}
                >
                  {pickerThumbUrl(selectedCover.thumbnail_url ?? selectedCover.design_url) ? (
                    <img
                      src={pickerThumbUrl(selectedCover.thumbnail_url ?? selectedCover.design_url) ?? undefined}
                      alt=""
                      aria-hidden="true"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                </span>
              ) : (
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${COLORS.gold}14` }}
                >
                  <svg className="w-4 h-4" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16v12H4V6z" />
                  </svg>
                </span>
              )}
              <span className="text-[13px] font-medium whitespace-nowrap" style={{ color: selectedCover ? UI.ink : COLORS.gold }}>
                {selectedCover ? 'Changer' : 'Choisir la couverture'}
              </span>
            </button>
          )}

          {/* ISSUE 9b (2026-07-05) — from the COVER, customers had no visible
              way to reach the pages (arrows are pages-view only). A subtle
              edge chip at the right mid-height jumps to spread 1; positioned
              at the extreme edge so it never covers the cover design. */}
          {!m2 && editorView === 'cover' && (
            <button
              onClick={() => goToSpread(0)}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-full transition-all active:scale-95"
              style={{
                background: UI.surface,
                border: `1.5px solid ${COLORS.gold}70`,
                boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                color: COLORS.gold,
              }}
              aria-label="Voir les pages"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
              <span className="text-[8px] font-semibold" style={{ color: UI.goldInk }}>Pages</span>
            </button>
          )}

          {editorView !== 'cover' && editorView !== 'intro' && editorView !== 'outro' ? (
            <>
              {/* Zoom HUD — docked in the pages bar from tablet width up (see
                  `zoomHud`); phones keep it floating bottom-right. */}
              {isMobile && !m2 && zoomHud}

              {!m2 && (<>
              {/* Navigation arrows */}
              <button
                onClick={prevSpread}
                disabled={currentSpread === 0}
                // NAV visibility (2026-07-04): the ghosted gold-on-beige circle was
                // nearly invisible. Solid white + border + shadow, 44px target.
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
                style={{
                  background: UI.surface,
                  border: `1.5px solid ${currentSpread === 0 ? COLORS.gold + '25' : COLORS.gold + '70'}`,
                  boxShadow: currentSpread === 0 ? 'none' : '0 2px 10px rgba(0,0,0,0.12)',
                  color:
                    currentSpread === 0 ? withAlpha(UI.muted, '40') : COLORS.gold,
                  cursor: currentSpread === 0 ? 'not-allowed' : 'pointer',
                }}
                aria-label="Page précédente"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={nextSpread}
                disabled={currentSpread >= totalSpreads - 1}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
                style={{
                  background: UI.surface,
                  border: `1.5px solid ${currentSpread >= totalSpreads - 1 ? COLORS.gold + '25' : COLORS.gold + '70'}`,
                  boxShadow: currentSpread >= totalSpreads - 1 ? 'none' : '0 2px 10px rgba(0,0,0,0.12)',
                  color:
                    currentSpread >= totalSpreads - 1
                      ? withAlpha(UI.muted, '40')
                      : COLORS.gold,
                  cursor:
                    currentSpread >= totalSpreads - 1
                      ? 'not-allowed'
                      : 'pointer',
                }}
                aria-label="Page suivante"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              </>)}

              {/* The open book spread */}
              {/* Detect if any slot on left/right page is in pan (edit) mode,
                  so we can lift overflow:hidden to let the ghost image show outside the frame */}
              {(() => {
                const leftHasPan = leftPage && panModeKey?.startsWith(`p${leftPage.page}-`);
                const rightHasPan = rightPage && panModeKey?.startsWith(`p${rightPage.page}-`);
                // Mobile hold-to-delete is armed only on empty pages (see
                // makePageHoldHandlers). A page is empty when no slot holds a photo.
                const leftIsEmpty = !!leftPage && (leftPage.photos?.filter(Boolean).length ?? 0) === 0;
                const rightIsEmpty = !!rightPage && (rightPage.photos?.filter(Boolean).length ?? 0) === 0;
                const leftLifted = liftedPageNumber != null && leftPage?.page === liftedPageNumber;
                const rightLifted = liftedPageNumber != null && rightPage?.page === liftedPageNumber;
                // A themed book's opening spread: S1 alone on the right, the
                // inside of the cover on the left — nothing to edit there.
                // (Never true in a plain book: its left page always exists.)
                const leftIsInsideCover = !leftPage && !!rightPage;
                return (
              <div
                data-spread="true"
                data-m2-zoomable={m2 ? '' : undefined}
                className={cn('flex', m2 ? 'max-w-none' : 'max-w-[95%] md:max-w-[88%]')}
                style={{
                  ...(m2 ? { ['--m2-zoom' as string]: zoom } : {}),
                  // locked album (new layout): shown, swipe/zoom still work on
                  // the canvas, nothing on the pages reacts
                  ...(m2Locked ? { pointerEvents: 'none' as const } : {}),
                  perspective: '1200px',
                  transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: zoomAnim ? 'transform 320ms ease' : 'transform 60ms linear',
                  willChange: 'transform',
                }}
                // D78 (2026-05-21): clicking anywhere on the spread that
                // ISN'T a PageElement deselects so the floating toolbar
                // disappears. PageElementOverlay calls stopPropagation in
                // its own mouseDown, so this handler only fires for the
                // cream margin, the page background, and the photo slots —
                // all "outside the text" from the customer's perspective.
                // Also called by the spread-navigation effect below when
                // the customer pages away from the selected element.
                onMouseDown={() => selectElement(null)}
              >
                {/* Left page */}
                <div
                  key={`L-${aiRevealKey}`}
                  // Night mode (2026-10-03): the page is the album — a light
                  // island, never tinted (index.css « ADMIN NIGHT MODE »).
                  data-adm-media=""
                  {...makePageHoldHandlers(leftPage?.page, leftIsEmpty)}
                  className={cn(
                    'rounded-l-sm transition-all duration-200 relative',
                    !leftHasPan && !leftLifted && 'overflow-hidden',
                    leftLifted && 'storiz-page-lift',
                    leftPage && pendingPagesHint.has(leftPage.page) && 'storiz-page-pulse',
                  )}
                  style={{
                    ...(m2HidePage('left') ? { display: 'none' } : {}),
                    width: pageFitWidthCss,
                    aspectRatio: '200 / 297',
                    background: COLORS.white,
                    // the page's own ink in BOTH modes (light: inherited from the root)
                    color: COLORS.dark,
                    boxShadow:
                      '-4px 2px 16px rgba(0,0,0,0.08), -1px 0 4px rgba(0,0,0,0.04)',
                    outline:
                      fixMode && leftPage && problemPageSet.has(leftPage.page)
                        ? `2px dashed ${COLORS.danger}`
                        : selectedPageSide === 'left' && !leftIsInsideCover
                        ? `2px solid ${COLORS.gold}`
                        : 'none',
                    outlineOffset: -2,
                    // Gold-cascade reveal — keyed off aiRevealKey so the
                    // animation only plays on a fresh regen, never on
                    // unrelated re-renders. 0ms delay on left, 60ms on right.
                    animation: aiRevealKey > 0 ? 'storizAiReveal 520ms cubic-bezier(0.22, 1, 0.36, 1) both' : undefined,
                    animationDelay: '0ms',
                    // D78 (2026-05-21): inline-size container so text
                    // elements inside the page can use `cqi` units and
                    // scale with the page width. Without this the same
                    // fontSize=60 looks tiny on 700-px desktop pages and
                    // huge on 157-px mobile pages — failing render
                    // parity with the PDF (which uses editorRefW=400 as
                    // its scaling reference).
                    containerType: 'inline-size',
                  }}
                  onClick={() => {
                    if (leftIsInsideCover) return;
                    setSelectedPageSide('left');
                    setPageTapCount((c) => c + 1); // S4 smart-hint signal
                    // Tap → satisfy the hint for this page so it stops pulsing.
                    if (leftPage && pendingPagesHint.has(leftPage.page)) {
                      setPendingPagesHint((s) => {
                        const next = new Set(s);
                        next.delete(leftPage.page);
                        return next;
                      });
                    }
                  }}
                >
                  {leftPage && !isOutroPage(leftPage) && pendingPagesHint.has(leftPage.page) && (leftPage.photos?.length ?? 0) === 0 && (leftPage.elements?.length ?? 0) === 0 && (
                    <div className="absolute inset-x-0 top-2 mx-auto w-fit z-20 pointer-events-none px-2.5 py-1 rounded-full text-[10px] font-semibold shadow"
                         style={{ background: COLORS.gold, color: COLORS.white }}>
                      Touchez pour ajouter des photos
                    </div>
                  )}
                  {leftPage && isOutroPage(leftPage) ? (
                    // « PAGE DE FIN » — the brand outro image, read-only (lib/outroPage.ts).
                    <OutroPageView
                      url={introOutroUrls.outro}
                      pageLabel={`${printPageOf(leftPage.page, bookHasIntro)}`}
                      onOpenPageMenu={() => openPageMenu(leftPage.page)}
                    />
                  ) : leftPage ? (
                    <PageView
                      photoGap={bookGap}
                      page={leftPage}
                      photoUrlMap={photoUrlMap}
                      photoFilenameMap={photoFilenameMap}
                      photoMetaMap={photoMetaMap}
                      pageLabel={`${printPageOf(leftPage.page, bookHasIntro)}`}
                      selectedSlotKey={selectedSlotKey}
                      onSelectSlot={handleSelectSlot}
                      panModeKey={panModeKey}
                      onSetPanMode={setPanModeKey}
                      slotPositions={slotPositions}
                      manualSlotEdits={manualSlotEditsRef.current}
                      onSlotPositionChange={(key, pos) => { recordManualSlotEdit(key, pos); setSlotPositions((p) => ({ ...p, [key]: pos })); }}
                      onRemoveFromSlot={handleRemoveFromSlot}
                      onDropToSlot={handleDropToSlot}
                      onDetachSlot={handleDetachSlotToElement}
                      draggingPhotoId={draggingPhotoId}
                      onSwitchToImages={() => { setSidebarTab('images'); setMobileSidebarOpen('images'); }}
                      selectedElementId={selectedElementId}
                      selectedElementIds={selectedElementIds}
                      onSelectElement={selectElement}
                      onUpdateElement={handleUpdateElement}
                      onDeleteElement={handleDeleteElement}
                      onDuplicateElement={() => handleDuplicate()}
                      onShowElementMoreToolbar={() => setElementToolbarOpen(true)}
                      onMoveElementToPage={handleMoveElementToPage}
                      editingElementId={editingElementId}
                      onSetEditingElement={setEditingElementId}
                      pickedPhotoForPlace={pickedPhotoForPlace}
                      onPickedPhotoUsed={() => setPickedPhotoForPlace(null)}
                      onSlotWaiting={(p, s) => setPendingPlaceTarget({ page: p, slot: s })}
                      awaitingTargetSlot={pendingPlaceTarget}
                      templateOverlayUrl={leftPage.template_id ? (templatesById.get(leftPage.template_id)?.overlay_print_png_url ?? null) : null}
                      templateOriginalSvgUrl={leftPage.template_id ? (templatesById.get(leftPage.template_id)?.original_svg_url ?? null) : null}
                      templateSlots={leftPage.template_id ? (templatesById.get(leftPage.template_id)?.slots ?? null) : null}
                      templatePhotoFits={templatePhotoFits}
                      onTemplateFitChange={updateFit}
                      templateEditKey={templateEditKey}
                      onSetTemplateEdit={setTemplateEditKey}
                      onOpenPageMenu={openPageMenu}
                      hideMenuButton={m2}
                      onPickPhotoForElement={handlePickPhotoForElement}
                      onTouchDragStart={beginTouchDrag}
                      windowPhotoOf={windowPhotoOf}
                      onSwapThemedPhoto={handleSwapThemedPhoto}
                      onRemoveThemedPhoto={handleRemoveThemedPhoto}
                    />
                  ) : leftIsInsideCover ? (
                    <InsideCoverPage />
                  ) : (
                    <EmptyPage />
                  )}
                  {leftLifted && leftPage && (
                    <PageLiftBin onDelete={() => requestDeletePages([leftPage.page])} />
                  )}
                </div>

                {/* Spine divider */}
                <div
                  className="w-[3px] self-stretch"
                  style={{
                    ...(m2 && !m2Double ? { display: 'none' } : {}),
                    background: `linear-gradient(to right, ${COLORS.dark}15, ${COLORS.dark}08, ${COLORS.dark}15)`,
                  }}
                />

                {/* Right page */}
                <div
                  key={`R-${aiRevealKey}`}
                  data-adm-media=""
                  {...makePageHoldHandlers(rightPage?.page, rightIsEmpty)}
                  className={cn(
                    'rounded-r-sm transition-all duration-200 relative',
                    !rightHasPan && !rightLifted && 'overflow-hidden',
                    rightLifted && 'storiz-page-lift',
                    rightPage && pendingPagesHint.has(rightPage.page) && 'storiz-page-pulse',
                  )}
                  style={{
                    ...(m2HidePage('right') ? { display: 'none' } : {}),
                    width: pageFitWidthCss,
                    aspectRatio: '200 / 297',
                    background: COLORS.white,
                    color: COLORS.dark,
                    boxShadow:
                      '4px 2px 16px rgba(0,0,0,0.08), 1px 0 4px rgba(0,0,0,0.04)',
                    outline:
                      fixMode && rightPage && problemPageSet.has(rightPage.page)
                        ? `2px dashed ${COLORS.danger}`
                        : selectedPageSide === 'right'
                        ? `2px solid ${COLORS.gold}`
                        : 'none',
                    outlineOffset: -2,
                    // See left-page comment — same render-parity fix.
                    containerType: 'inline-size',
                    // Right page lags 60ms behind the left for the cascade.
                    animation: aiRevealKey > 0 ? 'storizAiReveal 520ms cubic-bezier(0.22, 1, 0.36, 1) both' : undefined,
                    animationDelay: '60ms',
                  } as React.CSSProperties}
                  onClick={() => {
                    setSelectedPageSide('right');
                    setPageTapCount((c) => c + 1); // S4 smart-hint signal
                    if (rightPage && pendingPagesHint.has(rightPage.page)) {
                      setPendingPagesHint((s) => {
                        const next = new Set(s);
                        next.delete(rightPage.page);
                        return next;
                      });
                    }
                  }}
                >
                  {rightPage && !isOutroPage(rightPage) && pendingPagesHint.has(rightPage.page) && (rightPage.photos?.length ?? 0) === 0 && (rightPage.elements?.length ?? 0) === 0 && (
                    <div className="absolute inset-x-0 top-2 mx-auto w-fit z-20 pointer-events-none px-2.5 py-1 rounded-full text-[10px] font-semibold shadow"
                         style={{ background: COLORS.gold, color: COLORS.white }}>
                      Touchez pour ajouter des photos
                    </div>
                  )}
                  {rightPage && isOutroPage(rightPage) ? (
                    // « PAGE DE FIN » — the brand outro image, read-only (lib/outroPage.ts).
                    <OutroPageView
                      url={introOutroUrls.outro}
                      pageLabel={`${printPageOf(rightPage.page, bookHasIntro)}`}
                      onOpenPageMenu={() => openPageMenu(rightPage.page)}
                    />
                  ) : rightPage ? (
                    <PageView
                      photoGap={bookGap}
                      page={rightPage}
                      photoUrlMap={photoUrlMap}
                      photoFilenameMap={photoFilenameMap}
                      photoMetaMap={photoMetaMap}
                      pageLabel={`${printPageOf(rightPage.page, bookHasIntro)}`}
                      selectedSlotKey={selectedSlotKey}
                      onSelectSlot={handleSelectSlot}
                      panModeKey={panModeKey}
                      onSetPanMode={setPanModeKey}
                      slotPositions={slotPositions}
                      manualSlotEdits={manualSlotEditsRef.current}
                      onSlotPositionChange={(key, pos) => { recordManualSlotEdit(key, pos); setSlotPositions((p) => ({ ...p, [key]: pos })); }}
                      onRemoveFromSlot={handleRemoveFromSlot}
                      onDropToSlot={handleDropToSlot}
                      onDetachSlot={handleDetachSlotToElement}
                      draggingPhotoId={draggingPhotoId}
                      onSwitchToImages={() => { setSidebarTab('images'); setMobileSidebarOpen('images'); }}
                      selectedElementId={selectedElementId}
                      selectedElementIds={selectedElementIds}
                      onSelectElement={selectElement}
                      onUpdateElement={handleUpdateElement}
                      onDeleteElement={handleDeleteElement}
                      onDuplicateElement={() => handleDuplicate()}
                      onShowElementMoreToolbar={() => setElementToolbarOpen(true)}
                      onMoveElementToPage={handleMoveElementToPage}
                      editingElementId={editingElementId}
                      onSetEditingElement={setEditingElementId}
                      pickedPhotoForPlace={pickedPhotoForPlace}
                      onPickedPhotoUsed={() => setPickedPhotoForPlace(null)}
                      onSlotWaiting={(p, s) => setPendingPlaceTarget({ page: p, slot: s })}
                      awaitingTargetSlot={pendingPlaceTarget}
                      templateOverlayUrl={rightPage.template_id ? (templatesById.get(rightPage.template_id)?.overlay_print_png_url ?? null) : null}
                      templateOriginalSvgUrl={rightPage.template_id ? (templatesById.get(rightPage.template_id)?.original_svg_url ?? null) : null}
                      templateSlots={rightPage.template_id ? (templatesById.get(rightPage.template_id)?.slots ?? null) : null}
                      templatePhotoFits={templatePhotoFits}
                      onTemplateFitChange={updateFit}
                      templateEditKey={templateEditKey}
                      onSetTemplateEdit={setTemplateEditKey}
                      onOpenPageMenu={openPageMenu}
                      hideMenuButton={m2}
                      onPickPhotoForElement={handlePickPhotoForElement}
                      onTouchDragStart={beginTouchDrag}
                      windowPhotoOf={windowPhotoOf}
                      onSwapThemedPhoto={handleSwapThemedPhoto}
                      onRemoveThemedPhoto={handleRemoveThemedPhoto}
                    />
                  ) : (
                    <EmptyPage />
                  )}
                  {rightLifted && rightPage && (
                    <PageLiftBin onDelete={() => requestDeletePages([rightPage.page])} />
                  )}
                </div>
              </div>
                );
              })()}
            </>
          ) : null}

          {/* ═══════════════ MOBILE TOOLS DRAWER (rev 2026-06-08, G2) ═══════════════
              A gold CIRCLE at the bottom-LEFT of the canvas, mirroring the zoom
              pill (bottom-right) so it sits on the SAME row and never overlaps the
              flipbook. Hamburger when closed (X when open); first open per session
              shows pulse rings. Tapping it opens the tool PILLS UPWARD (stacked
              above the button, staggered, Texte nearest); tap a pill → opens that
              tool + closes; tap the backdrop → closes. Rendered INSIDE <main> as an
              absolute child so it tracks the canvas bottom and clears the CTA +
              photos bars below. lg:hidden (desktop uses the right sidebar). */}
          {!m2 && editorView === 'pages' && (
            <>
              {/* Backdrop — tap outside to close (fixed → covers the viewport). */}
              {toolsStripOpen && (
                <div
                  className="fixed inset-0 z-[28] lg:hidden"
                  onClick={() => setToolsStripOpen(false)}
                  aria-hidden="true"
                />
              )}

              {/* Tool pills — stack UPWARD above the button (Texte nearest). */}
              {toolsStripOpen && (
                <div
                  className="absolute left-3 z-30 lg:hidden flex flex-col-reverse gap-2"
                  style={{ bottom: 'calc(0.75rem + 60px)' }}
                  role="toolbar"
                  aria-label="Outils"
                >
                  {([
                    { key: 'text',     label: 'Texte',        kind: 'element' as const, Icon: TextToolIcon,  hint: true },
                    { key: 'photo',    label: 'Photo',        kind: 'element' as const, Icon: PhotoToolIcon },
                    { key: 'layouts',  label: 'Mise en page', kind: 'sheet'   as const, Icon: LayoutIcon },
                    { key: 'themes',   label: 'Thèmes',       kind: 'sheet'   as const, Icon: ThemeIcon },
                    { key: 'elements', label: 'Modèles',      kind: 'sheet'   as const, Icon: ImageIcon },
                  ]).map(({ key, label, kind, Icon, hint }, i) => {
                    const isActive = (kind === 'element' && activeTool === key)
                      || (kind === 'sheet' && mobileSidebarOpen === key);
                    // Element icons take no props; sheet icons take `active`.
                    const IconC = Icon as React.FC<{ active?: boolean }>;
                    return (
                      <button
                        key={key}
                        type="button"
                        {...(hint ? { 'data-onboarding-target': 'add-text' } : {})}
                        onClick={() => {
                          if (kind === 'element') {
                            const cp = selectedPageSide === 'left' ? leftPage : rightPage;
                            handleToolClick(cp?.page ?? null, key as ActiveTool);
                          } else {
                            setMobileSidebarOpen(mobileSidebarOpen === key ? null : (key as SidebarTab));
                          }
                          setToolsStripOpen(false); // close the drawer after picking a tool
                        }}
                        className="flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full active:scale-95"
                        style={{
                          background: UI.surface,
                          border: `0.5px solid ${isActive ? COLORS.gold : COLORS.gold + '80'}`,
                          boxShadow: '0 4px 14px rgba(0,0,0,0.10)',
                          animation: 'storizPillUp 240ms ease-out both',
                          animationDelay: `${i * 50}ms`,
                        }}
                        aria-label={label}
                      >
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: isActive ? `${COLORS.gold}26` : `${COLORS.gold}14` }}
                        >
                          <IconC active={isActive} />
                        </span>
                        <span className="text-[13px] font-medium whitespace-nowrap" style={{ color: UI.ink }}>
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* The gold circle — bottom-left, on the zoom-pill row. */}
              <div className="absolute left-3 bottom-3 z-30 lg:hidden" style={{ width: 52, height: 52 }}>
                {toolsPulse && !toolsStripOpen && (
                  <>
                    <span className="absolute inset-0 rounded-full pointer-events-none" style={{ background: COLORS.gold, animation: 'storizToolPulse 1.6s ease-out 2' }} />
                    <span className="absolute inset-0 rounded-full pointer-events-none" style={{ background: COLORS.gold, animation: 'storizToolPulse 1.6s ease-out 0.8s 2' }} />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setToolsStripOpen((v) => !v)}
                  aria-label={toolsStripOpen ? 'Fermer les outils' : 'Ouvrir les outils'}
                  aria-expanded={toolsStripOpen}
                  className="absolute inset-0 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDeep})`,
                    color: '#fff',
                    boxShadow: `0 4px 14px ${COLORS.gold}59`,
                  }}
                >
                  {toolsStripOpen ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 6l12 12M6 18L18 6" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 7h16M4 12h16M4 17h16" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Keyframes — pulse rings (expand + fade) + pill slide-UP. */}
              <style>{`
                @keyframes storizToolPulse {
                  0%   { transform: scale(1); opacity: 0.45; }
                  100% { transform: scale(2); opacity: 0; }
                }
                @keyframes storizPillUp {
                  from { opacity: 0; transform: translateY(12px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </>
          )}
        </main>

        {!m2 && (<>
        {/* ────── Right Sidebar: context tools ────── */}
        <aside
          className="hidden lg:flex lg:w-60 flex-col shrink-0"
          style={{
            background: UI.surface,
            borderLeft: `1px solid ${COLORS.gold}15`,
          }}
        >
          {editorView === 'cover' ? coverToolsSidebarEl : (
            <PageToolsSidebar
              photoGap={photoGap}
              onPhotoGapChange={handlePhotoGapChange}
              selectedPage={
                selectedPageSide === 'left' ? leftPage : rightPage
              }
              selectedSide={selectedPageSide}
              placedCount={placedPhotoIds.size}
              unplacedCount={unplacedPhotos.length}
              totalPages={totalPages}
              onLayoutChange={handleLayoutChange}
              activeTool={activeTool}
              onToolClick={(tool) => {
                const currentPage = selectedPageSide === 'left' ? leftPage : rightPage;
                handleToolClick(currentPage?.page ?? null, tool as any);
              }}
              selectedElement={(() => {
                if (!selectedElementId) return null;
                const currentPage = selectedPageSide === 'left' ? leftPage : rightPage;
                return currentPage?.elements?.find((el) => el.id === selectedElementId) ?? null;
              })()}
              onUpdateElement={(updates) => {
                if (!selectedElementId) return;
                const currentPage = selectedPageSide === 'left' ? leftPage : rightPage;
                if (currentPage) handleUpdateElement(currentPage.page, selectedElementId, updates);
              }}
              onDeleteElement={() => {
                if (!selectedElementId) return;
                // `selectedPageSide` was stale in some flows (drag-from-left,
                // drop-on-right left the side pointing at the wrong page) so
                // handleDeleteElement got a pageNumber that didn't match the
                // element's actual page → setLocalPages found no match and
                // the element stayed. findElementById walks every page and
                // returns the real (pageNumber, element) — same lookup the
                // inline trash on the SelectionPill already uses.
                const found = findElementById(selectedElementId);
                if (found) handleDeleteElement(found.pageNumber, found.element.id);
              }}
              onDeletePage={(p) => requestDeletePages([p])}
              // Deletable down to a 1-spread (2-page) floor — empty pages
              // included. Going below the paid page_count is allowed on
              // purpose; price reconciles on the next regen/submit. (Was
              // `> order.page_count`, which wrongly disabled the button for
              // empty pages whenever the album sat at its paid count.)
              canDeletePage={localPages.length > 2}
              onOpenTemplatePicker={() => {
                const currentPage = selectedPageSide === 'left' ? leftPage : rightPage;
                if (!currentPage) return;
                const idx = localPages.findIndex((p) => p.page === currentPage.page);
                if (idx !== -1) setTemplatePickerForPage(idx);
              }}
              onRemoveTemplate={() => {
                const currentPage = selectedPageSide === 'left' ? leftPage : rightPage;
                if (!currentPage) return;
                if (currentPage.theme) { handleRemoveThemeModel(currentPage.page); return; }
                const idx = localPages.findIndex((p) => p.page === currentPage.page);
                if (idx !== -1) removeTemplate(idx);
              }}
            />
          )}
        </aside>
        </>)}
      </div>

      {/* ═══════════════ « NOUVEL ÉDITEUR MOBILE » — bottom strip ═══════════════
          page row · docked sheet · context dock · tab bar (or the locked bar). */}
      {m2BottomEl}

      {/* ═══════════════ MOBILE COMMANDER CTA (Improvement 2) ═══════════════
            Mobile-only `shrink-0` band that sits in the natural flex column
            — between the canvas (above) and the mobile bottom tab bar
            (below). Inline, not fixed, so it CAN'T overlap the page-strip
            carousel that lives below the tab bar. Replaces the cramped
            checkmark icon that used to live in the header.

            Hidden when the validation modal is up (the customer is already
            mid-order) so it doesn't sit behind it.

            Design intent (per ui-ux-pro-max "premium without intrusive"):
            – Slim 44px band, full-width pill inside (HIG tap target).
            – Gold gradient ↔ same family as desktop CTA, brand-consistent.
            – Letter-spacing + uppercase-flavour copy for premium feel.
            – Subtle outer shadow (not overpowering); active:scale-[0.98]
              for tactile feedback. */}
      {!m2 && !showValidation && (
        <div
          className="md:hidden shrink-0 px-3 pt-2 pb-1"
          style={{
            background: UI.surface,
            borderTop: `1px solid ${COLORS.gold}15`,
          }}
        >
          <button
            type="button"
            onClick={handleCommanderClick}
            disabled={isOrderLocked}
            data-onboarding-target="finish-order"
            className="w-full h-11 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`,
              color: COLORS.white,
              boxShadow: `0 4px 14px ${COLORS.gold}45`,
              letterSpacing: '0.04em',
            }}
            aria-label={isAdminRegenMode ? 'Régénérer le PDF' : 'Terminer et commander'}
          >
            <span>{isAdminRegenMode ? 'Régénérer le PDF' : 'Terminer & Commander'}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* ═══════════════ MOBILE BOTTOM BAR — Photos picker ═══════════════
           The 3 sheet tools (Mise en page / Thèmes / Modèles) moved to the
           always-visible left strip (rev 2026-06-08). The bottom keeps only the
           Photos picker — the primary "place your photos" action — front and
           centre in the thumb zone. 44px min-height per Apple HIG. */}
      {!m2 && (
      <div className="flex md:hidden shrink-0 border-t justify-center" style={{ borderColor: `${COLORS.gold}15`, background: UI.surface }}>
        <button
          className="flex items-center justify-center gap-2 py-2.5 px-8 text-[12px] font-medium min-h-11"
          style={{ color: mobileSidebarOpen === 'images' ? COLORS.gold : UI.ink }}
          onClick={() => setMobileSidebarOpen(mobileSidebarOpen === 'images' ? null : 'images')}
          aria-label="Mes photos"
        >
          <ImageIcon active={mobileSidebarOpen === 'images'} />
          <span>Mes photos</span>
        </button>
      </div>
      )}

      {/* ═══════════════ MOBILE PHOTOS — modal gallery popup (G1, 2026-06-08) ══
           A centered/bottom-sheet MODAL (replaces the old inline strip): iPhone-
           Photos-style 3-col square grid, low-opacity "Placée" badge, LONG-PRESS
           → "Supprimer cette photo ?" confirm, "+ Ajouter" + "X" header. Tapping
           a photo fills the targeted empty cell (slot-first) and CLOSES; the
           photo-first arm flow keeps the popup open until a slot is tapped.
           z-40 so "+ Ajouter" (upload modal z-50) layers above it. md:hidden. */}
      {!m2 && mobileSidebarOpen === 'images' && (
        <div className="fixed inset-0 z-40 md:hidden flex items-end sm:items-center justify-center">
          {/* Backdrop — tap to close */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
            onClick={() => { setMobileSidebarOpen(null); setPickedPhotoForPlace(null); setPendingPlaceTarget(null); exitSelectMode(); }}
          />
          {/* Card */}
          <div
            className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[82vh]"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {photosPanelInner}
          </div>

          {/* Long-press delete confirm */}
          {photoDeleteConfirmEl}
        </div>
      )}

      {/* Layouts and Themes still use the full sheet (modal is fine for those) */}
      {!m2 && (mobileSidebarOpen === 'layouts' || mobileSidebarOpen === 'themes' || mobileSidebarOpen === 'elements') && (
        // data-tile-drag-hide: the sheet steps aside while a sticker is lifted (lib/tileDrag).
        <div className="fixed inset-0 z-40 md:hidden" data-tile-drag-hide="">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileSidebarOpen(null)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[60vh] overflow-y-auto shadow-2xl">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2 mb-1" />
            {mobileSidebarOpen === 'layouts' && layoutsSidebarMobileEl}
            {mobileSidebarOpen === 'themes' && (
              <ThemesSidebar
                covers={covers}
                categories={categories}
                selectedCoverId={selectedCover?.id ?? null}
                onSelectCover={(cover) => { handleSelectCover(cover); setMobileSidebarOpen(null); }}
              />
            )}
            {mobileSidebarOpen === 'elements' && elementsSidebarMobileEl}
          </div>
        </div>
      )}

      {/* ═══════════════ BOTTOM CAROUSEL ═══════════════ */}
      {/* Column container — strip on top, custom scrollbar directly below.
          Native scrollbar on the strip is hidden via .storiz-strip-no-native-bar
          (see index.css); HorizontalScrollbar paints a premium gold thumb +
          a faint track and handles drag / track-click to scroll. */}
      {!m2 && pageStripEl}

      {/* Phase 1 — layout-template gallery modal */}
      {/* iOS keyboard-catcher (visually hidden, focusable). Focused synchronously
          when adding text so Safari raises the keyboard; focus then transfers to
          the new element. font-size 16px stops iOS from auto-zooming on focus. */}
      <input
        ref={keyboardCatcherRef}
        aria-hidden="true"
        tabIndex={-1}
        inputMode="text"
        className="pointer-events-none"
        style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: m2 ? 0.01 : 0, fontSize: 16, border: 'none', background: 'transparent', zIndex: m2 ? 1 : -1 }}
        onChange={() => { /* swallow — real input goes to the element */ }}
      />

      {/* BLANK PAGE (2026-07-05) — page-options popover. Opened by a
          double-tap on the page background OR the page's "•••" button. Offers
          the blank-divider toggle + (when templates are on) the model picker. */}
      {pageMenuFor !== null && (() => {
        const pg = localPages.find((p) => p.page === pageMenuFor);
        const pageIndex = localPages.findIndex((p) => p.page === pageMenuFor);
        if (!pg) return null;
        // « PAGE DE FIN » (lib/outroPage.ts): nothing to edit on it — it can be
        // moved one page either way, or deleted (the shared confirm popup).
        if (isOutroPage(pg)) {
          const pinnedFirst = isThemedBook(localPages);
          const canLeft = pageIndex > (pinnedFirst ? 1 : 0);
          const canRight = pageIndex >= 0 && pageIndex < localPages.length - 1 && !(pinnedFirst && pageIndex === 0);
          const row = 'flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-gray-50 active:scale-[0.99] disabled:opacity-40 disabled:pointer-events-none';
          return (
            <div
              className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
              style={{ background: 'rgba(20,16,10,0.45)', backdropFilter: 'blur(3px)' }}
              onClick={() => setPageMenuFor(null)}
            >
              <div
                data-outro-page-menu=""
                className="bg-white rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
                style={{ border: `1px solid ${COLORS.gold}25` }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 pt-4 pb-2">
                  <h3 className="text-sm font-semibold" style={{ color: UI.ink, fontFamily: "'Fraunces', Georgia, serif" }}>
                    Options de la page
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: UI.muted }}>{`Page de fin · page ${printPageOf(pg.page, bookHasIntro)}`}</p>
                </div>
                <div className="px-3 pb-3 flex flex-col gap-1">
                  <div className="grid grid-cols-2 gap-1">
                    <button type="button" className={row} disabled={!canLeft} onClick={() => handleMoveOutroPage(pg.page, -1)}>
                      <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>← Déplacer</span>
                    </button>
                    <button type="button" className={`${row} justify-end`} disabled={!canRight} onClick={() => handleMoveOutroPage(pg.page, 1)}>
                      <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>Déplacer →</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    className={row}
                    disabled={localPages.length <= 2}
                    onClick={() => { setPageMenuFor(null); requestDeletePages([pg.page]); }}
                  >
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}33` }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.danger }} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
                      </svg>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold" style={{ color: UI.danger }}>Supprimer la page</span>
                      <span className="block text-[11px]" style={{ color: UI.muted }}>La page de fin automatique reste à la fin</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            style={{ background: 'rgba(20,16,10,0.45)', backdropFilter: 'blur(3px)' }}
            onClick={() => setPageMenuFor(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
              style={{ border: `1px solid ${COLORS.gold}25` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-sm font-semibold" style={{ color: UI.ink, fontFamily: "'Fraunces', Georgia, serif" }}>
                  Options de la page
                </h3>
                <p className="text-xs mt-0.5" style={{ color: UI.muted }}>{`Page ${pageMenuFor}`}</p>
              </div>
              <div className="px-3 pb-3 flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => {
                    // A model's page: « Retirer le modèle » (back to a grid of its photos) — never a
                    // bare un-blank, which would leave the model's pieces over a photo grid.
                    if (pg.theme) { handleRemoveThemeModel(pageMenuFor); setPageMenuFor(null); }
                    else handleToggleBlank(pageMenuFor);
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-gray-50 active:scale-[0.99]"
                >
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: pg.blank ? `${COLORS.gold}18` : UI.chip, border: `1px solid ${pg.blank ? COLORS.gold + '40' : UI.line}` }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: pg.blank ? UI.goldInk : UI.ink }} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="3" width="14" height="18" rx="2" />
                    </svg>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>
                      {pg.theme ? 'Retirer le modèle' : pg.blank ? 'Rétablir la page' : 'Page blanche'}
                    </span>
                    <span className="block text-[11px]" style={{ color: UI.muted }}>
                      {pg.theme ? 'Revenir à une grille de photos' : pg.blank ? 'Revenir à la mise en page' : 'Page blanche — séparateur ou libre'}
                    </span>
                  </span>
                </button>
                {/* ITEM 8 — blank page colour picker (mirrors the cover's). */}
                {pg.blank && !pg.theme && (
                  <div className="px-3 pt-1 pb-1">
                    <span className="block text-[11px] font-medium mb-1.5" style={{ color: UI.muted }}>Couleur de la page</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['#FFFFFF', '#F5EFE3', '#1A1A1A', '#C9A24A', '#2E4A3F', '#8C5A3C'].map((c) => {
                        const active = (pg.blank_color ?? '#FFFFFF').toUpperCase() === c;
                        return (
                          <button
                            key={c}
                            type="button"
                            aria-label={`Couleur ${c}`}
                            onClick={() => handleSetBlankColor(pageMenuFor, c)}
                            className="w-7 h-7 rounded-full transition-transform active:scale-90 border border-[rgba(0,0,0,0.12)] dark:border-adm-line-strong"
                            style={{ background: c, boxShadow: active ? `0 0 0 2px ${UI.surface}, 0 0 0 4px ${COLORS.gold}` : 'none' }}
                          />
                        );
                      })}
                      {/* Custom colour — native picker behind a + swatch. */}
                      <label
                        className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer relative"
                        style={{ background: UI.surface, border: `1px dashed ${COLORS.gold}80` }}
                        title="Couleur personnalisée"
                        aria-label="Couleur personnalisée"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.ink }} strokeWidth="2" strokeLinecap="round">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        <input
                          type="color"
                          value={pg.blank_color ?? '#FFFFFF'}
                          onChange={(e) => handleSetBlankColor(pageMenuFor, e.target.value.toUpperCase())}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </label>
                    </div>
                  </div>
                )}
                {/* MISE EN PAGE (2026-07-10) — grid-layout picker in the page
                    options popup, mirroring the right-panel picker so the
                    customer can change a page's photo grid straight from the
                    "•••" menu. Only on non-blank pages (a blank/divider page has
                    no grid). Reuses handleLayoutChange, so manual crops are
                    preserved across the switch. Popup stays open: the active
                    border moves + the page updates live behind the overlay. */}
                {!pg.blank && (
                  <div className="px-3 pt-1 pb-1.5">
                    <span className="block text-[11px] font-medium mb-1.5" style={{ color: UI.muted }}>Mise en page</span>
                    <div className="grid grid-cols-4 gap-1.5">
                      {(['1-up', '2-up', '3-up', '4-up'] as const).map((lt) => {
                        const isActive = pg.layout === lt;
                        return (
                          <button
                            key={lt}
                            type="button"
                            aria-label={`Mise en page ${lt}`}
                            className="rounded-md p-1.5 transition-all active:scale-95"
                            style={{
                              border: isActive ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
                              background: isActive ? `${COLORS.gold}10` : 'transparent',
                            }}
                            onClick={() => handleLayoutChange(pageMenuFor, lt)}
                          >
                            <LayoutMiniPreview layout={lt} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(!pg.blank || !!pg.theme) && pageIndex >= 0 && (
                  <button
                    type="button"
                    onClick={() => { setPageMenuFor(null); setTemplatePickerForPage(pageIndex); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-gray-50 active:scale-[0.99]"
                  >
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: UI.chip, border: `1px solid ${UI.line}` }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.ink }} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>Modèles</span>
                      <span className="block text-[11px]" style={{ color: UI.muted }}>{m2 ? 'Une page décorée avec vos photos' : 'Choisir une mise en page design'}</span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* « MODÈLES » (design system v2, 2026-10-14) — ONE picker for both doors (the right
          panel « Outils — Page N » and the « Options de la page » sheet): the album theme's
          models drawn with her own photos. The old Canva templates (all retired) are gone
          from here. */}
      {templatePickerForPage !== null && session?.id && localPages[templatePickerForPage] && (() => {
        const pg = localPages[templatePickerForPage];
        const pagePhotos = pg.photos.filter(Boolean);
        const placedHere = new Set(pagePhotos);
        const placedAnywhere = new Set(localPages.flatMap((p) => p.photos.filter(Boolean)));
        const unplaced = [...localUnassigned.filter((id) => !placedAnywhere.has(id)),
          ...allPhotos.map((p) => p.id).filter((id) => !placedAnywhere.has(id) && !localUnassigned.includes(id))];
        // Photos other GRID pages may lend when a model needs more (never from a themed page — its model needs them).
        const lendable = localPages.filter((p) => p.page !== pg.page && !p.theme && !p.blank && !p.template_id)
          .flatMap((p) => p.photos.filter((id) => id && !placedHere.has(id)).map((id) => ({ id, page: p.page })));
        const side = (pg as LayoutPage & { side?: 'left' | 'right' }).side
          ?? (isLeftPage(pg.page, themedBook) ? 'left' : 'right');
        return (
          <ThemeModelPicker
            sessionId={session.id}
            pageNumber={pg.page}
            pagePhotoIds={pagePhotos}
            unplacedIds={unplaced}
            lendable={lendable}
            usedImages={usedImagesExcept(pg.page)}
            albumPages={localPages.length}
            side={side}
            pageTheme={pg.theme ?? null}
            photoUrl={(id) => photoUrlMap.get(id) ?? null}
            onClose={() => setTemplatePickerForPage(null)}
            onApplied={(page, ids) => {
              applyThemedModel(templatePickerForPage, page, ids);
              setTemplatePickerForPage(null);
            }}
            photosOnPage={(n) => localPages.find((x) => x.page === n)?.photos.filter(Boolean) ?? []}
            onAppliedSpread={(left, right, leftPage, rightPage, ids) => {
              applyThemedSpread(left, right, leftPage, rightPage, ids);
              setTemplatePickerForPage(null);
            }}
          />
        );
      })()}

      {/* A model's notice: a face cut in a window, a photo the model cannot lose. */}
      {themeNotice && (
        <div
          role="status"
          className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[1200] max-w-[92vw] sm:max-w-md px-4 py-2.5 rounded-xl text-[12px] leading-snug shadow-xl"
          style={{ background: '#1F2937', color: '#fff' }}
          onClick={() => setThemeNotice(null)}
          data-theme-notice
        >
          {themeNotice}
        </div>
      )}

      {templateFill && (
        <TemplateFillModal
          template={templateFill.template}
          photos={allPhotos}
          onCancel={() => setTemplateFill(null)}
          onValidate={(photoIds) => {
            applyTemplateWithPhotos(templateFill.pageIndex, templateFill.template, photoIds);
            setTemplateFill(null);
          }}
        />
      )}

    </div>
    </MobileDockContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Cover View ───────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function CoverView({
  selectedCover,
  spineConfig,
  destination,
  year,
  onSpineClick,
  coverDestText,
  coverYearText,
  coverZoneOverrides,
  coverExtraZones,
  selectedCoverZone,
  onCoverZoneSelect,
  editingCoverZone,
  onCoverZoneEditChange,
  onCoverZoneChange,
  onCoverZoneDelete,
  onCoverExtraZoneChange,
  onCoverExtraZoneDelete,
  onCoverZoneTextChange,
  logoUrl,
  backCoverImages,
  onBackCoverFilesAdd,
  onBackCoverImageDelete,
  onBackCoverImageFullPage,
  onBackCoverImagesClear,
  backCoverFileRef,
  backCoverBgColor,
  frontCoverBgColor,
  hideFrontCoverImage,
  onHideFrontCoverImageChange,
  coverDesignTransform,
  onCoverDesignTransformChange,
  coverDesignSelected,
  onCoverDesignSelectedChange,
  onAllCoverBgColorChange,
  isAdmin,
  frontCoverImageOverrideUrl,
  hasCoverIconOverride,
  onUploadCoverIcon,
  onRestoreCoverIcon,
  coverIconFileRef,
  blankCoverImageUrl,
  blankCoverTransform,
  onBlankCoverTransformChange,
  onUploadBlankCoverImage,
  onRemoveBlankCoverImage,
  blankCoverFileRef,
  onOpenIconPicker,
  m2 = false,
  viewOnly = false,
  m2Face = null,
  coverElements = [],
  canEditCoverElements = false,
  selectedCoverElId = null,
  onSelectCoverEl,
  onUpdateCoverEl,
  onDeleteCoverEl,
  onDuplicateCoverEl,
  onLayerCoverEl,
  onMoveCoverElToFace,
  coverPhotoUrl,
  onCoverPhotoDrop,
  onOpenCoverPhotoPicker,
  onOpenCoverStickers,
  spineSettings,
  coverPrintSettings = DEFAULT_PRINT_SETTINGS,
  onAddCoverText,
}: {
  /** COVER TEXT (2026-10-30) — a new text on a face; returns its id (typing starts). */
  onAddCoverText?: (face: CoverFace) => string | null;
  /** The admin's print settings — the faces' safe zone (lib/coverElements.coverSafeZoneMm). */
  coverPrintSettings?: SafeZoneSettings;
  /** COVER ELEMENTS (2026-10-20) — stickers / icons / photos on the faces. */
  coverElements?: CoverElement[];
  /** The team places and edits them; a client sees them, read-only. */
  canEditCoverElements?: boolean;
  selectedCoverElId?: string | null;
  onSelectCoverEl?: (id: string | null) => void;
  onUpdateCoverEl?: (id: string, updates: Partial<CoverElement>) => void;
  onDeleteCoverEl?: (id: string) => void;
  onDuplicateCoverEl?: (id: string) => void;
  onLayerCoverEl?: (id: string, move: LayerMove) => void;
  onMoveCoverElToFace?: (id: string, face: CoverFace) => void;
  /** The editor's display copy of an album photo (never the print original). */
  coverPhotoUrl?: (photoId: string) => string | null;
  /** A photo dragged from « Mes photos » onto a face (desktop drag). */
  onCoverPhotoDrop?: (photoId: string, face: CoverFace, at: { fx: number; fy: number }) => void;
  onOpenCoverPhotoPicker?: () => void;
  onOpenCoverStickers?: () => void;
  spineSettings?: { spineWmm: number; jointGapMm: number };
  selectedCover: Cover | null;
  spineConfig: SpineConfig;
  destination: string;
  year: string;
  onSpineClick: () => void;
  coverDestText: string;
  coverYearText: string;
  coverZoneOverrides: Record<number, TextZoneOverride>;
  coverExtraZones: ExtraTextZone[];
  selectedCoverZone: string | null;
  onCoverZoneSelect: (key: string | null) => void;
  editingCoverZone: string | null;
  onCoverZoneEditChange: (key: string | null) => void;
  onCoverZoneChange: (i: number, ovr: TextZoneOverride) => void;
  onCoverZoneDelete: (i: number) => void;
  onCoverExtraZoneChange: (i: number, ez: ExtraTextZone) => void;
  onCoverExtraZoneDelete: (i: number) => void;
  onCoverZoneTextChange: (i: number, newText: string) => void;
  logoUrl: string | null;
  // BackCoverImage = { storage_path, signed_url } — uploaded to Supabase
  // Storage so they survive a refresh. signed_url is what we show inline.
  backCoverImages: { storage_path: string; signed_url: string | null; full_page?: boolean }[];
  onBackCoverFilesAdd: (files: File[]) => void | Promise<void>;
  onBackCoverImageDelete: (idx: number) => void;
  /** FULL-PAGE back photo (2026-07-15): toggle one image as the full-bleed
   *  back cover (exclusive). */
  onBackCoverImageFullPage: (idx: number, on: boolean) => void;
  onBackCoverImagesClear: () => void;
  backCoverFileRef?: React.RefObject<HTMLInputElement | null>;
  backCoverBgColor: string | null;
  frontCoverBgColor: string | null;
  hideFrontCoverImage: boolean;
  onHideFrontCoverImageChange: (v: boolean) => void;
  coverDesignTransform: { x: number; y: number; scale: number };
  onCoverDesignTransformChange: (t: { x: number; y: number; scale: number }) => void;
  coverDesignSelected: boolean;
  onCoverDesignSelectedChange: (selected: boolean) => void;
  /** Recolors front + spine + back at once. Bound to both cover-overlay dots. */
  onAllCoverBgColorChange: (c: string) => void;
  /** Admin-only custom cover-icon controls. */
  isAdmin: boolean;
  frontCoverImageOverrideUrl: string | null;
  hasCoverIconOverride: boolean;
  onUploadCoverIcon: (file: File) => void;
  onRestoreCoverIcon: () => void;
  coverIconFileRef?: React.RefObject<HTMLInputElement | null>;
  /** Client blank-cover front-image controls (shown only on a "vierge" cover). */
  blankCoverImageUrl: string | null;
  blankCoverTransform: { x: number; y: number; scale: number };
  onBlankCoverTransformChange: (t: { x: number; y: number; scale: number }) => void;
  onUploadBlankCoverImage: (file: File) => void;
  onRemoveBlankCoverImage: () => void;
  blankCoverFileRef?: React.RefObject<HTMLInputElement | null>;
  /** Opens the customer catalogue-icon picker (blank cover only). */
  onOpenIconPicker: () => void;
  /** « Nouvel éditeur mobile »: no control is drawn ON the cover (brushes,
   *  back-cover buttons, labels, the action row) — they live in the
   *  « Couverture » sheet; the spread takes all the room. */
  m2?: boolean;
  /** Locked album: the cover is shown, nothing on it reacts. */
  viewOnly?: boolean;
  /** « Nouvel éditeur mobile », one page at a time: only the front (or the
   *  back) panel, at full size. null = the whole wrap (back · spine · front). */
  m2Face?: 'front' | 'back' | null;
}) {
  // Back-cover background: user-controlled, but defaults to the front cover's
  // dominant color so back/spine/front all match by default.
  const coverBgColor = backCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight;
  const isBlankTheme = selectedCover?.id === '__blank__' || (selectedCover as any)?.back_cover_style === 'blank';
  const imgs = backCoverImages ?? [];
  const hasImages = imgs.length > 0;
  const maxBackImages = isBlankTheme ? 1 : 4;
  const canAddMore = imgs.length < maxBackImages;
  // UI overlap fix (2026-10-18): the spread used to be sized from the window
  // alone (min(75vh, 560px, 100vw/1.55)), ignoring the toolbar above it, the
  // staff theme bar and the side panels — so on a laptop it overflowed the
  // canvas and the « ADMIN » pills ended up on the cover. It is now sized
  // from the room actually left under the toolbar (measured), same caps.
  const spreadBoxRef = useRef<HTMLDivElement | null>(null);
  const spreadBox = useBoxSize(spreadBoxRef);
  const coverSpreadHeight = (() => {
    const vhCap = typeof window !== 'undefined' ? window.innerHeight * 0.75 : 560;
    if (spreadBox.w <= 0 || spreadBox.h <= 0) return `min(75vh, 560px, calc((100vw - 1.5rem) / 1.55))`;
    // back + front are the 200 × 300 mm image frame each at height h (the
    // print's panel — COVER_PANEL_ASPECT); the spine is w-7 / md:w-14.
    const spinePx = typeof window !== 'undefined' && window.innerWidth >= 768 ? 56 : 28;
    const byWidth = m2Face ? spreadBox.w / (200 / 300) : (spreadBox.w - spinePx) / (2 * 200 / 300);
    return `${Math.max(m2 ? 60 : 120, Math.floor(Math.min(560, m2 ? spreadBox.h : vhCap, spreadBox.h, byWidth)))}px`;
  })();

  // COVER ELEMENTS — a photo dragged from « Mes photos » (desktop HTML5 drag,
  // the same 'photoId' contract as the page slots) onto a face; the team only.
  const facePhotoDrop = (face: CoverFace) => canEditCoverElements && onCoverPhotoDrop ? {
    'data-slot-drop-target': `cover:${face}`,
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (!Array.from(e.dataTransfer.types).some((t) => t.toLowerCase() === 'photoid')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      e.currentTarget.classList.add('touch-drop-target-active');
    },
    onDragLeave: (e: React.DragEvent<HTMLElement>) => { e.currentTarget.classList.remove('touch-drop-target-active'); },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      e.currentTarget.classList.remove('touch-drop-target-active');
      const pid = e.dataTransfer.getData('photoId');
      if (!pid) return;
      e.preventDefault();
      e.stopPropagation();
      // The face box (not a child) — the element layer has the same rect.
      const layer = e.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-cover-el-layer="${face}"]`) ?? e.currentTarget;
      const r = layer.getBoundingClientRect();
      onCoverPhotoDrop(pid, face, {
        fx: Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width))),
        fy: Math.max(0, Math.min(1, (e.clientY - r.top) / Math.max(1, r.height))),
      });
    },
  } : {};
  // COVER TEXT (2026-10-30): the text being typed in, and each text's measured
  // words height (% of its face) — the overflow flag and the safe zone read it.
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [textHeights, setTextHeights] = useState<Record<string, number>>({});
  const onMeasureText = useCallback((id: string, h: number) => {
    setTextHeights((prev) => (prev[id] != null && Math.abs(prev[id] - h) < 0.05 ? prev : { ...prev, [id]: h }));
  }, []);
  useEffect(() => { if (!selectedCoverElId) setEditingTextId(null); }, [selectedCoverElId]);
  const [addTextOpen, setAddTextOpen] = useState(false);
  const faceLayer = (face: CoverFace, style?: React.CSSProperties) => (
    <CoverFaceElements
      printSettings={coverPrintSettings}
      editingTextId={editingTextId}
      onEditText={(id) => { if (id) onSelectCoverEl?.(id); setEditingTextId(id); }}
      textHeights={textHeights}
      onMeasureText={onMeasureText}
      face={face}
      elements={coverElements}
      editable={canEditCoverElements}
      selectedId={selectedCoverElId}
      onSelect={(id) => onSelectCoverEl?.(id)}
      onUpdate={(id, u) => onUpdateCoverEl?.(id, u)}
      onDelete={(id) => onDeleteCoverEl?.(id)}
      onDuplicate={(id) => onDuplicateCoverEl?.(id)}
      photoUrl={(pid) => coverPhotoUrl?.(pid) ?? null}
      style={style}
    />
  );
  const selectedCoverEl = canEditCoverElements && selectedCoverElId
    ? coverElements.find((e) => e.id === selectedCoverElId) ?? null
    : null;

  return (
    // pt-16 on < lg: the floating « Changer » cover pill (top-right, lg:hidden)
    // sits there. pr-11 / md:px-14: the « Pages › » edge chip (right-1, mid
    // height) — the cover never runs under it.
    <div
      className={cn('flex flex-col items-center self-stretch flex-1 min-h-0 w-full', m2 ? 'p-2' : 'pl-3 pr-11 md:px-14 pt-16 lg:pt-3 pb-3')}
      // A tap anywhere but on a cover element (or its toolbar) drops the selection.
      onPointerDownCapture={selectedCoverElId ? (e) => {
        if (!keepsCoverSelection(e.target)) onSelectCoverEl?.(null);
      } : undefined}
    >
      {/* Cover action toolbar — its own row ABOVE the spread (never over the
          artwork/title). Same gates + handlers as the old in-canvas clusters;
          only placement/styling changed. Rendered conditionally so there's no
          empty gap when there are no actions. */}
      {((isAdmin && !!selectedCover) || (!isAdmin && isBlankTheme)) && (
        <div
          data-admin-cover-icon={isAdmin && selectedCover ? 'true' : undefined}
          data-blank-cover-upload={!isAdmin && isBlankTheme ? 'true' : undefined}
          data-cover-toolbar=""
          onMouseDown={(e) => e.stopPropagation()}
          // m2: kept mounted (its file inputs serve the sheet) but not shown
          className={cn('shrink-0 flex flex-wrap items-center justify-center gap-1.5 mb-3 max-w-full px-2 py-1.5 rounded-2xl', m2 && 'hidden')}
          style={{ background: UI.surface, border: `1px solid ${COLORS.gold}25`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
        >
          {/* Admin-only custom cover-icon controls. Always reachable (not gated
              on selection/hide) so the admin can re-show or restore after a
              Delete. Customers never have an admin session, so this never
              renders for them. */}
          {isAdmin && selectedCover && (
            <>
              <span
                className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded"
                style={{ color: '#fff', background: 'rgba(0,0,0,0.4)' }}
              >
                Admin
              </span>
              {/* Icon chooser — on a blank cover the ADMIN gets the same
                  catalogue-icon picker the customer has (build the client's
                  cover for them). Renders via the same override chain. */}
              {isBlankTheme && (
                <button
                  type="button"
                  onClick={onOpenIconPicker}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap"
                  style={{ color: '#fff', background: '#C9A24A', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Choisir une icône du catalogue"
                >
                  ✨ {hasCoverIconOverride ? 'Changer l’icône' : 'Choisir une icône'}
                </button>
              )}
              {/* PRIMARY admin path — the admin's own photo fills the whole
                  front panel (object-cover) and fully REPLACES the catalogue
                  art (design + thumbnail), exactly like the customer "vierge"
                  path and mirrored in the PDF precedence. This is the "place an
                  image instead of the design" control. */}
              <input
                ref={blankCoverFileRef}
                type="file"
                accept={galleryAccept()}
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadBlankCoverImage(f);
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => blankCoverFileRef?.current?.click()}
                className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap"
                style={{ color: '#fff', background: '#C9A24A', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                title="Placer votre propre photo en plein cadre (remplace l'illustration)"
              >
                🖼️ {blankCoverImageUrl ? 'Changer la photo' : 'Ma photo (plein cadre)'}
              </button>
              {blankCoverImageUrl && (
                <button
                  type="button"
                  onClick={onRemoveBlankCoverImage}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Retirer votre photo (revenir à l'illustration)"
                >
                  Retirer la photo
                </button>
              )}
              {/* Secondary, contain-fit override — replaces the illustration
                  with a transparent PNG / logo (letterboxed, not full-bleed). */}
              <input
                ref={coverIconFileRef}
                type="file"
                accept={galleryAccept()}
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadCoverIcon(f);
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => coverIconFileRef?.current?.click()}
                className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                title="Remplacer l'illustration par un PNG/logo (ajusté, non plein cadre)"
              >
                Remplacer l'illustration
              </button>
              <button
                type="button"
                onClick={() => onHideFrontCoverImageChange(!hideFrontCoverImage)}
                className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                title={hideFrontCoverImage ? "Afficher l'illustration" : "Masquer l'illustration (fond uni)"}
              >
                {hideFrontCoverImage ? "Afficher l'illustration" : "Masquer l'illustration"}
              </button>
              {hasCoverIconOverride && (
                <button
                  type="button"
                  onClick={onRestoreCoverIcon}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Rétablir l'illustration d'origine"
                >
                  Rétablir l'original
                </button>
              )}
              {/* COVER ELEMENTS (2026-10-20) — stickers / icons anywhere on the
                  cover, and the client's own photos on the back. */}
              {/* COVER TEXT (2026-10-30) — any words, on the front, the back or the spine. */}
              {canEditCoverElements && onAddCoverText && (
                <span className="relative inline-flex" data-cover-add-text-wrap="">
                  <button
                    type="button"
                    onClick={() => setAddTextOpen((v) => !v)}
                    className="rounded-full text-[11px] font-semibold px-3 py-1.5 transition-colors whitespace-nowrap"
                    style={{ color: '#fff', background: COLORS.gold, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                    title="Ajouter un texte sur la couverture (avant, dos ou tranche)"
                    data-cover-add-text=""
                  >
                    + Texte
                  </button>
                  {addTextOpen && (
                    <span
                      className="absolute left-0 top-full mt-1 z-50 flex gap-1 rounded-full px-1 py-1"
                      style={{ background: UI.surface, border: `1px solid ${COLORS.gold}40`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
                    >
                      {(['front', 'back', 'spine'] as CoverFace[]).map((f) => (
                        <button
                          key={f}
                          type="button"
                          data-cover-add-text-face={f}
                          className="rounded-full text-[11px] font-medium px-2.5 py-1 whitespace-nowrap hover:bg-gray-50"
                          style={{ color: UI.ink, border: `1px solid ${COLORS.gold}30` }}
                          onClick={() => {
                            setAddTextOpen(false);
                            const id = onAddCoverText(f);
                            if (id) setEditingTextId(id);
                          }}
                        >
                          {f === 'front' ? 'Avant' : f === 'back' ? 'Dos' : 'Tranche'}
                        </button>
                      ))}
                    </span>
                  )}
                </span>
              )}
              {canEditCoverElements && onOpenCoverStickers && (
                <button
                  type="button"
                  onClick={onOpenCoverStickers}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Glissez un sticker ou une icône sur l'avant, le dos ou la tranche"
                  data-cover-add-stickers=""
                >
                  ✦ Stickers & icônes
                </button>
              )}
              {canEditCoverElements && onOpenCoverPhotoPicker && (
                <button
                  type="button"
                  onClick={onOpenCoverPhotoPicker}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Placer des photos déjà envoyées par le client sur le dos de la couverture"
                  data-cover-add-photos=""
                >
                  📷 Photos du client au dos
                </button>
              )}
            </>
          )}
          {/* Client blank-cover image control — on a "vierge" cover the
              customer can upload their OWN front-cover image. Same pills as
              admin but customer-facing (no Admin badge). */}
          {!isAdmin && isBlankTheme && (
            <>
              {/* Catalogue-icon picker — the décor PNGs double as icons the
                  customer can place on the vierge cover (rendered through the
                  same front-image override chain as the admin upload). */}
              <button
                type="button"
                onClick={onOpenIconPicker}
                className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap"
                style={{ color: '#fff', background: '#C9A24A', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                title="Choisir une icône du catalogue Storiz"
              >
                ✨ {hasCoverIconOverride ? 'Changer l’icône' : 'Choisir une icône'}
              </button>
              {hasCoverIconOverride && (
                <button
                  type="button"
                  onClick={onRestoreCoverIcon}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Retirer l'icône choisie"
                >
                  Retirer l’icône
                </button>
              )}
              <input
                ref={blankCoverFileRef}
                type="file"
                accept={galleryAccept()}
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadBlankCoverImage(f);
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => blankCoverFileRef?.current?.click()}
                className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap"
                style={{ color: '#fff', background: '#C9A24A', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                title="Importer ma propre image de couverture"
              >
                🖼️ {blankCoverImageUrl ? 'Changer l’image' : 'Importer ma propre image'}
              </button>
              {blankCoverImageUrl && (
                <button
                  type="button"
                  onClick={onRemoveBlankCoverImage}
                  className="rounded-full text-[11px] font-medium px-3 py-1.5 transition-colors whitespace-nowrap hover:bg-gray-50"
                  style={{ color: UI.muted, background: UI.surface, border: '1px solid rgba(201,162,74,0.25)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }}
                  title="Retirer l'image importée"
                >
                  Retirer l’image
                </button>
              )}
            </>
          )}
        </div>
      )}
      {/* Cover spread aspect ≈ (2*210 + spine_in_panel_units)/297. We cap
          the height by the viewport WIDTH so phones don't horizontal-scroll
          past the back cover — the overflow was the "cropped" complaint.
          1.55 is a slight over-estimate of the spread aspect to leave breathing
          room for the (fixed-px) spine. */}
      <div ref={spreadBoxRef} className="flex-1 min-h-0 w-full flex items-center justify-center">
      <div
        // Night mode: the cover is the album — a light island; the admin
        // pills above and the text toolbar below are chrome.
        data-adm-media=""
        data-cover-spread=""
        className="flex items-stretch"
        style={{
          height: coverSpreadHeight,
          color: COLORS.dark,
          ...(viewOnly ? { pointerEvents: 'none' as const } : {}),
        }}
      >
        {/* Back Cover — SAME background color as front cover. The face box is
            the print's 200 × 300 mm frame (COVER_PANEL_ASPECT) so its content
            and the cover elements sit where the PDF puts them. */}
        <div className="relative shrink-0" style={{ aspectRatio: COVER_PANEL_ASPECT, height: '100%', ...(m2Face === 'front' ? { display: 'none' } : {}) }}>
        <div
          {...facePhotoDrop('back')}
          className={cn('rounded-l-md overflow-hidden relative flex flex-col items-center justify-end py-4 px-4 w-full h-full', m2Face === 'back' && 'rounded-r-md')}
          style={{
            background: coverBgColor,
            boxShadow: '-4px 2px 20px rgba(0,0,0,0.1)',
          }}
        >
          {/* Back cover images */}
          {isBlankTheme && hasImages ? (
            /* Blank theme: single full-frame image */
            <div className="absolute inset-0">
              {imgs[0]?.signed_url ? (
                <img
                  src={imgs[0].signed_url}
                  alt="Quatrième de couverture"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50 text-[10px]">
                  Chargement...
                </div>
              )}
              {!m2 && <button
                // ALWAYS visible bin icon (was opacity-0 hover-only → the
                // full-frame back-cover photo couldn't be deleted, esp. on
                // touch). Bigger tap target + trash glyph.
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md active:scale-90 transition-transform"
                style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
                onClick={(e) => { e.stopPropagation(); onBackCoverImagesClear(); }}
                aria-label="Supprimer l'image du dos"
                title="Supprimer l'image"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>}
            </div>
          ) : !isBlankTheme && hasImages ? (() => {
            /* FULL-PAGE (2026-07-15): one image can be flagged full_page via the
               ⋯ menu — it renders full-bleed behind; the OTHERS keep the centred
               grid on top. No flag → the classic grid, unchanged. */
            const fullIdx = imgs.findIndex((im) => im.full_page);
            const gridEntries = imgs.map((im, oi) => ({ im, oi })).filter((e) => e.oi !== fullIdx);
            return (
            <>
            {fullIdx >= 0 && (
              <div className="absolute inset-0">
                {imgs[fullIdx].signed_url ? (
                  <img
                    src={imgs[fullIdx].signed_url!}
                    alt="Photo pleine page"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full bg-white/10" />
                )}
                {!m2 && <BackCoverTileMenu isFull size="lg" onToggleFull={() => onBackCoverImageFullPage(fullIdx, false)} />}
                {!m2 && <button
                  className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md active:scale-90 transition-transform"
                  style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
                  onClick={(e) => { e.stopPropagation(); onBackCoverImageDelete(fullIdx); }}
                  aria-label="Supprimer l'image pleine page"
                  title="Supprimer l'image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>}
              </div>
            )}
            {(gridEntries.length > 0 || canAddMore) && (
            <div className="flex-1 flex items-center justify-center w-full px-4 pt-4 relative z-[1]">
              <div className="relative w-[70%] aspect-square">
                <div className={cn(
                  'w-full h-full grid gap-1.5 rounded-lg overflow-hidden',
                  gridEntries.length <= 1 ? 'grid-cols-1 grid-rows-1' :
                  gridEntries.length === 2 ? 'grid-cols-2 grid-rows-1' :
                  gridEntries.length === 3 ? 'grid-cols-2 grid-rows-2' :
                  'grid-cols-2 grid-rows-2'
                )}>
                  {gridEntries.map(({ im: img, oi }, gi) => (
                    <div key={img.storage_path} className={cn(
                      'relative overflow-hidden rounded-md group/img',
                      gridEntries.length === 3 && gi === 2 ? 'col-span-2' : ''
                    )}>
                      {img.signed_url ? (
                        <img
                          src={img.signed_url}
                          alt={`Photo ${gi + 1}`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10" />
                      )}
                      {!m2 && <button
                        // H2 (2026-06-09): ALWAYS visible (was hover-only →
                        // invisible/untappable on touch). Bin/trash glyph so the
                        // "delete this image" affordance is unmistakable.
                        className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-md active:scale-90"
                        style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
                        onClick={(e) => { e.stopPropagation(); onBackCoverImageDelete(oi); }}
                        aria-label="Supprimer cette image"
                        title="Supprimer cette image"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>}
                      {!m2 && <BackCoverTileMenu isFull={false} onToggleFull={() => onBackCoverImageFullPage(oi, true)} />}
                    </div>
                  ))}
                </div>
                {/* Add more images button (if below max) */}
                {canAddMore && !m2 && (
                  <button
                    className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[7px] font-medium transition-all"
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)' }}
                    onClick={() => backCoverFileRef?.current?.click()}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                  >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    Ajouter ({imgs.length}/{maxBackImages})
                  </button>
                )}
              </div>
            </div>
            )}
            </>
            );
          })() : (
            /* Upload image placeholder (centered) */
            <div className="flex-1 flex items-center justify-center">
              {!m2 && <button
                className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl transition-all group"
                style={{ background: 'rgba(255,255,255,0.1)' }}
                onClick={() => backCoverFileRef?.current?.click()}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              >
                <svg className="w-6 h-6 transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-[8px] tracking-wide" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {isBlankTheme ? 'Ajouter une image' : `Ajouter des photos (max ${maxBackImages})`}
                </span>
              </button>}
            </div>
          )}

          {/* Hidden file input — shared for all modes */}
          <input
            ref={backCoverFileRef as any}
            type="file"
            accept={galleryAccept()}
            multiple={!isBlankTheme}
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length === 0) return;
              // Parent uploads each file to album-photos and updates state
              // with the resolved {storage_path, signed_url} entries.
              await onBackCoverFilesAdd(files);
            }}
          />

          {/* Logo at bottom — always visible */}
          <div className="relative z-10 shrink-0 flex items-center justify-center mb-2" style={{ height: 28 }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Storiz"
                className="max-h-full max-w-full object-contain"
                style={{ opacity: hasImages ? 0.8 : 0.6 }}
                draggable={false}
              />
            ) : (
              <span className="text-[8px] tracking-widest uppercase"
                style={{ color: hasImages ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)' }}>
                Storiz
              </span>
            )}
          </div>

          {!m2 && <div
            className="absolute bottom-1.5 left-0 right-0 text-center text-[7px] tracking-widest uppercase pointer-events-none"
            style={{ color: hasImages ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.35)' }}
          >
            Quatrième de couverture
          </div>}

          {/* Whole-cover color picker — recolors front, spine, AND back at
              once so the album reads as a single piece. Per-face fine-tuning
              still lives in the SpineEditor sidebar (Avant / Tranche / Arrière). */}
          {/* Brush button (not a bare colour dot) — reads as "colour this
              cover"; the small bar under the brush shows the current colour. */}
          {!m2 && <label
            data-onboarding-target="cover-color"
            className="absolute top-2 left-2 w-8 h-8 rounded-full bg-white shadow-md ring-1 ring-black/10 cursor-pointer flex flex-col items-center justify-center"
            title="Couleur de la couverture (recto, tranche & dos)"
            aria-label="Changer la couleur de la couverture"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
              <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
            </svg>
            <span className="block w-4 rounded-full mt-0.5" style={{ height: 3, background: coverBgColor }} />
            <input
              type="color"
              value={coverBgColor}
              onChange={(e) => onAllCoverBgColorChange(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </label>}
        </div>
        {faceLayer('back', { inset: 0 })}
        </div>

        {/* Spine — narrower on phones so the spread fits without clipping */}
        <div className="relative shrink-0 w-7 md:w-14" style={{ height: '100%', ...(m2Face ? { display: 'none' } : {}) }}>
        <div
          {...facePhotoDrop('spine')}
          className="relative cursor-pointer group overflow-hidden w-full h-full"
          style={{
            background: spineConfig.bgColor,
            boxShadow: `inset 2px 0 4px rgba(0,0,0,0.15), inset -2px 0 4px rgba(0,0,0,0.15)`,
            transition: 'box-shadow 0.2s',
          }}
          onClick={onSpineClick}
        >
          {/* Destination text (vertical) */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            <span
              className="font-bold tracking-wider uppercase whitespace-nowrap"
              style={{
                color: spineConfig.textColor,
                fontSize: spineConfig.fontSize * spineConfig.scale,
                // Arabic: the named Arabic face the PDF uses, and no letter-spacing —
                // spacing pulls joined Arabic letters apart (2026-10-26).
                fontFamily: fontStackFor(spineConfig.fontFamily ?? "'Londrina Solid', sans-serif", spineConfig.text || destination || ''),
                transform: `rotate(${spineConfig.rotation}deg)`,
                letterSpacing: hasArabic(spineConfig.text || destination || '') ? 0 : '0.12em',
              }}
            >
              {spineConfig.text || destination || 'DESTINATION'}
            </span>
          </div>

          {/* Spine year removed — destination text is the only label. */}

          {/* Edit hint overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.3)' }}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
        </div>
        {/* The spine's elements live on a TRUE-SCALE strip (the printed column
            × the 300 mm frame) centred on the spine: the on-screen spine is
            wider than to scale, the strip is what prints. */}
        {(canEditCoverElements || elementsOnFace(coverElements, 'spine').length > 0) && faceLayer('spine', {
          top: 0, bottom: 0, left: '50%', height: '100%',
          aspectRatio: String(faceAspect('spine', spineSettings)),
          transform: 'translateX(-50%)',
          ...(canEditCoverElements ? { outline: '1px dashed rgba(255,255,255,0.35)', outlineOffset: -1 } : {}),
        })}
        </div>

        {/* Front Cover — uses CoverThumbnail for real design + text zones */}
        <div className="relative shrink-0" style={{ aspectRatio: COVER_PANEL_ASPECT, height: '100%', ...(m2Face === 'back' ? { display: 'none' } : {}) }}>
        <div
          {...facePhotoDrop('front')}
          className={cn('rounded-r-md overflow-hidden relative w-full h-full', m2Face === 'front' && 'rounded-l-md')}
          style={{
            boxShadow: '4px 2px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${COLORS.gold}15`,
            background: frontCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight,
          }}
        >
          {selectedCover ? (
            <CoverThumbnail
              cover={selectedCover}
              className="w-full h-full"
              destinationText={coverDestText}
              yearText={coverYearText}
              zoneOverrides={coverZoneOverrides}
              extraZones={coverExtraZones}
              bgColorOverride={frontCoverBgColor}
              hideThumbnail={hideFrontCoverImage}
              frontImageSrcOverride={frontCoverImageOverrideUrl}
              blankCoverImageSrc={blankCoverImageUrl}
              blankCoverTransform={blankCoverTransform}
              onBlankCoverTransformChange={onBlankCoverTransformChange}
              designTransform={coverDesignTransform}
              onDesignTransformChange={onCoverDesignTransformChange}
              designSelected={coverDesignSelected}
              onDesignSelectedChange={onCoverDesignSelectedChange}
              interactive={!viewOnly}
              selectedZone={selectedCoverZone}
              onZoneSelect={onCoverZoneSelect}
              editingZone={editingCoverZone}
              onZoneEditChange={onCoverZoneEditChange}
              onZoneChange={onCoverZoneChange}
              onZoneDelete={onCoverZoneDelete}
              onExtraZoneChange={onCoverExtraZoneChange}
              onExtraZoneDelete={onCoverExtraZoneDelete}
              onZoneTextChange={onCoverZoneTextChange}
              isAdmin={isAdmin}
              isBlankCover={isBlankTheme}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
              <div
                className="text-4xl font-bold uppercase tracking-tight mb-2"
                style={{
                  fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  color: COLORS.gold,
                  lineHeight: 1,
                }}
              >
                {coverDestText || destination || 'DESTINATION'}
              </div>
              <div className="text-2xl font-light" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: UI.goldInk }}>
                {coverYearText || year || new Date().getFullYear()}
              </div>
              <div className="mt-8 text-[9px] tracking-widest uppercase" style={{ color: `${COLORS.muted}80` }}>
                Choisir une couverture
              </div>
              <div className="mt-2 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${COLORS.gold}15` }}>
                <svg className="w-5 h-5" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          )}
          {!m2 && <div
            className="absolute bottom-2 left-0 right-0 text-center text-[8px] tracking-widest uppercase pointer-events-none"
            style={{ color: selectedCover?.thumbnail_url && !hideFrontCoverImage ? 'rgba(255,255,255,0.5)' : `${COLORS.muted}50` }}
          >
            Couverture avant
          </div>}

          {/* Admin + customer cover action pills moved to the toolbar row
              ABOVE the spread (see top of CoverView) so they never overlay
              the artwork/title. */}

          {/* Design transform control — Canva-style floating pill, shown
              only after the customer clicks the artwork. Clicking the
              cover background dismisses it. Buttons mirror the wheel/pinch
              gesture for accessibility + mobile. */}
          {selectedCover?.design_url && !hideFrontCoverImage && coverDesignSelected && (
            <div
              data-cover-design-toolbar="true"
              onMouseDown={(e) => e.stopPropagation()}
              // Hidden on phones (md:flex) because the chip used to land on
              // top of the cover text and confused customers. Mobile users
              // adjust the design via the sidebar's "Couleurs de la
              // couverture" + drag/pinch on the artwork directly.
              className="hidden md:flex absolute top-2 left-2 items-center gap-0.5 rounded-full bg-white px-1 py-1"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)' }}
            >
              <button
                onClick={() => onCoverDesignTransformChange({
                  ...coverDesignTransform,
                  scale: Math.max(0.3, coverDesignTransform.scale / 1.1),
                })}
                className="w-6 h-6 rounded-full flex items-center justify-center text-sm hover:bg-gray-100"
                style={{ color: UI.ink }}
                title="Réduire"
              >−</button>
              <span
                className="text-[11px] tabular-nums w-9 text-center select-none"
                style={{ color: UI.ink }}
              >
                {Math.round(coverDesignTransform.scale * 100)}%
              </span>
              <button
                onClick={() => onCoverDesignTransformChange({
                  ...coverDesignTransform,
                  scale: Math.min(3, coverDesignTransform.scale * 1.1),
                })}
                className="w-6 h-6 rounded-full flex items-center justify-center text-sm hover:bg-gray-100"
                style={{ color: UI.ink }}
                title="Agrandir"
              >+</button>
              <div className="w-px h-4 mx-0.5" style={{ background: 'rgba(0,0,0,0.08)' }} />
              <button
                onClick={() => onCoverDesignTransformChange({ x: 0, y: 0, scale: 1 })}
                className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-100"
                style={{ color: UI.muted }}
                title="Réinitialiser la position"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6M5 19a8 8 0 0014.5-3M19 5A8 8 0 004.5 8" />
                </svg>
              </button>
            </div>
          )}

          {/* Front-cover colour picker. The illustration hide/show toggle lives
              in the admin pill cluster above ("Masquer l'illustration") — kept
              there as the single, labeled control to avoid two competing eye
              toggles. Customers keep only the colour picker. Bottom-right so
              the tiny dot never covers the title. */}
          {!m2 && <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
            <label
              className="relative w-8 h-8 rounded-full bg-white shadow-md ring-1 ring-black/10 cursor-pointer flex flex-col items-center justify-center"
              title="Couleur de la couverture (recto, tranche & dos)"
              aria-label="Changer la couleur de la couverture"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
              <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
            </svg>
              <span className="block w-4 rounded-full mt-0.5" style={{ height: 3, background: frontCoverBgColor ?? selectedCover?.dominant_color ?? COLORS.beigeLight }} />
              <input
                type="color"
                value={frontCoverBgColor ?? selectedCover?.dominant_color ?? '#F5F0E8'}
                onChange={(e) => onAllCoverBgColorChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          </div>}
        </div>
        {faceLayer('front', { inset: 0 })}
        </div>
      </div>
      </div>

      {/* COVER ELEMENTS — the selected sticker / photo: layers, face, delete.
          (Move, resize, rotate, duplicate are on the element itself — the
          same handles and pill as on the pages.) */}
      {selectedCoverEl && (
        <CoverElementBar
          el={selectedCoverEl}
          onLayer={(m) => onLayerCoverEl?.(selectedCoverEl.id, m)}
          onFace={(f) => onMoveCoverElToFace?.(selectedCoverEl.id, f)}
          onDelete={() => { setEditingTextId(null); onDeleteCoverEl?.(selectedCoverEl.id); }}
          onDone={() => onSelectCoverEl?.(null)}
          onUpdate={(u) => onUpdateCoverEl?.(selectedCoverEl.id, u)}
          onDuplicate={() => onDuplicateCoverEl?.(selectedCoverEl.id)}
          onEditText={() => setEditingTextId(selectedCoverEl.id)}
          outside={crossesSafeZone(selectedCoverEl, coverPrintSettings, textHeights[selectedCoverEl.id])}
          overflows={selectedCoverEl.type === 'text' && (textHeights[selectedCoverEl.id] ?? 0) > selectedCoverEl.height + 0.3}
          onFitSafe={() => {
            const fitted = fitIntoSafeZone(selectedCoverEl, coverPrintSettings, textHeights[selectedCoverEl.id]);
            onUpdateCoverEl?.(selectedCoverEl.id, {
              x: fitted.x, y: fitted.y, width: fitted.width, height: fitted.height,
              ...(fitted.type === 'text' ? { fontSize: fitted.fontSize, bandPad: fitted.bandPad } : {}),
            });
          }}
          onFitBox={selectedCoverEl.type === 'text' ? () => {
            const measureAt = canvasMeasureAt(fontStackFor(selectedCoverEl.fontFamily, selectedCoverEl.text ?? ''), selectedCoverEl.fontWeight ?? '600', selectedCoverEl.fontStyle ?? 'normal');
            if (!measureAt) return;
            const size = fitFontSize({
              text: selectedCoverEl.text ?? '',
              box: boxUnits(selectedCoverEl, faceAspect(selectedCoverEl.face, spineSettings)),
              lineHeight: textLineHeight(selectedCoverEl),
              measureAt,
            });
            onUpdateCoverEl?.(selectedCoverEl.id, { fontSize: size });
          } : undefined}
          spineSettings={spineSettings}
        />
      )}

      {/* Floating bottom toolbar — appears when a text zone is selected.
          Lets the user adjust color + size and confirm with a checkmark. */}
      {selectedCoverZone !== null && (
        <ZoneTextToolbar
          selectedKey={selectedCoverZone}
          zoneOverrides={coverZoneOverrides}
          extraZones={coverExtraZones}
          onZoneChange={onCoverZoneChange}
          onExtraZoneChange={onCoverExtraZoneChange}
          onClose={() => onCoverZoneSelect(null)}
          onDelete={m2 && (selectedCoverZone.startsWith('extra-') || isAdmin || isBlankTheme
            || !['destination', 'year'].includes(selectedCover?.text_zones?.[Number(selectedCoverZone)]?.type ?? ''))
            ? () => {
              const key = selectedCoverZone;
              onCoverZoneSelect(null);
              if (key.startsWith('extra-')) onCoverExtraZoneDelete(Number(key.slice(6)));
              else onCoverZoneDelete(Number(key));
            }
            : undefined}
        />
      )}
    </div>
  );
}

/**
 * A pointer-down on the cover drops the selected cover element — unless it
 * lands on the element itself or on ITS CONTROLS. ⚠️ « when I click delete, it
 * doesn't get deleted » (owner, 2026-10-30): a PHOTO element's white pill
 * (ClampedPill — trash, −/+, crop…) is portaled to <body>, so it is NOT inside
 * the element; this check only knew `[data-element-id]` and the cover bar, the
 * capture-phase handler deselected first, the pill unmounted under the finger
 * and the trash's click never landed. Every editor toolbar is marked
 * `data-editor-toolbar` (the white pill, the docked dark pill): they keep it.
 * Guard: coverElementDelete.test.tsx.
 */
export function keepsCoverSelection(target: EventTarget | null): boolean {
  const t = target as Element | null;
  if (!t || typeof t.closest !== 'function') return false;
  return !!t.closest('[data-element-id],[data-cover-el-toolbar],[data-editor-toolbar]');
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Cover elements (stickers / icons / photos on the faces, 2026-10-20) ─────
// ═══════════════════════════════════════════════════════════════════════════════
// One layer per face, exactly the face's printed frame (front/back: the 200 ×
// 300 mm image frame; spine: the printed column), holding the SAME
// PageElementOverlay the content pages use — drag, 8 resize handles, rotation,
// duplicate, delete, crop for photos. The layer itself lets taps through to
// the artwork (index.css: only the elements take the pointer). A client gets
// `editable=false`: the elements are drawn, never moved.
export function CoverFaceElements({
  face, elements, editable, selectedId, onSelect, onUpdate, onDelete, onDuplicate, photoUrl, style,
  printSettings, editingTextId = null, onEditText, textHeights, onMeasureText,
}: {
  face: CoverFace;
  elements: CoverElement[];
  editable: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<CoverElement>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  photoUrl: (photoId: string) => string | null;
  style?: React.CSSProperties;
  /** The admin's print settings — the safe zone (lib/coverElements.coverSafeZoneMm). */
  printSettings: SafeZoneSettings;
  /** COVER TEXT (2026-10-30) — the text being typed in, and its toggle. */
  editingTextId?: string | null;
  onEditText?: (id: string | null) => void;
  /** Measured height of each text's words, % of the face height. */
  textHeights?: Record<string, number>;
  onMeasureText?: (id: string, heightPct: number) => void;
}) {
  const els = elementsOnFace(elements, face);
  const plan = shadowPlan(els as unknown as PageElement[]);
  const safe = coverSafeZonePct(face, printSettings);
  return (
    <div
      data-cover-el-layer={face}
      // Drop zone for the Icônes / Stickers tiles (lib/tileDrag.ts) — the team only.
      data-element-drop={editable ? `cover:${face}` : undefined}
      className="absolute"
      // An inline-size query container: a cover TEXT sizes its letters in `cqi`
      // of THIS face — the print's fontSize × faceW / 400 (storiz-render-parity).
      style={{ ...style, pointerEvents: 'none', zIndex: 15, isolation: 'isolate', containerType: 'inline-size' }}
    >
      {/* THE SAFE ZONE (owner 2026-10-30, « not cut out when I flip it on the
          hardcover »): what lies outside this dashed frame may fold round the
          board or into the hinge. From the print settings, never invented. */}
      {editable && (
        <div
          data-cover-safe-zone={face}
          className="absolute pointer-events-none"
          style={{
            left: `${safe.x}%`, top: `${safe.y}%`, width: `${safe.w}%`, height: `${safe.h}%`,
            outline: '1px dashed rgba(220,38,38,0.45)', outlineOffset: -1, zIndex: 0,
          }}
        />
      )}
      {els.map((el, ei) => (
        <Fragment key={el.id}>
          {(plan.get(ei) ?? []).map((sh) => (
            <ElementShadow
              key={`sh-${sh.id}`}
              el={sh}
              imageSrc={sh.type === 'icon' && sh.imageUrl ? iconDisplayUrl(sh) ?? null : null}
            />
          ))}
          {el.type === 'text' ? (
            <CoverTextBox
              el={el}
              editable={editable}
              selected={editable && selectedId === el.id}
              editing={editable && editingTextId === el.id}
              onSelect={() => { if (editable) onSelect(el.id); }}
              onStartEditing={() => onEditText?.(el.id)}
              onStopEditing={() => onEditText?.(null)}
              onUpdate={(u) => onUpdate(el.id, u)}
              onMeasure={onMeasureText ? (h) => onMeasureText(el.id, h) : undefined}
            />
          ) : (
            <PageElementOverlay
              element={el as unknown as PageElement}
              pageNumber={-1}
              isSelected={editable && selectedId === el.id}
              isEditing={false}
              onSelect={() => { if (editable) onSelect(el.id); }}
              onDoubleClick={() => {}}
              onUpdate={(u) => onUpdate(el.id, u as Partial<CoverElement>)}
              onDelete={() => onDelete(el.id)}
              onDuplicate={() => onDuplicate(el.id)}
              onStopEditing={() => {}}
              siblings={els as unknown as PageElement[]}
              photoUrl={el.type === 'photo' && el.photoId ? photoUrl(el.photoId) : null}
              onDone={() => onSelect(null)}
              passive={!editable}
            />
          )}
          {editable && crossesSafeZone(el, printSettings, textHeights?.[el.id]) && (
            <SafeZoneWarning box={printedExtent(el, printSettings, textHeights?.[el.id])} label={selectedId === el.id} />
          )}
        </Fragment>
      ))}
    </div>
  );
}

/** The selected cover element's extra controls: layer order, which face, the
 *  safe zone — and for a TEXT (2026-10-30) its words, font, size (−/+, slider,
 *  « ajuster à la boîte »), colour, bold/italic, alignment, line spacing and
 *  band. Its root carries data-cover-el-toolbar: a tap here keeps the selection. */
export function CoverElementBar({
  el, onLayer, onFace, onDelete, onDone, onUpdate, onDuplicate, onEditText, outside, overflows, onFitSafe, onFitBox, spineSettings,
}: {
  el: CoverElement;
  onLayer: (m: LayerMove) => void;
  onFace: (f: CoverFace) => void;
  onDelete: () => void;
  onDone: () => void;
  onUpdate?: (u: Partial<CoverElement>) => void;
  onDuplicate?: () => void;
  onEditText?: () => void;
  /** What prints crosses the safe zone. */
  outside?: boolean;
  /** A text taller than its box. */
  overflows?: boolean;
  onFitSafe?: () => void;
  onFitBox?: () => void;
  spineSettings?: { spineWmm: number; jointGapMm: number };
}) {
  // « Nouvel éditeur mobile »: docked at the bottom like every selection bar
  const dock = useMobileDock();
  const btn = 'rounded-full text-[11px] font-medium px-2.5 py-1 whitespace-nowrap transition-colors hover:bg-gray-50';
  const btnStyle = { color: UI.ink, background: UI.surface, border: `1px solid ${COLORS.gold}30` };
  const on = { ...btnStyle, background: COLORS.gold, color: '#fff', border: 'none' };
  const isText = el.type === 'text';
  const fs = el.fontSize ?? 24;
  const pt = coverTextPt(fs, el.face, spineSettings);
  // Size steps in PRINTED points (the readout the team reads): ±10 %, at least 1 pt.
  const setPt = (p: number) => onUpdate?.({ fontSize: +coverFontSizeForPt(Math.max(2, Math.min(400, p)), el.face, spineSettings).toFixed(3) });
  const step = (dir: 1 | -1) => setPt(pt + dir * Math.max(1, Math.round(pt * 0.1)));
  const lh = el.lineHeight ?? 1.3;
  const hasBand = !!el.bandFill;
  const sep = <span className="w-px h-4 mx-0.5" style={{ background: `${COLORS.gold}30` }} />;
  const bar = (
    <div
      data-cover-el-toolbar=""
      data-editor-toolbar={dock ? 'true' : undefined}
      className="shrink-0 mt-3 flex flex-col items-center gap-1.5 max-w-full px-2 py-1.5 rounded-2xl"
      style={{ background: UI.surface, border: `1px solid ${COLORS.gold}25`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      {isText && onUpdate && (
        <div className="flex flex-wrap items-center justify-center gap-1.5" data-cover-text-bar="">
          <button type="button" className={btn} style={btnStyle} onClick={onEditText} title="Écrire / coller le texte (plusieurs lignes possibles)">✎ Modifier le texte</button>
          {sep}
          <select
            value={EDITOR_TEXT_FONTS.some((f) => f.family === el.fontFamily) ? el.fontFamily : ''}
            onChange={(e) => onUpdate({ fontFamily: e.target.value })}
            className="rounded-full text-[11px] px-2 py-1 max-w-[150px]"
            style={{ ...btnStyle, fontFamily: el.fontFamily }}
            title="Police"
            data-cover-text-font=""
          >
            {!EDITOR_TEXT_FONTS.some((f) => f.family === el.fontFamily) && <option value="">{(el.fontFamily ?? 'Police').split(',')[0].replace(/['"]/g, '')}</option>}
            {EDITOR_TEXT_FONTS.map((f) => <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>{f.label}</option>)}
          </select>
          {sep}
          <button type="button" className={btn} style={btnStyle} onClick={() => step(-1)} title="Plus petit" data-cover-text-smaller="">A−</button>
          <input
            type="range" min={4} max={160} step={1}
            value={Math.min(160, Math.max(4, Math.round(pt)))}
            onChange={(e) => setPt(Number(e.target.value))}
            className="w-24 accent-[#C9A24A]"
            title="Taille du texte"
            data-cover-text-size=""
          />
          <button type="button" className={btn} style={btnStyle} onClick={() => step(1)} title="Plus grand" data-cover-text-bigger="">A+</button>
          <span className="text-[11px] tabular-nums w-11 text-center" style={{ color: UI.ink }} data-cover-text-pt="">{Math.round(pt)} pt</span>
          {onFitBox && <button type="button" className={btn} style={btnStyle} onClick={onFitBox} title="La plus grande taille qui tient dans la boîte" data-cover-text-fit="">Ajuster à la boîte</button>}
          {sep}
          <label className="relative w-7 h-7 rounded-full cursor-pointer flex items-center justify-center" style={btnStyle} title="Couleur du texte">
            <span className="w-4 h-4 rounded-full" style={{ background: el.fontColor ?? '#1F2937', border: '1px solid rgba(0,0,0,0.15)' }} />
            <input type="color" value={el.fontColor ?? '#1F2937'} onChange={(e) => onUpdate({ fontColor: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
          <button type="button" className={btn} style={(el.fontWeight ?? '600') >= '600' ? on : btnStyle} onClick={() => onUpdate({ fontWeight: (el.fontWeight ?? '600') >= '600' ? '400' : '700' })} title="Gras"><b>G</b></button>
          <button type="button" className={btn} style={el.fontStyle === 'italic' ? on : btnStyle} onClick={() => onUpdate({ fontStyle: el.fontStyle === 'italic' ? 'normal' : 'italic' })} title="Italique"><i>I</i></button>
          {(['left', 'center', 'right'] as const).map((a) => (
            <button key={a} type="button" className={btn} style={(el.textAlign ?? 'center') === a ? on : btnStyle} onClick={() => onUpdate({ textAlign: a })} title={a === 'left' ? 'À gauche' : a === 'right' ? 'À droite' : 'Centré'}>
              {a === 'left' ? '⇤' : a === 'right' ? '⇥' : '↔'}
            </button>
          ))}
          {sep}
          <span className="text-[10px]" style={{ color: UI.muted }}>Interligne</span>
          <button type="button" className={btn} style={btnStyle} onClick={() => onUpdate({ lineHeight: +Math.max(0.7, lh - 0.1).toFixed(2) })}>−</button>
          <span className="text-[11px] tabular-nums w-7 text-center">{lh.toFixed(1)}</span>
          <button type="button" className={btn} style={btnStyle} onClick={() => onUpdate({ lineHeight: +Math.min(3, lh + 0.1).toFixed(2) })}>+</button>
          {sep}
          <button
            type="button" className={btn} style={hasBand ? on : btnStyle}
            onClick={() => onUpdate(hasBand ? { bandFill: undefined } : { bandFill: '#1A1A1A', bandOpacity: el.bandOpacity ?? 0.55, bandPad: el.bandPad ?? 2 })}
            title="Un fond derrière le texte" data-cover-text-band=""
          >Fond</button>
          {hasBand && (
            <>
              <label className="relative w-7 h-7 rounded-full cursor-pointer flex items-center justify-center" style={btnStyle} title="Couleur du fond">
                <span className="w-4 h-4 rounded" style={{ background: el.bandFill, border: '1px solid rgba(0,0,0,0.15)' }} />
                <input type="color" value={el.bandFill} onChange={(e) => onUpdate({ bandFill: e.target.value.toUpperCase() })} className="absolute inset-0 opacity-0 cursor-pointer" />
              </label>
              <span className="text-[10px]" style={{ color: UI.muted }}>Opacité</span>
              <input type="range" min={0.1} max={1} step={0.05} value={el.bandOpacity ?? 0.88} onChange={(e) => onUpdate({ bandOpacity: Number(e.target.value) })} className="w-16 accent-[#C9A24A]" title="Opacité du fond" />
              <span className="text-[10px]" style={{ color: UI.muted }}>Marge</span>
              <input type="range" min={0} max={10} step={0.5} value={el.bandPad ?? 0} onChange={(e) => onUpdate({ bandPad: Number(e.target.value) })} className="w-16 accent-[#C9A24A]" title="Marge du fond autour du texte" />
            </>
          )}
        </div>
      )}
      {(outside || overflows) && (
        <div className="flex flex-wrap items-center justify-center gap-1.5 text-[11px]" data-cover-safe-hint="">
          {outside && <span className="font-semibold" style={{ color: UI.danger }}>⚠ Sera coupé à l'impression (hors zone sûre)</span>}
          {overflows && <span className="font-semibold" style={{ color: '#B45309' }}>⚠ Le texte dépasse la boîte</span>}
          {outside && onFitSafe && (
            <button type="button" className={btn} style={{ ...btnStyle, color: '#fff', background: UI.danger, border: 'none' }} onClick={onFitSafe} data-cover-fit-safe="">
              Mettre dans la zone sûre
            </button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide px-1" style={{ color: UI.muted }}>
          {el.type === 'photo' ? 'Photo' : isText ? 'Texte' : 'Sticker'} · {COVER_FACE_LABEL[el.face]}
        </span>
        <button type="button" className={btn} style={btnStyle} onClick={() => onLayer('top')} title="Mettre au premier plan">Premier plan</button>
        <button type="button" className={btn} style={btnStyle} onClick={() => onLayer('up')} title="Avancer d'un plan">Avancer</button>
        <button type="button" className={btn} style={btnStyle} onClick={() => onLayer('down')} title="Reculer d'un plan">Reculer</button>
        <button type="button" className={btn} style={btnStyle} onClick={() => onLayer('bottom')} title="Mettre à l'arrière-plan">Arrière-plan</button>
        {sep}
        {(['front', 'spine', 'back'] as CoverFace[]).filter((f) => f !== el.face).map((f) => (
          <button key={f} type="button" className={btn} style={btnStyle} onClick={() => onFace(f)} title={`Déplacer vers : ${COVER_FACE_LABEL[f]}`}>
            → {f === 'front' ? 'Avant' : f === 'back' ? 'Dos' : 'Tranche'}
          </button>
        ))}
        {sep}
        {!outside && onFitSafe && <button type="button" className={btn} style={btnStyle} onClick={onFitSafe} title="Rentrer l'élément dans la zone imprimable sûre">Zone sûre</button>}
        {onDuplicate && <button type="button" className={btn} style={btnStyle} onClick={onDuplicate} title="Dupliquer">Dupliquer</button>}
        <button type="button" className={btn} style={{ ...btnStyle, color: UI.danger }} onClick={onDelete} title="Supprimer" data-cover-el-delete="">Supprimer</button>
        <button type="button" className={btn} style={{ ...btnStyle, background: COLORS.gold, color: '#fff', border: 'none' }} onClick={onDone}>Terminé</button>
      </div>
    </div>
  );
  return dock ? createPortal(bar, dock) : bar;
}

/** « Photos du client au dos » — the album's uploaded photos; a tap places one
 *  on the back cover (several in a row; Terminé closes). The editor shows the
 *  light tray copy; the print uses the original (drawPageElements). */
function CoverPhotoPickerModal({ photos, onPick, onClose }: {
  photos: PhotoWithUrl[];
  onPick: (photoId: string) => void;
  onClose: () => void;
}) {
  const [added, setAdded] = useState<string[]>([]);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose} data-cover-photo-picker="">
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: UI.surface, color: UI.ink }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${COLORS.gold}20` }}>
          <div>
            <h2 className="text-sm font-semibold">Photos du client au dos</h2>
            <p className="text-[11px]" style={{ color: UI.muted }}>Touchez une photo pour la placer sur le dos — puis déplacez-la, agrandissez-la, tournez-la. Vous pouvez aussi la glisser depuis « Mes photos ».</p>
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: COLORS.gold, color: '#fff' }}>
            Terminé{added.length ? ` (${added.length})` : ''}
          </button>
        </div>
        <div className="flex-1 overflow-auto p-3">
          {photos.length === 0 ? (
            <p className="text-xs text-center py-8" style={{ color: UI.muted }}>Aucune photo dans cet album.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
              {photos.map((p) => {
                const src = p.tray_signed_url ?? p.thumbnail_signed_url ?? p.signed_url ?? null;
                const n = added.filter((id) => id === p.id).length;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-cover-photo-pick={p.id}
                    onClick={() => { onPick(p.id); setAdded((a) => [...a, p.id]); }}
                    className="relative aspect-square rounded-md overflow-hidden active:scale-95 transition-transform"
                    style={{ background: `${COLORS.gold}10`, border: `2px solid ${n ? COLORS.gold : 'transparent'}` }}
                    title={p.original_filename ?? p.id}
                  >
                    {src && <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" draggable={false} />}
                    {n > 0 && (
                      <span className="absolute top-1 right-1 min-w-5 h-5 px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: COLORS.gold, color: '#fff' }}>
                        ✓{n > 1 ? n : ''}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Floating Zone Text Toolbar (mobile + desktop) ──────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function ZoneTextToolbar({
  selectedKey,
  zoneOverrides,
  extraZones,
  onZoneChange,
  onExtraZoneChange,
  onClose,
  onDelete,
}: {
  selectedKey: string;
  zoneOverrides: Record<number, TextZoneOverride>;
  extraZones: ExtraTextZone[];
  onZoneChange: (i: number, ovr: TextZoneOverride) => void;
  onExtraZoneChange: (i: number, ez: ExtraTextZone) => void;
  onClose: () => void;
  /** « Nouvel éditeur mobile »: delete from the docked bar (the 20 px chip on
   *  the cover is not drawn there). Absent = no button (mandatory title). */
  onDelete?: () => void;
}) {
  // « Nouvel éditeur mobile »: docked in the bottom strip, never over the cover.
  const dock = useMobileDock();
  const isExtra = selectedKey.startsWith('extra-');
  const idx = isExtra ? parseInt(selectedKey.replace('extra-', ''), 10) : parseInt(selectedKey, 10);
  const [fontOpen, setFontOpen] = useState(false);

  // For template zones the size is a multiplier (scale: 0.2..5).
  // For extra zones the size is the raw fontSize in pt.
  let scale = 1;
  let extraSize = 0;
  if (isExtra) {
    extraSize = extraZones[idx]?.fontSize ?? 18;
  } else {
    scale = zoneOverrides[idx]?.scale ?? 1;
  }

  const inc = () => {
    if (isExtra) {
      const ez = extraZones[idx];
      if (ez) onExtraZoneChange(idx, { ...ez, fontSize: Math.min(300, ez.fontSize + 8) });
    } else {
      onZoneChange(idx, { ...zoneOverrides[idx], scale: Math.min(5, scale + 0.15) });
    }
  };
  const dec = () => {
    if (isExtra) {
      const ez = extraZones[idx];
      if (ez) onExtraZoneChange(idx, { ...ez, fontSize: Math.max(10, ez.fontSize - 8) });
    } else {
      onZoneChange(idx, { ...zoneOverrides[idx], scale: Math.max(0.2, scale - 0.15) });
    }
  };
  const setColor = (color: string) => {
    if (isExtra) {
      const ez = extraZones[idx];
      if (ez) onExtraZoneChange(idx, { ...ez, color });
    } else {
      onZoneChange(idx, { ...zoneOverrides[idx], color });
    }
  };
  const setFontFamily = (family: string) => {
    if (isExtra) {
      const ez = extraZones[idx];
      if (ez) onExtraZoneChange(idx, { ...ez, fontFamily: family });
    } else {
      onZoneChange(idx, { ...zoneOverrides[idx], fontFamily: family });
    }
  };
  // Alignment applies to the free "extra" texts only (title/year keep their
  // template placement). It sets which edge the anchor pins + multi-line align.
  const extraAlign = isExtra ? (extraZones[idx]?.align ?? 'center') : 'center';
  const setAlign = (align: 'left' | 'center' | 'right') => {
    const ez = extraZones[idx];
    if (!ez) return;
    // The buttons POSITION the text: snap its anchor to the cover's left /
    // centre / right (inside the safe margin). The customer can still drag it
    // anywhere afterwards. 0.045 ≈ the print safe inset.
    const x = align === 'left' ? 0.045 : align === 'right' ? 0.955 : 0.5;
    onExtraZoneChange(idx, { ...ez, align, x });
  };

  const currentColor = isExtra
    ? (extraZones[idx]?.color ?? '#000000')
    : (zoneOverrides[idx]?.color ?? '#000000');
  const currentFamily = isExtra
    ? (extraZones[idx]?.fontFamily ?? '')
    : (zoneOverrides[idx]?.fontFamily ?? '');
  const currentLabel =
    EDITOR_TEXT_FONTS.find((f) => f.family === currentFamily)?.label ??
    (currentFamily ? (currentFamily.split(',')[0] || '').replace(/['"]/g, '').trim() : 'Police');

  const sizeLabel = isExtra ? `${Math.round(extraSize)}pt` : `${Math.round(scale * 100)}%`;

  const zoneBar = (
    <div
      data-editor-toolbar={dock ? 'true' : undefined}
      className={cn(dock ? 'relative' : 'fixed bottom-0 inset-x-0 z-50', 'px-3 py-3 flex items-center gap-2 justify-between flex-wrap')}
      style={{
        background: UI.surface,
        borderTop: `1px solid ${COLORS.gold}30`,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
        paddingBottom: dock ? 12 : 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Font picker popover */}
      {fontOpen && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -translate-y-full -top-2 p-2 rounded-xl bg-white shadow-xl overflow-y-auto"
          style={{ border: `1px solid ${COLORS.gold}30`, minWidth: 220, maxHeight: 280 }}
        >
          {EDITOR_TEXT_FONTS.map((f) => {
            const selected = f.family === currentFamily;
            return (
              <button
                key={f.family}
                onClick={() => { setFontFamily(f.family); setFontOpen(false); }}
                className="w-full flex items-center justify-between px-3 py-2 text-left rounded-lg"
                style={{
                  fontFamily: f.family,
                  fontSize: 16,
                  color: UI.ink,
                  background: selected ? `${COLORS.gold}15` : 'transparent',
                }}
              >
                <span className="truncate">{f.label}</span>
                {selected && (
                  <svg className="w-4 h-4 shrink-0 ml-2" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Size controls */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={dec}
          className="w-9 h-9 rounded-full flex items-center justify-center text-base font-bold"
          style={{ background: `${COLORS.gold}15`, color: UI.ink }}
          aria-label="Plus petit"
        >−</button>
        <span className="text-xs font-mono w-12 text-center" style={{ color: UI.ink }}>
          {sizeLabel}
        </span>
        <button
          onClick={inc}
          className="w-9 h-9 rounded-full flex items-center justify-center text-base font-bold"
          style={{ background: `${COLORS.gold}15`, color: UI.ink }}
          aria-label="Plus grand"
        >+</button>
      </div>

      {/* Alignment — free texts only (gauche / centré / droite) */}
      {isExtra && (
        <div className="flex items-center gap-1 rounded-full p-0.5" style={{ background: `${COLORS.gold}12` }}>
          {([
            { key: 'left' as const, label: 'Aligner à gauche', d: 'M4 6h16M4 10h10M4 14h16M4 18h10' },
            { key: 'center' as const, label: 'Centrer', d: 'M4 6h16M7 10h10M4 14h16M7 18h10' },
            { key: 'right' as const, label: 'Aligner à droite', d: 'M4 6h16M10 10h10M4 14h16M10 18h10' },
          ]).map((a) => (
            <button
              key={a.key}
              onClick={() => setAlign(a.key)}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                background: extraAlign === a.key ? COLORS.gold : 'transparent',
                color: extraAlign === a.key ? COLORS.white : UI.ink,
              }}
              title={a.label}
              aria-label={a.label}
              aria-pressed={extraAlign === a.key}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d={a.d} />
              </svg>
            </button>
          ))}
        </div>
      )}

      {/* Font picker trigger */}
      <button
        onClick={() => setFontOpen((v) => !v)}
        className="h-9 px-3 rounded-full flex items-center gap-1.5 text-xs"
        style={{
          background: fontOpen ? `${COLORS.gold}25` : `${COLORS.gold}15`,
          color: UI.ink,
          fontFamily: currentFamily || undefined,
          maxWidth: 160,
        }}
        title="Police"
      >
        <span className="truncate">{currentLabel}</span>
        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Color picker — both template and extra zones */}
      <label
        className="w-9 h-9 rounded-full border cursor-pointer overflow-hidden flex items-center justify-center border-[#C9A24A40] dark:border-adm-line-strong"
        style={{ background: currentColor }}
        title="Couleur"
      >
        <input
          type="color"
          value={currentColor}
          onChange={(e) => setColor(e.target.value)}
          className="opacity-0 w-full h-full cursor-pointer"
        />
      </label>

      {onDelete && (
        <button
          onClick={onDelete}
          aria-label="Supprimer ce texte"
          className="px-3 h-9 rounded-full text-xs font-semibold"
          style={{ background: `${COLORS.danger}12`, color: UI.danger }}
        >
          Supprimer
        </button>
      )}

      {/* Confirm */}
      <button
        onClick={onClose}
        className="px-4 h-9 rounded-full text-sm font-bold flex items-center gap-1.5 shadow-sm"
        style={{ background: COLORS.gold, color: COLORS.white }}
      >
        ✓ Terminé
      </button>
    </div>
  );
  return dock ? createPortal(zoneBar, dock) : zoneBar;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Editor checkpoint history panel (operators only, 2026-07-23) ─────────────
// One entry per ENDED editing session (server-detected idle gap) — never one
// per click. Restore = explicit confirm → server snapshots the current state
// ('pre-restore', so a restore is itself undoable) → overwrites → full reload
// (gcTime:0 fresh-load hydrates the restored state everywhere, incl. preview).
// ═══════════════════════════════════════════════════════════════════════════════
function EditorHistoryPanel({ shopifyOrderId, albumIndex, onClose }: {
  shopifyOrderId: string; albumIndex: number; onClose: () => void;
}) {
  const [confirming, setConfirming] = useState<{ id: string; when: string } | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['session-history', shopifyOrderId, albumIndex],
    queryFn: () => sessionsApi.history(shopifyOrderId, albumIndex),
    staleTime: 0,
    gcTime: 0,
  });
  const checkpoints = q.data?.checkpoints ?? [];

  const fmtWhen = (iso: string) => {
    const d = new Date(iso);
    const day = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${day} · ${time}`;
  };
  const fmtAgo = (iso: string) => {
    const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 60) return `il y a ${Math.max(1, min)} min`;
    if (min < 48 * 60) return `il y a ${Math.round(min / 60)} h`;
    return `il y a ${Math.round(min / 1440)} j`;
  };

  const doRestore = async () => {
    if (!confirming || restoring) return;
    setRestoring(true); setErr(null);
    try {
      await sessionsApi.restoreCheckpoint(shopifyOrderId, albumIndex, confirming.id);
      // Full reload — the editor re-hydrates the restored state from a truly
      // fresh fetch (gcTime:0) so canvas + preview + toolbars all match.
      window.location.reload();
    } catch (e) {
      setErr((e as Error)?.message ?? 'Échec de la restauration');
      setRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65]" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(17,17,17,.25)' }} />
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-[380px] flex flex-col shadow-2xl"
        style={{ background: UI.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${COLORS.gold}20` }}>
          <svg className="w-4 h-4" style={{ color: UI.goldInk }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3M3.05 11a9 9 0 111.5 5M3 16v-5h5" />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-bold" style={{ color: UI.ink, fontFamily: "'Fraunces', Georgia, serif" }}>Historique des versions</p>
            <p className="text-[10.5px]" style={{ color: UI.muted }}>Une version = une session d'édition terminée</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1.5" style={{ color: UI.muted }} aria-label="Fermer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
          {q.isLoading && <p className="text-xs px-2 py-4" style={{ color: UI.muted }}>Chargement…</p>}
          {!q.isLoading && checkpoints.length === 0 && (
            <div className="text-center px-4 py-10">
              <p className="text-[13px] font-semibold mb-1" style={{ color: UI.ink }}>Aucune version pour l'instant</p>
              <p className="text-[11.5px]" style={{ color: UI.muted }}>
                Une version est enregistrée automatiquement à la fin de chaque session d'édition (après ~20 min d'inactivité).
              </p>
            </div>
          )}
          {checkpoints.map((cp) => (
            <div
              key={cp.id}
              className="rounded-xl px-3 py-2.5 flex items-center gap-3"
              style={{
                border: `1px solid ${cp.is_current ? COLORS.gold : `${COLORS.gold}25`}`,
                background: cp.is_current ? `${COLORS.gold}0A` : UI.surface,
              }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold" style={{ color: UI.ink }}>
                  {fmtWhen(cp.snapshot_at)}
                  {cp.is_current && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: `${COLORS.gold}20`, color: UI.goldInk }}>actuelle</span>}
                  {cp.trigger === 'pre-restore' && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-[#6366f1] dark:text-[#A5B4FC]" style={{ background: '#6366f115' }}>avant restauration</span>}
                  {cp.trigger === 'pre-destructive' && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-[#B45309] dark:text-status-waiting" style={{ background: '#B4530915' }}>avant suppression</span>}
                  {cp.trigger === 'pre-relayout' && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-[#B45309] dark:text-status-waiting" style={{ background: '#B4530915' }}>avant régénération</span>}
                  {cp.trigger === 'pre-note' && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-[#6366f1] dark:text-[#A5B4FC]" style={{ background: '#6366f115' }}>avant note client</span>}
                  {cp.trigger === 'pre-agent' && <span className="ml-2 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-[#6366f1] dark:text-[#A5B4FC]" style={{ background: '#6366f115' }}>avant l'assistant</span>}
                </p>
                <p className="text-[10.5px]" style={{ color: UI.muted }}>
                  {fmtAgo(cp.snapshot_at)} · {cp.page_count} pages · {cp.photo_count} photos
                </p>
              </div>
              {!cp.is_current && (
                <button
                  onClick={() => setConfirming({ id: cp.id, when: fmtWhen(cp.snapshot_at) })}
                  className="shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold"
                  style={{ border: `1px solid ${COLORS.gold}50`, color: UI.goldInk, background: UI.surface }}
                >
                  Restaurer
                </button>
              )}
            </div>
          ))}
        </div>

        {err && <p className="px-4 pb-2 text-[11px]" style={{ color: UI.danger }}>{err}</p>}
      </div>

      {/* Confirm modal — restore is never one click. */}
      {confirming && (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-4" onClick={() => !restoring && setConfirming(null)}>
          <div className="absolute inset-0" style={{ background: 'rgba(17,17,17,.35)' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[15px] font-bold mb-1.5" style={{ color: UI.ink, fontFamily: "'Fraunces', Georgia, serif" }}>
              Restaurer cette version ?
            </p>
            <p className="text-[12.5px] mb-1" style={{ color: UI.ink }}>
              L'album reviendra à l'état du <b>{confirming.when}</b>.
            </p>
            <p className="text-[11.5px] mb-4" style={{ color: UI.muted }}>
              L'état actuel sera remplacé — une copie de sécurité est créée automatiquement, vous pourrez donc revenir en arrière. <b>Le PDF n'est PAS régénéré</b> : cliquez Régénérer quand l'album est le bon.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirming(null)}
                disabled={restoring}
                className="px-3.5 py-2 rounded-lg text-[12.5px] font-medium"
                style={{ border: `1px solid ${COLORS.gold}30`, color: UI.ink }}
              >
                Annuler
              </button>
              <button
                onClick={doRestore}
                disabled={restoring}
                className="px-4 py-2 rounded-lg text-[12.5px] font-bold disabled:opacity-60"
                style={{ background: COLORS.gold, color: COLORS.white }}
              >
                {restoring ? 'Restauration…' : 'Oui, restaurer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Spine Editor Panel ───────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// Small native <select> styled to match the sidebar inputs. Each option is
// rendered in its own typeface so the customer previews the look before
// picking. Empty string means "use the template/inherit default".
function FontSelect({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (family: string) => void;
  label?: string;
}) {
  // Fix #11 (2026-05-28): Londrina Solid is the default. Legacy data may
  // still carry an empty-string value — treat that as Londrina Solid so the
  // dropdown shows the right label, and emit the real family string on the
  // next change so re-renders / reloads round-trip the value.
  const LONDRINA_FAMILY = EDITOR_TEXT_FONTS[0].family; // 'Londrina Solid'
  const effectiveValue = value || LONDRINA_FAMILY;
  return (
    <div className="mt-1.5">
      {label && (
        <div className="text-[10px] mb-1" style={{ color: UI.muted }}>{label}</div>
      )}
      <select
        value={effectiveValue}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none cursor-pointer"
        style={{
          border: `1px solid ${COLORS.gold}30`,
          background: UI.panel,
          color: UI.ink,
          fontFamily: effectiveValue,
        }}
      >
        {EDITOR_TEXT_FONTS.map((f) => (
          <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
            {f.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SpineEditor({
  config,
  onChange,
  coverDestText,
  onCoverDestTextChange,
  coverYearText,
  onCoverYearTextChange,
  onCoverTitleBlur,
  frontCoverBgColor,
  onFrontCoverBgColorChange,
  backCoverBgColor,
  onBackCoverBgColorChange,
  hideFrontCoverImage,
  onHideFrontCoverImageChange,
  isAdmin,
  coverDestFontFamily,
  onCoverDestFontFamilyChange,
  coverYearFontFamily,
  onCoverYearFontFamilyChange,
  extraZones,
  onExtraZoneAdd,
  onExtraZoneChange,
  onExtraZoneDelete,
}: {
  config: SpineConfig;
  onChange: (c: SpineConfig) => void;
  editing?: boolean;
  onToggleEditing?: (v: boolean) => void;
  coverDestText: string;
  onCoverDestTextChange: (v: string) => void;
  coverYearText: string;
  onCoverYearTextChange: (v: string) => void;
  /** Restores the mandatory cover title if a customer blurs it empty (Fix 2). */
  onCoverTitleBlur?: (type: 'destination' | 'year') => void;
  frontCoverBgColor: string;
  onFrontCoverBgColorChange: (c: string) => void;
  backCoverBgColor: string;
  onBackCoverBgColorChange: (c: string) => void;
  hideFrontCoverImage: boolean;
  onHideFrontCoverImageChange: (v: boolean) => void;
  /** ADMIN-ONLY gate for the design-hide toggle. Customers cannot remove the
   *  cover illustration (#3). */
  isAdmin: boolean;
  coverDestFontFamily: string;
  onCoverDestFontFamilyChange: (f: string) => void;
  coverYearFontFamily: string;
  onCoverYearFontFamilyChange: (f: string) => void;
  /** Free "extra" cover texts (sous-titre, dédicace…) — same ExtraTextZone
   *  the cover already renders + embeds in the PDF; the sidebar just adds the
   *  add + type-the-words UI the editor was missing. */
  extraZones: ExtraTextZone[];
  onExtraZoneAdd: () => void;
  onExtraZoneChange: (i: number, ez: ExtraTextZone) => void;
  onExtraZoneDelete: (i: number) => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div
        className="px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${COLORS.gold}15` }}
      >
        <h3
          className="text-sm font-semibold"
          style={{
            color: UI.ink,
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontSize: 16,
          }}
        >
          Tranche & Couverture
        </h3>
        <p className="text-[10px] mt-0.5" style={{ color: UI.muted }}>
          Personnalisez la tranche de votre album
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Cover face colors — front / spine / back grouped at the top so
            the customer can recolor each face in one place. */}
        <div className="rounded-lg p-3" style={{ background: `${COLORS.gold}08`, border: `1px solid ${COLORS.gold}20` }}>
          <div className="text-[11px] font-medium mb-2" style={{ color: UI.ink }}>
            Couleurs de la couverture
          </div>

          {/* Front cover */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[11px]" style={{ color: UI.muted }}>Avant</span>
            <div className="flex items-center gap-1.5">
              {/* ADMIN-ONLY: only an admin may remove/hide the cover
                  illustration (#3). Customers keep the colour picker only. */}
              {isAdmin && (
              <button
                onClick={() => onHideFrontCoverImageChange(!hideFrontCoverImage)}
                className="px-2 py-1 rounded-md text-[10px]"
                style={{
                  background: hideFrontCoverImage ? COLORS.gold : `${COLORS.gold}15`,
                  color: hideFrontCoverImage ? COLORS.white : UI.ink,
                }}
                title={hideFrontCoverImage ? 'Afficher l’image' : 'Masquer l’image'}
              >
                {hideFrontCoverImage ? 'Sans image' : 'Avec image'}
              </button>
              )}
              <label
                className="w-7 h-7 rounded-md border cursor-pointer overflow-hidden flex items-center justify-center"
                style={{ borderColor: `${COLORS.gold}30`, background: frontCoverBgColor, padding: 0 }}
              >
                <input
                  type="color"
                  value={frontCoverBgColor}
                  onChange={(e) => onFrontCoverBgColorChange(e.target.value)}
                  className="opacity-0 w-full h-full cursor-pointer"
                />
              </label>
            </div>
          </div>

          {/* Spine */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[11px]" style={{ color: UI.muted }}>Tranche</span>
            <label
              className="w-7 h-7 rounded-md border cursor-pointer overflow-hidden flex items-center justify-center"
              style={{ borderColor: `${COLORS.gold}30`, background: config.bgColor, padding: 0 }}
            >
              <input
                type="color"
                value={config.bgColor}
                onChange={(e) => onChange({ ...config, bgColor: e.target.value })}
                className="opacity-0 w-full h-full cursor-pointer"
              />
            </label>
          </div>

          {/* Back cover */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px]" style={{ color: UI.muted }}>Arrière</span>
            <label
              className="w-7 h-7 rounded-md border cursor-pointer overflow-hidden flex items-center justify-center"
              style={{ borderColor: `${COLORS.gold}30`, background: backCoverBgColor, padding: 0 }}
            >
              <input
                type="color"
                value={backCoverBgColor}
                onChange={(e) => onBackCoverBgColorChange(e.target.value)}
                className="opacity-0 w-full h-full cursor-pointer"
              />
            </label>
          </div>
        </div>

        {/* Cover destination text */}
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: UI.ink }}>
            Texte couverture
          </label>
          <input
            type="text"
            value={coverDestText}
            onChange={(e) => onCoverDestTextChange(e.target.value)}
            onBlur={() => onCoverTitleBlur?.('destination')}
            placeholder="BALI"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
            style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink }}
          />
          <FontSelect
            value={coverDestFontFamily}
            onChange={onCoverDestFontFamilyChange}
            label="Police"
          />
        </div>

        {/* Cover year text */}
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: UI.ink }}>
            Année couverture
          </label>
          <input
            type="text"
            value={coverYearText}
            onChange={(e) => onCoverYearTextChange(e.target.value)}
            onBlur={() => onCoverTitleBlur?.('year')}
            placeholder="2025"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
            style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink }}
          />
          <FontSelect
            value={coverYearFontFamily}
            onChange={onCoverYearFontFamilyChange}
            label="Police"
          />
        </div>

        {/* Extra cover texts — sous-titre / dédicace under the title. Reuses the
            cover's own ExtraTextZone (already drawn in the editor, preview AND
            embedded in the PDF); this panel just adds the "add + type" UI the
            editor lacked. Each text is draggable on the cover; select it to
            change size/police via the bottom toolbar. */}
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: UI.ink }}>
            Texte en plus (sous-titre, dédicace…)
          </label>

          {extraZones.length > 0 && (
            <div className="space-y-2 mb-2">
              {extraZones.map((ez, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={ez.text}
                    onChange={(e) => onExtraZoneChange(i, { ...ez, text: e.target.value })}
                    placeholder="Votre texte…"
                    autoFocus={!ez.text}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                    style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink }}
                  />
                  <label
                    className="w-8 h-8 rounded-lg border cursor-pointer overflow-hidden shrink-0 flex items-center justify-center"
                    style={{ borderColor: `${COLORS.gold}40`, background: ez.color }}
                    title="Couleur du texte"
                  >
                    <input type="color" value={ez.color}
                      onChange={(e) => onExtraZoneChange(i, { ...ez, color: e.target.value })}
                      className="opacity-0 w-full h-full cursor-pointer" />
                  </label>
                  <button
                    onClick={() => onExtraZoneDelete(i)}
                    className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center hover:opacity-80"
                    style={{ border: `1px solid ${COLORS.gold}30`, color: UI.danger }}
                    title="Supprimer ce texte" aria-label="Supprimer ce texte"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-1 0v12a1 1 0 01-1 1H9a1 1 0 01-1-1V7" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={onExtraZoneAdd}
            className="w-full py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
            style={{ border: `1px dashed ${COLORS.gold}66`, color: COLORS.gold, background: 'transparent' }}
          >
            + Ajouter un texte
          </button>
          <p className="text-[10px] mt-1.5" style={{ color: UI.muted }}>
            Apparaît sous le titre. Glissez-le sur la couverture pour le placer; sélectionnez-le pour changer la taille et la police.
          </p>
        </div>

        {/* Spine text */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Texte de la tranche
          </label>
          <input
            type="text"
            value={config.text}
            onChange={(e) => onChange({ ...config, text: e.target.value })}
            placeholder="BALI"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
            style={{
              border: `1px solid ${COLORS.gold}30`,
              background: UI.panel,
              color: UI.ink,
            }}
          />
          <FontSelect
            value={config.fontFamily ?? ''}
            onChange={(f) => onChange({ ...config, fontFamily: f })}
            label="Police"
          />
        </div>

        {/* Year */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Annee
          </label>
          <input
            type="text"
            value={config.year}
            onChange={(e) => onChange({ ...config, year: e.target.value })}
            placeholder="2023"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
            style={{
              border: `1px solid ${COLORS.gold}30`,
              background: UI.panel,
              color: UI.ink,
            }}
          />
        </div>

        {/* Background color */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Couleur de fond
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.bgColor}
              onChange={(e) =>
                onChange({ ...config, bgColor: e.target.value })
              }
              className="w-8 h-8 rounded-md border-0 cursor-pointer"
              style={{ padding: 0 }}
            />
            <input
              type="text"
              value={config.bgColor}
              onChange={(e) =>
                onChange({ ...config, bgColor: e.target.value })
              }
              className="flex-1 px-3 py-1.5 rounded-lg text-xs font-mono outline-none"
              style={{
                border: `1px solid ${COLORS.gold}30`,
                background: UI.panel,
                color: UI.ink,
              }}
            />
          </div>
        </div>

        {/* Text color */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Couleur du texte
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.textColor}
              onChange={(e) =>
                onChange({ ...config, textColor: e.target.value })
              }
              className="w-8 h-8 rounded-md border-0 cursor-pointer"
              style={{ padding: 0 }}
            />
            <input
              type="text"
              value={config.textColor}
              onChange={(e) =>
                onChange({ ...config, textColor: e.target.value })
              }
              className="flex-1 px-3 py-1.5 rounded-lg text-xs font-mono outline-none"
              style={{
                border: `1px solid ${COLORS.gold}30`,
                background: UI.panel,
                color: UI.ink,
              }}
            />
          </div>
        </div>

        {/* Year color */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Couleur de l'annee
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.yearColor}
              onChange={(e) =>
                onChange({ ...config, yearColor: e.target.value })
              }
              className="w-8 h-8 rounded-md border-0 cursor-pointer"
              style={{ padding: 0 }}
            />
            <input
              type="text"
              value={config.yearColor}
              onChange={(e) =>
                onChange({ ...config, yearColor: e.target.value })
              }
              className="flex-1 px-3 py-1.5 rounded-lg text-xs font-mono outline-none"
              style={{
                border: `1px solid ${COLORS.gold}30`,
                background: UI.panel,
                color: UI.ink,
              }}
            />
          </div>
        </div>

        {/* Font size */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Taille du texte: {config.fontSize}px
          </label>
          <input
            type="range"
            min={12}
            max={48}
            value={config.fontSize}
            onChange={(e) =>
              onChange({ ...config, fontSize: parseInt(e.target.value) })
            }
            className="w-full accent-[#C9A24A]"
          />
        </div>

        {/* Year font size */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Taille de l'annee: {config.yearFontSize}px
          </label>
          <input
            type="range"
            min={8}
            max={24}
            value={config.yearFontSize}
            onChange={(e) =>
              onChange({
                ...config,
                yearFontSize: parseInt(e.target.value),
              })
            }
            className="w-full accent-[#C9A24A]"
          />
        </div>

        {/* Scale */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Echelle: {Math.round(config.scale * 100)}%
          </label>
          <input
            type="range"
            min={50}
            max={200}
            value={config.scale * 100}
            onChange={(e) =>
              onChange({ ...config, scale: parseInt(e.target.value) / 100 })
            }
            className="w-full accent-[#C9A24A]"
          />
        </div>

        {/* Rotation */}
        <div>
          <label
            className="block text-[11px] font-medium mb-1.5"
            style={{ color: UI.ink }}
          >
            Rotation: {config.rotation}deg
          </label>
          <input
            type="range"
            min={-180}
            max={180}
            value={config.rotation}
            onChange={(e) =>
              onChange({ ...config, rotation: parseInt(e.target.value) })
            }
            className="w-full accent-[#C9A24A]"
          />
        </div>

        {/* Quick color presets */}
        <div>
          <label
            className="block text-[11px] font-medium mb-2"
            style={{ color: UI.ink }}
          >
            Couleurs rapides
          </label>
          <div className="flex flex-wrap gap-1.5">
            {[
              '#D35400',
              '#C9A24A',
              '#1A1A1A',
              '#2C3E50',
              '#8E44AD',
              '#27AE60',
              '#E74C3C',
              '#2980B9',
              '#F39C12',
              '#1ABC9C',
            ].map((color) => (
              <button
                key={color}
                className="w-7 h-7 rounded-md transition-all hover:scale-110"
                style={{
                  background: color,
                  outline:
                    config.bgColor === color
                      ? `2px solid ${COLORS.gold}`
                      : '1px solid rgba(0,0,0,0.1)',
                  outlineOffset: 1,
                }}
                onClick={() => onChange({ ...config, bgColor: color })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── VirtualPhotoGrid (Batch 1 perf) ───────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// Windowed square-tile grid for the photo-LIST surfaces (desktop tray, slot
// chooser, mobile "Mes photos"). With 158 photos the old DOM mounted ALL tiles
// at once — heavy on iOS, which then discarded (reloaded) the page. This mounts
// only the on-screen rows (+overscan), so the DOM stays tiny regardless of count.
//
// Column count is derived from the live container width (ResizeObserver) and a
// per-surface `minTile` floor — mirrors the old CSS `repeat(auto-fill, minmax(..))`.
// Each row renders `columns` tiles via the `renderTile` render-prop, so each
// surface keeps its exact tile look (square, checkmark/placed overlay, drag +
// selection handlers). `gap` matches the old grid gutters.

interface VirtualGridRowProps {
  columns: number;
  count: number;
  gap: number;
  renderTile: (index: number) => ReactNode;
}

// react-window v2 row component: renders the `columns` tiles for one row as a
// flex line. Memo-friendly — react-window only re-renders rows whose props
// (rowProps) change.
function VirtualGridRow({
  index: rowIndex,
  style,
  columns,
  count,
  gap,
  renderTile,
}: RowComponentProps<VirtualGridRowProps>) {
  const start = rowIndex * columns;
  const tiles: ReactNode[] = [];
  for (let col = 0; col < columns; col++) {
    const i = start + col;
    if (i >= count) break;
    tiles.push(
      <div key={i} style={{ flex: `0 0 calc((100% - ${(columns - 1) * gap}px) / ${columns})`, minWidth: 0 }}>
        {renderTile(i)}
      </div>,
    );
  }
  return (
    <div style={{ ...style, display: 'flex', gap, paddingBottom: gap, alignItems: 'flex-start' }}>
      {tiles}
    </div>
  );
}

function VirtualPhotoGrid({
  count,
  minTile,
  gap,
  renderTile,
  className,
}: {
  count: number;
  /** Minimum tile width in px — column count = floor((width+gap)/(minTile+gap)). */
  minTile: number;
  gap: number;
  renderTile: (index: number) => ReactNode;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  // react-window v2 needs a NUMERIC pixel height to build its scroll viewport.
  // height:'100%' made it render ALL rows in an overflow:visible box → no scroll
  // on the mobile sheet. Measure the container's pixel height + feed it numerically.
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) {
        if (cr.width > 0) setWidth(cr.width);
        if (cr.height > 0) setHeight(cr.height);
      }
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    setHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  const columns = Math.max(1, Math.floor((width + gap) / (minTile + gap)));
  const rowCount = Math.ceil(count / columns);
  // Square tiles: tile width = (width - (columns-1)*gap) / columns; row height
  // = tile height (square) + the gap we add as paddingBottom on each row.
  const tileW = columns > 0 ? (width - (columns - 1) * gap) / columns : minTile;
  const rowHeight = Math.max(1, Math.round(tileW + gap));

  const rowProps = useMemo<VirtualGridRowProps>(
    () => ({ columns, count, gap, renderTile }),
    [columns, count, gap, renderTile],
  );

  return (
    <div ref={wrapRef} className={className} style={{ height: '100%', width: '100%' }}>
      {width > 0 && height > 0 && (
        <VirtualList
          rowComponent={VirtualGridRow}
          rowCount={rowCount}
          rowHeight={rowHeight}
          rowProps={rowProps}
          overscanCount={2}
          style={{ height, width: '100%' }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Images Sidebar ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function ImagesSidebar({
  hiddenDuplicates,
  onRestoredDuplicate,
  allPhotos,
  placedPhotoIds,
  unplacedCount,
  onDragStart,
  onDragEnd,
  onUploadMore,
  onDeletePhoto,
  onRotatePhoto,
  rotatingPhotoId,
  onClearAll,
  onDeleteAll,
  deletingAll,
  onAutoOrientAll,
  autoOrientProgress,
  isAdmin,
  onNormalizeAll,
  normalizeProgress,
  visionProgress,
  onTouchDragStart,
  useUploadOrder,
  onToggleUploadOrder,
  onPickForTarget,
  onCancelPick,
}: {
  hiddenDuplicates?: HiddenDuplicatePhoto[];
  onRestoredDuplicate?: () => void;
  allPhotos: PhotoWithUrl[];
  placedPhotoIds: Set<string>;
  unplacedCount: number;
  draggingPhotoId?: string | null;
  onDragStart: (photoId: string) => void;
  onDragEnd: () => void;
  onUploadMore: () => void;
  onDeletePhoto: (id: string) => void;
  onRotatePhoto: (id: string) => void;
  rotatingPhotoId: string | null;
  onClearAll: () => void;
  /** « Supprimer toutes les photos » (owner, 2026-10-26): deletes every photo of
   *  this album — placed and unplaced — keeps the pages. « Vider » only unplaces. */
  onDeleteAll?: () => Promise<void> | void;
  deletingAll?: boolean;
  onAutoOrientAll: () => void;
  autoOrientProgress: { done: number; total: number } | null;
  isAdmin?: boolean;
  onNormalizeAll?: () => void;
  normalizeProgress?: { done: number; total: number } | null;
  visionProgress: { phase: 'running' | 'done'; total: number; analyzed: number; cached: number; failed: number } | null;
  onTouchDragStart?: (photoId: string, signedUrl: string | null, e: React.TouchEvent) => void;
  useUploadOrder?: boolean;
  onToggleUploadOrder?: (next: boolean) => void;
  /** DESKTOP pick mode (2026-07-08): set while an empty photo ELEMENT on the
   *  canvas awaits a photo — clicking any tile fills it. undefined = normal. */
  onPickForTarget?: (photoId: string) => void;
  onCancelPick?: () => void;
}) {
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const isAutoOrienting = autoOrientProgress !== null;
  const isNormalizing = !!normalizeProgress;

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2.5" style={{ borderBottom: `1px solid ${COLORS.gold}10` }}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold" style={{ color: UI.ink }}>
            PHOTOS ({allPhotos.length})
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={onUploadMore}
              className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-all"
              style={{ background: `${COLORS.gold}15`, color: COLORS.gold, border: `1px solid ${COLORS.gold}30` }}
              title="Ajouter des photos"
            >
              + Ajouter
            </button>
            <button
              onClick={() => setConfirmClear(true)}
              className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-all"
              style={{ color: UI.danger, border: `1px solid ${COLORS.danger}30` }}
              title="Retirer toutes les photos des pages"
            >
              Vider
            </button>
          </div>
        </div>
        <p className="text-[10px] mt-0.5" style={{ color: UI.muted }}>
          {placedPhotoIds.size} placees · {unplacedCount} non placees
        </p>
        {onDeleteAll && allPhotos.length > 0 && (
          <button
            onClick={() => setConfirmDeleteAll(true)}
            disabled={deletingAll}
            className="mt-1.5 w-full px-2 py-1 rounded text-[10px] font-medium transition-all disabled:opacity-50"
            style={{ color: UI.danger, border: `1px solid ${COLORS.danger}30` }}
            title="Supprimer définitivement toutes les photos de cet album (les pages restent)"
          >
            {deletingAll ? 'Suppression…' : 'Supprimer toutes les photos'}
          </button>
        )}

        {/* Vision analysis runs automatically when the editor mounts; this
            pill is purely a status indicator. Hidden when there's nothing
            to report — no idle CTA that the customer might feel pressured
            to press. */}
        {visionProgress && (
          <div
            className="mt-2 w-full px-2 py-1 rounded text-[10px] font-medium flex items-center justify-center gap-1 text-[#6366f1] dark:text-[#A5B4FC]"
            // night: same indigo wash, light indigo text (AA)
            style={{ background: '#6366f110', border: `1px solid #6366f140` }}
            title="Analyse IA des photos (Gemini Flash)"
          >
            {visionProgress.phase === 'running' ? (
              <>
                <span className="inline-block w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#6366f180', borderTopColor: 'transparent' }} />
                Analyse IA en cours…
              </>
            ) : (
              <>
                ✓ {visionProgress.analyzed} analysée{visionProgress.analyzed > 1 ? 's' : ''}
                {visionProgress.cached > 0 ? `, ${visionProgress.cached} déjà fait` : ''}
                {visionProgress.failed > 0 ? `, ${visionProgress.failed} échec` : ''}
              </>
            )}
          </div>
        )}

        {/* Batch auto-orient — scans every photo, runs face detection at 4
            rotations, and rotates the ones where a non-zero orientation
            has clearly stronger face confidence. Single-tap fix for the
            "this photo is upside down" complaint. Disabled while a single
            slot is already rotating or a batch is in flight. */}
        <button
          onClick={onAutoOrientAll}
          disabled={isAutoOrienting || !!rotatingPhotoId || allPhotos.length === 0}
          className="mt-2 w-full px-2 py-1 rounded text-[10px] font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1"
          style={{ background: `${COLORS.gold}10`, color: COLORS.gold, border: `1px solid ${COLORS.gold}25` }}
          title="Detecter les visages et redresser les photos a l'envers"
        >
          {isAutoOrienting ? (
            <>
              <span className="inline-block w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${COLORS.gold}80`, borderTopColor: 'transparent' }} />
              Redressement {autoOrientProgress!.done}/{autoOrientProgress!.total}…
            </>
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 9A9 9 0 005.6 5.6M4 15a9 9 0 0014.4 3.4" />
              </svg>
              Pivoter auto
            </>
          )}
        </button>

        {/* Admin-only: re-store every photo's full-res original as the upright
            pixels shown on screen — fixes legacy orders (e.g. #1079) whose
            regenerate PDF prints sideways. Hidden from customers. */}
        {isAdmin && onNormalizeAll && (
          <button
            onClick={onNormalizeAll}
            disabled={isNormalizing || isAutoOrienting || !!rotatingPhotoId || allPhotos.length === 0}
            className="mt-1.5 w-full px-2 py-1 rounded text-[10px] font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-[#6366f1] dark:text-[#A5B4FC]"
            style={{ background: '#6366f110', border: '1px solid #6366f140' }}
            title="ADMIN — redresse l'original de chaque photo pour que le PDF régénéré corresponde à l'éditeur (qualité préservée)"
          >
            {isNormalizing ? (
              <>
                <span className="inline-block w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#6366f180', borderTopColor: 'transparent' }} />
                Redressement PDF {normalizeProgress!.done}/{normalizeProgress!.total}…
              </>
            ) : (
              <>🛠️ Redresser pour PDF (admin)</>
            )}
          </button>
        )}

        {/* Admin-only: lay photos out in UPLOAD ORDER (the order the client
            added them) instead of the date-based ordering. Default OFF.
            Persisted to the session so the next Régénérer honours it. When ON,
            the worker forces the deterministic engine (no AI similarity
            regrouping) and the frontend skips the reordering passes — faces
            still get face-aware crops + 1-up pages. Full-width control matching
            its siblings; FILLED gold when active so it clearly reads as ON. */}
        {isAdmin && onToggleUploadOrder && (
          <label
            className="mt-1.5 w-full px-2 py-1.5 rounded text-[10px] font-medium transition-all flex items-center gap-2 cursor-pointer select-none"
            style={
              useUploadOrder
                ? { background: `${COLORS.gold}18`, color: UI.goldInk, border: `1px solid ${COLORS.gold}55` }
                : { background: `${COLORS.gold}08`, color: COLORS.gold, border: `1px solid ${COLORS.gold}25` }
            }
            title="Place les photos dans l'ordre exact où le client les a ajoutées (ignore le tri par date). À appliquer avant Régénérer."
          >
            <input
              type="checkbox"
              checked={useUploadOrder}
              onChange={(e) => onToggleUploadOrder(e.target.checked)}
              className="shrink-0 w-3.5 h-3.5 cursor-pointer"
              style={{ accentColor: COLORS.gold }}
            />
            <span>Ordre d'upload</span>
          </label>
        )}

        {/* Delete-all confirmation — permanent, unlike « Vider » */}
        {confirmDeleteAll && onDeleteAll && (
          <div className="mt-2 p-2 rounded-lg" style={{ background: `${COLORS.danger}08`, border: `1px solid ${COLORS.danger}25` }}>
            <p className="text-[10px] mb-1.5" style={{ color: UI.danger }}>
              Supprimer définitivement les {allPhotos.length} photos de cet album ? Les pages restent, vides.
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setConfirmDeleteAll(false)} className="flex-1 px-2 py-1 rounded text-[9px] font-medium" style={{ color: UI.muted, border: `1px solid ${COLORS.gold}25` }}>
                Annuler
              </button>
              <button onClick={() => { setConfirmDeleteAll(false); void onDeleteAll(); }} className="flex-1 px-2 py-1 rounded text-[9px] font-medium" style={{ background: COLORS.danger, color: COLORS.white }}>
                Supprimer
              </button>
            </div>
          </div>
        )}
        {/* Clear confirmation */}
        {confirmClear && (
          <div className="mt-2 p-2 rounded-lg" style={{ background: `${COLORS.danger}08`, border: `1px solid ${COLORS.danger}25` }}>
            <p className="text-[10px] mb-1.5" style={{ color: UI.danger }}>Retirer toutes les photos des pages ?</p>
            <div className="flex gap-1.5">
              <button onClick={() => setConfirmClear(false)} className="flex-1 px-2 py-1 rounded text-[9px] font-medium" style={{ color: UI.muted, border: `1px solid ${COLORS.gold}25` }}>
                Annuler
              </button>
              <button onClick={() => { onClearAll(); setConfirmClear(false); }} className="flex-1 px-2 py-1 rounded text-[9px] font-medium" style={{ background: COLORS.danger, color: COLORS.white }}>
                Confirmer
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Pick-mode banner — an empty photo frame on the canvas awaits a photo */}
      {onPickForTarget && (
        <div className="mx-2 mt-2 px-2.5 py-2 rounded-lg flex items-center gap-2"
          style={{ background: `${COLORS.gold}12`, border: `1px solid ${COLORS.gold}35` }}>
          <span className="text-[10px] font-semibold leading-tight flex-1" style={{ color: UI.goldInk }}>
            Cliquez une photo pour remplir le cadre
          </span>
          {onCancelPick && (
            <button onClick={onCancelPick} className="text-[10px] underline shrink-0" style={{ color: UI.muted }}>
              Annuler
            </button>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0 p-2">
        {/* Square-thumbnail gallery; VIRTUALIZED — only on-screen rows mount
            (was all 158 → iOS reloads). Column count adapts to the panel width
            (≈3-4 cols at the ~256px desktop sidebar, minTile 64px = the old 4rem
            auto-fill floor). The tile look + drag/select handlers are unchanged. */}
        <VirtualPhotoGrid
          count={allPhotos.length}
          minTile={64}
          gap={6}
          renderTile={(i) => {
            const photo = allPhotos[i];
            if (!photo) return null;
            return (
              <PhotoThumbnail
                photo={photo}
                isPlaced={placedPhotoIds.has(photo.id)}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDelete={onDeletePhoto}
                onRotate={onRotatePhoto}
                isRotating={rotatingPhotoId === photo.id}
                onTouchDragStart={onTouchDragStart}
                onPick={onPickForTarget}
              />
            );
          }}
        />
      </div>
      <HiddenDuplicatesSection photos={hiddenDuplicates ?? []} onRestored={() => onRestoredDuplicate?.()} />
    </div>
  );
}

// Wrapped in React.memo (Batch 1 perf) so the virtualized tray stops
// re-rendering every tile on each drag/touch/edit — only tiles whose props
// actually change re-render. For memo to bite, the parent passes STABLE
// (useCallback'd) id-based callbacks: onDelete/onRotate now take the photo id
// so the parent never has to allocate a per-tile inline closure.
const PhotoThumbnail = memo(function PhotoThumbnail({
  photo,
  isPlaced,
  onDragStart,
  onDragEnd,
  onDelete,
  onRotate,
  isRotating,
  onTouchDragStart,
  onPick,
}: {
  photo: PhotoWithUrl;
  isPlaced: boolean;
  onDragStart: (photoId: string) => void;
  onDragEnd: () => void;
  onDelete?: (photoId: string) => void;
  onRotate?: (photoId: string) => void;
  isRotating?: boolean;
  /** Fix #7 (2026-05-28): touch-drag opt-in. AlbumEditorPage passes
   *  the `beginTouchDrag` from useTouchDragPhoto. The thumbnail just
   *  forwards the React touchstart event so the hook can read the
   *  finger position + create the floating clone. */
  onTouchDragStart?: (photoId: string, signedUrl: string | null, e: React.TouchEvent) => void;
  /** DESKTOP pick mode (2026-07-08): when an empty photo ELEMENT was clicked
   *  on the canvas, the sidebar enters pick mode — clicking a tile fills the
   *  element. Mirrors the mobile drawer's photoElementTarget behaviour, which
   *  desktop never had (clicking the frame looked like it did nothing). */
  onPick?: (photoId: string) => void;
}) {
  // Small (~320px) tray tier — the tile is ~80px, so it never needs the 1400px
  // working image (that download + decode was the iOS slowness). Falls back to
  // signed_url when the worker didn't mint a tray tier (HEIC / transform fail).
  const convertedUrl = useConvertedUrl(photo.tray_signed_url ?? photo.signed_url);
  // Don't spin forever. If the file is missing from storage (orphan row) or a
  // HEIC decode fails, show a "missing photo" tile after a grace period (or on
  // <img> error) instead of an endless spinner. Resets if the URL changes
  // (e.g. the customer re-uploads over it).
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [photo.tray_signed_url, photo.signed_url]);
  useEffect(() => {
    if (convertedUrl || failed) return;
    const t = setTimeout(() => setFailed(true), 15000);
    return () => clearTimeout(t);
  }, [convertedUrl, failed]);
  const dateLabel = photo.exif_date
    ? new Date(photo.exif_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })
    : '';

  return (
    // Night mode: a photo — a light island. The white backing is what a placed
    // (half-faded) photo blends with in light; on a dark panel it would look
    // dimmed instead (owner rule: photos never dimmed). Invisible in light.
    <div data-adm-media="" className="rounded-md" style={{ background: COLORS.white, color: COLORS.dark }}>
    <div
      className={cn(
        // Square via the padding-top:100% spacer below — NOT aspect-ratio
        // (older iOS Safari mishandles aspect-ratio → tall masonry cards).
        'relative w-full rounded-md overflow-hidden group cursor-grab transition-all',
        isPlaced && 'opacity-50',
      )}
      style={{
        background: `${COLORS.gold}08`,
        border: `1px solid ${COLORS.gold}15`,
      }}
      title={`${photo.original_filename ?? photo.id}${dateLabel ? ` — ${dateLabel}` : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('photoId', photo.id);
        e.dataTransfer.setData('photoSource', 'sidebar');
        e.dataTransfer.effectAllowed = 'copy';
        onDragStart(photo.id);
      }}
      onTouchStart={(e) => onTouchDragStart?.(photo.id, photo.signed_url ?? null, e)}
      onDragEnd={onDragEnd}
      onClick={onPick ? () => onPick(photo.id) : undefined}
      role={onPick ? 'button' : undefined}
    >
      <span aria-hidden className="block" style={{ paddingTop: '100%' }} />
      {failed ? (
        <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-1 px-1" style={{ background: `${COLORS.danger}0D` }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: UI.danger }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z M4 16l4-4 3 3 4-5 5 6 M3 3l18 18" />
          </svg>
          <span className="text-[7px] text-center leading-tight" style={{ color: UI.muted }}>Photo manquante</span>
        </div>
      ) : convertedUrl ? (
        <img
          src={convertedUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          // @ts-expect-error fetchpriority is valid HTML, missing from React types
          fetchpriority="low"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-1">
          <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: `${COLORS.gold}40`, borderTopColor: 'transparent' }} />
          <span className="text-[8px]" style={{ color: UI.muted }}>
            {isHeicFilename(photo.original_filename ?? '') ? 'HEIC' : '…'}
          </span>
        </div>
      )}
      {/* Rotate button on hover (top-left). Re-encodes the JPEG 90° CW and
          uploads back to the same path, so every consumer (editor, PDF) just
          works with upright pixels. Crucial for WhatsApp-shared photos that
          have no EXIF orientation to auto-rotate. */}
      {onRotate && (
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRotate(photo.id); }}
          disabled={isRotating}
          className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100 disabled:animate-spin"
          style={{ background: COLORS.gold, color: COLORS.white }}
          title="Pivoter cette photo de 90°"
        >
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 4v6h6M20 20v-6h-6M20 9A9 9 0 005.6 5.6M4 15a9 9 0 0014.4 3.4" />
          </svg>
        </button>
      )}
      {/* Delete button on hover */}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(photo.id); }}
          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: COLORS.danger, color: COLORS.white }}
          title="Supprimer cette photo"
        >
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {/* Date micro-label */}
      {dateLabel && !isPlaced && (
        <div className="absolute bottom-0 left-0 right-0 px-0.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: `${COLORS.dark}90` }}>
          <span className="text-[7px] text-white block truncate text-center">{dateLabel}</span>
        </div>
      )}
      {isPlaced && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-0.5"
          style={{ background: `${COLORS.success}CC` }}>
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Layouts Sidebar ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// Fix #8 (2026-05-28): tiny media-query hook. The Layouts sidebar's
// preview grid renders both sides of a spread by default, but on a
// 360px-wide phone that crops them into postage stamps. On mobile we
// render only the focused page's layout.
/** The live content size of an element (ResizeObserver). 0×0 until mounted. */
function useBoxSize(ref: React.RefObject<HTMLElement | null>): { w: number; h: number } {
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setSize((p) => (p.w === w && p.h === h ? p : { w, h }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function useIsMobile(maxWidthPx = 768): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [maxWidthPx]);
  return matches;
}

// ── Page-count slider (2026-07-07) ──────────────────────────────────────────
// Premium ÷4-snap slider for the album's total page count. Range 20–200, hard-
// snapped to multiples of 4 (print binds in signatures of 4). Shows a LIVE
// add-on price (pages beyond the bundle's base allowance × the per-page rate)
// while dragging; commits only on release (onCommit) so increasing/decreasing
// pages + the confirm-before-delete flow run once per gesture, not per pixel.
const PAGE_SLIDER_MIN = 20;
const PAGE_SLIDER_MAX = 300;
/** Drag snaps in 4s — fast, and every dragged value is already print-valid. */
const PAGE_SLIDER_STEP = 4;
/** The − / + buttons move ONE page, for fine control. The album really becomes
 *  that many pages (e.g. 77); the printer's "must divide by 4" rule is then
 *  satisfied by the existing print padding, which tops 77 up to 80 with outro
 *  duplicates at render time (pdfGenerator roundUpToMultipleOf4).
 *
 *  We deliberately do NOT snap on commit. Snapping would make "+1" jump by 4 and
 *  destroy the fine control that was the point of the change. Instead the panel
 *  shows the printed total live ("Imprimé : 80 pages"), so the rounding is
 *  visible before it happens rather than being a surprise afterwards.
 *  Pricing is unchanged: the customer is billed for the pages they chose (77),
 *  and the padding pages have never been billed. */
const PAGE_NUDGE_STEP = 1;
function clampPageCount(v: number): number {
  return Math.min(PAGE_SLIDER_MAX, Math.max(PAGE_SLIDER_MIN, Math.round(v)));
}
function snapPageCount(v: number): number {
  return clampPageCount(Math.round(v / PAGE_SLIDER_STEP) * PAGE_SLIDER_STEP);
}
/** The page count this album COMMITS to — the customer's pick snapped up to a
 *  multiple of 4. This is content only: it is what gets stored and what gets
 *  priced.
 *
 *  ⚠️ IT IS NOT THE PRINTED PAGE COUNT, and calling it `printedPageCount` was
 *  how the editor came to tell a #1198 customer "imprimé : 40" over a book the
 *  printer makes 44 pages long. The printed total adds the intro, the outro
 *  and the padding that reaches the next multiple of 4 — printedInteriorTotal
 *  in lib/printPageNumbers.ts owns that, and the label below uses it. */
function snapContentPagesTo4(v: number): number {
  const r = v % 4;
  return r === 0 ? v : v + (4 - r);
}
function PageCountSlider({
  pageCount,
  basePages,
  pricePerPage,
  hasIntroPage,
  hasOutroPage,
  onCommit,
  draft,
  onDraftChange,
}: {
  /** Current interior page count (localPages.length). */
  pageCount: number;
  /** Bundle base allowance (pages included, no surcharge). */
  basePages: number;
  /** Per extra-page rate in MAD. */
  pricePerPage: number;
  /** Whether the brand prints a Storiz intro / outro page. They are part of
   *  the printed interior, so they are part of the printed page count the
   *  label shows — see lib/printPageNumbers.ts. */
  hasIntroPage: boolean;
  hasOutroPage: boolean;
  /** Fired ONLY by Valider, with the ÷4-rounded-up target. */
  onCommit: (target: number) => void;
  /** Uncommitted value the customer is adjusting. null = in sync with the album. */
  draft: number | null;
  /** React setter — must accept an updater so rapid − / + clicks compose.
   *  Passing a plain value loses steps: React batches clicks fired in one task
   *  and every handler then reads the same stale `val`. */
  onDraftChange: React.Dispatch<React.SetStateAction<number | null>>;
}) {
  // DRAFT MODEL (2026-07-26). The draft lives in the PARENT (`draft` prop), not
  // here. Three bugs came from owning it locally:
  //  • the re-sync effect ran on pointer-release (`dragging` was a dependency),
  //    and on a DECREASE `applyPageCount` only opens a modal without touching
  //    localPages — so `pageCount` was still the old number and the effect
  //    slammed the slider back the instant you let go;
  //  • every − / + click called onCommit immediately, so the "réduire" modal
  //    fired mid-adjustment on a value the customer was only passing through;
  //  • the panel is conditionally rendered, so an uncommitted value died on a
  //    tab switch.
  // Now: nothing here commits anything. The parent holds the draft, and a
  // single "Valider" button is the one and only commit point.
  const val = draft ?? clampPageCount(pageCount);
  const setVal = (v: number) => onDraftChange(v);
  /** Step from the LATEST draft, not the one captured in this render. */
  const stepVal = (delta: number) =>
    onDraftChange((prev) => clampPageCount((prev ?? clampPageCount(pageCount)) + delta));
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const frac = (val - PAGE_SLIDER_MIN) / (PAGE_SLIDER_MAX - PAGE_SLIDER_MIN);
  // Price preview follows the PRINTED count, which is what the customer is
  // actually billed for once Valider snaps up to a multiple of 4.
  const committedVal = snapContentPagesTo4(val);
  const extraPages = Math.max(0, committedVal - basePages);
  const extraCost = extraPages * pricePerPage;
  const dirty = committedVal !== pageCount;

  const valAt = (clientX: number): number => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return val;
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    return snapPageCount(PAGE_SLIDER_MIN + p * (PAGE_SLIDER_MAX - PAGE_SLIDER_MIN));
  };
  const onDown = (e: React.PointerEvent) => {
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    setDragging(true);
    const nv = valAt(e.clientX);
    if (nv !== val) { setVal(nv); navigator.vibrate?.(8); }
  };
  const onMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const nv = valAt(e.clientX);
    if (nv !== val) { setVal(nv); navigator.vibrate?.(6); }
  };
  // Releasing the thumb applies NOTHING — it only ends the drag. Committing is
  // Valider's job alone.
  const onUp = () => { if (dragging) setDragging(false); };
  // − / + move by ONE page and only touch the draft.
  const nudge = (delta: number) => stepVal(delta);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: UI.muted }}>
          Nombre de pages
        </span>
        <span className="text-base font-bold tabular-nums" style={{ color: UI.ink }}>{val}</span>
      </div>
      {/* The printer works in blocks of 4, so Valider rounds UP to the next
          multiple of 4 (never down — that would silently delete a page the
          customer asked for). Shown live, before committing, so the number they
          get is never a surprise. */}
      {committedVal !== val && (
        <p className="mb-1.5 text-[10px] tabular-nums" style={{ color: UI.muted }}>
          Choisi : {val} → album {committedVal} pages, imprimé{' '}
          {printedInteriorTotal(committedVal, hasIntroPage, hasOutroPage)} pages
          (intro + outro, l’imprimeur travaille par 4)
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => nudge(-PAGE_NUDGE_STEP)}
          disabled={val <= PAGE_SLIDER_MIN}
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-lg disabled:opacity-30"
          style={{ background: `${COLORS.gold}14`, color: UI.ink }}
          aria-label="Retirer une page"
        >−</button>
        {/* Draggable track — 44px tall touch area, ÷4 snap. */}
        <div
          ref={trackRef}
          className="relative flex-1 h-11 flex items-center cursor-pointer select-none"
          style={{ touchAction: 'none' }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          role="slider"
          aria-valuemin={PAGE_SLIDER_MIN}
          aria-valuemax={PAGE_SLIDER_MAX}
          aria-valuenow={val}
          aria-label="Nombre de pages de l'album"
        >
          <div className="absolute left-0 right-0 h-1.5 rounded-full" style={{ background: `${COLORS.gold}22` }} />
          <div className="absolute left-0 h-1.5 rounded-full" style={{ width: `${frac * 100}%`, background: COLORS.gold }} />
          <div
            className="absolute rounded-full"
            style={{
              left: `calc(${frac * 100}% - 13px)`,
              width: 26, height: 26,
              background: UI.surface,
              border: `2.5px solid ${COLORS.gold}`,
              boxShadow: dragging ? '0 6px 16px rgba(0,0,0,0.28)' : '0 2px 6px rgba(0,0,0,0.16)',
              transform: dragging ? 'scale(1.12)' : 'scale(1)',
              transition: 'transform 120ms ease, box-shadow 120ms ease',
            }}
          />
        </div>
        <button
          onClick={() => nudge(PAGE_NUDGE_STEP)}
          disabled={val >= PAGE_SLIDER_MAX}
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-lg disabled:opacity-30"
          style={{ background: `${COLORS.gold}14`, color: UI.ink }}
          aria-label="Ajouter une page"
        >+</button>
      </div>
      {/* Live price */}
      <div
        className="mt-2.5 rounded-xl px-3 py-2 text-center"
        style={{ background: UI.panel, border: `1px solid ${COLORS.gold}20` }}
      >
        {extraPages > 0 ? (
          <span className="text-xs font-medium" style={{ color: UI.ink }}>
            +{extraPages} page{extraPages > 1 ? 's' : ''} supplémentaire{extraPages > 1 ? 's' : ''}
            {' · '}
            <span style={{ color: COLORS.gold, fontWeight: 700 }}>+{extraCost} MAD</span>
          </span>
        ) : (
          <span className="text-xs" style={{ color: UI.muted }}>Inclus dans votre formule</span>
        )}
      </div>
      {/* THE single commit point. Nothing above this button changes the album,
          shows a warning, or hits the network. Disabled until the draft differs
          from what the album already is, and labelled with the exact result so
          "Valider" is never a leap of faith. */}
      <button
        onClick={() => onCommit(committedVal)}
        disabled={!dirty}
        className="mt-2 w-full h-11 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-40"
        style={{ background: dirty ? COLORS.gold : `${COLORS.gold}30`, color: dirty ? COLORS.white : UI.muted }}
      >
        {dirty ? `Valider — ${committedVal} pages` : 'Aucun changement'}
      </button>
    </div>
  );
}

function LayoutsSidebar({
  selectedPage,
  pageCount,
  basePages,
  pricePerPage,
  hasIntroPage,
  hasOutroPage,
  onPageCountCommit,
  pageCountDraft,
  onPageCountDraftChange,
  onLayoutChange,
  onApplyToAll,
  layoutChoice,
  onLayoutChoiceChange,
  showRegenerateStyle = true,
}: {
  selectedPage: LayoutPage | null;
  pageCount: number;
  /** This ORDER's included-page allowance (`orders.base_pages_per_album`,
   *  migration 082) — NOT the current default. Drives the slider's live
   *  surcharge, so a legacy 50-page order must not be quoted against 36. */
  basePages: number;
  pricePerPage: number;
  /** Whether the brand prints a Storiz intro / outro page. They are part of
   *  the printed interior and therefore of every printed page number. */
  hasIntroPage: boolean;
  hasOutroPage: boolean;
  onPageCountCommit: (target: number) => void;
  /** Uncommitted page-count the customer is adjusting (parent-owned so it
   *  survives this panel unmounting on a tab switch / sheet dismiss). */
  pageCountDraft: number | null;
  onPageCountDraftChange: React.Dispatch<React.SetStateAction<number | null>>;
  onLayoutChange: (pageNumber: number, layout: '1-up' | '2-up' | '3-up' | '4-up') => void;
  onApplyToAll?: (layout: '1-up' | '2-up' | '3-up' | '4-up') => void;
  layoutChoice: 'one' | 'mixed';
  onLayoutChoiceChange: (choice: 'one' | 'mixed') => void;
  /** The « Style de mise en page » block drives the next « Régénérer » — off
   *  where the reader cannot regenerate (customer, new phone layout). */
  showRegenerateStyle?: boolean;
}) {
  const [confirmApplyAll, setConfirmApplyAll] = useState<'1-up' | '2-up' | '3-up' | '4-up' | null>(null);
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col h-full">
      <div
        className="px-3 py-2.5"
        style={{ borderBottom: `1px solid ${COLORS.gold}10` }}
      >
        <span
          className="text-xs font-semibold"
          style={{ color: UI.ink }}
        >
          MISES EN PAGE
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Total album page count — ÷4 snap, live add-on price. */}
        <div className="pb-3" style={{ borderBottom: `1px solid ${COLORS.gold}12` }}>
          <PageCountSlider
            pageCount={pageCount}
            basePages={basePages}
            pricePerPage={pricePerPage}
            hasIntroPage={hasIntroPage}
            hasOutroPage={hasOutroPage}
            onCommit={onPageCountCommit}
            draft={pageCountDraft}
            onDraftChange={onPageCountDraftChange}
          />
        </div>

        {/* Album layout style — the customer's checkout choice, editable here.
            Drives how "Régénérer" packs photos: one per page, or a 1–4 mix. */}
        {showRegenerateStyle && <div className="pb-3" style={{ borderBottom: `1px solid ${COLORS.gold}12` }}>
          <p className="text-[10px] font-medium mb-2" style={{ color: UI.ink }}>
            Style de mise en page
          </p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Style de mise en page">
            {([
              { key: 'one' as const,   title: '1 photo / page',   sub: 'Chaque photo en grand' },
              { key: 'mixed' as const, title: '1 à 4 / page',      sub: 'Agencement varié' },
            ]).map((opt) => {
              const active = layoutChoice === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onLayoutChoiceChange(opt.key)}
                  className="rounded-lg px-2 py-2 text-left transition-all"
                  style={{
                    border: active ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
                    background: active ? `${COLORS.gold}0F` : UI.panel,
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    {/* mini glyph: one full slot vs a 2×2 grid */}
                    {opt.key === 'one' ? (
                      <span className="inline-block rounded-sm" style={{ width: 12, height: 15, border: `1.5px solid ${active ? COLORS.gold : COLORS.muted}` }} />
                    ) : (
                      <span className="inline-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 1.5, width: 15, height: 15 }}>
                        {[0,1,2,3].map((i) => (
                          <span key={i} className="rounded-[1px]" style={{ background: active ? COLORS.gold : COLORS.muted, opacity: 0.85 }} />
                        ))}
                      </span>
                    )}
                    <span className="text-[11px] font-semibold" style={{ color: active ? COLORS.gold : UI.ink }}>
                      {opt.title}
                    </span>
                  </div>
                  <span className="text-[9px] leading-tight block" style={{ color: UI.muted }}>
                    {opt.sub}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[9px] mt-1.5 leading-snug" style={{ color: UI.muted }}>
            S'applique au prochain « Régénérer ».
          </p>
        </div>}

        {/* Apply selected page layout */}
        <div>
          <p className="text-[10px] font-medium mb-2" style={{ color: UI.muted }}>
            Page sélectionnée
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(['1-up', '2-up', '3-up', '4-up'] as const).map((layoutType) => {
              const isActive = selectedPage?.layout === layoutType;
              return (
                <button
                  key={`sel-${layoutType}`}
                  className="rounded-lg p-2 transition-all"
                  style={{
                    border: isActive ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
                    background: isActive ? `${COLORS.gold}08` : UI.panel,
                  }}
                  onClick={() => { if (selectedPage) onLayoutChange(selectedPage.page, layoutType); }}
                >
                  <div className="flex gap-0.5">
                    {/* Fix #8 (2026-05-28): on mobile, render ONLY the focused
                        page's mini-preview — the spread crop made each side
                        unreadable at phone widths. Desktop still shows both
                        sides so the customer sees what the spread will look
                        like end-to-end. */}
                    <LayoutMiniPreview layout={layoutType} />
                    {!isMobile && <LayoutMiniPreview layout={layoutType} />}
                  </div>
                  <p className="text-[9px] mt-1 text-center font-medium"
                    style={{ color: isActive ? COLORS.gold : UI.muted }}>
                    {layoutType === '1-up' ? '1 photo' : layoutType === '2-up' ? '2 photos' : layoutType === '3-up' ? '3 photos' : '4 photos'}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Apply to ALL pages */}
        {onApplyToAll && (
          <div style={{ borderTop: `1px solid ${COLORS.gold}15`, paddingTop: 12 }}>
            <p className="text-[10px] font-medium mb-2" style={{ color: UI.ink }}>
              Appliquer à toutes les pages
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {(['1-up', '2-up', '3-up', '4-up'] as const).map((lt) => (
                <button
                  key={`all-${lt}`}
                  className="rounded-lg py-2 px-1 text-[9px] font-medium transition-all"
                  style={{
                    border: `1px solid ${COLORS.gold}30`,
                    color: UI.ink,
                    background: UI.panel,
                  }}
                  onClick={() => setConfirmApplyAll(lt)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${COLORS.gold}15`; e.currentTarget.style.borderColor = COLORS.gold; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = UI.panel; e.currentTarget.style.borderColor = `${COLORS.gold}30`; }}
                >
                  {lt === '1-up' ? '1 photo/page' : lt === '2-up' ? '2 photos/page' : lt === '3-up' ? '3 photos/page' : '4 photos/page'}
                </button>
              ))}
            </div>
            {/* Confirmation */}
            {confirmApplyAll && (
              <div className="mt-2 p-2 rounded-lg" style={{ background: `${COLORS.gold}08`, border: `1px solid ${COLORS.gold}30` }}>
                <p className="text-[10px] mb-1.5" style={{ color: UI.ink }}>
                  Appliquer «{confirmApplyAll === '1-up' ? '1 photo' : confirmApplyAll === '2-up' ? '2 photos' : confirmApplyAll === '3-up' ? '3 photos' : '4 photos'}» à toutes les pages ?
                </p>
                <div className="flex gap-1.5">
                  <button onClick={() => setConfirmApplyAll(null)}
                    className="flex-1 px-2 py-1 rounded text-[9px] font-medium"
                    style={{ color: UI.muted, border: `1px solid ${COLORS.gold}25` }}>
                    Annuler
                  </button>
                  <button onClick={() => { onApplyToAll(confirmApplyAll); setConfirmApplyAll(null); }}
                    className="flex-1 px-2 py-1 rounded text-[9px] font-medium"
                    style={{ background: COLORS.gold, color: COLORS.white }}>
                    Confirmer
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Themes Sidebar ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function ThemesSidebar({
  covers,
  categories,
  selectedCoverId,
  onSelectCover,
  title = 'DESIGNS',
}: {
  /** Panel heading (« Nouvel éditeur mobile »: in French). */
  title?: string;
  covers: Cover[];
  categories: CoverCategory[];
  selectedCoverId: string | null;
  onSelectCover: (cover: Cover) => void;
}) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const normalize = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filtered = useMemo(() => {
    let list = covers.filter((c) => c.is_visible);
    if (activeCategory !== 'all') {
      list = list.filter((c) => c.categories.includes(activeCategory));
    }
    if (search.trim()) {
      const q = normalize(search.trim());
      list = list.filter(
        (c) =>
          normalize(c.name).includes(q) ||
          (c.search_tags ?? []).some((t) => normalize(t).includes(q)),
      );
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [covers, activeCategory, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2.5 shrink-0" style={{ borderBottom: `1px solid ${COLORS.gold}10` }}>
        <span className="text-xs font-semibold" style={{ color: UI.ink }}>
          {title}
        </span>
        {/* Search */}
        <div className="mt-2 relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: UI.muted }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-7 pr-2 py-1.5 rounded-lg text-xs outline-none"
            style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink }}
          />
        </div>
      </div>

      {/* Category tabs */}
      {categories.length > 0 && (
        <div className="flex gap-1 px-2 py-2 overflow-x-auto shrink-0" style={{ borderBottom: `1px solid ${COLORS.gold}10` }}>
          <button
            className="shrink-0 px-2 py-0.5 rounded text-[9px] font-medium transition-all"
            style={{
              background: activeCategory === 'all' ? COLORS.gold : 'transparent',
              color: activeCategory === 'all' ? COLORS.white : UI.muted,
              border: `1px solid ${activeCategory === 'all' ? COLORS.gold : COLORS.gold + '25'}`,
            }}
            onClick={() => setActiveCategory('all')}
          >
            Tous
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              className="shrink-0 px-2 py-0.5 rounded text-[9px] font-medium transition-all"
              style={{
                background: activeCategory === cat.slug ? COLORS.gold : 'transparent',
                color: activeCategory === cat.slug ? COLORS.white : UI.muted,
                border: `1px solid ${activeCategory === cat.slug ? COLORS.gold : COLORS.gold + '25'}`,
              }}
              onClick={() => setActiveCategory(cat.slug)}
            >
              {cat.label_fr}
            </button>
          ))}
        </div>
      )}

      {/* Cover grid */}
      <div className="flex-1 overflow-y-auto p-2">
        {filtered.length === 0 && !search.trim() ? (
          <div className="text-center py-8">
            <p className="text-[10px]" style={{ color: UI.muted }}>Aucun résultat</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {/* Blank theme — always shown first */}
            <button
              className="rounded-lg overflow-hidden transition-all hover:scale-[1.02] text-left"
              style={{
                border: selectedCoverId === null ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
                background: UI.surface,
              }}
              onClick={() => onSelectCover(BLANK_COVER)}
            >
              <div className="w-full aspect-[3/4] flex flex-col items-center justify-center"
                style={{ background: UI.surface, border: `1px dashed ${COLORS.gold}30` }}>
                <svg className="w-6 h-6 mb-1" style={{ color: `${COLORS.gold}50` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-[8px]" style={{ color: UI.muted }}>Vierge</span>
              </div>
              <div className="px-1 py-0.5">
                <p className="text-[8px] truncate font-medium" style={{ color: selectedCoverId === null ? COLORS.gold : UI.ink }}>
                  Couverture vierge
                </p>
                <p className="text-[7px] truncate" style={{ color: UI.muted }}>
                  Couleur, icônes &amp; votre photo
                </p>
              </div>
            </button>
            {filtered.map((cover) => {
              const isSelected = cover.id === selectedCoverId;
              return (
                <button
                  key={cover.id}
                  className="rounded-lg overflow-hidden transition-all hover:scale-[1.02] text-left"
                  style={{
                    border: isSelected ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
                    background: cover.dominant_color ?? COLORS.beigeLight,
                  }}
                  onClick={() => onSelectCover(cover)}
                >
                  {cover.thumbnail_url ? (
                    <img
                      src={cover.thumbnail_url}
                      alt={cover.name}
                      className="w-full aspect-[3/4] object-cover"
                      loading="lazy"
                      decoding="async"
                      // @ts-expect-error fetchpriority is valid HTML, missing from React types
                      fetchpriority="low"
                    />
                  ) : (
                    <div
                      className="w-full aspect-[3/4] flex items-center justify-center"
                      style={{ background: cover.dominant_color ?? COLORS.beige }}
                    >
                      <span className="text-[9px]" style={{ color: COLORS.muted }}>{cover.name}</span>
                    </div>
                  )}
                  {/* The card is the cover's own colour; at night the name gets a
                      dark strip of its own so it stays readable on any cover. */}
                  <div className="px-1 py-0.5 dark:bg-adm-surface">
                    <p className="text-[8px] truncate font-medium" style={{ color: isSelected ? COLORS.gold : UI.ink }}>
                      {cover.name}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Page Tools Sidebar ───────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function PageToolsSidebar({
  photoGap,
  onPhotoGapChange,
  selectedPage,
  placedCount,
  unplacedCount,
  totalPages,
  onLayoutChange,
  activeTool,
  onToolClick,
  selectedElement,
  onUpdateElement,
  onDeleteElement,
  onDeletePage,
  canDeletePage,
  onOpenTemplatePicker,
  onRemoveTemplate,
}: {
  /** B5 — album-wide gap between photos + its setter. */
  photoGap: PhotoGap;
  onPhotoGapChange: (next: Partial<PhotoGap>) => void;
  selectedPage: LayoutPage | null;
  selectedSide?: 'left' | 'right';
  placedCount: number;
  unplacedCount: number;
  totalPages: number;
  onLayoutChange: (pageNumber: number, layout: '1-up' | '2-up' | '3-up' | '4-up') => void;
  activeTool: string;
  onToolClick: (tool: string) => void;
  selectedElement: PageElement | null;
  onUpdateElement: (updates: Partial<PageElement>) => void;
  onDeleteElement: () => void;
  onDeletePage: (pageNumber: number) => void;
  canDeletePage: boolean;
  /** Phase 1 — open the layout-template gallery for the selected page. */
  onOpenTemplatePicker?: () => void;
  /** Phase 1 — clear template_id on the selected page. */
  onRemoveTemplate?: () => void;
}) {
  const tools: { key: string; icon: React.FC; label: string }[] = [
    { key: 'text', icon: TextToolIcon, label: 'Texte' },
    { key: 'photo', icon: PhotoToolIcon, label: 'Photo' },
    { key: 'rect', icon: RectToolIcon, label: 'Rectangle' },
    { key: 'ellipse', icon: EllipseToolIcon, label: 'Ellipse' },
  ];

  return (
    <div className="flex flex-col h-full">
      <div
        className="px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${COLORS.gold}15` }}
      >
        <h3
          className="text-sm font-semibold"
          style={{
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontSize: 16,
            color: UI.ink,
          }}
        >
          Outils — Page {selectedPage?.page ?? '—'}
        </h3>
      </div>

      {/* Tool buttons */}
      <div className="p-3 space-y-1">
        {tools.map(({ key, icon: Icon, label }) => {
          const isActive = activeTool === key;
          return (
            <button
              key={key}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left"
              style={{
                color: isActive ? COLORS.gold : UI.ink,
                background: isActive ? `${COLORS.gold}12` : 'transparent',
                border: isActive ? `1px solid ${COLORS.gold}30` : '1px solid transparent',
              }}
              onClick={() => onToolClick(key)}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${COLORS.gold}08`; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon />
              <span className="text-xs font-medium">{label}</span>
              {isActive && (
                <span className="ml-auto text-[8px] px-1.5 py-0.5 rounded" style={{ background: COLORS.gold, color: COLORS.white }}>
                  Actif
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected element properties */}
      {selectedElement && (
        <div className="px-3 py-3 space-y-3" style={{ borderTop: `1px solid ${COLORS.gold}15` }}>
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold" style={{ color: UI.ink }}>
              {selectedElement.type === 'text' ? 'Texte' : selectedElement.type === 'rect' ? 'Rectangle' : selectedElement.type === 'ellipse' ? 'Ellipse' : selectedElement.type === 'icon' ? (selectedElement.previewUrl ? 'Sticker' : 'Icône') : 'Photo'}
            </p>
            <button
              onClick={onDeleteElement}
              className="text-[9px] px-2 py-0.5 rounded transition-colors"
              style={{ background: `${COLORS.danger}10`, color: UI.danger, border: `1px solid ${COLORS.danger}25` }}
            >
              Supprimer
            </button>
          </div>

          {/* Text-specific controls */}
          {selectedElement.type === 'text' && (
            <>
              <div>
                <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Contenu</label>
                <textarea
                  value={selectedElement.text ?? ''}
                  onChange={(e) => onUpdateElement({ text: e.target.value })}
                  className="w-full px-2 py-1.5 rounded-lg text-xs outline-none resize-none"
                  style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink, minHeight: 48 }}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Taille</label>
                  <input
                    type="number"
                    value={selectedElement.fontSize ?? 18}
                    onChange={(e) => onUpdateElement({ fontSize: parseInt(e.target.value) || 18 })}
                    className="w-full px-2 py-1 rounded text-xs outline-none"
                    style={{ border: `1px solid ${COLORS.gold}30`, background: UI.panel, color: UI.ink }}
                    min={8} max={120}
                  />
                </div>
                <div>
                  <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Couleur</label>
                  <input
                    type="color"
                    value={selectedElement.fontColor ?? '#1A1A1A'}
                    onChange={(e) => onUpdateElement({ fontColor: e.target.value })}
                    className="w-full h-7 rounded cursor-pointer border-0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Alignement</label>
                <div className="flex gap-1">
                  {(['left', 'center', 'right'] as const).map((a) => (
                    <button key={a}
                      className="flex-1 py-1 rounded text-[9px] font-medium transition-all"
                      style={{
                        background: selectedElement.textAlign === a ? COLORS.gold : 'transparent',
                        color: selectedElement.textAlign === a ? COLORS.white : UI.muted,
                        border: `1px solid ${selectedElement.textAlign === a ? COLORS.gold : COLORS.gold + '25'}`,
                      }}
                      onClick={() => onUpdateElement({ textAlign: a })}
                      title={ALIGN_LABELS[a]}
                      aria-label={ALIGN_LABELS[a]}
                    >
                      {a === 'left' ? '◀' : a === 'center' ? '◆' : '▶'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Poids</label>
                <div className="flex gap-1">
                  {[{ v: '400', l: 'Normal' }, { v: '600', l: 'Semi-bold' }, { v: '700', l: 'Bold' }].map(({ v, l }) => (
                    <button key={v}
                      className="flex-1 py-1 rounded text-[9px] transition-all"
                      style={{
                        background: selectedElement.fontWeight === v ? COLORS.gold : 'transparent',
                        color: selectedElement.fontWeight === v ? COLORS.white : UI.muted,
                        border: `1px solid ${selectedElement.fontWeight === v ? COLORS.gold : COLORS.gold + '25'}`,
                        fontWeight: v,
                      }}
                      onClick={() => onUpdateElement({ fontWeight: v })}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Shape-specific controls */}
          {(selectedElement.type === 'rect' || selectedElement.type === 'ellipse') && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Remplissage</label>
                  <input
                    type="color"
                    value={(selectedElement.fill ?? '#C9A24A').replace(/[0-9a-f]{2}$/i, '')}
                    onChange={(e) => onUpdateElement({ fill: e.target.value + '40' })}
                    className="w-full h-7 rounded cursor-pointer border-0"
                  />
                </div>
                <div>
                  <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>Bordure</label>
                  <input
                    type="color"
                    value={selectedElement.stroke ?? '#C9A24A'}
                    onChange={(e) => onUpdateElement({ stroke: e.target.value })}
                    className="w-full h-7 rounded cursor-pointer border-0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>
                  Opacité: {Math.round((selectedElement.opacity ?? 1) * 100)}%
                </label>
                <input
                  type="range" min={0} max={100}
                  value={Math.round((selectedElement.opacity ?? 1) * 100)}
                  onChange={(e) => onUpdateElement({ opacity: parseInt(e.target.value) / 100 })}
                  className="w-full accent-[#C9A24A]"
                />
              </div>
              {selectedElement.type === 'rect' && (
                <div>
                  <label className="block text-[10px] mb-1" style={{ color: UI.muted }}>
                    Arrondi: {selectedElement.borderRadius ?? 0}px
                  </label>
                  <input
                    type="range" min={0} max={50}
                    value={selectedElement.borderRadius ?? 0}
                    onChange={(e) => onUpdateElement({ borderRadius: parseInt(e.target.value) })}
                    className="w-full accent-[#C9A24A]"
                  />
                </div>
              )}
            </>
          )}

          {/* Position controls (all element types) */}
          <div style={{ borderTop: `1px solid ${COLORS.gold}10`, paddingTop: 8 }}>
            <p className="text-[9px] font-medium mb-1.5" style={{ color: UI.muted }}>Position & taille (%)</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { k: 'x' as const, l: 'X' },
                { k: 'y' as const, l: 'Y' },
                { k: 'width' as const, l: 'L' },
                { k: 'height' as const, l: 'H' },
              ].map(({ k, l }) => (
                <div key={k} className="flex items-center gap-1">
                  <span className="text-[8px] w-3 shrink-0" style={{ color: UI.muted }}>{l}</span>
                  <input
                    type="number"
                    value={Math.round(selectedElement[k] ?? 0)}
                    onChange={(e) => onUpdateElement({ [k]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                    className="flex-1 px-1.5 py-0.5 rounded text-[10px] outline-none tabular-nums"
                    style={{ border: `1px solid ${COLORS.gold}20`, background: UI.panel, color: UI.ink }}
                    min={0} max={100}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Layout picker for selected page */}
      <div
        className="px-3 py-3"
        style={{ borderTop: `1px solid ${COLORS.gold}15` }}
      >
        <p
          className="text-[10px] font-medium mb-2"
          style={{ color: UI.muted }}
        >
          Mise en page de cette page
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {(['1-up', '2-up', '3-up', '4-up'] as const).map((lt) => {
            const isActive = selectedPage?.layout === lt;
            return (
              <button
                key={lt}
                className="rounded-md p-1.5 transition-all"
                style={{
                  border: isActive
                    ? `2px solid ${COLORS.gold}`
                    : `1px solid ${COLORS.gold}25`,
                  background: isActive
                    ? `${COLORS.gold}10`
                    : 'transparent',
                }}
                onClick={() => {
                  if (selectedPage) onLayoutChange(selectedPage.page, lt);
                }}
              >
                <LayoutMiniPreview layout={lt} />
              </button>
            );
          })}
        </div>

        {/* « Modèles » — the album theme's design system v2 models, drawn with
            the client's own photos (components/editor/ThemeModelPicker). */}
        {onOpenTemplatePicker && (
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => { if (selectedPage) onOpenTemplatePicker(); }}
              disabled={!selectedPage}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-40"
            >
              Modèles
            </button>
            {(selectedPage?.template_id || selectedPage?.theme) && onRemoveTemplate && (
              <button
                onClick={onRemoveTemplate}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Retirer le modèle
              </button>
            )}
          </div>
        )}

        {/* Delete-page button — works for empty pages too; only disabled at
            the 1-spread (2-page) hard floor. Opens the shared warning popup. */}
        <button
          disabled={!selectedPage || !canDeletePage}
          onClick={() => { if (selectedPage) onDeletePage(selectedPage.page); }}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: `${COLORS.danger}10`,
            color: UI.danger,
            border: `1px solid ${COLORS.danger}25`,
          }}
          title={canDeletePage ? 'Supprimer cette page' : 'Minimum 2 pages — suppression impossible'}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
          </svg>
          Supprimer cette page
        </button>
      </div>

      {/* B5 — ESPACE ENTRE LES PHOTOS. Per album, not per page. Off by
          default; when on, thickness in mm and the colour of the gap. The
          same value drives the page, the strip thumbnails and the print. */}
      <div className="px-4 py-3" style={{ borderTop: `1px solid ${COLORS.gold}15` }}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-medium" style={{ color: UI.muted }}>
            Espace entre les photos
            <span className="ml-1 text-[9px]" style={{ color: UI.hint60 }}>· tout l'album</span>
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={photoGap.mm > 0}
            aria-label="Espace entre les photos"
            onClick={() => onPhotoGapChange({ mm: photoGap.mm > 0 ? 0 : 3 })}
            className="relative w-8 h-[18px] rounded-full transition-colors"
            style={{ background: photoGap.mm > 0 ? COLORS.gold : withAlpha(UI.muted, '40') }}
          >
            <span
              className="absolute top-[2px] w-[14px] h-[14px] rounded-full transition-transform"
              style={{ background: UI.surface, left: 2, transform: photoGap.mm > 0 ? 'translateX(14px)' : 'translateX(0)' }}
            />
          </button>
        </div>
        {photoGap.mm > 0 && (
          <>
            <div className="flex items-center gap-1.5 mb-2">
              {[1, 2, 3, 5, 8].map((mm) => {
                const active = photoGap.mm === mm;
                return (
                  <button
                    key={mm}
                    type="button"
                    onClick={() => onPhotoGapChange({ mm })}
                    className="flex-1 rounded-md py-1 text-[10px] font-medium transition-all"
                    style={{
                      border: active ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}30`,
                      background: active ? `${COLORS.gold}12` : 'transparent',
                      color: active ? COLORS.gold : UI.ink,
                    }}
                  >
                    {mm} mm
                  </button>
                );
              })}
            </div>
            <input
              type="range"
              min={0.5}
              max={MAX_GAP_MM}
              step={0.5}
              value={photoGap.mm}
              onChange={(e) => onPhotoGapChange({ mm: Number(e.target.value) })}
              aria-label="Épaisseur de l'espace (mm)"
              className="w-full mb-2"
              style={{ accentColor: COLORS.gold }}
            />
            <span className="block text-[10px] font-medium mb-1.5" style={{ color: UI.muted }}>Couleur de l'espace</span>
            <div className="flex items-center gap-2 flex-wrap">
              {['#FFFFFF', '#F5EFE3', '#1A1A1A', '#C9A24A', '#2E4A3F', '#8C5A3C'].map((c) => {
                const active = photoGap.color.toUpperCase() === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Couleur ${c}`}
                    onClick={() => onPhotoGapChange({ color: c })}
                    className="w-6 h-6 rounded-full transition-transform active:scale-90 border border-[rgba(0,0,0,0.12)] dark:border-adm-line-strong"
                    style={{ background: c, boxShadow: active ? `0 0 0 2px ${UI.surface}, 0 0 0 4px ${COLORS.gold}` : 'none' }}
                  />
                );
              })}
              <label
                className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer relative"
                style={{ background: UI.surface, border: `1px dashed ${COLORS.gold}80` }}
                title="Couleur personnalisée"
                aria-label="Couleur personnalisée"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.ink }} strokeWidth="2" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <input
                  type="color"
                  value={photoGap.color}
                  onChange={(e) => onPhotoGapChange({ color: e.target.value.toUpperCase() })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>
          </>
        )}
      </div>

      {/* Stats */}
      <div
        className="mt-auto p-4"
        style={{ borderTop: `1px solid ${COLORS.gold}15` }}
      >
        <div className="space-y-1.5">
          {[
            { label: 'Photos placées', value: placedCount },
            { label: 'Non placées', value: unplacedCount },
            { label: 'Pages totales', value: totalPages },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: UI.muted }}>
                {label}
              </span>
              <span
                className="text-[10px] font-semibold"
                style={{ color: UI.ink }}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Page View ────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// (PillBtn removed 2026-07-05 — the dark templated-slot pill it served was
// replaced by the shared SelectedActionsBar/ClampedPill via TemplateSlotControls.)

const PageView = memo(function PageView({
  page,
  photoGap,
  photoUrlMap,
  photoFilenameMap,
  photoMetaMap,
  pageLabel,
  selectedSlotKey,
  onSelectSlot,
  panModeKey,
  onSetPanMode,
  slotPositions,
  manualSlotEdits,
  onSlotPositionChange,
  onRemoveFromSlot,
  onDropToSlot,
  onDetachSlot,
  draggingPhotoId,
  onSwitchToImages,
  selectedElementId,
  selectedElementIds,
  onSelectElement,
  onUpdateElement,
  onDeleteElement,
  onDuplicateElement,
  onShowElementMoreToolbar,
  onMoveElementToPage,
  editingElementId,
  onSetEditingElement,
  onSnapGuides,
  pickedPhotoForPlace,
  onPickedPhotoUsed,
  onSlotWaiting,
  awaitingTargetSlot,
  templateOverlayUrl,
  templateOriginalSvgUrl,
  templateSlots,
  templatePhotoFits,
  onTemplateFitChange,
  templateEditKey,
  onSetTemplateEdit,
  onOpenPageMenu,
  hideMenuButton = false,
  onPickPhotoForElement,
  onTouchDragStart,
  windowPhotoOf,
  onSwapThemedPhoto,
  onRemoveThemedPhoto,
}: {
  page: LayoutPage;
  /** B5 — the album's gap; the grid renders it and PhotoSlot's DPI badge reads it. */
  photoGap: PhotoGap;
  photoUrlMap: Map<string, string>;
  photoFilenameMap: Map<string, string>;
  photoMetaMap: Map<string, {
    width: number | null; height: number | null;
    face_count: number | null; face_spread: number | null;
    aspect_ratio: 'landscape' | 'portrait' | 'square' | null;
  }>;
  pageLabel: string;
  selectedSlotKey: string | null;
  onSelectSlot: (key: string | null) => void;
  panModeKey: string | null;
  onSetPanMode: (key: string | null) => void;
  // NOTE: this type was missing `crop` / `cropUnlocked`, which DO exist on the
  // real SlotPosition. They survive only because every writer spreads
  // `...objectPosition`; the moment someone builds a fresh object literal here
  // the customer's crop would be silently deleted. Listed explicitly so the
  // compiler protects them.
  slotPositions: Record<string, SlotPosition>;
  /** photoId -> customer-authored framing. Read-only here; used to lock the
   *  legacy self-heal out of hand-framed photos. */
  manualSlotEdits?: Record<string, unknown>;
  onSlotPositionChange: (key: string, pos: { x: number; y: number; scale?: number; rotation?: number; lockSize?: boolean }) => void;
  onRemoveFromSlot: (pageNumber: number, slotIndex: number) => void;
  onDropToSlot: (pageNumber: number, slotIndex: number, photoId: string, source?: 'slot' | 'sidebar') => void;
  draggingPhotoId: string | null;
  onSwitchToImages: () => void;
  /** Mobile tap-to-place: photo id picked from the photos strip. When set,
      tapping an empty slot drops this photo in (no drag needed). */
  pickedPhotoForPlace?: string | null;
  onPickedPhotoUsed?: () => void;
  /** Tap-empty-slot-first: parent records the awaited target so the next
      photo tap in the strip lands here. */
  onSlotWaiting?: (pageNumber: number, slotIndex: number) => void;
  /** When this slot is the awaited target, give it a visible glow. */
  awaitingTargetSlot?: { page: number; slot: number } | null;
  selectedElementId?: string | null;
  selectedElementIds?: Set<string>;
  /** `additive` is true when shift was held during the click — parent
   *  should ADD to the selection set rather than replacing. */
  onSelectElement?: (id: string | null, additive?: boolean) => void;
  onUpdateElement?: (pageNumber: number, elementId: string, updates: Partial<PageElement>) => void;
  onDeleteElement?: (pageNumber: number, elementId: string) => void;
  /** D78 Fix 1 (2026-05-21): clone the element. Wired by the new
   *  selection pill's duplicate button. */
  onDuplicateElement?: (pageNumber: number, elementId: string) => void;
  /** D78 Fix 1 (2026-05-21): customer tapped "More" on the selection
   *  pill — open the heavy bottom toolbar (font / colour / opacity / …). */
  onShowElementMoreToolbar?: () => void;
  /** Moves an element to another page in the spread (mid-drag). */
  onMoveElementToPage?: (fromPage: number, toPage: number, elementId: string, newX: number, newY: number) => void;
  editingElementId?: string | null;
  onSetEditingElement?: (id: string | null) => void;
  /** Snap-guide reporter — emitted during drag of any element on this
   *  page. Parent renders the dashed guide lines based on the latest
   *  emit; cleared on drag end. */
  onSnapGuides?: (guides: { v: number[]; h: number[] }) => void;
  /** Phase 1.5 — when a template is set on this page, the parent resolves the
   *  template's decorative overlay SVG URL and passes it in. The overlay
   *  renders as the TOP layer (z-stack top) ABOVE the customer photos so the
   *  template's decorative parts (polaroid borders, frames, captions) are
   *  never hidden under photos. */
  templateOverlayUrl?: string | null;
  /** Phase 2 (Task 7) — when the template has an original SVG uploaded AND every
   *  slot carries a `dom_index`, the editor uses the SVG-href-swap render path
   *  (TemplatedPageWithSvgFetch) for pixel-perfect Canva reproduction. Otherwise
   *  it falls back to the legacy per-slot TemplatedPhotoSlot grid + overlay PNG. */
  templateOriginalSvgUrl?: string | null;
  /** Phase 2 — template slot rectangles (0..1 absolute, top-left origin).
   *  When set, the page wrapper uses these positions for photos INSTEAD of
   *  the grid layout's getSlots() positions. */
  templateSlots?: LayoutTemplateSlot[] | null;
  /** Task 14 — per-slot photo fit (scale + offset) for templated pages, keyed
   *  by page number → slot index. Threaded into TemplatedPhotoSlot so drag-pan
   *  + the floating zoom pill share state with autosave. */
  templatePhotoFits?: Record<number, Record<number, PhotoFitInSlot>>;
  onTemplateFitChange?: (pageNumber: number, slotIndex: number, fit: PhotoFitInSlot) => void;
  /** FIX #1 — "p{n}-s{m}" of the templated slot currently in EDIT mode. */
  templateEditKey?: string | null;
  onSetTemplateEdit?: (key: string | null) => void;
  /** BLANK PAGE — open the page-options menu (blank / models). */
  onOpenPageMenu?: (pageNum: number) => void;
  /** « Nouvel éditeur mobile »: no « ••• » drawn on the page (it lives in the
   *  « Mise en page » sheet; double-tap on the page background still opens it). */
  hideMenuButton?: boolean;
  /** Detach a slot photo into a free element at the given page-% rect. */
  onDetachSlot?: (pageNumber: number, slotIndex: number, photoId: string, rect: { x: number; y: number; w: number; h: number }) => void;
  /** ISSUE 8 — customer tapped an EMPTY photo element: open Mes photos
   *  targeted so the next photo tap fills it. */
  onPickPhotoForElement?: (pageNum: number, elementId: string) => void;
  /** Long-press touch-drag pickup of a PLACED photo (Fix 2 of the mobile
   *  editor pass). Wired through to PhotoSlot; PhotoSlot calls it on touch-
   *  start of a filled cell when not yet in pan-mode. The hook itself owns
   *  the hold-timer + the floating clone preview + the drop hit-test. */
  onTouchDragStart?: (photoId: string, signedUrl: string | null, e: React.TouchEvent) => void;
  /** DESIGN SYSTEM v2 — a photo's size + faces, for a model window's crop (pan / zoom / warning). */
  windowPhotoOf?: (photoId: string | null | undefined) => WindowPhoto | null;
  /** A photo dropped / picked into a model's window. */
  onSwapThemedPhoto?: (pageNumber: number, elementId: string, photoId: string, source: 'slot' | 'sidebar') => void;
  /** « Retirer » on a model's window (the worker decides whether the model may lose it). */
  onRemoveThemedPhoto?: (pageNumber: number, elementId: string) => void;
}) {
  // « Nouvel éditeur mobile »: the « trop proche du bord » warning docks at the bottom
  const dock = useMobileDock();
  const slots = getSlots(page.layout);
  const elements = page.elements ?? [];
  // Fix G (2026-05-28): on mobile, a tap on a FILLED slot enters pan mode
  // directly so the corner-resize handles appear without a second gesture.
  // Desktop keeps the "click selects, double-click pans" model the cursor
  // affords.
  const isMobile = useIsMobile();
  // Snap-guide state, set by PageElementOverlay's drag handler. Arrays
  // hold the x (vertical lines) and y (horizontal lines) page-percent
  // positions that the active drag has snapped to. Empty → no lines.
  const [snapGuides, setSnapGuides] = useState<{ v: number[]; h: number[] }>({ v: [], h: [] });
  // Persistent low-opacity centre cross shown while ANY element is being
  // dragged on the page — visual reference so the customer always sees the
  // page midline before/after a snap actually fires. Tracked via a counter
  // (open drags) instead of a bool so concurrent drags don't race.
  const [draggingCount, setDraggingCount] = useState(0);
  const beginDrag = useCallback(() => setDraggingCount((n) => n + 1), []);
  const endDrag   = useCallback(() => setDraggingCount((n) => Math.max(0, n - 1)), []);
  const isAnyDragging = draggingCount > 0;

  // Pixel dimensions of the page wrapper — required by TemplatedPhotoSlot
  // to scale unit-space clip-path coords into pixel space. A ResizeObserver
  // keeps these in sync with the responsive page container above us.
  const pageRef = useRef<HTMLDivElement | null>(null);
  // FIX #2 — timestamp of the last background tap (template pages): two taps
  // within 350 ms on the empty area open the Modèles picker.
  const bgTapAtRef = useRef(0);
  const [pageDims, setPageDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPageDims({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={pageRef}
      data-page-number={page.page}
      // DRAG AND DROP (2026-10-20): a sticker / icon dropped here lands under the pointer (lib/tileDrag).
      data-element-drop={`page:${page.page}`}
      className="w-full h-full relative p-1.5 flex flex-col"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onSelectSlot(null);
          onSelectElement?.(null);
          onSetTemplateEdit?.(null);
          // FIX #2 — double-tap the template page BACKGROUND (not a photo)
          // → open the Modèles picker to switch models. Manual 2-tap window
          // (native dblclick is unreliable on touch). Photo taps never reach
          // here (slots stopPropagation / e.target !== currentTarget).
          // Double-tap the page BACKGROUND (not a photo) → open the page-options
          // menu (Page blanche + Modèles). Works on ALL pages now.
          if (onOpenPageMenu) {
            const now = Date.now();
            if (now - bgTapAtRef.current < 350) {
              bgTapAtRef.current = 0;
              onOpenPageMenu(page.page);
            } else {
              bgTapAtRef.current = now;
            }
          }
        }
      }}
    >
      {/* Page number */}
      <div className="absolute bottom-1 left-0 right-0 text-center z-10 pointer-events-none">
        <span className="text-[8px] font-medium" style={{ color: UI.muted }}>
          {pageLabel}
        </span>
      </div>

      {/* BLANK PAGE — discoverable page-options button (top-right). Opens the
          same menu as the background double-tap: Page blanche + Modèles. */}
      {onOpenPageMenu && !hideMenuButton && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenPageMenu(page.page); }}
          className="absolute top-1 right-1 z-20 w-6 h-6 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 active:scale-95 transition"
          style={{ background: 'rgba(255,255,255,0.92)', border: `1px solid ${COLORS.gold}30`, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
          title="Options de la page"
          aria-label="Options de la page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.dark} strokeWidth={2.5} strokeLinecap="round">
            <circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" />
          </svg>
        </button>
      )}

      {/* DESIGN SYSTEM STEP 2 — the page's paper, under photos and elements.
          Same bleed box and cover-fit as the print (decorStyle). */}
      <PageBackgroundLayer background={page.background} />

      {/* BLANK PAGE — pure white canvas, no photo slots. The element layer
          (text/photo/icons) still renders below, so the customer builds on it
          with the normal tools. A subtle hint shows until they add something. */}
      {page.blank && (
        <div className="absolute inset-0" style={{ background: page.background ? 'transparent' : (page.blank_color ?? '#ffffff') }}>
          {(page.elements?.length ?? 0) === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 pointer-events-none">
              <span className="text-[10px] font-medium" style={{ color: `${COLORS.gold}90` }}>Page blanche</span>
              <span className="text-[8px]" style={{ color: UI.muted }}>Ajoutez du texte, une photo, des icônes — ou laissez-la vierge</span>
            </div>
          )}
        </div>
      )}

      {/* Layout grid — flush by default to match the PDF output (no white seams
          between photos). Add a per-album gap toggle later if needed.
          For TEMPLATED pages we render a separate template-slots path below
          (PhotoSlot is heavily tied to grid math). */}
      {!page.template_id && !page.blank && (
      <div
        className="flex-1 flex flex-col min-h-0"
        // B5 — the gap in px for THIS page width, from the same fraction the
        // print uses (mm / 200). The container's background is what shows in
        // the gaps; slots are opaque above it.
        style={{
          gap: photoGapPx(photoGap.mm, pageDims.w),
          // STEP 2 — with paper, the gaps and margins show the paper (as in print).
          background: page.background ? undefined : (photoGap.mm > 0 ? photoGap.color : undefined),
          ...(page.background ? { position: 'relative' as const, zIndex: 1 } : {}),
          // Base rules: the 12/15 mm frame, from the same mm/200 rule as the gap.
          padding: framePaddingPx(photoGap, (Number(page.page) || 1) - 1, pageDims.w),
          ...(page.layout === '2-up-even' ? EVEN_CONTAINER : {}),
        }}
      >
        {slots.map((row, rowIdx) => {
          // WIDE-TOP row heights come from `top_h_frac` (top band) so the top
          // slot's aspect ratio equals the wide photo's AR → zero crop.
          // PARITY FIX (2026-07-14, #1102): 3-up prints top 60% / bottom 40%
          // (pdfGenerator getSlots: topH = H*0.6, and SLOT_AR_BY_LAYOUT's crop
          // math already assumed it) but the editor rendered equal flex-1 rows
          // (50/50) — photos and overlaid text elements looked shifted vs the
          // PDF/3D preview. 3-up now uses the same fractional-row mechanism.
          // Remaining layouts (2-up / 4-up / 3-up-flip) ARE equal rows in the
          // PDF, so flex-1 keeps them in parity.
          const topFrac =
            page.layout === 'wide-top'
              ? (page.top_h_frac ?? computeWideTopFrac(WIDE_TOP_AR_MIN))
              : page.layout === '3-up' ? 0.6 : page.layout === '5-up' ? FIVE_UP_TOP_FRAC : page.layout === '4-up-main' ? FOUR_MAIN_TOP_FRAC
              : page.layout === '2-up-even' ? (page.top_h_frac ?? null) : null;
          const rowStyle =
            page.layout === '2-up-even'
              ? { flex: `0 0 ${evenRowPct(page.top_h_frac)}%`, minHeight: 0 }
              : topFrac !== null
              ? twoRowFlex(topFrac, rowIdx, photoGapPx(photoGap.mm, pageDims.w))
              : undefined;
          return (
          <div
            key={rowIdx}
            className={cn('flex min-h-0', rowStyle === undefined && 'flex-1')}
            style={{ ...rowStyle, gap: photoGapPx(photoGap.mm, pageDims.w) }}
          >
            {row.map((slotIdx) => {
              const photoId = page.photos[slotIdx] ?? null;
              const signedUrl = photoId ? photoUrlMap.get(photoId) ?? null : null;
              const filename = photoId ? photoFilenameMap.get(photoId) ?? '' : '';
              const slotKey = `p${page.page}-s${slotIdx}`;
              const isSelected = selectedSlotKey === slotKey;
              const isPanMode = panModeKey === slotKey;
              const position = slotPositions[slotKey] ?? { x: 0, y: 0 };
              // Image dimensions for the PDF-mirroring cover-fit math in
              // PhotoSlot. Without these the slot can only do "stretch
              // 100% × 100% with object-fit:cover" which doesn't match
              // the PDF's overflow-then-translate model and produces the
              // cream halos when translate values are non-zero.
              const meta = photoId ? photoMetaMap.get(photoId) : null;
              const imgWidth = meta?.width ?? null;
              const imgHeight = meta?.height ?? null;
              // Slot aspect-ratio (W/H) — must match the PDF's getSlots
              // numbers in pdfGenerator.ts so editor + PDF render the same
              // saved slot_positions identically. Derived from A4 portrait
              // (210 × 297mm) with the standard 2.5mm gutter.
              // B5 — slot ratios from the SAME geometry as the print, at the
              // album's gap. The hardcoded table this replaced assumed a 2.5 mm
              // gutter the print never had.
              const slotAR = photoGapSlotArs(page.layout, photoGap.mm, topFrac, photoGap.baseRules)[slotIdx]
                ?? photoGapSlotArs(page.layout, photoGap.mm, topFrac, photoGap.baseRules)[0];

              // DÉPLACER REMOVED (2026-07-07): grid photos no longer detach into
              // a free element — double-tap gives full move-inside-the-cell
              // freedom instead. The detach helper was removed with the button.

              return (
                <PhotoSlot
                  key={slotIdx}
                  signedUrl={signedUrl}
                  filename={filename}
                  imgWidth={imgWidth}
                  imgHeight={imgHeight}
                  slotAR={slotAR}
                  layout={page.layout}
                  fill={page.fill}
                  slotIndex={slotIdx}
                  gapMm={photoGap.mm}
                  topHFrac={page.top_h_frac}
                  hasPhoto={!!photoId}
                  photoId={photoId ?? undefined}
                  slotKey={slotKey}
                  isSelected={isSelected}
                  isPanMode={isPanMode}
                  isMobile={isMobile}
                  // Locks the legacy self-heal out of any photo the customer
                  // has framed by hand (manualSlotEdits is keyed by photoId and
                  // is written by every pan / resize commit).
                  isManuallyEdited={!!(photoId && manualSlotEdits?.[photoId])}
                  objectPosition={position}
                  draggingPhotoId={draggingPhotoId}
                  onTouchDragStart={onTouchDragStart}
                  onSelect={() => {
                    // Tap-to-place wins: an empty slot tapped while a photo
                    // is "picked up" from the photos strip drops it here.
                    if (pickedPhotoForPlace && !photoId) {
                      onDropToSlot(page.page, slotIdx, pickedPhotoForPlace);
                      onPickedPhotoUsed?.();
                      return;
                    }
                    // TWEAK 1 (2026-06-08): MOBILE tap model — a single tap
                    // SELECTS the slot (gold ring + "2× pour éditer"); a SECOND
                    // tap on the already-selected slot enters edit mode
                    // (pan / rotate / swap / delete). This replaces the old
                    // Fix-G single-tap-to-edit shortcut so a stray tap no longer
                    // drops the customer straight into edit. It is deliberately
                    // NOT time-boxed (a timed double-tap is exactly what mobile
                    // users kept missing) — the two taps can be as slow as
                    // needed. Pan gestures + Terminé + outside-tap own the rest.
                    if (isMobile && photoId) {
                      if (isPanMode) return;                              // already cropping — ignore
                      if (isSelected) { onSetPanMode(slotKey); return; }  // 2nd tap → CROP-PAN inside the cell (clipped)
                      onSelectSlot(slotKey);                              // 1st tap → select only
                      onSelectElement?.(null);
                      return;
                    }
                    onSelectSlot(isSelected ? null : slotKey);
                    onSelectElement?.(null);
                  }}
                  onDoubleClick={() => {
                    // DOUBLE-TAP on a GRID photo = crop-pan INSIDE the fixed cell
                    // (clipped, overflow hidden). This respects the grid — zoom +
                    // drag move the crop within the cell, never spilling into the
                    // neighbour. ENTER-ONLY (idempotent) so a native dblclick
                    // double-firing can't toggle it back off; exit is onExitEdit
                    // (Terminé) / Escape / outside-click. Explicit free-move stays
                    // on the pill's "Déplacer" button (detachThisSlot).
                    onSelectSlot(slotKey);
                    onSetPanMode(slotKey);
                  }}
                  onExitEdit={() => onSetPanMode(null)}
                  onRemove={() => onRemoveFromSlot(page.page, slotIdx)}
                  // DÉPLACER REMOVED (2026-07-07): grid photos no longer detach
                  // into a free-floating element — double-tap now gives full
                  // move-inside-the-cell freedom (clampTranslateToCover), so the
                  // simpler model is "photos always live in their cell". Detach
                  // stays only for TEMPLATE slots (baked décor can't be cropped).
                  onDetach={undefined}
                  onReplace={() => {
                    // SWAP PHOTO (TWEAK 2): mark THIS slot as the pending
                    // target and open the photos strip — the next photo the
                    // customer taps drops here, replacing the current one
                    // (handleDropToSlot bumps the displaced photo to
                    // unassigned). Same affordance as filling an empty slot.
                    onSlotWaiting?.(page.page, slotIdx);
                    // FIX #3 (2026-07-05): DISMISS the floating bars before the
                    // picker opens. The slot stayed selected (and possibly in
                    // pan mode), so the max-z-index SelectedActionsBar/pill kept
                    // floating ON TOP of the "Mes photos" grid. The pending
                    // target above is a separate mechanism — deselecting here
                    // doesn't lose the destination slot.
                    onSelectSlot(null);
                    onSetPanMode(null);
                    onSwitchToImages();
                  }}
                  onDrop={(pid, src) => onDropToSlot(page.page, slotIdx, pid, src)}
                  onPositionChange={(pos) => onSlotPositionChange(slotKey, pos)}
                  onEmpty={() => {
                    // Photo-first: a photo was already picked → drop it here.
                    if (pickedPhotoForPlace) {
                      onDropToSlot(page.page, slotIdx, pickedPhotoForPlace);
                      onPickedPhotoUsed?.();
                      return;
                    }
                    // Slot-first: open the photos strip and remember THIS slot
                    // as the target so the next photo tap lands here.
                    onSlotWaiting?.(page.page, slotIdx);
                    onSwitchToImages();
                  }}
                  awaitingThisSlot={
                    awaitingTargetSlot?.page === page.page
                    && awaitingTargetSlot?.slot === slotIdx
                  }
                />
              );
            })}
          </div>
          );
        })}
      </div>
      )}

      {/* Phase 2 — TEMPLATED-page photo render path. Bypasses PhotoSlot's
          grid math entirely. Each slot is a TemplatedPhotoSlot which knows
          how to render the photo with the template's clip-path (Canva-style
          compositing). The decorative overlay sits ABOVE the photos. */}
      {page.template_id && !page.blank && templateSlots && templateSlots.length > 0 && (
        <>
          {templateOriginalSvgUrl && templateSlots.every((s) => typeof s.dom_index === 'number') ? (
            // Task 7 — SVG-href-swap render: one component, pixel-perfect
            // Canva reproduction. Used when the template has an original SVG
            // AND every slot carries a dom_index.
            <TemplatedPageWithSvgFetch
              svgUrl={templateOriginalSvgUrl}
              slots={templateSlots}
              photoUrlsBySlotIndex={Object.fromEntries(
                templateSlots.map((_, slotIdx) => {
                  const photoId = page.photos[slotIdx];
                  return [slotIdx, photoId ? (photoUrlMap.get(photoId) ?? null) : null];
                })
              )}
              photoFitsBySlotIndex={Object.fromEntries(
                templateSlots.map((_, slotIdx) => [slotIdx, templatePhotoFits?.[page.page]?.[slotIdx]])
              )}
              photoIdBySlotIndex={Object.fromEntries(
                templateSlots.map((_, slotIdx) => [slotIdx, page.photos[slotIdx] ?? null])
              )}
              pageWidthPx={pageDims.w}
              pageHeightPx={pageDims.h}
              interactive
              selectedSlotIndex={(() => {
                const m = selectedSlotKey?.match(/^p(\d+)-s(\d+)$/);
                if (!m) return null;
                if (parseInt(m[1], 10) !== page.page) return null;
                return parseInt(m[2], 10);
              })()}
              onSlotSelect={(slotIdx) => {
                const slotKey = `p${page.page}-s${slotIdx}`;
                const photoId = page.photos[slotIdx];
                // Mirror the legacy TemplatedPhotoSlot onSelect logic exactly.
                if (pickedPhotoForPlace && !photoId) {
                  onDropToSlot(page.page, slotIdx, pickedPhotoForPlace);
                  onPickedPhotoUsed?.();
                  return;
                }
                if (!photoId) {
                  onSlotWaiting?.(page.page, slotIdx);
                  onSwitchToImages();
                  return;
                }
                // 1st tap selects; 2nd tap on the already-selected slot pops the
                // photo out into a FREE element (2026-07-06 — was crop-pan edit).
                if (selectedSlotKey === slotKey) {
                  const s = templateSlots[slotIdx];
                  if (s && onDetachSlot) {
                    onDetachSlot(page.page, slotIdx, photoId, {
                      x: s.x * 100, y: s.y * 100, w: s.width * 100, h: s.height * 100,
                    });
                  }
                } else {
                  onSelectSlot(slotKey);
                  onSetTemplateEdit?.(null);
                }
                onSelectElement?.(null);
              }}
              onSlotDrop={(slotIdx, pid, src) => onDropToSlot(page.page, slotIdx, pid, src)}
              onFitChange={(slotIdx, fit) => onTemplateFitChange?.(page.page, slotIdx, fit)}
            />
          ) : (
            // Legacy render: per-slot TemplatedPhotoSlot + decorative overlay
            // PNG on top. Used when the template predates the SVG-href-swap
            // pipeline (no original_svg_url) or when slots are missing
            // dom_index annotations.
            <>
              {templateSlots.map((s, slotIdx) => {
                const photoId = page.photos[slotIdx];
                const signedUrl = photoId ? photoUrlMap.get(photoId) ?? null : null;
                const slotKey = `p${page.page}-s${slotIdx}`;
                return (
                  <TemplatedPhotoSlot
                    key={`tmpl-${slotIdx}`}
                    slot={s}
                    photoUrl={signedUrl}
                    photoFit={templatePhotoFits?.[page.page]?.[slotIdx]}
                    onFitChange={(fit) => onTemplateFitChange?.(page.page, slotIdx, fit)}
                    interactive
                    isSelected={selectedSlotKey === slotKey}
                    onSelect={() => {
                      // Tap-to-place wins on empty slots.
                      if (pickedPhotoForPlace && !photoId) {
                        onDropToSlot(page.page, slotIdx, pickedPhotoForPlace);
                        onPickedPhotoUsed?.();
                        return;
                      }
                      if (!photoId) {
                        onSlotWaiting?.(page.page, slotIdx);
                        onSwitchToImages();
                        return;
                      }
                      // 2nd tap → FREE TRANSFORM (detach) — was crop-pan edit.
                      if (selectedSlotKey === slotKey) {
                        if (onDetachSlot) {
                          onDetachSlot(page.page, slotIdx, photoId, {
                            x: s.x * 100, y: s.y * 100, w: s.width * 100, h: s.height * 100,
                          });
                        }
                      } else {
                        onSelectSlot(slotKey);
                        onSetTemplateEdit?.(null);
                      }
                      onSelectElement?.(null);
                    }}
                    pageWidthPx={pageDims.w}
                    pageHeightPx={pageDims.h}
                    // Task 23 — drop targets + slot↔slot swap. droppableId is the
                    // stable identifier promised by the contract; the actual
                    // payload travels through HTML5 dataTransfer to interop with
                    // the existing PhotoSlot/photo-strip drag system.
                    droppableId={`slot:${page.page}:${slotIdx}`}
                    photoIdForDrag={photoId ?? null}
                    onDrop={(pid, src) => onDropToSlot(page.page, slotIdx, pid, src)}
                  />
                );
              })}
              {templateOverlayUrl && (
                <img
                  src={templateOverlayUrl}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    pointerEvents: 'none', zIndex: 1000,
                  }}
                />
              )}
            </>
          )}
          {/* Task 14 — floating selection pill. Renders only when a templated
              slot of THIS page is selected. Sits above the decorative overlay
              (zIndex 2000 > overlay's 1000) so the buttons stay tappable.
              Stays OUTSIDE the SVG/legacy branch above — works for both paths
              because selectedSlotKey is the same. */}
          {(() => {
            if (!selectedSlotKey) return null;
            const m = selectedSlotKey.match(/^p(\d+)-s(\d+)$/);
            if (!m) return null;
            const selPage = parseInt(m[1], 10);
            const slotIdx = parseInt(m[2], 10);
            if (selPage !== page.page) return null;
            const s = templateSlots[slotIdx];
            if (!s) return null;
            const fit = templatePhotoFits?.[page.page]?.[slotIdx]
              ?? { scale: 1, offsetX: 0, offsetY: 0 };
            const photoId = page.photos[slotIdx];
            const isEmpty = !photoId;
            if (isEmpty) return null; // empty slots open the picker directly
            const inEdit = templateEditKey === selectedSlotKey;
            return (
              <>
              {/* ISSUE 3 (2026-07-05) — frame-edge RESIZE handles for template
                  slots (same gesture as a normal image in edit mode): drag a
                  corner outward to zoom the photo inside its clip, inward to
                  shrink. Scale is distance-to-slot-centre based, clamped like
                  the pill buttons (0.5–3×). Rendered in-page at the slot's
                  fractional rect; pan stays the existing drag inside the slot. */}
              {inEdit && (
                <TemplateResizeFrame
                  fracX={s.x} fracY={s.y} fracW={s.width} fracH={s.height}
                  scale={fit.scale}
                  onScale={(next) => onTemplateFitChange?.(page.page, slotIdx, { ...fit, scale: next })}
                />
              )}
              {/* FIX #1 (2026-07-05): templated slots now use the SAME controls
                  as normal pages — SelectedActionsBar when selected, white
                  ClampedPill (scale/reset/replace/trash/Terminé) in edit mode —
                  instead of the old dark in-frame toolbar. The bar/pill are
                  portaled OUTSIDE the frame; geometry is derived from the page
                  container + the template slot's fractional box, so it works
                  for both the SVG-swap and legacy render branches.
                  BUG FIX (2026-07-07): these lines were a bare double-slash
                  comment sitting directly in JSX, so they rendered as literal
                  TEXT on template pages. Now a real JSX comment. */}
              <TemplateSlotControls
                pageRef={pageRef}
                fracX={s.x}
                fracY={s.y}
                fracW={s.width}
                fracH={s.height}
                editMode={templateEditKey === selectedSlotKey}
                scale={fit.scale}
                onScaleChange={(next) => onTemplateFitChange?.(page.page, slotIdx, { ...fit, scale: +next.toFixed(2) })}
                onReset={() => onTemplateFitChange?.(page.page, slotIdx, { scale: 1, offsetX: 0, offsetY: 0 })}
                onReplace={() => {
                  onSlotWaiting?.(page.page, slotIdx);
                  onSelectSlot(null);
                  onSetTemplateEdit?.(null); // FIX #3 — bars never float over the picker
                  onSwitchToImages();
                }}
                onRemove={() => {
                  onRemoveFromSlot(page.page, slotIdx);
                  onSelectSlot(null);
                  onSetTemplateEdit?.(null);
                }}
                onDone={() => onSetTemplateEdit?.(null)}
                onDetach={onDetachSlot ? () => {
                  onDetachSlot(page.page, slotIdx, photoId, {
                    x: s.x * 100, y: s.y * 100, w: s.width * 100, h: s.height * 100,
                  });
                } : undefined}
              />
              </>
            );
          })()}
        </>
      )}

      {/* Page elements overlay. Text elements render via the shared
          TextOverlayChrome (same visual + gestures as the cover) — this
          is the unified text system landing per the 2026-06-05 spec.
          Shapes / icons / extra photos keep the legacy PageElementOverlay
          for now: those still need rotation, lock, snap, cross-page drag,
          and the chrome's text-centric API doesn't fit. */}

      {/* FIX 2 — print safe-zone guide. Always-on faint dashed margin drawn 5mm
          inside the trim edge (Canva-style). pointer-events:none so it never
          blocks drag/click; advisory only (no clamp) — the warning pill below
          appears if an element crosses it, but the client may still place it. */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: `${INTERIOR_SAFE_INSET_X}%`,
          top: `${INTERIOR_SAFE_INSET_Y}%`,
          right: `${INTERIOR_SAFE_INSET_X}%`,
          bottom: `${INTERIOR_SAFE_INSET_Y}%`,
          border: `1px dashed ${COLORS.gold}59`,
          borderRadius: 1,
          zIndex: 22,
        }}
      />

      {/* DESIGN SYSTEM STEP 2 — soft shadows, in the order the print draws
          them (decorStyle.shadowPlan: own, or before the group's first piece). */}
      {(() => { const plan = shadowPlan(elements); return elements.map((el, ei) => (
        <Fragment key={el.id}>
        {(plan.get(ei) ?? []).map((s) => (
          <ElementShadow
            key={`sh-${s.id}`}
            el={s}
            imageSrc={s.type === 'icon' && s.imageUrl ? iconDisplayUrl(s) ?? null : null}
          />
        ))}
        {el.type === 'text'
          ? (
            <InteriorTextElement
              element={el}
              pageNumber={page.page}
              isSelected={selectedElementIds?.has(el.id) || selectedElementId === el.id}
              isEditing={editingElementId === el.id}
              onSelect={(shift) => { onSelectElement?.(el.id, shift); onSelectSlot(null); }}
              onStartEditing={() => onSetEditingElement?.(el.id)}
              onStopEditing={() => onSetEditingElement?.(null)}
              onUpdate={(updates) => onUpdateElement?.(page.page, el.id, updates)}
              onDelete={() => onDeleteElement?.(page.page, el.id)}
              onDuplicate={onDuplicateElement ? (() => onDuplicateElement(page.page, el.id)) : undefined}
              onShowMoreToolbar={onShowElementMoreToolbar}
              onMovePage={(targetPage, nx, ny) => onMoveElementToPage?.(page.page, targetPage, el.id, nx, ny)}
            />
          )
          : (
            <PageElementOverlay
              element={el}
              pageNumber={page.page}
              isSelected={selectedElementIds?.has(el.id) || selectedElementId === el.id}
              isEditing={editingElementId === el.id}
              onSelect={(additive) => { onSelectElement?.(el.id, additive); onSelectSlot(null); }}
              onDoubleClick={() => { if (el.type === 'text') onSetEditingElement?.(el.id); }}
              onUpdate={(updates) => onUpdateElement?.(page.page, el.id, updates)}
              onDelete={() => onDeleteElement?.(page.page, el.id)}
              onDuplicate={onDuplicateElement ? (() => onDuplicateElement(page.page, el.id)) : undefined}
              onShowMoreToolbar={onShowElementMoreToolbar}
              onMovePage={(target, nx, ny) => onMoveElementToPage?.(page.page, target, el.id, nx, ny)}
              onStopEditing={() => onSetEditingElement?.(null)}
              siblings={elements}
              onSnapGuides={(g) => { setSnapGuides(g); onSnapGuides?.(g); }}
              onDragStateChange={(isDragging) => { isDragging ? beginDrag() : endDrag(); }}
              photoUrl={el.type === 'photo' && el.photoId ? (photoUrlMap.get(el.photoId) ?? null) : null}
              // DESIGN SYSTEM v2 — a model's pieces (paper, frame, tape, object, label…) are not
              // movable: they let taps and drops through to the photo window under them.
              passive={!!page.theme && !!el.layer && el.layer !== 'photo' && el.layer !== 'holder'}
              themedWindow={isModelWindow(page, el) ? {
                photo: windowPhotoOf?.(el.photoId) ?? null,
                spreadHalf: (page as { spreadHalf?: 'left' | 'right' }).spreadHalf,
                onSwap: (pid, src) => onSwapThemedPhoto?.(page.page, el.id, pid, src),
                onRemove: () => { onRemoveThemedPhoto?.(page.page, el.id); onSelectElement?.(null); },
              } : undefined}
              onPickPhoto={el.type === 'photo' && onPickPhotoForElement
                ? () => {
                    // Open the picker AND dismiss the floating edit pill so it
                    // doesn't cover the "Mes photos" grid. The replace target is
                    // held in photoElementTarget, so deselecting is safe.
                    onPickPhotoForElement(page.page, el.id);
                    onSelectElement?.(null);
                  }
                : undefined}
              onDone={() => onSelectElement?.(null)}
            />
          )}
        </Fragment>
      )); })()}

      {/* Persistent centre cross — only while an element is mid-drag. Very
          low opacity so it sits in the background as a quiet alignment
          reference; the snap-guide lines below render on top at full
          opacity when the element actually lines up. */}
      {isAnyDragging && (
        <>
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: '50%', width: 1, marginLeft: -0.5,
              background: '#3B82F6', opacity: 0.18,
              zIndex: 24,
            }}
          />
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: '50%', height: 1, marginTop: -0.5,
              background: '#3B82F6', opacity: 0.18,
              zIndex: 24,
            }}
          />
        </>
      )}

      {/* Section 5 — snap guide lines. Rendered as 1px dashed strokes
          covering the full page height (vertical guides) or width
          (horizontal guides). pointer-events:none so they never block
          drag/click. */}
      {snapGuides.v.map((x) => (
        <div
          key={`vg-${x}`}
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{ left: `${x}%`, width: 1, background: '#3B82F6', boxShadow: '0 0 0 0.5px #3B82F6', zIndex: 25 }}
        />
      ))}
      {snapGuides.h.map((y) => (
        <div
          key={`hg-${y}`}
          className="absolute left-0 right-0 pointer-events-none"
          style={{ top: `${y}%`, height: 1, background: '#3B82F6', boxShadow: '0 0 0 0.5px #3B82F6', zIndex: 25 }}
        />
      ))}

      {/* FIX 2 — non-blocking safe-zone warning pill. Shown for the selected
          element (text / photo / shape — all live in `elements`) when its box
          crosses into the 5mm print safe band. Tracks live during drag (the
          element commits x/y continuously). pointer-events:none, never blocks. */}
      {(() => {
        const selEl = elements.find((e) => selectedElementId === e.id || selectedElementIds?.has(e.id));
        if (!selEl || editingElementId === selEl.id || !isElementInSafeZone(selEl)) return null;
        const cx = Math.min(Math.max(selEl.x + selEl.width / 2, 24), 76);
        const ty = Math.min(selEl.y + selEl.height + 2.5, 90);
        if (dock) {
          return createPortal(
            <div data-editor-toolbar="true" role="status" className="text-center rounded-xl px-3 py-1.5" style={{ background: '#1F2937', color: '#fff', fontSize: 12, fontWeight: 600 }}>
              ⚠️ Trop proche du bord — risque de coupure à l'impression
            </div>,
            dock,
          );
        }
        return (
          <div
            className="absolute pointer-events-none"
            style={{ left: `${cx}%`, top: `${ty}%`, transform: 'translateX(-50%)', zIndex: 40, maxWidth: '88%' }}
          >
            <div
              style={{
                background: '#1F2937', color: '#fff', fontSize: 9, fontWeight: 600,
                lineHeight: 1.25, textAlign: 'center', padding: '4px 8px', borderRadius: 9999,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              }}
            >
              ⚠️ Trop proche du bord — risque de coupure à l'impression
            </div>
          </div>
        );
      })()}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Interior Text Element (unified text system) ──────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// Thin adapter that maps a `PageElement` (interior text) into the props the
// shared `TextOverlayChrome` (same primitive the cover uses) consumes.
//
// Why: the previous interior text component (PageElementOverlay text branch)
// had a much busier visual than the cover — pill above + 8 handles + rotation
// handle + textarea — while the cover used a clean dashed-blue box + 3 corner
// dots + red trash. Customer asked for parity. The chrome owns the look + the
// drag/resize/inline-edit gestures; this wrapper owns:
//   - PageElement → chrome props mapping
//   - resize multiplier → fontSize update
//   - "More" extras (placeholder — rotate / lock / duplicate go here later)
//
// Spec: 2026-06-05 mobile editor fix request, "Unify text system".
/** Exported for the local render check (web/dev/vcheck.tsx: editor = preview = PDF = 3D). */
export function InteriorTextElement({
  element,
  pageNumber,
  isSelected,
  isEditing,
  onSelect,
  onStartEditing,
  onStopEditing,
  onUpdate,
  onMovePage,
}: {
  element: PageElement;
  pageNumber: number;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: (shiftKey: boolean) => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onUpdate: (updates: Partial<PageElement>) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onShowMoreToolbar?: () => void;
  /** Cross-page drag — fires when the element's centre crosses into another
   *  page in the spread. Parent splices the element from this page's array
   *  into the target page's array at the new (x, y) in target coords. */
  onMovePage?: (targetPageNumber: number, newX: number, newY: number) => void;
}) {
  // ITEM 7 (2026-07-06) — corner-resize changes the BOX (width/height) and lets
  // the text reflow, instead of zooming the font. The font is page-relative
  // (the PAGE wrapper owns `container-type: inline-size`, so cqi tracks page
  // width, NOT box width) — so keeping fontSize constant while the box width
  // changes makes the text wrap. Baseline box dims captured at gesture start.
  const resizeBaseRef = useRef<{ fontSize: number; width: number; height: number; x: number; y: number }>({
    fontSize: element.fontSize ?? 18,
    width: element.width ?? 80,
    height: element.height ?? 12,
    x: element.x ?? 0,
    y: element.y ?? 0,
  });

  // Mirror the element id into a ref so the drag handler we attach to
  // window listeners can read the *latest* element bbox without growing
  // a useCallback dep list that retriggers re-binds on every state edit.
  const elementRef = useRef(element);
  useEffect(() => { elementRef.current = element; }, [element]);

  // Cross-page drag. The chrome delegates the whole gesture to us via
  // onDragRequest; we own pointerdown → pointermove → pointerup.
  //
  // Implementation note: we do NOT call onMovePage DURING the gesture.
  // Calling it mid-drag would unmount this component (the element id moves
  // to another page's elements[]), leaving the window listeners pointing at
  // a stale React tree — ping-pong between pages would stall. Instead we
  // update x/y in SOURCE page coords during drag (the box visually drifts
  // past the page edge if the customer drags across the spine), and only
  // commit the page change at pointerup based on a single hit-test.
  const pendingDragRef = useRef<{ x: number; y: number } | null>(null);
  const dragRafRef = useRef<number | null>(null);

  const handleDragRequest = useCallback((e: React.PointerEvent) => {
    // No isEditing guard (2026-07-08): the chrome's edit-escape watcher hands
    // the gesture over the instant a drag is detected DURING editing — the
    // isEditing prop is still stale-true at that moment, and the chrome is the
    // sole caller, only for legitimate drags. Locked stays blocking.
    if (element.locked) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const captureEl = e.currentTarget as HTMLElement;
    try { captureEl.setPointerCapture(e.pointerId); } catch { /* defensive */ }

    if (!isSelected) onSelect(e.shiftKey);

    // Spread + page enumeration for the pointerup hit-test (data-spread +
    // data-page-number are stamped by PageView; same selectors PageElementOverlay
    // uses for shape/icon cross-page drag).
    const overlayEl = captureEl;
    const spread = overlayEl.closest('[data-spread="true"]') as HTMLElement | null;
    const pageEls = spread
      ? Array.from(spread.querySelectorAll<HTMLElement>('[data-page-number]'))
      : [];

    const startScreenX = e.clientX;
    const startScreenY = e.clientY;
    const startEl = elementRef.current;
    const startX = startEl.x;
    const startY = startEl.y;
    const startW = startEl.width;
    const startH = startEl.height;

    // Source-page rect — coords during the drag are interpreted in this
    // page's % space, even if the customer's finger ends up over the
    // adjacent page. That's why we deliberately do NOT clamp to [0, 100 −
    // size]: an element being dragged across the spine has to render past
    // the edge of the source page visually.
    const sourcePage = overlayEl.parentElement; // the [data-page-number] container
    const sourceRect = sourcePage?.getBoundingClientRect();
    const cw = sourceRect?.width ?? 1;
    const ch = sourceRect?.height ?? 1;

    const flush = () => {
      dragRafRef.current = null;
      const p = pendingDragRef.current;
      if (p) onUpdate({ x: p.x, y: p.y });
    };

    const onMove = (ev: PointerEvent) => {
      const dxPct = ((ev.clientX - startScreenX) / cw) * 100;
      const dyPct = ((ev.clientY - startScreenY) / ch) * 100;
      // No clamp — the element can drift visibly across the spine.
      pendingDragRef.current = { x: startX + dxPct, y: startY + dyPct };
      if (dragRafRef.current == null) {
        dragRafRef.current = requestAnimationFrame(flush);
      }
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      const p = pendingDragRef.current;
      pendingDragRef.current = null;

      // Hit-test once at release. If the centre of the element lands on a
      // DIFFERENT page than its source, call onMovePage with coords in the
      // target page's % space. Otherwise just commit x/y on the source.
      // The element's centre is approximated as the release point — close
      // enough for the typical "drop on the facing page" gesture.
      let targetPageEl: HTMLElement | null = null;
      for (const pEl of pageEls) {
        const r = pEl.getBoundingClientRect();
        if (ev.clientX >= r.left && ev.clientX <= r.right
            && ev.clientY >= r.top && ev.clientY <= r.bottom) {
          targetPageEl = pEl;
          break;
        }
      }
      const targetPageNum = targetPageEl
        ? Number(targetPageEl.getAttribute('data-page-number') ?? pageNumber)
        : pageNumber;

      if (targetPageNum !== pageNumber && targetPageEl) {
        const r = targetPageEl.getBoundingClientRect();
        // Convert release point → target-page-local %, then back-off by
        // the element's original half-size so it lands centred under the
        // finger (matches the cover's drop ergonomics).
        const tx = Math.max(0, Math.min(100 - startW, ((ev.clientX - r.left) / r.width) * 100 - startW / 2));
        const ty = Math.max(0, Math.min(100 - startH, ((ev.clientY - r.top)  / r.height) * 100 - startH / 2));
        onMovePage?.(targetPageNum, tx, ty);
      } else if (p) {
        // Stayed on the source — commit with a BLEED clamp (2026-07-08). Text
        // used to be forced FULLY on-page here, so dragging it toward an edge
        // snapped it back ("it goes back up"). Now it matches shapes/photos
        // (PageElementOverlay): the box may run PAST the trim edge so the
        // client can place text right to the border / into the bleed — the
        // "risque de coupure" pill is advisory, never a stop. We keep only a
        // minimum strip on-page so text can never be dragged fully out of
        // sight and lost. KEEP = min(20% of page, half the box).
        const keepX = Math.min(20, startW / 2);
        const keepY = Math.min(20, startH / 2);
        const fx = Math.max(keepX - startW, Math.min(100 - keepX, p.x));
        const fy = Math.max(keepY - startH, Math.min(100 - keepY, p.y));
        onUpdate({ x: fx, y: fy });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [isEditing, element.locked, isSelected, pageNumber, onUpdate, onMovePage, onSelect]);

  const holderEmpty = !!element.holder && !(element.text ?? '').trim();
  return (
    <TextOverlayChrome
      elementId={element.id}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      // STEP 3 — an empty HOLDER shows its role's name, faded, until a person
      // writes in it; nothing of it prints (lib/decor/holders.ts).
      text={holderEmpty && !isEditing ? HOLDER_HINT[element.holder as HolderRole] ?? '' : (element.text ?? '')}
      fontFamily={element.fontFamily ?? "'Fraunces', 'Cormorant Garamond', Georgia, serif"}
      // Interior keeps the cqi-based sizing for PDF render parity (1cqi =
      // 1% of page width; `fontSize / 4` is the calibrated divisor that
      // matches editorRefW=400 in pdfGenerator.ts).
      fontSizePx={`${(element.fontSize ?? 18) / 4}cqi`}
      fontVariationSettings={printOpticalSize(element.fontSize)}
      fontWeight={element.fontWeight ?? '600'}
      fontStyle={element.fontStyle ?? 'normal'}
      textDecoration={element.textDecoration ?? 'none'}
      color={holderEmpty && !isEditing ? '#9A8F8099' : (element.fontColor ?? '#0F172A')}
      textAlign={element.textAlign ?? 'center'}
      outlineColor={element.outlineColor}
      outlineWidth={element.outlineWidth}
      bandFill={element.bandFill}
      bandOpacity={element.bandOpacity}
      rotation={element.rotation}
      selectAllOnEdit={(element.text ?? '').trim() === NEW_TEXT_PLACEHOLDER}
      onDragRequest={handleDragRequest}
      isSelected={isSelected}
      isEditing={isEditing}
      locked={!!element.locked}
      onSelect={onSelect}
      onStartEditing={onStartEditing}
      onStopEditing={onStopEditing}
      onTextChange={(next) => onUpdate({ text: next })}
      onPositionChange={(nextX, nextY) => onUpdate({ x: nextX, y: nextY })}
      onResize={(ratio, phase) => {
        if (phase === 'start') {
          resizeBaseRef.current = {
            fontSize: element.fontSize ?? 18,
            width: element.width ?? 80,
            height: element.height ?? 12,
            x: element.x ?? 0,
            y: element.y ?? 0,
          };
          return;
        }
        if (phase === 'end') return;
        // RESIZE = scale the TEXT itself under the cursor, mirroring the photo
        // free-transform (which scales the image). BEFORE: only the frame
        // width/height scaled while the font stayed fixed — so the centred
        // flex box just re-wrapped and re-centred the same-size text, which
        // read as the text *moving* (up/down/left), not resizing, and the
        // handle never tracked the cursor. NOW: the font scales by the drag
        // ratio, the box scales by the SAME applied ratio (wrapping stays
        // identical → the block zooms as one unit), and the block CENTRE is
        // held fixed so there is zero position drift.
        const base = resizeBaseRef.current;
        const nextFont = Math.max(6, Math.min(96, base.fontSize * ratio));
        const applied = base.fontSize > 0 ? nextFont / base.fontSize : 1;
        const cx = base.x + base.width / 2;
        const cy = base.y + base.height / 2;
        const nextW = Math.max(6, Math.min(100, base.width * applied));
        const nextH = Math.max(3, Math.min(100, base.height * applied));
        const nextX = cx - nextW / 2;
        const nextY = cy - nextH / 2;
        onUpdate({
          fontSize: +nextFont.toFixed(2),
          width: +nextW.toFixed(2),
          height: +nextH.toFixed(2),
          x: +nextX.toFixed(2),
          y: +nextY.toFixed(2),
        });
      }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Page Element Overlay (draggable, selectable, editable) ───────────────────
// ═══════════════════════════════════════════════════════════════════════════════

/** Exported for the local render check (web/dev/vcheck.tsx: editor = preview = PDF = 3D). */
export function PageElementOverlay({
  element,
  pageNumber,
  isSelected,
  isEditing,
  onSelect,
  onDoubleClick,
  onUpdate,
  onDelete,
  onDuplicate,
  onShowMoreToolbar,
  onMovePage,
  onStopEditing,
  siblings = [],
  onSnapGuides,
  onDragStateChange,
  photoUrl = null,
  onPickPhoto,
  onDone,
  passive = false,
  themedWindow,
}: {
  element: PageElement;
  /** Page this element currently lives on. Used by the drag handler to
   *  detect when the customer has dragged across the spine into the
   *  adjacent page. */
  pageNumber: number;
  isSelected: boolean;
  isEditing: boolean;
  /** Called when the element is clicked. `additive` is true when the
   *  user held shift (desktop) so the parent knows to add-to-selection
   *  instead of replacing it. */
  onSelect: (additive?: boolean) => void;
  onDoubleClick: () => void;
  onUpdate: (updates: Partial<PageElement>) => void;
  /** Fires when the customer hits the inline trash icon. */
  onDelete: () => void;
  /** D78 Fix 1 (2026-05-21): clone this element. Wired by the SelectionPill's
   *  duplicate button; routes through the parent's `handleDuplicate`. */
  onDuplicate?: () => void;
  /** D78 Fix 1 (2026-05-21): opens the legacy FloatingElementToolbar (font,
   *  colour, opacity, position…) — the heavy controls now live behind the
   *  pill's "More" button instead of always being shown. */
  onShowMoreToolbar?: () => void;
  /** Fires mid-drag when the element's centre crosses into a different
   *  page in the spread. The parent removes the element from this page
   *  and re-inserts it into the target page at (newX, newY) % coords. */
  onMovePage?: (targetPageNumber: number, newX: number, newY: number) => void;
  onStopEditing: () => void;
  /** Other elements on the same page — used by the snap-guide calc
   *  during drag to find alignment targets (edges, centres). */
  siblings?: PageElement[];
  /** Called during drag whenever the snap state changes. Parent
   *  renders dashed blue guide lines based on these axes. */
  onSnapGuides?: (guides: { v: number[]; h: number[] }) => void;
  /** Fires true on drag start, false on drag end. Parent uses it to show a
   *  persistent low-opacity centre cross while ANY drag is active, so the
   *  customer always has a midline reference even before the snap fires. */
  onDragStateChange?: (isDragging: boolean) => void;
  /** ISSUE 8 — signed display URL for element.photoId (photo elements). */
  photoUrl?: string | null;
  /** ISSUE 8 — open "Mes photos" targeted at this EMPTY photo element. */
  onPickPhoto?: () => void;
  /** UNIFICATION — the white pill's Terminé: deselect this element. */
  onDone?: () => void;
  /** DESIGN SYSTEM v2 — a model's piece (paper, frame, tape…): drawn, never moved; taps and
   *  drops go through it to the photo window underneath. */
  passive?: boolean;
  /** DESIGN SYSTEM v2 — this photo is a model's WINDOW: the box never moves or resizes; the
   *  client drags the photo INSIDE it (pan), zooms it (−/+, wheel, pinch), swaps it (drop a
   *  photo on it, or « Changer la photo ») and removes it when the model allows. A face the
   *  window cuts shows a warning — never a refusal: she decides. */
  themedWindow?: {
    /** The photo's size + faces (photos.face_data); null = unknown (its loaded size is used, no face warning). */
    photo: WindowPhoto | null;
    /** One half of a panorama (S5): half of ONE crop across the spine — it does not pan or zoom
     *  on its own (the two halves would no longer meet), and a face on the OTHER half is not cut. */
    spreadHalf?: 'left' | 'right';
    onSwap: (photoId: string, source: 'slot' | 'sidebar') => void;
    onRemove: () => void;
  };
}) {
  // « Nouvel éditeur mobile »: the element's dark pill docks at the bottom.
  const dock = useMobileDock();
  const elRef = useRef<HTMLDivElement>(null);
  // UNIFICATION (2026-07-05) — photo elements use the SAME white ClampedPill
  // as slot images. Track this element's viewport rect while selected (same
  // rAF-lite pattern as the slot trackers; one element selected at a time).
  const [photoPillRect, setPhotoPillRect] = useState<DOMRect | null>(null);
  // Local mirror of the drag state (parent's onDragStateChange is one-way).
  // The pill tracker + pill render pause while dragging (2026-07-05 fix).
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    // JANK FIX (2026-07-05): the per-frame rAF getBoundingClientRect→setState
    // loop that positions the photo pill used to run DURING a drag, on top of
    // the drag's own per-move setState — doubling re-renders every frame and
    // making the element stutter / "not go where I want". Pause the tracker
    // while dragging (the pill is hidden then anyway); on drop it recomputes.
    if (!(isSelected && !isEditing && element.type === 'photo') || dragging) {
      setPhotoPillRect(null);
      return;
    }
    const update = () => {
      const r = elRef.current?.getBoundingClientRect();
      if (r) setPhotoPillRect(r);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    let raf = 0;
    const tick = () => { update(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      cancelAnimationFrame(raf);
    };
  }, [isSelected, isEditing, element.type, dragging]);
  // Pill scale semantics for a photo ELEMENT: 100% = the 60%-of-page default
  // box. −/+ scale the FRAME (aspect kept, centred, clamped to the page).
  const PHOTO_BASE_W = 60;
  const photoScale = (element.width ?? PHOTO_BASE_W) / PHOTO_BASE_W;
  const applyPhotoScale = (next: number) => {
    const curW = element.width ?? PHOTO_BASE_W;
    const curH = element.height ?? PHOTO_BASE_W;
    const ratio = curH / Math.max(1, curW);
    const w = Math.max(12, Math.min(100, PHOTO_BASE_W * next));
    const h = Math.max(8, Math.min(100, w * ratio));
    const cx = (element.x ?? 0) + curW / 2;
    const cy = (element.y ?? 0) + curH / 2;
    onUpdate({
      x: Math.max(0, Math.min(100 - w, cx - w / 2)),
      y: Math.max(0, Math.min(100 - h, cy - h / 2)),
      width: w,
      height: h,
    });
  };

  // ── CROP (2026-07-23) — non-destructive crop window on a photo element. ──
  // The crop lives as `element.crop` {x,y,w,h} in 0..1 fractions of the FULL
  // image — the original file is never modified. Crop MODE shows the whole
  // photo contain-fit inside the element's footprint (dimmed outside the
  // window) with a draggable/resizable gold rect; Enregistrer commits, the
  // rect can be re-opened and adjusted any time. While crop mode is open the
  // element's own drag/resize/rotate are suspended (rotation re-applies on
  // exit) so gestures belong to the crop rect alone.
  const [cropMode, setCropMode] = useState(false);
  const [draftCrop, setDraftCrop] = useState<{ x: number; y: number; w: number; h: number }>({ x: 0, y: 0, w: 1, h: 1 });
  const draftCropRef = useRef(draftCrop);
  draftCropRef.current = draftCrop;
  // Natural pixel size of the loaded photo (from <img>.onLoad) — needed to
  // express the crop window's aspect for the cover math (parity with the
  // PDF, which reads the embedded bitmap's dimensions).
  const [natSize, setNatSize] = useState<{ w: number; h: number } | null>(null);
  // Layout aspect of the element box (offsetWidth/offsetHeight — unaffected
  // by the rotation transform, unlike getBoundingClientRect). Re-measured
  // when the box shape changes.
  const [boxAR, setBoxAR] = useState<number | null>(null);
  useEffect(() => {
    const el = elRef.current;
    if (el && el.offsetHeight > 0) setBoxAR(el.offsetWidth / el.offsetHeight);
  }, [element.width, element.height]);

  const enterCropMode = () => {
    setDraftCrop(element.crop ?? { x: 0, y: 0, w: 1, h: 1 });
    setCropMode(true);
  };
  // Deselecting (tap outside) while cropping = cancel — never leave a
  // pill-less crop overlay stranded on screen.
  useEffect(() => {
    if (!isSelected && cropMode) setCropMode(false);
  }, [isSelected, cropMode]);

  /** Move/resize the crop rect. `mode` is 'move' or a resize dir (nw…w);
   *  same pointer-capture + anchor-pinning pattern as handleResizeStart,
   *  but in normalized IMAGE coords clamped to the photo's bounds. */
  const startCropDrag = useCallback((e: React.PointerEvent, mode: 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w') => {
    e.stopPropagation();
    e.preventDefault();
    const host = elRef.current;
    const nat = natSize;
    if (!host || !nat) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const rect = host.getBoundingClientRect(); // crop mode renders unrotated → true box
    const imgAR = nat.w / nat.h;
    const hostAR = rect.width / rect.height;
    // Contain-fit display size of the full photo inside the element box (px).
    const dispW = imgAR > hostAR ? rect.width : rect.height * imgAR;
    const dispH = imgAR > hostAR ? rect.width / imgAR : rect.height;
    const startX = e.clientX, startY = e.clientY;
    const start = { ...draftCropRef.current };
    const MIN = 0.08; // crop can never shrink below 8% of the photo
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const dx = (ev.clientX - startX) / dispW; // pointer delta in image units
      const dy = (ev.clientY - startY) / dispH;
      let { x, y, w, h } = start;
      if (mode === 'move') {
        x = clamp(start.x + dx, 0, 1 - start.w);
        y = clamp(start.y + dy, 0, 1 - start.h);
      } else {
        if (mode.includes('w')) { const nx = clamp(start.x + dx, 0, start.x + start.w - MIN); w = start.x + start.w - nx; x = nx; }
        if (mode.includes('e')) { w = clamp(start.w + dx, MIN, 1 - start.x); }
        if (mode.includes('n')) { const ny = clamp(start.y + dy, 0, start.y + start.h - MIN); h = start.y + start.h - ny; y = ny; }
        if (mode.includes('s')) { h = clamp(start.h + dy, MIN, 1 - start.y); }
      }
      setDraftCrop({ x, y, w, h });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [natSize]);

  /** Enregistrer — commit the draft rect onto the element. A (near-)full rect
   *  clears the crop entirely. Unless the size is locked/pinned, the frame
   *  keeps its width and adapts its height to the crop's shape (centre kept)
   *  so the photo shows exactly the chosen window with no extra trimming;
   *  with LOCK SIZE on, the frame stays put and the window cover-fills it. */
  const saveCrop = () => {
    const cr = draftCropRef.current;
    const isFull = cr.x <= 0.005 && cr.y <= 0.005 && cr.w >= 0.99 && cr.h >= 0.99;
    const r4 = (v: number) => Math.round(v * 10000) / 10000;
    const updates: Partial<PageElement> = {
      crop: isFull ? undefined : { x: r4(cr.x), y: r4(cr.y), w: r4(cr.w), h: r4(cr.h) },
    };
    if (!isFull && !element.lockSize && !element.locked && natSize && boxAR) {
      const winAR = (cr.w * natSize.w) / (cr.h * natSize.h);
      // boxAR(px) = (w%/h%)·pageAR ⇒ new h% for boxAR(px)=winAR is h%·boxAR/winAR.
      const curW = element.width ?? PHOTO_BASE_W;
      const curH = element.height ?? PHOTO_BASE_W;
      const pageAR = (boxAR * curH) / Math.max(1, curW); // page px aspect (w/h)
      let wNew = curW;
      let hNew = curH * (boxAR / winAR);
      if (hNew > 100) {
        // A very tall crop would push the frame past the page — cap the
        // height and narrow the width instead so the frame's SHAPE still
        // matches the crop exactly (no silent re-trim of the chosen window).
        hNew = 100;
        wNew = Math.max(12, Math.min(100, (winAR * hNew) / Math.max(0.01, pageAR)));
      }
      hNew = Math.max(8, hNew);
      const cx = (element.x ?? 0) + curW / 2;
      const cy = (element.y ?? 0) + curH / 2;
      updates.width = wNew;
      updates.height = hNew;
      updates.x = Math.max(0, Math.min(100 - wNew, cx - wNew / 2));
      updates.y = Math.max(0, Math.min(100 - hNew, cy - hNew / 2));
    }
    onUpdate(updates);
    setCropMode(false);
  };

  // ── DESIGN SYSTEM v2 — the photo INSIDE a model's window ────────────────
  // One finger / the mouse pans, two fingers pinch-zoom, the wheel zooms; the
  // crop keeps the window's shape (lib/decor/themedWindow.ts) so the editor,
  // the preview and the print show the same part (cropMath: cropCss = cropSource).
  const winAR = boxAspect(element);
  const winPhoto = themedWindow?.photo ?? null;
  const winDims = useMemo(() => winPhoto ?? (natSize ? { width: natSize.w, height: natSize.h } : null), [winPhoto, natSize]);
  const winCrop = themedWindow && winDims ? currentCrop(element.crop, winDims, winAR) : null;
  const winCropRef = useRef(winCrop);
  winCropRef.current = winCrop;
  const winFaceCut = !!themedWindow?.photo && !!winCrop && !!element.photoId
    && (themedWindow.spreadHalf ? headsCutOnHalf(themedWindow.photo, winCrop, themedWindow.spreadHalf) : headsCut(themedWindow.photo, winCrop));
  const gestureRef = useRef<{ pts: Map<number, { x: number; y: number }>; crop0: { x: number; y: number; w: number; h: number } | null;
    start: { x: number; y: number } | null; dist0: number; mid: { x: number; y: number }; moved: boolean; rect: DOMRect | null } | null>(null);
  const zoomWindow = useCallback((factor: number, ax = 0.5, ay = 0.5) => {
    const c = winCropRef.current;
    if (!c || !winDims || themedWindow?.spreadHalf) return;
    onUpdate({ crop: zoomCrop(c, winDims, winAR, factor, ax, ay) });
  }, [winDims, winAR, onUpdate, themedWindow?.spreadHalf]);
  const handleThemedPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    const wasSelected = isSelected;
    // Mouse, not selected yet: no preventDefault and NO select here — the browser's own
    // drag starts with this photo, so it can go to another page or frame (owner,
    // 2026-10-17 — swap two model pages). Selecting on press re-rendered the element
    // as « selected » (not draggable) before the drag began: only the bare image ghost
    // moved and the drop carried no photo. A click still selects (onClick).
    if (!wasSelected && e.pointerType === 'mouse') return;
    onSelect(false);
    e.preventDefault();
    // Touch: the first tap only selects (the page still scrolls / swipes); then the photo moves.
    if (!wasSelected) return;
    if (!element.photoId || !winDims || themedWindow?.spreadHalf) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const g = gestureRef.current ?? (gestureRef.current = { pts: new Map(), crop0: null, start: null, dist0: 0, mid: { x: 0.5, y: 0.5 }, moved: false, rect: null });
    g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.rect = elRef.current?.getBoundingClientRect() ?? null;
    g.crop0 = winCropRef.current;
    const pts = [...g.pts.values()];
    if (pts.length >= 2) {
      g.dist0 = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const r = g.rect;
      g.mid = r ? { x: ((pts[0].x + pts[1].x) / 2 - r.left) / r.width, y: ((pts[0].y + pts[1].y) / 2 - r.top) / r.height } : { x: 0.5, y: 0.5 };
      return;
    }
    g.start = { x: e.clientX, y: e.clientY };
    g.moved = false;
    setDragging(true);
    const onMove = (ev: PointerEvent) => {
      const gg = gestureRef.current;
      if (!gg || !gg.pts.has(ev.pointerId) || !gg.crop0 || !gg.rect) return;
      ev.preventDefault();
      gg.pts.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      const all = [...gg.pts.values()];
      if (all.length >= 2) {
        const d = Math.hypot(all[0].x - all[1].x, all[0].y - all[1].y);
        onUpdate({ crop: zoomCrop(gg.crop0, winDims, winAR, d / gg.dist0, Math.min(1, Math.max(0, gg.mid.x)), Math.min(1, Math.max(0, gg.mid.y))) });
        return;
      }
      if (!gg.start) return;
      const dx = ev.clientX - gg.start.x, dy = ev.clientY - gg.start.y;
      if (!gg.moved && Math.hypot(dx, dy) < 3) return;
      gg.moved = true;
      onUpdate({ crop: panCrop(gg.crop0, dx / gg.rect.width, dy / gg.rect.height) });
    };
    const onUp = (ev: PointerEvent) => {
      const gg = gestureRef.current;
      if (!gg) return;
      gg.pts.delete(ev.pointerId);
      if (gg.pts.size === 1) {
        // a pinch ended with one finger still down: it pans from here
        const [rest] = [...gg.pts.values()];
        gg.start = rest; gg.crop0 = winCropRef.current;
        return;
      }
      if (gg.pts.size > 0) return;
      gestureRef.current = null;
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [isSelected, onSelect, element.photoId, winDims, winAR, onUpdate, themedWindow?.spreadHalf]);
  // The wheel zooms a selected window (non-passive, so the page does not scroll under it).
  useEffect(() => {
    const host = elRef.current;
    if (!host || !themedWindow || !isSelected) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const r = host.getBoundingClientRect();
      zoomWindow(Math.exp(-ev.deltaY * 0.0025), (ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.height);
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  }, [themedWindow, isSelected, zoomWindow]);

  // Pointer Events unify mouse + touch + pen so mobile customers can
  // drag the element with their finger — the old MouseEvent-only handler
  // was the reason text/shape elements were undraggable on iPhone.
  // setPointerCapture keeps the gesture alive even if the finger drifts
  // off the element.
  const handleMouseDown = useCallback((e: React.PointerEvent) => {
    if (isEditing) return; // Don't drag while editing text
    if (cropMode) return;  // CROP mode owns all gestures — no element drag
    // Mouse-only check: ignore right-click / middle-click.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    onSelect(e.shiftKey);                                  // shift = additive
    // D78 Fix 1: locked elements still select on click but can't be
    // dragged. Return AFTER onSelect so the SelectionPill still appears
    // so the customer can unlock it.
    if (element.locked) return;

    // Tell the parent a drag is starting so it can show the persistent
    // centre-cross alignment overlay until onUp clears it.
    onDragStateChange?.(true);
    setDragging(true); // pause the photo-pill rAF tracker so drag stays smooth

    const elRect = elRef.current?.getBoundingClientRect();
    if (!elRect) return;
    // Offset of the pointer inside the element at gesture start. Keeps
    // the element "stuck" to the finger/cursor regardless of where the
    // customer grabbed it (centre, corner, etc).
    const grabX = e.clientX - elRect.left;
    const grabY = e.clientY - elRect.top;

    // Enumerate every page in the visible spread so the drag can switch
    // the element's page when the cursor crosses the spine. Falls back
    // to the current parent if no data-page-number is in scope (older
    // wrappers without the attribute, defensive only).
    const spread = elRef.current?.closest('[data-spread="true"]') as HTMLElement | null;
    const pageEls = (spread
      ? Array.from(spread.querySelectorAll<HTMLElement>('[data-page-number]'))
      : []);
    const currentParent = elRef.current?.parentElement;

    const onMove = (ev: PointerEvent) => {
      // Where the element's top-left would land if it stayed glued to
      // the pointer (in viewport coords).
      const elScreenX = ev.clientX - grabX;
      const elScreenY = ev.clientY - grabY;
      const elCenterScreenX = elScreenX + elRect.width / 2;
      const elCenterScreenY = elScreenY + elRect.height / 2;

      // Find the page whose rect contains the element's centre point.
      // If none (cursor outside any page), keep the element on its
      // current page so it stays draggable instead of vanishing.
      let targetPage: HTMLElement | null = null;
      for (const p of pageEls) {
        const r = p.getBoundingClientRect();
        if (
          elCenterScreenX >= r.left && elCenterScreenX <= r.right
          && elCenterScreenY >= r.top && elCenterScreenY <= r.bottom
        ) {
          targetPage = p;
          break;
        }
      }
      const refEl = targetPage ?? currentParent ?? null;
      if (!refEl) return;
      const refRect = refEl.getBoundingClientRect();
      const targetPageNum = targetPage?.dataset.pageNumber
        ? Number(targetPage.dataset.pageNumber)
        : pageNumber;

      // Element coords in the target page's %-space.
      const rawX = ((elScreenX - refRect.left) / refRect.width) * 100;
      const rawY = ((elScreenY - refRect.top) / refRect.height) * 100;

      // Snap to the target page's centre + edges + same-page siblings.
      // Cross-page snap is intentionally skipped — sibling positions
      // are in the source page's coordinate system, snapping across
      // would jump the element to the wrong spot.
      const SNAP = 1.5;
      // BLEED DRAG (2026-07-06): let the element run PAST the page edges (drag it
      // under/over/off-side, like Canva / print bleed) instead of pinning it fully
      // on-page. Keep a minimum strip on-page so it can never be dragged completely
      // out of sight. KEEP = min(20% of page, half the element).
      const keepX = Math.min(20, element.width / 2);
      const keepY = Math.min(20, element.height / 2);
      let nx = Math.max(keepX - element.width, Math.min(100 - keepX, rawX));
      let ny = Math.max(keepY - element.height, Math.min(100 - keepY, rawY));
      const vTargets: number[] = [0, 50, 100];
      const hTargets: number[] = [0, 50, 100];
      if (targetPageNum === pageNumber) {
        for (const sib of siblings) {
          if (sib.id === element.id) continue;
          vTargets.push(sib.x, sib.x + sib.width / 2, sib.x + sib.width);
          hTargets.push(sib.y, sib.y + sib.height / 2, sib.y + sib.height);
        }
      }
      const activeV: number[] = [];
      const activeH: number[] = [];
      for (const t of vTargets) {
        if (Math.abs(nx - t) < SNAP)                       { nx = t;                       activeV.push(t); break; }
        if (Math.abs((nx + element.width / 2) - t) < SNAP) { nx = t - element.width / 2;   activeV.push(t); break; }
        if (Math.abs((nx + element.width) - t) < SNAP)     { nx = t - element.width;       activeV.push(t); break; }
      }
      for (const t of hTargets) {
        if (Math.abs(ny - t) < SNAP)                       { ny = t;                       activeH.push(t); break; }
        if (Math.abs((ny + element.height / 2) - t) < SNAP){ ny = t - element.height / 2;  activeH.push(t); break; }
        if (Math.abs((ny + element.height) - t) < SNAP)    { ny = t - element.height;      activeH.push(t); break; }
      }
      onSnapGuides?.({ v: targetPageNum === pageNumber ? activeV : [], h: targetPageNum === pageNumber ? activeH : [] });

      if (targetPageNum !== pageNumber && onMovePage) {
        // Cross-page move — parent removes the element from this page
        // and inserts it on the target. After re-render, this drag
        // handler will be torn down and the element's new overlay
        // takes over for the rest of the gesture.
        onMovePage(targetPageNum, nx, ny);
      } else {
        onUpdate({ x: nx, y: ny });
      }
    };
    const onUp = () => {
      onSnapGuides?.({ v: [], h: [] });   // hide snap-line guides
      onDragStateChange?.(false);         // hide persistent centre cross
      setDragging(false);                 // resume the pill tracker + reshow pill
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [element, pageNumber, isEditing, cropMode, onSelect, onUpdate, onMovePage, siblings, onSnapGuides, onDragStateChange]);

  // 8-direction resize handler (D78 Fix 1, 2026-05-21).
  // Each handle (4 corners + 4 edge midpoints) routes through here with
  // a direction string ('nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w').
  // Corner drags resize on BOTH axes; edges resize on ONE axis. The anchor
  // point (opposite corner or edge) stays fixed: e.g., 'nw' keeps the
  // bottom-right pinned and grows the top-left towards the cursor.
  //
  // Text elements get uniform-scale corner behaviour with fontSize
  // following the larger of the two axis ratios — same rationale as the
  // previous single-corner handler. Edge drags on text resize only that
  // one axis without touching fontSize (lets the customer widen a text
  // box without making the font bigger).
  type ResizeDir = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
  const handleResizeStart = useCallback((dir: ResizeDir, e: React.PointerEvent) => {
    if (element.locked || element.lockSize) return; // LOCK SIZE: resize off, move/rotate stay
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const parent = elRef.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const start = { x: element.x, y: element.y, w: element.width, h: element.height };
    const startFontSize = element.fontSize ?? 18;
    const isTextElement = element.type === 'text';
    const isCorner = dir === 'nw' || dir === 'ne' || dir === 'sw' || dir === 'se';

    const onMove = (ev: PointerEvent) => {
      const dx = ((ev.clientX - startX) / rect.width) * 100;
      const dy = ((ev.clientY - startY) / rect.height) * 100;

      // For text corners we want uniform scale so the box keeps its
      // aspect AND the fontSize tracks the visual size. Use the larger
      // of the two axis ratios so dragging either axis grows the text.
      if (isTextElement && isCorner) {
        const wRatio = (dir.includes('e') ? (start.w + dx) : (start.w - dx)) / start.w;
        const hRatio = (dir.includes('s') ? (start.h + dy) : (start.h - dy)) / start.h;
        const scale = Math.max(0.2, Math.max(wRatio, hRatio));
        const newW = start.w * scale;
        const newH = start.h * scale;
        let nx = start.x, ny = start.y;
        if (dir.includes('w')) nx = start.x + start.w - newW;
        if (dir.includes('n')) ny = start.y + start.h - newH;
        const newFs = Math.max(6, Math.min(200, startFontSize * scale));
        onUpdate({
          x: Math.max(0, Math.min(100 - newW, nx)),
          y: Math.max(0, Math.min(100 - newH, ny)),
          width: Math.max(5, newW),
          height: Math.max(3, newH),
          fontSize: newFs,
        });
        return;
      }

      // Generic 8-direction resize. Move the cursor-side edge(s) by the
      // delta; the opposite edge(s) stay pinned. Width/height shrink or
      // grow accordingly; the x/y origin moves only when dragging from
      // the north or west side (because growing west = moving x left).
      let nx = start.x, ny = start.y, nw = start.w, nh = start.h;
      if (dir.includes('w')) { nx = start.x + dx; nw = start.w - dx; }
      if (dir.includes('e')) { nw = start.w + dx; }
      if (dir.includes('n')) { ny = start.y + dy; nh = start.h - dy; }
      if (dir.includes('s')) { nh = start.h + dy; }

      // Min sizes — when shrinking past min, freeze the dragged edge at
      // the min boundary instead of letting it cross over the anchor.
      if (nw < 5) {
        if (dir.includes('w')) nx = start.x + start.w - 5;
        nw = 5;
      }
      if (nh < 3) {
        if (dir.includes('n')) ny = start.y + start.h - 3;
        nh = 3;
      }

      // Clamp inside the page bounds.
      nx = Math.max(0, Math.min(100 - nw, nx));
      ny = Math.max(0, Math.min(100 - nh, ny));

      onUpdate({ x: nx, y: ny, width: nw, height: nh });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [element, onUpdate]);

  // Rotation drag handle (D78 Fix 1, 2026-05-21). Sits below the frame
  // bottom centre. Dragging it changes element.rotation (degrees, around
  // the element centre). Uses getBoundingClientRect() to find the centre
  // because the wrapper already has `transform: rotate(...)` applied —
  // the axis-aligned bbox's centre still equals the rotation centre as
  // long as transform-origin is 'center'. Snaps to common angles within
  // ±5° for usability.
  const handleRotateStart = useCallback((e: React.PointerEvent) => {
    if (element.locked) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const elRect = elRef.current?.getBoundingClientRect();
    if (!elRect) return;
    const cx = elRect.left + elRect.width / 2;
    const cy = elRect.top + elRect.height / 2;
    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    const startRotation = element.rotation ?? 0;
    const onMove = (ev: PointerEvent) => {
      const angle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      let next = startRotation + (angle - startAngle);
      // Normalise to [-180, 180] for nicer storage.
      next = ((next + 180) % 360 + 360) % 360 - 180;
      // Snap to 0/45/90/-45/-90/-135/-180/135/180 within ±5°.
      const snaps = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
      for (const s of snaps) {
        if (Math.abs(next - s) < 5) { next = s === -180 ? 180 : s; break; }
      }
      onUpdate({ rotation: next });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [element, onUpdate]);

  // Compose the CSS transform for flipH/flipV. Each axis multiplies the
  // scale by -1. We avoid setting transform when neither flip is on so
  // we don't trigger an unneeded compositing layer.
  // D78 (2026-05-21): defensively ignore flipH/flipV for text elements —
  // mirrored text is never a legitimate design state and we've seen it
  // sneak in via legacy data (accidental flip-button tap before the
  // !isText guard shipped). The toolbar's reset button still lets the
  // customer clear the underlying flag, but at the render layer we
  // refuse to mirror text regardless of what's stored.
  // STEP 3 — a label tied to an empty holder is shown faded and never printed.
  const labelHidden = !!element.labelFor && hiddenInPrint(element, siblings);
  const allowFlip = element.type !== 'text';
  const flipPart = (allowFlip && (element.flipH || element.flipV))
    ? `scaleX(${element.flipH ? -1 : 1}) scaleY(${element.flipV ? -1 : 1})`
    : '';
  // D78 Fix 1: compose rotate + flip into a single transform. Rotate
  // first so flip mirrors the post-rotation visual — matches how the
  // toolbar reads in screen space when both are applied.
  const rotationPart = element.rotation ? `rotate(${element.rotation}deg)` : '';
  // CROP mode displays the photo flat (no rotation/flip) so the crop rect is
  // drawn in plain image space; the transform re-applies on exit/save.
  const composedTransform = cropMode
    ? undefined
    : ([rotationPart, flipPart].filter(Boolean).join(' ') || undefined);

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${element.x}%`,
    top: `${element.y}%`,
    width: `${element.width}%`,
    height: `${element.height}%`,
    // Z-ORDER (2026-07-06): client free elements (text, detached photos, icons)
    // sit ABOVE the template's decorative overlay PNG (zIndex 1000) so text/photos
    // added on top of a templated page actually show ON TOP, not behind the art.
    // Selected element floats above its siblings.
    zIndex: isSelected ? 1020 : 1010,
    cursor: isEditing ? 'text' : themedWindow ? (isSelected ? 'grab' : 'pointer') : (element.locked ? 'default' : 'move'),
    ...(passive ? { pointerEvents: 'none' as const } : {}),
    ...(themedWindow && isSelected ? { touchAction: 'none' as const } : {}),
    // D78 Fix 1: outline replaced by the SelectionFrame below (rendered
    // inside the wrapper). Keep outline off so the new dashed/border
    // frame is the single visual cue.
    transform: composedTransform,
    transformOrigin: 'center',
  };

  return (
    <div
      ref={elRef}
      data-element-id={element.id}
      // ISSUE 3 (2026-07-05) — iOS long-press was popping the native black
      // callout ON TOP of our own selection pill. Same suppression trio the
      // photo slots got; contextmenu blocked so only OUR controls ever show.
      style={{ ...baseStyle, WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
      // DESKTOP drag-drop (2026-07-08): photo elements accept a photo dragged
      // from the sidebar tray — same dataTransfer contract as the grid slots
      // ('photoId'). Empty frame → fills it; filled → replaces. stopPropagation
      // so the page/slot drop handlers underneath never double-handle it.
      // ⚠️ THE DROP EFFECT MATCHES THE DRAG (owner, 2026-10-17): a photo dragged from a page
      // says « move »; a frame that answered « copy » was refused by the browser — no drop
      // event at all, so two model pages could never trade their photos.
      onDragOver={element.type === 'photo' ? (e) => {
        e.preventDefault(); e.stopPropagation();
        const allowed = e.dataTransfer.effectAllowed;
        e.dataTransfer.dropEffect = allowed === 'move' ? 'move' : allowed === 'link' ? 'link' : 'copy';
      } : undefined}
      onDrop={element.type === 'photo' ? (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pid = e.dataTransfer.getData('photoId');
        const src = (e.dataTransfer.getData('photoSource') as 'slot' | 'sidebar') || 'sidebar';
        // A model's window swaps (face-safe crop, counters); any other photo element takes the id.
        if (pid) { if (themedWindow) themedWindow.onSwap(pid, src); else onUpdate({ photoId: pid }); }
      } : undefined}
      onPointerDown={themedWindow ? handleThemedPointerDown : handleMouseDown}
      // A model's photo, not selected: dragged like a grid photo (same dataTransfer
      // contract) — dropped on another frame the two trade (swapIntoWindow 'slot').
      draggable={!!themedWindow && !!element.photoId && !isSelected}
      onDragStart={themedWindow && element.photoId && !isSelected ? (e) => {
        e.stopPropagation();
        e.dataTransfer.setData('photoId', element.photoId!);
        e.dataTransfer.setData('photoSource', 'slot');
        e.dataTransfer.effectAllowed = 'copyMove';
      } : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e.shiftKey);
        // Mobile single-tap on a text element jumps straight to inline edit
        // mode. Desktop keeps the double-click semantic so dragging text
        // around with a mouse doesn't accidentally trigger the textarea.
        // Detection: `pointer: coarse` is touch-primary (iOS Safari, Android
        // Chrome). The wrapper's pointerdown drag handler still fires FIRST
        // on pointerdown — if the customer's finger moves, drag takes over
        // and the click never resolves, so we don't lose drag-by-text.
        if (
          element.type === 'text'
          && !isEditing
          && typeof window !== 'undefined'
          && window.matchMedia?.('(pointer: coarse)').matches
        ) {
          onDoubleClick();
        }
      }}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
    >
      {/* UNIFICATION (2026-07-05) — white pill for photo elements: identical
          control set to slot images (− % + · ⇄ · 🗑 · Terminé), portaled +
          viewport-clamped. Dark element pill is gated off for this type. */}
      {themedWindow && !themedWindow.spreadHalf && element.type === 'photo' && isSelected && !isEditing && !dragging && photoPillRect && (
        <ClampedPill
          slotRect={photoPillRect}
          preferAbove={photoPillRect.top >= 56}
          scale={winCrop && winDims ? zoomOf(winCrop, winDims, winAR) : 1}
          onScaleChange={(next) => {
            const cur = winCrop && winDims ? zoomOf(winCrop, winDims, winAR) : 1;
            zoomWindow(Math.max(1, next) / cur);
          }}
          onReset={themedWindow.photo ? () => onUpdate({ crop: swapCrop(themedWindow.photo!, winAR).crop }) : undefined}
          onReplace={onPickPhoto}
          onRemove={themedWindow.onRemove}
          onDone={onDone}
        />
      )}
      {!themedWindow && element.type === 'photo' && isSelected && !isEditing && !dragging && !cropMode && photoPillRect && (
        <ClampedPill
          slotRect={photoPillRect}
          preferAbove={photoPillRect.top >= 56}
          scale={photoScale}
          onScaleChange={applyPhotoScale}
          sizeLocked={!!element.lockSize}
          onToggleSizeLock={() => onUpdate({ lockSize: !element.lockSize })}
          onCrop={element.photoId && photoUrl && !element.locked ? enterCropMode : undefined}
          onReplace={onPickPhoto}
          onRemove={onDelete}
          onDone={onDone}
        />
      )}

      {/* CROP (2026-07-23) — while crop mode is open the white pill swaps for
          a compact confirm pill: Annuler · Photo entière · Enregistrer. */}
      {element.type === 'photo' && isSelected && cropMode && photoPillRect && (
        <CropPill
          slotRect={photoPillRect}
          preferAbove={photoPillRect.top >= 56}
          onCancel={() => setCropMode(false)}
          onFullPhoto={() => setDraftCrop({ x: 0, y: 0, w: 1, h: 1 })}
          onSave={saveCrop}
        />
      )}

      {/* Render element content */}
      {element.type === 'text' && (
        isEditing ? (
          <>
          {/* Floating "Terminé" pill — fixed to the viewport so it stays
              reachable above the iOS soft keyboard. The customer would
              otherwise have to scroll/tap-outside to dismiss, which on a
              tiny phone screen often double-fires onto the next element. */}
          <button
            type="button"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onStopEditing(); }}
            style={{
              position: 'fixed',
              top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
              right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
              zIndex: 60,
              background: COLORS.gold,
              color: COLORS.white,
              padding: '10px 18px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 14,
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            aria-label="Terminer l'édition du texte"
            title="Terminé"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
            Terminé
          </button>
          <textarea
            // Ref-based focus instead of React's `autoFocus`. iOS Safari
            // often ignores autoFocus on a freshly-mounted element because
            // the focus call has to be inside a synchronous user gesture;
            // by the time React commits we're in a microtask. Calling
            // .focus() from useEffect + scrollIntoView pushes the textarea
            // above the soft keyboard so the customer can see their typing.
            ref={(node) => {
              if (!node) return;
              try {
                node.focus({ preventScroll: false });
                // Place caret at the end so existing text isn't selected
                // (selecting-on-focus surprised customers who tapped to
                // append a word and instead overwrote everything).
                const len = node.value.length;
                node.setSelectionRange(len, len);
                node.scrollIntoView({ block: 'center', behavior: 'smooth' });
              } catch { /* defensive */ }
            }}
            value={element.text ?? ''}
            onChange={(e) => {
              const newText = e.target.value;
              // Grow the element's height with the content so multi-line
              // text isn't clipped while the customer keeps typing. We
              // measure scrollHeight (the natural height the textarea
              // wants) and convert to a percent of the page so it survives
              // the editor's % -based coords. Width stays locked.
              const ta = e.currentTarget;
              const parent = elRef.current?.parentElement;
              if (parent) {
                const parentH = parent.getBoundingClientRect().height || 1;
                // Reset to auto so scrollHeight measures the real content
                // height (otherwise it's pinned to the previous size).
                ta.style.height = 'auto';
                const naturalPx = ta.scrollHeight;
                ta.style.height = '';
                const naturalPct = (naturalPx / parentH) * 100;
                // Cap at the page bottom so the textarea can't push past
                // the trim. The 1 px floor avoids the textarea collapsing
                // when the customer clears the text completely.
                const cappedPct = Math.max(1, Math.min(100 - element.y, naturalPct));
                if (Math.abs(cappedPct - element.height) > 0.5) {
                  onUpdate({ text: newText, height: cappedPct });
                  return;
                }
              }
              onUpdate({ text: newText });
            }}
            onBlur={onStopEditing}
            onKeyDown={(e) => {
              // Escape exits; Enter inserts a new line natively (textarea
              // default). Customer asked for multi-line text — the
              // textarea already supports it, we just had to make sure
              // Enter isn't being intercepted anywhere.
              if (e.key === 'Escape') onStopEditing();
            }}
            className="w-full h-full resize-none outline-none bg-transparent"
            style={{
              fontFamily: fontStackFor(element.fontFamily ?? "'Fraunces', 'Cormorant Garamond', Georgia, serif", element.text),
              unicodeBidi: 'plaintext',
              // D78 (2026-05-21): fontSize is stored as "px relative to a
              // 400-px-wide page" — the same `editorRefW=400` constant the
              // PDF rasteriser uses to scale into PDF points. Rendering as
              // a constant CSS px value broke render parity because the
              // page is ~400px on desktop but ~157px on mobile — the same
              // value looked tiny on one and huge on the other. With the
              // page wrapper set to `container-type: inline-size`, 1cqi
              // equals 1% of the page's width, so fontSize/4 cqi tracks
              // the same proportion the PDF will print.
              fontSize: `${(element.fontSize ?? 18) / 4}cqi`,
              fontVariationSettings: printOpticalSize(element.fontSize),
              fontWeight: element.fontWeight ?? '600',
              fontStyle: element.fontStyle ?? 'normal',
              textDecoration: element.textDecoration ?? 'none',
              color: element.fontColor ?? COLORS.dark,
              textAlign: element.textAlign ?? 'center',
              lineHeight: 1.3,
              padding: 2,
              border: `1px dashed ${COLORS.gold}`,
              // Wrap long words so customers can't horizontally scroll the
              // textarea out of view at narrow widths.
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          />
          </>
        ) : (
          <div
            className="w-full h-full flex items-center overflow-hidden"
            style={{
              // The book-colour band behind a caption (lib/textBand.ts).
              background: bandCss(element),
              fontFamily: fontStackFor(element.fontFamily ?? "'Fraunces', 'Cormorant Garamond', Georgia, serif", element.text),
              unicodeBidi: 'plaintext',
              ...(element.outlineColor && (element.outlineWidth ?? 0) > 0 ? {
                WebkitTextStroke: `${2 * (element.outlineWidth as number)}em ${element.outlineColor}`,
                paintOrder: 'stroke fill',
              } : {}),
              // Same cqi conversion as the editing textarea — see comment
              // above for the render-parity rationale.
              fontSize: `${(element.fontSize ?? 18) / 4}cqi`,
              fontVariationSettings: printOpticalSize(element.fontSize),
              fontWeight: element.fontWeight ?? '600',
              fontStyle: element.fontStyle ?? 'normal',
              textDecoration: element.textDecoration ?? 'none',
              color: element.fontColor ?? COLORS.dark,
              textAlign: element.textAlign ?? 'center',
              justifyContent: element.textAlign === 'left' ? 'flex-start' : element.textAlign === 'right' ? 'flex-end' : 'center',
              lineHeight: 1.3,
              opacity: element.opacity ?? 1,
              padding: 2,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {element.text || 'Votre texte'}
          </div>
        )
      )}

      {/* Shapes paint as the PRINT does (lib/decor/shapePaint.ts): the fill at
          the print's alpha, then the line — a PHYSICAL width (stored in points,
          drawn in cqi of the page box), centred on the edge, opaque, and only
          with a stroke colour. A theme's line frame (1-04) is a rect: it used to
          be « strokeWidth px » inside the box at 80 % here — a different line
          on every screen and never the one that prints. */}
      {element.type === 'rect' && (
        <div className="w-full h-full relative" data-shape-rect="">
          <div
            className="absolute inset-0"
            style={{
              background: element.fill ?? RECT_FILL_DEFAULT,
              borderRadius: element.borderRadius ?? 0,
              opacity: fillOpacity(element, RECT_FILL_DEFAULT),
            }}
          />
          {hasStroke(element) && <div style={strokeLayerCss(element, element.borderRadius ?? 0)!} />}
        </div>
      )}

      {element.type === 'ellipse' && (
        <div className="w-full h-full relative" data-shape-ellipse="">
          <div
            className="absolute inset-0"
            style={{
              background: element.fill ?? ELLIPSE_FILL_DEFAULT,
              borderRadius: '50%',
              opacity: fillOpacity(element, ELLIPSE_FILL_DEFAULT),
            }}
          />
          {hasStroke(element) && <div style={strokeLayerCss(element, '50%')!} />}
        </div>
      )}

      {/* ISSUE 8 (2026-07-05): photo elements now actually hold a photo.
          Filled → the image renders cover-fit (PDF branch mirrors this).
          Empty → the placeholder is TAPPABLE and opens "Mes photos".
          CROP (2026-07-23): with a saved crop, the crop window is cover-fitted
          into the box — the <img> is sized/offset so exactly that window shows
          (overflow clipped by the wrapper). Same math as the PDF's canvas
          pre-crop in drawPageElements (parity). No crop → plain cover. */}
      {/* A model's window whose crop cuts a face: a warning on the photo (never printed). */}
      {themedWindow && winFaceCut && (
        <div
          data-face-warning=""
          className="absolute left-1/2 top-1 -translate-x-1/2 pointer-events-none px-2 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap"
          style={{ background: 'rgba(180,83,9,0.92)', color: '#fff', zIndex: 34, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
        >
          Visage coupé — déplacez la photo
        </div>
      )}
      {element.type === 'photo' && element.photoId && photoUrl && !cropMode && (() => {
        const cr = element.crop;
        let cropStyle: React.CSSProperties | null = null;
        if (cr && cr.w > 0 && cr.h > 0 && natSize && boxAR) {
          // lib/decor/cropMath.ts — the ONE maths the preview (ThemedPageView) and the
          // print (cropSource) use too: the crop window cover-fitted into the box.
          const c = cropCss(cr, natSize, boxAR);
          cropStyle = {
            position: 'absolute',
            left: `${c.left * 100}%`,
            top: `${c.top * 100}%`,
            width: `${c.width * 100}%`,
            height: `${c.height * 100}%`,
            maxWidth: 'none',
            opacity: element.opacity ?? 1,
          };
        }
        return (
          <div className="w-full h-full relative overflow-hidden pointer-events-none" style={photoMaskStyle(element.maskUrl)}>
            <img
              src={photoUrl}
              alt=""
              draggable={false}
              onLoad={(e) => {
                const im = e.currentTarget;
                if (im.naturalWidth > 0) setNatSize({ w: im.naturalWidth, h: im.naturalHeight });
              }}
              className={cropStyle ? 'select-none' : 'w-full h-full select-none'}
              style={cropStyle ?? { objectFit: 'cover', opacity: element.opacity ?? 1 }}
            />
          </div>
        );
      })()}

      {/* CROP mode overlay — full photo contain-fit in the element footprint,
          dimmed outside the draft window, gold rect with 8 handles on top.
          All geometry in % of the element box so it survives canvas zoom. */}
      {element.type === 'photo' && element.photoId && photoUrl && cropMode && (() => {
        const nat = natSize;
        const hostAR = boxAR ?? 1;
        const imgAR = nat ? nat.w / nat.h : 1;
        // Contain-fit display rect of the FULL photo, in % of the box.
        const dW = imgAR > hostAR ? 1 : (imgAR / hostAR);
        const dH = imgAR > hostAR ? (hostAR / imgAR) : 1;
        const dL = (1 - dW) / 2, dT = (1 - dH) / 2;
        const cr = draftCrop;
        // Draft window in % of the box.
        const rL = dL + cr.x * dW, rT = dT + cr.y * dH;
        const rW = cr.w * dW, rH = cr.h * dH;
        const pct = (v: number) => `${v * 100}%`;
        const cropHandles: Array<{ l: number; t: number; dir: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'; cursor: string }> = [
          { l: 0, t: 0, dir: 'nw', cursor: 'nwse-resize' }, { l: 0.5, t: 0, dir: 'n', cursor: 'ns-resize' },
          { l: 1, t: 0, dir: 'ne', cursor: 'nesw-resize' }, { l: 1, t: 0.5, dir: 'e', cursor: 'ew-resize' },
          { l: 1, t: 1, dir: 'se', cursor: 'nwse-resize' }, { l: 0.5, t: 1, dir: 's', cursor: 'ns-resize' },
          { l: 0, t: 1, dir: 'sw', cursor: 'nesw-resize' }, { l: 0, t: 0.5, dir: 'w', cursor: 'ew-resize' },
        ];
        return (
          <div data-crop-overlay="true" className="absolute inset-0 select-none" style={{ zIndex: 25, touchAction: 'none', background: 'rgba(20,16,10,0.5)' }}>
            {/* Full photo, dimmed (the "outside the crop" view). */}
            <img
              src={photoUrl}
              alt=""
              draggable={false}
              onLoad={(e) => {
                const im = e.currentTarget;
                if (im.naturalWidth > 0) setNatSize({ w: im.naturalWidth, h: im.naturalHeight });
              }}
              className="absolute pointer-events-none"
              style={{ left: pct(dL), top: pct(dT), width: pct(dW), height: pct(dH), maxWidth: 'none', opacity: 0.35 }}
            />
            {/* Bright window: the same photo clipped to the draft rect. */}
            <div
              className="absolute overflow-hidden"
              style={{ left: pct(rL), top: pct(rT), width: pct(rW), height: pct(rH), cursor: 'move', touchAction: 'none' }}
              onPointerDown={(e) => startCropDrag(e, 'move')}
            >
              <img
                src={photoUrl}
                alt=""
                draggable={false}
                className="absolute pointer-events-none"
                style={{
                  left: `${((dL - rL) / rW) * 100}%`,
                  top: `${((dT - rT) / rH) * 100}%`,
                  width: `${(dW / rW) * 100}%`,
                  height: `${(dH / rH) * 100}%`,
                  maxWidth: 'none',
                }}
              />
            </div>
            {/* Gold frame + 8 handles (same look as the selection frame). */}
            <div
              className="absolute pointer-events-none"
              style={{ left: pct(rL), top: pct(rT), width: pct(rW), height: pct(rH), border: `2px solid ${COLORS.gold}`, boxShadow: `0 0 0 1px ${COLORS.gold}30` }}
            />
            {cropHandles.map((h) => (
              <div
                key={h.dir}
                data-crop-handle={h.dir}
                className="absolute w-3 h-3 rounded-full"
                style={{
                  left: pct(rL + h.l * rW),
                  top: pct(rT + h.t * rH),
                  transform: 'translate(-50%, -50%)',
                  background: UI.surface,
                  border: `2px solid ${COLORS.gold}`,
                  cursor: h.cursor,
                  zIndex: 27,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  touchAction: 'none',
                }}
                onPointerDown={(e) => startCropDrag(e, h.dir)}
              />
            ))}
          </div>
        );
      })()}
      {element.type === 'photo' && !(element.photoId && photoUrl) && (
        <div
          className="w-full h-full flex flex-col items-center justify-center gap-1"
          style={{
            background: `${COLORS.gold}08`,
            border: `2px dashed ${COLORS.gold}30`,
            opacity: element.opacity ?? 1,
            cursor: onPickPhoto ? 'pointer' : undefined,
          }}
          onClick={onPickPhoto ? (e) => { e.stopPropagation(); onPickPhoto(); } : undefined}
        >
          <svg className="w-4 h-4" style={{ color: `${COLORS.gold}50` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {onPickPhoto && (
            <span className="text-[8px] font-medium" style={{ color: `${COLORS.gold}90` }}>
              Touchez pour choisir
            </span>
          )}
        </div>
      )}

      {/* Icon element (Section 9). Renders the raw SVG admin uploaded
          via Editor Resources. CSS `color` is the recolour tint; any
          `currentColor` strokes/fills inside the SVG inherit it. SVG
          is scaled to 100% of the element box. */}
      {/* Catalogue-décor icon — a transparent cover-décor PNG placed as an
          element. Editor shows a light CDN transform; the PDF embeds the
          full-res public URL contain-fit (parity branch in drawPageElements). */}
      {element.type === 'icon' && element.imageUrl && !element.svg && <TintFilter stops={element.tint} />}
      {element.type === 'icon' && element.imageUrl && !element.svg && (
        <img
          // A sticker's own 900 px copy (previewUrl), else a 400 px transform — the PDF prints imageUrl.
          src={iconDisplayUrl(element)}
          alt=""
          className={`w-full h-full ${element.fit === 'fill' ? 'object-fill' : 'object-contain'} pointer-events-none select-none`}
          draggable={false}
          loading="lazy"
          decoding="async"
          // STEP 3 — a label whose holder is empty is not printed: faded here.
          style={{ opacity: (element.opacity ?? 1) * (labelHidden ? 0.28 : 1), ...tintCss(element.tint) }}
        />
      )}

      {element.type === 'icon' && element.svg && (
        // ⚠️ `element.svg` is whatever the album's saver sent (the public
        // order-id route) — SafeSvg, never raw HTML (login review, 2026-10-01).
        <SafeSvg
          as="div"
          svg={element.svg}
          className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
          style={{
            opacity: element.opacity ?? 1,
            color: element.iconColor ?? element.fontColor ?? COLORS.dark,
          }}
        />
      )}

      {/* ── D78 Fix 1 (2026-05-21): Canva-style selection visuals. ──
          Replaces the single bottom-right resize dot + red delete chip
          with: a 2px gold frame border, 4 corner + 4 edge handles, a
          rotation drag handle below the frame, and a top-mounted Canva
          pill (rotate / lock / duplicate / delete / more). All rendered
          INSIDE the wrapper so they rotate + flip with the element. The
          pill stops propagation so taps on it never re-fire onSelect or
          the page-level "click-outside-to-deselect" handler. */}
      {isSelected && !isEditing && !cropMode && (() => {
        // Eight grab points around the element box (in element-local %).
        type Handle = { left: number; top: number; cursor: React.CSSProperties['cursor']; dir: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'; kind: 'corner' | 'edge' };
        const handles: Handle[] = [
          { left: 0,   top: 0,   cursor: 'nwse-resize', dir: 'nw', kind: 'corner' },
          { left: 100, top: 0,   cursor: 'nesw-resize', dir: 'ne', kind: 'corner' },
          { left: 0,   top: 100, cursor: 'nesw-resize', dir: 'sw', kind: 'corner' },
          { left: 100, top: 100, cursor: 'nwse-resize', dir: 'se', kind: 'corner' },
          { left: 50,  top: 0,   cursor: 'ns-resize',   dir: 'n',  kind: 'edge' },
          { left: 50,  top: 100, cursor: 'ns-resize',   dir: 's',  kind: 'edge' },
          { left: 0,   top: 50,  cursor: 'ew-resize',   dir: 'w',  kind: 'edge' },
          { left: 100, top: 50,  cursor: 'ew-resize',   dir: 'e',  kind: 'edge' },
        ];
        return (
          <>
            {/* Frame border */}
            <div
              className="absolute pointer-events-none"
              style={{
                left: 0, top: 0, width: '100%', height: '100%',
                border: `2px solid ${COLORS.gold}`,
                boxShadow: `0 0 0 1px ${COLORS.gold}30`,
                zIndex: 30,
              }}
            />

            {/* 8 handles — hidden when locked (full pin) OR size-locked so the
                customer can't resize. Move + rotate handles stay. The pill's
                lock button stays visible so they can flip it back on. */}
            {!element.locked && !element.lockSize && !themedWindow && handles.map((h) => {
              const isCorner = h.kind === 'corner';
              return (
                <div
                  key={h.dir}
                  className={`absolute ${isCorner ? 'w-3 h-3 rounded-full' : 'rounded-full'}`}
                  style={{
                    left: `${h.left}%`,
                    top: `${h.top}%`,
                    width: isCorner ? undefined : (h.cursor === 'ns-resize' ? 22 : 6),
                    height: isCorner ? undefined : (h.cursor === 'ns-resize' ? 6 : 22),
                    transform: 'translate(-50%, -50%)',
                    background: UI.surface,
                    border: `2px solid ${COLORS.gold}`,
                    cursor: h.cursor,
                    zIndex: 31,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => handleResizeStart(h.dir, e)}
                />
              );
            })}

            {/* Rotation handle — small white circle dangling below the
                frame, connected by a short stem. Hidden when locked (and on a
                model's window: its box is the model's). */}
            {!element.locked && !themedWindow && (
              <>
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: '50%', top: '100%',
                    width: 1, height: 22,
                    background: COLORS.gold,
                    transform: 'translateX(-50%)',
                    zIndex: 31,
                  }}
                />
                <div
                  className="absolute rounded-full flex items-center justify-center"
                  style={{
                    left: '50%', top: 'calc(100% + 22px)',
                    width: 22, height: 22,
                    transform: 'translate(-50%, -50%)',
                    background: UI.surface,
                    border: `2px solid ${COLORS.gold}`,
                    cursor: 'grab',
                    zIndex: 32,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                    touchAction: 'none',
                  }}
                  onPointerDown={handleRotateStart}
                  title="Rotation"
                  aria-label="Rotation"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.gold} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-3-6.7" />
                    <path d="M21 3v6h-6" />
                  </svg>
                </div>
              </>
            )}

            {/* Top-mounted Canva pill — rotate (mirrors the bottom drag
                handle for keyboard-only users), lock, duplicate, delete,
                more. Normally sits ABOVE the frame, but when the element is
                near the page top (element.y < 14) we flip it BELOW so the
                pill doesn't render off-canvas / under the page frame —
                that's the "Google Docs" toolbar-flip trick.
                UNIFICATION (2026-07-05): PHOTO elements do NOT get this dark
                element pill — they use the same white ClampedPill as every
                other image (slots + template slots), rendered below. Text/
                shapes/icons keep the dark pill (they need lock/duplicate/
                fonts). */}
            {element.type !== 'photo' && (() => {
              const pillFlipped = (element.y ?? 0) < 14;
              const pillNode = (
            <div
              className={dock ? 'flex flex-wrap items-center justify-center gap-1 px-2 py-1 rounded-2xl' : 'absolute flex items-center gap-0.5 px-1 py-1 rounded-full'}
              data-editor-toolbar={dock ? 'true' : undefined}
              style={dock ? { ...DOCKED_BAR_STYLE, background: '#1F2937', color: '#fff' } : {
                left: '50%',
                top: pillFlipped ? '100%' : 0,
                transform: pillFlipped
                  ? 'translate(-50%, 10px)'
                  : 'translate(-50%, calc(-100% - 10px))',
                background: '#1F2937',
                color: '#fff',
                zIndex: 33,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                whiteSpace: 'nowrap',
              }}
              // Was `onMouseDown` — which React does NOT fire on touch in
              // modern iOS Safari. The wrapper's onPointerDown handler then
              // grabbed pointer capture via setPointerCapture, so the tap on
              // the trash never resolved into a click on the button. Switch
              // to onPointerDown so propagation is stopped before the wrapper
              // can claim the gesture.
              onPointerDown={(e) => { e.stopPropagation(); }}
              // ⚠️ AND the mouse-down (2026-10-14, owner: « I can't delete the
              // sticker »). Stopping the POINTER-down kept the wrapper from
              // claiming the gesture, but it also kept the wrapper's
              // preventDefault from suppressing the browser's compatibility
              // MOUSE-down — which then bubbled to the spread's
              // `onMouseDown={() => selectElement(null)}` and deselected the
              // element BEFORE the click: the pill unmounted under the finger
              // and the trash (and every other pill button) did nothing, on
              // desktop and on touch alike. Guard: elementPill.test.tsx
              onMouseDown={(e) => { e.stopPropagation(); }}
              onClick={(e) => { e.stopPropagation(); }}
              onDoubleClick={(e) => { e.stopPropagation(); }}
            >
              {/* Rotate (alt entry; the real gesture is the dangling
                  handle. Tap = nudge by 90° clockwise for keyboard /
                  touch-without-precision users). */}
              <button
                type="button"
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 active:scale-95"
                onClick={() => onUpdate({ rotation: (((element.rotation ?? 0) + 90 + 180) % 360 + 360) % 360 - 180 })}
                title="Rotation +90°"
                aria-label="Rotation +90°"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-3-6.7" />
                  <path d="M21 3v6h-6" />
                </svg>
              </button>

              {/* Lock — toggle. Closed icon when locked, open when not. */}
              <button
                type="button"
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 active:scale-95"
                onClick={() => onUpdate({ locked: !element.locked })}
                title={element.locked ? 'Déverrouiller' : 'Verrouiller'}
                aria-label={element.locked ? 'Déverrouiller' : 'Verrouiller'}
              >
                {element.locked ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                )}
              </button>

              {/* Duplicate */}
              {onDuplicate && (
                <button
                  type="button"
                  className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 active:scale-95"
                  onClick={onDuplicate}
                  title="Dupliquer"
                  aria-label="Dupliquer"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              )}

              {/* Delete */}
              <button
                type="button"
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 active:scale-95"
                onClick={onDelete}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>

              {/* More (…) — opens the heavy FloatingElementToolbar (font,
                  colour, opacity, position…) on demand. Without this the
                  toolbar covered the whole bottom of the screen every
                  time anything was selected. */}
              {onShowMoreToolbar && (
                <button
                  type="button"
                  className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 active:scale-95"
                  onClick={onShowMoreToolbar}
                  title="Plus d'options"
                  aria-label="Plus d'options"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" />
                  </svg>
                </button>
              )}
            </div>
              );
              return dock ? createPortal(pillNode, dock) : pillNode;
            })()}
          </>
        );
      })()}
    </div>
  );
}

/** PDF-mirroring cover-fit CSS box. Given the image's pixel dimensions
 *  and the slot's aspect ratio, returns the left/top/width/height (in slot
 *  %) where the image should be positioned so that it covers the slot with
 *  natural overflow — same math as `computeTranslateDrawParams` in
 *  `pdfGenerator.ts`. Translates are applied as offsets to left/top, scale
 *  multiplies the box around its center.
 *
 *  Without this, the editor rendered `<img width:100% height:100%
 *  object-fit:cover transform:translate(X%)>`, which clamps the cover-fit
 *  result to the slot box and then slides that slot-sized result sideways
 *  — exposing the slot background as a cream halo. The PDF, by contrast,
 *  sizes the image to the cover-fit overflow dimensions and translates
 *  inside that. This helper aligns the two. */
/**
 * Fix #3 (2026-05-29): the pan-mode pill, portaled to <body> with the
 * geometry clamped to the viewport. The pill measures itself on first
 * paint and stores the size in a ref so subsequent rAF ticks read the
 * actual width / height — no hardcoded constants. Logic:
 *
 *   - desiredLeft = slot centre - pillWidth/2
 *   - leftClamp = [8, innerWidth - pillWidth - 8]
 *   - desiredTop = ABOVE the slot by 8 px (preferred)
 *   - if desiredTop < 8     → flip BELOW the slot
 *   - if desiredTop spills BELOW the viewport → snap to (innerHeight - h - 8)
 *
 * The pill stays attached to the slot during scroll/zoom because the
 * parent re-fires slotRect each frame; this component just clamps that
 * input. Rotate button intentionally NOT added — `objectPosition` only
 * carries x/y/scale today, and pdfMirrorBox / panBox / committed shape
 * have no rotation field, so a rotate UI would silently fail to render
 * in preview + PDF and break render parity.
 */
function ClampedPill({
  slotRect,
  preferAbove,
  scale,
  onScaleChange,
  sizeLocked,
  onToggleSizeLock,
  onCrop,
  onReplace,
  onRemove,
  onDone,
  onReset,
  onDetach,
}: {
  slotRect: DOMRect;
  preferAbove: boolean;
  scale: number;
  onScaleChange: (next: number) => void;
  /** LOCK SIZE (2026-07-07) — when true, the −/+ zoom buttons are disabled and
   *  the lock toggle shows its active (filled) state. `onToggleSizeLock` flips
   *  the per-slot lockSize flag. Both optional so callers that don't support
   *  size-locking (template / free-element pills) simply omit the toggle. */
  sizeLocked?: boolean;
  onToggleSizeLock?: () => void;
  /** CROP (2026-07-23) — opens crop mode on a photo element. Only the
   *  free-element caller passes this (slot photos crop by pan/zoom). */
  onCrop?: () => void;
  /** Move-freely: detach the photo from its slot into a free element. */
  onDetach?: () => void;
  /** Swap-photo button — opens the photos picker targeting this slot. */
  onReplace?: () => void;
  /** E2 — trash moved OUT of the frame into this pill (was covering the photo
   *  at the slot's bottom-centre). Non-destructive: returns the photo to the
   *  strip. */
  onRemove?: () => void;
  /** E2 — "Terminé" moved OUT of the frame into this pill. */
  onDone?: () => void;
  /** FIX #1 — reset framing (templated slots): scale 1, offsets 0. */
  onReset?: () => void;
}) {
  const pillRef = useRef<HTMLDivElement | null>(null);
  // « Nouvel éditeur mobile »: docked at the bottom instead of over the album.
  const dock = useMobileDock();
  // Hardcode initial guess so the first paint isn't off-screen; the rAF
  // tick replaces it with the measured size on the next frame.
  const [pillSize, setPillSize] = useState<{ w: number; h: number }>({ w: 104, h: 32 });
  useEffect(() => {
    const el = pillRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && (r.width !== pillSize.w || r.height !== pillSize.h)) {
      setPillSize({ w: r.width, h: r.height });
    }
  }, [pillSize.w, pillSize.h]);
  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  const slotCenter = slotRect.left + slotRect.width / 2;
  let pillLeft = slotCenter - pillSize.w / 2;
  // Clamp left/right into the viewport with an 8 px breathing margin.
  pillLeft = Math.max(8, Math.min(vw - pillSize.w - 8, pillLeft));
  // Try to sit ABOVE the slot; if no room, flip BELOW; if no room
  // BELOW either (very short viewport), snap to the bottom edge.
  let pillTop = preferAbove ? slotRect.top - pillSize.h - 8 : slotRect.bottom + 8;
  if (pillTop < 8) pillTop = slotRect.bottom + 8;
  if (pillTop + pillSize.h > vh - 8) pillTop = Math.max(8, vh - pillSize.h - 8);
  return createPortal(
    <div
      ref={pillRef}
      // Portaled to <body>, so it's outside the slot's [data-pan-mode] subtree.
      // This marker tells the pan-mode outside-detector to treat taps here as
      // "inside" → edit mode stays open while using the scale/rotate/swap tools.
      data-editor-toolbar="true"
      className="flex items-center gap-0.5 px-1 py-1 rounded-full bg-white"
      style={dock ? DOCKED_BAR_STYLE : {
        position: 'fixed',
        left: pillLeft,
        top: pillTop,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
        zIndex: 2147483647,
        isolation: 'isolate',
        pointerEvents: 'auto',
      }}
      // FIX (2026-07-06): STOP pointerdown too. The pill is a React child of the
      // draggable photo element; React portals bubble events through the React
      // tree, so a pointerdown on a pill button (Terminé/Delete/Replace) was
      // bubbling to the element's drag handler → re-selecting/starting a drag →
      // the button's click never took effect. mousedown/touchstart alone missed
      // the pointer-event path the drag actually uses.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <button
        className="w-7 h-7 rounded-full flex items-center justify-center text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        style={{ color: UI.ink }}
        disabled={sizeLocked}
        onClick={(e) => { e.stopPropagation(); if (!sizeLocked) onScaleChange(Math.max(0.5, scale - 0.1)); }}
        title={sizeLocked ? 'Taille verrouillée' : 'Réduire'}
      >−</button>
      <span className="text-[11px] tabular-nums w-9 text-center select-none" style={{ color: UI.ink, opacity: sizeLocked ? 0.4 : 1 }}>
        {Math.round(scale * 100)}%
      </span>
      <button
        className="w-7 h-7 rounded-full flex items-center justify-center text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        style={{ color: UI.ink }}
        disabled={sizeLocked}
        onClick={(e) => { e.stopPropagation(); if (!sizeLocked) onScaleChange(Math.min(3, scale + 0.1)); }}
        title={sizeLocked ? 'Taille verrouillée' : 'Agrandir'}
      >+</button>

      {/* LOCK SIZE (2026-07-07) — freeze the photo's zoom while keeping free pan.
          Only rendered when the caller supports it (grid slots). Filled gold =
          locked. SVG lock icon (never emoji), aria-pressed for a11y. */}
      {onToggleSizeLock && (
        <button
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100"
          style={sizeLocked ? { color: COLORS.white, background: COLORS.gold } : { color: UI.ink }}
          onClick={(e) => { e.stopPropagation(); onToggleSizeLock(); }}
          title={sizeLocked ? 'Déverrouiller la taille (autoriser le zoom)' : 'Verrouiller la taille (garder le déplacement)'}
          aria-label={sizeLocked ? 'Déverrouiller la taille' : 'Verrouiller la taille'}
          aria-pressed={!!sizeLocked}
        >
          {sizeLocked ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 9.9-1" />
            </svg>
          )}
        </button>
      )}

      {/* CROP (2026-07-23) — opens the crop rectangle on a photo element.
          Standard crop glyph (two interlocked L brackets), same 7×7 button
          pattern as its neighbours. */}
      {onCrop && (
        <button
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100"
          style={{ color: UI.ink }}
          onClick={(e) => { e.stopPropagation(); onCrop(); }}
          title="Recadrer la photo"
          aria-label="Recadrer la photo"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2v14a2 2 0 0 0 2 2h14" />
            <path d="M18 22V8a2 2 0 0 0-2-2H2" />
          </svg>
        </button>
      )}

      {/* MOVE FREELY (2026-07-06) — detach the photo from its slot into a free
          element so it drags/resizes/rotates anywhere, like the QR. Shown right
          here in the edit pill (where the customer lands on double-click). */}
      {onDetach && (
        <button
          className="h-7 pl-1.5 pr-2 ml-0.5 rounded-full flex items-center gap-1 text-[11px] font-semibold"
          style={{ color: COLORS.white, background: COLORS.gold }}
          onClick={(e) => { e.stopPropagation(); onDetach(); }}
          title="Déplacer librement — détacher la photo pour la bouger partout"
          aria-label="Déplacer librement"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
          </svg>
          Déplacer
        </button>
      )}

      {/* FIX #1 — reset framing (templated slots only pass onReset). */}
      {onReset && (
        <button
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100"
          style={{ color: UI.ink }}
          onClick={(e) => { e.stopPropagation(); onReset(); }}
          title="Réinitialiser le cadrage"
          aria-label="Réinitialiser le cadrage"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 3v6h-6" />
          </svg>
        </button>
      )}

      {/* Rotation removed from this toolbar (rev 2026-06-08, G3) — the ONLY
          rotation control is now the free drag handle hanging below the image
          frame (grab + drag to any angle, 5° cardinal snap). No rotate buttons. */}

      {/* Swap photo. Opens the photos picker with this slot targeted; the next
          photo tap replaces the current one. Icon is a horizontal two-arrow
          EXCHANGE glyph (NOT a circular/refresh arrow) so it can't be mistaken
          for a rotate control (G3). */}
      {onReplace && (
        <>
          <span className="mx-0.5 self-stretch w-px" style={{ background: '#0001' }} />
          <button
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100"
            style={{ color: UI.ink }}
            onClick={(e) => { e.stopPropagation(); onReplace(); }}
            title="Remplacer la photo"
            aria-label="Remplacer la photo"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 4 3 8l4 4" />
              <path d="M3 8h13" />
              <path d="M17 20l4-4-4-4" />
              <path d="M21 16H8" />
            </svg>
          </button>
        </>
      )}

      {/* E2 — delete + Terminé live HERE now (outside the frame) instead of
          floating over the photo at the slot's bottom-centre. */}
      {onRemove && (
        <>
          <span className="mx-0.5 self-stretch w-px" style={{ background: '#0001' }} />
          <button
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50"
            style={{ color: '#dc2626' }}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Supprimer la photo (renvoyée dans vos images)"
            aria-label="Supprimer la photo"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
          </button>
        </>
      )}
      {onDone && (
        <button
          className="ml-0.5 px-2.5 h-7 rounded-full text-[11px] font-semibold"
          style={{ background: COLORS.gold, color: COLORS.white }}
          onClick={(e) => { e.stopPropagation(); onDone(); }}
        >
          Terminé
        </button>
      )}
    </div>,
    dock ?? document.body,
  );
}

/** CROP (2026-07-23) — the confirm pill shown while crop mode is open on a
 *  photo element: Annuler · Photo entière · Enregistrer. Same portal +
 *  viewport-clamp + event-stop pattern as ClampedPill (it replaces it for
 *  the duration of the crop). */
function CropPill({
  slotRect,
  preferAbove,
  onCancel,
  onFullPhoto,
  onSave,
}: {
  slotRect: DOMRect;
  preferAbove: boolean;
  onCancel: () => void;
  onFullPhoto: () => void;
  onSave: () => void;
}) {
  const pillRef = useRef<HTMLDivElement | null>(null);
  // « Nouvel éditeur mobile »: docked at the bottom instead of over the album.
  const dock = useMobileDock();
  const [pillSize, setPillSize] = useState<{ w: number; h: number }>({ w: 220, h: 32 });
  useEffect(() => {
    const el = pillRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && (r.width !== pillSize.w || r.height !== pillSize.h)) {
      setPillSize({ w: r.width, h: r.height });
    }
  }, [pillSize.w, pillSize.h]);
  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  let pillLeft = slotRect.left + slotRect.width / 2 - pillSize.w / 2;
  pillLeft = Math.max(8, Math.min(vw - pillSize.w - 8, pillLeft));
  let pillTop = preferAbove ? slotRect.top - pillSize.h - 8 : slotRect.bottom + 8;
  if (pillTop < 8) pillTop = slotRect.bottom + 8;
  if (pillTop + pillSize.h > vh - 8) pillTop = Math.max(8, vh - pillSize.h - 8);
  return createPortal(
    <div
      ref={pillRef}
      data-editor-toolbar="true"
      className="flex items-center gap-0.5 px-1 py-1 rounded-full bg-white"
      style={dock ? DOCKED_BAR_STYLE : {
        position: 'fixed',
        left: pillLeft,
        top: pillTop,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
        zIndex: 2147483647,
        isolation: 'isolate',
        pointerEvents: 'auto',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <button
        className="px-2.5 h-7 rounded-full text-[11px] font-semibold hover:bg-gray-100"
        style={{ color: UI.ink }}
        onClick={(e) => { e.stopPropagation(); onCancel(); }}
      >
        Annuler
      </button>
      <span className="mx-0.5 self-stretch w-px" style={{ background: '#0001' }} />
      <button
        className="px-2.5 h-7 rounded-full text-[11px] font-medium hover:bg-gray-100"
        style={{ color: UI.ink }}
        onClick={(e) => { e.stopPropagation(); onFullPhoto(); }}
        title="Revenir à la photo entière"
      >
        Photo entière
      </button>
      <button
        className="ml-0.5 px-2.5 h-7 rounded-full text-[11px] font-semibold"
        style={{ background: COLORS.gold, color: COLORS.white }}
        onClick={(e) => { e.stopPropagation(); onSave(); }}
      >
        Enregistrer
      </button>
    </div>,
    dock ?? document.body,
  );
}

/** FIX #1 (2026-07-05) — bridge that gives TEMPLATED slots the exact same
 *  controls as normal slots. Tracks the slot's viewport rect from the page
 *  container + the template's fractional slot box (works for both the
 *  SVG-swap and legacy branches — no refs into child components needed),
 *  then renders SelectedActionsBar (selected) or ClampedPill (edit mode).
 *  Scale is clamped 0.5-3 by ClampedPill itself; pan stays the existing
 *  drag-to-pan inside TemplatedPhotoSlot / the SVG layer. */
function TemplateSlotControls({
  pageRef,
  fracX, fracY, fracW, fracH,
  editMode,
  scale,
  onScaleChange,
  onReset,
  onReplace,
  onRemove,
  onDone,
  onDetach,
}: {
  pageRef: React.RefObject<HTMLDivElement | null>;
  fracX: number; fracY: number; fracW: number; fracH: number;
  editMode: boolean;
  scale: number;
  onScaleChange: (next: number) => void;
  onReset: () => void;
  onReplace: () => void;
  onRemove: () => void;
  onDone: () => void;
  onDetach?: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    const update = () => {
      const el = pageRef.current;
      if (!el) return;
      const pr = el.getBoundingClientRect();
      setRect(new DOMRect(
        pr.left + fracX * pr.width,
        pr.top + fracY * pr.height,
        fracW * pr.width,
        fracH * pr.height,
      ));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Same rAF re-snap as the PhotoSlot tracker - only one slot is ever
    // selected, so a single loop runs at a time.
    let raf = 0;
    const tick = () => { update(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      cancelAnimationFrame(raf);
    };
  }, [pageRef, fracX, fracY, fracW, fracH]);
  if (!rect) return null;
  const preferAbove = rect.top >= 56;
  return editMode ? (
    <ClampedPill
      slotRect={rect}
      preferAbove={preferAbove}
      scale={scale}
      onScaleChange={onScaleChange}
      onReplace={onReplace}
      onRemove={onRemove}
      onDone={onDone}
      onReset={onReset}
      onDetach={onDetach}
    />
  ) : (
    <SelectedActionsBar
      slotRect={rect}
      preferAbove={preferAbove}
      onReplace={onReplace}
      onRemove={onRemove}
      onDetach={onDetach}
    />
  );
}

/** ISSUE 3 (2026-07-05) — corner resize handles for a template slot in edit
 *  mode. Mirrors PhotoSlot's corner-drag zoom: scale follows the pointer's
 *  distance from the slot centre relative to where the drag started, clamped
 *  0.5–3×. Pure pointer events; parent re-renders apply the fit live. */
function TemplateResizeFrame({
  fracX, fracY, fracW, fracH,
  scale,
  onScale,
}: {
  fracX: number; fracY: number; fracW: number; fracH: number;
  scale: number;
  onScale: (next: number) => void;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ d0: number; s0: number } | null>(null);
  const onHandleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const d0 = Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy));
    dragRef.current = { d0, s0: scale };
    // ITEM 3 (2026-07-06) — rAF-throttle the scale updates. Firing onScale on
    // every pointermove (~120 Hz) re-rendered the whole templated page per
    // event, which thrashed and made the resize feel laggy / snappy. Stash the
    // latest value and flush at most once per frame (same pattern as the text
    // drag). The centre (cx/cy) is captured at gesture start so the ratio stays
    // stable across re-renders.
    let pending: number | null = null;
    let raf = 0;
    const flush = () => { raf = 0; if (pending != null) onScale(pending); };
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dist = Math.hypot(ev.clientX - cx, ev.clientY - cy);
      pending = Math.max(0.5, Math.min(3, +(d.s0 * (dist / d.d0)).toFixed(2)));
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const onUp = () => {
      dragRef.current = null;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      if (pending != null) onScale(pending); // commit the final value
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const corners: Array<[string, React.CSSProperties]> = [
    ['nw', { left: -7, top: -7 }],
    ['ne', { right: -7, top: -7 }],
    ['sw', { left: -7, bottom: -7 }],
    ['se', { right: -7, bottom: -7 }],
  ];
  return (
    <div
      ref={frameRef}
      className="absolute pointer-events-none"
      style={{
        left: `${fracX * 100}%`,
        top: `${fracY * 100}%`,
        width: `${fracW * 100}%`,
        height: `${fracH * 100}%`,
        border: `2px solid ${COLORS.gold}`,
        zIndex: 1500,
      }}
    >
      {corners.map(([k, pos]) => (
        <div
          key={k}
          onPointerDown={onHandleDown}
          className="absolute rounded-full"
          style={{
            ...pos,
            width: 14,
            height: 14,
            background: UI.surface,
            border: `2px solid ${COLORS.gold}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            pointerEvents: 'auto',
            touchAction: 'none',
            cursor: `${k}-resize`,
          }}
        />
      ))}
    </div>
  );
}

/** E2 — selected-state actions OUTSIDE the frame. The edit hint, Remplacer
 *  (E4) and delete used to render on top of the photo (dark overlay + red X
 *  inside the slot); they now sit in a portaled bar above/below the slot,
 *  clamped to the viewport exactly like ClampedPill, so nothing covers the
 *  image. */
function SelectedActionsBar({
  slotRect,
  preferAbove,
  onReplace,
  onRemove,
  onDetach,
}: {
  slotRect: DOMRect;
  preferAbove: boolean;
  onReplace?: () => void;
  onRemove?: () => void;
  onDetach?: () => void;
}) {
  const barRef = useRef<HTMLDivElement | null>(null);
  // « Nouvel éditeur mobile »: docked at the bottom instead of over the album.
  const dock = useMobileDock();
  const [barSize, setBarSize] = useState<{ w: number; h: number }>({ w: 190, h: 32 });
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && (r.width !== barSize.w || r.height !== barSize.h)) {
      setBarSize({ w: r.width, h: r.height });
    }
  }, [barSize.w, barSize.h]);
  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  let left = slotRect.left + slotRect.width / 2 - barSize.w / 2;
  left = Math.max(8, Math.min(vw - barSize.w - 8, left));
  let top = preferAbove ? slotRect.top - barSize.h - 8 : slotRect.bottom + 8;
  if (top < 8) top = slotRect.bottom + 8;
  if (top + barSize.h > vh - 8) top = Math.max(8, vh - barSize.h - 8);
  return createPortal(
    <div
      ref={barRef}
      data-editor-toolbar="true"
      className="flex items-center gap-1 px-1.5 py-1 rounded-full bg-white"
      style={dock ? DOCKED_BAR_STYLE : {
        position: 'fixed',
        left,
        top,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
        zIndex: 2147483647,
        isolation: 'isolate',
        pointerEvents: 'auto',
      }}
      // FIX (2026-07-06): STOP pointerdown too. The pill is a React child of the
      // draggable photo element; React portals bubble events through the React
      // tree, so a pointerdown on a pill button (Terminé/Delete/Replace) was
      // bubbling to the element's drag handler → re-selecting/starting a drag →
      // the button's click never took effect. mousedown/touchstart alone missed
      // the pointer-event path the drag actually uses.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-[10px] px-1 select-none" style={{ color: UI.muted }}>
        2× pour éditer
      </span>
      {onDetach && (
        <button
          className="h-7 px-2 rounded-full flex items-center gap-1 text-[11px] font-medium hover:bg-gray-100"
          style={{ color: UI.ink }}
          onClick={(e) => { e.stopPropagation(); onDetach(); }}
          title="Déplacer librement — détacher la photo du cadre"
          aria-label="Déplacer librement"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
          </svg>
          Déplacer
        </button>
      )}
      {onReplace && (
        <button
          className="h-7 px-2 rounded-full flex items-center gap-1 text-[11px] font-medium hover:bg-gray-100"
          style={{ color: UI.ink }}
          onClick={(e) => { e.stopPropagation(); onReplace(); }}
          title="Remplacer la photo"
          aria-label="Remplacer la photo"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 4 3 8l4 4" />
            <path d="M3 8h13" />
            <path d="M17 20l4-4-4-4" />
            <path d="M21 16H8" />
          </svg>
          Remplacer
        </button>
      )}
      {onRemove && (
        <button
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50"
          style={{ color: '#dc2626' }}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Supprimer"
          aria-label="Supprimer la photo"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>,
    dock ?? document.body,
  );
}

export function pdfMirrorBox(
  imgWidth: number | null,
  imgHeight: number | null,
  slotAR: number,
  translateX: number,
  translateY: number,
  userScale: number,
  rotationDeg: number = 0,
  fitWidth: boolean = false,
): { left: number; top: number; width: number; height: number; valid: boolean } {
  if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0 || !slotAR || slotAR <= 0) {
    return { left: 0, top: 0, width: 100, height: 100, valid: false };
  }
  const imgAR = imgWidth / imgHeight;
  // Rotation cover-growth: scale the cover box up just enough that the photo
  // still covers the slot after the caller rotates it by `rotationDeg` about
  // its centre (no corner gaps). Shared with the PDF + preview via
  // slotRotation.ts so all three agree. Pass (slotAR, 1) as the slot dims —
  // the growth is scale-invariant, depending only on the aspect ratios + angle.
  const g = rotationCoverGrowth(slotAR, 1, imgWidth, imgHeight, rotationDeg);
  // FIT-WIDTH (solo landscape): the image fills the slot WIDTH and letterboxes
  // top/bottom — full image, no crop. width% = userScale; height% scales by
  // slotAR/imgAR (< 1 for a landscape). Mirrors computeTranslateDrawParams'
  // min() branch exactly (render parity).
  if (fitWidth) {
    const ratioW = userScale * g;
    const ratioH = (slotAR / imgAR) * userScale * g;
    // RULE 1 (2026-06-09): at scale 1 the image already spans the FULL slot
    // width (ratioW ≈ 1) — there is no horizontal slack, so a translateX would
    // only shove the letterboxed image into the cream margin (Problem A). Apply
    // translateX ONLY when the user zoomed in enough to overflow (ratioW > 1).
    // translateY still applies — fit-width has vertical letterbox slack to pan.
    const effTranslateX = Math.abs(ratioW - 1) > 0.0001 ? translateX : 0;  // zoomed in OR out: sideways room (2026-10-15)
    return {
      left: (1 - ratioW) / 2 * 100 + effTranslateX,
      top: (1 - ratioH) / 2 * 100 + translateY,
      width: ratioW * 100,
      height: ratioH * 100,
      valid: true,
    };
  }
  // Cover-fit overflow ratios. Whichever axis overflows the slot gets a
  // ratio > 1; the matching axis stays at 1. Multiplied by userScale × growth.
  const ratioW = Math.max(1, imgAR / slotAR) * userScale * g;
  const ratioH = Math.max(1, slotAR / imgAR) * userScale * g;
  // Centred position (negative when overflow extends past slot edges)
  const left = (1 - ratioW) / 2 * 100 + translateX;
  const top = (1 - ratioH) / 2 * 100 + translateY;
  return { left, top, width: ratioW * 100, height: ratioH * 100, valid: true };
}

/**
 * Clamp a pan offset (translateX/translateY, in slot %) so the photo ALWAYS
 * fully covers its slot — no exposed slot background (the "grey bar"). Uses the
 * exact same cover-fit model as `pdfMirrorBox`: the image box has
 * `width = ratioW×100%`, `left = (1-ratioW)/2×100 + translateX`. Full coverage
 * requires `left ≤ 0` AND `left + width ≥ 100`, which reduces to
 * `|translateX| ≤ (ratioW − 1)/2 × 100` (same for Y with ratioH). Because
 * ratioW/ratioH scale with `userScale`, zooming the photo up WIDENS the
 * allowed pan range — the customer reframes a full-bleed photo by zooming
 * first, then panning. At the cover-fit minimum (scale 1, AR-matched axis) the
 * bound is 0, so that axis is "locked" centred until the user zooms. The clamp
 * is applied to the STORED translate, which the editor, preview and PDF all
 * read, so render parity is preserved.
 */
export function clampTranslateToCover(
  imgWidth: number | null,
  imgHeight: number | null,
  slotAR: number,
  translateX: number,
  translateY: number,
  userScale: number,
  rotationDeg: number = 0,
  fitWidth: boolean = false,
): { x: number; y: number } {
  if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0 || !slotAR || slotAR <= 0) {
    return { x: translateX, y: translateY };
  }
  const imgAR = imgWidth / imgHeight;
  const g = rotationCoverGrowth(slotAR, 1, imgWidth, imgHeight, rotationDeg);
  let ratioW: number;
  let ratioH: number;
  if (fitWidth) {
    // fit-width: image spans full slot width (ratioW≈userScale×g) and
    // letterboxes vertically (ratioH = slotAR/imgAR × userScale × g). The
    // letterbox is INTENTIONAL cream margin — vertical pan would shove the
    // image into it, so Y has no coverage room and is clamped to 0. X only
    // has slack once zoomed past full width (mirrors pdfMirrorBox's
    // effTranslateX gate).
    ratioW = userScale * g;
    ratioH = (slotAR / imgAR) * userScale * g;
    // FREE-MOVE-IN-CELL: allow sliding within the cell at any zoom (abs), same
    // rationale as the normal branch below. Y stays 0 — the fit-width letterbox
    // (cream top/bottom) is intentional and has no horizontal-fit slack.
    // 2026-10-15 (owner, #1241): « we need to be able to move the images —
    // at least up and down — and minimise / maximise ». The letterbox is the
    // DEFAULT, not a cage: the photo now slides up/down inside its cell (its
    // edge stops at the cell edge, or pans when zoomed past the cell height),
    // and sideways once zoomed smaller or larger than the full width.
    const maxX = Math.abs(ratioW - 1) / 2 * 100;
    const maxY = Math.abs(ratioH - 1) / 2 * 100;
    return {
      x: Math.max(-maxX, Math.min(maxX, translateX)),
      y: Math.max(-maxY, Math.min(maxY, translateY)),
    };
  }
  ratioW = Math.max(1, imgAR / slotAR) * userScale * g;
  ratioH = Math.max(1, slotAR / imgAR) * userScale * g;
  // FREE-MOVE-IN-CELL (2026-07-07): the offset limit is the distance from
  // centre until the image's edge reaches the CELL edge — `abs(ratio-1)/2`.
  // • ratio > 1 (zoomed in / overflowing): limit = how far you can pan before
  //   exposing background — same as the old cover clamp.
  // • ratio < 1 (zoomed out / smaller than the cell): the old code forced this
  //   to 0 (photo pinned dead-centre — couldn't be moved at all). Now the photo
  //   can slide anywhere INSIDE its cell (its edge stops at the cell edge), so a
  //   smaller photo can be positioned freely with cell background around it.
  // Either way the photo stays within its cell (clipped) — it never spills into
  // the neighbour. The PDF reads the same stored x/y (no clamp there), so print
  // matches the editor.
  const maxX = Math.abs(ratioW - 1) / 2 * 100;
  const maxY = Math.abs(ratioH - 1) / 2 * 100;
  return {
    x: Math.max(-maxX, Math.min(maxX, translateX)),
    y: Math.max(-maxY, Math.min(maxY, translateY)),
  };
}

const PhotoSlot = memo(function PhotoSlot({
  signedUrl,
  filename,
  hasPhoto,
  photoId,
  slotKey,
  isSelected,
  isPanMode,
  isMobile,
  objectPosition,
  imgWidth,
  imgHeight,
  slotAR,
  layout,
  fill,
  slotIndex,
  topHFrac,
  gapMm,
  onSelect,
  onDoubleClick,
  onExitEdit,
  onRemove,
  onReplace,
  onDetach,
  onDrop,
  onPositionChange,
  onEmpty,
  awaitingThisSlot,
  onTouchDragStart,
  isManuallyEdited = false,
}: {
  signedUrl: string | null;
  /** True when the customer has hand-positioned/resized THIS photo. The legacy
   *  self-heal must never rewrite such a transform. */
  isManuallyEdited?: boolean;
  filename: string;
  hasPhoto: boolean;
  photoId?: string;
  slotKey?: string;
  isSelected: boolean;
  isPanMode: boolean;
  /** True on touch/phone layouts. Drives the single-tap-enters-edit shortcut
   *  (Fix G) so mobile doesn't pay the double-tap timer's 300 ms latency. */
  isMobile?: boolean;
  objectPosition: SlotPosition;
  draggingPhotoId?: string | null;
  /** Photo natural pixel dimensions — used to mirror the PDF's cover-fit
   *  overflow math so the editor and PDF render the same `slot_positions`
   *  identically. When null (still loading) we fall back to plain
   *  `object-fit: cover` on a slot-sized box. */
  imgWidth: number | null;
  imgHeight: number | null;
  /** Slot's aspect ratio (W/H). Must match the value the PDF uses in
   *  `getSlots`. Passed from the PageView per-slot. */
  slotAR: number;
  /** Page layout (e.g. '1-up') — drives the solo-landscape fit-width rule. */
  layout?: string;
  /** The page is a FILL page (soloFit.ts) — a 1-up landscape cover-fits. */
  fill?: boolean;
  /** Slot index on the page, and the wide-top band share. Used ONLY to work out
   *  the printed size of this slot for the low-resolution warning. */
  slotIndex?: number;
  topHFrac?: number;
  /** B5 — the album's gap; a gap shrinks the slot, so print DPI changes. */
  gapMm?: number;
  onSelect: () => void;
  onDoubleClick: () => void;
  /** Exit crop-pan edit mode (the pill's "Terminé"). Kept SEPARATE from
   *  onDoubleClick so a double-click only ever ENTERS — a native dblclick that
   *  double-fires can't toggle edit mode back off. */
  onExitEdit?: () => void;
  onRemove: () => void;
  /** Swap the photo in this slot for another (TWEAK 2 toolbar button). Opens
   *  the photos strip with this slot pre-targeted; next tap replaces. */
  onReplace?: () => void;
  /** Detach this photo out of the slot into a free, fully-transformable element. */
  onDetach?: () => void;
  onDrop: (photoId: string, source: 'slot' | 'sidebar') => void;
  onPositionChange: (pos: SlotPosition) => void;
  onEmpty: () => void;
  /** When true, the empty-slot UI gets a pulsing gold ring telling the user
      "the next photo you tap from the strip will land here". */
  awaitingThisSlot?: boolean;
  /** Long-press touch-drag pickup of THIS placed photo. Hooked into the
   *  existing useTouchDragPhoto pipeline (same one the sidebar strip uses)
   *  so the floating-clone preview + drop hit-test code is shared. */
  onTouchDragStart?: (photoId: string, signedUrl: string | null, e: React.TouchEvent) => void;
}) {
  const convertedUrl = useConvertedUrl(signedUrl);
  const slotRef = useRef<HTMLDivElement>(null);
  // One-shot guard so the legacy-over-pan self-heal fires at most once per
  // (photo + dims) — prevents an onPositionChange→re-render→re-heal loop.
  const healedKeyRef = useRef<string | null>(null);
  const panStartRef = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const resizeStartRef = useRef<{ mx: number; my: number; startScale: number; slotMin: number } | null>(null);
  // Two-finger pinch-to-zoom of the image inside its slot (pan mode only).
  const slotPinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  // Direct-DOM pan refs. During an active drag we DO NOT call setState /
  // onPositionChange — instead we stash the latest pointer delta in
  // `latestDeltaRef`, and a requestAnimationFrame loop reads it and mutates
  // `ghostImgRef` + `cellImgRef`'s style.left/style.top in place. Bypasses
  // React reconciliation entirely so the drag follows the finger pixel-for-
  // pixel even on slow devices. On pointerup we commit the final position
  // ONCE via onPositionChange so render parity (editor/preview/PDF) holds.
  // REAL CROP (2026-07-24): these now point at the CROP-WINDOW wrappers (the
  // clipping divs), not the <img>s — the rAF pan writes the same left/top it
  // always did, just one level up, so the inner image keeps its crop offset.
  const ghostImgRef = useRef<HTMLDivElement>(null);
  const cellImgRef = useRef<HTMLDivElement>(null);
  // Canva-style center alignment guides — gold lines that flash whenever the
  // photo's x or y is close enough to the slot centre during a pan. They're
  // also a "magnet": close to 50 % snaps to exactly 50 % so the photo lands
  // dead-centre with no fiddling. Mutated imperatively (display: block/none)
  // so panTick stays inside the render-parity rAF loop without re-renders.
  const snapVRef = useRef<HTMLDivElement>(null); // vertical line (x = 50 %)
  const snapHRef = useRef<HTMLDivElement>(null); // horizontal line (y = 50 %)
  // Snap window — how close to slot centre the photo's x (or y) must be
  // before it locks. Bumped 3 → 5 % so the snap "grabs" the customer's
  // gesture sooner without needing pixel-perfect placement. The two axes
  // are evaluated INDEPENDENTLY: dragging the photo hard right can still
  // lock Y to centre (horizontal centring) without forcing X to snap, and
  // vice-versa.
  const SNAP_THRESHOLD_PCT = 5;
  const latestDeltaRef = useRef<{ dx: number; dy: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  // SLOT CROP (2026-07-23): ref mirror of the crop-mode flag, declared early
  // so the pan/pinch/wheel handlers (defined above the crop state) can bail
  // out without a TDZ/deps dance. Synced in the crop block below.
  const slotCropModeRef = useRef(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const scale = objectPosition.scale ?? 1;
  // Print resolution of THIS photo at THIS slot's printed size. imgWidth/Height
  // come from photoMetaMap, i.e. photos.width/height — the SOURCE dimensions,
  // not the on-screen display copy, so the number reflects what will actually
  // be printed. null when dims are unknown → the badge renders nothing rather
  // than a guess.
  const printQuality = useMemo(
    () => (hasPhoto
      ? photoPrintQuality(imgWidth, imgHeight, layout ?? '1-up', slotIndex ?? 0, objectPosition.scale ?? 1, topHFrac, gapMm ?? 0)
      : null),
    [hasPhoto, imgWidth, imgHeight, layout, slotIndex, objectPosition.scale, topHFrac, gapMm],
  );
  // COVER FLOOR (2026-07-24): a grid-slot photo must always FILL its cell — a
  // photo smaller than the cell (userScale < 1) exposes the cream/white
  // background and, on a cropped photo, reads as "the crop was undone / it
  // went back to the whole image" (customer report). userScale = 1 is exactly
  // cover-fit for any aspect ratio (rotated photos over-cover via g ≥ 1, so 1
  // is a safe floor there too; fit-width solo slots treat 1 as full-width). So
  // every zoom control (−/+ pill, pinch, wheel, corner-resize) floors here.
  // Small-on-purpose photos are made with the free "Photo" tool, not slots.
  // 2026-07-26 — Yahya chose "option B": shrinking is allowed again on an
  // UNCROPPED photo too. The 2026-07-24 floor of 1 (cover-fit) stopped the
  // "crop went back to the whole photo" complaint, but it also made every
  // inward resize a SILENT no-op — drag a corner in and literally nothing
  // happened, which read as "resize is broken". A control that refuses without
  // saying so is worse than the framing trade-off it was protecting against.
  // Consequence accepted: a photo smaller than its cell shows the page
  // background around it (the cell clips, it never spills into a neighbour —
  // see clampTranslateToCover's ratio < 1 branch).
  const SLOT_MIN_SCALE = 0.2;
  // LOCK SIZE (2026-07-07): freeze zoom (resize handles, pinch, −/+ disabled);
  // crop-pan (x/y drag) stays active so the customer repositions inside the cell.
  // CROP LOCKS THE SIZE (2026-07-24, Yahya): a cropped photo stays EXACTLY as
  // cropped — zoom/resize would re-frame it. The padlock button releases it
  // (cropUnlocked) so zoom/resize come back on demand; pan always works.
  const sizeLocked = !!objectPosition.lockSize
    || (!!objectPosition.crop && !objectPosition.cropUnlocked);
  // Canva-style manual slot rotation (clockwise degrees). The photo's <img>
  // is rotated about its OWN centre (= slot centre + pan offset) via a CSS
  // transform; pdfMirrorBox grows the cover box so the slot stays fully
  // covered after the rotation (rotationCoverGrowth, slotRotation.ts). The
  // PDF + preview mirror this exactly for render parity.
  const rotation = objectPosition.rotation ?? 0;

  // D74 Issue 3 — cream halo on 1-up slots.
  // The DB's stored width/height can lag the JPEG's actual intrinsic
  // dimensions (HEIC→JPEG conversion drift, EXIF rotation that the
  // server didn't apply to its metadata, post-rotate cache mismatch).
  // When DB dims disagree with what `<img>` actually loads, the cover-fit
  // math in pdfMirrorBox produces an element that no longer covers the
  // slot — exposing the slot's beige background. naturalDims is the
  // browser's view of the loaded image, so it's always ground truth.
  // Captured via the <img> onLoad below and fed back into pdfMirrorBox.
  const [naturalDims, setNaturalDims] = useState<{ w: number; h: number } | null>(null);
  const rawImgWidth = naturalDims?.w ?? imgWidth;
  const rawImgHeight = naturalDims?.h ?? imgHeight;
  // REAL CROP (2026-07-24): the crop window IS the photo for every downstream
  // calculation (cover-fit, pan clamp, fit-width rule, PDF mirror). Feeding
  // cropped dims here — instead of compiling the crop into scale/translate —
  // is what makes zooming out or resizing unable to resurrect cropped-away
  // pixels. The <img> render below then shows that window via an offset box.
  const slotCrop = objectPosition.crop;
  const { w: effImgWidth, h: effImgHeight } = croppedDims(rawImgWidth, rawImgHeight, slotCrop);
  // Solo (1-up) landscape → fit the page WIDTH (full image, cream letterbox
  // top/bottom) instead of cover-fit. Same rule + math as the PDF + preview
  // (soloFit.ts + pdfMirrorBox's fitWidth branch) so all three stay in parity.
  const fitWidth = soloLandscapeFitWidth(layout, effImgWidth, effImgHeight, fill);
  useEffect(() => { setNaturalDims(null); }, [convertedUrl]);
  // LEGACY SELF-HEAL: existing albums may carry a `slot_position` that was
  // over-panned before the coverage clamp existed (e.g. #1083 page 32:
  // {x: 19.39} on a slot with only ~3% headroom → the slot background showed
  // through). Once the real image dims are known, if the stored translate
  // exceeds the cover bound, commit the clamped value so the gap closes and
  // the corrected position persists on the next save. Guarded by
  // healedKeyRef so it fires at most once per (photo + dims) — no re-heal loop.
  useEffect(() => {
    if (!hasPhoto || !effImgWidth || !effImgHeight) return;
    // E1 — NEVER rewrite the position while the customer is interacting with
    // this slot. The heal used to fire exactly when edit mode loaded the
    // image's dims, snapping the photo at the moment of entry — the product
    // rule is "entering edit changes NOTHING". Deferred to the next idle
    // render (after Terminé / deselect); genuinely broken stored positions
    // still get healed then.
    if (isPanMode || isSelected) return;
    // NEVER TOUCH A CUSTOMER-AUTHORED TRANSFORM (2026-07-26). This effect exists
    // only to repair LEGACY over-panned positions written before the coverage
    // clamp existed (#1083). It must not second-guess a framing the customer
    // just chose by hand.
    //
    // Why this was losing edits: PhotoSlot is `key={slotIdx}`, so flipping to
    // the next spread REUSES this component instance for a different photo,
    // which overwrites healedKeyRef. Coming back, the key looks novel, the heal
    // re-evaluates, and if the stored pan is out of bounds for the CURRENT
    // scale it silently rewrites x/y and autosaves the rewrite. Shrinking a
    // panned photo is exactly what puts it out of bounds — hence "resize +
    // reposition, navigate away and back, edit gone".
    if (isManuallyEdited) return;
    const key = `${photoId ?? slotKey ?? ''}:${effImgWidth}x${effImgHeight}`;
    if (healedKeyRef.current === key) return;
    const clamped = clampTranslateToCover(
      effImgWidth, effImgHeight, slotAR,
      objectPosition.x, objectPosition.y, scale, rotation, fitWidth,
    );
    const eps = 0.01;
    if (Math.abs(clamped.x - objectPosition.x) > eps || Math.abs(clamped.y - objectPosition.y) > eps) {
      healedKeyRef.current = key;
      onPositionChange({
        ...objectPosition,
        x: Math.round(clamped.x * 100) / 100,
        y: Math.round(clamped.y * 100) / 100,
      });
    } else {
      // In bounds — record so we don't re-evaluate every render.
      healedKeyRef.current = key;
    }
  }, [hasPhoto, photoId, slotKey, effImgWidth, effImgHeight, slotAR, scale, rotation, fitWidth, objectPosition, onPositionChange, isPanMode, isSelected, isManuallyEdited]);
  const handleImgLoaded = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const t = e.currentTarget;
    if (t.naturalWidth > 0 && t.naturalHeight > 0) {
      setNaturalDims((prev) =>
        prev?.w === t.naturalWidth && prev?.h === t.naturalHeight
          ? prev
          : { w: t.naturalWidth, h: t.naturalHeight },
      );
    }
  }, []);

  // NOTE: previously, entering pan/edit mode auto-"contained" the image
  // (rewrote `scale` so the full original showed). That DESTROYED the AI's
  // cover-fit framing on double-tap — the photo visibly shrank/repositioned.
  // Per product rule, double-tap must enter edit mode WITHOUT changing
  // anything: position, scale, translateX/Y stay exactly as the AI set them.
  // The customer can then pan/pinch from that untouched state. So the
  // auto-fit effect is intentionally removed — entering pan mode is now inert.

  // Fix #3 (2026-05-28): toolbar pill placement.
  // The −/100%/+ pill used to sit INSIDE the slot frame (`top-1.5`), which
  // overlapped the photo and made the photo hard to see while resizing.
  // Move it ABOVE the slot. If the slot is too close to the viewport top
  // (< 40px) — toolbar would clip off-screen — render BELOW instead.
  const [toolbarPlacement, setToolbarPlacement] = useState<'above' | 'below'>('above');
  // Fix F (2026-05-28): the pill now renders into a portal at <body> so it
  // sits above EVERY stacking context — the previous z-index: 60 lost to
  // the adjacent right-page wrapper on mobile. We track the slot's viewport
  // rect here so the portal can position the pill with `position: fixed`.
  const [slotRect, setSlotRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    // E2 — also track while merely SELECTED (not just pan mode): the selected
    // state's actions bar (hint / Remplacer / delete) is portaled outside the
    // frame and needs the live rect. Only one slot is selected at a time, so
    // the rAF loop never runs for more than one slot.
    if (!isPanMode && !isSelected) {
      setSlotRect(null);
      return;
    }
    const update = () => {
      const rect = slotRef.current?.getBoundingClientRect();
      if (!rect) return;
      setToolbarPlacement(rect.top < 56 ? 'below' : 'above');
      setSlotRect(rect);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Some long pan gestures move the slot (rare — the spread scrolls):
    // re-snap once per animation frame while in pan mode.
    let rafId = 0;
    const tick = () => { update(); rafId = requestAnimationFrame(tick); };
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      cancelAnimationFrame(rafId);
    };
  }, [isPanMode, isSelected]);

  // Tap-to-edit detection (TWEAK 1, 2026-06-08). A single `click`/tap selects;
  // a SECOND tap within ~300 ms enters edit (pan) mode directly. We drive this
  // off React's synthetic `click` (which fires for both mouse and touch taps)
  // and run our OWN tap-timer rather than the browser's native `dblclick`.
  // Native dblclick is unreliable on touch (many mobile browsers never emit it
  // for finger taps), so we replace it entirely — the normal-view slot below no
  // longer wires `onDoubleClick`, which prevents a native-dblclick + tap-timer
  // double-toggle. The first tap is deferred behind a timer so a quick second
  // tap can cancel the "select" and promote to "edit"; if no second tap lands
  // the timer fires `onSelect`. Mobile bypasses this timer entirely (see the
  // isMobile branch below): its PageView `onSelect` does 1st-tap-select /
  // 2nd-tap-edit with no time window, which is far more reliable for thumbs
  // than a timed double-tap (TWEAK 1, 2026-06-08).
  const handleSingleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // MOBILE: fire onSelect immediately. PageView onSelect implements the tap
    // model directly — 1st tap selects, 2nd tap on the selected slot enters
    // edit — with no time window, which thumbs hit reliably.
    if (isMobile) { onSelect(); return; }
    // DESKTOP: use the browser's native click-count (e.detail), which honours
    // the OS double-click speed. The old hand-rolled 300 ms timer required two
    // clicks <300 ms apart, so a normal-speed double-click missed and the
    // customer had to spam-click to enter edit mode. Now: the 1st click
    // (detail 1) selects instantly; the 2nd click of a double-click
    // (detail ≥ 2) promotes straight to edit/pan mode.
    if (e.detail >= 2) { onDoubleClick(); return; }
    onSelect();
  }, [isMobile, onSelect, onDoubleClick]);

  // Corner-handle resize: drag any corner outward to zoom in, inward to zoom
  // out. Same gesture as Canva. Scale is derived from how far the cursor
  // moved away from the slot centre relative to the slot's shorter side, so
  // the feel matches regardless of slot aspect ratio. Clamped to [0.5, 3]×
  // to match the +/- buttons.
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (sizeLocked) return; // LOCK SIZE: corner resize disabled (crop-pan stays active)
    e.preventDefault();
    e.stopPropagation();
    const rect = slotRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Distance from cursor to slot centre at gesture start. We track the
    // delta from this initial distance so dragging away grows + dragging in
    // shrinks — independent of which corner the user grabbed.
    const startDist = Math.hypot(e.clientX - cx, e.clientY - cy);
    const slotMin = Math.min(rect.width, rect.height);
    resizeStartRef.current = { mx: e.clientX, my: e.clientY, startScale: scale, slotMin };

    const onMove = (ev: MouseEvent) => {
      const s = resizeStartRef.current;
      if (!s) return;
      const newDist = Math.hypot(ev.clientX - cx, ev.clientY - cy);
      // Linear scale: 1 unit of slotMin movement ≈ 1× change. Felt right in
      // testing without being twitchy at small slot sizes.
      const ratio = newDist / Math.max(1, startDist);
      // Lowered the floor from 0.5 → 0.2 so customers can shrink a photo
      // down to 20 % of the cover-fit size (was clamped at 50 % which the
      // customer reported as "can't minimize more"). Note: going below ~0.4
      // exposes background colour on the slot — that's intentional, lets
      // the customer compose smaller-on-purpose layouts.
      const next = Math.max(SLOT_MIN_SCALE, Math.min(3, s.startScale * ratio));
      // RE-CLAMP THE PAN TO THE NEW SCALE (2026-07-26). Shrinking collapses the
      // pan bound (|ratio-1|/2), so a photo that was legally panned at scale 1
      // is suddenly out of bounds at 0.6. Leaving the stale x/y here is what
      // made the LEGACY SELF-HEAL rewrite it on the next spread flip — the
      // "my edit reverted when I came back" bug. The -/+ pill already did this;
      // the handles/pinch/wheel did not. Now they all agree.
      {
        const _c = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, next, rotation, fitWidth);
        onPositionChange({ ...objectPosition, scale: Math.round(next * 100) / 100, x: _c.x, y: _c.y });
      }
    };
    const onUp = () => {
      resizeStartRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [scale, objectPosition, onPositionChange]);

  // Touch variant of the resize gesture so mobile customers can also grab a
  // corner and drag. Mirrors the desktop handler — same distance-from-centre
  // math + clamp.
  const handleResizeTouchStart = useCallback((e: React.TouchEvent) => {
    if (sizeLocked) return; // LOCK SIZE: corner resize disabled (crop-pan stays active)
    e.preventDefault();
    e.stopPropagation();
    const rect = slotRef.current?.getBoundingClientRect();
    const t = e.touches[0];
    if (!rect || !t) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startDist = Math.hypot(t.clientX - cx, t.clientY - cy);
    const slotMin = Math.min(rect.width, rect.height);
    resizeStartRef.current = { mx: t.clientX, my: t.clientY, startScale: scale, slotMin };
    const onMove = (ev: TouchEvent) => {
      const s = resizeStartRef.current;
      const tt = ev.touches[0];
      if (!s || !tt) return;
      ev.preventDefault();
      const newDist = Math.hypot(tt.clientX - cx, tt.clientY - cy);
      const ratio = newDist / Math.max(1, startDist);
      // Floor lowered to 0.2 — same change as the mouse handler above.
      const next = Math.max(SLOT_MIN_SCALE, Math.min(3, s.startScale * ratio));
      // RE-CLAMP THE PAN TO THE NEW SCALE (2026-07-26). Shrinking collapses the
      // pan bound (|ratio-1|/2), so a photo that was legally panned at scale 1
      // is suddenly out of bounds at 0.6. Leaving the stale x/y here is what
      // made the LEGACY SELF-HEAL rewrite it on the next spread flip — the
      // "my edit reverted when I came back" bug. The -/+ pill already did this;
      // the handles/pinch/wheel did not. Now they all agree.
      {
        const _c = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, next, rotation, fitWidth);
        onPositionChange({ ...objectPosition, scale: Math.round(next * 100) / 100, x: _c.x, y: _c.y });
      }
    };
    const onEnd = () => {
      resizeStartRef.current = null;
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }, [scale, objectPosition, onPositionChange]);

  // ── Pan: drag to reposition in edit mode ──
  // 1:1 cursor following — mouse delta in pixels translates directly to
  // pan offset in % of slot dimensions. No clamp (the customer can drag
  // the image off the slot edges if they want) and no scale-divide (the
  // old amplification was twitchy and made low-scale dragging stop
  // following the cursor once it hit the [0, 100] clamp).
  // rAF tick: read the latest pointer delta, recompute the pdfMirrorBox
  // exactly the way the React render does, and mutate the two <img>
  // elements' style.left/top in place. NO state writes, NO onPositionChange.
  // Re-scheduled only while a drag is in progress (panStartRef set).
  // Re-uses the live `pdfMirrorBox` math so what the user sees during the
  // drag is byte-identical to what React will render after commit — render
  // parity preserved (see storiz-render-parity).
  const panTick = useCallback(() => {
    rafIdRef.current = null;
    const start = panStartRef.current;
    const delta = latestDeltaRef.current;
    if (!start || !delta) return;
    let candidateX = start.ox + delta.dx;
    let candidateY = start.oy + delta.dy;
    // Canva-style centre snap. Per the `_v: 2` slot-positions convention
    // (storiz-render-parity), `x` and `y` are translate OFFSETS in slot
    // percent — 0 means the photo is centred, NOT 50. Earlier this code
    // snapped to 50 which pushed the image halfway off-centre; the gold
    // lines flashed but the photo never actually landed on the midline.
    // Snap target is 0 on each axis. The two axes evaluate independently
    // so you can drag the photo far right and still lock Y to centre
    // (horizontal centring without forcing vertical).
    const distFromCx = Math.abs(candidateX);
    const distFromCy = Math.abs(candidateY);
    const snapX = distFromCx < SNAP_THRESHOLD_PCT;
    const snapY = distFromCy < SNAP_THRESHOLD_PCT;
    if (snapX) { candidateX = 0; delta.dx = -start.ox; }
    if (snapY) { candidateY = 0; delta.dy = -start.oy; }
    // COVERAGE CLAMP (live): keep the photo fully covering its slot during the
    // drag so the customer physically can't pan past the cover-fit edge and
    // expose the slot background. Mirror back into `delta` so the pointerup
    // commit (s.ox + d.dx) persists exactly the clamped value the user saw.
    {
      const clamped = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, candidateX, candidateY, scale, rotation, fitWidth);
      if (clamped.x !== candidateX) { candidateX = clamped.x; delta.dx = candidateX - start.ox; }
      if (clamped.y !== candidateY) { candidateY = clamped.y; delta.dy = candidateY - start.oy; }
    }
    // Lines are revealed at low opacity (0.25) when the pan starts and stay
    // visible for the whole gesture; on snap-contact we bump to 0.95 so the
    // customer sees the line "light up". Pan-end resets display to none.
    const vEl = snapVRef.current;
    if (vEl) vEl.style.opacity = snapX ? '0.95' : '0.25';
    const hEl = snapHRef.current;
    if (hEl) hEl.style.opacity = snapY ? '0.95' : '0.25';
    const box = pdfMirrorBox(effImgWidth, effImgHeight, slotAR, candidateX, candidateY, scale, rotation, fitWidth);
    if (box.valid) {
      const leftStr = `${box.left}%`;
      const topStr = `${box.top}%`;
      const ghost = ghostImgRef.current;
      if (ghost) {
        ghost.style.left = leftStr;
        ghost.style.top = topStr;
      }
      const cell = cellImgRef.current;
      if (cell) {
        cell.style.left = leftStr;
        cell.style.top = topStr;
      }
    }
    // Re-arm only if drag still active. The pointermove handler also
    // re-arms when it sees rafIdRef.current === null, so this provides
    // continuous animation if the pointer is held still mid-gesture.
    if (panStartRef.current) {
      rafIdRef.current = requestAnimationFrame(panTick);
    }
  }, [effImgWidth, effImgHeight, slotAR, scale, rotation, fitWidth]);

  const handlePanMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isPanMode) return;
    if (slotCropModeRef.current) return; // crop mode owns all gestures
    e.preventDefault();
    e.stopPropagation();
    panStartRef.current = { mx: e.clientX, my: e.clientY, ox: objectPosition.x, oy: objectPosition.y };
    latestDeltaRef.current = { dx: 0, dy: 0 };
    // Reveal the centre-cross alignment guides at low opacity for the whole
    // gesture — panTick raises them to full opacity when the photo enters
    // SNAP_THRESHOLD_PCT of centre, so the customer sees a quiet midline
    // always AND a bright "snapped!" confirmation on contact.
    if (snapVRef.current) { snapVRef.current.style.display = 'block'; snapVRef.current.style.opacity = '0.25'; }
    if (snapHRef.current) { snapHRef.current.style.display = 'block'; snapHRef.current.style.opacity = '0.25'; }
    const slotRect = slotRef.current?.getBoundingClientRect();
    const slotW = slotRect?.width ?? 200;
    const slotH = slotRect?.height ?? 280;
    // Kick off the rAF loop. The loop self-cancels when panStartRef clears.
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(panTick);
    }
    const onMove = (ev: MouseEvent) => {
      const s = panStartRef.current;
      if (!s) return;
      // Stash latest delta; the rAF loop will pick it up. No DOM mutation
      // here — keeps the high-frequency pointermove path branch-free.
      latestDeltaRef.current = {
        dx: ((ev.clientX - s.mx) / slotW) * 100,
        dy: ((ev.clientY - s.my) / slotH) * 100,
      };
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(panTick);
      }
    };
    const onUp = () => {
      const s = panStartRef.current;
      const d = latestDeltaRef.current;
      // Cancel any pending rAF before we commit, so the post-commit React
      // render isn't fighting a stale tick that's about to overwrite styles.
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      panStartRef.current = null;
      latestDeltaRef.current = null;
      // Hide alignment guides — gesture over.
      if (snapVRef.current) snapVRef.current.style.display = 'none';
      if (snapHRef.current) snapHRef.current.style.display = 'none';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
      if (s && d) {
        // panTick has already written snapped values into d.dx/dy when the
        // photo was within SNAP_THRESHOLD_PCT of centre, so this commit
        // persists the snapped position directly. Defensive coverage clamp in
        // case the gesture committed before any rAF tick clamped `d`.
        const committed = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, s.ox + d.dx, s.oy + d.dy, scale, rotation, fitWidth);
        onPositionChange({
          ...objectPosition,
          x: committed.x,
          y: committed.y,
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // DATA-LOSS FIX (2026-07-25): desktop analogue of the touchcancel gap.
    // Releasing outside the window (or an alt-tab / devtools focus steal) never
    // fired mouseup, so the commit was skipped and the drag was lost the same
    // silent way. blur is the reliable catch-all here.
    window.addEventListener('blur', onUp);
  }, [isPanMode, objectPosition, onPositionChange, panTick, effImgWidth, effImgHeight, slotAR, scale, rotation, fitWidth]);

  // ── Touch support for pan ──
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Long-press pickup: when the cell has a placed photo AND the customer
    // isn't already in pan-mode, start the touch-drag hold timer. The hook
    // owns the 700ms hold + 8px cancel + floating clone preview + drop hit-
    // test against [data-slot-drop-target]. If the customer just taps + lifts
    // before 700ms, nothing happens here and the click-handler enters pan-
    // mode as before — preserves Fix G's "first tap → pan-mode" behaviour.
    if (!isPanMode && hasPhoto && photoId && onTouchDragStart) {
      onTouchDragStart(photoId, signedUrl, e);
    }
    if (!isPanMode) return;
    if (slotCropModeRef.current) return; // crop mode owns all gestures

    // Two-finger pinch-to-zoom the image (iPhone-style). Only in pan mode, and
    // we stopPropagation so the canvas-zoom handler (handleViewportTouchStart)
    // doesn't also fire. Writes objectPosition.scale — the same field the PDF
    // reads — so editor/preview/PDF stay in parity.
    if (e.touches.length === 2) {
      e.stopPropagation();
      // LOCK SIZE: swallow the pinch (so canvas-zoom doesn't fire) but don't
      // change the photo's scale. Crop-pan (single-finger, below) stays active.
      if (sizeLocked) return;
      const a = e.touches[0], b = e.touches[1];
      const startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
      slotPinchRef.current = { startDist, startScale: objectPosition.scale ?? 1 };
      const onPinchMove = (ev: TouchEvent) => {
        const p = slotPinchRef.current;
        if (!p || ev.touches.length < 2) return;
        ev.preventDefault();
        const dist = Math.hypot(
          ev.touches[0].clientX - ev.touches[1].clientX,
          ev.touches[0].clientY - ev.touches[1].clientY,
        );
        const next = Math.max(SLOT_MIN_SCALE, Math.min(3, p.startScale * (dist / p.startDist)));
        // RE-CLAMP THE PAN TO THE NEW SCALE (2026-07-26). Shrinking collapses the
      // pan bound (|ratio-1|/2), so a photo that was legally panned at scale 1
      // is suddenly out of bounds at 0.6. Leaving the stale x/y here is what
      // made the LEGACY SELF-HEAL rewrite it on the next spread flip — the
      // "my edit reverted when I came back" bug. The -/+ pill already did this;
      // the handles/pinch/wheel did not. Now they all agree.
      {
        const _c = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, next, rotation, fitWidth);
        onPositionChange({ ...objectPosition, scale: Math.round(next * 100) / 100, x: _c.x, y: _c.y });
      }
      };
      const onPinchEnd = () => {
        slotPinchRef.current = null;
        window.removeEventListener('touchmove', onPinchMove);
        window.removeEventListener('touchend', onPinchEnd);
        window.removeEventListener('touchcancel', onPinchEnd);
      };
      window.addEventListener('touchmove', onPinchMove, { passive: false });
      window.addEventListener('touchend', onPinchEnd);
      window.addEventListener('touchcancel', onPinchEnd);
      return;
    }

    const t = e.touches[0];
    panStartRef.current = { mx: t.clientX, my: t.clientY, ox: objectPosition.x, oy: objectPosition.y };
    latestDeltaRef.current = { dx: 0, dy: 0 };
    // Same centre-cross reveal as the mouse path — visible at low opacity
    // for the whole gesture; panTick flips to full opacity on snap.
    if (snapVRef.current) { snapVRef.current.style.display = 'block'; snapVRef.current.style.opacity = '0.25'; }
    if (snapHRef.current) { snapHRef.current.style.display = 'block'; snapHRef.current.style.opacity = '0.25'; }
    const slotRect = slotRef.current?.getBoundingClientRect();
    const slotW = slotRect?.width ?? 200;
    const slotH = slotRect?.height ?? 280;
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(panTick);
    }
    const onMove = (ev: TouchEvent) => {
      const s = panStartRef.current;
      if (!s) return;
      ev.preventDefault();
      const touch = ev.touches[0];
      if (!touch) return;
      latestDeltaRef.current = {
        dx: ((touch.clientX - s.mx) / slotW) * 100,
        dy: ((touch.clientY - s.my) / slotH) * 100,
      };
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(panTick);
      }
    };
    const onEnd = () => {
      const s = panStartRef.current;
      const d = latestDeltaRef.current;
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      panStartRef.current = null;
      latestDeltaRef.current = null;
      // Hide alignment guides — gesture over (touch path).
      if (snapVRef.current) snapVRef.current.style.display = 'none';
      if (snapHRef.current) snapHRef.current.style.display = 'none';
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      if (s && d) {
        // Defensive coverage clamp (touch path) — same as the mouse commit.
        const committed = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, s.ox + d.dx, s.oy + d.dy, scale, rotation, fitWidth);
        onPositionChange({
          ...objectPosition,
          x: committed.x,
          y: committed.y,
        });
      }
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    // DATA-LOSS FIX (2026-07-25): also commit on touchcancel. This is the ONLY
    // place the drag is saved — "Terminé" is a pure UI toggle and writes
    // nothing. If the OS/browser cancelled the gesture (notification, edge
    // swipe, the page claiming the scroll) onEnd never ran, so the offset was
    // silently dropped while the rAF loop kept PAINTING the moved photo — it
    // only snapped back when Terminé forced a re-render from the unchanged
    // stored position. That is the "my drag was lost" report. The pinch branch
    // above already handled touchcancel; the pan branch did not.
    window.addEventListener('touchcancel', onEnd);
  }, [isPanMode, objectPosition, onPositionChange, panTick, effImgWidth, effImgHeight, slotAR, scale, rotation, fitWidth]);

  // ── Scroll to zoom in edit mode ──
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!isPanMode) return;
    if (slotCropModeRef.current) { e.stopPropagation(); return; } // crop mode: no wheel-zoom
    // SIZE LOCK — the wheel was the one zoom path that never checked it, unlike
    // the handles (handleResizeStart), pinch, and the −/+ pill. On a cropped
    // slot the lock exists precisely to stop re-framing, so a stray desktop
    // scroll silently undid the customer's crop — the "my crop moved" report.
    if (sizeLocked) { e.stopPropagation(); return; }
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const newScale = Math.max(SLOT_MIN_SCALE, Math.min(3, scale + delta));
    // Same re-clamp as the handles / pinch / pill — see RE-CLAMP note above.
    const c = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, newScale, rotation, fitWidth);
    onPositionChange({ ...objectPosition, scale: Math.round(newScale * 100) / 100, x: c.x, y: c.y });
  }, [isPanMode, scale, objectPosition, onPositionChange, sizeLocked, SLOT_MIN_SCALE,
      effImgWidth, effImgHeight, slotAR, rotation, fitWidth]);

  // ── Canva-style manual rotation drag ──
  // Lifted from the free-element rotate handle in this file. The angle is
  // computed via atan2 about the slot's centre (getBoundingClientRect, which
  // is the visual pivot the <img> rotates around since transform-origin is
  // 'center'). Shift snaps the result to the nearest 15°. Uses pointer events
  // + setPointerCapture so it tracks on touch too. We DON'T mutate transform
  // imperatively here — onPositionChange commits the new rotation and React
  // re-renders the <img> with the updated `rotate(...)`. Pan (which mutates
  // style.left/top, not transform) is unaffected and the rotation persists
  // through it. Live degree readout is mirrored to a ref-held DOM node so the
  // gesture stays cheap.
  const rotReadoutRef = useRef<HTMLDivElement>(null);
  // The rotate handle's DOM node — used to fire a one-shot visual pulse when a
  // cardinal snap LOCKS (TWEAK 3). Mutated imperatively so the snap feedback
  // doesn't trigger a React re-render mid-gesture.
  const rotHandleRef = useRef<HTMLDivElement>(null);
  // True only while the rotate handle is actively being dragged — keeps the
  // live degree readout visible even as the angle passes through exactly 0°
  // (rev 2026-06-08: it was previously hidden whenever rotation === 0, so it
  // would blink off mid-drag near straight).
  const [rotating, setRotating] = useState(false);
  // Debounce for the snap feedback: tracks the cardinal we're currently snapped
  // to (0/90/180/270, or null when free). Haptic + pulse fire ONCE per entry
  // into a snapped state, not every frame.
  const lastSnapTargetRef = useRef<number | null>(null);
  const pulseRotHandle = useCallback(() => {
    const el = rotHandleRef.current;
    if (!el) return;
    el.style.transition = 'transform 150ms ease-out';
    el.style.transform = 'scale(1.15)';
    window.setTimeout(() => {
      if (rotHandleRef.current) rotHandleRef.current.style.transform = 'scale(1)';
    }, 150);
  }, []);
  const handleRotateDragStart = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const rect = slotRef.current?.getBoundingClientRect();
    if (!rect) return;
    setRotating(true);
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    const startRotation = rotation;
    // Seed the snap-target debounce from the gesture's starting angle so a drag
    // that begins already on a cardinal doesn't re-fire the feedback instantly.
    const normStart = ((startRotation % 360) + 360) % 360;
    lastSnapTargetRef.current = [0, 90, 180, 270].find((c) =>
      Math.min(Math.abs(normStart - c), 360 - Math.abs(normStart - c)) <= 5) ?? null;
    const onMove = (ev: PointerEvent) => {
      const ang = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      let next = startRotation + (ang - startAngle);
      // Shift snaps to the nearest 15° (unchanged legacy behaviour).
      if (ev.shiftKey) next = Math.round(next / 15) * 15;
      // TWEAK 3 — cardinal magnet. Normalise to [0,360) and, if within 5° of
      // 0/90/180/270, snap exactly to it. We keep the un-normalised `next` for
      // storage continuity but apply the snapped value when locked.
      const norm = ((next % 360) + 360) % 360;
      let snapTarget: number | null = null;
      for (const c of [0, 90, 180, 270]) {
        const d = Math.min(Math.abs(norm - c), 360 - Math.abs(norm - c));
        if (d <= 5) { snapTarget = c; break; }
      }
      if (snapTarget !== null) {
        // Snap `next` onto the nearest multiple of 90 that equals snapTarget,
        // preserving the full-turn count so we don't jump ±360 on lock.
        const turns = Math.round((next - snapTarget) / 360);
        next = snapTarget + turns * 360;
      }
      // Round to 0.1° for clean storage / readout.
      next = Math.round(next * 10) / 10;
      // Fire snap feedback ONCE on entry into a snapped cardinal (debounced via
      // lastSnapTargetRef): a brief haptic + a visual pulse on the handle.
      if (snapTarget !== lastSnapTargetRef.current) {
        if (snapTarget !== null) {
          try { navigator.vibrate?.(30); } catch { /* unsupported */ }
          pulseRotHandle();
        }
        lastSnapTargetRef.current = snapTarget;
      }
      const readout = rotReadoutRef.current;
      if (readout) readout.textContent = `${Math.round(next)}°`;
      onPositionChange({ ...objectPosition, rotation: next });
    };
    const onUp = () => {
      setRotating(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [rotation, objectPosition, onPositionChange, pulseRotHandle]);

  // ── SLOT CROP (rebuilt 2026-07-24) — a REAL crop for photos in layout slots.
  // v1 compiled the crop into scale/translate; that only ZOOMED the photo, so
  // zooming out or resizing the frame brought the cropped-away parts straight
  // back ("it ignores my crop"). Now the rect is stored as `SlotPosition.crop`
  // (normalized 0..1 of the ORIGINAL photo) and the crop WINDOW becomes the
  // photo everywhere: cover-fit, pan clamp, editor <img> (clipping wrapper)
  // and the PDF. Pan/zoom then operate on the cropped photo, so the discarded
  // pixels can never reappear. Non-destructive: the file is untouched and
  // re-opening the tool shows the whole photo with the saved rect pre-placed.
  // Hidden for fit-width (solo-landscape) slots — those show the full photo by
  // design.
  const [slotCropMode, setSlotCropMode] = useState(false);
  slotCropModeRef.current = slotCropMode;
  const [slotDraftCrop, setSlotDraftCrop] = useState<{ x: number; y: number; w: number; h: number }>({ x: 0, y: 0, w: 1, h: 1 });
  const slotDraftCropRef = useRef(slotDraftCrop);
  slotDraftCropRef.current = slotDraftCrop;
  // The rendered cell's px aspect, measured once on crop entry — used for the
  // contain-fit display of the full photo + the px→normalized drag mapping.
  const [cropHostAR, setCropHostAR] = useState<number | null>(null);

  const enterSlotCrop = useCallback(() => {
    if (!rawImgWidth || !rawImgHeight) return;
    const r = slotRef.current?.getBoundingClientRect();
    setCropHostAR(r && r.height > 0 ? r.width / r.height : slotAR);
    // Crop mode shows the WHOLE original photo with the saved rect pre-placed
    // (or the full frame when there's no crop yet) — so a crop is always
    // re-editable and can be widened again, not just tightened.
    setSlotDraftCrop(slotCrop ?? { x: 0, y: 0, w: 1, h: 1 });
    setSlotCropMode(true);
  }, [rawImgWidth, rawImgHeight, slotAR, slotCrop]);

  /** Same pointer pattern as the free-element crop: 'move' drags the window,
   *  a dir resizes it with the opposite edge pinned; all clamped inside the
   *  photo, min 8% per axis. Coordinates are normalized image units. */
  const startSlotCropDrag = useCallback((e: React.PointerEvent, mode: 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w') => {
    e.stopPropagation();
    e.preventDefault();
    const host = slotRef.current;
    // Crop mode displays the ORIGINAL photo, so the px→normalized mapping
    // must use the raw dims (effImg* are the cropped dims).
    if (!host || !rawImgWidth || !rawImgHeight) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const rect = host.getBoundingClientRect();
    const imgAR = rawImgWidth / rawImgHeight;
    const hostARv = rect.width / Math.max(1, rect.height);
    const dispW = imgAR > hostARv ? rect.width : rect.height * imgAR;
    const dispH = imgAR > hostARv ? rect.width / imgAR : rect.height;
    const startX = e.clientX, startY = e.clientY;
    const start = { ...slotDraftCropRef.current };
    const MIN = 0.08;
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const dx = (ev.clientX - startX) / dispW;
      const dy = (ev.clientY - startY) / dispH;
      let { x, y, w, h } = start;
      if (mode === 'move') {
        x = clamp(start.x + dx, 0, 1 - start.w);
        y = clamp(start.y + dy, 0, 1 - start.h);
      } else {
        if (mode.includes('w')) { const nx = clamp(start.x + dx, 0, start.x + start.w - MIN); w = start.x + start.w - nx; x = nx; }
        if (mode.includes('e')) { w = clamp(start.w + dx, MIN, 1 - start.x); }
        if (mode.includes('n')) { const ny = clamp(start.y + dy, 0, start.y + start.h - MIN); h = start.y + start.h - ny; y = ny; }
        if (mode.includes('s')) { h = clamp(start.h + dy, MIN, 1 - start.y); }
      }
      setSlotDraftCrop({ x, y, w, h });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [rawImgWidth, rawImgHeight]);

  /** Enregistrer — store the rect as a REAL crop. The kept window becomes the
   *  photo: pan/zoom reset to neutral so it cover-fills the cell immediately
   *  in the chosen framing (no surprise zoom level), and from then on every
   *  gesture operates on the cropped photo. A full-frame rect clears the crop
   *  ("Photo entière"). */
  const saveSlotCrop = useCallback(() => {
    const cr = slotDraftCropRef.current;
    if (cr.w <= 0 || cr.h <= 0) { setSlotCropMode(false); return; }
    const isFull = cr.x <= 0.005 && cr.y <= 0.005 && cr.w >= 0.99 && cr.h >= 0.99;
    const r4 = (v: number) => Math.round(v * 10000) / 10000;
    onPositionChange({
      ...objectPosition,
      crop: isFull ? undefined : { x: r4(cr.x), y: r4(cr.y), w: r4(cr.w), h: r4(cr.h) },
      // A fresh crop starts LOCKED again (padlock re-releases it on demand).
      cropUnlocked: false,
      // The crop IS the framing → start it centred at cover-fit.
      x: 0,
      y: 0,
      scale: 1,
    });
    setSlotCropMode(false);
  }, [objectPosition, onPositionChange]);

  // Leaving edit mode (Terminé / outside tap unmounts or flips isPanMode)
  // must never strand the crop overlay.
  useEffect(() => {
    if (!isPanMode && slotCropMode) setSlotCropMode(false);
  }, [isPanMode, slotCropMode]);

  // ── Empty slot ──
  if (!hasPhoto) {
    const highlighted = isDragOver || awaitingThisSlot;
    return (
      <div
        // Fix E (2026-05-28): empty slots also carry the drop-target attr so
        // the touch DnD pipeline finds them via elementFromPoint → closest().
        data-slot-drop-target={slotKey}
        data-slot-empty="true"
        className={cn(
          'flex-1 rounded-sm flex items-center justify-center transition-all cursor-pointer',
          awaitingThisSlot && 'animate-[pulse_1.3s_ease-in-out_infinite]',
        )}
        style={{
          background: highlighted ? `${COLORS.gold}20` : UI.panel,
          border: `2px dashed ${highlighted ? COLORS.gold : COLORS.gold + '25'}`,
          minHeight: 0,
        }}
        onClick={onEmpty}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setIsDragOver(false);
          const pid = e.dataTransfer.getData('photoId');
          const src = (e.dataTransfer.getData('photoSource') as 'slot' | 'sidebar') || 'sidebar';
          if (pid) onDrop(pid, src);
        }}
      >
        <div className="text-center p-1">
          <svg className="w-4 h-4 mx-auto mb-0.5" style={{ color: highlighted ? COLORS.gold : `${COLORS.gold}40` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span className="text-[7px]" style={{ color: highlighted ? COLORS.gold : `${COLORS.muted}60` }}>
            {awaitingThisSlot ? 'Choisir une photo' : isDragOver ? 'Déposer' : 'Toucher pour ajouter'}
          </span>
        </div>
      </div>
    );
  }

  // ── Canva-style edit mode ──
  // The image is clipped to the slot (overflow hidden) so it never bleeds
  // onto neighbouring pages — the earlier "ghost overflowing" UI looked
  // broken when a user zoomed past 100%. Pan = drag the body; resize = drag
  // any corner outward/inward; +/-/Terminé are kept as explicit affordances
  // because old / mobile users have trouble grabbing the corner handles.
  // The `data-slot-key` attribute lets the page-level handler exit edit
  // mode cleanly when the customer clicks anywhere outside this slot.
  if (isPanMode && convertedUrl) {
    // Image bounding box in % of slot. Uses the same PDF-mirroring math
    // as the resting render so the frame + corner handles wrap the
    // actual visible image bbox (which may overflow the slot in
    // landscape-in-portrait-slot situations, etc.). The previous formula
    // assumed image is exactly slot-sized at scale=1 — wrong whenever
    // the image and slot aspects differed.
    const panBox = pdfMirrorBox(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, scale, rotation, fitWidth);
    const imgLeft = panBox.left;
    const imgTop = panBox.top;
    const imgRight = imgLeft + panBox.width;
    const imgBottom = imgTop + panBox.height;
    // The frame border is the slot-clamped intersection — drawn over the
    // cell boundary as a soft gold ring (purely cosmetic).
    const frameLeft = Math.max(0, imgLeft);
    const frameTop = Math.max(0, imgTop);
    const frameRight = Math.min(100, imgRight);
    const frameBottom = Math.min(100, imgBottom);
    const frameWidth = Math.max(0, frameRight - frameLeft);
    const frameHeight = Math.max(0, frameBottom - frameTop);

    // Fix #2 (2026-05-28): handles track the IMAGE bbox, not the slot.
    // When scale > 1 the image overflows the slot — handles must follow the
    // actual image corners (which may be negative or > 100% in slot %),
    // otherwise they collapse into a small inner rectangle inside the
    // visible image and look broken. Resize MATH (`handleResizeStart`) is
    // unchanged; only the visual positions move.
    type Handle = { left: number; top: number; cursor: React.CSSProperties['cursor']; kind: 'corner' | 'edge' };
    const midX = (imgLeft + imgRight) / 2;
    const midY = (imgTop + imgBottom) / 2;
    const handles: Handle[] = [
      // Corners
      { left: imgLeft,  top: imgTop,    cursor: 'nwse-resize', kind: 'corner' },
      { left: imgRight, top: imgTop,    cursor: 'nesw-resize', kind: 'corner' },
      { left: imgLeft,  top: imgBottom, cursor: 'nesw-resize', kind: 'corner' },
      { left: imgRight, top: imgBottom, cursor: 'nwse-resize', kind: 'corner' },
      // Edge midpoints
      { left: midX,     top: imgTop,    cursor: 'ns-resize',   kind: 'edge' },
      { left: midX,     top: imgBottom, cursor: 'ns-resize',   kind: 'edge' },
      { left: imgLeft,  top: midY,      cursor: 'ew-resize',   kind: 'edge' },
      { left: imgRight, top: midY,      cursor: 'ew-resize',   kind: 'edge' },
    ];

    return (
      <div
        ref={slotRef}
        data-slot-key={slotKey}
        data-pan-mode="true"
        className="flex-1 relative"
        style={{
          minHeight: 0,
          cursor: 'move',
          zIndex: 50,
          // DATA-LOSS FIX (2026-07-25): claim the gesture. The editor canvas
          // above sets touchAction:'pan-y', so without this the browser was
          // still allowed to take over a VERTICAL photo drag and fire
          // touchcancel — which used to drop the offset entirely. Every other
          // drag surface in this file already pins 'none'; this one didn't,
          // which is why repositioning was the gesture that lost work.
          touchAction: 'none',
          // E4 — no native iOS long-press callout while panning/editing.
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // D78 Fix 2 (2026-05-21): overflow visible so the ghost image
          // can extend past the cell, matching the Canva pan-mode UX
          // ("full image visible, the part outside the cell dimmed").
          // The clipping wrapper below provides the sharp in-cell view;
          // the page-level overflow:visible flag (lifted while any slot
          // on the page is in pan mode) lets the ghost bleed across the
          // page background without being clipped by the spread.
          overflow: 'visible',
        }}
        onMouseDown={handlePanMouseDown}
        onTouchStart={handleTouchStart}
        onWheel={handleWheel}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
      >
        {/* GHOST — full overflow size at 40% opacity. Renders BEHIND the
            sharp clipping wrapper, so where the cell window covers it
            the user sees the sharp pixels; outside the window they see
            the dimmed pixels. Matches Canva's pan-mode UX exactly.
            Hidden during crop mode (the overlay shows its own full photo). */}
        {!slotCropMode && panBox.valid && (
          <div
            ref={ghostImgRef}
            className="pointer-events-none select-none"
            style={{
              position: 'absolute',
              left: `${panBox.left}%`,
              top: `${panBox.top}%`,
              width: `${panBox.width}%`,
              height: `${panBox.height}%`,
              // REAL CROP: this wrapper IS the kept window and it CLIPS, so a
              // cropped photo's discarded pixels are never drawn — not even as
              // the dimmed ghost.
              overflow: 'hidden',
              opacity: 0.4,
              zIndex: 1,
              transform: `rotate(${rotation}deg)`,
              transformOrigin: 'center center',
            }}
          >
            <img
              src={convertedUrl}
              alt=""
              draggable={false}
              onLoad={handleImgLoaded}
              style={{ position: 'absolute', ...cropImgStyle(slotCrop), maxWidth: 'none', maxHeight: 'none', display: 'block', objectFit: 'cover' }}
            />
          </div>
        )}

        {/* CELL WINDOW — clips the sharp 100% image to the slot rect.
            inset-0 ties it to the slot's bounds; overflow:hidden cuts
            the image at the cell edges. Stacked above the ghost so the
            in-cell region reads at full opacity. pointer-events:none so
            the parent's pan/drag handlers receive the gesture. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ overflow: 'hidden', zIndex: 2 }}
        >
          {panBox.valid ? (
            <div
              ref={cellImgRef}
              style={{
                position: 'absolute',
                left: `${panBox.left}%`,
                top: `${panBox.top}%`,
                width: `${panBox.width}%`,
                height: `${panBox.height}%`,
                overflow: 'hidden', // REAL CROP window (see ghost above)
                // Manual rotation — same as the resting render. rAF pan
                // mutates style.left/top (not transform) so this survives a
                // live drag.
                transform: `rotate(${rotation}deg)`,
                transformOrigin: 'center center',
              }}
            >
              <img
                src={convertedUrl}
                alt=""
                draggable={false}
                style={{ position: 'absolute', ...cropImgStyle(slotCrop), maxWidth: 'none', maxHeight: 'none', display: 'block', objectFit: 'cover' }}
              />
            </div>
          ) : (
            <img
              src={convertedUrl}
              alt=""
              className="absolute inset-0 w-full h-full"
              draggable={false}
              style={{ objectFit: 'cover' }}
            />
          )}
          {/* Canva-style centre alignment guides — flashed by panTick when
              the photo lands within SNAP_THRESHOLD_PCT of the slot centre.
              Hidden by default; panTick mutates display: block on snap and
              the pan onUp/onEnd handlers reset to none. Inside the same
              `overflow: hidden` wrapper as the image so they clip cleanly
              to the slot edges. */}
          <div
            ref={snapVRef}
            style={{
              display: 'none', position: 'absolute',
              left: '50%', top: 0, bottom: 0,
              width: 1, marginLeft: -0.5,
              background: COLORS.gold, opacity: 0.85,
              boxShadow: `0 0 4px ${COLORS.gold}`,
              pointerEvents: 'none', zIndex: 3,
            }}
          />
          <div
            ref={snapHRef}
            style={{
              display: 'none', position: 'absolute',
              top: '50%', left: 0, right: 0,
              height: 1, marginTop: -0.5,
              background: COLORS.gold, opacity: 0.85,
              boxShadow: `0 0 4px ${COLORS.gold}`,
              pointerEvents: 'none', zIndex: 3,
            }}
          />
        </div>

        {/* DASHED CELL BOUNDARY — shows exactly where the printed crop
            will land. Anchored to the slot rectangle, not the image
            bbox. pointer-events:none so it doesn't block the pan drag. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            border: `2px dashed ${COLORS.gold}`,
            zIndex: 3,
          }}
        />

        {/* Frame border + handles — tied to the IMAGE's visible bounding
            box, not the slot. When the customer shrinks the image to 50%,
            the frame shrinks with it and the corners sit at the corners of
            the image, not the page. */}
        {!slotCropMode && frameWidth > 0 && frameHeight > 0 && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${frameLeft}%`,
              top: `${frameTop}%`,
              width: `${frameWidth}%`,
              height: `${frameHeight}%`,
              border: `2px solid ${COLORS.gold}`,
              boxShadow: `0 0 0 1px ${COLORS.gold}30, 0 0 0 3px rgba(255,255,255,0.5)`,
              zIndex: 4,
            }}
          />
        )}

        {/* Resize handles — corners (round, 20 px) + edge midpoints
            (rounded rect, narrower). Drag any of them outward to zoom in,
            inward to zoom out — same gesture for all 8. LOCK SIZE: hidden so
            the customer can crop-pan without changing the zoom. */}
        {!slotCropMode && !sizeLocked && handles.map((h, i) => {
          const isCorner = h.kind === 'corner';
          return (
            <div
              key={i}
              className={`absolute ${isCorner ? 'w-5 h-5 rounded-full' : 'rounded-full'}`}
              style={{
                left: `${h.left}%`,
                top: `${h.top}%`,
                width: isCorner ? undefined : (h.cursor === 'ns-resize' ? 28 : 8),
                height: isCorner ? undefined : (h.cursor === 'ns-resize' ? 8 : 28),
                transform: 'translate(-50%, -50%)',
                background: UI.surface,
                border: `2px solid ${COLORS.gold}`,
                cursor: h.cursor,
                zIndex: 5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                touchAction: 'none',
              }}
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeTouchStart}
            />
          );
        })}

        {/* Canva-style MANUAL rotate handle (rev 2026-06-08, G3) — the ONLY
            rotation control. A 28px gold circle hanging just BELOW the slot's
            bottom-centre. Grab + drag to rotate freely about the image centre;
            the cover box auto-grows (rotationCoverGrowth) so no slot bg shows.
            Within 5° of a cardinal it magnet-snaps (haptic + pulse); double-click
            resets to 0°; live degree readout sits below it. Lives OUTSIDE the
            slot (translate +100%) with z-index above the inside Terminé/trash
            cluster + resize handles so it never collides with them. Pointer
            events (setPointerCapture in the handler) so it works on touch. */}
        {!slotCropMode && (
        <div
          className="absolute left-1/2 bottom-0 flex flex-col items-center"
          style={{ transform: 'translate(-50%, calc(100% + 8px))', pointerEvents: 'none', zIndex: 7 }}
          onMouseDown={(e) => e.stopPropagation()}
>
          <div
            ref={rotHandleRef}
            className="rounded-full flex items-center justify-center"
            style={{
              width: 28, height: 28,
              background: UI.surface,
              border: `2px solid ${COLORS.gold}`,
              cursor: 'grab',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
              pointerEvents: 'auto',
              touchAction: 'none',
            }}
            onPointerDown={handleRotateDragStart}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onPositionChange({ ...objectPosition, rotation: 0 });
            }}
            title="Pivoter (glisser pour tourner librement, double-clic = 0°)"
            aria-label="Pivoter la photo"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={COLORS.gold} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <path d="M21 3v6h-6" />
            </svg>
          </div>
          {/* Live degree readout — below the handle, visible while rotating. */}
          <div
            ref={rotReadoutRef}
            className="mt-0.5 px-1 rounded text-[8px] font-medium tabular-nums"
            style={{
              background: 'rgba(0,0,0,0.6)', color: '#fff',
              visibility: (rotation || rotating) ? 'visible' : 'hidden',
            }}
          >
            {Math.round(rotation)}°
          </div>
        </div>
        )}

        {/* SLOT CROP overlay — full photo contain-fit in the cell, dimmed
            outside the draft window, gold rect + 8 handles (same UX as the
            free-photo crop). Sits above every edit-mode decoration; its own
            pointer handlers stopPropagation so pan/pinch never fire. */}
        {slotCropMode && convertedUrl && rawImgWidth && rawImgHeight && (() => {
          const hostARv = cropHostAR ?? slotAR;
          // ORIGINAL photo dims — the overlay always shows the whole photo so
          // the customer can widen an existing crop, not just tighten it.
          const imgAR = rawImgWidth / rawImgHeight;
          const dW = imgAR > hostARv ? 1 : (imgAR / hostARv);
          const dH = imgAR > hostARv ? (hostARv / imgAR) : 1;
          const dL = (1 - dW) / 2, dT = (1 - dH) / 2;
          const cr = slotDraftCrop;
          const rL = dL + cr.x * dW, rT = dT + cr.y * dH;
          const rW = cr.w * dW, rH = cr.h * dH;
          const pct = (v: number) => `${v * 100}%`;
          const cropHandles: Array<{ l: number; t: number; dir: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'; cursor: string }> = [
            { l: 0, t: 0, dir: 'nw', cursor: 'nwse-resize' }, { l: 0.5, t: 0, dir: 'n', cursor: 'ns-resize' },
            { l: 1, t: 0, dir: 'ne', cursor: 'nesw-resize' }, { l: 1, t: 0.5, dir: 'e', cursor: 'ew-resize' },
            { l: 1, t: 1, dir: 'se', cursor: 'nwse-resize' }, { l: 0.5, t: 1, dir: 's', cursor: 'ns-resize' },
            { l: 0, t: 1, dir: 'sw', cursor: 'nesw-resize' }, { l: 0, t: 0.5, dir: 'w', cursor: 'ew-resize' },
          ];
          return (
            <div
              data-crop-overlay="true"
              className="absolute inset-0 select-none"
              style={{ zIndex: 20, touchAction: 'none', background: 'rgba(20,16,10,0.55)' }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <img
                src={convertedUrl}
                alt=""
                draggable={false}
                className="absolute pointer-events-none"
                style={{ left: pct(dL), top: pct(dT), width: pct(dW), height: pct(dH), maxWidth: 'none', maxHeight: 'none', opacity: 0.35 }}
              />
              <div
                className="absolute overflow-hidden"
                style={{ left: pct(rL), top: pct(rT), width: pct(rW), height: pct(rH), cursor: 'move', touchAction: 'none' }}
                onPointerDown={(e) => startSlotCropDrag(e, 'move')}
              >
                <img
                  src={convertedUrl}
                  alt=""
                  draggable={false}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${((dL - rL) / rW) * 100}%`,
                    top: `${((dT - rT) / rH) * 100}%`,
                    width: `${(dW / rW) * 100}%`,
                    height: `${(dH / rH) * 100}%`,
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }}
                />
              </div>
              <div
                className="absolute pointer-events-none"
                style={{ left: pct(rL), top: pct(rT), width: pct(rW), height: pct(rH), border: `2px solid ${COLORS.gold}`, boxShadow: `0 0 0 1px ${COLORS.gold}30` }}
              />
              {cropHandles.map((h) => (
                <div
                  key={h.dir}
                  data-crop-handle={h.dir}
                  className="absolute w-3 h-3 rounded-full"
                  style={{
                    left: pct(rL + h.l * rW),
                    top: pct(rT + h.t * rH),
                    transform: 'translate(-50%, -50%)',
                    background: UI.surface,
                    border: `2px solid ${COLORS.gold}`,
                    cursor: h.cursor,
                    zIndex: 22,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => startSlotCropDrag(e, h.dir)}
                />
              ))}
              {/* Instruction — makes the "whole photo appears" step read as
                  intentional (not a reset). Pinned to the overlay top. */}
              <div
                className="absolute left-1/2 top-2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap pointer-events-none"
                style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', zIndex: 23 }}
              >
                Glissez le cadre pour choisir la partie à garder
              </div>
            </div>
          );
        })()}

        {/* E2 — the Delete + Terminé cluster moved OUT of the frame into the
            ClampedPill below (it used to float over the photo at the slot's
            bottom-centre, hiding exactly what the customer was framing). */}

        {/* Fix F (2026-05-28): zoom pill rendered in a portal at <body> with
            position:fixed. The previous absolute pill inside the slot lost
            its z-index race to the adjacent right-page wrapper on mobile.
            Position is recalculated each animation frame from slotRect so
            the pill follows the slot if the viewport scrolls or resizes.

            Fix G (2026-05-28): the e8160b0 portal STILL appeared clipped on
            mobile because the spread wrapper at ~line 5169 has BOTH
            `perspective: 1200px` AND `transform: translate(...) scale(...)`
            — that combination would have trapped a `position: fixed`
            element to the transformed ancestor's containing block IF the
            pill were rendered inside the wrapper. createPortal escapes
            that (pill is a direct child of <body>) so the geometry is
            already right — but to guarantee the pill wins every stacking
            race we now use a max-int z-index AND an `isolation: isolate`
            of its own, which forces it into its own stacking context at
            the top of the body's child list.

            Fix #3 (2026-05-29): full viewport CLAMP. Previously the pill
            was anchored via `transform: translate(-50%, ...)` from the
            slot's centre. Slots near the LEFT or RIGHT edge pushed the
            pill off-screen horizontally; slots near the BOTTOM with
            placement='below' pushed it off the bottom. We now compute the
            raw desired position, then clamp to keep the pill 8 px inside
            every viewport edge. The pill's actual width/height come from
            its measured rect — we read it once into pillSizeRef and reuse
            that on every rAF tick. */}
        {slotRect && !slotCropMode && (
          <ClampedPill
            slotRect={slotRect}
            preferAbove={toolbarPlacement === 'above'}
            scale={scale}
            onScaleChange={(next) => {
              // Floor at cover-fit (never shrink below filling the cell) and
              // re-clamp the pan to the new scale so no background is exposed —
              // this is what stops "− zooms out into the whole photo" (crop-lost).
              const s = Math.max(SLOT_MIN_SCALE, Math.min(3, next));
              const c = clampTranslateToCover(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, s, rotation, fitWidth);
              onPositionChange({ ...objectPosition, scale: s, x: c.x, y: c.y });
            }}
            sizeLocked={sizeLocked}
            onToggleSizeLock={() => onPositionChange(
              sizeLocked
                // UNLOCK — clear the manual lock and release the crop lock too,
                // so a cropped photo can be zoomed/resized again on demand.
                ? { ...objectPosition, lockSize: false, cropUnlocked: true }
                // LOCK — manual freeze; also re-arms a released crop lock.
                : { ...objectPosition, lockSize: true, cropUnlocked: false },
            )}
            // Crop stays reachable on a CROPPED photo even though the crop
            // size-locks it (that's how you re-frame or reset it); the manual
            // LOCK SIZE toggle still hides it.
            onCrop={!fitWidth && !objectPosition.lockSize && effImgWidth && effImgHeight ? enterSlotCrop : undefined}
            onReplace={onReplace}
            onRemove={onRemove}
            onDone={onExitEdit ?? onDoubleClick}
            onDetach={onDetach}
          />
        )}

        {/* SLOT CROP — while cropping, the pill swaps for the crop confirm
            pill (same component as the free-photo crop). */}
        {slotRect && slotCropMode && (
          <CropPill
            slotRect={slotRect}
            preferAbove={toolbarPlacement === 'above'}
            onCancel={() => setSlotCropMode(false)}
            onFullPhoto={() => setSlotDraftCrop({ x: 0, y: 0, w: 1, h: 1 })}
            onSave={saveSlotCrop}
          />
        )}
      </div>
    );
  }

  // ── Normal view ──
  return (
    <div
      ref={slotRef}
      data-slot-key={slotKey}
      data-slot-empty={hasPhoto ? undefined : 'true'}
      data-slot-drop-target={slotKey}
      className="flex-1 rounded-sm overflow-hidden relative group"
      style={{
        background: `${COLORS.gold}08`,
        cursor: 'pointer',
        outline: isSelected ? `2px solid ${COLORS.gold}` : 'none',
        outlineOffset: -2,
        minHeight: 0,
        // E4 — kill iOS Safari's long-press image callout (Save/Copy sheet) on
        // the canvas: our own press gestures (select, drag, edit) must never
        // be hijacked by the native menu. Inherits to the <img> children.
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        // E1 — 'manipulation' disables iOS Safari's DOUBLE-TAP ZOOM on the
        // slot (and the legacy 300 ms click delay). A fast tap-tap used to
        // trigger the browser's zoom bounce mid-way into edit mode — the
        // "image snaps around" jank — because nothing opted the canvas out.
        // Scroll/pinch on the page still work ('manipulation' keeps pan+zoom
        // outside the element's own taps).
        touchAction: 'manipulation',
      }}
      onContextMenu={(e) => e.preventDefault()}
      draggable={hasPhoto}
      onDragStart={hasPhoto ? (e) => {
        if (photoId) {
          e.dataTransfer.setData('photoId', photoId);
          e.dataTransfer.setData('photoSource', 'slot');
          e.dataTransfer.effectAllowed = 'move';
        }
      } : undefined}
      onClick={handleSingleClick}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setIsDragOver(false);
        const pid = e.dataTransfer.getData('photoId');
        const src = (e.dataTransfer.getData('photoSource') as 'slot' | 'sidebar') || 'sidebar';
        if (pid) onDrop(pid, src);
      }}
    >
      {convertedUrl ? (
        (() => {
          // Mirror PDF's cover-fit-with-overflow math: the <img> is sized
          // to the cover-fit overflow dimensions (not to the slot), then
          // positioned via left/top inside the slot. The slot's
          // overflow:hidden clips the parts that fall outside. This
          // matches `computeTranslateDrawParams` in pdfGenerator.ts so
          // editor and PDF render saved slot_positions identically.
          const box = pdfMirrorBox(effImgWidth, effImgHeight, slotAR, objectPosition.x, objectPosition.y, scale, rotation, fitWidth);
          // When photo dims aren't loaded yet (rare; photoMetaMap fills
          // from DB on session load), fall back to plain cover-fit. No
          // translate is applied in that case — better than a halo.
          if (!box.valid) {
            return (
              <img
                src={convertedUrl} alt="" loading="lazy" draggable={false}
                onLoad={handleImgLoaded}
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
              />
            );
          }
          // D74 Issue 3 — defensive coverage clamp. Even with the
          // pdfMirrorBox math, certain combinations (translate larger
          // than the available overflow, or a transient first-paint
          // where naturalDims hasn't loaded yet) can let the image
          // element shrink below the slot and expose the cream
          // background. We FORCE the element to cover the slot: width
          // and height >= 100%, and left/top clamped so every slot edge
          // sits inside the image rectangle.
          //
          // D78 (2026-05-21): SKIP the clamp when the customer has
          // explicitly shrunk the photo via pan-mode handles (scale<1).
          // Previously the clamp snapped any user-set scale<1 back to
          // slot-fill, undoing the resize the moment they exited pan.
          // The clamp still applies for the default (scale=1 / undefined)
          // case so cream halos stay fixed.
          const userExplicitScale = objectPosition && (objectPosition as { scale?: number }).scale;
          // RULE 4 (2026-06-09): ANY deliberate scale — zoom-IN (>1) or shrink
          // (<1) — counts as "user-positioned". Previously only shrink did, so a
          // zoom-in got clamped back to slot-fill and snapped back on Terminé.
          const userShrunk = typeof userExplicitScale === 'number' && userExplicitScale !== 1;
          // B1/B2 fix (2026-06-09): honor ANY deliberate positioning — pan
          // (x/y), rotation, or shrink — by rendering the RAW cover box, exactly
          // like EDIT mode and the PDF. The old clamp (a) zeroed a fill-scale
          // pan on the tight axis, so edits snapped back on Terminé, and (b)
          // re-centred the box before rotate(), so rotation pivoted off-centre
          // and the image jumped. The coverage clamp now applies ONLY to the
          // untouched default slot (the cream-halo guard for stale DB dims);
          // untouched slots render byte-identically to before.
          const userPositioned = userShrunk
            || objectPosition.x !== 0
            || objectPosition.y !== 0
            || rotation !== 0;
          // RULE 1/3 (2026-06-09): a fit-width (solo-landscape) slot letterboxes
          // by design — box.height < 100% on purpose. NEVER clamp it. The clamp
          // is the cream-halo guard for COVER-fit slots with stale DB dims only;
          // skipping it for fit-width makes the editor render the SAME letterbox
          // the PDF does (computeTranslateDrawParams has no clamp).
          const skipClamp = userPositioned || fitWidth;
          const safeWidth = skipClamp ? box.width : Math.max(100, box.width);
          const safeHeight = skipClamp ? box.height : Math.max(100, box.height);
          const safeLeft = skipClamp ? box.left : Math.max(100 - safeWidth, Math.min(0, box.left));
          const safeTop = skipClamp ? box.top : Math.max(100 - safeHeight, Math.min(0, box.top));
          return (
            <div
              style={{
                position: 'absolute',
                left: `${safeLeft}%`,
                top: `${safeTop}%`,
                width: `${safeWidth}%`,
                height: `${safeHeight}%`,
                // REAL CROP (2026-07-24) — clipping crop-window wrapper. With
                // no crop this is a plain box the image fills, so uncropped
                // slots render exactly as before.
                overflow: 'hidden',
                transform: `rotate(${rotation}deg)`,
                transformOrigin: 'center center',
              }}
            >
            <img
              src={convertedUrl} alt="" loading="lazy" draggable={false}
              onLoad={handleImgLoaded}
              style={{
                position: 'absolute',
                ...cropImgStyle(slotCrop),
                // D75 (2026-05-20): Tailwind preflight applies
                // `img { max-width: 100%; height: auto; }` globally. Our
                // cover-fit math intentionally overflows the slot (width
                // > 100%) and pairs it with a negative `left` to crop
                // horizontally. Without disabling max-width the browser
                // silently clamps width to 100% but keeps the negative
                // left, shifting the image off-slot and exposing the
                // slot bg as a "cream halo" on the opposite side.
                maxWidth: 'none',
                maxHeight: 'none',
                display: 'block',
                // Defensive cover-fit: when DB photo.width/height match the
                // actual JPEG bytes, the element aspect equals the image
                // aspect and `cover` is a no-op. When DB dims are stale
                // (post-rotation lag, HEIC→JPEG drift, EXIF mismatch),
                // `cover` preserves the image's natural aspect by cropping
                // inside the element rather than stretching to fit it.
                // Without this, the browser defaults to `fill` which
                // squashes/elongates the photo whenever dims diverge.
                objectFit: 'cover',
                // Canva-style manual rotation. Rotates the <img> about its
                // own centre (= slot centre + pan offset, since left/top
                // already bake in the translate). At 0° this is a no-op so
                // non-rotated slots render byte-identically to before. The
                // cover box was already grown by rotationCoverGrowth inside
                // pdfMirrorBox so the slot stays fully covered. The PDF +
                // preview mirror this exactly (render parity). Rotation now
                // lives on the wrapper (above) so it rotates crop + image
                // together.
              }}
            />
            </div>
          );
        })()
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
          <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: `${COLORS.gold}40`, borderTopColor: 'transparent' }} />
          <span className="text-[7px]" style={{ color: UI.muted }}>
            {isHeicFilename(filename) ? 'HEIC…' : '…'}
          </span>
        </div>
      )}

      {/* ISSUE 1 — beige pulse while the canvas image is still fetching/
          decoding (naturalDims lands onLoad). The slot used to sit near-white
          during the load, which read as a broken blank page. */}
      {hasPhoto && convertedUrl && !naturalDims && (
        <div className="absolute inset-0 animate-pulse pointer-events-none" style={{ background: `${COLORS.gold}15` }} />
      )}

      {/* Drag-over overlay */}
      {isDragOver && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${COLORS.gold}40` }}>
          <span className="text-[9px] font-semibold" style={{ color: COLORS.white }}>Remplacer</span>
        </div>
      )}

      {/* Selected state (E2): the hint + Remplacer + delete now live in a
          portaled bar OUTSIDE the frame (SelectedActionsBar) so nothing
          covers the photo. In-frame we keep only a light tint + the gold
          outline as the selection cue. */}
      {isSelected && (
        <div className="absolute inset-0 bg-black/10 pointer-events-none" />
      )}
      {isSelected && !isPanMode && slotRect && (
        <SelectedActionsBar
          slotRect={slotRect}
          preferAbove={toolbarPlacement === 'above'}
          onReplace={onReplace}
          onRemove={onRemove}
          onDetach={onDetach}
        />
      )}

      {/* Scale indicator when zoomed */}
      {scale !== 1 && !isSelected && (
        <div className="absolute bottom-0.5 right-0.5 px-1 rounded text-[6px] font-medium"
          style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}>
          {Math.round(scale * 100)}%
        </div>
      )}

      {/* LOW-RESOLUTION WARNING (2026-07-29). Advisory only: it never blocks
          submit and never alters layout. The photo simply does not carry enough
          pixels for the size it is printed at — usually because a chat app
          re-compressed it, or because its full-quality original never finished
          uploading. Flagged here, while the customer can still re-send it.
          pointer-events-none so it can't swallow a tap/drag on the slot. */}
      {printQuality && printQuality.level !== 'good' && !isPanMode && (
        <div
          className="absolute top-0.5 left-0.5 flex items-center gap-0.5 px-1 py-px rounded pointer-events-none"
          style={{
            background: printQuality.level === 'bad' ? 'rgba(168,52,42,0.92)' : 'rgba(176,122,30,0.92)',
            color: '#fff',
          }}
          title={
            `Cette photo s'imprimera à environ ${printQuality.dpi} DPI à cette taille` +
            ` (${printQuality.neededPx} px seraient nécessaires pour 300 DPI).` +
            ` Demandez l'original au client (sur WhatsApp : envoyer en « Document »)` +
            ` ou placez-la dans une case plus petite.`
          }
        >
          <svg className="w-2 h-2 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
          <span className="text-[6px] font-bold leading-none tabular-nums">{printQuality.dpi}</span>
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Empty Page ───────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The left side of a THEMED book's opening spread: the inside of the cover,
 * facing the S1 page (print page 1, on the right — bookStructure.ts). It is
 * not a page of the album: nothing is printed on it and nothing can be placed
 * there, so it is drawn as the book's board, not as an empty page to fill.
 */
function InsideCoverPage() {
  return (
    <div
      className="w-full h-full flex items-end justify-center pb-4 select-none"
      style={{ background: `linear-gradient(135deg, ${UI.surface} 0%, ${COLORS.gold}12 100%)` }}
      aria-label="Intérieur de la couverture"
      data-inside-cover=""
    >
      <span className="text-[10px] font-medium tracking-wide" style={{ color: UI.muted }}>
        Intérieur de la couverture
      </span>
    </div>
  );
}

function EmptyPage() {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center"
      style={{ background: UI.surface }}
    >
      <svg
        className="w-10 h-10"
        style={{ color: `${COLORS.gold}30` }}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Cover Mini Thumbnail (for carousel) ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function CoverMiniThumbnail({
  cover,
  spineConfig,
}: {
  cover: Cover | null;
  spineConfig: SpineConfig;
}) {
  return (
    // Night mode: the cover — a light island.
    <div className="flex" data-adm-media="" style={{ height: 64, color: COLORS.dark }}>
      {/* Back cover mini */}
      <div
        className="rounded-l-sm"
        style={{
          width: 38,
          height: 64,
          background: UI.panel,
          border: `1px solid ${COLORS.gold}15`,
          borderRight: 'none',
        }}
      />
      {/* Spine mini */}
      <div
        style={{
          width: 8,
          height: 64,
          background: spineConfig.bgColor,
        }}
      />
      {/* Front cover mini */}
      <div
        className="rounded-r-sm overflow-hidden"
        style={{
          width: 38,
          height: 64,
          background: cover?.dominant_color ?? COLORS.beigeLight,
          border: `1px solid ${COLORS.gold}15`,
          borderLeft: 'none',
        }}
      >
        {cover?.thumbnail_url && (
          <img
            src={cover.thumbnail_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Bookend Thumbnail + Preview (intro / outro branding pages) ───────────────
// Read-only flanking pages the admin uploads in Settings → Identité de marque.
// The PDF generator prepends the intro and appends the outro to every interior
// PDF; the editor shows them here so the customer's preview matches the print.
// ═══════════════════════════════════════════════════════════════════════════════

function isPdfUrl(url: string | null): boolean {
  if (!url) return false;
  return /\.pdf(\?|$)/i.test(url);
}

// ── « PAGE DE FIN » (coupons plan 2026-10-28, lib/outroPage.ts) ──────────────

/** The strip's « + » menu: « 2 pages photo » (the old « + ») / « Page de fin ».
 *  Portalled + fixed: the strip scrolls and clips, the menu must not. */
function InsertPageMenu({
  anchor,
  onTwoPages,
  onOutro,
  onClose,
}: {
  anchor: { left: number; top: number; bottom: number };
  onTwoPages: () => void;
  onOutro: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Next tick: the click that opened the menu must not close it.
    const t = window.setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const W = 208;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const left = Math.max(8, Math.min(vw - W - 8, anchor.left - W / 2));
  const above = anchor.top > 150;
  const item = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-gray-50 active:scale-[0.99]';
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Ajouter"
      data-insert-page-menu=""
      className="fixed z-[1200] rounded-2xl shadow-2xl p-1.5"
      style={{
        left, width: W,
        ...(above ? { bottom: vh - anchor.top + 8 } : { top: anchor.bottom + 8 }),
        background: UI.surface, border: `1px solid ${COLORS.gold}30`,
      }}
    >
      <button type="button" role="menuitem" className={item} onClick={onTwoPages}>
        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: UI.chip, border: `1px solid ${UI.line}` }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.ink }} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="9" height="16" rx="1.5" /><rect x="13" y="4" width="9" height="16" rx="1.5" />
          </svg>
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>2 pages photo</span>
          <span className="block text-[11px]" style={{ color: UI.muted }}>Une double page à remplir</span>
        </span>
      </button>
      <button type="button" role="menuitem" className={item} onClick={onOutro}>
        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${COLORS.gold}18`, border: `1px solid ${COLORS.gold}40` }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: UI.goldInk }} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 15h6M9 11h6" />
          </svg>
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold" style={{ color: UI.ink }}>Page de fin</span>
          <span className="block text-[11px]" style={{ color: UI.muted }}>La page Storiz de fin d’album, ici</span>
        </span>
      </button>
    </div>,
    document.body,
  );
}

/** A « Page de fin » in the editor: the brand outro image, read-only (same
 *  image + cover-fit as BookendPreview and the PDF), with the page's
 *  « Options » button (delete, move one page). */
function OutroPageView({
  url,
  pageLabel,
  onOpenPageMenu,
}: {
  url: string | null;
  pageLabel: string;
  onOpenPageMenu?: () => void;
}) {
  return (
    <div className="w-full h-full relative" data-outro-page="">
      {url && !isPdfUrl(url) ? (
        <img src={url} alt="Page de fin" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center">
          <span className="text-[11px] font-semibold" style={{ color: `${COLORS.gold}` }}>Page de fin</span>
          <span className="text-[9px]" style={{ color: UI.muted }}>
            {url ? 'Fichier PDF — imprimé tel quel' : 'Aucune page de fin configurée'}
          </span>
        </div>
      )}
      {onOpenPageMenu && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenPageMenu(); }}
          className="absolute top-1 right-1 z-20 w-6 h-6 rounded-full flex items-center justify-center opacity-80 hover:opacity-100 active:scale-95 transition"
          style={{ background: 'rgba(255,255,255,0.92)', border: `1px solid ${COLORS.gold}30`, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
          title="Options de la page"
          aria-label="Options de la page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.dark} strokeWidth={2.5} strokeLinecap="round">
            <circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" />
          </svg>
        </button>
      )}
      <div className="absolute bottom-1 left-0 right-0 text-center z-10 pointer-events-none">
        <span className="text-[8px] font-medium px-1 rounded" style={{ color: UI.muted, background: 'rgba(255,255,255,0.75)' }}>{pageLabel}</span>
      </div>
    </div>
  );
}

function BookendThumb({
  url,
  label,
  pageLabel,
  active,
  onClick,
}: {
  url: string | null;
  label: string;
  /** Page number shown under the name, e.g. "p.1" for the intro. Omitted on
   *  bookends that are not counted as pages. */
  pageLabel?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 flex flex-col items-center gap-1 transition-all"
      title={`${label} — page de branding ajoutée automatiquement`}
    >
      <div
        // Night mode: a page of the album — a light island.
        data-adm-media=""
        className="rounded-md overflow-hidden flex items-center justify-center"
        style={{
          width: 60,
          height: 64,
          background: COLORS.beigeLight,
          color: COLORS.dark,
          outline: active ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}25`,
          outlineOffset: 1,
        }}
      >
        {url && !isPdfUrl(url) ? (
          <img
            src={url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            // @ts-expect-error fetchpriority is valid HTML, missing from React types
            fetchpriority="low"
          />
        ) : (
          <span className="text-[18px]" style={{ color: `${COLORS.gold}80` }}>
            {url ? '📄' : '＋'}
          </span>
        )}
      </div>
      <span
        className="text-[9px] font-medium"
        style={{ color: active ? COLORS.gold : UI.muted }}
      >
        {label}
        {pageLabel && (
          <span className="ml-1" style={{ color: UI.muted }}>{pageLabel}</span>
        )}
      </span>
    </button>
  );
}

function BookendPreview({
  url,
  label,
  caption,
}: {
  url: string | null;
  label: string;
  caption: string;
}) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-6 py-8 gap-4">
      <div
        // Night mode: the outro page is the album — a light island.
        data-adm-media=""
        className="rounded-sm overflow-hidden flex items-center justify-center"
        style={{
          width: 'min(60vw, 400px)',
          aspectRatio: '200 / 297',
          background: COLORS.white,
          color: COLORS.dark,
          boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
          border: `1px solid ${COLORS.gold}15`,
        }}
      >
        {url ? (
          isPdfUrl(url) ? (
            <div className="flex flex-col items-center gap-3 px-6 text-center">
              <span className="text-5xl">📄</span>
              <p className="text-xs" style={{ color: UI.muted }}>
                Fichier PDF — aperçu non disponible
              </p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-xs underline"
                style={{ color: COLORS.gold }}
              >
                Voir le fichier
              </a>
            </div>
          ) : (
            <img src={url} alt={label} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 text-center">
            <span className="text-4xl" style={{ color: `${COLORS.gold}40` }}>📄</span>
            <p className="text-xs" style={{ color: UI.muted }}>
              Aucune page de branding configurée
            </p>
          </div>
        )}
      </div>
      <div className="text-center max-w-md">
        <p className="text-sm font-semibold" style={{ color: UI.ink }}>{label}</p>
        <p className="text-xs mt-1" style={{ color: UI.muted }}>{caption}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Intro Editable View ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// The intro page STAYS the branding bookend (same background image, incl. the
// bottom QR code) but the client can now add text / photos / shapes on top of
// it with the SAME free-floating element system the content pages use. The
// elements live in standalone `introElements` state (NOT in localPages) so the
// intro never enters AI layout / balancing / page_count / pricing — it stays a
// FREE bookend.
//
// Render parity: elements render through the EXACT SAME components content
// pages use (`InteriorTextElement` for text, `PageElementOverlay` for
// shapes / photos / icons) and the PDF draws them with the same `%`-based
// `drawPageElements`, over the same background — so the editor and the print
// match.
//
// QR safe zone: the bottom `INTRO_QR_SAFE_ZONE_PCT` band is reserved for the
// QR code in the branding image. A dashed overlay marks it; the parent's
// intro handlers CLAMP every element out of it, so the QR is never covered.
function IntroEditableView({
  url,
  elements,
  selectedElementId,
  editingElementId,
  onSelectElement,
  onAddElement,
  onUpdateElement,
  onDeleteElement,
  onDuplicateElement,
  onSetEditingElement,
  hideTools = false,
  fitHeight,
}: {
  url: string | null;
  elements: PageElement[];
  selectedElementId: string | null;
  editingElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onAddElement: (type: PageElement['type']) => void;
  onUpdateElement: (elementId: string, updates: Partial<PageElement>) => void;
  onDeleteElement: (elementId: string) => void;
  onDuplicateElement: (elementId: string) => void;
  onSetEditingElement: (id: string | null) => void;
  /** « Nouvel éditeur mobile »: « + Texte » lives in the Texte tab, not above the page. */
  hideTools?: boolean;
  /** « Nouvel éditeur mobile »: the page height that fits the free room (it
   *  used to follow 70vh and got cut when a sheet or the keyboard was up). */
  fitHeight?: string;
}) {
  const isImage = !!url && !isPdfUrl(url);
  // The intro is a TEXT / design page, not a photo page — only the text tool
  // is offered here (FIX B, 2026-06-08). Photo / Rectangle / Ellipse were
  // removed; the content-page sidebars keep all four (separate arrays).
  const tools: { type: PageElement['type']; label: string }[] = [
    { type: 'text', label: 'Texte' },
  ];
  return (
    <div className={cn('w-full h-full flex flex-col items-center justify-center gap-2', fitHeight ? 'p-2' : 'px-4 py-4')}>
      {/* Add-element toolbar — intro is text-only. */}
      {!hideTools && <div className="flex items-center gap-2 flex-wrap justify-center">
        {tools.map((t) => (
          <button
            key={t.type}
            type="button"
            onClick={() => onAddElement(t.type)}
            className="px-3 py-1.5 rounded-full text-xs font-medium active:scale-95 transition-transform"
            style={{ background: `${COLORS.gold}14`, color: UI.goldDeep, border: `1px solid ${COLORS.gold}40` }}
          >
            + {t.label}
          </button>
        ))}
      </div>}

      {/* A4 portrait page surface. The element layer is the direct parent of
          every element so the shared drag math (parentElement bbox → %)
          resolves against this exact box. data-page-number is harmless: the
          intro has no spread, so the elements' cross-page drag never fires. */}
      <div
        data-page-number={0}
        // Night mode: the intro page is the album — a light island.
        data-adm-media=""
        className="relative rounded-sm overflow-hidden"
        // Background tap. FIX (2026-06-10): if a text element is mid-edit, the
        // FIRST background tap just finishes editing (blur commits the text)
        // while KEEPING the element selected, so the font/size/colour toolbar
        // stays up — that's the "type, then style it" flow customers expect.
        // A second tap (nothing focused) clears the selection. Using
        // pointerdown so it runs BEFORE the contenteditable's blur fires.
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          const ae = document.activeElement as HTMLElement | null;
          if (ae && ae.isContentEditable) {
            // Finish typing: commit (text already flows via onUpdate), drop
            // the keyboard AND exit edit mode, but KEEP the selection so the
            // style toolbar stays up. Without the explicit stop-editing the
            // mobile keyboard-catcher (not the chrome's editable) holds
            // focus, the chrome's blur→stopEditing never fires, and the
            // element stays stuck in edit mode.
            ae.blur();
            onSetEditingElement(null);
            return;
          }
          onSelectElement(null);
        }}
        style={{
          // FIX A (2026-06-08): height-driven like the cover surface so the
          // intro fills the available editor height and renders at the same
          // scale as the content pages — instead of the old width-only
          // `min(70vw,440px)` that read small on phones. aspectRatio keeps the
          // A4 (210/297) shape; the `calc((100vw - 2rem) * 1.414)` term caps
          // height so the derived width never overflows a narrow screen.
          height: fitHeight ?? 'min(70vh, 600px, calc((100vw - 2rem) * 1.414))',
          aspectRatio: '200 / 297',
          // RENDER PARITY (2026-06-12): the intro text elements size in `cqi`
          // (fontSize/4 cqi — see InteriorTextElement), which only tracks the
          // PDF's editorRefW=400 proportion when an ancestor establishes an
          // inline-size query container. The two content-page surfaces already
          // set this; the intro surface was missing it, so its `cqi` resolved
          // against the VIEWPORT instead of the page — making intro text huge
          // on desktop, tiny on mobile, and matching the PDF on neither. Adding
          // the container makes intro text proportional to the page width on
          // every surface, so editor (desktop + mobile) and the printed PDF
          // finally agree. The page already has a definite width (height ÷
          // 1.414 via aspectRatio), so containment changes no dimensions.
          containerType: 'inline-size',
          // ⚠️ ITS OWN LAYER (2026-10-10, #MAN-082802). A text element sits at
          // z 1010–1020 (above a template's frame); with nothing isolating this
          // page, the intro's long letter painted OVER the « Régénérer le PDF »
          // dialog (z 60). The content pages are already contained.
          isolation: 'isolate',
          background: COLORS.white,
          color: COLORS.dark,
          boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
          border: `1px solid ${COLORS.gold}15`,
          touchAction: 'none',
        }}
      >
        {/* Branding background — cover-fit to match the PDF's intro raster. A
            null / PDF intro shows a blank white page so the layer still works. */}
        {isImage && (
          <img
            src={url!}
            alt="Page d'intro"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
            draggable={false}
          />
        )}

        {/* Client element layer. SAME components the content pages use. */}
        {elements.map((el) => (
          el.type === 'text' ? (
            <InteriorTextElement
              key={el.id}
              element={el}
              pageNumber={0}
              isSelected={selectedElementId === el.id}
              isEditing={editingElementId === el.id}
              onSelect={() => onSelectElement(el.id)}
              onStartEditing={() => onSetEditingElement(el.id)}
              onStopEditing={() => onSetEditingElement(null)}
              onUpdate={(updates) => onUpdateElement(el.id, updates)}
              onDelete={() => onDeleteElement(el.id)}
              onDuplicate={() => onDuplicateElement(el.id)}
              onMovePage={undefined}
            />
          ) : (
            <PageElementOverlay
              key={el.id}
              element={el}
              pageNumber={0}
              isSelected={selectedElementId === el.id}
              isEditing={editingElementId === el.id}
              onSelect={() => onSelectElement(el.id)}
              onDoubleClick={() => {}}
              onUpdate={(updates) => onUpdateElement(el.id, updates)}
              onDelete={() => onDeleteElement(el.id)}
              onDuplicate={() => onDuplicateElement(el.id)}
              onMovePage={undefined}
              onStopEditing={() => onSetEditingElement(null)}
              // FIX (2026-07-06): "Terminé" on a photo element's pill had no
              // handler on the intro/dedication page (this overlay omitted
              // onDone), so tapping it did nothing. Deselect to close, like the
              // interior overlay.
              onDone={() => onSelectElement(null)}
              siblings={elements}
            />
          )
        ))}

        {/* ── QR safe zone overlay ──────────────────────────────────────────
            Dashed band over the bottom INTRO_QR_SAFE_ZONE_PCT of the page.
            pointer-events:none so it never blocks the surface click-to-deselect
            or element gestures above it. Matches the editor's dashed-guide
            visual language (dashed + low-opacity gold). */}
        <div
          className="absolute left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none"
          style={{
            height: `${INTRO_QR_SAFE_ZONE_PCT * 100}%`,
            background: `${COLORS.gold}0E`,
            borderTop: `2px dashed ${COLORS.gold}70`,
            zIndex: 30,
          }}
        >
          <span
            className="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide"
            style={{ background: `${COLORS.gold}22`, color: UI.goldDeep }}
          >
            Zone QR code — ne pas couvrir
          </span>
        </div>
      </div>
      {/* FIX D (2026-06-08): the directive caption ("Ajoutez vos textes…") was
          removed — the intro is OPTIONAL and blank by default, so there is no
          prompt pushing the customer to add anything. The page is already
          identified as "Intro" in the bottom page strip, and the on-page
          dashed band still protects the QR zone. */}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Spread Thumbnail (for carousel) ──────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const SpreadThumb = memo(function SpreadThumb({
  page,
  photoUrlMap,
  side,
  premiumAfter,
  photoGap,
  outroUrl,
}: {
  page: LayoutPage | null;
  photoUrlMap: Map<string, string>;
  /** « Page de fin » pages show this brand image (outro_page_2_url). */
  outroUrl?: string | null;
  /** B5 — same gap as the page, scaled to the 42 px thumb. */
  photoGap: PhotoGap;
  side: 'left' | 'right';
  /** Pages past this number are tinted "premium" (they cost extra). It is this
   *  ORDER's frozen allowance, not the current default — see migration 082. */
  premiumAfter: number;
}) {
  if (!page) {
    return (
      <div
        // Night mode: a page of the album — a light island.
        data-adm-media=""
        className={cn(
          'flex items-center justify-center',
          side === 'left' ? 'rounded-l-sm' : 'rounded-r-sm',
        )}
        style={{
          width: 42,
          height: 60,
          background: COLORS.white,
          color: COLORS.dark,
          border: `1px solid ${COLORS.gold}15`,
        }}
      >
        <span className="text-[7px]" style={{ color: UI.muted }}>—</span>
      </div>
    );
  }

  // « PAGE DE FIN » (lib/outroPage.ts) — the brand outro image, like the Outro thumb.
  if (isOutroPage(page)) {
    return (
      <div
        data-adm-media=""
        data-outro-page=""
        className={cn('overflow-hidden relative flex items-center justify-center', side === 'left' ? 'rounded-l-sm' : 'rounded-r-sm')}
        style={{ width: 42, height: 60, background: COLORS.beigeLight, color: COLORS.dark, border: `1px solid ${COLORS.gold}40` }}
        title="Page de fin"
      >
        {outroUrl && !isPdfUrl(outroUrl) ? (
          <img src={outroUrl} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" draggable={false} />
        ) : (
          <span className="text-[6px] font-semibold text-center leading-tight" style={{ color: UI.goldInk }}>Page<br />de fin</span>
        )}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center" style={{ background: `${COLORS.dark}60`, height: 10 }}>
          <span className="text-[5px] font-medium" style={{ color: COLORS.white }}>{page.page}</span>
        </div>
      </div>
    );
  }

  const slots = getSlots(page.layout);
  // A panorama's right half lists no photo of its own (the left half does): its thumb shows the photo it draws.
  const half = page as LayoutPage & { spreadHalf?: string; elements?: Array<{ type?: string; photoId?: string }> };
  const photoIds = half.spreadHalf === 'right' && !page.photos.length
    ? (half.elements ?? []).filter((e) => e.type === 'photo' && e.photoId).map((e) => e.photoId as string).slice(0, 1)
    : page.photos;
  const isPremium = page.page > premiumAfter;

  return (
    <div
      data-adm-media=""
      className={cn(
        'overflow-hidden relative',
        side === 'left' ? 'rounded-l-sm' : 'rounded-r-sm',
      )}
      style={{
        width: 42,
        height: 60,
        background: COLORS.white,
        color: COLORS.dark,
        border: `1px solid ${isPremium ? COLORS.gold + '40' : COLORS.gold + '15'}`,
      }}
    >
      {/* Render the actual layout grid with real photos */}
      <div className="w-full h-full flex flex-col" style={{ gap: photoGapPx(photoGap.mm, 42), background: photoGap.mm > 0 ? photoGap.color : undefined, padding: framePaddingPx(photoGap, (Number(page.page) || 1) - 1, 42), ...(page.layout === '2-up-even' ? EVEN_CONTAINER : {}) }}>
        {slots.map((row, rowIdx) => (
          <div
            key={rowIdx}
            // PARITY (2026-07-14): 3-up prints 60/40, not equal rows — keep the
            // mini-thumb honest so the strip matches the page + PDF.
            className={cn('flex', !THUMB_TOP_FRAC[page.layout] && page.layout !== '2-up-even' && 'flex-1')}
            style={{ gap: photoGapPx(photoGap.mm, 42), minHeight: 0, ...(THUMB_TOP_FRAC[page.layout] ? twoRowFlex(THUMB_TOP_FRAC[page.layout], rowIdx, photoGapPx(photoGap.mm, 42)) : {}), ...(page.layout === '2-up-even' ? { flex: `0 0 ${evenRowPct(page.top_h_frac)}%` } : {}) }}
          >
            {row.map((slotIdx) => {
              const photoId = photoIds[slotIdx];
              const signedUrl = photoId ? photoUrlMap.get(photoId) ?? null : null;
              return (
                <SpreadThumbSlot
                  key={slotIdx}
                  signedUrl={signedUrl}
                  hasPhoto={!!photoId}
                />
              );
            })}
          </div>
        ))}
      </div>
      {/* Page number badge */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center justify-center"
        style={{ background: `${COLORS.dark}60`, height: 10 }}
      >
        <span className="text-[5px] font-medium" style={{ color: COLORS.white }}>
          {page.page}
        </span>
      </div>
      {/* Premium page indicator */}
      {isPremium && (
        <div
          className="absolute top-0 right-0 w-3 h-3 flex items-center justify-center rounded-bl-sm"
          style={{ background: COLORS.gold, color: COLORS.white }}
        >
          <span className="text-[5px] font-bold">+</span>
        </div>
      )}
    </div>
  );
});

const SpreadThumbSlot = memo(function SpreadThumbSlot({
  signedUrl,
  hasPhoto,
}: {
  signedUrl: string | null;
  hasPhoto: boolean;
}) {
  const convertedUrl = useConvertedUrl(signedUrl);

  if (!hasPhoto) {
    return (
      <div className="flex-1" style={{ background: `${COLORS.gold}12`, minHeight: 0 }} />
    );
  }

  return (
    <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
      {convertedUrl ? (
        <img
          src={convertedUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          // @ts-expect-error fetchpriority is valid HTML, missing from React types
          fetchpriority="low"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full" style={{ background: `${COLORS.gold}20` }} />
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Focus Mode (Canva-style crop overlay) ────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function FocusMode({
  signedUrl,
  position,
  onPositionChange,
  onClose,
  onSmartCrop,
}: {
  signedUrl: string | null;
  position: { x: number; y: number };
  onPositionChange: (pos: { x: number; y: number }) => void;
  onClose: () => void;
  onSmartCrop: () => void;
}) {
  const convertedUrl = useConvertedUrl(signedUrl);
  const containerRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    panStartRef.current = { mx: e.clientX, my: e.clientY, ox: position.x, oy: position.y };
    const onMove = (ev: MouseEvent) => {
      if (!panStartRef.current) return;
      const dx = (ev.clientX - panStartRef.current.mx) * 0.25;
      const dy = (ev.clientY - panStartRef.current.my) * 0.25;
      onPositionChange({
        x: Math.max(0, Math.min(100, panStartRef.current.ox - dx)),
        y: Math.max(0, Math.min(100, panStartRef.current.oy - dy)),
      });
    };
    const onUp = () => {
      panStartRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [position, onPositionChange]);

  // Touch support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    panStartRef.current = { mx: t.clientX, my: t.clientY, ox: position.x, oy: position.y };
    const onMove = (ev: TouchEvent) => {
      if (!panStartRef.current) return;
      const touch = ev.touches[0];
      const dx = (touch.clientX - panStartRef.current.mx) * 0.25;
      const dy = (touch.clientY - panStartRef.current.my) * 0.25;
      onPositionChange({
        x: Math.max(0, Math.min(100, panStartRef.current.ox - dx)),
        y: Math.max(0, Math.min(100, panStartRef.current.oy - dy)),
      });
    };
    const onEnd = () => {
      panStartRef.current = null;
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }, [position, onPositionChange]);

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
        style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Instructions */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 px-4 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(4px)' }}>
        <span className="text-xs text-white/80 font-medium">Glissez pour recadrer</span>
        <span className="text-white/40">|</span>
        <span className="text-[10px] text-white/60">ESC pour fermer</span>
      </div>

      {/* The image in a frame with crop visualization */}
      <div
        ref={containerRef}
        className="relative cursor-grab active:cursor-grabbing select-none"
        style={{ width: 'min(80vw, 600px)', aspectRatio: '200 / 297' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Frame border */}
        <div className="absolute inset-0 rounded-lg" style={{ border: '2px solid rgba(255,255,255,0.3)', pointerEvents: 'none', zIndex: 2 }} />

        {/* Image */}
        {convertedUrl ? (
          <img
            src={convertedUrl}
            alt=""
            className="w-full h-full rounded-lg"
            style={{
              objectFit: 'cover',
              objectPosition: `${position.x}% ${position.y}%`,
            }}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full rounded-lg flex items-center justify-center" style={{ background: COLORS.dark }}>
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: COLORS.gold, borderTopColor: 'transparent' }} />
          </div>
        )}

        {/* Corner crop indicators */}
        {[
          { top: 8, left: 8 },
          { top: 8, right: 8 },
          { bottom: 8, left: 8 },
          { bottom: 8, right: 8 },
        ].map((pos, i) => (
          <div key={i} className="absolute w-4 h-4 pointer-events-none" style={{ ...pos, zIndex: 3 }}>
            <div className="w-full h-full" style={{
              borderTop: (pos as any).top !== undefined ? '2px solid rgba(255,255,255,0.7)' : 'none',
              borderBottom: (pos as any).bottom !== undefined ? '2px solid rgba(255,255,255,0.7)' : 'none',
              borderLeft: (pos as any).left !== undefined ? '2px solid rgba(255,255,255,0.7)' : 'none',
              borderRight: (pos as any).right !== undefined ? '2px solid rgba(255,255,255,0.7)' : 'none',
            }} />
          </div>
        ))}

        {/* Position indicator */}
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md text-[10px] font-mono z-10" style={{ background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.8)' }}>
          {position.x}% · {position.y}%
        </div>
      </div>

      {/* Bottom toolbar */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
        <button
          onClick={onSmartCrop}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ background: COLORS.gold, color: COLORS.white, boxShadow: `0 4px 12px ${COLORS.gold}40` }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Smart Crop
        </button>
        <button
          onClick={() => onPositionChange({ x: 0, y: 0 })}
          className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
        >
          Centrer
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
        >
          Terminé
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Layout Mini Preview ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function LayoutMiniPreview({
  layout,
}: {
  layout: '1-up' | '2-up' | '3-up' | '4-up';
}) {
  const slots = getSlots(layout);
  return (
    <div className="aspect-[210/297] flex flex-col gap-0.5 w-full">
      {slots.map((row, rowIdx) => (
        <div
          key={rowIdx}
          // PARITY (2026-07-14): show 3-up's real 60/40 print split in the picker icon.
          className={cn('flex gap-0.5', layout !== '3-up' && 'flex-1')}
          style={layout === '3-up' ? { flex: `${rowIdx === 0 ? 0.6 : 0.4} 0 0%` } : undefined}
        >
          {row.map((slotIdx) => (
            <div
              key={slotIdx}
              className="flex-1 rounded-[2px]"
              style={{ background: `${COLORS.gold}30` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Icon Components ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function ImageIcon({ active }: { active: boolean }) {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: active ? COLORS.gold : UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function LayoutIcon({ active }: { active: boolean }) {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: active ? COLORS.gold : UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
      />
    </svg>
  );
}

function ThemeIcon({ active }: { active: boolean }) {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: active ? COLORS.gold : UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
      />
    </svg>
  );
}

function TextToolIcon() {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: UI.muted }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 7V4h16v3M9 20h6M12 4v16"
      />
    </svg>
  );
}

function PhotoToolIcon() {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function RectToolIcon() {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={1.5} />
    </svg>
  );
}

function EllipseToolIcon() {
  return (
    <svg
      className="w-4 h-4"
      style={{ color: UI.muted }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <ellipse cx="12" cy="12" rx="9" ry="9" strokeWidth={1.5} />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Helpers ──────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// Restored to the original 2-up/3-up grid per user feedback — the
// portrait-aware side-by-side split was reverted. Subject framing is
// improved in the PDF generator's computeDrawParams (gentle zoom +
// downward face-centroid bias); the slot geometry itself stays as
// it was before commit c54ea46.
// Fix #4 / #5 (2026-05-28): one spread thumbnail button.
//   * Tap — jumps to the spread (existing goToSpread).
//   * X chip is ALWAYS visible (top-right corner, red circle) when the
//     spread is deletable. Tap → window.confirm → handleDeletePage.
//     Was previously a destructive one-tap delete.
//   * Reorder is owned by dnd-kit's TouchSensor (2 s hold, see
//     SpreadStripSortable) — no in-component long-press logic.
// ⋯ options menu on a back-cover photo (2026-07-15): toggles "Image pleine
// page" (full-bleed back cover, exclusive) ⇄ "Remettre en grille". Local
// open-state per tile; tap-away closes.
function BackCoverTileMenu({ isFull, size = 'sm', onToggleFull }: { isFull: boolean; size?: 'sm' | 'lg'; onToggleFull: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('absolute', size === 'lg' ? 'top-2 left-2' : 'top-1 left-1')} onClick={(e) => e.stopPropagation()}>
      <button
        className={cn(size === 'lg' ? 'w-8 h-8' : 'w-6 h-6', 'rounded-full flex items-center justify-center shadow-md active:scale-90')}
        style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
        onClick={() => setOpen((v) => !v)}
        aria-label="Options de l'image"
        title="Options de l'image"
      >
        <svg className={size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'} fill="currentColor" viewBox="0 0 24 24">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-xl py-1 whitespace-nowrap" style={{ background: UI.surface, border: '1px solid rgba(0,0,0,0.08)' }}>
            <button
              className="w-full text-left px-3 py-1.5 text-[11px] font-medium hover:bg-gray-50"
              style={{ color: UI.ink }}
              onClick={() => { setOpen(false); onToggleFull(); }}
            >
              {isFull ? '⊞ Remettre en grille' : '⛶ Image pleine page'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SpreadThumbButton({
  spreadIdx,
  canDelete,
  isActive,
  onTap,
  onDelete,
  isMobile,
  children,
}: {
  spreadIdx: number;
  canDelete: boolean;
  /** Only the currently-selected spread shows the corner ✕ — keeps the
      strip visually quiet until the customer has actually picked a page. */
  isActive: boolean;
  onTap: () => void;
  onDelete: () => void;
  /** Slightly larger touch target on mobile for the corner X. */
  isMobile: boolean;
  children: React.ReactNode;
}) {
  const chipSize = isMobile ? 22 : 18;
  const iconSize = isMobile ? 12 : 10;
  // Visibility split (the regression fix): on MOBILE only the active spread
  // shows the ✕ (keeps the 1-D strip uncluttered; the canvas hold-to-delete is
  // the primary mobile path). On DESKTOP the ✕ is rendered on every deletable
  // thumb and revealed on hover — plus it stays lit on the active spread — so a
  // single hover+click deletes from any thumbnail. Commit 062db2de had gated it
  // behind `&& isActive` everywhere, which hid it until you first clicked a
  // spread and read as "scroller delete is broken" on desktop.
  const showChip = canDelete && (isMobile ? isActive : true);
  const hoverRevealed = !isMobile && !isActive; // desktop, non-active → fade in on hover
  return (
    <div className="group relative" data-spread-thumb-idx={spreadIdx}>
      <button
        onClick={onTap}
        className="shrink-0 flex flex-col items-center gap-1 transition-all"
        // pan-x (not 'none') lets the browser handle native horizontal scroll
        // when the customer drags the strip via a thumbnail — without this it
        // was 'none', which made every drag that STARTED on a thumb feel
        // sticky because native scroll was disabled. Reorder still works:
        // SpreadStripSortable's TouchSensor only arms after a 400 ms hold
        // (12 px tolerance), so a normal scroll swipe wins the gesture.
        // We block vertical pan + zoom on purpose — the strip is 1-D.
        //
        // WebkitTouchCallout: 'none' kills iOS Safari's native long-press
        // image callout ("Save Image / Copy / Open in New Tab") + the floating
        // image-peek preview. Without it, the long-press hold required for reorder
        // was hijacked by Safari the moment it recognised the thumbnail's
        // <img> — the customer saw the iOS share sheet instead of the drag
        // affordance. The property inherits, so descendant <img>s are covered.
        style={{ touchAction: 'pan-x', WebkitTouchCallout: 'none' }}
      >
        {children}
      </button>

      {/* Delete chip — rendered INLINE inside the thumb wrapper so it follows
          the thumb naturally (no createPortal + requestAnimationFrame chasing
          the strip's getBoundingClientRect each frame, which made the chip
          feel buggy during scroll and float over modals at z-60). Sits at the
          top-right corner of the frame with a 6-px overhang on each side; the
          strip's vertical buffer (items-center + h-[100px] vs the 60-px thumb)
          leaves enough room for the overhang without clipping. The parent
          gates `canDelete` on overlay state so this chip cannot appear over
          the validation modal / sheets / drawer. */}
      {showChip && (
        <button
          data-onboarding-target="page-delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          title="Supprimer ce spread"
          aria-label="Supprimer ce spread"
          className={cn(
            'absolute rounded-full flex items-center justify-center transition-all active:scale-90',
            hoverRevealed && 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
          )}
          style={{
            top: -6,
            right: -6,
            width: chipSize,
            height: chipSize,
            background: '#DC2626',
            color: COLORS.white,
            boxShadow: '0 2px 6px rgba(220, 38, 38, 0.35), 0 0 0 1.5px #fff',
            zIndex: 5,
          }}
        >
          <svg width={iconSize} height={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Drag-to-scroll for the bottom spread strip (desktop mouse only) ────────
// Grab the strip body and slide it 1:1 with the cursor — the standard
// drag-to-scroll carousel gesture. Deliberately MOUSE-ONLY: touch already
// drag-scrolls natively (overflow-x + WebkitOverflowScrolling momentum), and
// the mobile reorder path (TouchSensor long-press) is finely tuned, so we
// don't lay a finger on it. Two things keep this from stepping on anything:
//   1. pausedRef — set true while a dnd-kit reorder is live; the move handler
//      bails so scroll and reorder never fight. A quick grab-and-slide never
//      arms a reorder anyway (MouseSensor needs a 250 ms hold and cancels past
//      6 px), so the two gestures are cleanly separated by intent.
//   2. click guard — once a drag has actually moved, we swallow the click that
//      the browser fires on mouseup, so a *drag* doesn't also navigate / insert
//      / delete. A plain click with no movement passes straight through, so
//      tap-to-open a spread, the + insert buttons and the ✕ delete all still work.
// Returns an onMouseDown handler to spread onto the strip container. Attaching
// declaratively (vs a ref effect) is timing-safe: React binds it whenever the
// container renders, even if the strip mounts after the album finishes loading.
function useMouseDragScroll(pausedRef: React.RefObject<boolean>) {
  const st = useRef({ down: false, dragging: false, startX: 0, startScroll: 0 });
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0 || pausedRef.current) return; // left button only
      const el = e.currentTarget;
      const s = st.current;
      s.down = true;
      s.dragging = false;
      s.startX = e.clientX;
      s.startScroll = el.scrollLeft;

      const cleanup = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      const onMove = (ev: MouseEvent) => {
        if (!s.down) return;
        // Yield completely to an active dnd-kit reorder.
        if (pausedRef.current) {
          s.down = false;
          s.dragging = false;
          el.style.cursor = 'grab';
          cleanup();
          return;
        }
        const dx = ev.clientX - s.startX;
        if (!s.dragging && Math.abs(dx) > 5) {
          s.dragging = true;
          el.style.cursor = 'grabbing';
        }
        if (s.dragging) {
          ev.preventDefault(); // stop text/image selection while dragging
          el.scrollLeft = s.startScroll - dx;
        }
      };
      const onUp = () => {
        cleanup();
        s.down = false;
        el.style.cursor = 'grab';
        if (s.dragging) {
          // Swallow the trailing click so a scroll gesture never fires a
          // thumbnail tap / + insert / ✕ delete. once:true auto-removes it;
          // the timeout clears it if (rarely) no click follows.
          const swallow = (cev: Event) => {
            cev.stopPropagation();
            cev.preventDefault();
          };
          el.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => el.removeEventListener('click', swallow, true), 0);
        }
        s.dragging = false;
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [pausedRef],
  );
}

// ─── Drag-to-reorder wiring for the bottom spread strip ─────────────────────
// Wraps the spread thumbnails in a single DndContext + SortableContext.
// Sensors are tuned to coexist with normal taps + horizontal scroll:
//   MouseSensor — 8 px distance threshold so desktop taps still register as
//     clicks on the thumb / delete X / insert + buttons, and a small drag
//     starts a reorder immediately.
//   TouchSensor — 400 ms long-press delay + 12 px tolerance (E5). The old
//     2000 ms hold was untenable on a phone: nobody keeps a finger perfectly
//     still for 2 s, so the hold aborted into a scroll ~every time and
//     reorder felt broken on iPhone. 400 ms is the platform-familiar
//     long-press; 12 px of wobble is forgiven during the hold (fingers drift),
//     while a genuine scroll swipe moves >12 px well before 400 ms and still
//     wins cleanly.
//
// IMPORTANT: we deliberately use MouseSensor (NOT PointerSensor) for the mouse
// path. PointerSensor handles Pointer Events, which INCLUDE touch — so on a
// phone its 8 px / no-delay constraint would activate a drag the instant a
// scroll swipe slid past 8 px, hijacking the gesture before the TouchSensor's
// long-press ever ran. That was the cause of accidental reorders while
// scrolling. MouseSensor ignores touch, so touch is governed solely by the
// long-press; the tolerance aborts the hold the moment the finger
// slides, letting a normal scroll win every time.
function SpreadStripSortable({
  totalSpreads,
  onReorder,
  onActiveChange,
  children,
}: {
  totalSpreads: number;
  onReorder: (from: number, to: number) => void;
  /** Fires true when a reorder drag starts, false on end/cancel. The parent's
      drag-to-scroll handler reads this to fully yield while reordering. */
  onActiveChange?: (active: boolean) => void;
  children: React.ReactNode;
}) {
  const sensors = useSensors(
    // Desktop reorder is now HOLD-then-drag (250 ms), mirroring the mobile
    // long-press: a quick grab-and-slide (drag-to-scroll attempt) never steals
    // a page anymore — reordering is a deliberate press-hold-move. 6 px of
    // wobble forgiven during the hold; a fast horizontal fling moves further
    // sooner and cleanly aborts into a scroll.
    useSensor(MouseSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 12 } }),
  );
  const items = useMemo(
    () => Array.from({ length: totalSpreads }, (_, i) => `s${i}`),
    [totalSpreads],
  );
  const handleEnd = (e: DragEndEvent) => {
    onActiveChange?.(false);
    if (!e.over) return;
    const fromId = String(e.active.id);
    const toId   = String(e.over.id);
    const from = parseInt(fromId.replace('s', ''), 10);
    const to   = parseInt(toId.replace('s',  ''), 10);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;
    if (from === to) return;
    onReorder(from, to);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => onActiveChange?.(true)}
      onDragEnd={handleEnd}
      onDragCancel={() => onActiveChange?.(false)}
    >
      <SortableContext items={items} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

function SortableSpreadItem({
  spreadIdx,
  disabled = false,
  children,
  className,
  ...rest
}: {
  spreadIdx: number;
  /** A themed book's opening spread: neither dragged nor dropped on. */
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `s${spreadIdx}`, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        // Lift the thumbnail off the strip the moment the 2 s hold arms the
        // drag (isDragging flips true before any movement). The scale + shadow
        // is the "now draggable" cue the customer feels when the long-press
        // completes; spreadDragTransform composes it onto dnd-kit's translate
        // null-safely (avoids the "undefined scale()" trap when idle).
        transform: spreadDragTransform(DnDCSS.Transform.toString(transform), isDragging),
        transition: isDragging ? 'transform 120ms ease' : transition,
        opacity: isDragging ? 0.9 : 1,
        boxShadow: isDragging ? '0 10px 26px rgba(0,0,0,0.28)' : undefined,
        zIndex: isDragging ? 50 : undefined,
        // E5 — the 400 ms hold now overlaps iOS Safari's own ~500 ms image
        // long-press callout; suppress the native menu on the strip so the
        // hold always belongs to the reorder drag.
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
      className={className}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </div>
  );
}

function getSlots(layout: string): number[][] {
  switch (layout) {
    case '1-up':  return [[0]];
    case '2-up':  return [[0], [1]];
    // 3-up: 1 wide hero on top + 2 narrow supports below. Matches the
    // preview (`AlbumFlowPage.tsx:getSlots`) and the PDF generator
    // (`pdfGenerator.ts:getSlots` 3-up case). Previously this was
    // `[[0,1],[2]]` (2 top + 1 bottom) which made the editor render
    // 3-up upside-down relative to preview + PDF — a real
    // editor-vs-preview mismatch.
    case '3-up':  return [[0], [1, 2]];
    case '4-up':  return [[0, 1], [2, 3]];
    // WIDE-TOP: two full-width stacked slots — slot 0 (wide group photo) on
    // top, slot 1 (companion) below. Same row structure as 2-up; the
    // DIFFERENCE is the row HEIGHTS, which come from `top_h_frac` (applied in
    // the PageView render + the PDF/preview getSlots), not equal halves.
    case 'wide-top': return [[0], [1]];
    // 3-up-flip: 2 portraits on top + 1 wide on the bottom. Row arrays MUST
    // match the preview + PDF getSlots (slots 0,1 top row; slot 2 bottom).
    case '3-up-flip': return [[0, 1], [2]];
    // 2-up-side (base rules): one row, two portraits side by side.
    case '2-up-side': return [[0, 1]];
    // 5-up: one big band, then two rows of two. 6-up: three rows of two.
    case '5-up': return [[0], [1, 2], [3, 4]];
    case '6-up': return [[0, 1], [2, 3], [4, 5]];
    // 4-up-main: one big band, three below.
    case '4-up-main': return [[0], [1, 2, 3]];
    // 2-up-even: two landscapes, the spare height shared evenly.
    case '2-up-even': return [[0], [1]];
    default:      return [[0]];
  }
}

/**
 * Return the slot rectangles for a given page. When a template is set, use
 * the template's slots; otherwise fall back to the layout-derived slots.
 *
 * Phase 1 note: the editor's per-page render still uses `getSlots(page.layout)`
 * even on templated pages. This helper is wired up here so future phases can
 * swap in the template's absolute rectangles without another structural pass.
 *
 * `void`-suffix references below are deliberate — the helpers aren't called
 * yet (the template PNG overlay is enough for Phase 1), but we keep them
 * exported-as-noop so the next phase can swap them in without rewriting
 * geometry math. The dummy void-call makes TS's "noUnusedLocals" happy.
 */
function slotsForPage(
  page: { template_id?: string | null; layout: string },
  templatesById: Map<string, LayoutTemplate>,
): { x: number; y: number; width: number; height: number }[] | number[][] {
  if (page.template_id) {
    const t = templatesById.get(page.template_id);
    if (t) {
      return t.slots.map((s) => ({ x: s.x, y: s.y, width: s.width, height: s.height }));
    }
  }
  return getSlots(page.layout);
}

function isTemplateSlots(
  s: ReturnType<typeof slotsForPage>,
): s is { x: number; y: number; width: number; height: number }[] {
  return s.length > 0 && typeof (s[0] as { x: unknown }).x === 'number';
}

// Phase 1 — keep references alive so noUnusedLocals doesn't strip these
// helpers before Phase 2 wires them into the geometry layer.
void slotsForPage;
void isTemplateSlots;

// Returns the relative aspect ratio of a slot for a given layout + slot
// index. Same ratios as the PDF generator's getSlots in pdfGenerator.ts so
// the editor's fit decision matches what the PDF will render.
//   1-up: full A4 portrait → 210 × 297
//   2-up: half-height row  → 210 × 148
//   3-up: top wide row 60% + 2 bottom 40% squares
//   4-up: 2×2 grid → ~105 × 148 each
//
// Currently unused after the cover-fit-only switch; kept for future
// reuse if we revive per-photo contain decisions.
// @ts-expect-error retained for future reuse
function slotDimensionsForLayout(layout: string, slotIndex: number): { width: number; height: number } {
  switch (layout) {
    case '1-up': return { width: 210, height: 297 };
    case '2-up': return { width: 210, height: 148 };
    case '3-up':
      return slotIndex === 0
        ? { width: 210, height: 178 }
        : { width: 105, height: 119 };
    case '4-up': return { width: 105, height: 148 };
    default:     return { width: 210, height: 297 };
  }
}

// ───────────────────────────────────────────────────────────────────────────
// AI Layout Loader — shown while /ai/layout is in flight. Intentionally
// minimal: branded animation + a single friendly message + smooth progress.
// No exposed internals (face detection / burst grouping / scene labels).
// ───────────────────────────────────────────────────────────────────────────
function AiLayoutLoader({ photoCount: _photoCount }: { photoCount: number }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => setElapsed(Date.now() - start), 200);
    return () => clearInterval(t);
  }, []);
  // Estimate: typical layout call finishes in ~18 s. Cap at 95% so the bar
  // never claims "done" while we're still waiting on the response.
  const pct = Math.min(95, (elapsed / 18000) * 100);

  // Fix #2 (2026-05-29): rotating premium messages so the customer never
  // stares at a static label. Stages cycle every ~1.5 s; total ~7.5 s loop
  // — well within the 18 s budget so the customer sees every beat before
  // the call returns. setTimeout cues are intentionally fake-progress —
  // we don't have real signal from the worker — but they reassure the
  // customer that the experience is alive and premium.
  const STAGES = [
    'Analyse de vos photos…',
    'Détection des moments forts…',
    'Sélection des modèles…',
    'Équilibrage des doubles pages…',
    'Mise en page finale…',
  ];
  const stageIdx = Math.min(STAGES.length - 1, Math.floor(elapsed / 1500));

  return (
    <div className="text-center py-7 px-2">
      <style>{`
        @keyframes storizFloat    { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes storizSpinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes storizStageIn  { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Animated AI orb */}
      <div className="relative mx-auto w-24 h-24 mb-5" style={{ animation: 'storizFloat 3s ease-in-out infinite' }}>
        <div className="absolute inset-0 rounded-full animate-ping opacity-30"
             style={{ background: `radial-gradient(circle, ${COLORS.gold}40 0%, transparent 70%)` }} />
        <div className="absolute inset-2 rounded-full border-2 border-dashed"
             style={{ borderColor: `${COLORS.gold}55`, animation: 'storizSpinSlow 9s linear infinite' }} />
        <div className="absolute inset-5 rounded-full flex items-center justify-center shadow-lg"
             style={{ background: `linear-gradient(135deg, ${COLORS.goldLight} 0%, ${COLORS.gold} 60%, ${COLORS.goldDark} 100%)` }}>
          <span className="text-2xl select-none">✨</span>
        </div>
        <div className="absolute inset-0" style={{ animation: 'storizSpinSlow 4s linear infinite reverse' }}>
          <span className="absolute w-2 h-2 rounded-full shadow"
                style={{ background: COLORS.gold, top: '-2px', left: '50%', transform: 'translateX(-50%)' }} />
        </div>
      </div>

      {/* Single friendly headline + a rotating staged subtitle */}
      <h3 className="font-bold mb-1"
          style={{ color: UI.ink, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", fontSize: 22 }}>
        Création de votre album
      </h3>
      <p
        key={stageIdx}
        className="text-xs mb-5"
        style={{
          color: UI.muted,
          // Each new stage fades+rises in so the swap feels intentional.
          animation: 'storizStageIn 320ms ease-out both',
        }}
      >
        {STAGES[stageIdx]}
      </p>

      {/* Smooth progress bar */}
      <div className="w-full max-w-[240px] mx-auto h-1.5 rounded-full overflow-hidden"
           style={{ background: `${COLORS.gold}18` }}>
        <div className="h-full rounded-full transition-[width] duration-300 ease-out"
             style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS.goldLight}, ${COLORS.gold})` }} />
      </div>

      {/* Three bouncing dots for rhythm */}
      <div className="flex items-center justify-center gap-1.5 mt-4">
        {[0, 150, 300].map((delay) => (
          <span key={delay} className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ background: COLORS.gold, animationDelay: `${delay}ms` }} />
        ))}
      </div>
    </div>
  );
}

// ─── Editor text fonts (Section 7 — Google-Fonts-backed) ───────────────────
// Loaded via <link> in index.html so they're already available by the
// time the toolbar opens. Order = how they appear in "All fonts" in the
// picker. Each entry's `family` is the exact CSS font-family string we
// set on the element, so it round-trips correctly through the PDF
// generator.
// localStorage keys for the picker's persistent preferences. Starred =
// favourites the user pinned to the top; Recent = last 5 picks so the
// most-used fonts always sit one tap away.
const FONT_STARRED_KEY = 'storiz_editor_starred_fonts';
const FONT_RECENT_KEY  = 'storiz_editor_recent_fonts';
const FONT_RECENT_LIMIT = 5;

function loadFontList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch { return []; }
}
function saveFontList(key: string, list: string[]) {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* ignore quota */ }
}

// ─── Éléments sidebar (Section 9) ──────────────────────────────────────────
// The resources panel (customer AND operator editor; side panel on desktop,
// bottom sheet on a phone), top to bottom:
//   1. « Modèles de page » — the door to the design system v2 models
//      (components/editor/ThemeModelPicker, the same picker as « Mise en page »
//      and the page menu). The old Canva template list (layout_templates, all
//      retired) is gone: it only ever said « Aucun modèle disponible… ».
//   2. « Icônes » — every cover décor (components/editor/StickerPanel).
//   3. « Stickers & icônes » — the design system's pieces as stickers, and the
//      admin's own SVG icons (Paramètres → Ressources de l'éditeur) alongside.
function ElementsSidebar({
  onInsertIcon,
  onInsertCoverIcon,
  onAddSticker,
  albumTheme,
  covers,
  destination,
  selectedPage,
  onOpenModels,
  onRemoveTemplate,
}: {
  onInsertIcon: (icon: EditorIcon) => void;
  /** Places a catalogue-décor icon (cover design PNG) on the focused page. */
  onInsertCoverIcon: (cover: Cover) => void;
  /** Places a sticker (lib/stickers/catalog.ts) on the focused page. */
  onAddSticker: (sticker: Sticker) => void;
  /** The album's theme — its stickers open first. */
  albumTheme: string | null;
  /** Covers catalogue (already fetched by the editor — no extra query). */
  covers: Cover[];
  /** This album's COD destination — matching icons float to the top. */
  destination: string;
  selectedPage?: LayoutPage | null;
  /** Opens the « Modèles » picker on the focused page. */
  onOpenModels?: () => void;
  /** Removes the focused page's model. */
  onRemoveTemplate?: () => void;
}) {
  // The admin's SVG icons (editor_icons).
  const { data: iconsData, isLoading: iconsLoading } = useQuery({
    queryKey: ['editor-icons'],
    queryFn: () => editorResourcesApi.listIcons(),
    staleTime: 60_000,
  });
  const [elementsOpen, setElementsOpen] = useState(true);
  const icons = iconsData?.icons ?? [];

  return (
    <div className="p-3 space-y-4">
      {/* Section 1 — the v2 « Modèles » for this page. */}
      {onOpenModels && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold" style={{ color: UI.ink }}>Modèles de page</p>
            {(selectedPage?.template_id || selectedPage?.theme) && onRemoveTemplate && (
              <button
                onClick={onRemoveTemplate}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: `${COLORS.danger}15`, color: UI.danger }}
                title="Retirer le modèle de cette page"
              >
                Retirer
              </button>
            )}
          </div>
          <button
            onClick={() => { if (selectedPage) onOpenModels(); }}
            disabled={!selectedPage}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors disabled:opacity-40"
            style={{ background: `${COLORS.gold}12`, border: `1px solid ${COLORS.gold}35`, color: UI.ink }}
          >
            <svg className="w-5 h-5 shrink-0" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="3" width="16" height="18" rx="2" strokeWidth={1.6} />
              <rect x="7" y="6" width="10" height="6" rx="1" strokeWidth={1.4} />
              <path d="M7 15h6M7 18h4" strokeWidth={1.4} strokeLinecap="round" />
            </svg>
            <span className="flex-1 min-w-0">
              <span className="block text-xs font-semibold">Choisir un modèle</span>
              <span className="block text-[10px]" style={{ color: UI.muted }}>Une page décorée avec vos photos</span>
            </span>
          </button>
        </div>
      )}

      {/* Section 2 — Icônes Storiz: every cover décor as a placeable icon
          (light 200 px thumbs; destination/event matches first, ✦). */}
      <div style={{ borderTop: onOpenModels ? `1px solid ${COLORS.gold}15` : undefined, paddingTop: onOpenModels ? 12 : 0 }}>
        <p className="px-1 pb-1.5 text-[11px] font-semibold" style={{ color: UI.ink }}>Icônes</p>
        <CoverIconsSection covers={covers} destination={destination} onInsertCoverIcon={onInsertCoverIcon} />
      </div>

      {/* Section 3 — Stickers (design system pieces) + the admin's icons. */}
      <div style={{ borderTop: `1px solid ${COLORS.gold}15`, paddingTop: 12 }}>
        <button
          onClick={() => setElementsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-1 py-1 text-[11px] font-semibold"
          style={{ color: UI.ink }}
          aria-expanded={elementsOpen}
        >
          <span>Stickers</span>
          <svg
            className={cn('w-3 h-3 transition-transform', elementsOpen && 'rotate-180')}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {elementsOpen && (
          <div className="mt-2">
            <StickersSection
              albumTheme={albumTheme}
              onAddSticker={onAddSticker}
              adminIcons={icons}
              adminIconsLoading={iconsLoading}
              onInsertIcon={onInsertIcon}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Template Fill Modal ───────────────────────────────────────────────────
// After a template is picked, this previews its image zones. Each zone shows a
// "+" until the customer assigns one of their uploaded photos (cover-fit).
// Valider applies the template to the page with the chosen photos in place.
function TemplateFillModal({
  template,
  photos,
  onCancel,
  onValidate,
}: {
  template: LayoutTemplate;
  photos: Array<{ id: string; signed_url?: string | null; tray_signed_url?: string | null; thumbnail_signed_url?: string | null }>;
  onCancel: () => void;
  onValidate: (photoIds: (string | null)[]) => void;
}) {
  const slotCount = template.slot_count;
  const [assign, setAssign] = useState<(string | null)[]>(() => new Array(slotCount).fill(null));
  const [pickingSlot, setPickingSlot] = useState<number | null>(null);
  const photoUrl = (id: string | null) => {
    if (!id) return null;
    const p = photos.find((x) => x.id === id);
    // Tray tier first (~320px) — this is a small preview thumbnail, no need for
    // the 1400px working image. Canvas/preview/PDF still use signed_url/original.
    return p?.tray_signed_url ?? p?.signed_url ?? p?.thumbnail_signed_url ?? null;
  };
  const filledCount = assign.filter(Boolean).length;
  // SVG-href-swap templates render the photo INSIDE the emplacement with décor
  // on top (same renderer as the editor + PDF) — mirrors the editor's condition.
  const isSvgTemplate = !!template.original_svg_url && template.slots.every((s) => typeof s.dom_index === 'number');
  const PREVIEW_W = 270;
  const PREVIEW_H = Math.round((PREVIEW_W * 297) / 210);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            {pickingSlot === null ? 'Remplir le modèle' : 'Choisir une photo'}
          </h2>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {pickingSlot === null ? (
            <>
              <p className="text-xs text-gray-500 mb-3">Touchez <b>+</b> pour ajouter une photo. Sur une zone remplie : <b>↻</b> remplacer, <b>✕</b> retirer.</p>
              {isSvgTemplate ? (
                // Photo swapped INTO the slot <image>; the SVG décor renders ON
                // TOP — identical compositing to the editor + PDF. The "+" hints
                // on empty slots are pointer-events-none so taps reach the
                // renderer's hit layer (onSlotSelect → open the picker).
                <div className="relative mx-auto rounded-lg overflow-hidden border border-gray-200" style={{ width: PREVIEW_W, height: PREVIEW_H }}>
                  <TemplatedPageWithSvgFetch
                    svgUrl={template.original_svg_url!}
                    slots={template.slots}
                    photoUrlsBySlotIndex={Object.fromEntries(
                      template.slots.map((_, i) => [i, photoUrl(assign[i])]),
                    )}
                    photoFitsBySlotIndex={{}}
                    photoIdBySlotIndex={Object.fromEntries(template.slots.map((_, i) => [i, assign[i] ?? null]))}
                    pageWidthPx={PREVIEW_W}
                    pageHeightPx={PREVIEW_H}
                    interactive
                    selectedSlotIndex={null}
                    onSlotSelect={(slotIdx: number) => setPickingSlot(slotIdx)}
                    onSlotDrop={() => {}}
                    onFitChange={() => {}}
                  />
                  {template.slots.map((slot, i) => (
                    <div
                      key={i}
                      className="absolute"
                      style={{ left: `${slot.x * 100}%`, top: `${slot.y * 100}%`, width: `${slot.width * 100}%`, height: `${slot.height * 100}%`, pointerEvents: 'none' }}
                    >
                      {assign[i] ? (
                        // Filled zone — replace (re-open picker) + remove (clear).
                        <div className="absolute top-1 right-1 flex gap-1" style={{ pointerEvents: 'auto' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setPickingSlot(i); }}
                            title="Remplacer la photo"
                            className="w-6 h-6 rounded-full bg-white/95 shadow flex items-center justify-center"
                            style={{ color: COLORS.gold }}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setAssign((a) => { const n = [...a]; n[i] = null; return n; }); }}
                            title="Retirer la photo"
                            className="w-6 h-6 rounded-full bg-white/95 shadow flex items-center justify-center text-red-600"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="flex items-center justify-center rounded-full bg-white/85 shadow" style={{ width: 26, height: 26, color: COLORS.gold, fontSize: 20, lineHeight: 1 }}>+</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Legacy (non-SVG) templates: photos in slot rects + the
                // decorative overlay PNG drawn ON TOP (matches the PDF's
                // "overlay drawn last" order).
                <div
                  className="relative mx-auto aspect-[210/297] rounded-lg overflow-hidden border border-gray-200"
                  style={{ maxWidth: PREVIEW_W, backgroundColor: template.background_color ?? '#f3f4f6' }}
                >
                  {template.slots.map((slot, i) => {
                    const url = photoUrl(assign[i]);
                    return (
                      <div
                        key={i}
                        role="button"
                        onClick={() => setPickingSlot(i)}
                        className="absolute flex items-center justify-center overflow-hidden rounded-sm cursor-pointer"
                        style={{
                          left: `${slot.x * 100}%`, top: `${slot.y * 100}%`,
                          width: `${slot.width * 100}%`, height: `${slot.height * 100}%`,
                          border: url ? '2px solid transparent' : `2px dashed ${COLORS.gold}99`,
                          background: url ? 'transparent' : `${COLORS.gold}14`,
                        }}
                      >
                        {url
                          ? <img src={url} alt="" className="w-full h-full object-cover" />
                          : <span className="text-2xl font-light leading-none" style={{ color: COLORS.gold }}>+</span>}
                        {url && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setAssign((a) => { const n = [...a]; n[i] = null; return n; }); }}
                            title="Retirer la photo"
                            className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/95 shadow flex items-center justify-center text-red-600"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {(template.overlay_print_png_url || template.overlay_svg_url) && (
                    <img
                      src={template.overlay_print_png_url ?? template.overlay_svg_url ?? undefined}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => setPickingSlot(null)} className="text-sm font-medium mb-3" style={{ color: COLORS.gold }}>← Retour</button>
              {photos.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-10">Aucune photo importée.</p>
              ) : (
                // VIRTUALIZED (Batch 1): only on-screen rows mount. Bounded 60vh
                // height so react-window can measure. minTile 88px ≈ the old 3-col
                // grid; tiles use the tray tier via photoUrl(). The grid below
                // owns its own scroll, so the parent body scroll is unused here.
                <div style={{ height: '60vh' }}>
                  <VirtualPhotoGrid
                    count={photos.length}
                    minTile={88}
                    gap={8}
                    renderTile={(i) => {
                      const p = photos[i];
                      if (!p) return null;
                      const u = photoUrl(p.id); // tray tier first
                      return (
                        <button
                          onClick={() => {
                            setAssign((a) => { const n = [...a]; n[pickingSlot] = p.id; return n; });
                            setPickingSlot(null);
                          }}
                          className="aspect-square w-full rounded-lg overflow-hidden border border-gray-200 hover:border-brand-500"
                        >
                          {u && <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />}
                        </button>
                      );
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {pickingSlot === null && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-400">{filledCount}/{slotCount} zone{slotCount > 1 ? 's' : ''} remplie{filledCount > 1 ? 's' : ''}</span>
            <button
              onClick={() => onValidate(assign)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: COLORS.gold }}
            >
              Valider
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** The three text-alignment labels, one place (2026-10-18: the toolbar's
 *  centre button read « Aligner au centre » and the owner saw the wrong
 *  tooltip on it). */
const ALIGN_LABELS = { left: 'Aligner à gauche', center: 'Centrer', right: 'Aligner à droite' } as const;

/** A toolbar icon button. Module-level on purpose (2026-10-18): it used to be
 *  declared INSIDE FloatingElementToolbar, so every render made a new
 *  component type and React re-mounted every button — under the cursor the
 *  native tooltip could belong to a button that no longer existed. */
function ToolbarBtn({
  label, active, onClick, onPointerDown, onPointerUp, children,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
  onPointerDown?: () => void;
  onPointerUp?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      title={label}
      aria-label={label}
      className="w-11 h-11 md:w-8 md:h-8 shrink-0 rounded-lg flex items-center justify-center transition-all active:scale-95"
      style={{
        background: active ? `${COLORS.gold}20` : 'transparent',
        color: active ? COLORS.gold : UI.ink,
      }}
    >
      {children}
    </button>
  );
}

// ─── Floating element toolbar (Section 1 + Section 6) ──────────────────────
// Appears bottom-centred whenever a PageElement is selected.
//
// Section 1 controls — universal:
//   color, opacity, crop (photo), flip, position, delete.
// Section 6 extension — when a text element is selected, the toolbar
//   shows a second row with: font picker, font size +/-, bold,
//   italic, underline, alignment.
//
// All writes go through onUpdate so the history recorder picks them up.
function FloatingElementToolbar({
  element,
  documentFonts,
  customFonts = [],
  // isEditing prop kept for callers but no longer read: the single Terminé
  // button (2026-07-08) shows in every state, not just while typing.
  onUpdate,
  onDelete,
  onStopEditing,
  boundsRef,
  inline = false,
}: {
  element: PageElement;
  /** « Nouvel éditeur mobile »: the bar is a block in the page's bottom strip
   *  (in the flow, above the keyboard), not a fixed overlay. Same controls. */
  inline?: boolean;
  /** The canvas (<main>): the bar is placed inside it, above or below the
   *  selected element — never over it, the strip or the header. */
  boundsRef?: React.RefObject<HTMLElement | null>;
  /** Fonts currently in use anywhere in the album — fed into the
   *  picker's "Document fonts" section so the customer can hop
   *  between styles already on their pages. */
  documentFonts: string[];
  /** Custom fonts loaded via Admin → Editor Resources → Fonts. Shown
   *  in the "Toutes les polices" section after the baseline 12. */
  customFonts?: { family: string; label: string }[];
  /** True when the element is in inline text-edit mode. Drives the
   *  Terminé button so the customer has a clear way out on mobile
   *  (tapping outside the textarea blurs it, but on touch that's
   *  fiddly and the inline trash icon is too aggressive). */
  isEditing?: boolean;
  onUpdate: (updates: Partial<PageElement>) => void;
  onDelete: () => void;
  /** Called from the Terminé button — drops out of text-edit mode
   *  without deselecting the element. */
  onStopEditing?: () => void;
}) {
  const [open, setOpen] = useState<'color' | 'opacity' | 'position' | 'font' | null>(null);
  // Starred + recently used fonts (persisted in localStorage). State is
  // lazy-init so the first render reads from localStorage exactly once.
  const [starred, setStarred] = useState<string[]>(() => loadFontList(FONT_STARRED_KEY));
  const [recent, setRecent]   = useState<string[]>(() => loadFontList(FONT_RECENT_KEY));

  const toggleStar = useCallback((family: string) => {
    setStarred((prev) => {
      const next = prev.includes(family) ? prev.filter((f) => f !== family) : [family, ...prev];
      saveFontList(FONT_STARRED_KEY, next);
      return next;
    });
  }, []);

  const pickFont = useCallback((family: string) => {
    onUpdate({ fontFamily: family });
    setRecent((prev) => {
      const next = [family, ...prev.filter((f) => f !== family)].slice(0, FONT_RECENT_LIMIT);
      saveFontList(FONT_RECENT_KEY, next);
      return next;
    });
    setOpen(null);
  }, [onUpdate]);

  // What "color" means depends on the element type:
  //   - text     → fontColor
  //   - rect /
  //     ellipse  → fill
  //   - photo    → no colour control (skip)
  const isText = element.type === 'text';
  const isShape = element.type === 'rect' || element.type === 'ellipse';
  const isPhoto = element.type === 'photo';
  const colorValue = isText ? (element.fontColor ?? '#000000') : (element.fill ?? '#000000');
  const supportsColor = isText || isShape;

  const opacityPct = Math.round((element.opacity ?? 1) * 100);

  const handleColorChange = (hex: string) => {
    if (isText) onUpdate({ fontColor: hex });
    else if (isShape) onUpdate({ fill: hex });
  };

  // Long-press flip-vertical: regular click = flipH, long-press = flipV
  const flipPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flipDidLongPressRef = useRef(false);
  const handleFlipDown = () => {
    flipDidLongPressRef.current = false;
    flipPressRef.current = setTimeout(() => {
      flipDidLongPressRef.current = true;
      onUpdate({ flipV: !element.flipV });
    }, 500);
  };
  const handleFlipUp = () => {
    if (flipPressRef.current) {
      clearTimeout(flipPressRef.current);
      flipPressRef.current = null;
    }
    if (!flipDidLongPressRef.current) {
      onUpdate({ flipH: !element.flipH });
    }
  };

  // ONE bar, placed next to the element (UI overlap fix 2026-10-18). The two
  // stacked bars used to sit bottom-centre over the page and its page numbers,
  // often right on the text being edited. From tablet width up the bar now goes
  // ABOVE the element when there is room in the canvas, else BELOW it, and
  // follows it (drag, zoom, resize). Phones keep it docked above the tab bar.
  const dockBottomMq = useIsMobile();
  const dockBottom = dockBottomMq || inline;
  const barRowRef = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<{ side: 'above' | 'below'; x: number; y: number } | null>(null);
  useEffect(() => {
    if (dockBottom) { setPlace(null); return; }
    let raf = 0;
    let last = '';
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const bar = barRowRef.current;
      if (!bar) return;
      const m = boundsRef?.current?.getBoundingClientRect();
      const b0 = m ?? { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
      // never over the pages bar (« Régénérer », « Modèles », zoom) either
      const pagesBar = boundsRef?.current?.querySelector('[data-pages-bar]')?.getBoundingClientRect();
      const b = { top: Math.max(b0.top, pagesBar?.bottom ?? b0.top), bottom: b0.bottom, left: b0.left, right: b0.right };
      const node = document.querySelector(`[data-element-id="${CSS.escape(element.id)}"]`);
      // element not on screen (another spread, unmounting): bottom of the canvas
      const r = node?.getBoundingClientRect()
        ?? { top: b.bottom, bottom: b.bottom, left: (b.left + b.right) / 2, right: (b.left + b.right) / 2 };
      const h = bar.offsetHeight;
      const w = bar.offsetWidth;
      const GAP = 10;
      const roomAbove = r.top - b.top;
      const roomBelow = b.bottom - r.bottom;
      const side: 'above' | 'below' = roomAbove >= h + GAP ? 'above'
        : roomBelow >= h + GAP ? 'below'
        : roomAbove >= roomBelow ? 'above' : 'below';
      // above → y is the bar's BOTTOM edge; below → its TOP edge.
      const y = side === 'above'
        ? Math.max(b.top + 4 + h, r.top - GAP)
        : Math.min(b.bottom - 4 - h, r.bottom + GAP);
      const fitsCanvas = w <= (b.right - b.left) - 16;
      const minX = (fitsCanvas ? b.left : 0) + 8 + w / 2;
      const maxX = (fitsCanvas ? b.right : window.innerWidth) - 8 - w / 2;
      const x = Math.min(Math.max((r.left + r.right) / 2, minX), Math.max(minX, maxX));
      const key = `${side}|${Math.round(x)}|${Math.round(y)}`;
      if (key !== last) { last = key; setPlace({ side, x, y }); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [element.id, dockBottom, boundsRef]);
  const opensBelow = !dockBottom && place?.side === 'below';

  // The ONE bar (2026-10-18 — was two stacked bars): text controls, then
  // actions. Wraps on phones so every control is visible at once.
  const controlsBar = (
    <div
      ref={barRowRef}
      className="flex flex-wrap md:flex-nowrap items-center gap-1 px-2 py-1 rounded-2xl bg-white shadow-2xl mx-auto max-w-full justify-center"
      style={{ border: `1px solid ${COLORS.gold}30` }}
    >
      {/* « Nouvel éditeur mobile »: the words themselves in a REAL field (16 px) —
          a tap on it always raises the iOS keyboard; it writes the same
          element.text the page shows and prints. */}
      {inline && isText && (
        <textarea
          value={element.text === NEW_TEXT_PLACEHOLDER ? '' : (element.text ?? '')}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder={NEW_TEXT_PLACEHOLDER}
          aria-label="Modifier le texte"
          rows={2}
          className="basis-full w-full min-h-11 px-3 py-2 rounded-xl outline-none resize-none"
          style={{ fontSize: 16, lineHeight: 1.3, color: UI.ink, background: UI.panel, border: `1px solid ${COLORS.gold}55` }}
        />
      )}
      {/* Text controls — font + size + B/I/U + alignment (text only). */}
      {isText && (
        <>
          {/* Font family — opens the picker panel above */}
          <button
            onClick={() => setOpen((o) => o === 'font' ? null : 'font')}
            className="h-11 sm:h-9 md:h-8 px-2.5 shrink-0 rounded-lg flex items-center gap-1.5 text-sm transition-all active:scale-95"
            style={{
              background: open === 'font' ? `${COLORS.gold}20` : 'transparent',
              color: UI.ink,
              fontFamily: element.fontFamily ?? EDITOR_TEXT_FONTS[0].family,
              minWidth: 104,
              maxWidth: 150,
            }}
            title="Police"
          >
            <span className="truncate flex-1 text-left">
              {EDITOR_TEXT_FONTS.find((f) => f.family === element.fontFamily)?.label ?? EDITOR_TEXT_FONTS[0].label}
            </span>
            <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className="w-px h-6 mx-1" style={{ background: `${COLORS.gold}20` }} />

          {/* Font size — − number + */}
          <ToolbarBtn label="Diminuer la taille" onClick={() => onUpdate({ fontSize: Math.max(6, (element.fontSize ?? 18) - 2) })}>
            <span className="text-base font-bold">−</span>
          </ToolbarBtn>
          <input
            type="number"
            value={Math.round(element.fontSize ?? 18)}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onUpdate({ fontSize: Math.max(6, Math.min(200, n)) });
            }}
            className="w-11 h-9 md:h-8 shrink-0 text-center text-sm rounded outline-none border tabular-nums"
            style={{ borderColor: `${COLORS.gold}30` }}
            aria-label="Taille de police"
          />
          <ToolbarBtn label="Augmenter la taille" onClick={() => onUpdate({ fontSize: Math.min(200, (element.fontSize ?? 18) + 2) })}>
            <span className="text-base font-bold">+</span>
          </ToolbarBtn>

          <div className="w-px h-6 mx-1" style={{ background: `${COLORS.gold}20` }} />

          {/* Bold / Italic / Underline */}
          {/* fontWeight is stored as a string ('400' | 'bold') — toggle just flips between bold + 600 (default). */}
          <ToolbarBtn
            label="Gras"
            active={element.fontWeight === 'bold' || element.fontWeight === '700'}
            onClick={() => {
              const isBold = element.fontWeight === 'bold' || element.fontWeight === '700';
              onUpdate({ fontWeight: isBold ? '600' : 'bold' });
            }}
          >
            <span className="text-base font-bold">B</span>
          </ToolbarBtn>
          {/* Italic is implemented as a CSS fontStyle but PageElement
              doesn't have that field today — store it on textAlign as a
              hack would be wrong; instead we tag it on a NEW field
              we'll add to PageElement. For now we expose the button and
              persist via element.fontWeight encoding using a sentinel
              not great but stable: store italic as fontWeight value
              prefixed with 'italic-' so the renderer can split it. */}
          <ToolbarBtn
            label="Italique"
            active={!!element.fontStyle && element.fontStyle === 'italic'}
            onClick={() => onUpdate({ fontStyle: element.fontStyle === 'italic' ? 'normal' : 'italic' } as any)}
          >
            <span className="text-base italic">I</span>
          </ToolbarBtn>
          <ToolbarBtn
            label="Souligné"
            active={!!element.textDecoration && element.textDecoration === 'underline'}
            onClick={() => onUpdate({ textDecoration: element.textDecoration === 'underline' ? 'none' : 'underline' } as any)}
          >
            <span className="text-base underline">U</span>
          </ToolbarBtn>

          <div className="w-px h-6 mx-1" style={{ background: `${COLORS.gold}20` }} />

          {/* Alignment: left / center / right */}
          {([
            { v: 'left'   as const, icon: 'M3 6h18M3 12h12M3 18h18' },
            { v: 'center' as const, icon: 'M3 6h18M6 12h12M3 18h18' },
            { v: 'right'  as const, icon: 'M3 6h18M9 12h12M3 18h18' },
          ]).map(({ v, icon }) => (
            <ToolbarBtn
              key={v}
              label={ALIGN_LABELS[v]}
              active={(element.textAlign ?? 'center') === v}
              onClick={() => onUpdate({ textAlign: v })}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
              </svg>
            </ToolbarBtn>
          ))}

          <div className="w-px h-6 mx-1" style={{ background: `${COLORS.gold}20` }} />
        </>
      )}

      {/* Actions — colour, opacity, crop/flip (non-text), delete, Terminé. */}
        {supportsColor && (
          <ToolbarBtn label="Couleur du texte" active={open === 'color'} onClick={() => setOpen((o) => o === 'color' ? null : 'color')}>
            {/* Always-visible color swatch: the inner circle shows the current
                text color, while a permanent 2px gold ring + a thin dark inner
                outline keep the control distinct even when the color is pure
                white on a white/cream toolbar (customer reported it vanished). */}
            <span
              className="w-5 h-5 rounded-full block cursor-pointer transition-transform"
              style={{
                background: colorValue,
                border: `2px solid ${COLORS.gold}`,
                // inner hairline keeps a white swatch readable; outer soft ring adds depth
                boxShadow: `inset 0 0 0 1px rgba(31,41,55,0.25), 0 0 0 1px ${COLORS.gold}40`,
              }}
            />
          </ToolbarBtn>
        )}

        <ToolbarBtn label="Opacité" active={open === 'opacity'} onClick={() => setOpen((o) => o === 'opacity' ? null : 'opacity')}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8 8 6 11 6 15a6 6 0 0012 0c0-4-2-7-6-13z" />
          </svg>
        </ToolbarBtn>

        {/* Crop — photo elements only; opens slot-photo pan mode in a future
            pass. For now we surface the button and tell the user it's coming
            so the layout is complete and predictable. */}
        {isPhoto && (
          <ToolbarBtn label="Recadrer" onClick={() => alert('Recadrage avancé : bientôt disponible. Pour l\'instant, déplacez la photo dans son emplacement depuis la barre latérale.')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 2v14a2 2 0 002 2h14M2 6h14a2 2 0 012 2v14" />
            </svg>
          </ToolbarBtn>
        )}

        {/* Flip — only useful for photos/shapes. Mirroring TEXT just
            produces unreadable output (no legitimate design use case),
            so we hide the button for text elements. Customers reported
            accidentally tapping flip and then thinking the font picker
            broke their text (D78 2026-05-21). */}
        {!isText && (
          <ToolbarBtn
            label="Retourner (clic court = horizontal, maintenir = vertical)"
            active={!!element.flipH || !!element.flipV}
            onPointerDown={handleFlipDown}
            onPointerUp={handleFlipUp}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3v18M3 12h18M7 7l-4 5 4 5M17 7l4 5-4 5" />
            </svg>
          </ToolbarBtn>
        )}
        {/* Text-only escape hatch: if a text element has flip set from
            legacy data OR an accidental tap before the fix shipped, show
            a single reset action so the customer isn't stranded with
            unreadable mirrored text. Vanishes once both flips are off. */}
        {isText && (element.flipH || element.flipV) && (
          <ToolbarBtn
            label="Annuler le retournement"
            active={true}
            onClick={() => onUpdate({ flipH: false, flipV: false })}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" />
            </svg>
          </ToolbarBtn>
        )}

        {/* ITEM 5 (2026-07-06) — the numeric X/Y/W/H "Position et taille" tab was
            removed: it duplicated the drag gesture (position) and the corner
            resize handles (size), which was the conflicting second settings
            system. One panel now owns styling (font/size/colour); position and
            frame size are done by direct manipulation on the canvas. */}

        <ToolbarBtn label="Supprimer" onClick={onDelete}>
          <svg className="w-5 h-5" style={{ color: '#DC2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
          </svg>
        </ToolbarBtn>

        {/* ONE confirm button (2026-07-08 — unified text state). The old pair
            (gold "Terminé" while typing + always-on green ✓ "Valider") were
            LITERAL duplicates — both called this same onStopEditing, which at
            the render site exits editing + closes the toolbar + deselects.
            Customers read them as two required steps (and a focus race made
            the gold one swallow its first tap — see the toolbar root's
            onPointerDown). One always-visible gold Terminé now closes
            everything in a single tap, for text and non-text elements alike. */}
        {onStopEditing && (
          <button
            type="button"
            onClick={onStopEditing}
            className="ml-1 px-3 h-11 md:h-8 shrink-0 rounded-lg flex items-center gap-1 text-sm font-medium active:scale-95"
            style={{ background: COLORS.gold, color: COLORS.white }}
            aria-label="Terminer et fermer"
            title="Terminé"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>Terminé</span>
          </button>
        )}
    </div>
  );

  return (
    <div
      // D78 (2026-05-21) kept the bar off the bottom thumbnail strip; it now
      // never leaves the canvas at all (see `place` above). Phones: docked
      // 76 px above the in-app tab bar (64 px bar + 12 px gap), full width,
      // controls wrap so every one is visible at once.
      className={cn(
        'storiz-element-toolbar',
        inline ? 'relative w-full px-1' : 'fixed z-[55]',
        !inline && (dockBottom ? 'left-1/2 -translate-x-1/2 w-full max-w-[100vw] px-2' : 'max-w-[calc(100vw-16px)]'),
      )}
      data-element-toolbar={inline ? 'inline' : dockBottom ? 'docked' : (place?.side ?? 'measuring')}
      // UNIFIED TEXT STATE (2026-07-08): keep the contentEditable span FOCUSED
      // when tapping toolbar controls. Without this, pointerdown on any button
      // blurred the text → isEditing flipped → the gold Terminé unmounted
      // BEFORE its click registered → tap lost → customers needed a second tap
      // (the "double confirm" bug). preventDefault on pointerdown stops the
      // focus steal; clicks still fire. Inputs/selects are exempted — they
      // legitimately need focus (e.g. the font-size number field).
      onPointerDown={(e) => {
        e.stopPropagation();
        const t = e.target as HTMLElement;
        if (!t.closest('input,select,textarea')) e.preventDefault();
      }}
      style={inline ? undefined : dockBottom
        ? { bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)' }
        : {
            left: place?.x ?? 0,
            top: place?.y ?? 0,
            transform: `translate(-50%, ${place?.side === 'below' ? '0' : '-100%'})`,
            // first frame: measured off-screen-invisible, then placed
            visibility: place ? 'visible' : 'hidden',
          }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Panels open on the side AWAY from the element: above the bar when
          the bar is above it, below the bar when the bar is below it. */}
      {opensBelow && controlsBar}
      {/* Expandable panel (color / opacity / position) */}
      {open === 'color' && supportsColor && (
        <div className={cn(opensBelow ? 'mt-2' : 'mb-2', 'mx-auto p-3 rounded-xl bg-white shadow-xl flex items-center gap-3')}
             style={{ border: `1px solid ${COLORS.gold}30`, minWidth: 280 }}>
          <input
            type="color"
            value={colorValue}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-12 h-12 rounded cursor-pointer border-0"
            style={{ background: 'transparent' }}
            aria-label="Couleur"
          />
          <input
            type="text"
            value={colorValue}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9A-Fa-f]{6}$/.test(v)) handleColorChange(v);
            }}
            placeholder="#000000"
            className="flex-1 px-2 py-1.5 text-sm rounded border outline-none"
            style={{ borderColor: `${COLORS.gold}30`, fontFamily: 'monospace' }}
            maxLength={7}
          />
        </div>
      )}

      {open === 'opacity' && (
        <div className={cn(opensBelow ? 'mt-2' : 'mb-2', 'mx-auto p-3 rounded-xl bg-white shadow-xl flex items-center gap-3')}
             style={{ border: `1px solid ${COLORS.gold}30`, minWidth: 280 }}>
          <span className="text-xs font-medium" style={{ color: UI.muted }}>0%</span>
          <input
            type="range" min={0} max={100} step={1}
            value={opacityPct}
            onChange={(e) => onUpdate({ opacity: Number(e.target.value) / 100 })}
            className="flex-1"
            aria-label="Opacité"
          />
          <span className="text-xs font-semibold tabular-nums w-10 text-right" style={{ color: UI.ink }}>
            {opacityPct}%
          </span>
        </div>
      )}


      {/* Font picker panel — Section 7. Four sections:
            ⭐ Starred fonts (favourited via the star button)
            📄 Document fonts (already in use elsewhere in the album)
            🕘 Recently used (last picks, last-in-first)
            🅰 All fonts
          Each row renders the font name in its OWN typeface so the
          customer previews the look before tapping. Selected font shows
          a tick; star toggles favourite. Full-screen modal on mobile. */}
      {open === 'font' && isText && (() => {
        const currentFamily = element.fontFamily ?? '';
        const known = new Map(EDITOR_TEXT_FONTS.map((f) => [f.family, f.label] as const));
        const labelFor = (family: string) => {
          const exact = known.get(family);
          if (exact) return exact;
          const guess = (family.split(',')[0] || '').replace(/['"]/g, '').trim();
          return guess || 'Police personnalisée';
        };

        // De-dupe the document-fonts list against starred so a single font
        // never appears in two sections back-to-back.
        const starredSet = new Set(starred);
        const docFonts = documentFonts.filter((f) => !starredSet.has(f));
        const docSet   = new Set(docFonts);
        const recentFonts = recent.filter((f) => !starredSet.has(f) && !docSet.has(f));
        const recentSet   = new Set(recentFonts);

        const Row = ({ family }: { family: string }) => {
          const selected = family === currentFamily;
          const isStarred = starredSet.has(family);
          return (
            <div
              className="w-full flex items-center gap-2 rounded-lg"
              style={{ background: selected ? `${COLORS.gold}15` : 'transparent' }}
            >
              <button
                onClick={() => pickFont(family)}
                className="flex-1 flex items-center justify-between px-3 py-2.5 text-left transition-colors min-h-11"
                style={{ fontFamily: family, fontSize: 18, color: UI.ink }}
              >
                <span className="truncate">{labelFor(family)}</span>
                {selected && (
                  <svg className="w-4 h-4 shrink-0 ml-2" style={{ color: COLORS.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); toggleStar(family); }}
                className="w-11 h-11 flex items-center justify-center rounded-lg shrink-0"
                aria-label={isStarred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                title={isStarred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                <svg
                  className="w-5 h-5"
                  fill={isStarred ? COLORS.gold : 'none'}
                  stroke="currentColor"
                  style={{ color: isStarred ? COLORS.gold : UI.muted }}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.075 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.518-4.674z" />
                </svg>
              </button>
            </div>
          );
        };

        const Section = ({ title, fonts }: { title: string; fonts: string[] }) =>
          fonts.length === 0 ? null : (
            <div className="mb-1.5">
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: UI.muted }}>
                {title}
              </div>
              {fonts.map((family) => <Row key={family} family={family} />)}
            </div>
          );

        const builtins = EDITOR_TEXT_FONTS.map((f) => f.family);
        const custom   = customFonts.map((f) => f.family);
        const allFamilies = [...builtins, ...custom]
          .filter((f) => !starredSet.has(f) && !docSet.has(f) && !recentSet.has(f));
        // Merge custom font labels into the known map so labelFor()
        // shows the user-supplied name instead of the parsed first
        // CSS family token.
        for (const cf of customFonts) known.set(cf.family, cf.label);

        return (
          <div
            className={cn(opensBelow ? 'mt-2' : 'mb-2', 'mx-auto p-2 rounded-xl bg-white shadow-xl overflow-y-auto')}
            style={{ border: `1px solid ${COLORS.gold}30`, minWidth: 300, width: 'min(420px, 92vw)', maxHeight: 'min(60vh, 420px)' }}
          >
            <Section title="⭐ Favoris"          fonts={starred} />
            <Section title="📄 Polices du document" fonts={docFonts} />
            <Section title="🕘 Récemment utilisées" fonts={recentFonts} />
            <Section title="🅰 Toutes les polices"   fonts={allFamilies} />
          </div>
        );
      })()}

      {!opensBelow && controlsBar}
    </div>
  );
}

/** « Nouvel éditeur mobile »: the cover / intro surface under the same
 *  zoom + pan as the spread (pinch, typing). Off = its children as they were. */
function M2Zoom({ on, style, children }: { on: boolean; style: React.CSSProperties; children: React.ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <div data-m2-zoomable="" className="flex flex-col items-center justify-center self-stretch flex-1 min-h-0 w-full" style={style}>
      {children}
    </div>
  );
}

// ─── Mobile hold-to-delete bin affordance ───────────────────────────────────
// Centered pill shown over a lifted (held ~1s) empty page. The wrapper is
// pointer-events-none so it never blocks the page beneath; only the button is
// interactive. Tapping it opens the shared warning popup.
function PageLiftBin({ onDelete }: { onDelete: () => void }) {
  return (
    <div className="absolute inset-0 z-[31] flex items-center justify-center pointer-events-none">
      <button
        data-page-lift-bin
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="storiz-pop-in pointer-events-auto flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95"
        style={{ background: COLORS.danger, boxShadow: `0 10px 24px ${COLORS.danger}55` }}
        aria-label="Supprimer cette page"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
        </svg>
        Supprimer cette page
      </button>
    </div>
  );
}

// ─── Shared page-delete confirmation popup ──────────────────────────────────
// One premium, minimal dialog reused by every delete entry point (scroller ✕,
// desktop tools panel, mobile hold-to-delete bin). Copy is fixed per spec; the
// title pluralises when a whole spread (2 pages) is being removed.
function PageDeleteConfirmModal({
  count,
  title: titleProp,
  message,
  confirmLabel = 'Confirmer',
  onConfirm,
  onCancel,
}: {
  count: number;
  /** Override the default "Supprimer …" heading (e.g. page-count reduction). */
  title?: string;
  /** Override the default sub-copy. */
  message?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const title = titleProp ?? (count > 1 ? `Supprimer ces ${count} pages ?` : 'Supprimer cette page ?');
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-5"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm storiz-fade-in" />
      <div
        className="storiz-pop-in relative w-full max-w-[340px] rounded-2xl bg-white p-6 text-center"
        style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.22), 0 4px 14px rgba(0,0,0,0.10)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Danger-tinted bin glyph */}
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: `${COLORS.danger}14` }}
        >
          <svg className="h-6 w-6" fill="none" stroke={COLORS.danger} viewBox="0 0 24 24" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
          </svg>
        </div>

        <h2
          className="mb-1.5 text-lg font-semibold"
          style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: UI.ink }}
        >
          {title}
        </h2>
        <p className="mb-6 text-sm leading-relaxed" style={{ color: UI.muted }}>
          {message ?? 'Les pages suivantes seront renumérotées 📄'}
        </p>

        <div className="flex gap-2.5">
          <button
            onClick={onCancel}
            className="flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
            style={{ background: UI.chip, color: UI.ink }}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97]"
            style={{ background: COLORS.danger, boxShadow: `0 6px 16px ${COLORS.danger}40` }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// « Page de fin » pieces, for the DEV fixture page /admin/_dev/outro
// (pages/dev/OutroPagePreviewPage.tsx) — the same components the editor draws.
export { InsertPageMenu, OutroPageView, SpreadThumb };
